/* Generated: 2026-11-02 | TikTok: 34 | Instagram: 1265 | YouTube: 537 */
/**
 * CoraCreaCrafts Cinematic Video Gallery
 * @module video-gallery-ui
 *
 * Main gallery UI controller. Manages filtering, searching, sorting, rendering, and modal interactions.
 * Data is injected at build time via generate-video-gallery.js (replaces DATA_PLACEHOLDER comment).
 *
 * Architecture:
 * - State-driven UI (state object holds all reactive data)
 * - Event delegation for filter/category/view buttons
 * - Modal for full-video playback with navigation (arrows + keyboard)
 * - Instagram embeds with fallback blockquote links
 * - Secure thumbnail error handling with platform-specific fallbacks
 *
 * Key Functions:
 * - init() — Initialize gallery (called on DOMContentLoaded or if already loaded)
 * - applyFilters() — Re-filter and re-render grid based on state
 * - openModal(index) — Open full-screen modal for video at index
 * - closeModal() — Close modal, clean up embeds
 * - bindEvents() — Attach all event listeners (filter, search, sort, modal navigation)
 *
 * State Object:
 * - filteredVideos: currently displayed videos after filters/search
 * - activeFilter: 'all' or platform name
 * - activeCategory: 'all' or category name
 * - searchQuery: text search string (title + description + tags + creator)
 * - sortBy: 'newest', 'oldest', or 'title'
 * - viewMode: 'cards' or 'list'
 * - currentModalIndex: index of open modal video, -1 if closed
 *
 * Constants:
 * - DOM_IDS: all element IDs in the gallery HTML
 * - CLASS_SELECTORS: all interactive CSS class selectors
 * - CATEGORIES: category ID to display name mapping
 * - PLATFORM_LABELS: platform to display name mapping
 * - ICONS: SVG icons for metadata (creator, calendar, play button)
 * - MONTH_NAMES: abbreviated month names for date formatting
 * - DEFAULT_PLACEHOLDER_SVG: gradient fallback image
 *
 * Dependencies:
 * - safeSetHTML(element, html) - from video-gallery-security.js. Parses and inserts
 *   HTML. It does no sanitizing: no tag filtering, no attribute stripping, no URL
 *   scheme check. The XSS control is escapeHtml() at construction, below. A
 *   three-argument sanitizing version lived in theme.liquid for one day in
 *   December 2025 and was deleted; do not pass it unescaped data on the strength
 *   of its name.
 * - video-gallery-security.js — validation functions
 *
 * Notes:
 * - All HTML is generated via string concatenation with escapeHtml() for XSS safety
 * - Instagram embeds use embed.js script loading with retry logic
 * - Keyboard navigation: Escape to close, Arrow Left/Right to navigate
 * - Thumbnail error fallback does NOT validate embedId; it interpolates it into a YouTube URL
 *   and trusts the caller, as _handleThumbError's own param doc says. The values reach it from
 *   data attributes via a delegated capture listener rather than an inline onerror.
 */

/* ========== DOM SELECTOR CONSTANTS ========== */

/**
 * Map of all element IDs used in the gallery
 * @type {Object<string, string>}
 * @constant
 */
const DOM_IDS = {
  GRID: 'vg-grid',
  LOADER: 'vg-loader',
  SEARCH: 'vg-search',
  SORT: 'vg-sort',
  RESULTS_COUNT: 'vg-results-count',
  MODAL: 'vg-modal',
  MODAL_EMBED: 'vg-modal-embed',
  MODAL_TITLE: 'vg-modal-title',
  MODAL_DESC: 'vg-modal-desc',
  MODAL_DETAILS: 'vg-modal-details',
  MODAL_TAGS: 'vg-modal-tags',
  MODAL_LINK: 'vg-modal-link',
  MODAL_CLOSE: 'vg-modal-close',
  MODAL_PREV: 'vg-modal-prev',
  MODAL_NEXT: 'vg-modal-next',
  STAT_TOTAL: 'vg-stat-total',
  STAT_TIKTOK: 'vg-stat-tiktok',
  STAT_INSTAGRAM: 'vg-stat-instagram',
  STAT_YOUTUBE: 'vg-stat-youtube',
};

/**
 * Map of all CSS class selectors used in the gallery
 * @type {Object<string, string>}
 * @constant
 */
const CLASS_SELECTORS = {
  FILTER_BTN: '.vg-filter-btn',
  CAT_BTN: '.vg-cat-btn',
  VIEW_BTN: '.vg-view-btn',
  CARD: '.vg-card',
  BADGE: 'vg-badge',
  GRID: 'vg-grid',
  ACTIVE: 'active',
  OPEN: 'open',
  IG_LOADING: 'vg-ig-loading',
};

/**
 * Default fallback SVG when thumbnail fails to load
 * Gradient background with play button icon (400×225px aspect ratio)
 * @type {string}
 * @constant
 */
const DEFAULT_PLACEHOLDER_SVG =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjIyNSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48bGluZWFyR3JhZGllbnQgaWQ9ImciIHgxPSIwJSIgeTE9IjAlIiB4Mj0iMTAwJSIgeTI9IjEwMCUiPjxzdG9wIG9mZnNldD0iMCUiIHN0b3AtY29sb3I9IiMyMDEwMzAiLz48c3RvcCBvZmZzZXQ9IjEwMCUiIHN0b3AtY29sb3I9IiMxMDEwMjAiLz48L2xpbmVhckdyYWRpZW50PjwvZGVmcz48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjIyNSIgZmlsbD0idXJsKCNnKSIvPjxjaXJjbGUgY3g9IjIwMCIgY3k9IjExMiIgcj0iMzAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzUwNDA3MCIgc3Ryb2tlLXdpZHRoPSIyIi8+PHBvbHlnb24gcG9pbnRzPSIxOTIsMTAwIDIxNSwxMTIgMTkyLDEyNCIgZmlsbD0iIzUwNDA3MCIvPjwvc3ZnPg==';
/**
 * Backward compatibility alias for DEFAULT_PLACEHOLDER_SVG
 * @type {string}
 * @deprecated Use DEFAULT_PLACEHOLDER_SVG instead
 * @private
 */
const _FALLBACK_SVG = DEFAULT_PLACEHOLDER_SVG;

/**
 * Private thumbnail error handler
 *
 * Attempts YouTube fallback (maxresdefault → hqdefault),
 * then uses default SVG placeholder. Wraps handleThumbErrorSecure() for window scope.
 *
 * @param {HTMLImageElement} img — image element with failed src
 * @param {string} platform — 'youtube', 'tiktok', or 'instagram'
 * @param {string} embedId — video embedId (format not validated here, trusts caller)
 * @returns {void}
 * @private
 */
function _handleThumbError(img, platform, embedId) {
  // YouTube: try hqdefault before giving up on maxresdefault
  if (platform === 'youtube' && img.src.indexOf('maxresdefault') !== -1) {
    if (window.VideoGalleryMetrics) {
      window.VideoGalleryMetrics.logThumbnailFallback(platform, embedId, 'maxres→hqdef');
    }
    img.src = 'https://img.youtube.com/vi/' + embedId + '/hqdefault.jpg';
    return;
  }
  img.onerror = null;
  // The onerror null is for any caller still invoking window.handleThumbError directly; the
  // attribute is what the delegated capture listener checks, since it has no handler to clear.
  img.setAttribute('data-vg-thumb-fallback', 'done');
  if (window.VideoGalleryMetrics) {
    window.VideoGalleryMetrics.logThumbnailFallback(platform, embedId, 'fallback to SVG');
  }
  img.src = DEFAULT_PLACEHOLDER_SVG;
}

(function () {
  'use strict';

  /* ---------- Video Data ---------- */

  /**
   * All video entries injected at build time
   * Replaced by generate-video-gallery.js script
   * @type {Array<Object>}
   * @constant
   */
  const VIDEOS = [
        // === TikTok Videos (34 entries) ===
    {
      id: 'tt-1',
      platform: 'tiktok',
      title: 'Medieval Vintage Craft Box Reveal',
      description: 'It\'s finally time to reveal a sneak peek of our November Vintage Craft Box! This Medieval theme holds a special place in my heart. I can\'t wait for you to experience the magic and charm of this collection!',
      url: 'https://www.tiktok.com/@coracreacrafts_shop/video/7437272843777248554',
      embedId: '7437272843777248554',
      category: 'box-reveal',
      tags: ['medieval', 'vintage craft box', 'sneak peek', 'journaling supplies'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://wsrv.nl/?url=p16-common-sign.tiktokcdn-us.com%2Ftos-useast5-p-0068-tx%2Fbbe0f961df1c4d3f9e3423590f3a41be_1731625032~tplv-tiktokx-dmt-logom%3Atos-useast5-i-0068-tx%2Foogj9kOAPqebPjMZGHQUmbLD4AAE4AeZIfIAG2.image%3Fdr%3D9634%26x-expires%3D1791986400%26x-signature%3DbiOeO2VDThVCQcPWd2tpBJB7nP4%253D%26t%3D4d5b0474%26ps%3D13740610%26shp%3D81f88b70%26shcp%3D43f4a2f9%26idc%3Duseast8&w=400&output=jpg'
    },
    {
      id: 'tt-2',
      platform: 'tiktok',
      title: 'DIY Witchy Box - Live Craft Session',
      description: 'Thank you all for participating in last Friday\'s live! I had so much fun decorating a random plain box and turning it into a Witchy storage for my desk!',
      url: 'https://www.tiktok.com/@coracreacrafts_shop/video/7431297131173940526',
      embedId: '7431297131173940526',
      category: 'tutorial',
      tags: ['diy', 'witchy', 'halloween', 'live', 'crafting'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://wsrv.nl/?url=p16-common-sign.tiktokcdn-us.com%2Ftos-useast5-p-0068-tx%2Fa96029718f624af49dd967d02e1c294f_1730233702~tplv-tiktokx-dmt-logom%3Atos-useast5-i-0068-tx%2FoUhRlApCIIwAUJ8f3AEA2IBzA28iiBnKBwjKRk.image%3Fdr%3D9634%26x-expires%3D1791986400%26x-signature%3DCvnMjfULP5p1AAxIw0LSRyciy%252Bs%253D%26t%3D4d5b0474%26ps%3D13740610%26shp%3D81f88b70%26shcp%3D43f4a2f9%26idc%3Duseast8&w=400&output=jpg'
    },
    {
      id: 'tt-3',
      platform: 'tiktok',
      title: 'Digital Journal Kits Showcase',
      description: 'Creating our Digital Journal Kits has been one of the highlights of this year for me. I love knowing that they give everyone a chance to sit down, get creative, and make something beautiful.',
      url: 'https://www.tiktok.com/@coracreacrafts_shop/video/7452181977563712814',
      embedId: '7452181977563712814',
      category: 'product-showcase',
      tags: ['digital journal kit', 'creative tools', 'mindful crafting', 'junk journal'],
      creator: 'CoraCreaCrafts',
      date: '2024-12',
      thumbnail: 'https://wsrv.nl/?url=p16-common-sign.tiktokcdn-us.com%2Ftos-useast5-p-0068-tx%2FoEAKEIu8OHC78ej5AGChzRe1LJ4QkQejBIGjWS~tplv-tiktokx-dmt-logom%3Atos-useast5-i-0068-tx%2FowegyA8XQWQAe7MDifAFIfWQnfAJA7W9MDc7SI.image%3Fdr%3D9634%26x-expires%3D1791986400%26x-signature%3D1imPTAb3hN8OPvSumdKcMdUqHkI%253D%26t%3D4d5b0474%26ps%3D13740610%26shp%3D81f88b70%26shcp%3D43f4a2f9%26idc%3Duseast8&w=400&output=jpg'
    },
    {
      id: 'tt-4',
      platform: 'tiktok',
      title: '2024 Advent Calendar Spoilers',
      description: 'SPOILERS! I\'m absolutely blown away by how many of you have already grabbed a 2024 Advent Calendar! If you haven\'t gotten yours yet, there\'s still time!',
      url: 'https://www.tiktok.com/@coracreacrafts_shop/video/7436760830856744234',
      embedId: '7436760830856744234',
      category: 'product-showcase',
      tags: ['advent calendar', 'christmas', 'stationery', 'journaling supplies'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://wsrv.nl/?url=p16-common-sign.tiktokcdn-us.com%2Ftos-useast5-p-0068-tx%2FokQpgGHfjXAaAOGODLpAeFWqowkIDIvAkDVTCe~tplv-tiktokx-origin.image%3Fdr%3D9636%26x-expires%3D1791986400%26x-signature%3DRk4IBfg3%252Ba85LMVzFeJJZcpXSjA%253D%26t%3D4d5b0474%26ps%3D13740610%26shp%3D81f88b70%26shcp%3D43f4a2f9%26idc%3Duseast8&w=400&output=jpg'
    },
    {
      id: 'tt-5',
      platform: 'tiktok',
      title: 'Gothic Christmas Washi Tape Spread',
      description: 'Can you believe this whole spread came from just one washi tape? I used the Gothic Washi Tape, and I\'m obsessed with how it turned out! Sometimes, keeping it simple works best.',
      url: 'https://www.tiktok.com/@coracreacrafts_shop/video/7448770094286753070',
      embedId: '7448770094286753070',
      category: 'journal-spread',
      tags: ['gothic christmas', 'washi tape', 'dark holiday', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-12',
      thumbnail: 'https://wsrv.nl/?url=p19-common-sign.tiktokcdn-us.com%2Ftos-useast5-p-0068-tx%2Fo8aPfAlc1SHbmEQnqXFfDBgDDWyPiuQ4RlD8En~tplv-tiktokx-dmt-logom%3Atos-useast5-i-0068-tx%2Fo4OFQe5IAMQqfAAAAskE8WdYHHkbbg63jrvXfA.image%3Fdr%3D9634%26x-expires%3D1791986400%26x-signature%3DQ5P71769ftg%252FxZ5Oqwy5VP%252FYCDg%253D%26t%3D4d5b0474%26ps%3D13740610%26shp%3D81f88b70%26shcp%3D43f4a2f9%26idc%3Duseast8&w=400&output=jpg'
    },
    {
      id: 'tt-6',
      platform: 'tiktok',
      title: 'Our Small Business Journey',
      description: 'Creating CoraCreaCrafts has been the hardest yet most rewarding journey I could have ever dreamed of. We\'re resilient, creative, and not alone. It\'s going to be ok!',
      url: 'https://www.tiktok.com/@coracreacrafts_shop/video/7490718854243470634',
      embedId: '7490718854243470634',
      category: 'behind-the-scenes',
      tags: ['small business', 'business owner', 'vintage crafts', 'inspiring'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://wsrv.nl/?url=p16-common-sign.tiktokcdn-us.com%2Ftos-useast5-p-0068-tx%2FoEC0AYf6WLzpKIMLQPIGpUe2QeGGAGjbHk0tWj~tplv-tiktokx-origin.image%3Fdr%3D9636%26x-expires%3D1791986400%26x-signature%3DDYY2igUWEDYfwC%252BPvFjJCg%252FhkI4%253D%26t%3D4d5b0474%26ps%3D13740610%26shp%3D81f88b70%26shcp%3D43f4a2f9%26idc%3Duseast8&w=400&output=jpg'
    },
    {
      id: 'tt-7',
      platform: 'tiktok',
      title: 'Air of Autumn Box Spread',
      description: 'Our lovely ambassador Liu created this stunning Autumn Spread using the supplies from our Air of Autumn Box. You can buy these individually on the website now too!',
      url: 'https://www.tiktok.com/@coracreacrafts_shop/video/7436203812328639787',
      embedId: '7436203812328639787',
      category: 'journal-spread',
      tags: ['autumn', 'ambassador', 'scrapbook', 'journaling supplies'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://wsrv.nl/?url=p16-common-sign.tiktokcdn-us.com%2Ftos-useast5-p-0068-tx%2FoM6HGA4fIygKUzGzRLA1QfugfgnNmcKfYrOsFl~tplv-tiktokx-origin.image%3Fdr%3D9636%26x-expires%3D1791986400%26x-signature%3Dp06iBKJAm%252BZ9gKCcofP8idiYRtQ%253D%26t%3D4d5b0474%26ps%3D13740610%26shp%3D81f88b70%26shcp%3D43f4a2f9%26idc%3Duseast8&w=400&output=jpg'
    },
    {
      id: 'tt-8',
      platform: 'tiktok',
      title: 'Alice in Wonderland Spread',
      description: 'Creating this Alice in Wonderland-inspired spread made me want to watch the movie again! Thank you all for the incredible love on this box. You can pre-order it now!',
      url: 'https://www.tiktok.com/@coracreacrafts_shop/video/7474015223427648814',
      embedId: '7474015223427648814',
      category: 'journal-spread',
      tags: ['alice in wonderland', 'fantasy', 'crafting community', 'scrapbook'],
      creator: 'CoraCreaCrafts',
      date: '2025-02',
      thumbnail: 'https://wsrv.nl/?url=p16-common-sign.tiktokcdn-us.com%2Ftos-useast5-p-0068-tx%2FosHCfADcoC6HWFjoPueeGEkIAIM5COlIoMghEk~tplv-tiktokx-dmt-logom%3Atos-useast5-i-0068-tx%2FoQAAsjCAgiIWCIA5YZBY4kvJpE87AkSJBisEA.image%3Fdr%3D9634%26x-expires%3D1791986400%26x-signature%3D%252FSIL5tD8FdaQ2e8CwJUuG8TCdOc%253D%26t%3D4d5b0474%26ps%3D13740610%26shp%3D81f88b70%26shcp%3D43f4a2f9%26idc%3Duseast8&w=400&output=jpg'
    },
    {
      id: 'tt-9',
      platform: 'tiktok',
      title: 'La Gioconda - Mona Lisa Mini Journal',
      description: 'Did you know the original name of the famous painting we call Mona Lisa is La Gioconda? This is because the painting represents Lisa del Giocondo, the wife of a wealthy silk merchant.',
      url: 'https://www.tiktok.com/@coracreacrafts_shop/video/7382331498503982378',
      embedId: '7382331498503982378',
      category: 'tutorial',
      tags: ['mona lisa', 'gioconda', 'mini journal', 'asmr'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://wsrv.nl/?url=p19-common-sign.tiktokcdn-us.com%2Ftos-useast5-p-0068-tx%2F9a1f9ef8054a4b1d8515f57075000a9a_1718833003~tplv-tiktokx-dmt-logom%3Atos-useast5-i-0068-tx%2Fo42qEaFMAeSjogyzDADAI9cyR5mgTCBEA0E1fO.image%3Fdr%3D9634%26x-expires%3D1791986400%26x-signature%3D%252BT5F3Q2Ds9pn12Owtdxjn8z9umg%253D%26t%3D4d5b0474%26ps%3D13740610%26shp%3D81f88b70%26shcp%3D43f4a2f9%26idc%3Duseast8&w=400&output=jpg'
    },
    {
      id: 'tt-10',
      platform: 'tiktok',
      title: 'Alice in Wonderland Sunday Crafting',
      description: 'Sundays are for crafting! Sharing a little Alice in Wonderland magic with this mini journal spread! How are you spending your creative Sunday?',
      url: 'https://www.tiktok.com/@coracreacrafts_shop/video/7477358700617895210',
      embedId: '7477358700617895210',
      category: 'journal-spread',
      tags: ['alice in wonderland', 'junk journal', 'sunday crafting', 'mini journal'],
      creator: 'CoraCreaCrafts',
      date: '2025-02',
      thumbnail: 'https://wsrv.nl/?url=p16-common-sign.tiktokcdn-us.com%2Ftos-useast5-p-0068-tx%2FoUOnBJTUEFIF621AJgfQx6aERnSpDDJ0EfCQ4S~tplv-tiktokx-dmt-logom%3Atos-useast5-i-0068-tx%2FoAjn0B4RIAJatKAC2B6iqtUAVEDIwAiwzEBw4f.image%3Fdr%3D9634%26x-expires%3D1791986400%26x-signature%3Dy27V%252BrSE8Af3hsXfK7YD8sl1L%252FQ%253D%26t%3D4d5b0474%26ps%3D13740610%26shp%3D81f88b70%26shcp%3D43f4a2f9%26idc%3Duseast8&w=400&output=jpg'
    },
    {
      id: 'tt-c1',
      platform: 'tiktok',
      title: 'February Mystery Box Unboxing',
      description: 'The February Vintage Craft Box theme is Mystery! Since I\'m a huge fan of true crime podcasts and Agatha Christie, this box speaks to me!',
      url: 'https://www.tiktok.com/@liu_journal/video/7336621050072960288',
      embedId: '7336621050072960288',
      category: 'unboxing',
      tags: ['mystery', 'unboxing', 'agatha christie', 'ambassador'],
      creator: 'Liu Journal',
      date: '2024-02',
      thumbnail: 'https://wsrv.nl/?url=p16-common-sign.tiktokcdn-us.com%2Ftos-useast2a-p-0037-euttp%2F21ed3554b0cc426fb6c2aacd028b5567_1708190211~tplv-tiktokx-origin.image%3Fdr%3D9636%26x-expires%3D1791986400%26x-signature%3DUkG%252BmD8Nwb7yt3ER38WfqFYl%252F6c%253D%26t%3D4d5b0474%26ps%3D13740610%26shp%3D81f88b70%26shcp%3D43f4a2f9%26idc%3Duseast8&w=400&output=jpg'
    },
    {
      id: 'tt-c2',
      platform: 'tiktok',
      title: 'Scotland April Box Unboxing',
      description: 'Scotland! That is the theme of the new Vintage Box. This April edition is full of beautiful items. Scotland Landscapes Transparent Tape, Stamp Washi, Thistle Wooden Stamp, and more!',
      url: 'https://www.tiktok.com/@liu_journal/video/7489859855428324610',
      embedId: '7489859855428324610',
      category: 'unboxing',
      tags: ['scotland', 'unboxing', 'washi tape', 'ambassador'],
      creator: 'Liu Journal',
      date: '2025-03',
      thumbnail: 'https://wsrv.nl/?url=p19-common-sign.tiktokcdn-us.com%2Ftos-no1a-p-0037-no%2FoApFAiUXITMgHeAfPf56iQURf7Ap6DeHxAEAAs~tplv-tiktokx-origin.image%3Fdr%3D9636%26x-expires%3D1791986400%26x-signature%3Di%252BcEODRQrpn3%252F7HtJsJiOOtOklc%253D%26t%3D4d5b0474%26ps%3D13740610%26shp%3D81f88b70%26shcp%3D43f4a2f9%26idc%3Duseast8&w=400&output=jpg'
    },
    {
      id: 'tt-c3',
      platform: 'tiktok',
      title: 'Ancient Pharmacy Box Spread',
      description: 'Sunday Funday! Today\'s spread is featuring products from the Ancient Pharmacy box. I also added one of the newest collaboration tapes.',
      url: 'https://www.tiktok.com/@liu_journal/video/7448680846019595542',
      embedId: '7448680846019595542',
      category: 'journal-spread',
      tags: ['ancient pharmacy', 'apothecary', 'journal spread', 'ambassador'],
      creator: 'Liu Journal',
      date: '2024-12',
      thumbnail: 'https://wsrv.nl/?url=p19-common-sign.tiktokcdn-us.com%2Ftos-no1a-p-0037-no%2FoEAKAeflGsC6LAwiNfIBBWI8fUgQNmlApYMKeA~tplv-tiktokx-origin.image%3Fdr%3D9636%26x-expires%3D1791986400%26x-signature%3D7Z6QuadgULEfqqwnU1zgJR2KmZ0%253D%26t%3D4d5b0474%26ps%3D13740610%26shp%3D81f88b70%26shcp%3D43f4a2f9%26idc%3Duseast8&w=400&output=jpg'
    },
    {
      id: 'tt-c4',
      platform: 'tiktok',
      title: 'Alice Box Unboxing - First Look',
      description: 'New month, new box! The theme this month is Alice! A real treat for Alice in Wonderland fans. It is just very magical! All the items are beautiful and inspiring!',
      url: 'https://www.tiktok.com/@liu_journal/video/7469836806947704086',
      embedId: '7469836806947704086',
      category: 'unboxing',
      tags: ['alice in wonderland', 'unboxing', 'craft supplies', 'ambassador'],
      creator: 'Liu Journal',
      date: '2025-01',
      thumbnail: 'https://wsrv.nl/?url=p16-common-sign.tiktokcdn-us.com%2Ftos-no1a-p-0037-no%2FoAkQEaBzCwAIPCI8AiVQfAwAqBTB1ixiRSyMPC~tplv-tiktokx-origin.image%3Fdr%3D9636%26x-expires%3D1791986400%26x-signature%3DfU8BzBI8JUdzVHkCKCczilnjjk0%253D%26t%3D4d5b0474%26ps%3D13740610%26shp%3D81f88b70%26shcp%3D43f4a2f9%26idc%3Duseast8&w=400&output=jpg'
    },
    {
      id: 'tt-c5',
      platform: 'tiktok',
      title: 'Gothic Box Spread - Sunday Creation',
      description: 'Another spread with the Gothic Vintage Craft Box. This box has so many beautiful products. A true inspiration!',
      url: 'https://www.tiktok.com/@liu_journal/video/7469463389316713750',
      embedId: '7469463389316713750',
      category: 'journal-spread',
      tags: ['gothic', 'journal spread', 'dark academia', 'ambassador'],
      creator: 'Liu Journal',
      date: '2025-01',
      thumbnail: 'https://wsrv.nl/?url=p19-common-sign.tiktokcdn-us.com%2Ftos-no1a-p-0037-no%2FokAARLTLrUFsGufAjeeFbuIqAIIdLFAQFPhXIw~tplv-tiktokx-origin.image%3Fdr%3D9636%26x-expires%3D1791986400%26x-signature%3D8o5AO4VfkizxwW6gWx6YqCC1EN0%253D%26t%3D4d5b0474%26ps%3D13740610%26shp%3D81f88b70%26shcp%3D43f4a2f9%26idc%3Duseast8&w=400&output=jpg'
    },
    {
      id: 'tt-c6',
      platform: 'tiktok',
      title: 'Alice Box - We Are All Mad Here',
      description: 'We are all mad here! Another spread with the beautiful Alice Vintage Craft Box. This box is a dream and the items are super easy to put together.',
      url: 'https://www.tiktok.com/@liu_journal/video/7480602790285364502',
      embedId: '7480602790285364502',
      category: 'journal-spread',
      tags: ['alice in wonderland', 'journal spread', 'mixed media', 'ambassador'],
      creator: 'Liu Journal',
      date: '2025-03',
      thumbnail: 'https://wsrv.nl/?url=p16-common-sign.tiktokcdn-us.com%2Ftos-no1a-p-0037-no%2FoklcCkAzEEvwwWAFHcnEQDJC0IBvk5AfgAfFDR~tplv-tiktokx-origin.image%3Fdr%3D9636%26x-expires%3D1791986400%26x-signature%3DZv7DPHzbTZzxhmPbP%252BQHPfrOweQ%253D%26t%3D4d5b0474%26ps%3D13740610%26shp%3D81f88b70%26shcp%3D43f4a2f9%26idc%3Duseast8&w=400&output=jpg'
    },
    {
      id: 'tt-c7',
      platform: 'tiktok',
      title: 'English Manors Box Unboxing',
      description: 'Unboxing the English Manors Theme Box from CoraCreaCrafts! A beautifully curated collection celebrating the charm of English country estates.',
      url: 'https://www.tiktok.com/@liu_journal/video/7556284728639311126',
      embedId: '7556284728639311126',
      category: 'unboxing',
      tags: ['english manors', 'unboxing', 'vintage', 'ambassador'],
      creator: 'Liu Journal',
      date: '2025-07',
      thumbnail: 'https://wsrv.nl/?url=p19-common-sign.tiktokcdn-us.com%2Ftos-no1a-p-0037-no%2FoIce1AeGogUUcAAGd6dxsMApfQfRXYhuNexCin~tplv-tiktokx-origin.image%3Fdr%3D9636%26x-expires%3D1791986400%26x-signature%3DCPUPfIgnTQy12ecYwuCTs2bcaZA%253D%26t%3D4d5b0474%26ps%3D13740610%26shp%3D81f88b70%26shcp%3D43f4a2f9%26idc%3Duseast8&w=400&output=jpg'
    },
    {
      id: 'tt-c8',
      platform: 'tiktok',
      title: 'Product Review - Owl Grimoire & More',
      description: 'Beautiful supplies from CoraCreaCrafts! Featuring the Owl Grimoire Foil Notebook, Secret Garden Washi Tape, Magic Jars Transparent Tape, and Witchcraft Foil Stamp Washi.',
      url: 'https://www.tiktok.com/@pocketartistry/video/7432696642895170849',
      embedId: '7432696642895170849',
      category: 'product-showcase',
      tags: ['owl grimoire', 'secret garden', 'washi tape', 'witchcraft', 'asmr'],
      creator: 'Pocket Artistry',
      date: '2024-11',
      thumbnail: 'https://wsrv.nl/?url=p19-common-sign.tiktokcdn-us.com%2Ftos-useast2a-p-0037-euttp%2FoIdC58iklIB1HxJA8gDofKiSzmIHIWYhnGlxPA~tplv-tiktokx-origin.image%3Fdr%3D9636%26x-expires%3D1791986400%26x-signature%3DPpogNWYvYVqB0jaIxgmSavAQmy4%253D%26t%3D4d5b0474%26ps%3D13740610%26shp%3D81f88b70%26shcp%3D43f4a2f9%26idc%3Duseast8&w=400&output=jpg'
    },
    {
      id: 'tt-11',
      platform: 'tiktok',
      title: 'Halloween Collection Sale - 20% Off',
      description: 'Our spooky Halloween collection is now 20% off! Don\'t miss these hauntingly beautiful crafting supplies.',
      url: 'https://www.tiktok.com/@coracreacrafts_shop/video/7431585393847323947',
      embedId: '7431585393847323947',
      category: 'product-showcase',
      tags: ['halloween', 'sale', '20% off', 'spooky collection'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://wsrv.nl/?url=p16-common-sign.tiktokcdn-us.com%2Ftos-useast5-p-0068-tx%2F427843269863448eb245e9cfb46055ac_1730300818~tplv-tiktokx-dmt-logom%3Atos-useast5-i-0068-tx%2FoQeDFCnTkEsApBuEIAAwEfcHSlIMnQASODURgS.image%3Fdr%3D9634%26x-expires%3D1791986400%26x-signature%3Di0MF9hXCyuE8ESo91T7wTPIGt4E%253D%26t%3D4d5b0474%26ps%3D13740610%26shp%3D81f88b70%26shcp%3D43f4a2f9%26idc%3Duseast8&w=400&output=jpg'
    },
    {
      id: 'tt-12',
      platform: 'tiktok',
      title: 'Victorian ASMR - Treasure Box Highlights',
      description: 'Victorian ASMR subscription box - Treasure Box Highlights. What\'s your favorite piece from this beautiful collection?',
      url: 'https://www.tiktok.com/@coracreacrafts_old/video/7272495089149119776',
      embedId: '7272495089149119776',
      category: 'product-showcase',
      tags: ['victorian', 'asmr', 'treasure box', 'highlights'],
      creator: 'CoraCreaCrafts',
      date: '2023-09',
      thumbnail: 'https://wsrv.nl/?url=p16-common-sign.tiktokcdn-us.com%2Ftos-useast2a-p-0037-euttp%2Fo0jdrzuEmqfohAaNCoOtUny8FPIDdAPBhIxACo~tplv-tiktokx-dmt-logom%3Atos-useast2a-i-0068-euttp%2Fo4yDEtACAzhAXoAAzF0BNf8OvhHOgI4kIody4A.image%3Fdr%3D9634%26x-expires%3D1791986400%26x-signature%3DDbDt3t7FAD64PD61wvpQ4BOFFgw%253D%26t%3D4d5b0474%26ps%3D13740610%26shp%3D81f88b70%26shcp%3D43f4a2f9%26idc%3Duseast8&w=400&output=jpg'
    },
    {
      id: 'tt-13',
      platform: 'tiktok',
      title: 'Dark Academia - October Vintage Craft Box',
      description: 'Dark Academia - Vintage Craft Box theme for October. P.s: do you recognize the music?',
      url: 'https://www.tiktok.com/@coracreacrafts_old/video/7287720822632942881',
      embedId: '7287720822632942881',
      category: 'box-reveal',
      tags: ['dark academia', 'october', 'vintage craft box', 'aesthetic'],
      creator: 'CoraCreaCrafts',
      date: '2023-10',
      thumbnail: 'https://wsrv.nl/?url=p19-common-sign.tiktokcdn-us.com%2Ftos-useast2a-p-0037-euttp%2Fbafe4991f4c34973888bac0588639c3a_1696804739~tplv-tiktokx-dmt-logom%3Atos-useast2a-i-0068-euttp%2FoglADDA8dIAgBRAQefEIA9QyZIX3nb3CPuSlyG.image%3Fdr%3D9634%26x-expires%3D1791986400%26x-signature%3DseeULJQhD3zN%252FrAmye7tw7%252BnJnY%253D%26t%3D4d5b0474%26ps%3D13740610%26shp%3D81f88b70%26shcp%3D43f4a2f9%26idc%3Duseast8&w=400&output=jpg'
    },
    {
      id: 'tt-c9',
      platform: 'tiktok',
      title: 'ASMR Scrapbook - Secret Garden Collection',
      description: 'Beautiful ASMR scrapbooking with CoraCreaCrafts Owl Grimoire Foil Notebook, Secret Garden Washi Tape, Secret Garden Ephemera Pack, and Insects Cards.',
      url: 'https://www.tiktok.com/@pocketartistry/video/7434904485618519329',
      embedId: '7434904485618519329',
      category: 'journal-spread',
      tags: ['asmr', 'scrapbook', 'secret garden', 'ephemera'],
      creator: 'Pocket Artistry',
      date: '2024-11',
      thumbnail: 'https://wsrv.nl/?url=p19-common-sign.tiktokcdn-us.com%2Ftos-useast2a-p-0037-euttp%2Fo8iFAUYicly7WmZBITM0BVPSywIBG4wuAmEXA~tplv-tiktokx-origin.image%3Fdr%3D9636%26x-expires%3D1791986400%26x-signature%3DR2zKQPb4xVoYC%252BkTyBTAHi92geI%253D%26t%3D4d5b0474%26ps%3D13740610%26shp%3D81f88b70%26shcp%3D43f4a2f9%26idc%3Duseast8&w=400&output=jpg'
    },
    {
      id: 'tt-c10',
      platform: 'tiktok',
      title: 'ASMR Scrapbook - Witchcraft Collection',
      description: 'ASMR scrapbooking featuring the Owl Grimoire Notebook, Witchcraft Foil Stamp Washi, Secret Garden Ephemera, and Ancient Cities Vellum.',
      url: 'https://www.tiktok.com/@pocketartistry/video/7439381357999951136',
      embedId: '7439381357999951136',
      category: 'journal-spread',
      tags: ['asmr', 'witchcraft', 'scrapbook', 'vellum'],
      creator: 'Pocket Artistry',
      date: '2024-11',
      thumbnail: 'https://wsrv.nl/?url=p19-common-sign.tiktokcdn-us.com%2Ftos-useast2a-p-0037-euttp%2FoEeGgRuuMLbHjWj3k5E9gkweAgLfNcAf9rG8cI~tplv-tiktokx-origin.image%3Fdr%3D9636%26x-expires%3D1791986400%26x-signature%3DTCR0QGY1n2AWsSWGymr4%252FOtr9QQ%253D%26t%3D4d5b0474%26ps%3D13740610%26shp%3D81f88b70%26shcp%3D43f4a2f9%26idc%3Duseast8&w=400&output=jpg'
    },
    {
      id: 'tt-c11',
      platform: 'tiktok',
      title: 'Advent Calendar - Last 10 Days Unwrapping',
      description: 'Unwrapping the last 10 days of the CoraCreaCrafts Advent Calendar! Each day brings a new crafting surprise.',
      url: 'https://www.tiktok.com/@liu_journal/video/7449800349004614934',
      embedId: '7449800349004614934',
      category: 'unboxing',
      tags: ['advent calendar', 'christmas', 'unwrapping', 'daily surprise'],
      creator: 'Liu Journal',
      date: '2024-12',
      thumbnail: 'https://wsrv.nl/?url=p16-common-sign.tiktokcdn-us.com%2Ftos-no1a-p-0037-no%2FoQGOvfPNAAACAj1yAQLQxBIGre9BzegILIFGMg~tplv-tiktokx-origin.image%3Fdr%3D9636%26x-expires%3D1791986400%26x-signature%3Daxq8bDXzSGWHABNQYdts%252FFFsVKg%253D%26t%3D4d5b0474%26ps%3D13740610%26shp%3D81f88b70%26shcp%3D43f4a2f9%26idc%3Duseast8&w=400&output=jpg'
    },
    {
      id: 'tt-c12',
      platform: 'tiktok',
      title: 'Medieval Box Spread - Knight Papers',
      description: 'November Medieval box spread with Hogwarts-inspired knight papers. These detailed illustrations are perfect for themed journal pages.',
      url: 'https://www.tiktok.com/@liu_journal/video/7443491908996844822',
      embedId: '7443491908996844822',
      category: 'journal-spread',
      tags: ['medieval', 'knights', 'hogwarts inspired', 'journal spread'],
      creator: 'Liu Journal',
      date: '2024-11',
      thumbnail: 'https://wsrv.nl/?url=p16-common-sign.tiktokcdn-us.com%2Ftos-no1a-p-0037-no%2FokmMkQgTfAESvhBrADRneGC7mEFkFEAm6JAI21~tplv-tiktokx-origin.image%3Fdr%3D9636%26x-expires%3D1791986400%26x-signature%3DClhGZDakrUCUUjttcKuvkbXtBp4%253D%26t%3D4d5b0474%26ps%3D13740610%26shp%3D81f88b70%26shcp%3D43f4a2f9%26idc%3Duseast8&w=400&output=jpg'
    },
    {
      id: 'tt-c13',
      platform: 'tiktok',
      title: 'CoraCreaCrafts Haul',
      description: 'My CoraCreaCrafts haul! Showing off all the gorgeous vintage-inspired crafting supplies I ordered.',
      url: 'https://www.tiktok.com/@the_ripley_journals/video/7311072643091008814',
      embedId: '7311072643091008814',
      category: 'unboxing',
      tags: ['haul', 'crafting supplies', 'vintage', 'shopping'],
      creator: 'The Ripley Journals',
      date: '2024-01',
      thumbnail: 'https://wsrv.nl/?url=p19-common-sign.tiktokcdn-us.com%2Ftos-useast5-p-0068-tx%2FowflhkRjEoDdWsGESSFU7IEeDOVUQgBtiGDSVA~tplv-tiktokx-origin.image%3Fdr%3D9636%26x-expires%3D1791986400%26x-signature%3DEb1G34RWxHGt3GimEFnR3exTY9w%253D%26t%3D4d5b0474%26ps%3D13740610%26shp%3D81f88b70%26shcp%3D43f4a2f9%26idc%3Duseast8&w=400&output=jpg'
    },
    {
      id: 'tt-c14',
      platform: 'tiktok',
      title: 'November Box - CCC On Stage Challenge',
      description: 'November Subscription Box entry for the #CCCOnStageChallenge! Creative community challenge using this month\'s supplies.',
      url: 'https://www.tiktok.com/@_junkjournal_/video/7301182184960871681',
      embedId: '7301182184960871681',
      category: 'journal-spread',
      tags: ['challenge', 'CCCChallenge', 'november', 'community'],
      creator: 'Junk Journal',
      date: '2023-11',
      thumbnail: 'https://wsrv.nl/?url=p16-common-sign.tiktokcdn-us.com%2Ftos-alisg-p-0037%2F2be06fd9e9da42d89de4168269f37ecf_1699938956~tplv-tiktokx-dmt-logom%3Atos-alisg-i-0068%2FoMihPA0VMAAEBBYACwAxZF4skogIZEBATiiyG.image%3Fdr%3D9634%26x-expires%3D1791986400%26x-signature%3DEWTNuY2iSYg7ejUYLgAKKSeCBNI%253D%26t%3D4d5b0474%26ps%3D13740610%26shp%3D81f88b70%26shcp%3D43f4a2f9%26idc%3Duseast8&w=400&output=jpg'
    },
    {
      id: 'tt-c15',
      platform: 'tiktok',
      title: 'Curiosities Box Preview',
      description: 'A look at the beautifully curated Curiosities Box from CoraCreaCrafts! Filled with unique vintage-inspired ephemera and crafting treasures.',
      url: 'https://www.tiktok.com/@coracreacrafts_old/video/7194171649363774726',
      embedId: '7194171649363774726',
      category: 'box-reveal',
      tags: ['curiosities box', 'preview', 'vintage', 'ephemera'],
      creator: 'CoraCreaCrafts',
      date: '2023-02',
      thumbnail: 'https://wsrv.nl/?url=p19-common-sign.tiktokcdn-us.com%2Ftos-maliva-p-0068%2Fdfaad293017a4712a0b277a218cbfb07_1675023624~tplv-tiktokx-origin.image%3Fdr%3D9636%26x-expires%3D1791986400%26x-signature%3DcgXOFGBTFVURMCS9ZHTndXj02kY%253D%26t%3D4d5b0474%26ps%3D13740610%26shp%3D81f88b70%26shcp%3D43f4a2f9%26idc%3Duseast8&w=400&output=jpg'
    },
    {
      id: 'tt-c16',
      platform: 'tiktok',
      title: 'Four Elements September Box Unboxing',
      description: 'Unboxing the Four Elements themed September box from CoraCreaCrafts! Earth, Water, Fire, Air - each element represented in beautiful crafting supplies.',
      url: 'https://www.tiktok.com/@yourenchantedstudio/video/7418015789279890734',
      embedId: '7418015789279890734',
      category: 'unboxing',
      tags: ['four elements', 'september', 'unboxing', 'earth water fire air'],
      creator: 'Your Enchanted Studio',
      date: '2024-09',
      thumbnail: 'https://wsrv.nl/?url=p19-common-sign.tiktokcdn-us.com%2Ftos-useast5-p-0068-tx%2Ff214d5902b72451a8bed41a862c7372a_1727141398~tplv-tiktokx-origin.image%3Fdr%3D9636%26x-expires%3D1791986400%26x-signature%3D%252F%252FDbqKkRFK1mvw8cgKgheybSWbI%253D%26t%3D4d5b0474%26ps%3D13740610%26shp%3D81f88b70%26shcp%3D43f4a2f9%26idc%3Duseast8&w=400&output=jpg'
    },
    {
      id: 'tt-c17',
      platform: 'tiktok',
      title: 'Mystery Vintage Box Unboxing',
      description: 'Opening the Mystery themed Vintage Box from CoraCreaCrafts! Love the detective and Agatha Christie vibes in this beautifully curated collection.',
      url: 'https://www.tiktok.com/@suchijournalive/video/7356315237504666923',
      embedId: '7356315237504666923',
      category: 'unboxing',
      tags: ['mystery', 'vintage box', 'unboxing', 'detective'],
      creator: 'Suchi Journal Live',
      date: '2024-04',
      thumbnail: 'https://wsrv.nl/?url=p16-common-sign.tiktokcdn-us.com%2Ftos-useast5-p-0068-tx%2F63be982f5feb417da0f6ca81341ee62f_1712775639~tplv-tiktokx-dmt-logom%3Atos-useast5-i-0068-tx%2FoYKWI3KFzEJACABy4BCi1ADiIkBAZNA7pwfNdE.image%3Fdr%3D9634%26x-expires%3D1791986400%26x-signature%3DYq%252F7cMUcdERvGB0eaQOsajoC09U%253D%26t%3D4d5b0474%26ps%3D13740610%26shp%3D81f88b70%26shcp%3D43f4a2f9%26idc%3Duseast8&w=400&output=jpg'
    },
    {
      id: 'tt-c18',
      platform: 'tiktok',
      title: 'Vintage Stores Box Unboxing',
      description: 'Unboxing the Vintage Stores themed box from CoraCreaCrafts! This collection celebrates the charm of old shops and antique finds.',
      url: 'https://www.tiktok.com/@liu_journal/video/7313990793327381793',
      embedId: '7313990793327381793',
      category: 'unboxing',
      tags: ['vintage stores', 'antique', 'unboxing', 'ambassador'],
      creator: 'Liu Journal',
      date: '2023-12',
      thumbnail: 'https://wsrv.nl/?url=p19-common-sign.tiktokcdn-us.com%2Ftos-useast2a-p-0037-euttp%2F080aa032e2104141b283eb3c3d009c77_1702921208~tplv-tiktokx-origin.image%3Fdr%3D9636%26x-expires%3D1791986400%26x-signature%3DmYwbFiwCoiP1vYfUpjVPaw%252B9hZA%253D%26t%3D4d5b0474%26ps%3D13740610%26shp%3D81f88b70%26shcp%3D43f4a2f9%26idc%3Duseast8&w=400&output=jpg'
    },
    {
      id: 'tt-c19',
      platform: 'tiktok',
      title: 'Folklore & Witchcraft April Box',
      description: 'The April Vintage Craft Box theme is Folklore & Witchcraft! A magical collection filled with mystical ephemera and enchanting supplies.',
      url: 'https://www.tiktok.com/@bookworm_journal/video/7224123379933121798',
      embedId: '7224123379933121798',
      category: 'unboxing',
      tags: ['folklore', 'witchcraft', 'april', 'mystical'],
      creator: 'Bookworm Journal',
      date: '2023-04',
      thumbnail: 'https://wsrv.nl/?url=p16-common-sign.tiktokcdn-us.com%2Ftos-maliva-p-0068%2F4f391a2a6a7c4a7793af30b5b2a6b503_1681997302~tplv-tiktokx-dmt-logom%3Atos-useast2a-v-0068%2F952c436f1c06424ab57c46cb61242e3b.image%3Fdr%3D9634%26x-expires%3D1791986400%26x-signature%3DRroOKKf0X6jB8Wh83Bq%252FK3L1toc%253D%26t%3D4d5b0474%26ps%3D13740610%26shp%3D81f88b70%26shcp%3D43f4a2f9%26idc%3Duseast8&w=400&output=jpg'
    },
    {
      id: 'tt-c20',
      platform: 'tiktok',
      title: 'CoraCreaCrafts Supplies Showcase',
      description: 'Showcasing beautiful crafting supplies from CoraCreaCrafts! Washi tapes, ephemera packs, stickers, and more for your journaling projects.',
      url: 'https://www.tiktok.com/@lanna.journaling/video/7069080890781044010',
      embedId: '7069080890781044010',
      category: 'product-showcase',
      tags: ['supplies', 'washi tape', 'ephemera', 'showcase'],
      creator: 'Lanna Journaling',
      date: '2022-12',
      thumbnail: 'https://wsrv.nl/?url=p19-common-sign.tiktokcdn-us.com%2Ftos-useast5-p-0068-tx%2F2738a307cee648e0962b06fddb78d969~tplv-tiktokx-origin.image%3Fdr%3D9636%26x-expires%3D1791986400%26x-signature%3DcbDEbcypqmPHqlf8FNgRQ1Z%252BeoI%253D%26t%3D4d5b0474%26ps%3D13740610%26shp%3D81f88b70%26shcp%3D43f4a2f9%26idc%3Duseast8&w=400&output=jpg'
    },
    {
      id: 'tt-c21',
      platform: 'tiktok',
      title: 'Unboxing Royal France Box',
      description: 'Liu Journal unboxes the stunning Royal France themed Vintage Craft Box from CoraCreaCrafts! Elegant ephemera, royal-inspired papers, and gorgeous French vintage supplies.',
      url: 'https://www.tiktok.com/@liu_journal/video/7546635906879343894',
      embedId: '7546635906879343894',
      category: 'unboxing',
      tags: ['royal france', 'unboxing', 'vintage craft box', 'community'],
      creator: 'Liu Journal',
      date: '2025-01',
      thumbnail: 'https://wsrv.nl/?url=p19-common-sign.tiktokcdn-us.com%2Ftos-no1a-p-0037-no%2Fo0YUiFaCBjAZJk74LWZSEGiIBmPPPlyIVsGYB~tplv-tiktokx-origin.image%3Fdr%3D9636%26x-expires%3D1791986400%26x-signature%3Dn23P%252BmwhyANwUH%252B9%252FuJN0QI7ww8%253D%26t%3D4d5b0474%26ps%3D13740610%26shp%3D81f88b70%26shcp%3D43f4a2f9%26idc%3Duseast8&w=400&output=jpg'
    },

    // === Instagram Reels (1265 entries) ===
    {
      id: 'ig-api-1',
      platform: 'instagram',
      title: 'Been brewing this one for a while… 🧹',
      description: 'Been brewing this one for a while… 🧹 Closing out Spooky Month with a little witchy-inspired journaling, featuring my favorite bat transparent tape (again, of course!) 🦇  #artjournal #scrapbookjourna',
      url: 'https://www.instagram.com/reel/DfIb4YqBN1u/',
      embedId: 'DfIb4YqBN1u',
      category: 'journal-spread',
      tags: ['artjournal', 'scrapbookjournal', 'journalspread', 'witches', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DfIb4YqBN1u/media/?size=m'
    },
    {
      id: 'ig-api-2',
      platform: 'instagram',
      title: 'There\'s always room for one more roll of tape, right? 👀',
      description: 'There\'s always room for one more roll of tape, right? 👀 Which one\'s your favorite? Let us know in the comments! 🖤  #junkjournal #junkjournalcommunity #craftaddict',
      url: 'https://www.instagram.com/reel/DfF0MhhBX47/',
      embedId: 'DfF0MhhBX47',
      category: 'product-showcase',
      tags: ['junkjournal', 'junkjournalcommunity', 'craftaddict', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DfF0MhhBX47/media/?size=m'
    },
    {
      id: 'ig-api-3',
      platform: 'instagram',
      title: 'A Court of Splintered Harmony is finally here. What are your thoughts so far? 😳',
      description: 'A Court of Splintered Harmony is finally here. What are your thoughts so far? 😳  #creativejournaling #stationeryaddict #acotarfanart #bookishcommunity',
      url: 'https://www.instagram.com/reel/DfCp5yPItP5/',
      embedId: 'DfCp5yPItP5',
      category: 'product-showcase',
      tags: ['creativejournaling', 'stationeryaddict', 'acotarfanart', 'bookishcommunity', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DfCp5yPItP5/media/?size=m'
    },
    {
      id: 'ig-api-4',
      platform: 'instagram',
      title: 'The spread you\'ve all been waiting to see! I have a feeling this might be one...',
      description: 'The spread you\'ve all been waiting to see! I have a feeling this might be one to remember 🕯️🥀  Our Candlelit Remembrance VCBs are shipping this week and will be with you soon! 🤎  #creativejournalin',
      url: 'https://www.instagram.com/reel/De7hGGjB4QJ/',
      embedId: 'De7hGGjB4QJ',
      category: 'journal-spread',
      tags: ['creativejournaling', 'vintagecrafts', 'nostalgia', 'journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2026-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/De7hGGjB4QJ/media/?size=m'
    },
    {
      id: 'ig-api-5',
      platform: 'instagram',
      title: 'WINNER 🏆 @frency.stf  congratulations 🎉',
      description: 'WINNER 🏆 @frency.stf  congratulations 🎉  We just did something very spontaneous.. Giveaway time 😍 We saw each other at @littlecraftfest and wanted to do something special together.  👻 Follow @cora',
      url: 'https://www.instagram.com/reel/De5Ulcood2C/',
      embedId: 'De5Ulcood2C',
      category: 'product-showcase',
      tags: ['journalingsupplies', 'journalinggiveaway', 'stationeryaddict', 'stationerylover', 'stationeryfest', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/De5Ulcood2C/media/?size=m'
    },
    {
      id: 'ig-api-6',
      platform: 'instagram',
      title: 'There\'s just something about this page that feels like a peek into another wo...',
      description: 'There\'s just something about this page that feels like a peek into another world 🖤 Would you try a window cutout in your journal? 👀  #spookyseason #craftyjournal #whimsical #junkjournal #creativejou',
      url: 'https://www.instagram.com/reel/De2mX7xBtr8/',
      embedId: 'De2mX7xBtr8',
      category: 'product-showcase',
      tags: ['spookyseason', 'craftyjournal', 'whimsical', 'junkjournal', 'creativejournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/De2mX7xBtr8/media/?size=m'
    },
    {
      id: 'ig-api-7',
      platform: 'instagram',
      title: 'The time for our Autumn Magic CB is almost at an end, so I wanted to share th...',
      description: 'The time for our Autumn Magic CB is almost at an end, so I wanted to share the coziness with you all one more time 🍂  Stay tuned for more whimsical themes to come!  #autumnal #cozyjournaling #artjour',
      url: 'https://www.instagram.com/reel/DexSb8UIRqm/',
      embedId: 'DexSb8UIRqm',
      category: 'product-showcase',
      tags: ['autumnal', 'cozyjournaling', 'artjournal', 'journaling', 'autumn'],
      creator: 'CoraCreaCrafts',
      date: '2026-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DexSb8UIRqm/media/?size=m'
    },
    {
      id: 'ig-api-8',
      platform: 'instagram',
      title: 'Halloween is cool and so is Spooky @littlecraftfest - we\'re bringing some cut...',
      description: 'Halloween is cool and so is Spooky @littlecraftfest - we\'re bringing some cute purple spooky specials this time 💜 Can\'t wait to see so many of you there!!  #journalingsupplies #spookylittlecraftfest ',
      url: 'https://www.instagram.com/reel/DeuHM7_Rv53/',
      embedId: 'DeuHM7_Rv53',
      category: 'product-showcase',
      tags: ['journalingsupplies', 'spookylittlecraftfest', 'spookystationery', 'halloweenstationery', 'journaling', 'halloween'],
      creator: 'CoraCreaCrafts',
      date: '2026-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DeuHM7_Rv53/media/?size=m'
    },
    {
      id: 'ig-api-9',
      platform: 'instagram',
      title: 'Nostalgic Victorian Vibes 💜 what do you think of this theme? \'Candlelit Reme...',
      description: 'Nostalgic Victorian Vibes 💜 what do you think of this theme? \'Candlelit Rememberance\' was voted for by you and it\'s finally coming to life. Shipping next week!   #creativejournaling #nostalgia #vinta',
      url: 'https://www.instagram.com/reel/DentZStR48T/',
      embedId: 'DentZStR48T',
      category: 'product-showcase',
      tags: ['creativejournaling', 'nostalgia', 'vintageephemera', 'journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2026-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DentZStR48T/media/?size=m'
    },
    {
      id: 'ig-api-10',
      platform: 'instagram',
      title: 'Journaling my birthday dinner from last weekend 🎈 Such happy memories just n...',
      description: 'Journaling my birthday dinner from last weekend 🎈 Such happy memories just needed a spot in my journal 🫶🏽 thank you for all your wishes and to everyone who celebrated with me ❤️   #junkjournal #bir',
      url: 'https://www.instagram.com/reel/DedHWdNM1MN/',
      embedId: 'DedHWdNM1MN',
      category: 'product-showcase',
      tags: ['junkjournal', 'birthdaygirl', 'stationeryaddict', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DedHWdNM1MN/media/?size=m'
    },
    {
      id: 'ig-api-11',
      platform: 'instagram',
      title: 'Over the moon unpacking the @thequirkycupcollective products for our shop 😍 ...',
      description: 'Over the moon unpacking the @thequirkycupcollective products for our shop 😍 now available online and in store @creativecorner_nashville   #creativejournaling #bookishcommunity #junkjournal #stationer',
      url: 'https://www.instagram.com/reel/DeKbkVqoo1_/',
      embedId: 'DeKbkVqoo1_',
      category: 'behind-the-scenes',
      tags: ['creativejournaling', 'bookishcommunity', 'junkjournal', 'stationeryaddict', 'nashvilletn', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DeKbkVqoo1_/media/?size=m'
    },
    {
      id: 'ig-api-12',
      platform: 'instagram',
      title: 'HOUSTON 🖤 We\'re coming to the first ever Spooky Little Craft Fest in October',
      description: 'HOUSTON 🖤 We\'re coming to the first ever Spooky Little Craft Fest in October!  I\'ll have a table full of our moodiest gothic and Halloween bits, and I\'m running three workshops across the weekend. Ti',
      url: 'https://www.instagram.com/reel/DdbtUdPRNIM/',
      embedId: 'DdbtUdPRNIM',
      category: 'product-showcase',
      tags: ['spookylittlecraftfest', 'houston', 'houstonevents', 'craftfair', 'junkjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DdbtUdPRNIM/media/?size=m'
    },
    {
      id: 'ig-api-13',
      platform: 'instagram',
      title: 'What color should I do next? Green ink and a new pen (restocked!) and suddenl...',
      description: 'What color should I do next? Green ink and a new pen (restocked!) and suddenly I can’t stop writing 💚 Fall does this to me every year 🍂  #junkjournal #greenink #fountainpen #journalspread #handwriti',
      url: 'https://www.instagram.com/reel/DdWtkhLR4cA/',
      embedId: 'DdWtkhLR4cA',
      category: 'journal-spread',
      tags: ['junkjournal', 'greenink', 'fountainpen', 'journalspread', 'handwriting', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DdWtkhLR4cA/media/?size=m'
    },
    {
      id: 'ig-api-14',
      platform: 'instagram',
      title: 'If Salem had its own stationery collection, we think it would look a little s...',
      description: 'If Salem had its own stationery collection, we think it would look a little something like this 🕯️🦇✨ P.s: last tape shown is our upgrade, sold separately from the box!  #spookystationery #witchystat',
      url: 'https://www.instagram.com/reel/DdC0peGov01/',
      embedId: 'DdC0peGov01',
      category: 'product-showcase',
      tags: ['spookystationery', 'witchystationery', 'halloweenstationery', 'junkjournalsupplies', 'stationeryaddict', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DdC0peGov01/media/?size=m'
    },
    {
      id: 'ig-api-15',
      platform: 'instagram',
      title: 'September is here -> time for darker pages & Raven @esterbrook_official pen 🖤',
      description: 'September is here -> time for darker pages & Raven @esterbrook_official pen 🖤   #junkjournal #darkacademia #junkjournaling #vintagejournal #junkjournalcommunity',
      url: 'https://www.instagram.com/reel/DczcmCHIBZC/',
      embedId: 'DczcmCHIBZC',
      category: 'product-showcase',
      tags: ['junkjournal', 'darkacademia', 'junkjournaling', 'vintagejournal', 'junkjournalcommunity', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DczcmCHIBZC/media/?size=m'
    },
    {
      id: 'ig-api-16',
      platform: 'instagram',
      title: 'Does anyone else\'s desk get this bad between projects, or is it just me? ✨',
      description: 'Does anyone else\'s desk get this bad between projects, or is it just me? ✨  #craftroom #deskreset #junkjournal #craftdesk #cleanwithme',
      url: 'https://www.instagram.com/reel/DcoF6u8BIJY/',
      embedId: 'DcoF6u8BIJY',
      category: 'product-showcase',
      tags: ['craftroom', 'deskreset', 'junkjournal', 'craftdesk', 'cleanwithme', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DcoF6u8BIJY/media/?size=m'
    },
    {
      id: 'ig-api-17',
      platform: 'instagram',
      title: 'Everything I cram in my bag to journal on the go 🥰 What\'s the most unhinged ...',
      description: 'Everything I cram in my bag to journal on the go 🥰 What\'s the most unhinged thing in your craft bag?  #whatsinmybag #junkjournal #journalbag #stationeryaddict #junkjournaling',
      url: 'https://www.instagram.com/reel/Dce-pgwsk5s/',
      embedId: 'Dce-pgwsk5s',
      category: 'product-showcase',
      tags: ['whatsinmybag', 'junkjournal', 'journalbag', 'stationeryaddict', 'junkjournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Dce-pgwsk5s/media/?size=m'
    },
    {
      id: 'ig-api-18',
      platform: 'instagram',
      title: 'POV: you’ve just stepped into 221B Baker Street 🔍🕯️ You loved our last myst...',
      description: 'POV: you’ve just stepped into 221B Baker Street 🔍🕯️ You loved our last mystery box, so we had to bring it back, this time with a Sherlock twist. Who’s ready to investigate?  #murdermystery #sherlock',
      url: 'https://www.instagram.com/reel/DccG-_Woibg/',
      embedId: 'DccG-_Woibg',
      category: 'box-reveal',
      tags: ['murdermystery', 'sherlock', 'junkjournal', 'papercrafting', 'junkjournalcommunity', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DccG-_Woibg/media/?size=m'
    },
    {
      id: 'ig-api-19',
      platform: 'instagram',
      title: 'Find the Oasis in the Desert 🌙✨',
      description: 'Find the Oasis in the Desert 🌙✨  The pages, the sounds, the little details… turn the volume up for this one 🤎 #asmr #arabiannights #junkjournal #junkjournalcommunity #papercrafting',
      url: 'https://www.instagram.com/reel/DcZ9oVZsTYP/',
      embedId: 'DcZ9oVZsTYP',
      category: 'product-showcase',
      tags: ['asmr', 'arabiannights', 'junkjournal', 'junkjournalcommunity', 'papercrafting', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DcZ9oVZsTYP/media/?size=m'
    },
    {
      id: 'ig-api-20',
      platform: 'instagram',
      title: 'My August gratitude tracker with new favorite grid tapes! ✨',
      description: 'My August gratitude tracker with new favorite grid tapes! ✨ What\'s one thing you\'re grateful for this month?  #gratitudejournal #junkjournal #junkjournalcommunity #papercrafting #vintagecraftbox',
      url: 'https://www.instagram.com/reel/DcR2waRo1_E/',
      embedId: 'DcR2waRo1_E',
      category: 'box-reveal',
      tags: ['gratitudejournal', 'junkjournal', 'junkjournalcommunity', 'papercrafting', 'vintagecraftbox', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DcR2waRo1_E/media/?size=m'
    },
    {
      id: 'ig-api-21',
      platform: 'instagram',
      title: 'Lucky Star ⭐ that fits in your pocket! We just stocked some @esterbrook_offic...',
      description: 'Lucky Star ⭐ that fits in your pocket! We just stocked some @esterbrook_official niblets in Seaglass and Botanical Gardens! They\'ve been sold out for months and we only have a few 🤫🖋️   Come grab yo',
      url: 'https://www.instagram.com/reel/DcO7wpUhbvx/',
      embedId: 'DcO7wpUhbvx',
      category: 'product-showcase',
      tags: ['creativejournaling', 'journalingsupplies', 'stationeryaddict', 'penaddict', 'stationerystore', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DcO7wpUhbvx/media/?size=m'
    },
    {
      id: 'ig-api-22',
      platform: 'instagram',
      title: 'Lost the whole evening to my Arabian Nights box 🌙',
      description: 'Lost the whole evening to my Arabian Nights box 🌙  #vintagecraftbox #arabiannights #junkjournal #junkjournalspread #papercraft craftertok junkjournaling craftbox',
      url: 'https://www.instagram.com/reel/DcL-ubwRXT9/',
      embedId: 'DcL-ubwRXT9',
      category: 'journal-spread',
      tags: ['vintagecraftbox', 'arabiannights', 'junkjournal', 'junkjournalspread', 'papercraft', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DcL-ubwRXT9/media/?size=m'
    },
    {
      id: 'ig-api-23',
      platform: 'instagram',
      title: 'Inspired by the tales, patterns, and magic of the East, we wanted this collec...',
      description: 'Inspired by the tales, patterns, and magic of the East, we wanted this collection to feel like it had been pulled straight from the pages of an Arabian night. Here’s the first look at the Arabian Nigh',
      url: 'https://www.instagram.com/reel/Db-77hyR7bp/',
      embedId: 'Db-77hyR7bp',
      category: 'box-reveal',
      tags: ['vintagecraftbox', 'arabiannights', 'junkjournal', 'papercrafting', 'junkjournalcommunity', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Db-77hyR7bp/media/?size=m'
    },
    {
      id: 'ig-api-24',
      platform: 'instagram',
      title: 'My journal = just what I needed 😁 anyone feeling that way too',
      description: 'My journal = just what I needed 😁 anyone feeling that way too?  #journalingcommunity #creativejournaling #journalinspiration #junkjournal #stationerycommunity',
      url: 'https://www.instagram.com/reel/Db60OLBo4DE/',
      embedId: 'Db60OLBo4DE',
      category: 'product-showcase',
      tags: ['journalingcommunity', 'creativejournaling', 'journalinspiration', 'junkjournal', 'stationerycommunity', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Db60OLBo4DE/media/?size=m'
    },
    {
      id: 'ig-api-25',
      platform: 'instagram',
      title: 'Which one are you picking first? 👀',
      description: 'Which one are you picking first? 👀 #stickertok #stickerlover #stickersheet #stickercollection #StationeryLover',
      url: 'https://www.instagram.com/reel/DbyyKfDIUxj/',
      embedId: 'DbyyKfDIUxj',
      category: 'product-showcase',
      tags: ['stickertok', 'stickerlover', 'stickersheet', 'stickercollection', 'stationerylover', 'stickers'],
      creator: 'CoraCreaCrafts',
      date: '2026-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DbyyKfDIUxj/media/?size=m'
    },
    {
      id: 'ig-api-26',
      platform: 'instagram',
      title: 'I\'m SO excited to share this with you all',
      description: 'I\'m SO excited to share this with you all! We are now the exclusive US distributor for Fox and Fables 🤍 Her brand new collection is witchy, folk inspired PET tapes and the designs are so good I genui',
      url: 'https://www.instagram.com/reel/DbvwOBNRECE/',
      embedId: 'DbvwOBNRECE',
      category: 'product-showcase',
      tags: ['foxandfables', 'pettape', 'crafttok', 'junkjournaltok', 'journaltok', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DbvwOBNRECE/media/?size=m'
    },
    {
      id: 'ig-api-27',
      platform: 'instagram',
      title: 'A little peek at some Moon Child favorites. 🌙 Simple details, beautiful desi...',
      description: 'A little peek at some Moon Child favorites. 🌙 Simple details, beautiful designs, and plenty of inspiration in every piece.  #MoonChild #StationeryLover #Journaling #PaperGoods',
      url: 'https://www.instagram.com/reel/DbqNU8bRzRl/',
      embedId: 'DbqNU8bRzRl',
      category: 'product-showcase',
      tags: ['moonchild', 'stationerylover', 'journaling', 'papergoods'],
      creator: 'CoraCreaCrafts',
      date: '2026-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DbqNU8bRzRl/media/?size=m'
    },
    {
      id: 'ig-api-28',
      platform: 'instagram',
      title: 'Today’s mission: get these sticker sheets into our subscribers’ mailboxes as ...',
      description: 'Today’s mission: get these sticker sheets into our subscribers’ mailboxes as fast as possible 💌🌸   #StickerLoversClub #happymail #stickersheets #journalingcommunity',
      url: 'https://www.instagram.com/reel/DbhSFJRsm2n/',
      embedId: 'DbhSFJRsm2n',
      category: 'product-showcase',
      tags: ['stickerloversclub', 'happymail', 'stickersheets', 'journalingcommunity', 'journaling', 'stickers'],
      creator: 'CoraCreaCrafts',
      date: '2026-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DbhSFJRsm2n/media/?size=m'
    },
    {
      id: 'ig-api-29',
      platform: 'instagram',
      title: 'Okay, I’m officially obsessed with these new arrivals @esterbrook_official 🌸...',
      description: 'Okay, I’m officially obsessed with these new arrivals @esterbrook_official 🌸 The Sakura St. collection just landed on the website and in our store @creativecorner_nashville and I can’t stop looking a',
      url: 'https://www.instagram.com/reel/Dbb_9oYReJZ/',
      embedId: 'Dbb_9oYReJZ',
      category: 'product-showcase',
      tags: ['stationerylover', 'fountainpen', 'desksetup', 'newarrivals'],
      creator: 'CoraCreaCrafts',
      date: '2026-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Dbb_9oYReJZ/media/?size=m'
    },
    {
      id: 'ig-api-30',
      platform: 'instagram',
      title: 'A little stationery drop to brighten the week. ✨ These favorites inspired by ...',
      description: 'A little stationery drop to brighten the week. ✨ These favorites inspired by an enchanted New York will be available at @stationery.fest and I can\'t wait to share them with you. #StationeryFest #Stati',
      url: 'https://www.instagram.com/reel/DbWQ4kho8aM/',
      embedId: 'DbWQ4kho8aM',
      category: 'product-showcase',
      tags: ['stationeryfest', 'stationerylover', 'journaling', 'newyorkstationery'],
      creator: 'CoraCreaCrafts',
      date: '2026-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DbWQ4kho8aM/media/?size=m'
    },
    {
      id: 'ig-api-31',
      platform: 'instagram',
      title: 'Made this gratitude tracker with pieces from the Ancient Civilizations box. T...',
      description: 'Made this gratitude tracker with pieces from the Ancient Civilizations box. This little grid for every day of the month that makes such a big difference in my life!  #junkjournal #gratitudejournal #ha',
      url: 'https://www.instagram.com/reel/DbTFl08xypf/',
      embedId: 'DbTFl08xypf',
      category: 'product-showcase',
      tags: ['junkjournal', 'gratitudejournal', 'habittracker', 'ancientcivilizations', 'journalingideas', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DbTFl08xypf/media/?size=m'
    },
    {
      id: 'ig-api-32',
      platform: 'instagram',
      title: 'These new arrivals are full of charm. Here\'s a closer look at every little de...',
      description: 'These new arrivals are full of charm. Here\'s a closer look at every little detail. 💙  #StationeryLover #PaperCraft #NewArrivals  ',
      url: 'https://www.instagram.com/reel/DbPCRl3I1Rw/',
      embedId: 'DbPCRl3I1Rw',
      category: 'product-showcase',
      tags: ['stationerylover', 'papercraft', 'newarrivals'],
      creator: 'CoraCreaCrafts',
      date: '2026-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DbPCRl3I1Rw/media/?size=m'
    },
    {
      id: 'ig-api-33',
      platform: 'instagram',
      title: 'New goodies just landed and already making their way out. This one went to @a...',
      description: 'New goodies just landed and already making their way out. This one went to @aida_gatch. Come see what\'s in. #craftsupplies #newarrivals #packwithme',
      url: 'https://www.instagram.com/reel/DbLtKt2xhZM/',
      embedId: 'DbLtKt2xhZM',
      category: 'behind-the-scenes',
      tags: ['craftsupplies', 'newarrivals', 'packwithme'],
      creator: 'CoraCreaCrafts',
      date: '2026-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DbLtKt2xhZM/media/?size=m'
    },
    {
      id: 'ig-api-34',
      platform: 'instagram',
      title: 'There\'s something special about summer, a feeling I can\'t quite describe. Thi...',
      description: 'There\'s something special about summer, a feeling I can\'t quite describe. This is what I wanted this box to feel like.   #Journaling #StationeryLover #CreativeInspiration #summerjunkjournal #asmrjourn',
      url: 'https://www.instagram.com/reel/DbGdKIAR0ov/',
      embedId: 'DbGdKIAR0ov',
      category: 'product-showcase',
      tags: ['journaling', 'stationerylover', 'creativeinspiration', 'summerjunkjournal', 'asmrjournaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DbGdKIAR0ov/media/?size=m'
    },
    {
      id: 'ig-api-35',
      platform: 'instagram',
      title: 'Which side of habit-tracking are you, the pretty spread you never fill in, or...',
      description: 'Which side of habit-tracking are you, the pretty spread you never fill in, or actually ticking every box? 💀 I used our Mesoamerican pieces for this one and I love how it turned out! #junkjournal #hab',
      url: 'https://www.instagram.com/reel/DbB--w6ohdj/',
      embedId: 'DbB--w6ohdj',
      category: 'journal-spread',
      tags: ['junkjournal', 'habittracker', 'junkjournaling', 'journalspread', 'vintagejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DbB--w6ohdj/media/?size=m'
    },
    {
      id: 'ig-api-36',
      platform: 'instagram',
      title: 'Today’s pages are inspired by slow mornings, fields, and a little farm life 🌾✨',
      description: 'Today’s pages are inspired by slow mornings, fields, and a little farm life 🌾✨ It was so lovely to do something a little different with this VCB Box. I loved romanticising the little moments of count',
      url: 'https://www.instagram.com/reel/Da5qDVyx309/',
      embedId: 'Da5qDVyx309',
      category: 'box-reveal',
      tags: ['vintagecraftbox', 'farmtheme', 'farmcore', 'journalingcommunity', 'creativejournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Da5qDVyx309/media/?size=m'
    },
    {
      id: 'ig-api-37',
      platform: 'instagram',
      title: 'The best part about the Sticker Lover Club? There\'s always another project wa...',
      description: 'The best part about the Sticker Lover Club? There\'s always another project waiting to be made! Today\'s choice: ATCs ✂️💌 #stickerloverclub #junkjournal #artisttradingcards #papercrafts #stationeryaddi',
      url: 'https://www.instagram.com/reel/DayI3XhoLJo/',
      embedId: 'DayI3XhoLJo',
      category: 'product-showcase',
      tags: ['stickerloverclub', 'junkjournal', 'artisttradingcards', 'papercrafts', 'stationeryaddict', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DayI3XhoLJo/media/?size=m'
    },
    {
      id: 'ig-api-38',
      platform: 'instagram',
      title: 'A little blue inspiration for your Friday ✨💙',
      description: 'A little blue inspiration for your Friday ✨💙  This spread features some exciting new additions to our online store, a Nouveau Bleu Esterbrook pen paired with the beautiful Crystal Blue Ferris Wheel P',
      url: 'https://www.instagram.com/reel/DaoGghzoKVa/',
      embedId: 'DaoGghzoKVa',
      category: 'journal-spread',
      tags: ['fountainpens', 'stationerycommunity', 'journalingideas', 'ferriswheelpress', 'esterbrookpen', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DaoGghzoKVa/media/?size=m'
    },
    {
      id: 'ig-api-39',
      platform: 'instagram',
      title: 'We turned garden, farm & cottage life into a craft box… for quiet moments, sl...',
      description: 'We turned garden, farm & cottage life into a craft box… for quiet moments, slower days, and a bit of creativity when you feel like it 🤍  Hope you love our first box in this theme as much as we do! 🌿',
      url: 'https://www.instagram.com/reel/DalMCaIRKcX/',
      embedId: 'DalMCaIRKcX',
      category: 'box-reveal',
      tags: ['gardenlife', 'farmlife', 'cottagecore', 'slowliving', 'craftcommunity'],
      creator: 'CoraCreaCrafts',
      date: '2026-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DalMCaIRKcX/media/?size=m'
    },
    {
      id: 'ig-api-40',
      platform: 'instagram',
      title: 'Making this spread made me wish I could throw on a flapper dress and disappea...',
      description: 'Making this spread made me wish I could throw on a flapper dress and disappear into a 1920s gala for the night! 🥂 Art Deco is just so my thing. This collection sold out the first time round, but we j',
      url: 'https://www.instagram.com/reel/Daf2-XbhJZ6/',
      embedId: 'Daf2-XbhJZ6',
      category: 'journal-spread',
      tags: ['artdeco', 'junkjournal', 'vintageaesthetic', 'gatsby', 'junkjournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Daf2-XbhJZ6/media/?size=m'
    },
    {
      id: 'ig-api-41',
      platform: 'instagram',
      title: 'Happy 4th of July! All these goodies are. $3.76, $7.76 or $17.76 until tomorr...',
      description: 'Happy 4th of July! All these goodies are. $3.76, $7.76 or $17.76 until tomorrow only ❤️',
      url: 'https://www.instagram.com/reel/DaZG2kjsOZz/',
      embedId: 'DaZG2kjsOZz',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2026-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DaZG2kjsOZz/media/?size=m'
    },
    {
      id: 'ig-api-42',
      platform: 'instagram',
      title: 'Proof that functional pages don\'t have to be boring...🍋',
      description: 'Proof that functional pages don\'t have to be boring...🍋 This gratitude tracker is inspired by long summer lunches, lemon trees, and the Italian coast 🇮🇹  #italiansummer #gratitudetracker #junkjourn',
      url: 'https://www.instagram.com/reel/DaTgKwvIbex/',
      embedId: 'DaTgKwvIbex',
      category: 'product-showcase',
      tags: ['italiansummer', 'gratitudetracker', 'junkjournal', 'journalinspiration', 'papercraft', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DaTgKwvIbex/media/?size=m'
    },
    {
      id: 'ig-api-43',
      platform: 'instagram',
      title: 'How did they build the pyramids? 🤔😲 What\'s your take',
      description: 'How did they build the pyramids? 🤔😲 What\'s your take?  #creativejournaling #ancientcivilizations #historyjournal #junkjournal #vintagejournal',
      url: 'https://www.instagram.com/reel/DaRKRqUIgeZ/',
      embedId: 'DaRKRqUIgeZ',
      category: 'product-showcase',
      tags: ['creativejournaling', 'ancientcivilizations', 'historyjournal', 'junkjournal', 'vintagejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DaRKRqUIgeZ/media/?size=m'
    },
    {
      id: 'ig-api-44',
      platform: 'instagram',
      title: 'The easiest way to start a new journal/junk journal! Make an about me journal...',
      description: 'The easiest way to start a new journal/junk journal! Make an about me journal page ✨ Tag someone who needs to try this 💌  #aboutmejournal #journalpage #journalingprompts #scrapbookpages #creativejour',
      url: 'https://www.instagram.com/reel/DaIt8eYReR6/',
      embedId: 'DaIt8eYReR6',
      category: 'journal-spread',
      tags: ['aboutmejournal', 'journalpage', 'journalingprompts', 'scrapbookpages', 'creativejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DaIt8eYReR6/media/?size=m'
    },
    {
      id: 'ig-api-45',
      platform: 'instagram',
      title: 'A Great Gatsby-inspired spread using our Art Deco Box ✨',
      description: 'A Great Gatsby-inspired spread using our Art Deco Box ✨ This is still one of my favorite themes to create. I love all the geometric patterns, gold accents, and vintage details that make the Art Deco s',
      url: 'https://www.instagram.com/reel/DZ-QRE5RcDs/',
      embedId: 'DZ-QRE5RcDs',
      category: 'journal-spread',
      tags: ['artdeco', 'greatgatsby', 'roaringtwenties', 'vintagejournal', 'junkjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DZ-QRE5RcDs/media/?size=m'
    },
    {
      id: 'ig-api-46',
      platform: 'instagram',
      title: 'International Sticker Lover Club members get these gorgeous stamps on their h...',
      description: 'International Sticker Lover Club members get these gorgeous stamps on their happy mail every month ✈️💌 Be honest… who\'s collecting them? 👀 #stickerloverclub #stickermail #happymail #stickercollectio',
      url: 'https://www.instagram.com/reel/DZ8iH0ZI24_/',
      embedId: 'DZ8iH0ZI24_',
      category: 'product-showcase',
      tags: ['stickerloverclub', 'stickermail', 'happymail', 'stickercollection', 'journalingcommunity', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DZ8iH0ZI24_/media/?size=m'
    },
    {
      id: 'ig-api-47',
      platform: 'instagram',
      title: 'Just me and my travel journal above the clouds ✈️🤍  I get this sudden urge t...',
      description: 'Just me and my travel journal above the clouds ✈️🤍  I get this sudden urge to write everything down when i’m in the air… does anyone else feel more inspired when they fly? #traveljournal #traveltok  ',
      url: 'https://www.instagram.com/reel/DZyLDsYI2ZL/',
      embedId: 'DZyLDsYI2ZL',
      category: 'product-showcase',
      tags: ['traveljournal', 'traveltok', 'romanticisingyourlife', 'traveldiaries', 'journalwithme', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DZyLDsYI2ZL/media/?size=m'
    },
    {
      id: 'ig-api-48',
      platform: 'instagram',
      title: 'Today is the last day to get this edition of the sticker lover club! This edi...',
      description: 'Today is the last day to get this edition of the sticker lover club! This edition is super special because it comes with pre-made slides, you can just peel, decorate and fold 🙌🏽  #stickerlover #stic',
      url: 'https://www.instagram.com/reel/DZlFCz3I5Id/',
      embedId: 'DZlFCz3I5Id',
      category: 'product-showcase',
      tags: ['stickerlover', 'stickersubscription', 'asmrjournaling', 'scrapbooksupplies', 'journaling', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2026-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DZlFCz3I5Id/media/?size=m'
    },
    {
      id: 'ig-api-49',
      platform: 'instagram',
      title: 'Two weddings, two countries, one very full travel journal ✈️ I came home from...',
      description: 'Two weddings, two countries, one very full travel journal ✈️ I came home from Belgium and Egypt with my head full of ideas, little details, and moments I didn\'t want to forget. Some of that inspiratio',
      url: 'https://www.instagram.com/reel/DZkf_GoxbZ2/',
      embedId: 'DZkf_GoxbZ2',
      category: 'product-showcase',
      tags: ['traveljournal', 'junkjournal', 'traveljournaling', 'vintagejournal', 'craftcommunity', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DZkf_GoxbZ2/media/?size=m'
    },
    {
      id: 'ig-api-50',
      platform: 'instagram',
      title: 'This is your sign to add a little magic to your reading with our Swords & Sto...',
      description: 'This is your sign to add a little magic to your reading with our Swords & Stories bookmarks 🗡️📚 #booktok #bookmarks #fantasybooks #bookishtiktok #booknook',
      url: 'https://www.instagram.com/reel/DZdBzpgxJZD/',
      embedId: 'DZdBzpgxJZD',
      category: 'product-showcase',
      tags: ['booktok', 'bookmarks', 'fantasybooks', 'bookishtiktok', 'booknook', 'fantasy'],
      creator: 'CoraCreaCrafts',
      date: '2026-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DZdBzpgxJZD/media/?size=m'
    },
    {
      id: 'ig-api-51',
      platform: 'instagram',
      title: 'My friend got married and I got to make all her wedding stationery: signs, in...',
      description: 'My friend got married and I got to make all her wedding stationery: signs, invites, guest book, pens, stickers, menus, table decor 🥹 One of my favorite things I\'ve ever worked on!   @mariechen2293  #',
      url: 'https://www.instagram.com/reel/DZWV7K2sZvy/',
      embedId: 'DZWV7K2sZvy',
      category: 'product-showcase',
      tags: ['weddingstationery', 'weddingplacecards', 'waxseals', 'stationerydesign', 'handmadewedding', 'stickers'],
      creator: 'CoraCreaCrafts',
      date: '2026-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DZWV7K2sZvy/media/?size=m'
    },
    {
      id: 'ig-api-52',
      platform: 'instagram',
      title: 'Say ciao to our Italian Summer Vintage Craft Box 🇮🇹 Vespas, Sicilian lemons...',
      description: 'Say ciao to our Italian Summer Vintage Craft Box 🇮🇹 Vespas, Sicilian lemons, gelato, maiolica tiles, Pisa, we packed all our favorite bits of Italy into one box!  What would you craft with this one?',
      url: 'https://www.instagram.com/reel/DZTZSjzon4n/',
      embedId: 'DZTZSjzon4n',
      category: 'behind-the-scenes',
      tags: ['italiansummer', 'washitape', 'junkjournal', 'italianaesthetic', 'stationeryaddict', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2026-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DZTZSjzon4n/media/?size=m'
    },
    {
      id: 'ig-api-53',
      platform: 'instagram',
      title: 'Evergreen is back, and this time it showed up with the Niblet in tow. This is...',
      description: 'Evergreen is back, and this time it showed up with the Niblet in tow. This is the first Niblet colorway @esterbrook_official has ever done!   Both are in the shop now. Online & in store @creativecorne',
      url: 'https://www.instagram.com/reel/DZPwX9FBy9-/',
      embedId: 'DZPwX9FBy9-',
      category: 'product-showcase',
      tags: ['fountainpen', 'esterbrook', 'penstore', 'niblet', 'pennerds'],
      creator: 'CoraCreaCrafts',
      date: '2026-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DZPwX9FBy9-/media/?size=m'
    },
    {
      id: 'ig-api-54',
      platform: 'instagram',
      title: 'Introducing our newest Treasure Box: Ancient Civilizations✨',
      description: 'Introducing our newest Treasure Box: Ancient Civilizations✨  This one\'s inspired by Mesoamerican art and craftsmanship. Earthy tones, intricate patterns, all the symbols and details that make this kin',
      url: 'https://www.instagram.com/reel/DZN-vd9IO6v/',
      embedId: 'DZN-vd9IO6v',
      category: 'box-reveal',
      tags: ['ancientcivilizations', 'mesoamericanart', 'aztecaesthetic', 'mayanart', 'ancienthistory'],
      creator: 'CoraCreaCrafts',
      date: '2026-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DZN-vd9IO6v/media/?size=m'
    },
    {
      id: 'ig-api-55',
      platform: 'instagram',
      title: 'You have to see our new tapes 🌙 I wasn\'t planning on keeping any for myself....',
      description: 'You have to see our new tapes 🌙 I wasn\'t planning on keeping any for myself. Then I tilted one in the light and that plan went straight out the window! Comment your favorite below 🌙 #washitape #junk',
      url: 'https://www.instagram.com/reel/DZFca9KMTdr/',
      embedId: 'DZFca9KMTdr',
      category: 'product-showcase',
      tags: ['washitape', 'junkjournalsupplies', 'craftsupplies', 'papercraft', 'stationeryaddict', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2026-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DZFca9KMTdr/media/?size=m'
    },
    {
      id: 'ig-api-56',
      platform: 'instagram',
      title: 'Once upon a time, on a journal page... 📖✨ Fables & Fairytales collection is ...',
      description: 'Once upon a time, on a journal page... 📖✨ Fables & Fairytales collection is pure storybook magic 🌟 - a lot of items from this collection are sold out but don\'t worry, it\'s being restocked!  #junkjou',
      url: 'https://www.instagram.com/reel/DY7Q9KAK1NI/',
      embedId: 'DY7Q9KAK1NI',
      category: 'journal-spread',
      tags: ['junkjournal', 'fairytaleaesthetic', 'journalspread', 'scrapbooking', 'papercraft', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DY7Q9KAK1NI/media/?size=m'
    },
    {
      id: 'ig-api-57',
      platform: 'instagram',
      title: 'Your tape drawer called...it wants these 🌸☀️',
      description: 'Your tape drawer called...it wants these 🌸☀️ #washitape #washitapeaddict #junkjournal #papertape #washicollection',
      url: 'https://www.instagram.com/reel/DY2HxfzsKfM/',
      embedId: 'DY2HxfzsKfM',
      category: 'product-showcase',
      tags: ['washitape', 'washitapeaddict', 'junkjournal', 'papertape', 'washicollection', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2026-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DY2HxfzsKfM/media/?size=m'
    },
    {
      id: 'ig-api-58',
      platform: 'instagram',
      title: 'POV: it\'s the last day of the sale and we\'re packing grab bags like our lives...',
      description: 'POV: it\'s the last day of the sale and we\'re packing grab bags like our lives depend on it 😅 It\'s the last chance to get your grab bag and shop our up to 40% off sale!   Sale ends tonight at 11:59pm ',
      url: 'https://www.instagram.com/reel/DYxJJ2Ch3Fh/',
      embedId: 'DYxJJ2Ch3Fh',
      category: 'behind-the-scenes',
      tags: ['grabbag', 'memorialdaysale', 'coracreacrafts', 'junkjournal', 'behindthescenes', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DYxJJ2Ch3Fh/media/?size=m'
    },
    {
      id: 'ig-api-59',
      platform: 'instagram',
      title: 'Memorial Day weekend is here ✨',
      description: 'Memorial Day weekend is here ✨ And so are our grab bags! They\'re back in 3 different styles, and members get to shop them first today at an even bigger discount 😍 If there are any left over, they\'ll ',
      url: 'https://www.instagram.com/reel/DYpVoBoheLJ/',
      embedId: 'DYpVoBoheLJ',
      category: 'product-showcase',
      tags: ['memorialdaysale', 'junkjournal', 'coracreacrafts', 'grabbag', 'craftsupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DYpVoBoheLJ/media/?size=m'
    },
    {
      id: 'ig-api-60',
      platform: 'instagram',
      title: 'This is HUGE 😲 especially for our TINY stationery store in Nashville! Thank ...',
      description: 'This is HUGE 😲 especially for our TINY stationery store in Nashville! Thank you @esterbrook_official  Come get yours in our store in Nashville, we only have a couple!   #fountainpen #creativejournali',
      url: 'https://www.instagram.com/reel/DYaMwz1Ix4F/',
      embedId: 'DYaMwz1Ix4F',
      category: 'product-showcase',
      tags: ['fountainpen', 'creativejournaling', 'journalingcommunity', 'stationerystore', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DYaMwz1Ix4F/media/?size=m'
    },
    {
      id: 'ig-api-61',
      platform: 'instagram',
      title: 'Can\'t wait for the Spooky LCF @littlecraftfest What a special weekend we had ...',
      description: 'Can\'t wait for the Spooky LCF @littlecraftfest What a special weekend we had at the Little Craft Fest 🌸 3 amazing workshops, so many lovely people, and the best helpers by my side, I couldn\'t have as',
      url: 'https://www.instagram.com/reel/DYX6kiQo17I/',
      embedId: 'DYX6kiQo17I',
      category: 'product-showcase',
      tags: ['littlecraftfest', 'craftworkshop', 'junkjournaling', 'craftcommunity', 'vintagecrafts', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DYX6kiQo17I/media/?size=m'
    },
    {
      id: 'ig-api-62',
      platform: 'instagram',
      title: 'Spent the morning filling in my May journal with Edition 05 and remembered wh...',
      description: 'Spent the morning filling in my May journal with Edition 05 and remembered why I loved designing this one so much! There\'s only a few left now before Edition 06 takes over tonight 🌸  #stickerlover #j',
      url: 'https://www.instagram.com/reel/DYVICW4o2hU/',
      embedId: 'DYVICW4o2hU',
      category: 'product-showcase',
      tags: ['stickerlover', 'journalwithme', 'vintagestickers', 'stickersubscription', 'junkjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DYVICW4o2hU/media/?size=m'
    },
    {
      id: 'ig-api-63',
      platform: 'instagram',
      title: 'The most glamarous box we\'ve ever made 🌟😍',
      description: 'The most glamarous box we\'ve ever made 🌟😍  #artdeco #artdecostyle #1920saesthetic #vintageaesthetic #junkjournal',
      url: 'https://www.instagram.com/reel/DYQLtOQI0Ju/',
      embedId: 'DYQLtOQI0Ju',
      category: 'box-reveal',
      tags: ['artdeco', 'artdecostyle', '1920saesthetic', 'vintageaesthetic', 'junkjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DYQLtOQI0Ju/media/?size=m'
    },
    {
      id: 'ig-api-64',
      platform: 'instagram',
      title: 'And just like that, the story continues... 📖🌙 Happy Mother\'s Day 💐',
      description: 'And just like that, the story continues... 📖🌙 Happy Mother\'s Day 💐   Part 2 of our Fables & Fairy Tales unboxing is here! Hope you\'ve loved this magical theme as much as we do 🤍   To celebrate thi',
      url: 'https://www.instagram.com/reel/DYKbOhdxvg3/',
      embedId: 'DYKbOhdxvg3',
      category: 'unboxing',
      tags: ['fairycore', 'fairytaleaesthetic', 'folklore', 'junkjournal', 'journalingcommunity', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DYKbOhdxvg3/media/?size=m'
    },
    {
      id: 'ig-api-65',
      platform: 'instagram',
      title: 'Caught up on my Travel Journal for April 😍 Two big events needed to be recor...',
      description: 'Caught up on my Travel Journal for April 😍 Two big events needed to be recorded: Little Craft Fest and my trip back to Nashville via New Orleans ✍🏽   I hosted a workshop on how to set up a Travel Jo',
      url: 'https://www.instagram.com/reel/DYImL3Fom1c/',
      embedId: 'DYImL3Fom1c',
      category: 'tutorial',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DYImL3Fom1c/media/?size=m'
    },
    {
      id: 'ig-api-66',
      platform: 'instagram',
      title: 'Surprise! 🌶️ We\'re celebrating Cinco de Mayo with a little treat for you... ...',
      description: 'Surprise! 🌶️ We\'re celebrating Cinco de Mayo with a little treat for you... every sticker sheet is just $5 and we\'ve picked out 10 special items for 50% off too! 🌵  Sale ends TODAY at 11:59pm CST, d',
      url: 'https://www.instagram.com/reel/DX97a_foHHa/',
      embedId: 'DX97a_foHHa',
      category: 'product-showcase',
      tags: ['cincodemayo', 'stickersale', 'craftsale', 'stickeraddict', 'craftingcommunity', 'stickers'],
      creator: 'CoraCreaCrafts',
      date: '2026-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DX97a_foHHa/media/?size=m'
    },
    {
      id: 'ig-api-67',
      platform: 'instagram',
      title: 'Once upon a time, there was a Treasure Box for the ones who never stopped bel...',
      description: 'Once upon a time, there was a Treasure Box for the ones who never stopped believing in fairytales 📖🌙 So excited to reveal part 1 of our Fables & Fairy Tales unboxing! This is one of the most magical',
      url: 'https://www.instagram.com/reel/DX7z-qJIDO4/',
      embedId: 'DX7z-qJIDO4',
      category: 'unboxing',
      tags: ['fairycore', 'fairytaleaesthetic', 'storybook', 'onceuponatime', 'junkjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DX7z-qJIDO4/media/?size=m'
    },
    {
      id: 'ig-api-68',
      platform: 'instagram',
      title: 'POV: the prettiest sticker storage on your desk is one you made yourself 🌸 C...',
      description: 'POV: the prettiest sticker storage on your desk is one you made yourself 🌸 Come make yours with us:  🌷 May 14th | 6:30-8:30pm   📍 Creative Corner Nashville  Link in bio to book!  #nashvilleworkshop',
      url: 'https://www.instagram.com/reel/DX4Zz3uRA6a/',
      embedId: 'DX4Zz3uRA6a',
      category: 'product-showcase',
      tags: ['nashvilleworkshop', 'craftworkshop', 'junkjournaling', 'stickerlover', 'nashvillesmallbusiness', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DX4Zz3uRA6a/media/?size=m'
    },
    {
      id: 'ig-api-69',
      platform: 'instagram',
      title: 'The cutest tiny wee things you ever did see 🐭🍄 Our newest Vintage Craft Box...',
      description: 'The cutest tiny wee things you ever did see 🐭🍄 Our newest Vintage Craft Box is full of mice, mushrooms and miniature magic, made for the cottagecore crafters who love all things small and sweet! Com',
      url: 'https://www.instagram.com/reel/DXzYEwWRKNy/',
      embedId: 'DXzYEwWRKNy',
      category: 'box-reveal',
      tags: ['vintagecraftbox', 'miniaturecrafts', 'cottagecoreaesthetic', 'journalingcommunity', 'stationeryaddict', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DXzYEwWRKNy/media/?size=m'
    },
    {
      id: 'ig-api-70',
      platform: 'instagram',
      title: 'The sun was out, the windows were open, and I had the Birds. butterflies & fo...',
      description: 'The sun was out, the windows were open, and I had the Birds. butterflies & foliage Box spread across the whole of my craft table 🌷 This was what I ended up with, it truly was one of those afternoons ',
      url: 'https://www.instagram.com/reel/DXrbBq5hJeV/',
      embedId: 'DXrbBq5hJeV',
      category: 'journal-spread',
      tags: ['vintagejournaling', 'junkjournaling', 'springcrafts', 'craftsubscriptionbox', 'slowcrafting', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DXrbBq5hJeV/media/?size=m'
    },
    {
      id: 'ig-api-71',
      platform: 'instagram',
      title: 'Consider this your gentle nudge to start that habit tracker you\'ve been think...',
      description: 'Consider this your gentle nudge to start that habit tracker you\'ve been thinking about 🌷 It\'s had such a positive impact on me and honestly, making it is half the fun ✨  #habittracker #junkjournaling',
      url: 'https://www.instagram.com/reel/DXfE7YaBouL/',
      embedId: 'DXfE7YaBouL',
      category: 'product-showcase',
      tags: ['habittracker', 'junkjournaling', 'bulletjournal', 'slowliving', 'journalingcommunity', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DXfE7YaBouL/media/?size=m'
    },
    {
      id: 'ig-api-72',
      platform: 'instagram',
      title: 'This one\'s for the crafters who romanticise everything ✨ Morning light, fresh...',
      description: 'This one\'s for the crafters who romanticise everything ✨ Morning light, fresh flowers, handwritten notes, a good book in the garden… if that sounds like your kind of spring, you\'re going to love what\'',
      url: 'https://www.instagram.com/reel/DXaOWTfiNXS/',
      embedId: 'DXaOWTfiNXS',
      category: 'box-reveal',
      tags: ['vintageaesthetic', 'craftsubscriptionbox', 'journalingcommunity', 'slowliving', 'springcrafts', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DXaOWTfiNXS/media/?size=m'
    },
    {
      id: 'ig-api-73',
      platform: 'instagram',
      title: 'Last chance to grab a spot in my Everything Journal workshop this Friday at t...',
      description: 'Last chance to grab a spot in my Everything Journal workshop this Friday at the Little Craft Fest!🌷 We\'ll be building one beautiful journal that holds art, planning, and mental health all in one plac',
      url: 'https://www.instagram.com/reel/DXXY1nniKuJ/',
      embedId: 'DXXY1nniKuJ',
      category: 'product-showcase',
      tags: ['littlecraftfest', 'craftworkshop', 'journalingworkshop', 'artjournaling', 'junkjournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DXXY1nniKuJ/media/?size=m'
    },
    {
      id: 'ig-api-74',
      platform: 'instagram',
      title: 'We’re celebrating our 2nd year attending the SOLD OUT Little Craft Fest with ...',
      description: 'We’re celebrating our 2nd year attending the SOLD OUT Little Craft Fest with a special giveaway! We’re picking 2 followers to win a 3‑Day Pass to Little Craft Fest to join us for a weekend of creativi',
      url: 'https://www.instagram.com/reel/DXHS2mZEVmQ/',
      embedId: 'DXHS2mZEVmQ',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2026-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DXHS2mZEVmQ/media/?size=m'
    },
    {
      id: 'ig-api-75',
      platform: 'instagram',
      title: 'Secrets of the Pharaohs Junk Journal Spread✨ Should we bring back more Egypt ...',
      description: 'Secrets of the Pharaohs Junk Journal Spread✨ Should we bring back more Egypt themed products? Drop a 🏺 below!  #junkjournal #egyptianaesthetic #ancientegypt #junkjournalspread #vintagejournaling',
      url: 'https://www.instagram.com/reel/DW_pb04EeLH/',
      embedId: 'DW_pb04EeLH',
      category: 'journal-spread',
      tags: ['junkjournal', 'egyptianaesthetic', 'ancientegypt', 'junkjournalspread', 'vintagejournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DW_pb04EeLH/media/?size=m'
    },
    {
      id: 'ig-api-76',
      platform: 'instagram',
      title: 'A special project that deserved 125 envelopes decorated by hand! So grateful ...',
      description: 'A special project that deserved 125 envelopes decorated by hand! So grateful to have been part of  @collabvent this spring 🌼    #collabvent #craftbox #junkjournal #vintagecraft #stationeryaddict',
      url: 'https://www.instagram.com/reel/DW9OeHpkVwy/',
      embedId: 'DW9OeHpkVwy',
      category: 'box-reveal',
      tags: ['collabvent', 'craftbox', 'junkjournal', 'vintagecraft', 'stationeryaddict', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DW9OeHpkVwy/media/?size=m'
    },
    {
      id: 'ig-api-77',
      platform: 'instagram',
      title: 'POV: you open your new vintage craft box and spring falls out 🌿🦋',
      description: 'POV: you open your new vintage craft box and spring falls out 🌿🦋  Meet Birds, Butterflies & Foliage our watercolour love letter to the season that always feels like a second beginning. soft washes, ',
      url: 'https://www.instagram.com/reel/DWriip0CNpx/',
      embedId: 'DWriip0CNpx',
      category: 'box-reveal',
      tags: ['subscriptionbox', 'craftsupplies', 'junkjournal', 'vintagecraft', 'springaesthetic', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DWriip0CNpx/media/?size=m'
    },
    {
      id: 'ig-api-78',
      platform: 'instagram',
      title: 'I\'ve wanted to release a special perfume to capture my love for crafting for ...',
      description: 'I\'ve wanted to release a special perfume to capture my love for crafting for years and today is finally the day 🥹   Craft Room N°5 is here. And yes, I named it after my favorite place in the world! T',
      url: 'https://www.instagram.com/reel/DWl60IFh2_I/',
      embedId: 'DWl60IFh2_I',
      category: 'product-showcase',
      tags: ['perfume', 'perfumelovers', 'newperfume', 'craftroom', 'junkjournal', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2026-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DWl60IFh2_I/media/?size=m'
    },
    {
      id: 'ig-api-79',
      platform: 'instagram',
      title: 'One splash of water and our Alchemy Burst powders do THIS🔮 The background ma...',
      description: 'One splash of water and our Alchemy Burst powders do THIS🔮 The background made this Fortune Teller spread come alive perfectly! Have you tried them yet?✨   #junkjournal #fortuneteller #mixedmedia #cr',
      url: 'https://www.instagram.com/reel/DWZmYTeCFN-/',
      embedId: 'DWZmYTeCFN-',
      category: 'journal-spread',
      tags: ['junkjournal', 'fortuneteller', 'mixedmedia', 'craftinspo', 'powderart', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DWZmYTeCFN-/media/?size=m'
    },
    {
      id: 'ig-api-80',
      platform: 'instagram',
      title: 'Just a couple of crafty girls on Channel 5 News living their best life! 😂 We...',
      description: 'Just a couple of crafty girls on Channel 5 News living their best life! 😂 We were in the studio talking all things junk journaling and had the BEST time 🤭✨  @newschannel5 @talkofthetown_nc5   #nashv',
      url: 'https://www.instagram.com/reel/DWXiKH1CMI2/',
      embedId: 'DWXiKH1CMI2',
      category: 'product-showcase',
      tags: ['nashvilletn', 'nashvillelocal', 'nashvillesmallbusiness', 'visitmusiccity', 'junkjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DWXiKH1CMI2/media/?size=m'
    },
    {
      id: 'ig-api-81',
      platform: 'instagram',
      title: 'March needed a little intention setting so, I made this gratitude log using p...',
      description: 'March needed a little intention setting so, I made this gratitude log using pieces from our Treasure Box and I am obsessed with how it turned out 🔮   #gratitudejournal #junkjournal #fortuneteller #bu',
      url: 'https://www.instagram.com/reel/DWRwqCTiMJf/',
      embedId: 'DWRwqCTiMJf',
      category: 'box-reveal',
      tags: ['gratitudejournal', 'junkjournal', 'fortuneteller', 'bujoinspo', 'craftinglife', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DWRwqCTiMJf/media/?size=m'
    },
    {
      id: 'ig-api-82',
      platform: 'instagram',
      title: 'Sometimes the best pages are the ones you never planned 🤎',
      description: 'Sometimes the best pages are the ones you never planned 🤎   No rules, no plan, just creating and I am so happy with how this turned out! do you ever just let go and see what happens? ✨   #junkjournal',
      url: 'https://www.instagram.com/reel/DWJvyfBhY0T/',
      embedId: 'DWJvyfBhY0T',
      category: 'product-showcase',
      tags: ['junkjournal', 'creativejournaling', 'artjournaling', 'journalinspiration', 'mixedmediaart', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DWJvyfBhY0T/media/?size=m'
    },
    {
      id: 'ig-api-83',
      platform: 'instagram',
      title: '2026 has really shown me how much I love a good habit tracker 😅 so when you ...',
      description: '2026 has really shown me how much I love a good habit tracker 😅 so when you asked if you could use the Sticker Lover Club stickers for one, obviously we had to make it. Who else likes tracking their ',
      url: 'https://www.instagram.com/reel/DWEZhJ2hOkf/',
      embedId: 'DWEZhJ2hOkf',
      category: 'product-showcase',
      tags: ['habittracker', 'journaling', 'plannercommunity', 'stickers', 'bulletjournal'],
      creator: 'CoraCreaCrafts',
      date: '2026-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DWEZhJ2hOkf/media/?size=m'
    },
    {
      id: 'ig-api-84',
      platform: 'instagram',
      title: 'We\'re giving away a $100 gift card this St Patrick\'s Day, every order on our ...',
      description: 'We\'re giving away a $100 gift card this St Patrick\'s Day, every order on our website enters you automatically! PLUS spend over $50 and get 17% off, spend over $150 and get 25% off  Tag someone you\'d s',
      url: 'https://www.instagram.com/reel/DWACNILiL8_/',
      embedId: 'DWACNILiL8_',
      category: 'product-showcase',
      tags: ['stpatricksday', 'craftgiveaway', 'stationerylover', 'junkjournal', 'craftsupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DWACNILiL8_/media/?size=m'
    },
    {
      id: 'ig-api-85',
      platform: 'instagram',
      title: 'If you love glitter, you need to come to this ✨ Janice from Nashville Junk Jo...',
      description: 'If you love glitter, you need to come to this ✨ Janice from Nashville Junk Journal Club and I are taking over Creative Corner Nashville this Friday for a Junk Journal Glitter Bar and it is going to be',
      url: 'https://www.instagram.com/reel/DV6V83lkWzq/',
      embedId: 'DV6V83lkWzq',
      category: 'product-showcase',
      tags: ['thingstodoinnashville', 'junkjournaling', 'glitterbar', 'nashvillecrafts', 'nashville', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DV6V83lkWzq/media/?size=m'
    },
    {
      id: 'ig-api-86',
      platform: 'instagram',
      title: 'I failed 😭 Our Ireland-themed box is everything and I cannot stop going thro...',
      description: 'I failed 😭 Our Ireland-themed box is everything and I cannot stop going through it. Today\'s the perfect day to join me as we have 13% off site wide for 13 hours only. Friday 13th isn\'t so unlucky aft',
      url: 'https://www.instagram.com/reel/DV1QS2aEYMG/',
      embedId: 'DV1QS2aEYMG',
      category: 'product-showcase',
      tags: ['fridaythe13th', 'stpatricksday', 'irelandaesthetic', 'junkjournal', 'creativejournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DV1QS2aEYMG/media/?size=m'
    },
    {
      id: 'ig-api-87',
      platform: 'instagram',
      title: 'The universe called and it left you a box 🔮 Introducing our Fortune Teller T...',
      description: 'The universe called and it left you a box 🔮 Introducing our Fortune Teller Treasure Box. Bohemian textures, celestial details, worn vintage pages and that beautiful feeling of mystery running through',
      url: 'https://www.instagram.com/reel/DVzLxOqCBd7/',
      embedId: 'DVzLxOqCBd7',
      category: 'box-reveal',
      tags: ['fortuneteller', 'junkjournaling', 'witchyaesthetic', 'bohoaesthetic', 'vintageephemera', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DVzLxOqCBd7/media/?size=m'
    },
    {
      id: 'ig-api-88',
      platform: 'instagram',
      title: 'Who\'s part of the Sticker Lovers Club? 🙋‍♀️ Edition 003 is almost gone, we o...',
      description: 'Who\'s part of the Sticker Lovers Club? 🙋‍♀️ Edition 003 is almost gone, we only 2 left and once they\'re gone that\'s it! Edition 004 is already on its way 😍  #journalingstickers #stickers #creativejo',
      url: 'https://www.instagram.com/reel/DVt6IJzCJKz/',
      embedId: 'DVt6IJzCJKz',
      category: 'product-showcase',
      tags: ['journalingstickers', 'stickers', 'creativejournaling', 'stickeraddict', 'junkjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DVt6IJzCJKz/media/?size=m'
    },
    {
      id: 'ig-api-89',
      platform: 'instagram',
      title: 'Is this spread Peanut approved? 🐈☘️',
      description: 'Is this spread Peanut approved? 🐈☘️  #irelandtravel #junkjournaling #creativejournaling',
      url: 'https://www.instagram.com/reel/DVi6uyNEdob/',
      embedId: 'DVi6uyNEdob',
      category: 'journal-spread',
      tags: ['irelandtravel', 'junkjournaling', 'creativejournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DVi6uyNEdob/media/?size=m'
    },
    {
      id: 'ig-api-90',
      platform: 'instagram',
      title: 'House Stark using the Legends & Lore Box 🐺 ❄️ which house should I do next',
      description: 'House Stark using the Legends & Lore Box 🐺 ❄️ which house should I do next?   #junkjournal #housestark #fantasyjournal #papercrafting #gameofthrones',
      url: 'https://www.instagram.com/reel/DVYria5ke5q/',
      embedId: 'DVYria5ke5q',
      category: 'box-reveal',
      tags: ['junkjournal', 'housestark', 'fantasyjournal', 'papercrafting', 'gameofthrones', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DVYria5ke5q/media/?size=m'
    },
    {
      id: 'ig-api-91',
      platform: 'instagram',
      title: 'Our Ireland VCB is finally here!! 🍀 I’ve been wanting to create this theme f...',
      description: 'Our Ireland VCB is finally here!! 🍀 I’ve been wanting to create this theme for so long, so getting to finally bring it to life feels really special. And with March and St. Patrick’s Day coming up, th',
      url: 'https://www.instagram.com/reel/DVRwLHtiAi9/',
      embedId: 'DVRwLHtiAi9',
      category: 'product-showcase',
      tags: ['irelandcrafts', 'stpatricksdaycrafts', 'irelandinspired', 'junkjournalcommunity', 'vintagejournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DVRwLHtiAi9/media/?size=m'
    },
    {
      id: 'ig-api-92',
      platform: 'instagram',
      title: 'Tonight 🙌🏽❤️ link in bio',
      description: 'Tonight 🙌🏽❤️ link in bio!',
      url: 'https://www.instagram.com/reel/DVOrmRTkfID/',
      embedId: 'DVOrmRTkfID',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2026-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DVOrmRTkfID/media/?size=m'
    },
    {
      id: 'ig-api-93',
      platform: 'instagram',
      title: 'Packing up Legends & Lore boxes today 🏰✨ this one\'s been so fun to put toget...',
      description: 'Packing up Legends & Lore boxes today 🏰✨ this one\'s been so fun to put together. Any Game of Thrones fans in here? 🤎  #legendsandlore #medievalfantasy #packingorders #coracreacrafts #vintagepapercra',
      url: 'https://www.instagram.com/reel/DVKC2lgCNm-/',
      embedId: 'DVKC2lgCNm-',
      category: 'behind-the-scenes',
      tags: ['legendsandlore', 'medievalfantasy', 'packingorders', 'coracreacrafts', 'vintagepapercrafting', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2026-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DVKC2lgCNm-/media/?size=m'
    },
    {
      id: 'ig-api-94',
      platform: 'instagram',
      title: 'POV: when one of your favorite authors wants to collaborate again 🥹',
      description: 'POV: when one of your favorite authors wants to collaborate again 🥹  So excited to finally share our brand new bundle with Laura Frantz for her new book The Belle of Chatham. If you love crafting and',
      url: 'https://www.instagram.com/reel/DU_3qgRiMbf/',
      embedId: 'DU_3qgRiMbf',
      category: 'box-reveal',
      tags: ['laurafrantz', 'thebelleofchatham', 'vintagepapercrafting', 'coracreacrafts', 'bookishcrafts', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2026-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DU_3qgRiMbf/media/?size=m'
    },
    {
      id: 'ig-api-95',
      platform: 'instagram',
      title: 'How good a knight are you? ⚔️',
      description: 'How good a knight are you? ⚔️  Introducing our brand new Legends & Lore limited edition box, a magical collaboration with @storm_sparrow inspired by epic realms, dragonlords and the kind of stories yo',
      url: 'https://www.instagram.com/reel/DUtnUh1CIna/',
      embedId: 'DUtnUh1CIna',
      category: 'box-reveal',
      tags: ['gameofthrones', 'aknightofthesevenkingdoms', 'fantasystationery', 'junkjournal', 'stationeryaddict', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DUtnUh1CIna/media/?size=m'
    },
    {
      id: 'ig-api-96',
      platform: 'instagram',
      title: 'Still sticking to my 2026 habits and wasn\'t about to let a trip to Mexico sto...',
      description: 'Still sticking to my 2026 habits and wasn\'t about to let a trip to Mexico stop me. Made my February habit tracker on a sunbed and I loved it! Also made a little friend 🥹   #habittracker #journaling #',
      url: 'https://www.instagram.com/reel/DUl-XqfCMh7/',
      embedId: 'DUl-XqfCMh7',
      category: 'product-showcase',
      tags: ['habittracker', 'journaling', 'craftanywhere', '2026habits', 'papercrafts'],
      creator: 'CoraCreaCrafts',
      date: '2026-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DUl-XqfCMh7/media/?size=m'
    },
    {
      id: 'ig-api-97',
      platform: 'instagram',
      title: 'Come step inside the Dreamscape Curiosities box with me! ✨',
      description: 'Come step inside the Dreamscape Curiosities box with me! ✨   I wanted to take you through some pieces up close so you can really see all the details. This one is so special. What\'s your favorite piece',
      url: 'https://www.instagram.com/reel/DUblk3RiFyv/',
      embedId: 'DUblk3RiFyv',
      category: 'unboxing',
      tags: ['junkjournal', 'craftbox', 'unboxing', 'papercrafting', 'dreamscape', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DUblk3RiFyv/media/?size=m'
    },
    {
      id: 'ig-api-98',
      platform: 'instagram',
      title: 'Behind the scenes there is always support (or not 😭) I lost my cool there fo...',
      description: 'Behind the scenes there is always support (or not 😭) I lost my cool there for a second 😂',
      url: 'https://www.instagram.com/reel/DUO9JynERCk/',
      embedId: 'DUO9JynERCk',
      category: 'behind-the-scenes',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2026-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DUO9JynERCk/media/?size=m'
    },
    {
      id: 'ig-api-99',
      platform: 'instagram',
      title: 'A snowy storm day called for something cozy and a little dreamy, so I spent t...',
      description: 'A snowy storm day called for something cozy and a little dreamy, so I spent the afternoon with my journal making my first Dreamscape spread, and I\'m obsessed with how the background turned out! ❄️✨️  ',
      url: 'https://www.instagram.com/reel/DULu3BSke_w/',
      embedId: 'DULu3BSke_w',
      category: 'journal-spread',
      tags: ['dreamscape', 'artjournal', 'artjournaling', 'mixedmediaart', 'journalspread', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DULu3BSke_w/media/?size=m'
    },
    {
      id: 'ig-api-100',
      platform: 'instagram',
      title: 'Ever wonder what your journal would look like if it wandered through a dream?...',
      description: 'Ever wonder what your journal would look like if it wandered through a dream? 🌙✨️  Floating figures, curious animals, impossible landscapes and a little box full of supplies to take it there... that\'',
      url: 'https://www.instagram.com/reel/DUGmbeoERpz/',
      embedId: 'DUGmbeoERpz',
      category: 'product-showcase',
      tags: ['artjournaling', 'mixedmedia', 'creativejournal', 'junkjournal', 'stationerylove', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DUGmbeoERpz/media/?size=m'
    },
    {
      id: 'ig-api-101',
      platform: 'instagram',
      title: 'For a while last year I truly felt like I had lost my purpose and was so unin...',
      description: 'For a while last year I truly felt like I had lost my purpose and was so uninspired to create: I would just make something to make something but my heart wasn\'t in it. This year I\'m going back to maki',
      url: 'https://www.instagram.com/reel/DUBPfG8kW_H/',
      embedId: 'DUBPfG8kW_H',
      category: 'journal-spread',
      tags: ['journalinspo', 'journalingcommunity', 'junkjournal', 'papercrafting', 'stationeryaddict', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DUBPfG8kW_H/media/?size=m'
    },
    {
      id: 'ig-api-102',
      platform: 'instagram',
      title: 'Packing up our new 365 stickers today! It’s always nice sending these out kno...',
      description: 'Packing up our new 365 stickers today! It’s always nice sending these out knowing they’ll be part of someone’s everyday journaling. Would you like to see more products like this? 🤍   #stickershop #jo',
      url: 'https://www.instagram.com/reel/DT3VDFdEbHC/',
      embedId: 'DT3VDFdEbHC',
      category: 'behind-the-scenes',
      tags: ['stickershop', 'journalingcommunity', 'plannerstickers', 'stationeryaddict', 'plannerlife', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DT3VDFdEbHC/media/?size=m'
    },
    {
      id: 'ig-api-103',
      platform: 'instagram',
      title: 'Setting up my habit tracker for 2026 ✨',
      description: '#stationeryshop Setting up my habit tracker for 2026 ✨  I’ve been decorating it with some of my favourite celtic pieces, and it makes tracking little routines and celebrating small wins feel a bit mor',
      url: 'https://www.instagram.com/reel/DTyQ-GTkuDm/',
      embedId: 'DTyQ-GTkuDm',
      category: 'product-showcase',
      tags: ['stationeryshop', 'journalingcommunity', 'celticcraft', 'plannercommunity', 'creativeprocess', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DTyQ-GTkuDm/media/?size=m'
    },
    {
      id: 'ig-api-104',
      platform: 'instagram',
      title: 'Pack an order with us! 🤍',
      description: 'Pack an order with us! 🤍  Kristi ordered 5 of our boxes! So many goodies and new themes heading her way. Thank you so much, Kristi! @thedecorativemind   Comment if you\'d like to see more of these 🙋‍',
      url: 'https://www.instagram.com/reel/DTsrxM1CdvA/',
      embedId: 'DTsrxM1CdvA',
      category: 'behind-the-scenes',
      tags: ['packwithme', 'stationeryshop', 'papergoods', 'journalingcommunity', 'creativeinspo', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DTsrxM1CdvA/media/?size=m'
    },
    {
      id: 'ig-api-105',
      platform: 'instagram',
      title: '365 Days of Stickers 😬',
      description: '365 Days of Stickers 😬',
      url: 'https://www.instagram.com/reel/DTqP9pHka23/',
      embedId: 'DTqP9pHka23',
      category: 'product-showcase',
      tags: ['stickers'],
      creator: 'CoraCreaCrafts',
      date: '2026-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DTqP9pHka23/media/?size=m'
    },
    {
      id: 'ig-api-106',
      platform: 'instagram',
      title: 'Crafting my goals into a vision I can actually see 🤍',
      description: 'Crafting my goals into a vision I can actually see 🤍  As a maker, it’s so inspiring to map out what i want to create, explore, and bring to life this year, all the little ideas, projects, and dreams ',
      url: 'https://www.instagram.com/reel/DTnpMKpjcZO/',
      embedId: 'DTnpMKpjcZO',
      category: 'product-showcase',
      tags: ['visionboard', 'plannercommunity', 'creativeprocess', 'journalingcommunity', 'stationeryshop', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DTnpMKpjcZO/media/?size=m'
    },
    {
      id: 'ig-api-107',
      platform: 'instagram',
      title: 'I just needed to get out my hat for this video 🤣 I am SO excited to finally ...',
      description: 'I just needed to get out my hat for this video 🤣 I am SO excited to finally show you what\'s inside The New Look VCB! 🤍 This one\'s inspired by postwar 1940s–50s elegance, think graceful silhouettes, ',
      url: 'https://www.instagram.com/reel/DTins5Mkc4N/',
      embedId: 'DTins5Mkc4N',
      category: 'product-showcase',
      tags: ['vintagecraft', 'retrojournaling', 'junkjournal', 'papercrafting', 'scrapbooking', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DTins5Mkc4N/media/?size=m'
    },
    {
      id: 'ig-api-108',
      platform: 'instagram',
      title: 'Who wants a closer look inside our Celtic Box? 🍀',
      description: 'Who wants a closer look inside our Celtic Box? 🍀  I’m so excited to finally share all the little details and goodies packed inside! What’s your favorite piece?💚    #celticbox #stationeryshop #journa',
      url: 'https://www.instagram.com/reel/DTdPpU5kfyY/',
      embedId: 'DTdPpU5kfyY',
      category: 'behind-the-scenes',
      tags: ['celticbox', 'stationeryshop', 'journalingcommunity', 'papergoods', 'creativeinspo', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DTdPpU5kfyY/media/?size=m'
    },
    {
      id: 'ig-api-109',
      platform: 'instagram',
      title: 'Still pinching myself 🥹 We had News Channel 5 come by the shop to film for t...',
      description: 'Still pinching myself 🥹 We had News Channel 5 come by the shop to film for their "Talk of the Town" segment, and it finally aired on Wednesday!  Watching it back, I just felt so incredibly proud. It ',
      url: 'https://www.instagram.com/reel/DTTdYAIEkNO/',
      embedId: 'DTTdYAIEkNO',
      category: 'product-showcase',
      tags: ['talkofthetown', 'smallbusinessowner', 'stationeryaddict', 'nashcreative', 'nashville_tn'],
      creator: 'CoraCreaCrafts',
      date: '2026-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DTTdYAIEkNO/media/?size=m'
    },
    {
      id: 'ig-api-110',
      platform: 'instagram',
      title: 'Some of my favorites from our brand new arrivals 😌',
      description: 'Some of my favorites from our brand new arrivals 😌  I may have “borrowed” a couple for my own desk already…  Which one is your favorite?  #journaling #papercrafting #creativecommunity #stationeryaddi',
      url: 'https://www.instagram.com/reel/DTQZNZTkcdy/',
      embedId: 'DTQZNZTkcdy',
      category: 'product-showcase',
      tags: ['journaling', 'papercrafting', 'creativecommunity', 'stationeryaddict', 'junkjournal'],
      creator: 'CoraCreaCrafts',
      date: '2026-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DTQZNZTkcdy/media/?size=m'
    },
    {
      id: 'ig-api-111',
      platform: 'instagram',
      title: 'This year, one of my resolutions is to have more relaxing moments just me and...',
      description: 'This year, one of my resolutions is to have more relaxing moments just me and my journal 🤗 finding my creative spark again is super important to me and this weekends session was a good start, it was ',
      url: 'https://www.instagram.com/reel/DTJZl2vkQ7D/',
      embedId: 'DTJZl2vkQ7D',
      category: 'product-showcase',
      tags: ['journaling', 'advent calendar', 'stickers'],
      creator: 'CoraCreaCrafts',
      date: '2026-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DTJZl2vkQ7D/media/?size=m'
    },
    {
      id: 'ig-api-112',
      platform: 'instagram',
      title: 'New year, new habit. Still in my pj’s, just choosing to start my mornings wit...',
      description: 'New year, new habit. Still in my pj’s, just choosing to start my mornings with gratitude 🤍 One small thing a day. Even on the hard days.',
      url: 'https://www.instagram.com/reel/DTGOZc_ERgV/',
      embedId: 'DTGOZc_ERgV',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2026-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DTGOZc_ERgV/media/?size=m'
    },
    {
      id: 'ig-api-113',
      platform: 'instagram',
      title: 'Mini Argentina Vlog 😍🎄 spending the holidays there was a dream',
      description: 'Mini Argentina Vlog 😍🎄 spending the holidays there was a dream.  Special thank you to  @agusgri - my special talented chef in the family @joacogula - my special talented social media person in the f',
      url: 'https://www.instagram.com/reel/DTBbIS6Evqg/',
      embedId: 'DTBbIS6Evqg',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2026-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DTBbIS6Evqg/media/?size=m'
    },
    {
      id: 'ig-api-114',
      platform: 'instagram',
      title: 'My favorite new theme revealed from this coffee shop 😊☕ nevermind my accent ...',
      description: 'My favorite new theme revealed from this coffee shop 😊☕ nevermind my accent in Spanish 😂  The Celtic Box is now available to order and will ship in the next 2 weeks ✨',
      url: 'https://www.instagram.com/reel/DSz-4kGEa5T/',
      embedId: 'DSz-4kGEa5T',
      category: 'box-reveal',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2025-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DSz-4kGEa5T/media/?size=m'
    },
    {
      id: 'ig-api-115',
      platform: 'instagram',
      title: 'As the year comes to a close, I’m clearing the shelves to make room for what’...',
      description: 'As the year comes to a close, I’m clearing the shelves to make room for what’s next!  From now until Monday you can get up to 50% off across the site.   There\'s so many amazing deals so, I hope you fi',
      url: 'https://www.instagram.com/reel/DSu8LMgkSIW/',
      embedId: 'DSu8LMgkSIW',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2025-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DSu8LMgkSIW/media/?size=m'
    },
    {
      id: 'ig-api-116',
      platform: 'instagram',
      title: 'Merry Christmas to all of you with a tiny delay ❤️',
      description: 'Merry Christmas to all of you with a tiny delay ❤️  Thank you for being here, creating alongside me, and supporting this little corner of the internet.  I hope your Christmas wasslow, cosy, and full o',
      url: 'https://www.instagram.com/reel/DSu6FT7EX6i/',
      embedId: 'DSu6FT7EX6i',
      category: 'journal-spread',
      tags: ['christmasspread', 'journalingcommunity', 'creativejournals', 'papercrafts', 'holidayvibes', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DSu6FT7EX6i/media/?size=m'
    },
    {
      id: 'ig-api-117',
      platform: 'instagram',
      title: 'It\'s Day 5 of our 12 Days of Christmas! We wanted to do something extra speci...',
      description: 'It\'s Day 5 of our 12 Days of Christmas! We wanted to do something extra special…✨  2 lucky winners will receive a box packed with crafting goodies we know you’ll love.  How to enter:  🤍 Follow @corac',
      url: 'https://www.instagram.com/reel/DSXt0TFEb46/',
      embedId: 'DSXt0TFEb46',
      category: 'tutorial',
      tags: ['giveaway', 'christmasgiveaway', '12daysofchristmas', 'journaling', 'christmas'],
      creator: 'CoraCreaCrafts',
      date: '2025-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DSXt0TFEb46/media/?size=m'
    },
    {
      id: 'ig-api-118',
      platform: 'instagram',
      title: 'Turns out the weights are just boxes... welcome to warehouse life! 📦',
      description: 'Turns out the weights are just boxes... welcome to warehouse life! 📦   #smallbusinesslife #warehouselife #packorders businesstoks creativebusiness makersgonnamake behindthescenes shopowner dayintheli',
      url: 'https://www.instagram.com/reel/DSVubTrEn9Q/',
      embedId: 'DSVubTrEn9Q',
      category: 'behind-the-scenes',
      tags: ['smallbusinesslife', 'warehouselife', 'packorders'],
      creator: 'CoraCreaCrafts',
      date: '2025-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DSVubTrEn9Q/media/?size=m'
    },
    {
      id: 'ig-api-119',
      platform: 'instagram',
      title: 'Anyone else obsessed with ATC cards right now? I used our Library themed supp...',
      description: 'Anyone else obsessed with ATC cards right now? I used our Library themed supplies to make this one and was super happy with how it turned out! ✂️✨   #ATC #atccards #artisttradingcards papercrafting mi',
      url: 'https://www.instagram.com/reel/DSLfNMcEvVk/',
      embedId: 'DSLfNMcEvVk',
      category: 'product-showcase',
      tags: ['atc', 'atccards', 'artisttradingcards', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DSLfNMcEvVk/media/?size=m'
    },
    {
      id: 'ig-api-120',
      platform: 'instagram',
      title: 'Have you started christmas crafting yet? ✨',
      description: 'Have you started christmas crafting yet? ✨   Our new Christmas Journaling Book is here and it\'s perfect for spreads, DIY tags and wrapping!   #christmasjournaling #journalingcommunity #stationerylove ',
      url: 'https://www.instagram.com/reel/DSGWSbRkfnB/',
      embedId: 'DSGWSbRkfnB',
      category: 'journal-spread',
      tags: ['christmasjournaling', 'journalingcommunity', 'stationerylove', 'journaling', 'christmas'],
      creator: 'CoraCreaCrafts',
      date: '2025-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DSGWSbRkfnB/media/?size=m'
    },
    {
      id: 'ig-api-121',
      platform: 'instagram',
      title: 'You voted, and we listened! 🖤 This month’s box brings the haunting beauty of...',
      description: 'You voted, and we listened! 🖤 This month’s box brings the haunting beauty of Gothic literature to your journal, think Poe, flickering candlelight, and stormy nights of creativity 🕯️📖  #journalingin',
      url: 'https://www.instagram.com/reel/DSDgWRwEuST/',
      embedId: 'DSDgWRwEuST',
      category: 'box-reveal',
      tags: ['journalinginspo', 'journalersofinstagram', 'craftbox', 'journaling', 'gothic'],
      creator: 'CoraCreaCrafts',
      date: '2025-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DSDgWRwEuST/media/?size=m'
    },
    {
      id: 'ig-api-122',
      platform: 'instagram',
      title: 'Post-weekend restock here in store! 🎉',
      description: 'Post-weekend restock here in store! 🎉  This store has been such an incredible adventure so far and I\'m so grateful for all of you who\'ve come visit us and support us in person or from afar ❤️❤️  #nas',
      url: 'https://www.instagram.com/reel/DSBkL2bjPSC/',
      embedId: 'DSBkL2bjPSC',
      category: 'product-showcase',
      tags: ['nashvillestore', 'craftingcommunity', 'handmadecrafts', 'advent calendar'],
      creator: 'CoraCreaCrafts',
      date: '2025-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DSBkL2bjPSC/media/?size=m'
    },
    {
      id: 'ig-api-123',
      platform: 'instagram',
      title: 'Winter Magic has arrived! Let the cozy crafting begin ✨❄️',
      description: 'Winter Magic has arrived! Let the cozy crafting begin ✨❄️  What\'s your favorite item from this month\'s Curiosities Box?  #wintermagic #junkjournal #junkjournaling creativejournaling artjournaling jour',
      url: 'https://www.instagram.com/reel/DR23CsyksVz/',
      embedId: 'DR23CsyksVz',
      category: 'box-reveal',
      tags: ['wintermagic', 'junkjournal', 'junkjournaling', 'journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DR23CsyksVz/media/?size=m'
    },
    {
      id: 'ig-api-124',
      platform: 'instagram',
      title: 'Can’t wait for you to open your advent calendars this month! Any guess what n...',
      description: 'Can’t wait for you to open your advent calendars this month! Any guess what number 24 holds?? ✨  #craftingcommunity #holidaycrafting #smallbusinessowner makersofinstagram christmascrafts handmadecraft',
      url: 'https://www.instagram.com/reel/DRxlp2HEkZV/',
      embedId: 'DRxlp2HEkZV',
      category: 'product-showcase',
      tags: ['craftingcommunity', 'holidaycrafting', 'smallbusinessowner', 'christmas', 'advent calendar'],
      creator: 'CoraCreaCrafts',
      date: '2025-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DRxlp2HEkZV/media/?size=m'
    },
    {
      id: 'ig-api-125',
      platform: 'instagram',
      title: 'Our biggest sale of the year is here! Im so excited to share it with you 🎉',
      description: 'Our biggest sale of the year is here! Im so excited to share it with you 🎉  From now until Monday:  ✨ Up to 40% off sitewide (over 130 items are 40% off)  ✨ 15% off subscriptions  ✨ Last few Grab Bag',
      url: 'https://www.instagram.com/reel/DRm_R-ukqsq/',
      embedId: 'DRm_R-ukqsq',
      category: 'tutorial',
      tags: ['blackfridaysale', 'holidaycrafts', 'craftsupplies', 'journaling', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2025-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DRm_R-ukqsq/media/?size=m'
    },
    {
      id: 'ig-api-126',
      platform: 'instagram',
      title: 'Our Black Friday grab bags are going fast! 👀✨',
      description: 'Our Black Friday grab bags are going fast! 👀✨  Members get early access at $75 for a couple more hours today. If there are any left, they’ll go live for non-members tomorrow at $90.  Not a member yet',
      url: 'https://www.instagram.com/reel/DRlSt0jCX0A/',
      embedId: 'DRlSt0jCX0A',
      category: 'product-showcase',
      tags: ['blackfridaydeals', 'membersonly', 'craftingcommunity', 'christmas'],
      creator: 'CoraCreaCrafts',
      date: '2025-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DRlSt0jCX0A/media/?size=m'
    },
    {
      id: 'ig-api-127',
      platform: 'instagram',
      title: 'Some spreads just write themselves…especially when your supplies are full of ...',
      description: 'Some spreads just write themselves…especially when your supplies are full of library-inspired treasures. Can’t wait for you to get your Library Vintage Craft Box and start creating your own story-fill',
      url: 'https://www.instagram.com/reel/DRdY_AeDORI/',
      embedId: 'DRdY_AeDORI',
      category: 'journal-spread',
      tags: ['vintagecraftbox', 'booklovers', 'journalspread', 'journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DRdY_AeDORI/media/?size=m'
    },
    {
      id: 'ig-api-128',
      platform: 'instagram',
      title: 'Made a full set of dragon-themed ATC cards and I’m a little bit obsessed with...',
      description: 'Made a full set of dragon-themed ATC cards and I’m a little bit obsessed with how they turned out! Comment which Dragon you are below!🐉✨   #dragonart #fantasyartist #mythicalcreatureart fantasycrafts',
      url: 'https://www.instagram.com/reel/DRX-F77kpmG/',
      embedId: 'DRX-F77kpmG',
      category: 'box-reveal',
      tags: ['dragonart', 'fantasyartist', 'mythicalcreatureart', 'journaling', 'vintage', 'fantasy'],
      creator: 'CoraCreaCrafts',
      date: '2025-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DRX-F77kpmG/media/?size=m'
    },
    {
      id: 'ig-api-129',
      platform: 'instagram',
      title: 'Dreadful weather = Christmas mode on ✂️🎄 Our new Christmas arrivals here, in...',
      description: 'Dreadful weather = Christmas mode on ✂️🎄 Our new Christmas arrivals here, including this Journaling Book with a little bit of everything you need inside to be creative this season 🎁 Available online',
      url: 'https://www.instagram.com/reel/DRSoz9Hkmvj/',
      embedId: 'DRSoz9Hkmvj',
      category: 'product-showcase',
      tags: ['creativejournaling', 'scrapbooksupplies', 'christmasmagic', 'journalinginspiration', 'stationeryshop', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DRSoz9Hkmvj/media/?size=m'
    },
    {
      id: 'ig-api-130',
      platform: 'instagram',
      title: 'POV: you open the Dragon’s Treasure Box and find our first-ever coloring book...',
      description: 'POV: you open the Dragon’s Treasure Box and find our first-ever coloring book + the prettiest felt tips 🐉✨  #dragonart #coloringbookcommunity #adultcoloring #creativejournaling #stationeryaddict #dra',
      url: 'https://www.instagram.com/reel/DRQgGonjCtz/',
      embedId: 'DRQgGonjCtz',
      category: 'box-reveal',
      tags: ['dragonart', 'coloringbookcommunity', 'adultcoloring', 'creativejournaling', 'stationeryaddict', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DRQgGonjCtz/media/?size=m'
    },
    {
      id: 'ig-api-131',
      platform: 'instagram',
      title: 'This is your sign to pause, unwind, and make the countdown to Christmas a lit...',
      description: 'This is your sign to pause, unwind, and make the countdown to Christmas a little more crafty with our Advent Calendars! P.S. Our small calendar is now sold out! 🎄✂️    #adventcal  #christmascountdown',
      url: 'https://www.instagram.com/reel/DRIVahJkp53/',
      embedId: 'DRIVahJkp53',
      category: 'tutorial',
      tags: ['adventcal', 'christmascountdown', 'countdowntochristmas', 'holidaycountdown', 'diyadventcalendar', 'christmas'],
      creator: 'CoraCreaCrafts',
      date: '2025-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DRIVahJkp53/media/?size=m'
    },
    {
      id: 'ig-api-132',
      platform: 'instagram',
      title: 'Warning: this month’s Vintage Craft Box will make you crave quiet library nig...',
      description: 'Warning: this month’s Vintage Craft Box will make you crave quiet library nights, the scent of old pages, and hours lost in thought! What\'s your favourite item? 📚✨  #coracreacrafts #librarybox #vinta',
      url: 'https://www.instagram.com/reel/DRAswl_kueG/',
      embedId: 'DRAswl_kueG',
      category: 'box-reveal',
      tags: ['coracreacrafts', 'librarybox', 'vintagestationery', 'creativejournaling', 'journalingcommunity', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DRAswl_kueG/media/?size=m'
    },
    {
      id: 'ig-api-133',
      platform: 'instagram',
      title: 'We just packed a huge order of our Advent Calendars for our friends at @atlas...',
      description: 'We just packed a huge order of our Advent Calendars for our friends at @atlasstationers Can you guess how many calendars we packed in these 2 boxes? 👀   #coracreacrafts #adventcalendar #stationeryadd',
      url: 'https://www.instagram.com/reel/DQ7dQqQEhfR/',
      embedId: 'DQ7dQqQEhfR',
      category: 'behind-the-scenes',
      tags: ['coracreacrafts', 'adventcalendar', 'stationeryaddict', 'journalcommunity', 'vintagestationery', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DQ7dQqQEhfR/media/?size=m'
    },
    {
      id: 'ig-api-134',
      platform: 'instagram',
      title: 'So many of you asked… here’s a little behind-the-scenes peek at the making of...',
      description: 'So many of you asked… here’s a little behind-the-scenes peek at the making of our Dream Calendar. Would you like to see more of these? ✨  #dreamcalendar #behindthescenes #craftstudio #handmadewithlove',
      url: 'https://www.instagram.com/reel/DQ1ygWvEb5a/',
      embedId: 'DQ1ygWvEb5a',
      category: 'product-showcase',
      tags: ['dreamcalendar', 'behindthescenes', 'craftstudio', 'handmadewithlove', 'creativeprocess', 'advent calendar'],
      creator: 'CoraCreaCrafts',
      date: '2025-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DQ1ygWvEb5a/media/?size=m'
    },
    {
      id: 'ig-api-135',
      platform: 'instagram',
      title: 'It’s finally here… dragons! 🐉 You’ve been asking for this theme forever',
      description: 'It’s finally here… dragons! 🐉 You’ve been asking for this theme forever!  Fire, scales, wings… all the dragon magic you love in one box. Plus we have something completely new our first coloring book ',
      url: 'https://www.instagram.com/reel/DQxkcJmjPM9/',
      embedId: 'DQxkcJmjPM9',
      category: 'tutorial',
      tags: ['dragonbox', 'dragons', 'craftbox', 'dragonlover', 'mythicalcreatures'],
      creator: 'CoraCreaCrafts',
      date: '2025-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DQxkcJmjPM9/media/?size=m'
    },
    {
      id: 'ig-api-136',
      platform: 'instagram',
      title: 'December 1 is nearly here... If your initial is in this video, tag the friend...',
      description: 'December 1 is nearly here... If your initial is in this video, tag the friend who owes you an advent calendar 😉  #adventcalendar #advent #crafting #handmade #diycrafts #papercraft #slowcrafting #make',
      url: 'https://www.instagram.com/reel/DQpy6IsEvKX/',
      embedId: 'DQpy6IsEvKX',
      category: 'tutorial',
      tags: ['adventcalendar', 'advent', 'crafting', 'handmade', 'diycrafts', 'advent calendar'],
      creator: 'CoraCreaCrafts',
      date: '2025-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DQpy6IsEvKX/media/?size=m'
    },
    {
      id: 'ig-api-137',
      platform: 'instagram',
      title: 'What\'s in Coralie\'s bag? A few everyday favorites you might just recognize 🥰',
      description: 'What\'s in Coralie\'s bag? A few everyday favorites you might just recognize 🥰   #whatsinmybag #founder #nashville #smallbusiness #nashvillesmallbusiness #trending #nashvillestore #crafting #junkjourna',
      url: 'https://www.instagram.com/reel/DQj5BOrEQuQ/',
      embedId: 'DQj5BOrEQuQ',
      category: 'product-showcase',
      tags: ['whatsinmybag', 'founder', 'nashville', 'smallbusiness', 'nashvillesmallbusiness', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DQj5BOrEQuQ/media/?size=m'
    },
    {
      id: 'ig-api-138',
      platform: 'instagram',
      title: 'Happy Halloween! 🎃 To celebrate, we’re running our biggest sale of the year....',
      description: 'Happy Halloween! 🎃 To celebrate, we’re running our biggest sale of the year. This weekend only get 50% off our Halloween Collection. It’s the last chance to grab your favorites before they disappear ',
      url: 'https://www.instagram.com/reel/DQfByFUkbvQ/',
      embedId: 'DQfByFUkbvQ',
      category: 'product-showcase',
      tags: ['coracreacrafts', 'junkjournal', 'artjournaling', 'vintagecrafts', 'halloweensale', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DQfByFUkbvQ/media/?size=m'
    },
    {
      id: 'ig-api-139',
      platform: 'instagram',
      title: 'Who needs to stay calm 😂',
      description: 'Who needs to stay calm 😂  I’ve had so many questions about when these would be back, and now i can finally say… they’re here!   Our mini cabinets are one of those things that just make any craft spac',
      url: 'https://www.instagram.com/reel/DQcFMquEcXl/',
      embedId: 'DQcFMquEcXl',
      category: 'product-showcase',
      tags: ['stationeryaddict', 'journalingcommunity', 'vintagejournals', 'craftstorage', 'papercrafting', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DQcFMquEcXl/media/?size=m'
    },
    {
      id: 'ig-api-140',
      platform: 'instagram',
      title: 'Sometimes you just need a celestial-themed spread...the little window cut out...',
      description: 'Sometimes you just need a celestial-themed spread...the little window cut out feature makes me so happy!🪐✨   #journalingcommunity #scrapbooklove #journalinspiration #papercreatives #vintagejournals #',
      url: 'https://www.instagram.com/reel/DQXi7ckkjJh/',
      embedId: 'DQXi7ckkjJh',
      category: 'journal-spread',
      tags: ['journalingcommunity', 'scrapbooklove', 'journalinspiration', 'papercreatives', 'vintagejournals', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DQXi7ckkjJh/media/?size=m'
    },
    {
      id: 'ig-api-141',
      platform: 'instagram',
      title: 'Last Day to get the Alchemy box! Sometimes you just need to create an alchemy...',
      description: 'Last Day to get the Alchemy box! Sometimes you just need to create an alchemy-themed spread 🧙‍♂️⚗️✨  Have you tried something like this in your journal?    #scrapbooking #journalingcommunity #junkjou',
      url: 'https://www.instagram.com/reel/DQR3D1uEdoN/',
      embedId: 'DQR3D1uEdoN',
      category: 'journal-spread',
      tags: ['scrapbooking', 'journalingcommunity', 'junkjournal', 'vintageephemera', 'papercrafting', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DQR3D1uEdoN/media/?size=m'
    },
    {
      id: 'ig-api-142',
      platform: 'instagram',
      title: 'Come with me to host our creative journaling workshop, inspired by all things...',
      description: 'Come with me to host our creative journaling workshop, inspired by all things fantasy, at Green Hills Mall! 🪄 It was so much fun meeting everyone and seeing your beautiful creations. Stay tuned more ',
      url: 'https://www.instagram.com/reel/DQHP1GmDaro/',
      embedId: 'DQHP1GmDaro',
      category: 'product-showcase',
      tags: ['creativejournaling', 'fantasyjournaling', 'journalingworkshop', 'coracreacrafts', 'craftcommunity', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DQHP1GmDaro/media/?size=m'
    },
    {
      id: 'ig-api-143',
      platform: 'instagram',
      title: 'Warning! Spoilers Included in this video ⚠️I couldn’t resist showing a few pe...',
      description: 'Warning! Spoilers Included in this video ⚠️I couldn’t resist showing a few peeks of our Advent Calendar �❤️� It’s packed with the cutest treasures I know you’ll love!  Tag someone who needs one before',
      url: 'https://www.instagram.com/reel/DP7f376DENZ/',
      embedId: 'DP7f376DENZ',
      category: 'behind-the-scenes',
      tags: ['adventcalendar', 'journalingcommunity', 'vintagestationery', 'junkjournal', 'creativejournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DP7f376DENZ/media/?size=m'
    },
    {
      id: 'ig-api-144',
      platform: 'instagram',
      title: 'I fell in love with so many of our Halloween items...honestly, it was hard to...',
      description: 'I fell in love with so many of our Halloween items...honestly, it was hard to choose what to include in this spread. Which one do you love most? 🎃👀  #halloweenscrapbook #junkjournal #vintagecrafts #',
      url: 'https://www.instagram.com/reel/DP1n1VIkcSs/',
      embedId: 'DP1n1VIkcSs',
      category: 'journal-spread',
      tags: ['halloweenscrapbook', 'junkjournal', 'vintagecrafts', 'papercrafting', 'scrapbookingideas', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DP1n1VIkcSs/media/?size=m'
    },
    {
      id: 'ig-api-145',
      platform: 'instagram',
      title: 'I’ve always been drawn to the night sky, there’s something so peaceful and my...',
      description: 'I’ve always been drawn to the night sky, there’s something so peaceful and mysterious about it ✨  This month’s Vintage Craft Box is inspired by that feeling… celestial details, golden touches, and a l',
      url: 'https://www.instagram.com/reel/DPwqgU8ESo_/',
      embedId: 'DPwqgU8ESo_',
      category: 'box-reveal',
      tags: ['craftbox', 'subscriptionbox', 'handmade', 'vintage', 'smallbusiness', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2025-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DPwqgU8ESo_/media/?size=m'
    },
    {
      id: 'ig-api-146',
      platform: 'instagram',
      title: 'I didn’t expect a rolling pin to become one of my favorite crafting tools 😂 ...',
      description: 'I didn’t expect a rolling pin to become one of my favorite crafting tools 😂 Loving the color and texture on this one, the embossing turned out so nicely with our English Manor collection! What’s the ',
      url: 'https://www.instagram.com/reel/DPr9383kc7D/',
      embedId: 'DPr9383kc7D',
      category: 'tutorial',
      tags: ['englishmanor', 'vintagecrafts', 'crafttutorial', 'embossing', 'stationerylove', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DPr9383kc7D/media/?size=m'
    },
    {
      id: 'ig-api-147',
      platform: 'instagram',
      title: 'It’s officially spooky crafting season 👻 Here’s a peek at some of my favorit...',
      description: 'It’s officially spooky crafting season 👻 Here’s a peek at some of my favorite Halloween items this year. Which one would you pick? ✨️  #halloween #spookycrafting #crafting #creative #nashville #nashv',
      url: 'https://www.instagram.com/reel/DPmQf-UES8O/',
      embedId: 'DPmQf-UES8O',
      category: 'product-showcase',
      tags: ['halloween', 'spookycrafting', 'crafting', 'creative', 'nashville'],
      creator: 'CoraCreaCrafts',
      date: '2025-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DPmQf-UES8O/media/?size=m'
    },
    {
      id: 'ig-api-148',
      platform: 'instagram',
      title: 'Our 2025 Advent Calendars are finally here! 😍✨ I’ve been so excited to share...',
      description: 'Our 2025 Advent Calendars are finally here! 😍✨ I’ve been so excited to share this with you. December is about to get a little magical, and I can’t wait for you to see everything we’ve been working on',
      url: 'https://www.instagram.com/reel/DPhlEldEoZa/',
      embedId: 'DPhlEldEoZa',
      category: 'box-reveal',
      tags: ['adventcalendar', 'christmascountdown', 'holidayseason', 'festivevibes', 'handmadeholiday', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DPhlEldEoZa/media/?size=m'
    },
    {
      id: 'ig-api-149',
      platform: 'instagram',
      title: 'Introducing our alchemy box ✨️🛸 inspired by my long-time fascination with tr...',
      description: 'Introducing our alchemy box ✨️🛸 inspired by my long-time fascination with transformation, symbolism, and a little bit of magic. think ancient manuscripts, celestial influences, and the mix of science',
      url: 'https://www.instagram.com/reel/DPeo-ZlkcsV/',
      embedId: 'DPeo-ZlkcsV',
      category: 'box-reveal',
      tags: ['coracreacrafts', 'alchemybox', 'journaling', 'stationeryaddict', 'vintagestationery', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DPeo-ZlkcsV/media/?size=m'
    },
    {
      id: 'ig-api-150',
      platform: 'instagram',
      title: 'This weekend is my birthday! 🎈 I’m so grateful to be able to celebrate it wi...',
      description: 'This weekend is my birthday! 🎈 I’m so grateful to be able to celebrate it with a community that has supported me in so many ways. To say thank you, I’ve added a few little surprises in the shop this ',
      url: 'https://www.instagram.com/reel/DPajmACjOYz/',
      embedId: 'DPajmACjOYz',
      category: 'product-showcase',
      tags: ['birthdaysale', 'shopsmall', 'birthdaytreat', 'shopbirthday', 'smallbusinesslove', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DPajmACjOYz/media/?size=m'
    },
    {
      id: 'ig-api-151',
      platform: 'instagram',
      title: 'I’m still obsessed with the little treats from the dream calendar ✨ They make...',
      description: 'I’m still obsessed with the little treats from the dream calendar ✨ They make journaling so much fun and did you know you can now you can shop them individually! 🤍  #coracreacrafts #dreamcalendar #jo',
      url: 'https://www.instagram.com/reel/DPPLBFHCBJg/',
      embedId: 'DPPLBFHCBJg',
      category: 'journal-spread',
      tags: ['coracreacrafts', 'dreamcalendar', 'journaling', 'stationeryaddict', 'plannercommunity', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2025-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DPPLBFHCBJg/media/?size=m'
    },
    {
      id: 'ig-api-152',
      platform: 'instagram',
      title: 'The first peek at our Halloween tapes is here! I’ve been playing with them al...',
      description: 'The first peek at our Halloween tapes is here! I’ve been playing with them all week, and honestly, these might be the best ones we’ve ever made 👻 Which one\'s your favorite? 👀   #coracreacrafts #hall',
      url: 'https://www.instagram.com/reel/DPM8BFHCZgO/',
      embedId: 'DPM8BFHCZgO',
      category: 'journal-spread',
      tags: ['coracreacrafts', 'halloweentapes', 'halloweenstationery', 'halloweenvibes', 'spookyjournaling', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2025-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DPM8BFHCZgO/media/?size=m'
    },
    {
      id: 'ig-api-153',
      platform: 'instagram',
      title: 'This is our most asked about pen 👀 Here’s exactly how to use it so your jour...',
      description: 'This is our most asked about pen 👀 Here’s exactly how to use it so your journaling feels effortless🖋️✨  #coracreacrafts #fountainpen #journaling #pentok #stationeryaddict #vintagestationery #journal',
      url: 'https://www.instagram.com/reel/DPElqZIiMF7/',
      embedId: 'DPElqZIiMF7',
      category: 'journal-spread',
      tags: ['coracreacrafts', 'fountainpen', 'journaling', 'pentok', 'stationeryaddict', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DPElqZIiMF7/media/?size=m'
    },
    {
      id: 'ig-api-154',
      platform: 'instagram',
      title: 'Wandering into the Fairyworld one last time… this spread has all the little t...',
      description: 'Wandering into the Fairyworld one last time… this spread has all the little touches that made this theme so special to me. Just a little reminder, the Fairyworld Treasure Box will only be available un',
      url: 'https://www.instagram.com/reel/DPCTm0-iPyf/',
      embedId: 'DPCTm0-iPyf',
      category: 'journal-spread',
      tags: ['fairyjournal', 'whimsicalspreads', 'vintagejournaling', 'journalart', 'creativejournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DPCTm0-iPyf/media/?size=m'
    },
    {
      id: 'ig-api-155',
      platform: 'instagram',
      title: 'when your husband picks your old journal for a flip-through… throwback to thi...',
      description: 'when your husband picks your old journal for a flip-through… throwback to this journal I filled in 2022/2023!  I loved rediscovering these pages so had to share ✨   Which one’s your favorite? 🖊️📖   ',
      url: 'https://www.instagram.com/reel/DO9ZL1vEufC/',
      embedId: 'DO9ZL1vEufC',
      category: 'product-showcase',
      tags: ['vintagejournal', 'journalflip', 'memorylane', 'creativemoments', 'handmadewithlove', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DO9ZL1vEufC/media/?size=m'
    },
    {
      id: 'ig-api-156',
      platform: 'instagram',
      title: 'Just unboxed the Alchemy Curiosities Box for the first time and wow! Every pi...',
      description: 'Just unboxed the Alchemy Curiosities Box for the first time and wow! Every piece feels like a little secret waiting to be discovered gold, moon, potions, and symbols of transformation. This one’s for ',
      url: 'https://www.instagram.com/reel/DO4fOIXDFn-/',
      embedId: 'DO4fOIXDFn-',
      category: 'unboxing',
      tags: ['vintagecraft', 'alchemybox', 'papercrafts', 'handmadecrafts', 'craftingcommunity', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DO4fOIXDFn-/media/?size=m'
    },
    {
      id: 'ig-api-157',
      platform: 'instagram',
      title: 'Only a handful of seats remain for our fantasy-inspired creative journaling w...',
      description: 'Only a handful of seats remain for our fantasy-inspired creative journaling workshop!  ✨Sept 27 · 12:30–2PM  ✨The Mall at Green Hills, Nashville @mallatgreenhills  An afternoon of hands-on creativity ',
      url: 'https://www.instagram.com/reel/DOzMNhrEdi1/',
      embedId: 'DOzMNhrEdi1',
      category: 'tutorial',
      tags: ['creativejournaling', 'fantasyjournal', 'mixedmediaart', 'booklovers', 'journalingworkshop', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DOzMNhrEdi1/media/?size=m'
    },
    {
      id: 'ig-api-158',
      platform: 'instagram',
      title: 'This spread feels like stepping into the world of French royalty, sprays of b...',
      description: 'This spread feels like stepping into the world of French royalty, sprays of blue, rich details, and that quiet sense of grandeur I’ve always been drawn to. Growing up, I spent so much time surrounded ',
      url: 'https://www.instagram.com/reel/DOrTsnjka4u/',
      embedId: 'DOrTsnjka4u',
      category: 'journal-spread',
      tags: ['journaling', 'scrapbooking', 'junkjournal', 'creativejournal', 'journalspread', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DOrTsnjka4u/media/?size=m'
    },
    {
      id: 'ig-api-159',
      platform: 'instagram',
      title: 'Part 2 of my journal flip through is here! Thank you for all your lovely comm...',
      description: 'Part 2 of my journal flip through is here! Thank you for all your lovely comments on part 1. I\'d love to know what do you do with your old journals? 🤍  #journalflipthrough #journalingcommunity #creat',
      url: 'https://www.instagram.com/reel/DOmWZLFER3T/',
      embedId: 'DOmWZLFER3T',
      category: 'product-showcase',
      tags: ['journalflipthrough', 'journalingcommunity', 'creativejournaling', 'artjournal', 'bujo', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DOmWZLFER3T/media/?size=m'
    },
    {
      id: 'ig-api-160',
      platform: 'instagram',
      title: 'Fairy World is here! 🧚',
      description: 'Fairy World is here! 🧚  This month’s VCB is inspired by enchanted forests, shimmering wings, and hidden realms!  Every piece inside is designed to feel like you’ve stumbled across something magical, ',
      url: 'https://www.instagram.com/reel/DOenq9ICXw3/',
      embedId: 'DOenq9ICXw3',
      category: 'box-reveal',
      tags: ['fairyworld', 'fairycore', 'vintagecraftbox', 'journalingcommunity', 'stationeryaddict', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DOenq9ICXw3/media/?size=m'
    },
    {
      id: 'ig-api-161',
      platform: 'instagram',
      title: 'A Fantasy Inspired Creative Workshop for Book Lovers 📚🗡️ Join us at the Gre...',
      description: 'A Fantasy Inspired Creative Workshop for Book Lovers 📚🗡️ Join us at the Green Hills Mall in Nashville with your bestie for a super fun hands-on class including $120 of creative supplies + themed dri',
      url: 'https://www.instagram.com/reel/DOZnwfpDMqt/',
      embedId: 'DOZnwfpDMqt',
      category: 'product-showcase',
      tags: ['journalinginspiration', 'fantasyart', 'fantasybooks', 'creativejournal', 'nashvilletn', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DOZnwfpDMqt/media/?size=m'
    },
    {
      id: 'ig-api-162',
      platform: 'instagram',
      title: 'Our Dark Gothic Collection is here! 🖤',
      description: 'Our Dark Gothic Collection is here! 🖤   To celebrate, I had to bring back one of my favorite Halloween spreads from last year. Creating these collections is always such a fun process, and this time i',
      url: 'https://www.instagram.com/reel/DOUQa6CEq6a/',
      embedId: 'DOUQa6CEq6a',
      category: 'journal-spread',
      tags: ['darkgothiccollection', 'halloweenjournaling', 'spookyseason', 'junkjournaling', 'journalcommunity', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DOUQa6CEq6a/media/?size=m'
    },
    {
      id: 'ig-api-163',
      platform: 'instagram',
      title: 'Pulled out an old journal today and got lost in the pages for a while. It’s f...',
      description: 'Pulled out an old journal today and got lost in the pages for a while. It’s funny how some ideas stick and some fade, but every page tells a story of that moment. Would you like to see part 2? 👀  #vi',
      url: 'https://www.instagram.com/reel/DOMgARJkvF8/',
      embedId: 'DOMgARJkvF8',
      category: 'product-showcase',
      tags: ['vintagejournal', 'junkjournal', 'vintagecraft', 'ephemera', 'artjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DOMgARJkvF8/media/?size=m'
    },
    {
      id: 'ig-api-164',
      platform: 'instagram',
      title: 'I couldn’t wait to share our English Manors Treasure Box with you all! Inspir...',
      description: 'I couldn’t wait to share our English Manors Treasure Box with you all! Inspired by the quiet charm of the countryside, it brings together the elegance of old estates, timeless manor interiors, and the',
      url: 'https://www.instagram.com/reel/DOHjIacDDYN/',
      embedId: 'DOHjIacDDYN',
      category: 'unboxing',
      tags: ['stationeryaddict', 'creativejournaling', 'vintagevibes', 'englishcountryside', 'journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DOHjIacDDYN/media/?size=m'
    },
    {
      id: 'ig-api-165',
      platform: 'instagram',
      title: 'Tonight\'s Live Sale was so fun! Thank you everyone for joining. ✨✨',
      description: 'Tonight\'s Live Sale was so fun! Thank you everyone for joining. ✨✨',
      url: 'https://www.instagram.com/reel/DOCdswhDGi7/',
      embedId: 'DOCdswhDGi7',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2025-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DOCdswhDGi7/media/?size=m'
    },
    {
      id: 'ig-api-166',
      platform: 'instagram',
      title: 'Chasing dreams can feel hard, but putting them on paper makes them real. I sp...',
      description: 'Chasing dreams can feel hard, but putting them on paper makes them real. I spent some time using items from our Dream Calendar for creative journaling, it’s so satisfying to see ideas, papers, and was',
      url: 'https://www.instagram.com/reel/DN9EujtEuHd/',
      embedId: 'DN9EujtEuHd',
      category: 'product-showcase',
      tags: ['creativejournaling', 'dreamjournal', 'chasingdreams', 'dreamonpaper', 'journalwithme', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2025-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DN9EujtEuHd/media/?size=m'
    },
    {
      id: 'ig-api-167',
      platform: 'instagram',
      title: 'From January through August, each Vintage Craft Box has felt like its own lit...',
      description: 'From January through August, each Vintage Craft Box has felt like its own little world and it’s been such a joy seeing how you’ve brought those themes to life. Today, a brand new one joins the collect',
      url: 'https://www.instagram.com/reel/DN3bzBi4oGZ/',
      embedId: 'DN3bzBi4oGZ',
      category: 'unboxing',
      tags: ['vintagecraftbox', 'coracreacrafts', 'junkjournal', 'journalinspiration', 'creativejournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DN3bzBi4oGZ/media/?size=m'
    },
    {
      id: 'ig-api-168',
      platform: 'instagram',
      title: 'I love what I do so much, sometimes I get lost in creating and don’t want to ...',
      description: 'I love what I do so much, sometimes I get lost in creating and don’t want to stop. But honestly, I wouldn’t have it any other way! I’m so thankful every day to share this passion with all of you and f',
      url: 'https://www.instagram.com/reel/DNvtNyh4hAI/',
      embedId: 'DNvtNyh4hAI',
      category: 'product-showcase',
      tags: ['smallbusinesslove', 'makersgonnamake', 'creativejourney', 'founderlife', 'handmadewithlove'],
      creator: 'CoraCreaCrafts',
      date: '2025-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DNvtNyh4hAI/media/?size=m'
    },
    {
      id: 'ig-api-169',
      platform: 'instagram',
      title: 'SPOILER ALERT 🚨 I’m still loving every bit of the Mid-Year Dream Calendar, s...',
      description: 'SPOILER ALERT 🚨 I’m still loving every bit of the Mid-Year Dream Calendar, so I wanted to show how I’ve been using a few of the pieces inside! I designed this to bring a little more intention (and fu',
      url: 'https://www.instagram.com/reel/DNrAVrt5DuO/',
      embedId: 'DNrAVrt5DuO',
      category: 'unboxing',
      tags: ['journalingcommunity', 'creativejournaling', 'stationerylove', 'junkjournalideas', 'calendarunboxing', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DNrAVrt5DuO/media/?size=m'
    },
    {
      id: 'ig-api-170',
      platform: 'instagram',
      title: 'This little habit has made such a difference in my days, just ten quiet minut...',
      description: 'This little habit has made such a difference in my days, just ten quiet minutes to reflect, unwind, and let my thoughts land on paper. It’s never about making something perfect, just about showing up ',
      url: 'https://www.instagram.com/reel/DNn4vOexh0D/',
      embedId: 'DNn4vOexh0D',
      category: 'product-showcase',
      tags: ['journalingroutine', 'mindfulmoment', 'creativejournaling', 'craftingjoy', 'journalinspo', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DNn4vOexh0D/media/?size=m'
    },
    {
      id: 'ig-api-171',
      platform: 'instagram',
      title: 'Ever wonder what journaling is really about',
      description: 'Ever wonder what journaling is really about? To me, it’s not just writing words, it’s creating a space where you can be yourself. No rules, no pressure, just your own kind of creativity and thoughts. ',
      url: 'https://www.instagram.com/reel/DNgS4S-RwCv/',
      embedId: 'DNgS4S-RwCv',
      category: 'product-showcase',
      tags: ['creativejournaling', 'journaling', 'journalinspiration', 'junkjournal', 'artjournal', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DNgS4S-RwCv/media/?size=m'
    },
    {
      id: 'ig-api-172',
      platform: 'instagram',
      title: 'One of the best parts of running a small business is hearing your kind words ...',
      description: 'One of the best parts of running a small business is hearing your kind words about our products! All of your reviews truly mean the world and keep me going every day. Thank you for supporting us ✨💗 .',
      url: 'https://www.instagram.com/reel/DNdx8_ShG-u/',
      embedId: 'DNdx8_ShG-u',
      category: 'product-showcase',
      tags: ['smallbusiness', 'customerlove', 'handmadewithlove', 'supportlocalbusiness', 'creativecommunity'],
      creator: 'CoraCreaCrafts',
      date: '2025-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DNdx8_ShG-u/media/?size=m'
    },
    {
      id: 'ig-api-173',
      platform: 'instagram',
      title: 'When I was putting together our travel journal kit, I wanted to create someth...',
      description: 'When I was putting together our travel journal kit, I wanted to create something that feels like a little adventure in a box. It’s perfect for anyone who loves capturing memories and ideas on the go. ',
      url: 'https://www.instagram.com/reel/DNbWabcpq4h/',
      embedId: 'DNbWabcpq4h',
      category: 'product-showcase',
      tags: ['traveljournal', 'journalcommunity', 'handmadewithlove', 'creativejournaling', 'smallbusinesslove', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DNbWabcpq4h/media/?size=m'
    },
    {
      id: 'ig-api-174',
      platform: 'instagram',
      title: '🎄They’re back but with a new version, too! ✨ 2025 Advent Calendars now in tw...',
      description: '🎄They’re back but with a new version, too! ✨ 2025 Advent Calendars now in two sizes (yes, we listened 😉) Your beloved 2023 and 2024 large format along with a newer, cuter, smaller version that’s jus',
      url: 'https://www.instagram.com/reel/DNY8xbMSSWx/',
      embedId: 'DNY8xbMSSWx',
      category: 'behind-the-scenes',
      tags: ['adventcalender', 'preorder', 'christmas', 'gifting', 'crafting', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DNY8xbMSSWx/media/?size=m'
    },
    {
      id: 'ig-api-175',
      platform: 'instagram',
      title: 'Taking a little trip to versailles with this spread I made using some treasur...',
      description: 'Taking a little trip to versailles with this spread I made using some treasures from our Royal France Vintage Craft Box. There’s something about the elegance of this and the ornate details that really',
      url: 'https://www.instagram.com/reel/DNQ1L-hRNky/',
      embedId: 'DNQ1L-hRNky',
      category: 'unboxing',
      tags: ['vintagecraftbox', 'royalfrance', 'versaillesspread', 'journalingcommunity', 'artjournalinspo', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DNQ1L-hRNky/media/?size=m'
    },
    {
      id: 'ig-api-176',
      platform: 'instagram',
      title: 'There’s something about vintage ships and ocean blues that feels like a quiet...',
      description: 'There’s something about vintage ships and ocean blues that feels like a quiet pause, like the calm before the change of seasons. August is still summer, but you can feel autumn inching in. I’ve been h',
      url: 'https://www.instagram.com/reel/DNLp4PipPF_/',
      embedId: 'DNLp4PipPF_',
      category: 'journal-spread',
      tags: ['vintagejournaling', 'shipaesthetic', 'oceanvibes', 'journalingcommunity', 'craftwithme', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DNLp4PipPF_/media/?size=m'
    },
    {
      id: 'ig-api-177',
      platform: 'instagram',
      title: 'You asked and you shall receive! Here are some of my favorite pieces from the...',
      description: 'You asked and you shall receive! Here are some of my favorite pieces from the Legends & Lore: An Astronomer’s Legacy in action! I can’t wait to hear what you think ✨ . . #fantasyart #mythologyart #mag',
      url: 'https://www.instagram.com/reel/DNG5495h5qU/',
      embedId: 'DNG5495h5qU',
      category: 'product-showcase',
      tags: ['fantasyart', 'mythologyart', 'magicaljournaling', 'astrologyart', 'cosmicjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DNG5495h5qU/media/?size=m'
    },
    {
      id: 'ig-api-178',
      platform: 'instagram',
      title: 'Have you tried our new pastes yet? Watching the colors swirl and textures pop...',
      description: 'Have you tried our new pastes yet? Watching the colors swirl and textures pop is seriously addictive. These little jars are full of creative magic and instantly make any project come to life! Give the',
      url: 'https://www.instagram.com/reel/DNBQ9lysoCf/',
      embedId: 'DNBQ9lysoCf',
      category: 'box-reveal',
      tags: ['vintagecraftbox', 'creativepastes', 'artjournaling', 'mixedmediaart', 'craftsupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DNBQ9lysoCf/media/?size=m'
    },
    {
      id: 'ig-api-179',
      platform: 'instagram',
      title: 'August’s vintage craft box is here and it’s a special one... meet Royal Franc...',
      description: 'August’s vintage craft box is here and it’s a special one... meet Royal France! Some of you may know that I grew up in the french-speaking part of Switzerland, so French history and culture were alway',
      url: 'https://www.instagram.com/reel/DM8QxTqR8WU/',
      embedId: 'DM8QxTqR8WU',
      category: 'unboxing',
      tags: ['vintagecraftbox', 'royalfrance', 'frenchinspired', 'journalingcommunity', 'creativejournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DM8QxTqR8WU/media/?size=m'
    },
    {
      id: 'ig-api-180',
      platform: 'instagram',
      title: 'There’s something about blending blues, pinks, and yellows that feels like me...',
      description: 'There’s something about blending blues, pinks, and yellows that feels like mermaid magic to me. Creating this spread took me somewhere peaceful and inspiring, like slipping beneath the waves to a quie',
      url: 'https://www.instagram.com/reel/DM5zFwYJsps/',
      embedId: 'DM5zFwYJsps',
      category: 'journal-spread',
      tags: ['mermaidjournal', 'oceanvibes', 'journalingcommunity', 'creativejournaling', 'stationerylove', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DM5zFwYJsps/media/?size=m'
    },
    {
      id: 'ig-api-181',
      platform: 'instagram',
      title: 'To celebrate our End of July Flash Sale (up to 20% off sitewide until midnigh...',
      description: 'To celebrate our End of July Flash Sale (up to 20% off sitewide until midnight!), we’re giving one lucky winner the chance to win the ultimate craft box, packed with vintage treasures! 🎁   How to ent',
      url: 'https://www.instagram.com/reel/DMxzBRls4L6/',
      embedId: 'DMxzBRls4L6',
      category: 'tutorial',
      tags: ['giveaway', 'crafts', 'handmade', 'diy', 'crafting', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DMxzBRls4L6/media/?size=m'
    },
    {
      id: 'ig-api-182',
      platform: 'instagram',
      title: 'There’s nothing better than finding an old box and turning it into something ...',
      description: 'There’s nothing better than finding an old box and turning it into something beautiful. Upcycling is one of my favorite ways to craft with meaning and the lock & key pieces from our Curiosities Box we',
      url: 'https://www.instagram.com/reel/DMtMPC0SvR4/',
      embedId: 'DMtMPC0SvR4',
      category: 'tutorial',
      tags: ['upcycling', 'upcycle', 'upcyclingdiy', 'diy', 'thriftflip'],
      creator: 'CoraCreaCrafts',
      date: '2025-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DMtMPC0SvR4/media/?size=m'
    },
    {
      id: 'ig-api-183',
      platform: 'instagram',
      title: 'My favorite thing about a travel journal is that it’s not just for one trip, ...',
      description: 'My favorite thing about a travel journal is that it’s not just for one trip, it’s a place to collect memories from many adventures. I set up my new Travel Journal Kit just before heading to Europe and',
      url: 'https://www.instagram.com/reel/DMlPXJ_JEOi/',
      embedId: 'DMlPXJ_JEOi',
      category: 'tutorial',
      tags: ['traveljournal', 'journalingcommunity', 'creativejournaling', 'bulletjournalideas', 'travelmemories', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DMlPXJ_JEOi/media/?size=m'
    },
    {
      id: 'ig-api-184',
      platform: 'instagram',
      title: 'Sometimes the best spreads aren’t perfect, they’re a little messy, full of te...',
      description: 'Sometimes the best spreads aren’t perfect, they’re a little messy, full of texture and character. That’s why I love using our Archive Sprays to build unique backgrounds on my spreads and to add unexpe',
      url: 'https://www.instagram.com/reel/DMgTWsyyPRi/',
      embedId: 'DMgTWsyyPRi',
      category: 'journal-spread',
      tags: ['journaling', 'mixedmediaart', 'crafttok', 'bulletjournal', 'arttutorial'],
      creator: 'CoraCreaCrafts',
      date: '2025-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DMgTWsyyPRi/media/?size=m'
    },
    {
      id: 'ig-api-185',
      platform: 'instagram',
      title: 'It’s finally here…',
      description: 'It’s finally here… The second edition of Legends & Lore: The Fantasy Collection has landed, The Astronomer’s Legacy. We’ve once again teamed up with the wonderfully talented @storm_sparrow to craft a ',
      url: 'https://www.instagram.com/reel/DMarTEbxBUe/',
      embedId: 'DMarTEbxBUe',
      category: 'box-reveal',
      tags: ['fantasy', 'bookstagram', 'stationery', 'reading', 'limitededition', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DMarTEbxBUe/media/?size=m'
    },
    {
      id: 'ig-api-186',
      platform: 'instagram',
      title: 'I find so much inspiration for my spreads when I change my surroundings, whet...',
      description: 'I find so much inspiration for my spreads when I change my surroundings, whether it’s a cozy coffee shop or a quiet park. Getting out of my usual space feels like a creative reset: a chance to breathe',
      url: 'https://www.instagram.com/reel/DMVw4D-S21N/',
      embedId: 'DMVw4D-S21N',
      category: 'journal-spread',
      tags: ['creativeboost', 'journalingcommunity', 'creativeprocess', 'craftingideas', 'spreadinspo', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DMVw4D-S21N/media/?size=m'
    },
    {
      id: 'ig-api-187',
      platform: 'instagram',
      title: 'There’s a unique kind of beauty in academia-inspired spreads that I always co...',
      description: 'There’s a unique kind of beauty in academia-inspired spreads that I always come back to. Whether it’s the rich, moody feel of dark academia or the soft, sunlit calm of summer academia, both hold a qui',
      url: 'https://www.instagram.com/reel/DMQ0g5dSq0h/',
      embedId: 'DMQ0g5dSq0h',
      category: 'journal-spread',
      tags: ['darkacademia', 'academia', 'journalinspo', 'journalingcommunity', 'artjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DMQ0g5dSq0h/media/?size=m'
    },
    {
      id: 'ig-api-188',
      platform: 'instagram',
      title: 'Our Scotland items are finally restocked! This theme has been one of your fav...',
      description: 'Our Scotland items are finally restocked! This theme has been one of your favorites this year, and honestly, hearing your stories has been the best part. Some of you told me it reminded you of home, o',
      url: 'https://www.instagram.com/reel/DMN0sbBMKTR/',
      embedId: 'DMN0sbBMKTR',
      category: 'journal-spread',
      tags: ['scotland', 'scotlandtravel', 'scottishcraft', 'traveljournal', 'scrapbooking', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DMN0sbBMKTR/media/?size=m'
    },
    {
      id: 'ig-api-189',
      platform: 'instagram',
      title: 'Here’s a closer look at the packaging of our mid-year dream calendar. Honestl...',
      description: 'Here’s a closer look at the packaging of our mid-year dream calendar. Honestly, I was so excited when it arrived, the little boxes are seriously one of my favorite parts. I’m already thinking about ho',
      url: 'https://www.instagram.com/reel/DMIzLDXpZ1V/',
      embedId: 'DMIzLDXpZ1V',
      category: 'tutorial',
      tags: ['journaling', 'scrapbooking', 'crafting', 'creative', 'diy', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DMIzLDXpZ1V/media/?size=m'
    },
    {
      id: 'ig-api-190',
      platform: 'instagram',
      title: 'This is my first outer space spread using supplies from our Vintage Craft Box...',
      description: 'This is my first outer space spread using supplies from our Vintage Craft Box. I had so much fun bringing this one to life, especially using our new Archive Sprays to add some beautiful color and text',
      url: 'https://www.instagram.com/reel/DMDq7WfJrnk/',
      embedId: 'DMDq7WfJrnk',
      category: 'journal-spread',
      tags: ['spaceart', 'artjournal', 'creativejournaling', 'junkjournal', 'vintagecrafts', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DMDq7WfJrnk/media/?size=m'
    },
    {
      id: 'ig-api-191',
      platform: 'instagram',
      title: 'Me vs. the art I create…',
      description: 'Me vs. the art I create… It doesn’t always turn out how I pictured it, but that’s what I love most about the process and why I keep creating ✨ . . #artjournal #creativejournaling #junkjournal #vintage',
      url: 'https://www.instagram.com/reel/DL-0YGaSA5H/',
      embedId: 'DL-0YGaSA5H',
      category: 'product-showcase',
      tags: ['artjournal', 'creativejournaling', 'junkjournal', 'vintagecrafts', 'mixedmedia', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DL-0YGaSA5H/media/?size=m'
    },
    {
      id: 'ig-api-192',
      platform: 'instagram',
      title: 'Just shared a quick tutorial for one of my favourite easy card designs, a cen...',
      description: 'Just shared a quick tutorial for one of my favourite easy card designs, a centre fold card. This one’s navigation-themed, which feels perfect for summer. It looks impressive but comes together surpris',
      url: 'https://www.instagram.com/reel/DL730uosCj-/',
      embedId: 'DL730uosCj-',
      category: 'tutorial',
      tags: ['vintagecrafts', 'vintagejournal', 'vintageaesthetic', 'junkjournal', 'paperephemera', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DL730uosCj-/media/?size=m'
    },
    {
      id: 'ig-api-193',
      platform: 'instagram',
      title: 'I’ve been wanting to do a locks & keys theme for ages and it finally came tog...',
      description: 'I’ve been wanting to do a locks & keys theme for ages and it finally came together in this month’s Curiosities Box! It’s filled with antique keys, ornate locks, and little touches that feel like they’',
      url: 'https://www.instagram.com/reel/DL21C4Rp0DD/',
      embedId: 'DL21C4Rp0DD',
      category: 'unboxing',
      tags: ['journalingcommunity', 'creativejournaling', 'vintageaesthetic', 'bulletjournaladdict', 'artjournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DL21C4Rp0DD/media/?size=m'
    },
    {
      id: 'ig-api-194',
      platform: 'instagram',
      title: 'I finally had time to sit down with our new Academia supplies this weekend an...',
      description: 'I finally had time to sit down with our new Academia supplies this weekend and made my first spread with them! I love how it turned out a little moody but still bright, with those little pops of orang',
      url: 'https://www.instagram.com/reel/DL0rNjIymDS/',
      embedId: 'DL0rNjIymDS',
      category: 'journal-spread',
      tags: ['darkacademia', 'summeracademia', 'moodyaesthetic', 'journaling', 'journalspread', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DL0rNjIymDS/media/?size=m'
    },
    {
      id: 'ig-api-195',
      platform: 'instagram',
      title: 'Our 4th of July sale is here! 🇺🇸',
      description: 'Our 4th of July sale is here! 🇺🇸 I wanted to do somethig special for this year so from now through Sunday, you’ll find: ✨Up to 30% off sitewide ✨ Buy 2, Get 1 Free on all stamps & stickers ✨30% off ',
      url: 'https://www.instagram.com/reel/DLsRMxXMqkN/',
      embedId: 'DLsRMxXMqkN',
      category: 'product-showcase',
      tags: ['4thofjuly', 'independenceday', 'holidaysale', 'starsandstripes', 'stationeryaddict', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DLsRMxXMqkN/media/?size=m'
    },
    {
      id: 'ig-api-196',
      platform: 'instagram',
      title: 'Our first fan-voted Vintage Craft Box of 2025 is here… and it’s out of this w...',
      description: 'Our first fan-voted Vintage Craft Box of 2025 is here… and it’s out of this world! Fun fact: I filmed it forgetting the clear stamps! 😅 So those will also be part of it. This Outer Space theme has be',
      url: 'https://www.instagram.com/reel/DLqBB6kJOX2/',
      embedId: 'DLqBB6kJOX2',
      category: 'box-reveal',
      tags: ['spaceart', 'spaceaesthetic', 'galaxy', 'astronomy', 'craftbox', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DLqBB6kJOX2/media/?size=m'
    },
    {
      id: 'ig-api-197',
      platform: 'instagram',
      title: 'I never set out to make ASMR videos… but turns out, my craft table is full of...',
      description: 'I never set out to make ASMR videos… but turns out, my craft table is full of oddly satisfying sounds. Here are a few I come back to again and again! What’s your favorite sound? . . #asmr #asmrsounds ',
      url: 'https://www.instagram.com/reel/DLkyl-KJrSp/',
      embedId: 'DLkyl-KJrSp',
      category: 'product-showcase',
      tags: ['asmr', 'asmrsounds', 'oddlysatisfying', 'craftasmr', 'asmrcrafts', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DLkyl-KJrSp/media/?size=m'
    },
    {
      id: 'ig-api-198',
      platform: 'instagram',
      title: 'For me, sundays are about slowing down completely, finding quiet moments, and...',
      description: 'For me, sundays are about slowing down completely, finding quiet moments, and spending time with my journals to feel creative and truly unwind. It’s simple, but it helps me reset and feel ready for th',
      url: 'https://www.instagram.com/reel/DLfmQQYJhIk/',
      embedId: 'DLfmQQYJhIk',
      category: 'product-showcase',
      tags: ['journaling', 'selfcare', 'mindfulness', 'creativity', 'artjournaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DLfmQQYJhIk/media/?size=m'
    },
    {
      id: 'ig-api-199',
      platform: 'instagram',
      title: 'Just a spontaneous little sneak peek at something I’ve been working on behind...',
      description: 'Just a spontaneous little sneak peek at something I’ve been working on behind the scenes… that JUST arrived 😍 Our new Travel Journals & Kits are here! I’ll be launching them during tomorrow’s live sa',
      url: 'https://www.instagram.com/reel/DLc6n2iMO3P/',
      embedId: 'DLc6n2iMO3P',
      category: 'box-reveal',
      tags: ['traveljournal', 'travelersnotebook', 'journaling', 'journal', 'bulletjournal', 'stickers'],
      creator: 'CoraCreaCrafts',
      date: '2025-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DLc6n2iMO3P/media/?size=m'
    },
    {
      id: 'ig-api-200',
      platform: 'instagram',
      title: 'Honestly, our Make Your Own Map Pack is way more addictive than I expected......',
      description: 'Honestly, our Make Your Own Map Pack is way more addictive than I expected... It’s one of those projects you don’t realize you’ll keep coming back to. I can’t stop adding stickers and little details e',
      url: 'https://www.instagram.com/reel/DLazZrhyIt5/',
      embedId: 'DLazZrhyIt5',
      category: 'tutorial',
      tags: ['vintagemaps', 'diycraftkit', 'makeyourownmap', 'craftaddict', 'vintagecrafts', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DLazZrhyIt5/media/?size=m'
    },
    {
      id: 'ig-api-201',
      platform: 'instagram',
      title: 'POV: You’re unboxing our mid-year dream calendar and it feels like pure magic ✨',
      description: 'POV: You’re unboxing our mid-year dream calendar and it feels like pure magic ✨ I’m so excited to finally share our new, beautiful calendar with you all! I’m so proud of how it’s turned out, from the ',
      url: 'https://www.instagram.com/reel/DLVoohTy1AQ/',
      embedId: 'DLVoohTy1AQ',
      category: 'unboxing',
      tags: ['handmade', 'smallbusiness', 'craftsupplies', 'creativecommunity', 'vintagecrafts', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DLVoohTy1AQ/media/?size=m'
    },
    {
      id: 'ig-api-202',
      platform: 'instagram',
      title: 'Flipping through this completed journal brought up more emotions than I expected',
      description: 'Flipping through this completed journal brought up more emotions than I expected. Each page took me back, not just to the spreads, but to who I was when I made them… and the products we had at CoraCre',
      url: 'https://www.instagram.com/reel/DLQRPnHpxk6/',
      embedId: 'DLQRPnHpxk6',
      category: 'journal-spread',
      tags: ['journalingjourney', 'journalflipthrough', 'creativereflection', 'memorykeeping', 'coracreacrafts', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DLQRPnHpxk6/media/?size=m'
    },
    {
      id: 'ig-api-203',
      platform: 'instagram',
      title: 'I wasn’t sure where this spread was going at first. I knew I wanted to use my...',
      description: 'I wasn’t sure where this spread was going at first. I knew I wanted to use my paints and play with mixed textures, but I didn’t have a clear vision. Once I got started, it slowly turned into a little ',
      url: 'https://www.instagram.com/reel/DLI6xqgSVuh/',
      embedId: 'DLI6xqgSVuh',
      category: 'journal-spread',
      tags: ['deepsea', 'creativejournaling', 'oceanlife', 'relaxingcrafts', 'vintagejournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DLI6xqgSVuh/media/?size=m'
    },
    {
      id: 'ig-api-204',
      platform: 'instagram',
      title: 'This Treasure Box is a special one, it’s our first theme chosen by the community',
      description: 'This Treasure Box is a special one, it’s our first theme chosen by the community! Say hello to Academia 🖤 This theme came out on top with an overwhelming majority, and I couldn’t wait to bring it to ',
      url: 'https://www.instagram.com/reel/DLGLQvoy9qF/',
      embedId: 'DLGLQvoy9qF',
      category: 'unboxing',
      tags: ['darkacademia', 'unboxing', 'journalingcommunity', 'stationeryaddict', 'vintageaesthetic', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DLGLQvoy9qF/media/?size=m'
    },
    {
      id: 'ig-api-205',
      platform: 'instagram',
      title: 'Lately, I’ve been playing with some of our witchy supplies and loving the moo...',
      description: 'Lately, I’ve been playing with some of our witchy supplies and loving the moody, playful vibe they bring to my journaling. Adding little bits of writing onto the pages has been a fun way to switch thi',
      url: 'https://www.instagram.com/reel/DLDKgnVsvaH/',
      embedId: 'DLDKgnVsvaH',
      category: 'journal-spread',
      tags: ['witchyjournaling', 'darkacademia', 'moodyvibes', 'journalingcommunity', 'creativejournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DLDKgnVsvaH/media/?size=m'
    },
    {
      id: 'ig-api-206',
      platform: 'instagram',
      title: 'I’ve had a few questions about what pairs well with the Navigation Box, so I ...',
      description: 'I’ve had a few questions about what pairs well with the Navigation Box, so I wanted to share some of my favorites that I’ve been reaching for in my own spreads. This theme is definitely putting me in ',
      url: 'https://www.instagram.com/reel/DLAgpoMMS94/',
      embedId: 'DLAgpoMMS94',
      category: 'journal-spread',
      tags: ['navigation', 'seaofcreativity', 'paperwaves', 'navigateyourday', 'journalingjourney', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DLAgpoMMS94/media/?size=m'
    },
    {
      id: 'ig-api-207',
      platform: 'instagram',
      title: 'Here’s a peek at one of our June Digital Journal Club Subscriptions, this one...',
      description: 'Here’s a peek at one of our June Digital Journal Club Subscriptions, this one’s all about Navigation. I love putting these together each month, it’s such a different creative process from designing th',
      url: 'https://www.instagram.com/reel/DK-BmKeMf6Q/',
      embedId: 'DK-BmKeMf6Q',
      category: 'box-reveal',
      tags: ['digitaljournal', 'digitaljournaling', 'digitalplanner', 'goodnotes', 'digitalstickers', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DK-BmKeMf6Q/media/?size=m'
    },
    {
      id: 'ig-api-208',
      platform: 'instagram',
      title: 'This workshop didn’t go how I thought it would… but somehow it turned out eve...',
      description: 'This workshop didn’t go how I thought it would… but somehow it turned out even better. The venue fell through and only a few people signed up, so I hosted it in my living room instead. And honestly, i',
      url: 'https://www.instagram.com/reel/DK74g6RSEje/',
      embedId: 'DK74g6RSEje',
      category: 'product-showcase',
      tags: ['journalingworkshop', 'creativeworkshop', 'vintagecrafts', 'journalingcommunity', 'handsoncreativity', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DK74g6RSEje/media/?size=m'
    },
    {
      id: 'ig-api-209',
      platform: 'instagram',
      title: 'My grandfather used to build miniature sailing ships by hand',
      description: 'My grandfather used to build miniature sailing ships by hand. I remember watching him carefully add each tiny rope and sail — it felt like every boat had its own story waiting to be told. That’s what ',
      url: 'https://www.instagram.com/reel/DKxmAKRSEPO/',
      embedId: 'DKxmAKRSEPO',
      category: 'box-reveal',
      tags: ['memoryinspiredjournaling', 'vintagesummervibes', 'nauticaljunkjournal', 'travelthemedcrafts', 'handmadestories', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DKxmAKRSEPO/media/?size=m'
    },
    {
      id: 'ig-api-210',
      platform: 'instagram',
      title: 'Pack a wholesale order with me! This part of the process never gets old, care...',
      description: 'Pack a wholesale order with me! This part of the process never gets old, carefully selecting and packing each piece, knowing they’re about to become part of someone else’s creative journey. It’s a rem',
      url: 'https://www.instagram.com/reel/DKvBYerSve2/',
      embedId: 'DKvBYerSve2',
      category: 'behind-the-scenes',
      tags: ['wholesale', 'smallbusiness', 'handmade', 'creativecommunity', 'founderlife'],
      creator: 'CoraCreaCrafts',
      date: '2025-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DKvBYerSve2/media/?size=m'
    },
    {
      id: 'ig-api-211',
      platform: 'instagram',
      title: 'Whenever I come back from a trip, there’s this strange mix of joy and sadness...',
      description: 'Whenever I come back from a trip, there’s this strange mix of joy and sadness. I’m grateful for the experience but also a little heartbroken that it’s already over. Journaling helps me make sense of t',
      url: 'https://www.instagram.com/reel/DKsPzAiyA-e/',
      embedId: 'DKsPzAiyA-e',
      category: 'journal-spread',
      tags: ['traveljournal', 'memorykeeping', 'junkjournalcommunity', 'journalspread', 'travelmemories', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DKsPzAiyA-e/media/?size=m'
    },
    {
      id: 'ig-api-212',
      platform: 'instagram',
      title: 'Notebooks are such a personal thing. The way they feel in your hands, the tex...',
      description: 'Notebooks are such a personal thing. The way they feel in your hands, the texture, even the way they age over time, it all matters.  I’ve always felt that a notebook should be a space you want to retu',
      url: 'https://www.instagram.com/reel/DKp8_PCyIHo/',
      embedId: 'DKp8_PCyIHo',
      category: 'journal-spread',
      tags: ['notebooklove', 'journalingcommunity', 'creativejournaling', 'stationeryaddict', 'journalinspiration', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DKp8_PCyIHo/media/?size=m'
    },
    {
      id: 'ig-api-213',
      platform: 'instagram',
      title: 'The new Wicked trailer dropped yesterday and I haven’t stopped thinking about...',
      description: 'The new Wicked trailer dropped yesterday and I haven’t stopped thinking about it, so I pulled out a few of our products that felt like they belonged in that world! ✨ Are you team Glinda or team Elphab',
      url: 'https://www.instagram.com/reel/DKkNfLxMwQx/',
      embedId: 'DKkNfLxMwQx',
      category: 'journal-spread',
      tags: ['wickedmovie', 'glindaorelphaba', 'craftingtiktok', 'journalinginspo', 'darkacademiaaesthetic', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DKkNfLxMwQx/media/?size=m'
    },
    {
      id: 'ig-api-214',
      platform: 'instagram',
      title: 'It’s almost summer, and this month’s Vintage Craft Box feels like the perfect...',
      description: 'It’s almost summer, and this month’s Vintage Craft Box feels like the perfect way to start the season. ⁣The theme is Navigation, inspired by my love for sailing, those quiet evenings getting lost in o',
      url: 'https://www.instagram.com/reel/DKhwREGsSr2/',
      embedId: 'DKhwREGsSr2',
      category: 'box-reveal',
      tags: ['vintagejournaling', 'junkjournalcommunity', 'craftingjoy', 'paperlover', 'ephemeracollective', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DKhwREGsSr2/media/?size=m'
    },
    {
      id: 'ig-api-215',
      platform: 'instagram',
      title: 'I’m definitely still in my Coquette era, these soft, muted colors and delicat...',
      description: 'I’m definitely still in my Coquette era, these soft, muted colors and delicate details keep drawing me back whenever I sit down to create. There’s something timeless and quietly beautiful about them t',
      url: 'https://www.instagram.com/reel/DKctMolxrnz/',
      embedId: 'DKctMolxrnz',
      category: 'journal-spread',
      tags: ['journalingcommunity', 'creativejournaling', 'stationeryaddict', 'artjournaling', 'craftingcommunity', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DKctMolxrnz/media/?size=m'
    },
    {
      id: 'ig-api-216',
      platform: 'instagram',
      title: 'I wanted to take a little time for myself and revisit some of my favorite sup...',
      description: 'I wanted to take a little time for myself and revisit some of my favorite supplies I hadn’t used in a while. There’s something really comforting about going back to familiar pieces and seeing them in ',
      url: 'https://www.instagram.com/reel/DKaJ-7SJOCE/',
      embedId: 'DKaJ-7SJOCE',
      category: 'journal-spread',
      tags: ['journalingcommunity', 'creativeescape', 'mindfulcrafting', 'relaxandcreate', 'crafttherapy', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DKaJ-7SJOCE/media/?size=m'
    },
    {
      id: 'ig-api-217',
      platform: 'instagram',
      title: 'Starting CoraCreaCrafts was never part of some big, perfect plan. It began wi...',
      description: 'Starting CoraCreaCrafts was never part of some big, perfect plan. It began with a love for vintage paper and journaling and a quiet dream that maybe, just maybe, I could turn that into something real.',
      url: 'https://www.instagram.com/reel/DKXanvsRvuy/',
      embedId: 'DKXanvsRvuy',
      category: 'product-showcase',
      tags: ['entrepreneur', 'smallbusiness', 'femalefounder', 'businessmotivation', 'entrepreneurlife', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DKXanvsRvuy/media/?size=m'
    },
    {
      id: 'ig-api-218',
      platform: 'instagram',
      title: 'I really enjoyed putting together this mermaid-themed spread and wanted to sh...',
      description: 'I really enjoyed putting together this mermaid-themed spread and wanted to share a bit of fun with you so guess your mermaid name from the options on screen! This box is what I reach for when I want t',
      url: 'https://www.instagram.com/reel/DKSR9W8xaYh/',
      embedId: 'DKSR9W8xaYh',
      category: 'journal-spread',
      tags: ['mermaidjournal', 'seacraft', 'journalingfun', 'creativejournaling', 'mermaidspread', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DKSR9W8xaYh/media/?size=m'
    },
    {
      id: 'ig-api-219',
      platform: 'instagram',
      title: 'It’s that time again our end-of-the-month live sale is back this Saturday',
      description: 'It’s that time again our end-of-the-month live sale is back this Saturday! I always look forward to these because it’s a chance to connect with all of you and share some amazing deals with you all. I ',
      url: 'https://www.instagram.com/reel/DKPvrJ8x2jJ/',
      embedId: 'DKPvrJ8x2jJ',
      category: 'behind-the-scenes',
      tags: ['livesale', 'shopwithme', 'smallbusinesslove', 'communityfirst', 'handcraftedtreasures'],
      creator: 'CoraCreaCrafts',
      date: '2025-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DKPvrJ8x2jJ/media/?size=m'
    },
    {
      id: 'ig-api-220',
      platform: 'instagram',
      title: 'I’ve been trying to push myself to experiment more with my spreads lately, an...',
      description: 'I’ve been trying to push myself to experiment more with my spreads lately, and the steampunk collection felt like the perfect excuse. I wanted to create a double-page “frame wall” layout, something a ',
      url: 'https://www.instagram.com/reel/DKM69X2R6QN/',
      embedId: 'DKM69X2R6QN',
      category: 'journal-spread',
      tags: ['junkjournal', 'journalspread', 'steampunk', 'vintagejournaling', 'creativejournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DKM69X2R6QN/media/?size=m'
    },
    {
      id: 'ig-api-221',
      platform: 'instagram',
      title: 'I’m really looking forward to our journaling workshop on June 5th in Nashvill...',
      description: 'I’m really looking forward to our journaling workshop on June 5th in Nashville, hosted with Jacqueline from Sapphire & Sage @sapphiresagebtq  Together, we’ll create a calm, creative space where you ca',
      url: 'https://www.instagram.com/reel/DKKJqmXx6VM/',
      embedId: 'DKKJqmXx6VM',
      category: 'product-showcase',
      tags: ['journalingcommunity', 'creativejournaling', 'intentionsetting', 'mindfuljournaling', 'nashvilleevents', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DKKJqmXx6VM/media/?size=m'
    },
    {
      id: 'ig-api-222',
      platform: 'instagram',
      title: 'Happy Memorial Day to those of you celebrating today 💙',
      description: 'Happy Memorial Day to those of you celebrating today 💙 It’s been so lovely to see so many of you pick up a grab bag and shop the sale this weekend,thank you for all the support. There’s still one mor',
      url: 'https://www.instagram.com/reel/DKJIkl_Mst-/',
      embedId: 'DKJIkl_Mst-',
      category: 'product-showcase',
      tags: ['grabbag', 'craftingsupplies', 'memorialdaysale', 'memorialday', 'junkjournalcommunity', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DKJIkl_Mst-/media/?size=m'
    },
    {
      id: 'ig-api-223',
      platform: 'instagram',
      title: 'My Kindle cover collection is definitely growing… this time I wanted to make ...',
      description: 'My Kindle cover collection is definitely growing… this time I wanted to make a coquette-themed one. It felt perfect for this time of year, when I take my Kindle to the park and read in the sun. There’',
      url: 'https://www.instagram.com/reel/DKFg8M8JkXL/',
      embedId: 'DKFg8M8JkXL',
      category: 'tutorial',
      tags: ['kindlecover', 'diyproject', 'coquetteaesthetic', 'journalingcommunity', 'handmadewithlove', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DKFg8M8JkXL/media/?size=m'
    },
    {
      id: 'ig-api-224',
      platform: 'instagram',
      title: 'We put together a fresh set of grab bags just for the Memorial Day Sale, four...',
      description: 'We put together a fresh set of grab bags just for the Memorial Day Sale, four sizes this time, including some very full ones (you can probably tell from the video 😅) Leave your guess for how many ite',
      url: 'https://www.instagram.com/reel/DKC5qEWJJfu/',
      embedId: 'DKC5qEWJJfu',
      category: 'product-showcase',
      tags: ['grabbag', 'craftingsupplies', 'memorialdaysale', 'memorialday', 'junkjournalcommunity', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DKC5qEWJJfu/media/?size=m'
    },
    {
      id: 'ig-api-225',
      platform: 'instagram',
      title: 'We’ve been so excited to bring these back for you all',
      description: 'We’ve been so excited to bring these back for you all! Grab bags return tomorrow in 4 sizes and this time we’ve added our biggest one yet, with 110 items inside 😍 They’re honestly one of my favorite ',
      url: 'https://www.instagram.com/reel/DKAwRApSocB/',
      embedId: 'DKAwRApSocB',
      category: 'product-showcase',
      tags: ['grabbags', 'craftingsupplies', 'junkjournalcommunity', 'memorialweekend', 'stationeryaddict', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DKAwRApSocB/media/?size=m'
    },
    {
      id: 'ig-api-226',
      platform: 'instagram',
      title: 'There’s something about fantasy themes that unlocks a different part of my br...',
      description: 'There’s something about fantasy themes that unlocks a different part of my brain. Every time I create one of these spreads, it opens up space for stories, daydreams, and all the little ideas that don’',
      url: 'https://www.instagram.com/reel/DJ9g99vRJs5/',
      embedId: 'DJ9g99vRJs5',
      category: 'journal-spread',
      tags: ['fantasyjournal', 'artjournal', 'bulletjournal', 'creativejournaling', 'fantasybooks', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DJ9g99vRJs5/media/?size=m'
    },
    {
      id: 'ig-api-227',
      platform: 'instagram',
      title: 'I’ve been playing around with some of the bits from the Mermaid Curiosities b...',
      description: 'I’ve been playing around with some of the bits from the Mermaid Curiosities box today 🐚  I didn’t have a clear plan when I started, but it ended up turning into something I really loved, sometimes th',
      url: 'https://www.instagram.com/reel/DJ4ZH50xKFj/',
      embedId: 'DJ4ZH50xKFj',
      category: 'box-reveal',
      tags: ['junkjournaling', 'creativejournaling', 'journalwithme', 'vintagejournal', 'artjournalpages', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DJ4ZH50xKFj/media/?size=m'
    },
    {
      id: 'ig-api-228',
      platform: 'instagram',
      title: 'The team have been hard at work packing up your Coquette subscription boxes! ...',
      description: 'The team have been hard at work packing up your Coquette subscription boxes! We had so much fun putting these together and can’t wait for you all to start receiving them. Hope you love what’s inside ',
      url: 'https://www.instagram.com/reel/DJ11IBXRIQo/',
      embedId: 'DJ11IBXRIQo',
      category: 'behind-the-scenes',
      tags: ['coquette', 'craftingcommunity', 'subscriptionbox', 'coracreacrafts', 'creativeinspiration', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DJ11IBXRIQo/media/?size=m'
    },
    {
      id: 'ig-api-229',
      platform: 'instagram',
      title: 'Cats and crafting always come first! Who else can relate? 🙋‍♀️',
      description: 'Cats and crafting always come first! Who else can relate? 🙋‍♀️  #craflife #makermoments #cats #creativefounder #smallbizlife #craftcommunity #craftingaddict #entrepreneurlife #catlovers #PassionProje',
      url: 'https://www.instagram.com/reel/DJxJamavNIW/',
      embedId: 'DJxJamavNIW',
      category: 'product-showcase',
      tags: ['craflife', 'makermoments', 'cats', 'creativefounder', 'smallbizlife'],
      creator: 'CoraCreaCrafts',
      date: '2025-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DJxJamavNIW/media/?size=m'
    },
    {
      id: 'ig-api-230',
      platform: 'instagram',
      title: 'If you placed an order during our Cinco de Mayo sale, don’t miss this video',
      description: 'If you placed an order during our Cinco de Mayo sale, don’t miss this video! Rachel and Asia went on a little hunt in the warehouse to draw the 5 winning raffle numbers! Each winner will receive a spe',
      url: 'https://www.instagram.com/reel/DJuvXLZydav/',
      embedId: 'DJuvXLZydav',
      category: 'product-showcase',
      tags: ['raffletickets', 'specialsale', 'stationerygiveaway', 'asmrjournaling', 'journalingsupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DJuvXLZydav/media/?size=m'
    },
    {
      id: 'ig-api-231',
      platform: 'instagram',
      title: 'Curious about Tarot, where it comes from and what the cards really mean',
      description: 'Curious about Tarot, where it comes from and what the cards really mean? Join us tonight at 6:30 PM CST on our Instagram for a special live session with our guest tarot reader, Jennah! She’ll walk us ',
      url: 'https://www.instagram.com/reel/DJrhASlxArz/',
      embedId: 'DJrhASlxArz',
      category: 'product-showcase',
      tags: ['tarotcards', 'live', 'tarotreading', 'mystical', 'magic'],
      creator: 'CoraCreaCrafts',
      date: '2025-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DJrhASlxArz/media/?size=m'
    },
    {
      id: 'ig-api-232',
      platform: 'instagram',
      title: 'Today felt like the perfect slow journaling day so I pulled out my Coquette B...',
      description: 'Today felt like the perfect slow journaling day so I pulled out my Coquette Box and created something soft, romantic, and dreamy. There’s just something about lace, florals, and soft colors that makes',
      url: 'https://www.instagram.com/reel/DJpZ2kXyg2A/',
      embedId: 'DJpZ2kXyg2A',
      category: 'journal-spread',
      tags: ['junkjournaling', 'journalwithme', 'stationerylove', 'vintagejournaling', 'slowjournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DJpZ2kXyg2A/media/?size=m'
    },
    {
      id: 'ig-api-233',
      platform: 'instagram',
      title: 'Here’s a behind-the-scenes look at prepping for The Little Craft Fest, packin...',
      description: 'Here’s a behind-the-scenes look at prepping for The Little Craft Fest, packing supplies, shipping everything out, and feeling all the nerves leading up to the event. It was my first big festival, and ',
      url: 'https://www.instagram.com/reel/DJka9zMSi6u/',
      embedId: 'DJka9zMSi6u',
      category: 'journal-spread',
      tags: ['behindthescenes', 'littlecraftfest', 'founderlife', 'coracreacrafts', 'smallbusiness', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DJka9zMSi6u/media/?size=m'
    },
    {
      id: 'ig-api-234',
      platform: 'instagram',
      title: 'Thanks so much for all the love on Part 1 of my flip-through! I had so many r...',
      description: 'Thanks so much for all the love on Part 1 of my flip-through! I had so many requests for a second part, so here it is. It’s always a bit special looking back at each spread and remembering what went i',
      url: 'https://www.instagram.com/reel/DJcZ5lspHhH/',
      embedId: 'DJcZ5lspHhH',
      category: 'journal-spread',
      tags: ['junkjournal', 'creativejournaling', 'journalinspiration', 'leatherjournal', 'vintagejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DJcZ5lspHhH/media/?size=m'
    },
    {
      id: 'ig-api-235',
      platform: 'instagram',
      title: 'Update: we only have about 10 left ☺️ Spending the day wrapping up our Mother...',
      description: 'Update: we only have about 10 left ☺️ Spending the day wrapping up our Mother\'s Day Mystery Bundles ✨ Each one’s a little different, filled with vintage-style pieces that I thought would be perfect fo',
      url: 'https://www.instagram.com/reel/DJaK0Cny0Ol/',
      embedId: 'DJaK0Cny0Ol',
      category: 'product-showcase',
      tags: ['mothersday', 'gifting', 'mysterybundle', 'creativejournaling', 'giftideas', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DJaK0Cny0Ol/media/?size=m'
    },
    {
      id: 'ig-api-236',
      platform: 'instagram',
      title: 'I’m so excited to share our newest Curiosities Box: Mermaids & Wonders of the...',
      description: 'I’m so excited to share our newest Curiosities Box: Mermaids & Wonders of the Sea! 🐚✨ I designed this one to feel like a long-lost treasure, something you’d discover in a forgotten shipwreck, with wh',
      url: 'https://www.instagram.com/reel/DJU4zYfyFm6/',
      embedId: 'DJU4zYfyFm6',
      category: 'box-reveal',
      tags: ['coracreacrafts', 'sea', 'mermaid', 'subscriptionbox', 'sealover', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DJU4zYfyFm6/media/?size=m'
    },
    {
      id: 'ig-api-237',
      platform: 'instagram',
      title: 'I had so much fun putting this spread together with our Dichotomy-themed supp...',
      description: 'I had so much fun putting this spread together with our Dichotomy-themed supplies. I designed this collection around the idea of contrast and balance, and it’s been amazing to see it come to life in s',
      url: 'https://www.instagram.com/reel/DJKxdbzyCUJ/',
      embedId: 'DJKxdbzyCUJ',
      category: 'journal-spread',
      tags: ['dichotomy', 'creativejournaling', 'journaling', 'vintage', 'scrapbooking'],
      creator: 'CoraCreaCrafts',
      date: '2025-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DJKxdbzyCUJ/media/?size=m'
    },
    {
      id: 'ig-api-238',
      platform: 'instagram',
      title: 'Meet our newest Vintage Craft Box for May: Coquette 🌸',
      description: 'Meet our newest Vintage Craft Box for May: Coquette 🌸  Elegant, feminine, and full of charm.  Fun fact: this is probably the softest design I’ve ever created, color-wise! Think pale pinks, florals, a',
      url: 'https://www.instagram.com/reel/DJIA3Q0yfmO/',
      embedId: 'DJIA3Q0yfmO',
      category: 'unboxing',
      tags: ['coracreacrafts', 'coquette', 'junkjournal', 'vintagejournaling', 'subscriptionbox', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DJIA3Q0yfmO/media/?size=m'
    },
    {
      id: 'ig-api-239',
      platform: 'instagram',
      title: 'It’s been a while since I did a proper journal flip-through like this, I forg...',
      description: 'It’s been a while since I did a proper journal flip-through like this, I forgot how much I missed it 🖤 Comment if you would like to see a part 2!    #junkjournal #creativejournaling #journalinspirati',
      url: 'https://www.instagram.com/reel/DJArWBis580/',
      embedId: 'DJArWBis580',
      category: 'product-showcase',
      tags: ['junkjournal', 'creativejournaling', 'journalinspiration', 'leatherjournal', 'vintagejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DJArWBis580/media/?size=m'
    },
    {
      id: 'ig-api-240',
      platform: 'instagram',
      title: 'Here’s a gentle reminder that crafting joy can be found in the simplest momen...',
      description: 'Here’s a gentle reminder that crafting joy can be found in the simplest moments like sunshine and your favorite supplies. Nothing beats that feeling of being fully in the moment, just you and your cre',
      url: 'https://www.instagram.com/reel/DI9VU8AJsvC/',
      embedId: 'DI9VU8AJsvC',
      category: 'product-showcase',
      tags: ['craftingjoy', 'coracreacrafts', 'vintagevibes', 'journalingmoments', 'gentlereminder', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DI9VU8AJsvC/media/?size=m'
    },
    {
      id: 'ig-api-241',
      platform: 'instagram',
      title: 'You’ve been asking for this theme for a while, and I’m so happy to finally sh...',
      description: 'You’ve been asking for this theme for a while, and I’m so happy to finally share it with you.  Meet our Witches & Potions Treasure Box filled with vintage details, mysterious little finds, and a hint ',
      url: 'https://www.instagram.com/reel/DI5VL-yM58m/',
      embedId: 'DI5VL-yM58m',
      category: 'box-reveal',
      tags: ['witches', 'coracreacrafts', 'witchycrafts', 'moodyaesthetic', 'darkacademia', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DI5VL-yM58m/media/?size=m'
    },
    {
      id: 'ig-api-242',
      platform: 'instagram',
      title: 'So excited to reveal this exclusive tape designed just for @littlecraftplace,...',
      description: 'So excited to reveal this exclusive tape designed just for @littlecraftplace, inspired by their beautiful storefront and the love they pour into everything they do. We named it Little Craft Love 💖. I',
      url: 'https://www.instagram.com/reel/DIzLzuSJi4Q/',
      embedId: 'DIzLzuSJi4Q',
      category: 'box-reveal',
      tags: ['littlecraftplace', 'exclusivetape', 'craftcommunity', 'coracreacrafts', 'littlecraftfest', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2025-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DIzLzuSJi4Q/media/?size=m'
    },
    {
      id: 'ig-api-243',
      platform: 'instagram',
      title: 'I’ve been having too much fun making my own Kindle covers so when the Alice c...',
      description: 'I’ve been having too much fun making my own Kindle covers so when the Alice collection came back in stock, I knew I had to make an Alice inspired one!  Reading is a big part of my life, so turning som',
      url: 'https://www.instagram.com/reel/DIwkbhNJ-Xn/',
      embedId: 'DIwkbhNJ-Xn',
      category: 'behind-the-scenes',
      tags: ['smallbusinessowner', 'womenwhomake', 'booklover', 'bookishcrafts', 'kindle', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DIwkbhNJ-Xn/media/?size=m'
    },
    {
      id: 'ig-api-244',
      platform: 'instagram',
      title: 'This spread is a little nod to the season of renewal and reflection, using pi...',
      description: 'This spread is a little nod to the season of renewal and reflection, using pieces from our Easter Digital Kit.   Whether you\'re celebrating today or simply soaking in the fresh, hopeful energy of spri',
      url: 'https://www.instagram.com/reel/DIrIcZRsqE1/',
      embedId: 'DIrIcZRsqE1',
      category: 'journal-spread',
      tags: ['springspread', 'vintagejournaling', 'eastervibes', 'seasonalart', 'paperart', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DIrIcZRsqE1/media/?size=m'
    },
    {
      id: 'ig-api-245',
      platform: 'instagram',
      title: 'For me, mini journaling has been so much more than just a craft, it’s a way t...',
      description: 'For me, mini journaling has been so much more than just a craft, it’s a way to spark creativity, reflect, and keep my mind from feeling overwhelmed.   I’m so excited to share my passion for mini journ',
      url: 'https://www.instagram.com/reel/DImSZKLpw3q/',
      embedId: 'DImSZKLpw3q',
      category: 'product-showcase',
      tags: ['littlecraftfest', 'minijournaling', 'creativeblock', 'vintagecrafting', 'workshop', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DImSZKLpw3q/media/?size=m'
    },
    {
      id: 'ig-api-246',
      platform: 'instagram',
      title: 'When someone jokingly offered to send haggis if I created a Scotland spread, ...',
      description: 'When someone jokingly offered to send haggis if I created a Scotland spread, I couldn’t resist the challenge! 🏴󠁧󠁢󠁳󠁣󠁴󠁿 I\'m waiting for it now 🤣 There’s something magical about Scotland’s wild b',
      url: 'https://www.instagram.com/reel/DIkIZWmyGM2/',
      embedId: 'DIkIZWmyGM2',
      category: 'journal-spread',
      tags: ['fyp', 'coracreacrafts', 'scotland', 'subscriptionbox', 'scottish', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DIkIZWmyGM2/media/?size=m'
    },
    {
      id: 'ig-api-247',
      platform: 'instagram',
      title: 'Wasn’t planning a sale this week… but here we are 👀 Plus there is an extra l...',
      description: 'Wasn’t planning a sale this week… but here we are 👀 Plus there is an extra little surprise if you\'re part of our broadcast channel! Join in my bio if you want to know 🤫  For the next two days, we’re',
      url: 'https://www.instagram.com/reel/DIeurTdpPpG/',
      embedId: 'DIeurTdpPpG',
      category: 'product-showcase',
      tags: ['coracreacrafts', 'vintageournaling', 'flashsale', 'junkjournal', 'creativejournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DIeurTdpPpG/media/?size=m'
    },
    {
      id: 'ig-api-248',
      platform: 'instagram',
      title: 'Steampunk has always fascinated me, there’s something so captivating about th...',
      description: 'Steampunk has always fascinated me, there’s something so captivating about the blend of history and imagination. The worn metals, mechanical details, and Victorian flair feel like a peek into an alter',
      url: 'https://www.instagram.com/reel/DIcZt3jSDES/',
      embedId: 'DIcZt3jSDES',
      category: 'journal-spread',
      tags: ['steampunk', 'junkjournal', 'vintageephemera', 'coracreacrafts', 'creativejournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DIcZt3jSDES/media/?size=m'
    },
    {
      id: 'ig-api-249',
      platform: 'instagram',
      title: 'Between work and everything else, I don’t always take the time to slow down. ...',
      description: 'Between work and everything else, I don’t always take the time to slow down. This weekend, I decided to grab some flowers and make my own bouquets, it’s my way of romanticizing spring and bringing a l',
      url: 'https://www.instagram.com/reel/DIZdy3BSzI2/',
      embedId: 'DIZdy3BSzI2',
      category: 'tutorial',
      tags: ['springvibes', 'romanticizespring', 'sundayreset', 'sundayslowday', 'coracreacrafts'],
      creator: 'CoraCreaCrafts',
      date: '2025-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DIZdy3BSzI2/media/?size=m'
    },
    {
      id: 'ig-api-250',
      platform: 'instagram',
      title: 'That moment you realize your new tape is giving major Chappell Roan vibes... ...',
      description: 'That moment you realize your new tape is giving major Chappell Roan vibes... so, of course, a spread had to happen ✨  @chappellroan, what do you think? 👀  #chappellroan #pinkponyclub #midwestprincess',
      url: 'https://www.instagram.com/reel/DIUDQn8Jm1h/',
      embedId: 'DIUDQn8Jm1h',
      category: 'journal-spread',
      tags: ['chappellroan', 'pinkponyclub', 'midwestprincess', 'goodluckbabe', 'creativejournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DIUDQn8Jm1h/media/?size=m'
    },
    {
      id: 'ig-api-251',
      platform: 'instagram',
      title: 'Guess what I spotted? The Loch Ness Monster! 🦕',
      description: 'Guess what I spotted? The Loch Ness Monster! 🦕  Okay, maybe it’s just a sticker from our Scotland Vintage Craft Box 😅 What’s your favorite part of Scotland? 🏴󠁧󠁢󠁳󠁣󠁴󠁿 #subscriptionbox #scotland',
      url: 'https://www.instagram.com/reel/DIRy7nNy3f2/',
      embedId: 'DIRy7nNy3f2',
      category: 'box-reveal',
      tags: ['subscriptionbox', 'scotland', 'lochnessmonster', 'vintagecrafting', 'scottish', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DIRy7nNy3f2/media/?size=m'
    },
    {
      id: 'ig-api-252',
      platform: 'instagram',
      title: 'Creating CoraCreaCrafts has been the hardest yet most rewarding journey I cou...',
      description: 'Creating CoraCreaCrafts has been the hardest yet most rewarding journey I could have ever dreamed of. Lately, with all the uncertainty, rising costs, new tariffs, and the ever-changing challenges we a',
      url: 'https://www.instagram.com/reel/DIKKjZHyThU/',
      embedId: 'DIKKjZHyThU',
      category: 'product-showcase',
      tags: ['businessowner', 'fyp', 'coracreacrafts', 'smallbusiness', 'crafting', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DIKKjZHyThU/media/?size=m'
    },
    {
      id: 'ig-api-253',
      platform: 'instagram',
      title: 'Spend a day with me as a small business owner prepping for the Art Crawl at H...',
      description: 'Spend a day with me as a small business owner prepping for the Art Crawl at Hester & Cook! ✨ Thank you for havin me @hesterandcook 🖤  #smallbusinesslife #founder #femalebusiness #coracreacrafts #dayi',
      url: 'https://www.instagram.com/reel/DIHotbLyl1-/',
      embedId: 'DIHotbLyl1-',
      category: 'product-showcase',
      tags: ['smallbusinesslife', 'founder', 'femalebusiness', 'coracreacrafts', 'dayinmylife'],
      creator: 'CoraCreaCrafts',
      date: '2025-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DIHotbLyl1-/media/?size=m'
    },
    {
      id: 'ig-api-254',
      platform: 'instagram',
      title: 'I\'m obsessed with Dragons, so being able to make a Black Dragon spread with m...',
      description: 'I\'m obsessed with Dragons, so being able to make a Black Dragon spread with my Empyrean Dragons Digital Kit was pure magic 🐉 Have you tried this Digital Kit yet? 🖤 #darkfantasy #dragons #junkjournal',
      url: 'https://www.instagram.com/reel/DIB2PZMR6mQ/',
      embedId: 'DIB2PZMR6mQ',
      category: 'journal-spread',
      tags: ['darkfantasy', 'dragons', 'junkjournal', 'vintagecrafting', 'coracreacrafts', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DIB2PZMR6mQ/media/?size=m'
    },
    {
      id: 'ig-api-255',
      platform: 'instagram',
      title: 'Our April Vintage Craft Box has arrived, and this month, we’re taking you on ...',
      description: 'Our April Vintage Craft Box has arrived, and this month, we’re taking you on a creative journey to Scotland! 🏴󠁧󠁢󠁳󠁣󠁴󠁿 Who\'s excited for this one?  #scotland #coracreacrafts #vintagecrafting #sta',
      url: 'https://www.instagram.com/reel/DH92gSuscsY/',
      embedId: 'DH92gSuscsY',
      category: 'unboxing',
      tags: ['scotland', 'coracreacrafts', 'vintagecrafting', 'stationarybox', 'creativejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DH92gSuscsY/media/?size=m'
    },
    {
      id: 'ig-api-256',
      platform: 'instagram',
      title: 'I\'ve had so many questions about printing our digital kits, so I put together...',
      description: 'I\'ve had so many questions about printing our digital kits, so I put together this video to help! 🖨️  From the best paper to the right printer, I’m sharing my tips to get the best results. Let me kno',
      url: 'https://www.instagram.com/reel/DH1K-zHJcV8/',
      embedId: 'DH1K-zHJcV8',
      category: 'product-showcase',
      tags: ['coracreacrafts', 'digitaljournaling', 'craftingtips', 'printingathome', 'creativejournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DH1K-zHJcV8/media/?size=m'
    },
    {
      id: 'ig-api-257',
      platform: 'instagram',
      title: 'Come pack an order with me! Today, I’m putting together Helen’s order, filled...',
      description: 'Come pack an order with me! Today, I’m putting together Helen’s order, filled with lots of goodies! I always love seeing what you all pick out ❤️ Want me to pack your order next? Drop a comment below!',
      url: 'https://www.instagram.com/reel/DHt9jcbSab_/',
      embedId: 'DHt9jcbSab_',
      category: 'behind-the-scenes',
      tags: ['packanorderwithme', 'smallbusinesslove', 'creativejournaling', 'stationeryaddict', 'packingorders', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DHt9jcbSab_/media/?size=m'
    },
    {
      id: 'ig-api-258',
      platform: 'instagram',
      title: 'Made a cute rose specimen card 😍Today is your last day to grab our Red Treas...',
      description: 'Made a cute rose specimen card 😍Today is your last day to grab our Red Treasure Box! ❤️ #coracreacrafts #red #junkjournal #scrapbooking #vintage #crafting',
      url: 'https://www.instagram.com/reel/DHrB7b_p3MT/',
      embedId: 'DHrB7b_p3MT',
      category: 'box-reveal',
      tags: ['coracreacrafts', 'red', 'junkjournal', 'scrapbooking', 'vintage', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DHrB7b_p3MT/media/?size=m'
    },
    {
      id: 'ig-api-259',
      platform: 'instagram',
      title: 'Decided my Kindle needed a little steampunk glow up! ⚙️📖 Who\'s enjoyed the 2...',
      description: 'Decided my Kindle needed a little steampunk glow up! ⚙️📖 Who\'s enjoyed the 2nd season of Arcane? Watch me turn it into something way more me. Who’s gonna try this too? 👀  #steampunkstyle #DIY #craft',
      url: 'https://www.instagram.com/reel/DHl8DRTpm87/',
      embedId: 'DHl8DRTpm87',
      category: 'tutorial',
      tags: ['steampunkstyle', 'diy', 'craftingfun', 'vintageaesthetic', 'handmade', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DHl8DRTpm87/media/?size=m'
    },
    {
      id: 'ig-api-260',
      platform: 'instagram',
      title: 'A special guest voiceover today! 🎤✨ Watch my Sister In Law Grace bring this ...',
      description: 'A special guest voiceover today! 🎤✨ Watch my Sister In Law Grace bring this Spread to life! What do you think of her narration? 😆  #craftingfun #springcreativity #junkjournal #scrapbooking #coracrea',
      url: 'https://www.instagram.com/reel/DHeezWsyyzj/',
      embedId: 'DHeezWsyyzj',
      category: 'journal-spread',
      tags: ['craftingfun', 'springcreativity', 'junkjournal', 'scrapbooking', 'coracreacrafts', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DHeezWsyyzj/media/?size=m'
    },
    {
      id: 'ig-api-261',
      platform: 'instagram',
      title: 'Coffee in hand, crafting in full swing! ☕🌸',
      description: 'Coffee in hand, crafting in full swing! ☕🌸 Celebrating the first day of spring by making this coffee and flower themed card. Who else is ready to craft their way through spring?   #springcrafting #co',
      url: 'https://www.instagram.com/reel/DHb3rurSzA7/',
      embedId: 'DHb3rurSzA7',
      category: 'product-showcase',
      tags: ['springcrafting', 'coracreacrafts', 'creativejournaling', 'scrapbooking', 'junkjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DHb3rurSzA7/media/?size=m'
    },
    {
      id: 'ig-api-262',
      platform: 'instagram',
      title: 'Steampunk is back! ⚙️ This has been one of our most requested themes since 20...',
      description: 'Steampunk is back! ⚙️ This has been one of our most requested themes since 2023, and I’m so excited to bring it back! I really wanted to capture the magic of invention and adventure with these pieces.',
      url: 'https://www.instagram.com/reel/DHZMDyLJy3I/',
      embedId: 'DHZMDyLJy3I',
      category: 'unboxing',
      tags: ['coracreacrafts', 'steampunk', 'steampunkcrafts', 'craftingaddict', 'vintagecrafts', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DHZMDyLJy3I/media/?size=m'
    },
    {
      id: 'ig-api-263',
      platform: 'instagram',
      title: 'I like my money right where I can see it.... in my ever growing pile of craft...',
      description: 'I like my money right where I can see it.... in my ever growing pile of craft supplies 🤭 And with our Buy 2 Get 1 Free sale now’s the perfect excuse to grab just a few more supplies. 👀 Sale ends ton',
      url: 'https://www.instagram.com/reel/DHWZdA8pmZ_/',
      embedId: 'DHWZdA8pmZ_',
      category: 'product-showcase',
      tags: ['sale', 'stpatricksday', 'coracreacrafts', 'trending', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DHWZdA8pmZ_/media/?size=m'
    },
    {
      id: 'ig-api-264',
      platform: 'instagram',
      title: 'I\'m so so excited to announce that we are now carrying tapes from the wonderf...',
      description: 'I\'m so so excited to announce that we are now carrying tapes from the wonderful @sindhurascrafts in our shop! ♥️😍 We have 19 new styles for you to check out, all completely different and so unique! S',
      url: 'https://www.instagram.com/reel/DHOVrT9MsCv/',
      embedId: 'DHOVrT9MsCv',
      category: 'product-showcase',
      tags: ['gothic', 'dark academia'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DHOVrT9MsCv/media/?size=m'
    },
    {
      id: 'ig-api-265',
      platform: 'instagram',
      title: 'Getting into the St. Patrick’s Day spirit with this festive spread! 🍀✨ Had s...',
      description: 'Getting into the St. Patrick’s Day spirit with this festive spread! 🍀✨ Had so much fun putting it together using some of my favorite supplies. All the items I used are listed in the video! Who else i',
      url: 'https://www.instagram.com/reel/DHL4mZdpwMj/',
      embedId: 'DHL4mZdpwMj',
      category: 'journal-spread',
      tags: ['stpatricksday', 'coracreacrafts', 'ireland', 'creativejournaling', 'digitalkit', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DHL4mZdpwMj/media/?size=m'
    },
    {
      id: 'ig-api-266',
      platform: 'instagram',
      title: 'Restocked! The Alice collection is back, but it’s going quick! ✨ I made these...',
      description: 'Restocked! The Alice collection is back, but it’s going quick! ✨ I made these Alice-inspired tags and loved how they turned out! 😍 Don’t miss out, get yours now! #coracreacrafts #vintagecrafting #ali',
      url: 'https://www.instagram.com/reel/DHJoaOcRlsE/',
      embedId: 'DHJoaOcRlsE',
      category: 'tutorial',
      tags: ['coracreacrafts', 'vintagecrafting', 'aliceinwonderland', 'restocked', 'scrapbooking', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DHJoaOcRlsE/media/?size=m'
    },
    {
      id: 'ig-api-267',
      platform: 'instagram',
      title: 'I had so much fun creating these three new digital kits and can\'t wait to see...',
      description: 'I had so much fun creating these three new digital kits and can\'t wait to see the magic you create with them! Which one is your favorite?🤭  #coracreacrafts #digitalkit #CreativeJournaling #digitaleph',
      url: 'https://www.instagram.com/reel/DHHfOMDynkC/',
      embedId: 'DHHfOMDynkC',
      category: 'product-showcase',
      tags: ['coracreacrafts', 'digitalkit', 'creativejournaling', 'digitalephemera', 'creativejournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DHHfOMDynkC/media/?size=m'
    },
    {
      id: 'ig-api-268',
      platform: 'instagram',
      title: 'I’ve been wanting to create a day & night dichotomy spread for so long, and I...',
      description: 'I’ve been wanting to create a day & night dichotomy spread for so long, and I finally did it! ☀️🌙  I loved playing with the contrast between light and dark! What side is your favorite?   #coracreacra',
      url: 'https://www.instagram.com/reel/DHElnAiJMEm/',
      embedId: 'DHElnAiJMEm',
      category: 'journal-spread',
      tags: ['coracreacrafts', 'dichotomy', 'creativejournaling', 'opposites', 'lightanddark', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DHElnAiJMEm/media/?size=m'
    },
    {
      id: 'ig-api-269',
      platform: 'instagram',
      title: 'Rice paper is one of my favorite materials to craft with! Here are 3 easy way...',
      description: 'Rice paper is one of my favorite materials to craft with! Here are 3 easy ways to use it✨  How do you love using rice paper in your projects? 😍  #ricepaper #coracreacrafts #3ways #DIY #handmadewithlo',
      url: 'https://www.instagram.com/reel/DHB5_pSRNEr/',
      embedId: 'DHB5_pSRNEr',
      category: 'tutorial',
      tags: ['ricepaper', 'coracreacrafts', '3ways', 'diy', 'handmadewithlove', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DHB5_pSRNEr/media/?size=m'
    },
    {
      id: 'ig-api-270',
      platform: 'instagram',
      title: 'Our new Dragon Creative Notebook is an immediate add to cart....who else agre...',
      description: 'Our new Dragon Creative Notebook is an immediate add to cart....who else agrees? 🐉✨  #coracreacrafts #fantasy #notebook #scrapbooking #creativejournaling #dragon',
      url: 'https://www.instagram.com/reel/DG_pv1eyCXV/',
      embedId: 'DG_pv1eyCXV',
      category: 'product-showcase',
      tags: ['coracreacrafts', 'fantasy', 'notebook', 'scrapbooking', 'creativejournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DG_pv1eyCXV/media/?size=m'
    },
    {
      id: 'ig-api-271',
      platform: 'instagram',
      title: 'Happy International Women’s Day! ✨💐',
      description: 'Happy International Women’s Day! ✨💐  Today, we’re celebrating the incredible strength, creativity, and power of women, both past and present. I’m beyond grateful for the amazing women who have suppor',
      url: 'https://www.instagram.com/reel/DG8inrXSAjR/',
      embedId: 'DG8inrXSAjR',
      category: 'product-showcase',
      tags: ['happyinternationalwomensday', 'iwd25', 'internationalwomensday', 'smallbuisness', 'handmadewithlove'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DG8inrXSAjR/media/?size=m'
    },
    {
      id: 'ig-api-272',
      platform: 'instagram',
      title: 'My idea of the perfect relaxing day is just me, my crafts, and zero concept o...',
      description: 'My idea of the perfect relaxing day is just me, my crafts, and zero concept of time ✨  #coracreacrafts #craftythings #creativejournaling #vintage #girlythings #selfcare',
      url: 'https://www.instagram.com/reel/DG6gKCwypRO/',
      embedId: 'DG6gKCwypRO',
      category: 'product-showcase',
      tags: ['coracreacrafts', 'craftythings', 'creativejournaling', 'vintage', 'girlythings', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DG6gKCwypRO/media/?size=m'
    },
    {
      id: 'ig-api-273',
      platform: 'instagram',
      title: 'Our newest wax items have arrived! 🕯️✨ I’m absolutely obsessed with the Rose...',
      description: 'Our newest wax items have arrived! 🕯️✨ I’m absolutely obsessed with the Rosewood Wax Seal Furnace Set 😍 Did you know you can upgrade your March VCB and add one of our wax items....Don’t miss out!  #',
      url: 'https://www.instagram.com/reel/DG1PvpCJ90N/',
      embedId: 'DG1PvpCJ90N',
      category: 'product-showcase',
      tags: ['waxseal', 'coracreacrafts', 'vintagecrafting', 'creativejournaling', 'scrapbooking', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DG1PvpCJ90N/media/?size=m'
    },
    {
      id: 'ig-api-274',
      platform: 'instagram',
      title: 'This March step into the world of Dichotomy—a theme where contrasts collide a...',
      description: 'This March step into the world of Dichotomy—a theme where contrasts collide and opposites create harmony 🌞🌚 Comment your favorite product below ⬇️  #vintagecrafts #junkjournal #dichotomy #oppositesa',
      url: 'https://www.instagram.com/reel/DGyx2JNpBss/',
      embedId: 'DGyx2JNpBss',
      category: 'product-showcase',
      tags: ['vintagecrafts', 'junkjournal', 'dichotomy', 'oppositesattract', 'mixedmediaart', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DGyx2JNpBss/media/?size=m'
    },
    {
      id: 'ig-api-275',
      platform: 'instagram',
      title: 'It\'s Giveaway Time! 😍 We\'re giving away 3 sets of 3-days tickets for you and...',
      description: 'It\'s Giveaway Time! 😍 We\'re giving away 3 sets of 3-days tickets for you and your besties to attend the first ever Houston Stationery Fest - Little Craft Fest on April 25-27 ✨   To enter you must fol',
      url: 'https://www.instagram.com/reel/DGveQ0MxBtt/',
      embedId: 'DGveQ0MxBtt',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DGveQ0MxBtt/media/?size=m'
    },
    {
      id: 'ig-api-276',
      platform: 'instagram',
      title: 'Sundays are for crafting ✨ Sharing a little Alice in Wonderland magic with th...',
      description: 'Sundays are for crafting ✨ Sharing a little Alice in Wonderland magic with this mini journal spread! How are you spending your creative Sunday?  #junkjournal #aliceinwonderland #sundaycrafting #creati',
      url: 'https://www.instagram.com/reel/DGto7jiyy1E/',
      embedId: 'DGto7jiyy1E',
      category: 'journal-spread',
      tags: ['junkjournal', 'aliceinwonderland', 'sundaycrafting', 'creativejournaling', 'minijournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DGto7jiyy1E/media/?size=m'
    },
    {
      id: 'ig-api-277',
      platform: 'instagram',
      title: 'You asked, we listened! 🤭',
      description: 'You asked, we listened! 🤭 We’re so excited to introduce our End-of-Month Live Sale, kicking off this Friday, February 28th!  📍 Instagram Live: 5-7 PM CST 📍 TikTok Live: 7-9 PM CST Get ready for exc',
      url: 'https://www.instagram.com/reel/DGltbWUSLDH/',
      embedId: 'DGltbWUSLDH',
      category: 'product-showcase',
      tags: ['livesale', 'exclusivedeals', 'craftingcommunity', 'coracreacrafts', 'craftingsupplies', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DGltbWUSLDH/media/?size=m'
    },
    {
      id: 'ig-api-278',
      platform: 'instagram',
      title: 'Nothing resets my mind like a little crafting. What\'s your go-to craft when y...',
      description: 'Nothing resets my mind like a little crafting. What\'s your go-to craft when you need to relax?  #scrapbooking #craftingtherapy #coracreacrafts #creativeescape #vintagejournaling',
      url: 'https://www.instagram.com/reel/DGjb8nVSfws/',
      embedId: 'DGjb8nVSfws',
      category: 'product-showcase',
      tags: ['scrapbooking', 'craftingtherapy', 'coracreacrafts', 'creativeescape', 'vintagejournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DGjb8nVSfws/media/?size=m'
    },
    {
      id: 'ig-api-279',
      platform: 'instagram',
      title: 'Free wax mold? Yes, please! 🕯✨ Get yours with orders over $50—while supplies...',
      description: 'Free wax mold? Yes, please! 🕯✨ Get yours with orders over $50—while supplies last!  #vintagecrafts #freebiefind #waxsealmagic #creativejournaling #coracreacrafts',
      url: 'https://www.instagram.com/reel/DGgmQo9yFKf/',
      embedId: 'DGgmQo9yFKf',
      category: 'product-showcase',
      tags: ['vintagecrafts', 'freebiefind', 'waxsealmagic', 'creativejournaling', 'coracreacrafts', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DGgmQo9yFKf/media/?size=m'
    },
    {
      id: 'ig-api-280',
      platform: 'instagram',
      title: 'There’s something truly special about birds, and as a fellow bird lover, I’ve...',
      description: 'There’s something truly special about birds, and as a fellow bird lover, I’ve put together some gift ideas for anyone who shares the love for our feathered friends! 🐦💖   🐦 fantasy paper pack - incl',
      url: 'https://www.instagram.com/reel/DGbtquApy4n/',
      embedId: 'DGbtquApy4n',
      category: 'behind-the-scenes',
      tags: ['birdlovers', 'giftideas', 'featheredfriends', 'natureinspired', 'birdiegifts', 'fantasy'],
      creator: 'CoraCreaCrafts',
      date: '2025-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DGbtquApy4n/media/?size=m'
    },
    {
      id: 'ig-api-281',
      platform: 'instagram',
      title: 'Creating this Alice in Wonderland-inspired spread made me want to watch the m...',
      description: 'Creating this Alice in Wonderland-inspired spread made me want to watch the movie again! 🎩🐇🫖🃏 Thank you all for the incredible love on this box—it means so much! You can pre-order it now! ❤️ I use',
      url: 'https://www.instagram.com/reel/DGWdmy1S8Bo/',
      embedId: 'DGWdmy1S8Bo',
      category: 'journal-spread',
      tags: ['alicebox', 'aliceinwonderland', 'alicecrafts', 'craftingcommunity', 'scrapbookinspiration', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DGWdmy1S8Bo/media/?size=m'
    },
    {
      id: 'ig-api-282',
      platform: 'instagram',
      title: 'We want YOU to pick the next Treasure Box theme! ✨',
      description: 'We want YOU to pick the next Treasure Box theme! ✨ Drop a comment below with your favorite theme 1. Dark Academia 2. Dreamscape 3. Vintage Travel 4. Summer vacation 5. Outer space 6. Regency 7. Fairy ',
      url: 'https://www.instagram.com/reel/DGTi04Xpg7j/',
      embedId: 'DGTi04Xpg7j',
      category: 'box-reveal',
      tags: ['treasurebox', 'youdecide', 'vintagecrafts', 'coracreacrafts', 'creativejournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DGTi04Xpg7j/media/?size=m'
    },
    {
      id: 'ig-api-283',
      platform: 'instagram',
      title: '✨ New Treasure Box Alert! ✨ Who\'s excited for this one? ❤️',
      description: '✨ New Treasure Box Alert! ✨ Who\'s excited for this one? ❤️  #treasurebox #mysterybox #boxreveal #smallbusinessfinds #craftlovers',
      url: 'https://www.instagram.com/reel/DGO3XZBsO6I/',
      embedId: 'DGO3XZBsO6I',
      category: 'box-reveal',
      tags: ['treasurebox', 'mysterybox', 'boxreveal', 'smallbusinessfinds', 'craftlovers'],
      creator: 'CoraCreaCrafts',
      date: '2025-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DGO3XZBsO6I/media/?size=m'
    },
    {
      id: 'ig-api-284',
      platform: 'instagram',
      title: 'Treat Yourself This Feb-YOU-ary! 🎀',
      description: 'Treat Yourself This Feb-YOU-ary! 🎀  This month is all about you—your creativity, your joy, and the little things that make you happy! ✨ We’re sharing some of our favorite stickers to add a special to',
      url: 'https://www.instagram.com/reel/DGJlH5ryYjX/',
      embedId: 'DGJlH5ryYjX',
      category: 'product-showcase',
      tags: ['febyouary', 'treatyourself', 'stickerlover', 'vintagejournaling', 'creativejournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DGJlH5ryYjX/media/?size=m'
    },
    {
      id: 'ig-api-285',
      platform: 'instagram',
      title: 'Whether you’re celebrating love, friendship, or simply taking time for yourse...',
      description: 'Whether you’re celebrating love, friendship, or simply taking time for yourself, today is a reminder of the little things that bring us joy 💌  Here’s a special Valentine’s spread filled with vintage ',
      url: 'https://www.instagram.com/reel/DGENzWhvAFV/',
      embedId: 'DGENzWhvAFV',
      category: 'journal-spread',
      tags: ['valentinesday', 'vintagejournaling', 'creativejournaling', 'lovetocreate', 'junkjournallove', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DGENzWhvAFV/media/?size=m'
    },
    {
      id: 'ig-api-286',
      platform: 'instagram',
      title: '✨ GIVEAWAY ALERT ✨',
      description: '✨ GIVEAWAY ALERT ✨ We’re celebrating love week with the ultimate Galentine’s giveaway! You and your bestie could win a $50 CCC gift card to shop all the vintage goodies you love! 💌  How to enter:💫Li',
      url: 'https://www.instagram.com/reel/DF_R-jdvSWE/',
      embedId: 'DF_R-jdvSWE',
      category: 'journal-spread',
      tags: ['galentinesgiveaway', 'valentinesday', 'vintagecrafts', 'creativejournaling', 'giveaway', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DF_R-jdvSWE/media/?size=m'
    },
    {
      id: 'ig-api-287',
      platform: 'instagram',
      title: 'Welcome to the Fourth Wing! ✨ Now I want to decorate and add my own touch to ...',
      description: 'Welcome to the Fourth Wing! ✨ Now I want to decorate and add my own touch to every book… who else feels the same? 📖 #BookishCreativity #CoraCreaCrafts #FourthWingDecor  #VintageAesthetic #CreativeJou',
      url: 'https://www.instagram.com/reel/DF8tIN2vYy4/',
      embedId: 'DF8tIN2vYy4',
      category: 'product-showcase',
      tags: ['bookishcreativity', 'coracreacrafts', 'fourthwingdecor', 'vintageaesthetic', 'creativejournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DF8tIN2vYy4/media/?size=m'
    },
    {
      id: 'ig-api-288',
      platform: 'instagram',
      title: 'Adding a touch of vintage romance to my journal with our newest collection! ...',
      description: 'Adding a touch of vintage romance to my journal with our newest collection! 💌✨ Can’t wait to see the beautiful pages you create! 💕 #CoraCreaCrafts #VintageJournaling #CreativeMoments #VintageAesthet',
      url: 'https://www.instagram.com/reel/DF56jw4viI8/',
      embedId: 'DF56jw4viI8',
      category: 'product-showcase',
      tags: ['coracreacrafts', 'vintagejournaling', 'creativemoments', 'vintageaesthetic', 'journalinglove', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DF56jw4viI8/media/?size=m'
    },
    {
      id: 'ig-api-289',
      platform: 'instagram',
      title: 'There’s something magical about opening a brand new notebook, especially when...',
      description: 'There’s something magical about opening a brand new notebook, especially when it’s our Ancient Arcane Foil Notebook ✨ With its dark academia design, gold foiling, and aged parchment pages, it’s the pe',
      url: 'https://www.instagram.com/reel/DF26ZN6SLqz/',
      embedId: 'DF26ZN6SLqz',
      category: 'product-showcase',
      tags: ['stationeryaddict', 'darkacademia', 'journaling', 'notebooklove', 'stationerylover', 'dark academia'],
      creator: 'CoraCreaCrafts',
      date: '2025-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DF26ZN6SLqz/media/?size=m'
    },
    {
      id: 'ig-api-290',
      platform: 'instagram',
      title: 'I forgot how much fun cut-out spreads can be until I created this one with ou...',
      description: 'I forgot how much fun cut-out spreads can be until I created this one with our Fantasy Winter supplies! ❄️ Anyone else a fan of this style? P.S. Our Fantasy Winter Curiosities Box won’t be around for ',
      url: 'https://www.instagram.com/reel/DFyML9BSeos/',
      embedId: 'DFyML9BSeos',
      category: 'journal-spread',
      tags: ['scrapbooking', 'craftingcommunity', 'fantasywinter', 'cutoutart', 'junkjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DFyML9BSeos/media/?size=m'
    },
    {
      id: 'ig-api-291',
      platform: 'instagram',
      title: 'Our Alice in Wonderland Vintage Craft Box is finally here! 🃏This isn’t the A...',
      description: 'Our Alice in Wonderland Vintage Craft Box is finally here! 🃏This isn’t the Alice you know, you’ll find dark fairies, gothic details, and a whole new twist on the classic tale. I’ve been dying to shar',
      url: 'https://www.instagram.com/reel/DFvuVN8ypK_/',
      embedId: 'DFvuVN8ypK_',
      category: 'box-reveal',
      tags: ['aliceinwonderland', 'gothicaesthetic', 'darkfairytale', 'creativejournaling', 'mixedmediaart', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DFvuVN8ypK_/media/?size=m'
    },
    {
      id: 'ig-api-292',
      platform: 'instagram',
      title: 'So excited to share the unboxing of our brand new Valentines collection! I ho...',
      description: 'So excited to share the unboxing of our brand new Valentines collection! I hope you love it as much as we do 🎀 Tag a friend who needs to add this to their basket! 💖  #valentines2025 #craftinglove #n',
      url: 'https://www.instagram.com/reel/DFrAK5dSV7J/',
      embedId: 'DFrAK5dSV7J',
      category: 'unboxing',
      tags: ['valentines2025', 'craftinglove', 'newarrivals', 'valentinescollection', 'vintageromance', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DFrAK5dSV7J/media/?size=m'
    },
    {
      id: 'ig-api-293',
      platform: 'instagram',
      title: 'Have you ever created a pop-up card? It’s such a fun way to add a personal to...',
      description: 'Have you ever created a pop-up card? It’s such a fun way to add a personal touch to any occasion! ✨ I’ve put together a full step-by-step tutorial over on our YouTube channel to show you how to make y',
      url: 'https://www.instagram.com/reel/DFn3OI9ypsC/',
      embedId: 'DFn3OI9ypsC',
      category: 'tutorial',
      tags: ['diycards', 'crafttutorial', 'popupcard', 'cardmaking', 'diyinspiration'],
      creator: 'CoraCreaCrafts',
      date: '2025-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DFn3OI9ypsC/media/?size=m'
    },
    {
      id: 'ig-api-294',
      platform: 'instagram',
      title: 'You can only pick ONE new arrival… 👀 Which one are you choosing? Drop your a...',
      description: 'You can only pick ONE new arrival… 👀 Which one are you choosing? Drop your answer in the comments! ⬇️✨  #JournalingCommunity #StationeryAddict #CreativeJournaling #NewArrival #JustDropped #ShopNow',
      url: 'https://www.instagram.com/reel/DFihOKIpAlS/',
      embedId: 'DFihOKIpAlS',
      category: 'product-showcase',
      tags: ['journalingcommunity', 'stationeryaddict', 'creativejournaling', 'newarrival', 'justdropped', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DFihOKIpAlS/media/?size=m'
    },
    {
      id: 'ig-api-295',
      platform: 'instagram',
      title: 'Exciting news!! Our Gothic Box is back in stock! 🖤 I’ve been blown away by a...',
      description: 'Exciting news!! Our Gothic Box is back in stock! 🖤 I’ve been blown away by all the love for this box, and it makes me so happy to see your beautiful creations.Don’t miss your chance to grab one! ✨  #',
      url: 'https://www.instagram.com/reel/DFeLjjYMsHT/',
      embedId: 'DFeLjjYMsHT',
      category: 'box-reveal',
      tags: ['gothicaesthetic', 'junkjournal', 'coracreacrafts', 'creativejournaling', 'gothicvibes', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DFeLjjYMsHT/media/?size=m'
    },
    {
      id: 'ig-api-296',
      platform: 'instagram',
      title: 'If you can only pick ONE item from our Legends & Lore Box… 👀 Which one are y...',
      description: 'If you can only pick ONE item from our Legends & Lore Box… 👀 Which one are you choosing? ✨ Don\'t wait too long—we have only have 6 of these limited edition boxes left in stock! ⏳   #LegendsAndLore #L',
      url: 'https://www.instagram.com/reel/DFbWM4jy2BX/',
      embedId: 'DFbWM4jy2BX',
      category: 'box-reveal',
      tags: ['legendsandlore', 'limitededition', 'mysticalfinds', 'vintageaesthetic', 'journalinspo', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DFbWM4jy2BX/media/?size=m'
    },
    {
      id: 'ig-api-297',
      platform: 'instagram',
      title: 'Elven magic awaits! 🧝‍♀️💫',
      description: 'Elven magic awaits! 🧝‍♀️💫 Products used:  Elves Transparent Tape Faux Wax Seals Sticker Sheets Elven Ruins Sticker Box  #ElvenMagic #CraftWithMe #JunkJournal #ScrapbookIdeas #FantasyCrafts #Creative',
      url: 'https://www.instagram.com/reel/DFNyNapRcOV/',
      embedId: 'DFNyNapRcOV',
      category: 'box-reveal',
      tags: ['elvenmagic', 'craftwithme', 'junkjournal', 'scrapbookideas', 'fantasycrafts', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DFNyNapRcOV/media/?size=m'
    },
    {
      id: 'ig-api-298',
      platform: 'instagram',
      title: 'I can’t wait for you to see what’s inside our Fantasy Winter Box! ❄️ From fro...',
      description: 'I can’t wait for you to see what’s inside our Fantasy Winter Box! ❄️ From frosty creatures to enchanted landscapes, every detail is crafted to bring a little magic to your winter season. Let the magic',
      url: 'https://www.instagram.com/reel/DFLPdl8xVWw/',
      embedId: 'DFLPdl8xVWw',
      category: 'unboxing',
      tags: ['fantasywinterbox', 'unboxingmagic', 'wintercrafts', 'fantasyinspired', 'creativemagic', 'fantasy'],
      creator: 'CoraCreaCrafts',
      date: '2025-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DFLPdl8xVWw/media/?size=m'
    },
    {
      id: 'ig-api-299',
      platform: 'instagram',
      title: 'Just a little upcycle magic to keep my crafting space fresh! ✨ Want to see ho...',
      description: 'Just a little upcycle magic to keep my crafting space fresh! ✨ Want to see how it\'s done? Head over to our YouTube for the full step-by-step tutorial! Link in bio   #Upcycling #UpcycledCrafts #DIYProj',
      url: 'https://www.instagram.com/reel/DFJP7N3S5nu/',
      embedId: 'DFJP7N3S5nu',
      category: 'tutorial',
      tags: ['upcycling', 'upcycledcrafts', 'diyprojects', 'craftingcommunity', 'sustainablecrafting'],
      creator: 'CoraCreaCrafts',
      date: '2025-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DFJP7N3S5nu/media/?size=m'
    },
    {
      id: 'ig-api-300',
      platform: 'instagram',
      title: 'Here are a few of our team\'s top picks to brighten your week! What’s on your ...',
      description: 'Here are a few of our team\'s top picks to brighten your week! What’s on your wishlist? 🩵  #MondayMotivation #TeamPicks #WishlistGoals #CraftingCommunity #journalingsupplies',
      url: 'https://www.instagram.com/reel/DFGqvIty9cj/',
      embedId: 'DFGqvIty9cj',
      category: 'product-showcase',
      tags: ['mondaymotivation', 'teampicks', 'wishlistgoals', 'craftingcommunity', 'journalingsupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DFGqvIty9cj/media/?size=m'
    },
    {
      id: 'ig-api-301',
      platform: 'instagram',
      title: 'I absolutely love reading, so this collaboration with @laurafrantzauthor is i...',
      description: 'I absolutely love reading, so this collaboration with @laurafrantzauthor is incredibly special to me! ✨ Bringing her beautiful story, The Indigo Heiress, to life through crafting products has been suc',
      url: 'https://www.instagram.com/reel/DFBbZP5S2jq/',
      embedId: 'DFBbZP5S2jq',
      category: 'product-showcase',
      tags: ['theindigoheiress', 'laurafrantz', 'historicalfiction', 'bookishcrafts', 'craftingcommunity'],
      creator: 'CoraCreaCrafts',
      date: '2025-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DFBbZP5S2jq/media/?size=m'
    },
    {
      id: 'ig-api-302',
      platform: 'instagram',
      title: '✨ New Box Alert! ✨ I’m so excited to introduce our newest quarterly subscript...',
      description: '✨ New Box Alert! ✨ I’m so excited to introduce our newest quarterly subscription box, Legends & Lore Fantasy Collection! In collaboration with @storm_sparrow this box brings your favorite stories to l',
      url: 'https://www.instagram.com/reel/DE8KJQjSj3L/',
      embedId: 'DE8KJQjSj3L',
      category: 'box-reveal',
      tags: ['fantasyaddict', 'bookstagram', 'fantasybooks', 'fantasyfandom', 'fantasycrafts', 'advent calendar'],
      creator: 'CoraCreaCrafts',
      date: '2025-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DE8KJQjSj3L/media/?size=m'
    },
    {
      id: 'ig-api-303',
      platform: 'instagram',
      title: 'Something is coming tomorrow ✨  Can you guess what it is',
      description: 'Something is coming tomorrow ✨  Can you guess what it is?  #NewProductAlert #NewArrival #LaunchingSoon #SmallBusinessLove #StationeryLovers  #fourthwing #onyxstorm',
      url: 'https://www.instagram.com/reel/DE4_fc6M03v/',
      embedId: 'DE4_fc6M03v',
      category: 'product-showcase',
      tags: ['newproductalert', 'newarrival', 'launchingsoon', 'smallbusinesslove', 'stationerylovers'],
      creator: 'CoraCreaCrafts',
      date: '2025-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DE4_fc6M03v/media/?size=m'
    },
    {
      id: 'ig-api-304',
      platform: 'instagram',
      title: 'Using our Elven-themed supplies, I created this Lost City of Gondolin small s...',
      description: 'Using our Elven-themed supplies, I created this Lost City of Gondolin small spread and I’m loving how it turned out! Small spreads feel less intimidating yet still pack so much charm 🌿✨  Do you prefe',
      url: 'https://www.instagram.com/reel/DEyR9hjsMVM/',
      embedId: 'DEyR9hjsMVM',
      category: 'journal-spread',
      tags: ['elvencrafts', 'smallspreads', 'creativejournaling', 'coracreacrafts', 'scrapbooking', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DEyR9hjsMVM/media/?size=m'
    },
    {
      id: 'ig-api-305',
      platform: 'instagram',
      title: 'Dark, mysterious, and oh-so-vintage 🖤',
      description: 'Dark, mysterious, and oh-so-vintage 🖤 I couldn’t resist creating this card with our Gothic Vintage Craft Box!  It’s full of moody gothic vibes that are perfect for this time of year.  Who’s excited t',
      url: 'https://www.instagram.com/reel/DEqab2ySYyN/',
      embedId: 'DEqab2ySYyN',
      category: 'box-reveal',
      tags: ['gothiccrafts', 'vintagecrafting', 'creativejournaling', 'moodyvibes', 'gothicaesthetic', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DEqab2ySYyN/media/?size=m'
    },
    {
      id: 'ig-api-306',
      platform: 'instagram',
      title: '2025 is here, and so are our new arrivals! 😍 We’ve got tons of new goodies u...',
      description: '2025 is here, and so are our new arrivals! 😍 We’ve got tons of new goodies up on the site, with more videos coming your way!  Which one is your favorite? 👀   #NewArrivals#CoraCreaCrafts #CreativeJou',
      url: 'https://www.instagram.com/reel/DElUPN6sWtO/',
      embedId: 'DElUPN6sWtO',
      category: 'product-showcase',
      tags: ['newarrivals', 'coracreacrafts', 'creativejournaling', 'vintagevibes', 'craftingsupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DElUPN6sWtO/media/?size=m'
    },
    {
      id: 'ig-api-307',
      platform: 'instagram',
      title: 'Who’s excited for the first VCB of 2025? 👀 Say hello to our Gothic box 🖤',
      description: 'Who’s excited for the first VCB of 2025? 👀 Say hello to our Gothic box 🖤  #GothicCrafts #VictorianVibes #CoraCreaCrafts #CraftSubscription #VintageCraftBox #CreativeInspo #DarkAesthetics',
      url: 'https://www.instagram.com/reel/DEgBzN0yfMw/',
      embedId: 'DEgBzN0yfMw',
      category: 'box-reveal',
      tags: ['gothiccrafts', 'victorianvibes', 'coracreacrafts', 'craftsubscription', 'vintagecraftbox', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DEgBzN0yfMw/media/?size=m'
    },
    {
      id: 'ig-api-308',
      platform: 'instagram',
      title: 'Raise your hand if you could use a pastry right now 🙋‍♀️! 🧁🥐',
      description: 'Raise your hand if you could use a pastry right now 🙋‍♀️! 🧁🥐   #BakingSupplies #CreativeKitchen #CoraCreaCrafts #ShopNow #CreativeJournaling #Scrapbooking',
      url: 'https://www.instagram.com/reel/DEYR9F1ycyW/',
      embedId: 'DEYR9F1ycyW',
      category: 'product-showcase',
      tags: ['bakingsupplies', 'creativekitchen', 'coracreacrafts', 'shopnow', 'creativejournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DEYR9F1ycyW/media/?size=m'
    },
    {
      id: 'ig-api-309',
      platform: 'instagram',
      title: 'Happy New Year! 🎉',
      description: 'Happy New Year! 🎉 What better way to start 2025 than by sharing how I’ve organized my Planning Companion for the year ahead? I absolutely love how versatile this planner is—it’s perfect for keeping m',
      url: 'https://www.instagram.com/reel/DEVy5hHM8Uh/',
      embedId: 'DEVy5hHM8Uh',
      category: 'product-showcase',
      tags: ['2025planner', 'planningcompanion', 'creativeplanning', 'newyearorganization', 'coracreacrafts', 'stickers'],
      creator: 'CoraCreaCrafts',
      date: '2025-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DEVy5hHM8Uh/media/?size=m'
    },
    {
      id: 'ig-api-310',
      platform: 'instagram',
      title: 'After the busy holiday season, I finally had a chance to sit down and craft w...',
      description: 'After the busy holiday season, I finally had a chance to sit down and craft with the beautiful supplies from our Elven Box. I’m loving how these pieces brought a little bit of magic and calm to my cre',
      url: 'https://www.instagram.com/reel/DEIjM19yLRs/',
      embedId: 'DEIjM19yLRs',
      category: 'tutorial',
      tags: ['elvenbox', 'craftingafterchristmas', 'creativejournaling', 'coracreacrafts', 'fantasycrafting', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DEIjM19yLRs/media/?size=m'
    },
    {
      id: 'ig-api-311',
      platform: 'instagram',
      title: 'Merry Christmas from all of us here at CoraCreaCrafts! 🎄',
      description: 'Merry Christmas from all of us here at CoraCreaCrafts! 🎄 Thank you for your incredible support this year, while we went through big transitions. Your passion for crafting has made everything we do po',
      url: 'https://www.instagram.com/reel/DEBEpRZSaDP/',
      embedId: 'DEBEpRZSaDP',
      category: 'product-showcase',
      tags: ['merrychristmas', 'thankyou', 'craftingcommunity', 'grateful', 'smallbusinesssupport', 'christmas'],
      creator: 'CoraCreaCrafts',
      date: '2024-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DEBEpRZSaDP/media/?size=m'
    },
    {
      id: 'ig-api-312',
      platform: 'instagram',
      title: '🚨 It’s finally here—Day 24 of the Advent Calendar! 🎁As we unwrap the last s...',
      description: '🚨 It’s finally here—Day 24 of the Advent Calendar! 🎁As we unwrap the last surprise, I’m thrilled to reveal something you’ve all been asking for: an organizer for your supplies! 🗂️Thank you for join',
      url: 'https://www.instagram.com/reel/DD-sx97S4Up/',
      embedId: 'DD-sx97S4Up',
      category: 'box-reveal',
      tags: ['adventcalendar', 'stickerfolder', 'stickerorganizer', 'craftingessentials', 'newrelease', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DD-sx97S4Up/media/?size=m'
    },
    {
      id: 'ig-api-313',
      platform: 'instagram',
      title: 'Creating our Digital Journal Kits has been one of the highlights of this year...',
      description: 'Creating our Digital Journal Kits has been one of the highlights of this year for me. I love knowing that they give everyone a chance to sit down, get creative, and make something beautiful—no matter ',
      url: 'https://www.instagram.com/reel/DD8EYHByEer/',
      embedId: 'DD8EYHByEer',
      category: 'product-showcase',
      tags: ['digitaljournalkit', 'creativejourney', 'craftyourstory', 'journalinspiration', 'creativetools', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DD8EYHByEer/media/?size=m'
    },
    {
      id: 'ig-api-314',
      platform: 'instagram',
      title: 'I’ve been so excited to share this with you—our Elven Treasure Box is finally...',
      description: 'I’ve been so excited to share this with you—our Elven Treasure Box is finally here! ✨ Every piece in this box was chosen to bring a little magic and wonder to your crafting. Unboxing it feels like ste',
      url: 'https://www.instagram.com/reel/DD0fgXuSaZF/',
      embedId: 'DD0fgXuSaZF',
      category: 'unboxing',
      tags: ['magicalstationery', 'creativejourney', 'craftingmagic', 'stationerylover', 'mysticalvibes', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DD0fgXuSaZF/media/?size=m'
    },
    {
      id: 'ig-api-315',
      platform: 'instagram',
      title: 'Every year, I look forward to making my own Christmas tags—it’s so much fun a...',
      description: 'Every year, I look forward to making my own Christmas tags—it’s so much fun and adds such a special handmade touch to gifts! 🎁 Have you ever tried making your own tags? Let me know in the comments!  ',
      url: 'https://www.instagram.com/reel/DDxQAfgSXCe/',
      embedId: 'DDxQAfgSXCe',
      category: 'tutorial',
      tags: ['coracreacrafts', 'creativejournaling', 'diychristmastags', 'handmadeholidays', 'christmascrafting', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DDxQAfgSXCe/media/?size=m'
    },
    {
      id: 'ig-api-316',
      platform: 'instagram',
      title: 'I haven’t reached for my mini journal much over the last few months, but crea...',
      description: 'I haven’t reached for my mini journal much over the last few months, but creating this spread with supplies from The Writing Desk Box reminded me just how much I’ve missed it! There’s something so mag',
      url: 'https://www.instagram.com/reel/DDp8zuMSn8E/',
      embedId: 'DDp8zuMSn8E',
      category: 'journal-spread',
      tags: ['coracreacrafts', 'thewritingdeskbox', 'creativejournaling', 'journalspread', 'minijournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DDp8zuMSn8E/media/?size=m'
    },
    {
      id: 'ig-api-317',
      platform: 'instagram',
      title: 'Can you believe this whole spread came from just one washi tape',
      description: 'Can you believe this whole spread came from just one washi tape?   I used the Gothic Washi Tape, and I’m obsessed with how it turned out! Sometimes, keeping it simple works best.What’s your go-to wash',
      url: 'https://www.instagram.com/reel/DDiGFWNRawE/',
      embedId: 'DDiGFWNRawE',
      category: 'journal-spread',
      tags: ['gothicchristmas', 'darkholidayvibes', 'christmaswithatwist', 'gothicjournaling', 'creativejournaling', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2024-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DDiGFWNRawE/media/?size=m'
    },
    {
      id: 'ig-api-318',
      platform: 'instagram',
      title: 'The wait is over! Our 12 Days of Christmas starts TOMORROW! 🎁',
      description: 'The wait is over! Our 12 Days of Christmas starts TOMORROW! 🎁 Every day, we’ll reveal a brand-new surprise on our socials and website—think giveaways, special deals, and festive fun you won’t want to',
      url: 'https://www.instagram.com/reel/DDfqs8lR5_p/',
      embedId: 'DDfqs8lR5_p',
      category: 'box-reveal',
      tags: ['holidaycountdown', 'christmasdeals', 'holidaygiveaways', 'stationerylovers', 'craftychristmas', 'christmas'],
      creator: 'CoraCreaCrafts',
      date: '2024-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DDfqs8lR5_p/media/?size=m'
    },
    {
      id: 'ig-api-319',
      platform: 'instagram',
      title: 'I had so much fun putting together this double-page spread using the goodies ...',
      description: 'I had so much fun putting together this double-page spread using the goodies from December’s Writing Desk Box. It’s always so special to see the pieces come to life on the page, it feels like I’m tell',
      url: 'https://www.instagram.com/reel/DDdIDWpxpG1/',
      embedId: 'DDdIDWpxpG1',
      category: 'journal-spread',
      tags: ['coracreacrafts', 'thewritingdeskbox', 'stationeryaddict', 'creativejournaling', 'junkjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DDdIDWpxpG1/media/?size=m'
    },
    {
      id: 'ig-api-320',
      platform: 'instagram',
      title: 'Why buy wrapping paper when you can make your own? 👀 Handmade wrapping paper...',
      description: 'Why buy wrapping paper when you can make your own? 👀 Handmade wrapping paper makes every gift extra special, because the wrapping should be just as thoughtful as what’s inside! 🎁  #HandmadeWrappingP',
      url: 'https://www.instagram.com/reel/DDYBC8ASPH9/',
      embedId: 'DDYBC8ASPH9',
      category: 'tutorial',
      tags: ['handmadewrappingpaper', 'giftwrappingideas', 'creativegifts', 'diygiftwrap', 'wrappingpapercrafts'],
      creator: 'CoraCreaCrafts',
      date: '2024-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DDYBC8ASPH9/media/?size=m'
    },
    {
      id: 'ig-api-321',
      platform: 'instagram',
      title: 'Who knew coffee could transform into art? ☕✨',
      description: 'Who knew coffee could transform into art? ☕✨ Using coffee as paint/stainer adds the perfect touch of vintage warmth to your journal spreads. This spread is extra special, featuring treasures from our ',
      url: 'https://www.instagram.com/reel/DDVZR_BxU7b/',
      embedId: 'DDVZR_BxU7b',
      category: 'journal-spread',
      tags: ['coffeeart', 'journalingideas', 'creativejournaling', 'vintageaesthetic', 'craftingcommunity', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DDVZR_BxU7b/media/?size=m'
    },
    {
      id: 'ig-api-322',
      platform: 'instagram',
      title: 'Here’s my first double-page Christmas spread using our Christmas supplies! 🎄...',
      description: 'Here’s my first double-page Christmas spread using our Christmas supplies! 🎄✨ Have you started your Christmas crafting yet 👀 #journalingsupplies #christmasjournaling #christmastime #asmrjournaling #',
      url: 'https://www.instagram.com/reel/DDQEp8ixNXp/',
      embedId: 'DDQEp8ixNXp',
      category: 'journal-spread',
      tags: ['journalingsupplies', 'christmasjournaling', 'christmastime', 'asmrjournaling', 'scrapbooksupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DDQEp8ixNXp/media/?size=m'
    },
    {
      id: 'ig-api-323',
      platform: 'instagram',
      title: 'Here’s a look inside the December Vintage Craft Box, our Writers Box edition,...',
      description: 'Here’s a look inside the December Vintage Craft Box, our Writers Box edition, chosen as a fan favorite by YOU! 📜✒️ This box is so special to me, and I hope you all love it as much as I do. 🖤 Let me ',
      url: 'https://www.instagram.com/reel/DDNa7FDREM5/',
      embedId: 'DDNa7FDREM5',
      category: 'box-reveal',
      tags: ['journalingsupplies', 'asmrjournaling', 'scrapbooksupplies', 'creativejournaling', 'desksupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DDNa7FDREM5/media/?size=m'
    },
    {
      id: 'ig-api-324',
      platform: 'instagram',
      title: 'I absolutely love upcycling, and my latest project was transforming a simple ...',
      description: 'I absolutely love upcycling, and my latest project was transforming a simple box into something adorable using our advent calendar packaging! 🎄 So many of you asked if this packaging would be availab',
      url: 'https://www.instagram.com/reel/DDInYqjSvs7/',
      embedId: 'DDInYqjSvs7',
      category: 'behind-the-scenes',
      tags: ['advent calendar'],
      creator: 'CoraCreaCrafts',
      date: '2024-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DDInYqjSvs7/media/?size=m'
    },
    {
      id: 'ig-api-325',
      platform: 'instagram',
      title: 'SPOILER!! 🚨 Day 1 of Advent is finally here! ✨ I’ve been counting down the d...',
      description: 'SPOILER!! 🚨 Day 1 of Advent is finally here! ✨ I’ve been counting down the days for you to start opening your calendars and couldn’t wait to share today’s surprise! What do you think of this festive ',
      url: 'https://www.instagram.com/reel/DDDSMy8xx1w/',
      embedId: 'DDDSMy8xx1w',
      category: 'product-showcase',
      tags: ['journalingsupplies', 'adventcalendar', 'creativejournaling', 'journalinginspiration', 'scrapbooksupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DDDSMy8xx1w/media/?size=m'
    },
    {
      id: 'ig-api-326',
      platform: 'instagram',
      title: 'One more sleep until December 1st… who’s ready to start opening their advent ...',
      description: 'One more sleep until December 1st… who’s ready to start opening their advent calendars? 👀🎄📆  #hellodecember #adventcalendar #1stofadvent #christmastime #creativejournaling #scrapbooksupplies',
      url: 'https://www.instagram.com/reel/DDBbucGRi9U/',
      embedId: 'DDBbucGRi9U',
      category: 'product-showcase',
      tags: ['hellodecember', 'adventcalendar', '1stofadvent', 'christmastime', 'creativejournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DDBbucGRi9U/media/?size=m'
    },
    {
      id: 'ig-api-327',
      platform: 'instagram',
      title: 'Our BIGGEST surprise of the year is finally here, and it’s packed with deals ...',
      description: 'Our BIGGEST surprise of the year is finally here, and it’s packed with deals you’ll LOVE: 🛍️ Up to 30% off sitewide 🎁 Spend $100+ to enter a raffle for a chance to win a mystery box full with curate',
      url: 'https://www.instagram.com/reel/DC9ltw6xxM_/',
      embedId: 'DC9ltw6xxM_',
      category: 'behind-the-scenes',
      tags: ['subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DC9ltw6xxM_/media/?size=m'
    },
    {
      id: 'ig-api-328',
      platform: 'instagram',
      title: 'Coffee as watercolor? Yes, please! ☕🎨',
      description: 'Coffee as watercolor? Yes, please! ☕🎨  I used real coffee to create the background for this spread, and I’m loving how it turned out. Paired with goodies from our Vintage Baking and Cooking Box! Have',
      url: 'https://www.instagram.com/reel/DC7dfcaRj2o/',
      embedId: 'DC7dfcaRj2o',
      category: 'journal-spread',
      tags: ['journalingsupplies', 'coffeespread', 'coffeedyedpaper', 'asmrjournaling', 'creativejournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DC7dfcaRj2o/media/?size=m'
    },
    {
      id: 'ig-api-329',
      platform: 'instagram',
      title: 'Double the pages, double the creativity! 😉 I\'ve been loving the flow of crea...',
      description: 'Double the pages, double the creativity! 😉 I\'ve been loving the flow of creating double-page spreads lately, there\'s something so satisfying about seeing the full picture come together!  Do you craft',
      url: 'https://www.instagram.com/reel/DC5CIc0xW1z/',
      embedId: 'DC5CIc0xW1z',
      category: 'journal-spread',
      tags: ['journalingsupplies', 'creativejournaling', 'journalinginspiration', 'asmrjournal', 'autumncrafts', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DC5CIc0xW1z/media/?size=m'
    },
    {
      id: 'ig-api-330',
      platform: 'instagram',
      title: 'Exciting news! Our Grab Bags are back! This time, we’re offering different si...',
      description: 'Exciting news! Our Grab Bags are back! This time, we’re offering different sizes to choose from, each packed with a wide selection of treasures you’ll love 😍 These are always a favorite and won’t las',
      url: 'https://www.instagram.com/reel/DC1zJstRxQo/',
      embedId: 'DC1zJstRxQo',
      category: 'behind-the-scenes',
      tags: ['grabbags', 'scrapbooksupplies', 'journalingsupplies', 'blackfridaysale', 'creativejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DC1zJstRxQo/media/?size=m'
    },
    {
      id: 'ig-api-331',
      platform: 'instagram',
      title: 'Everyone I have an announcement! 📣',
      description: 'Everyone I have an announcement! 📣  Our Black Friday sale week starts now!!  Check back here daily for exclusive deals you won’t want to miss, leading up to a huge surprise on Friday, the 29th! 👀  G',
      url: 'https://www.instagram.com/reel/DCzSSKARbw1/',
      embedId: 'DCzSSKARbw1',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DCzSSKARbw1/media/?size=m'
    },
    {
      id: 'ig-api-332',
      platform: 'instagram',
      title: 'I started this spread during a TikTok live and just had to share the finishin...',
      description: 'I started this spread during a TikTok live and just had to share the finishing touches with you ✨ All created using supplies from our Celebration of Women treasure box! Have you grabbed yours yet? 👀 ',
      url: 'https://www.instagram.com/reel/DCxZ5HGR7xI/',
      embedId: 'DCxZ5HGR7xI',
      category: 'journal-spread',
      tags: ['womensbox', 'scrapbooksupplies', 'asmrjournaling', 'creativejournal', 'journalinginspiration', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DCxZ5HGR7xI/media/?size=m'
    },
    {
      id: 'ig-api-333',
      platform: 'instagram',
      title: 'Step into the Middle Ages with our November Digital Journal Subscription! Kni...',
      description: 'Step into the Middle Ages with our November Digital Journal Subscription! Knights, castles, and medieval charm await, perfect for your next journaling spread! 🏰✨ #digitalkit #digitajournalkit #junkjo',
      url: 'https://www.instagram.com/reel/DCuF2KtRZZ_/',
      embedId: 'DCuF2KtRZZ_',
      category: 'journal-spread',
      tags: ['digitalkit', 'digitajournalkit', 'junkjournalkit', 'creativejournaling', 'scrapbooksupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DCuF2KtRZZ_/media/?size=m'
    },
    {
      id: 'ig-api-334',
      platform: 'instagram',
      title: 'SPOILER ALERT! It’s time for a sneak peek inside day 12 of our advent calenda...',
      description: 'SPOILER ALERT! It’s time for a sneak peek inside day 12 of our advent calendar - and trust me it’s a good one!🤭 Don’t miss this exclusive look at what’s waiting behind the door ✨  #journalingsupplies',
      url: 'https://www.instagram.com/reel/DCsKeDtRVJv/',
      embedId: 'DCsKeDtRVJv',
      category: 'box-reveal',
      tags: ['journalingsupplies', 'asmrjournaling', 'adventcalendar', 'craftingsupplies', 'craftingadventcalendar', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DCsKeDtRVJv/media/?size=m'
    },
    {
      id: 'ig-api-335',
      platform: 'instagram',
      title: 'Ready to add some flavor to your crafting? 🥐🍪☕️',
      description: 'Ready to add some flavor to your crafting? 🥐🍪☕️  Our newest Curiosities Box: Baking & Cooking is here, and I’ve been so excited to share it with you! This box has everything you need to add a sprink',
      url: 'https://www.instagram.com/reel/DCpqxDjREI9/',
      embedId: 'DCpqxDjREI9',
      category: 'box-reveal',
      tags: ['journalingsupplies', 'cozybakery', 'asmrjournaling', 'scrapbooksupplies', 'journalinginspiration', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DCpqxDjREI9/media/?size=m'
    },
    {
      id: 'ig-api-336',
      platform: 'instagram',
      title: 'The cutest, pinkest spread I\'ve ever made! ✨ Playing with our new A Celebrati...',
      description: 'The cutest, pinkest spread I\'ve ever made! ✨ Playing with our new A Celebration of Women treasure box and I\'m obsessed 💝  #pinkspread #pinkaesthetic #womensbox #creativejournaling #asmrjournal #journ',
      url: 'https://www.instagram.com/reel/DCmyVOQx1f7/',
      embedId: 'DCmyVOQx1f7',
      category: 'journal-spread',
      tags: ['pinkspread', 'pinkaesthetic', 'womensbox', 'creativejournaling', 'asmrjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DCmyVOQx1f7/media/?size=m'
    },
    {
      id: 'ig-api-337',
      platform: 'instagram',
      title: 'Only ONE WEEK left to grab our Medieval Vintage Craft Box! 🏰 Don’t miss your...',
      description: 'Only ONE WEEK left to grab our Medieval Vintage Craft Box! 🏰 Don’t miss your chance to own this enchanting collection of medieval-inspired treasures ✨  #journalingsupplies #medieval #journalinginspir',
      url: 'https://www.instagram.com/reel/DCjxHxtR829/',
      embedId: 'DCjxHxtR829',
      category: 'box-reveal',
      tags: ['journalingsupplies', 'medieval', 'journalinginspiration', 'medievaljournal', 'creativejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DCjxHxtR829/media/?size=m'
    },
    {
      id: 'ig-api-338',
      platform: 'instagram',
      title: 'Our BRAND NEW DIY Tote Bag Kits have landed! 👜 Get creative with three style...',
      description: 'Our BRAND NEW DIY Tote Bag Kits have landed! 👜 Get creative with three styles to choose from: Witchy, Nature, and Forests. Perfect for gifting or crafting your own unique tote!   #diykit #diycrafts #',
      url: 'https://www.instagram.com/reel/DCh-047xA5U/',
      embedId: 'DCh-047xA5U',
      category: 'tutorial',
      tags: ['diykit', 'diycrafts', 'journalingsupplies', 'creativejournaling', 'journalinginspiration', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DCh-047xA5U/media/?size=m'
    },
    {
      id: 'ig-api-339',
      platform: 'instagram',
      title: 'Turn your wounds into Wisdom 🌿 using the cards & affirmation stickers from o...',
      description: 'Turn your wounds into Wisdom 🌿 using the cards & affirmation stickers from our ‘A Celebration of Women’ Treasure Box! I’m so excited to see all the beautiful spreads you’ll create with it. Don’t forg',
      url: 'https://www.instagram.com/reel/DCfb2DER7sD/',
      embedId: 'DCfb2DER7sD',
      category: 'journal-spread',
      tags: ['journalingsupplies', 'creativejournaling', 'creativejournal', 'journalinginspiration', 'asmrjournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DCfb2DER7sD/media/?size=m'
    },
    {
      id: 'ig-api-340',
      platform: 'instagram',
      title: 'Our Medieval Vintage Box features a special collaboration with the talented M...',
      description: 'Our Medieval Vintage Box features a special collaboration with the talented Meggie Retrow Boutique @meggieretrowboutique bringing you these stunning paper dolls! ✨ I had so much fun assembling mine!! ',
      url: 'https://www.instagram.com/reel/DCcZzWdxJzd/',
      embedId: 'DCcZzWdxJzd',
      category: 'box-reveal',
      tags: ['journalingsupplies', 'medievalart', 'knightdoll', 'paperdoll', 'creativejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DCcZzWdxJzd/media/?size=m'
    },
    {
      id: 'ig-api-341',
      platform: 'instagram',
      title: 'After 3 months of hard work, our new website is finally live 🤩 Go explore th...',
      description: 'After 3 months of hard work, our new website is finally live 🤩 Go explore the new features and get 20% off today! If you have are an existing customer please click on create account and use your same',
      url: 'https://www.instagram.com/reel/DCZpfLSRoPw/',
      embedId: 'DCZpfLSRoPw',
      category: 'product-showcase',
      tags: ['journalingsupplies', 'newwebsite', 'scrapbooksupplies', 'journalinginspiration', 'smallbusiness', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DCZpfLSRoPw/media/?size=m'
    },
    {
      id: 'ig-api-342',
      platform: 'instagram',
      title: 'Creating spreads using our Medieval Vintage Craft Box has been so much fun!! ...',
      description: 'Creating spreads using our Medieval Vintage Craft Box has been so much fun!! The supplies in this box are such a joy to work with 💫  Have you got yours yet?  #medieval #journalingsupplies #creativejo',
      url: 'https://www.instagram.com/reel/DCSVabMR9ou/',
      embedId: 'DCSVabMR9ou',
      category: 'journal-spread',
      tags: ['medieval', 'journalingsupplies', 'creativejournaling', 'scrapbooksupplies', 'fantasyart', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DCSVabMR9ou/media/?size=m'
    },
    {
      id: 'ig-api-343',
      platform: 'instagram',
      title: 'Our lovely ambassador Liu created this stunning Autumn Spread using the suppl...',
      description: 'Our lovely ambassador Liu created this stunning Autumn Spread using the supplies from our Air of Autumn Box. You can buy these individually on the website now too! 🧡  #journalingsupplies #asmrjournal',
      url: 'https://www.instagram.com/reel/DCPmnINx4dl/',
      embedId: 'DCPmnINx4dl',
      category: 'journal-spread',
      tags: ['journalingsupplies', 'asmrjournaling', 'creativejournaling', 'scrapbooksupplies', 'autumncrafts', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DCPmnINx4dl/media/?size=m'
    },
    {
      id: 'ig-api-344',
      platform: 'instagram',
      title: 'SPOILERS 🚨 I’m absolutely blown away by how many of you have already grabbed...',
      description: 'SPOILERS 🚨 I’m absolutely blown away by how many of you have already grabbed a 2024 Advent Calendar! 🎄 If you haven’t gotten yours yet, there’s still time! Make this holiday season extra special and',
      url: 'https://www.instagram.com/reel/DCNWUBpRr80/',
      embedId: 'DCNWUBpRr80',
      category: 'product-showcase',
      tags: ['adventcalendar', 'asmrjournaling', 'creativejournal', 'journalingsupplies', 'stationerylovers', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DCNWUBpRr80/media/?size=m'
    },
    {
      id: 'ig-api-345',
      platform: 'instagram',
      title: 'Introducing \'A Celebration of Women\' our newest Treasure Box! There are a bun...',
      description: 'Introducing \'A Celebration of Women\' our newest Treasure Box! There are a bunch of surprise items not displayed here or on the photos 🤫 This box is so special to me and I\'m super excited to announce ',
      url: 'https://www.instagram.com/reel/DCH-pxeR0ZK/',
      embedId: 'DCH-pxeR0ZK',
      category: 'box-reveal',
      tags: ['journalingsupplies', 'womeninbusiness', 'womenpower', 'womensupportingwomen', 'creativejournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DCH-pxeR0ZK/media/?size=m'
    },
    {
      id: 'ig-api-346',
      platform: 'instagram',
      title: 'It’s finally time to reveal a sneak peek of our November Vintage Craft Box! ✨...',
      description: 'It’s finally time to reveal a sneak peek of our November Vintage Craft Box! ✨ This Medieval theme holds a special place in my heart. I can’t wait for you to experience the magic and charm of this coll',
      url: 'https://www.instagram.com/reel/DCC9gU-xZXg/',
      embedId: 'DCC9gU-xZXg',
      category: 'box-reveal',
      tags: ['medieval', 'journalingsupplies', 'asmrjournaling', 'creativejournaling', 'journalinginspiration', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DCC9gU-xZXg/media/?size=m'
    },
    {
      id: 'ig-api-347',
      platform: 'instagram',
      title: 'SNEAKS PEAKS IN VIDEO 🚨  Stop everything! The 1st of December will soon be h...',
      description: 'SNEAKS PEAKS IN VIDEO 🚨  Stop everything! The 1st of December will soon be here. Have you got your Advent Calendar yet? 💫  #adventcalendar #asmrjournaling #creativejournaling #scrapbooksupplies #chr',
      url: 'https://www.instagram.com/reel/DCAMULFxSUk/',
      embedId: 'DCAMULFxSUk',
      category: 'box-reveal',
      tags: ['adventcalendar', 'asmrjournaling', 'creativejournaling', 'scrapbooksupplies', 'christmastime', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DCAMULFxSUk/media/?size=m'
    },
    {
      id: 'ig-api-348',
      platform: 'instagram',
      title: 'Take a look at my new favorite journal! ✨',
      description: 'Take a look at my new favorite journal! ✨  Meet our new Owl Grimoire Foil Notebook which I hope will inspire you on your creative journey! It\'s a hardcover with beautiful foiled gold decorations on th',
      url: 'https://www.instagram.com/reel/DB95mCDxRZG/',
      embedId: 'DB95mCDxRZG',
      category: 'product-showcase',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DB95mCDxRZG/media/?size=m'
    },
    {
      id: 'ig-api-349',
      platform: 'instagram',
      title: 'Make some Autumnal Tags with me! I wanted to try and make something a little ...',
      description: 'Make some Autumnal Tags with me! I wanted to try and make something a little different with the supplies from my An Air of Autumn Box and loved how these turned out 🍂 What\'s your favorite things to c',
      url: 'https://www.instagram.com/reel/DB7SPXSRZxV/',
      embedId: 'DB7SPXSRZxV',
      category: 'tutorial',
      tags: ['fallcrafts', 'journalingsupplies', 'diytags', 'asmrjournaling', 'creativejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DB7SPXSRZxV/media/?size=m'
    },
    {
      id: 'ig-api-350',
      platform: 'instagram',
      title: 'SPOILER ALERT 🚨 It\'s officially 1 month until the 1st of December today!! 🎄...',
      description: 'SPOILER ALERT 🚨 It\'s officially 1 month until the 1st of December today!! 🎄 Who\'s got their hands on the CoraCreaCrafts 2024 Advent Calendar so far?👀    #adventcalendar #stationerylovers #craftinga',
      url: 'https://www.instagram.com/reel/DB1_bMLxQZw/',
      embedId: 'DB1_bMLxQZw',
      category: 'product-showcase',
      tags: ['adventcalendar', 'stationerylovers', 'craftingaddict', 'scrapbooksupplies', 'journalinginspiration', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DB1_bMLxQZw/media/?size=m'
    },
    {
      id: 'ig-api-351',
      platform: 'instagram',
      title: 'I can\'t believe Halloween is tomorrow, Spooky Season has flown by! 👻 This be...',
      description: 'I can\'t believe Halloween is tomorrow, Spooky Season has flown by! 👻 This beautiful spread was made by our ambassador @liu_journal using the supplies from our Halloween collection 🎃 How will you be ',
      url: 'https://www.instagram.com/reel/DBwYBZtxm1_/',
      embedId: 'DBwYBZtxm1_',
      category: 'journal-spread',
      tags: ['halloweencandles', 'halloweenspread', 'asmrjournaling', 'journalingsupplies', 'journalinginspiration', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DBwYBZtxm1_/media/?size=m'
    },
    {
      id: 'ig-api-352',
      platform: 'instagram',
      title: 'I thought it would be fun for me to take you through a timestamped day in my ...',
      description: 'I thought it would be fun for me to take you through a timestamped day in my life! Thank you all for giving me my dream job running CoraCreaCrafts ❤️',
      url: 'https://www.instagram.com/reel/DBrkw1KR7WX/',
      embedId: 'DBrkw1KR7WX',
      category: 'behind-the-scenes',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DBrkw1KR7WX/media/?size=m'
    },
    {
      id: 'ig-api-353',
      platform: 'instagram',
      title: 'I absolutely loved making this small style journal out of our October Digital...',
      description: 'I absolutely loved making this small style journal out of our October Digital Journal Kit! I was so happy with how it turned out 😍 Has anyone tried doing this before?',
      url: 'https://www.instagram.com/reel/DBpUEYKxem4/',
      embedId: 'DBpUEYKxem4',
      category: 'product-showcase',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DBpUEYKxem4/media/?size=m'
    },
    {
      id: 'ig-api-354',
      platform: 'instagram',
      title: 'It\'s almost Halloween! To celebrate spooky season you can get 20% off our Hal...',
      description: 'It\'s almost Halloween! To celebrate spooky season you can get 20% off our Halloween Collection using code LASTMIN on our website 👻  Let us know in the comments what items you picked up 🎃  #halloween',
      url: 'https://www.instagram.com/reel/DBj9HKdxnrk/',
      embedId: 'DBj9HKdxnrk',
      category: 'product-showcase',
      tags: ['halloweencandles', 'halloweenstickers', 'halloweenjournal', 'asmrjournaling', 'journalinspiration', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DBj9HKdxnrk/media/?size=m'
    },
    {
      id: 'ig-api-355',
      platform: 'instagram',
      title: 'There\'s only a few days left to get your hands on our Vintage Pharmacy Vintag...',
      description: 'There\'s only a few days left to get your hands on our Vintage Pharmacy Vintage Craft Box! I loved how this small spread turned out 😍   #vintagepharmacy #pharmacybottles #asmrjournaling #creativejourn',
      url: 'https://www.instagram.com/reel/DBhROyrxvaX/',
      embedId: 'DBhROyrxvaX',
      category: 'journal-spread',
      tags: ['vintagepharmacy', 'pharmacybottles', 'asmrjournaling', 'creativejournal', 'scrapbookingasmr', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DBhROyrxvaX/media/?size=m'
    },
    {
      id: 'ig-api-356',
      platform: 'instagram',
      title: 'Our 2024 Advent Calendar reveal is finally here!! I have been so excited for ...',
      description: 'Our 2024 Advent Calendar reveal is finally here!! I have been so excited for this moment and can\'t wait for you all to see the new packaging and sneak peak of the surprises inside 🎁  Who\'s excited fo',
      url: 'https://www.instagram.com/reel/DBeqhgVxbly/',
      embedId: 'DBeqhgVxbly',
      category: 'box-reveal',
      tags: ['adventcalendar', 'stationerylovers', 'creativegifts', 'journalingsupplies', 'craftingsupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DBeqhgVxbly/media/?size=m'
    },
    {
      id: 'ig-api-357',
      platform: 'instagram',
      title: 'Who\'s excited for Halloween? 👻  I\'m trying to make the most out of my Hallow...',
      description: 'Who\'s excited for Halloween? 👻  I\'m trying to make the most out of my Halloween crafting before the season is over! There\'s still time to shop our Halloween Collection to add some magic to your sprea',
      url: 'https://www.instagram.com/reel/DBcqPPqxim2/',
      embedId: 'DBcqPPqxim2',
      category: 'journal-spread',
      tags: ['halloween'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DBcqPPqxim2/media/?size=m'
    },
    {
      id: 'ig-api-358',
      platform: 'instagram',
      title: 'Have you tried our Digital Journal Kit yet? The themes of these follow our mo...',
      description: 'Have you tried our Digital Journal Kit yet? The themes of these follow our monthly Vintage Craft Box and are perfect if you want to try something different (without the clutter!) 🙌   #digitajournalki',
      url: 'https://www.instagram.com/reel/DBaMrvyxHvf/',
      embedId: 'DBaMrvyxHvf',
      category: 'box-reveal',
      tags: ['digitajournalkit', 'journalingsupplies', 'junkjournalkit', 'digitalkit', 'scrapbooksupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DBaMrvyxHvf/media/?size=m'
    },
    {
      id: 'ig-api-359',
      platform: 'instagram',
      title: 'Our workshop is less than 1 week away - I\'m so excited! 🥹 Here is a tour of ...',
      description: 'Our workshop is less than 1 week away - I\'m so excited! 🥹 Here is a tour of the beautiful space where it will be held!  Don\'t forget its on 26th October 4pm-6pm at Forevermore in Nashville. We still ',
      url: 'https://www.instagram.com/reel/DBXNMTQxEYl/',
      embedId: 'DBXNMTQxEYl',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DBXNMTQxEYl/media/?size=m'
    },
    {
      id: 'ig-api-360',
      platform: 'instagram',
      title: 'Double page Ancient Pharmacy spread ✨ I\'ve really loved working with this the...',
      description: 'Double page Ancient Pharmacy spread ✨ I\'ve really loved working with this theme so far ☺️   Announcement: we will be a few days late shipping your VCBs this month due to a delay receiving supplies, th',
      url: 'https://www.instagram.com/reel/DBR3G2ax70-/',
      embedId: 'DBR3G2ax70-',
      category: 'journal-spread',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DBR3G2ax70-/media/?size=m'
    },
    {
      id: 'ig-api-361',
      platform: 'instagram',
      title: 'Harry Potter is one of my favorite things to watch this time of year 🖤 What ...',
      description: 'Harry Potter is one of my favorite things to watch this time of year 🖤 What theme would you like to see next?',
      url: 'https://www.instagram.com/reel/DBMOYiARTlH/',
      embedId: 'DBMOYiARTlH',
      category: 'product-showcase',
      tags: ['harry potter'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DBMOYiARTlH/media/?size=m'
    },
    {
      id: 'ig-api-362',
      platform: 'instagram',
      title: 'I\'m obsessed with our Leather Pouch for Fall 🍂  It’s my go-to for every cozy...',
      description: 'I\'m obsessed with our Leather Pouch for Fall 🍂  It’s my go-to for every cozy autumn adventure! What’s your fall essential? 🍁',
      url: 'https://www.instagram.com/reel/DBHuFrLxloC/',
      embedId: 'DBHuFrLxloC',
      category: 'product-showcase',
      tags: ['autumn', 'advent calendar'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DBHuFrLxloC/media/?size=m'
    },
    {
      id: 'ig-api-363',
      platform: 'instagram',
      title: 'I finally made my first spread in my new journal using our restocked Mysterie...',
      description: 'I finally made my first spread in my new journal using our restocked Mysteries of The Night supplies ✨ I love the Specimen Ephemera Cutouts 🖤  #journalinspiration #asmrjournaling #craftingsupplies #s',
      url: 'https://www.instagram.com/reel/DBFES_0xhIo/',
      embedId: 'DBFES_0xhIo',
      category: 'journal-spread',
      tags: ['journalinspiration', 'asmrjournaling', 'craftingsupplies', 'scrapbookingasmr', 'creativejournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DBFES_0xhIo/media/?size=m'
    },
    {
      id: 'ig-api-364',
      platform: 'instagram',
      title: 'Vintage Pharmacy is here! ✨',
      description: 'Vintage Pharmacy is here! ✨  This box even comes with a special treat inside 👀 Which product is your favorite? Now available on our website!',
      url: 'https://www.instagram.com/reel/DBAH6zlximN/',
      embedId: 'DBAH6zlximN',
      category: 'box-reveal',
      tags: ['vintage'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DBAH6zlximN/media/?size=m'
    },
    {
      id: 'ig-api-365',
      platform: 'instagram',
      title: 'Make a spooky card with me using some of my favorite Halloween supplies! 🖤👻...',
      description: 'Make a spooky card with me using some of my favorite Halloween supplies! 🖤👻 Have you started your halloween crafting yet? 🎃   #journalingsupplies #cardmaking #halloweencandles #halloweencards #hall',
      url: 'https://www.instagram.com/reel/DA6NwfvRJvk/',
      embedId: 'DA6NwfvRJvk',
      category: 'product-showcase',
      tags: ['journalingsupplies', 'cardmaking', 'halloweencandles', 'halloweencards', 'halloweenstickers', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DA6NwfvRJvk/media/?size=m'
    },
    {
      id: 'ig-api-366',
      platform: 'instagram',
      title: 'Part 2 of my quick-fire Q&A is here! I hope you all enjoyed watching as much ...',
      description: 'Part 2 of my quick-fire Q&A is here! I hope you all enjoyed watching as much as I did answering these questions! Let me know if you\'d love to see more of these in the future! 👀💬  #journalingsupplies',
      url: 'https://www.instagram.com/reel/DA3jGjhR-T9/',
      embedId: 'DA3jGjhR-T9',
      category: 'product-showcase',
      tags: ['journalingsupplies', 'creativejournaling', 'journalinspiration', 'smallbusinessowner', 'smallbizowner', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DA3jGjhR-T9/media/?size=m'
    },
    {
      id: 'ig-api-367',
      platform: 'instagram',
      title: 'Make a Autumnal tote bag with me while I tell you all the details about our u...',
      description: 'Make a Autumnal tote bag with me while I tell you all the details about our upcoming workshop here in Nashville! Have you got your ticket yet?👀  #nashvillecrafting #nashvillecreatives #nashvilleevent',
      url: 'https://www.instagram.com/reel/DAzAqGNxtfh/',
      embedId: 'DAzAqGNxtfh',
      category: 'tutorial',
      tags: ['nashvillecrafting', 'nashvillecreatives', 'nashvilleevents', 'nashvilleworkshop', 'nashvillecreates', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DAzAqGNxtfh/media/?size=m'
    },
    {
      id: 'ig-api-368',
      platform: 'instagram',
      title: 'Today I\'m celebrating 32 years of youth! To celebrate I\'m doing a 15% off + y...',
      description: 'Today I\'m celebrating 32 years of youth! To celebrate I\'m doing a 15% off + you\'ll receive a gift from me for any order placed today on the website. Use the code: BIRTHDAYGIRL 🎉  Here\'s a fun rapid-f',
      url: 'https://www.instagram.com/reel/DAtVOGNxRjl/',
      embedId: 'DAtVOGNxRjl',
      category: 'product-showcase',
      tags: ['journalingsupplies', 'smallbusiness', 'stationerylovers', 'stationerystore', 'womeninbusiness', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DAtVOGNxRjl/media/?size=m'
    },
    {
      id: 'ig-api-369',
      platform: 'instagram',
      title: 'You heard it here first! @adriannasjournals and I are hosting our first ever ...',
      description: 'You heard it here first! @adriannasjournals and I are hosting our first ever workshop in Nashville! Join us for \'Autumn Artistry\': A DIY Canvas Bag Decoration Workshop where you can transform a simple',
      url: 'https://www.instagram.com/reel/DAqwlHbxxAN/',
      embedId: 'DAqwlHbxxAN',
      category: 'tutorial',
      tags: ['nashvilleevents', 'nashvillecrafting', 'nashvillecreatives', 'journalingevents', 'autumncrafts', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DAqwlHbxxAN/media/?size=m'
    },
    {
      id: 'ig-api-370',
      platform: 'instagram',
      title: 'TikTok Shop Live Host Wanted in Nashville',
      description: 'TikTok Shop Live Host Wanted in Nashville! We\'re looking for a vibrant TikTok Shop Live Host to join our team! DM us for all the details! ✨  #nashvillejobs #tiktoklivehost #livehost #smallbusiness #st',
      url: 'https://www.instagram.com/reel/DAos-UiR-Sb/',
      embedId: 'DAos-UiR-Sb',
      category: 'product-showcase',
      tags: ['nashvillejobs', 'tiktoklivehost', 'livehost', 'smallbusiness', 'stationerylovers'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DAos-UiR-Sb/media/?size=m'
    },
    {
      id: 'ig-api-371',
      platform: 'instagram',
      title: 'October is here! 🎃 It\'s only right to kick off spooky season with a witchy-i...',
      description: 'October is here! 🎃 It\'s only right to kick off spooky season with a witchy-inspired tutorial 🧙‍♀️ What Halloween spreads are you planning to create this month? 👻  #witchyvibes #halloweenjournal #ha',
      url: 'https://www.instagram.com/reel/DAllGsLR2YM/',
      embedId: 'DAllGsLR2YM',
      category: 'journal-spread',
      tags: ['witchyvibes', 'halloweenjournal', 'halloweenspread', 'journalingsupplies', 'creativejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DAllGsLR2YM/media/?size=m'
    },
    {
      id: 'ig-api-372',
      platform: 'instagram',
      title: 'I wasn\'t sad exactly 😅 who else',
      description: 'I wasn\'t sad exactly 😅 who else?   #craftingsupplies #craftingaddict #journalingsupplies #journalingcommunity #halloweenjournal #halloweencandles',
      url: 'https://www.instagram.com/reel/DAjVTe3R_RG/',
      embedId: 'DAjVTe3R_RG',
      category: 'product-showcase',
      tags: ['craftingsupplies', 'craftingaddict', 'journalingsupplies', 'journalingcommunity', 'halloweenjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DAjVTe3R_RG/media/?size=m'
    },
    {
      id: 'ig-api-373',
      platform: 'instagram',
      title: 'Autumn magic unboxed! 🍂📦 Our Ambassador @liu_journal reveals the enchanting...',
      description: 'Autumn magic unboxed! 🍂📦 Our Ambassador @liu_journal reveals the enchanting contents of our \'An Air of Autumn Curiosities Box.\' Who else wants to get their hands on this? 👀  #journalingsupplies #au',
      url: 'https://www.instagram.com/reel/DAgU8wNxPwe/',
      embedId: 'DAgU8wNxPwe',
      category: 'unboxing',
      tags: ['journalingsupplies', 'autumnjournal', 'fallcrafts', 'creativejournal', 'asmrjournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DAgU8wNxPwe/media/?size=m'
    },
    {
      id: 'ig-api-374',
      platform: 'instagram',
      title: 'Have you explored our exclusive CCC X ViaVee leather collection yet? 👜✨ Whic...',
      description: 'Have you explored our exclusive CCC X ViaVee leather collection yet? 👜✨ Which piece catches your eye?  #journalingsupplies #leatheritems #asmrjournaling #creativejournal #handmadeleather',
      url: 'https://www.instagram.com/reel/DAeH83URqfE/',
      embedId: 'DAeH83URqfE',
      category: 'product-showcase',
      tags: ['journalingsupplies', 'leatheritems', 'asmrjournaling', 'creativejournal', 'handmadeleather', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DAeH83URqfE/media/?size=m'
    },
    {
      id: 'ig-api-375',
      platform: 'instagram',
      title: 'Spooky season has arrived! 🎃👻 Check out our newest Halloween arrivals, perf...',
      description: 'Spooky season has arrived! 🎃👻 Check out our newest Halloween arrivals, perfect for adding some magic and mystery to your crafts. 🕸️✨ Which one is your favorite?  #halloweenjournal #journalingsuppli',
      url: 'https://www.instagram.com/reel/DAbF47mxhEy/',
      embedId: 'DAbF47mxhEy',
      category: 'product-showcase',
      tags: ['halloweenjournal', 'journalingsupplies', 'creativejournaling', 'scrapbooksupplies', 'halloweendecor', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DAbF47mxhEy/media/?size=m'
    },
    {
      id: 'ig-api-376',
      platform: 'instagram',
      title: 'Ready to start scrapbooking? 🌙 This beginner-friendly tutorial using our Mys...',
      description: 'Ready to start scrapbooking? 🌙 This beginner-friendly tutorial using our Mysteries of the Night Treasure Box is perfect for first-timers or anyone seeking easy inspiration ✨  Follow along and create ',
      url: 'https://www.instagram.com/reel/DAWqbUnxU4c/',
      embedId: 'DAWqbUnxU4c',
      category: 'tutorial',
      tags: ['scrapbookinglayout', 'scrapbookingasmr', 'creativejournaling', 'journalinginspiration', 'creativejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DAWqbUnxU4c/media/?size=m'
    },
    {
      id: 'ig-api-377',
      platform: 'instagram',
      title: 'Back by request - a Water Themed spread using our Four Elements Vintage Craft...',
      description: 'Back by request - a Water Themed spread using our Four Elements Vintage Craft Box! 🌊✨ Theres only 3 days left to get our September Box so don\'t miss out!  #journalingsupplies #asmrjournaling #creativ',
      url: 'https://www.instagram.com/reel/DATedcpP2jM/',
      embedId: 'DATedcpP2jM',
      category: 'journal-spread',
      tags: ['journalingsupplies', 'asmrjournaling', 'creativejournaling', 'scrapbooksupplies', '4elements', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DATedcpP2jM/media/?size=m'
    },
    {
      id: 'ig-api-378',
      platform: 'instagram',
      title: 'Today marks the official start of fall 🍂 That crisp air, those golden leaves...',
      description: 'Today marks the official start of fall 🍂 That crisp air, those golden leaves... and our Air of Autumn Curiosities box capturing it all! Comment below what you are most excited for this fall?',
      url: 'https://www.instagram.com/reel/DAPHqaGxqY0/',
      embedId: 'DAPHqaGxqY0',
      category: 'product-showcase',
      tags: ['autumn'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DAPHqaGxqY0/media/?size=m'
    },
    {
      id: 'ig-api-379',
      platform: 'instagram',
      title: 'September\'s VCB is straight fire... and water, earth, and air too! 😉 Have yo...',
      description: 'September\'s VCB is straight fire... and water, earth, and air too! 😉 Have you made a large spread using our 4 elements box yet?',
      url: 'https://www.instagram.com/reel/DALpd94xR6P/',
      embedId: 'DALpd94xR6P',
      category: 'journal-spread',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DALpd94xR6P/media/?size=m'
    },
    {
      id: 'ig-api-380',
      platform: 'instagram',
      title: 'Dreams, sketched in passion, now stitched in leather. 🖤',
      description: 'Dreams, sketched in passion, now stitched in leather. 🖤 Feeling these designs in my hands is surreal. I can\'t wait for you to experience them too. Which piece do you like the most? 👀',
      url: 'https://www.instagram.com/reel/DAJLPrCxPVc/',
      embedId: 'DAJLPrCxPVc',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DAJLPrCxPVc/media/?size=m'
    },
    {
      id: 'ig-api-381',
      platform: 'instagram',
      title: 'Part 2 ✨ Mysteries of the Night Treasures ➡️ we are restocking more of this b...',
      description: 'Part 2 ✨ Mysteries of the Night Treasures ➡️ we are restocking more of this box due to popular demand! Link in bio 🩶  #journalingsupplies #nightsky #mysteriesofthenighr #treasures #stationerylovers',
      url: 'https://www.instagram.com/reel/DAGyxINxkDh/',
      embedId: 'DAGyxINxkDh',
      category: 'product-showcase',
      tags: ['journalingsupplies', 'nightsky', 'mysteriesofthenighr', 'treasures', 'stationerylovers', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DAGyxINxkDh/media/?size=m'
    },
    {
      id: 'ig-api-382',
      platform: 'instagram',
      title: 'The Best is yet to come 😊 Lemon themed Table Cards with @hesterandcook and o...',
      description: 'The Best is yet to come 😊 Lemon themed Table Cards with @hesterandcook and our Vineyards & Orchards products 🍋  #motivationalquotes #journalingsupplies #tablecards #asmrjournaling #journalinginspira',
      url: 'https://www.instagram.com/reel/DABSrUSxydk/',
      embedId: 'DABSrUSxydk',
      category: 'product-showcase',
      tags: ['motivationalquotes', 'journalingsupplies', 'tablecards', 'asmrjournaling', 'journalinginspiration', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/DABSrUSxydk/media/?size=m'
    },
    {
      id: 'ig-api-383',
      platform: 'instagram',
      title: 'Scraptember! ✂️ This month we want to showcase your lovely journal pages and ...',
      description: 'Scraptember! ✂️ This month we want to showcase your lovely journal pages and projects using our CCC products. Just tag us & use the #scraptemberccc until 25th of September - we will share our favorite',
      url: 'https://www.instagram.com/reel/C__BKBtRv3d/',
      embedId: 'C__BKBtRv3d',
      category: 'journal-spread',
      tags: ['scraptemberccc', 'journalingsupplies', 'journalimgcommunity', 'scrapbookcommunity', 'scrapbooking', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C__BKBtRv3d/media/?size=m'
    },
    {
      id: 'ig-api-384',
      platform: 'instagram',
      title: 'Which color & which style do you prefer',
      description: 'Which color & which style do you prefer? ➡️ Map, Nude, Blue, Grey, Chocolate  ➡️ Small Journal, Belt Bag, Phone Bag, Zipper Wallet, Ephemera Pouch, Pouch Bag   #creativejournaling #leatherbag #scrapbo',
      url: 'https://www.instagram.com/reel/C_6Sti0RPPA/',
      embedId: 'C_6Sti0RPPA',
      category: 'product-showcase',
      tags: ['creativejournaling', 'leatherbag', 'scrapbooksupplies', 'journalinspiration', 'handmadeleather', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C_6Sti0RPPA/media/?size=m'
    },
    {
      id: 'ig-api-385',
      platform: 'instagram',
      title: 'Friday the 13th 🩶☘️ Do you believe in creating your own luck',
      description: 'Friday the 13th 🩶☘️ Do you believe in creating your own luck?  #journalingsupplies #asmrjournaling #creativejournaling #stamping #journalinginspiration',
      url: 'https://www.instagram.com/reel/C_3qdsTxXwK/',
      embedId: 'C_3qdsTxXwK',
      category: 'product-showcase',
      tags: ['journalingsupplies', 'asmrjournaling', 'creativejournaling', 'stamping', 'journalinginspiration', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C_3qdsTxXwK/media/?size=m'
    },
    {
      id: 'ig-api-386',
      platform: 'instagram',
      title: 'Which element should I do next',
      description: 'Which element should I do next?   #fire #elements #4elements #nature #journalinginspiration',
      url: 'https://www.instagram.com/reel/C_04IVdx5Is/',
      embedId: 'C_04IVdx5Is',
      category: 'product-showcase',
      tags: ['fire', 'elements', '4elements', 'nature', 'journalinginspiration', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C_04IVdx5Is/media/?size=m'
    },
    {
      id: 'ig-api-387',
      platform: 'instagram',
      title: 'BTS of making a wooden sign for CCC 😍 this was such a fun project',
      description: 'BTS of making a wooden sign for CCC 😍 this was such a fun project!   #artmarket #artbooth #marketsetup #artistmarket #farmersmarket #scrapbooksupplies #journalinginspiration',
      url: 'https://www.instagram.com/reel/C_vi6NJxMcI/',
      embedId: 'C_vi6NJxMcI',
      category: 'behind-the-scenes',
      tags: ['artmarket', 'artbooth', 'marketsetup', 'artistmarket', 'farmersmarket', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C_vi6NJxMcI/media/?size=m'
    },
    {
      id: 'ig-api-388',
      platform: 'instagram',
      title: 'Which one do you think matches the best',
      description: 'Which one do you think matches the best?? #journalingsupplies #dresses #stationery #creativejournaling',
      url: 'https://www.instagram.com/reel/C_tVJclxd_p/',
      embedId: 'C_tVJclxd_p',
      category: 'product-showcase',
      tags: ['journalingsupplies', 'dresses', 'stationery', 'creativejournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C_tVJclxd_p/media/?size=m'
    },
    {
      id: 'ig-api-389',
      platform: 'instagram',
      title: 'Highlights part 1. From our Mysteries of the Night Treasure Box ✨ We are runn...',
      description: 'Highlights part 1. From our Mysteries of the Night Treasure Box ✨ We are running a little late with it but part 2 is coming soon, shipping at the end of the month 🌛 we only have about 25 left in stoc',
      url: 'https://www.instagram.com/reel/C_lx7eBRbcq/',
      embedId: 'C_lx7eBRbcq',
      category: 'product-showcase',
      tags: ['journalingsupplies', 'mysteriesofthenight', 'owls', 'asmrjournaling', 'creativejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C_lx7eBRbcq/media/?size=m'
    },
    {
      id: 'ig-api-390',
      platform: 'instagram',
      title: 'What are you most excited about when getting an advent calendar? 📆 I think f...',
      description: 'What are you most excited about when getting an advent calendar? 📆 I think for me it\'s just the element of surprise wondering what each new days will bring✨  #adventcalendar #stationeryadventcalendar',
      url: 'https://www.instagram.com/reel/C_jSfLnRC4-/',
      embedId: 'C_jSfLnRC4-',
      category: 'product-showcase',
      tags: ['adventcalendar', 'stationeryadventcalendar', 'craftingsupplies', 'creativejournaling', 'journaling', 'advent calendar'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C_jSfLnRC4-/media/?size=m'
    },
    {
      id: 'ig-api-391',
      platform: 'instagram',
      title: 'An Air of Autumn 🍁 do you also feel it in the air already',
      description: 'An Air of Autumn 🍁 do you also feel it in the air already? Our new box celebrating this beautiful season is now available for order!  #creativejournaling #journalingsupplies #scrapbooksupplies #autum',
      url: 'https://www.instagram.com/reel/C_gE9FCRwBK/',
      embedId: 'C_gE9FCRwBK',
      category: 'product-showcase',
      tags: ['creativejournaling', 'journalingsupplies', 'scrapbooksupplies', 'autumnvibes', 'asmrjournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C_gE9FCRwBK/media/?size=m'
    },
    {
      id: 'ig-api-392',
      platform: 'instagram',
      title: 'We are looking to collab with a female owned business on a special project 💁...',
      description: 'We are looking to collab with a female owned business on a special project 💁‍♀️ Tag any brands below or DM to learn more 👯‍♀️   #werelookingfor #pov #comedy #coworkers #nashvillesmallbusiness #femal',
      url: 'https://www.instagram.com/reel/C_dVzuARwXc/',
      embedId: 'C_dVzuARwXc',
      category: 'product-showcase',
      tags: ['werelookingfor', 'pov', 'comedy', 'coworkers', 'nashvillesmallbusiness'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C_dVzuARwXc/media/?size=m'
    },
    {
      id: 'ig-api-393',
      platform: 'instagram',
      title: 'Let\'s pack a grab bag together ❤️',
      description: 'Let\'s pack a grab bag together ❤️',
      url: 'https://www.instagram.com/reel/C_Y6gm5x4Vw/',
      embedId: 'C_Y6gm5x4Vw',
      category: 'behind-the-scenes',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C_Y6gm5x4Vw/media/?size=m'
    },
    {
      id: 'ig-api-394',
      platform: 'instagram',
      title: 'Presenting all the different grab bags we have this time ❤️🙌 you can find th...',
      description: 'Presenting all the different grab bags we have this time ❤️🙌 you can find them on our website!',
      url: 'https://www.instagram.com/reel/C_Wp3z7x-5Q/',
      embedId: 'C_Wp3z7x-5Q',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C_Wp3z7x-5Q/media/?size=m'
    },
    {
      id: 'ig-api-395',
      platform: 'instagram',
      title: 'They\'re back 😍📦 6 different types/sizes, there\'s one for everyone',
      description: 'They\'re back 😍📦 6 different types/sizes, there\'s one for everyone!  #grabbags #creativejournal #scrapbooksupplies #asmrjournal #labordaysale',
      url: 'https://www.instagram.com/reel/C_T2BMXR1e4/',
      embedId: 'C_T2BMXR1e4',
      category: 'product-showcase',
      tags: ['grabbags', 'creativejournal', 'scrapbooksupplies', 'asmrjournal', 'labordaysale', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C_T2BMXR1e4/media/?size=m'
    },
    {
      id: 'ig-api-396',
      platform: 'instagram',
      title: 'We are so excited for our 2024 Advent Calendars 😍 while these sneak peeks ar...',
      description: 'We are so excited for our 2024 Advent Calendars 😍 while these sneak peeks are from last year this might give you a little idea of what you could receive! The whole packaging design and products will ',
      url: 'https://www.instagram.com/reel/C_Ovk3GRNgx/',
      embedId: 'C_Ovk3GRNgx',
      category: 'box-reveal',
      tags: ['asmrjournaling', 'adventcalendar', 'craftinginspiration', 'craftingsupplies', 'scrapbooksupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C_Ovk3GRNgx/media/?size=m'
    },
    {
      id: 'ig-api-397',
      platform: 'instagram',
      title: '4 Elements Box 🌬️🔥🌱💧',
      description: '4 Elements Box 🌬️🔥🌱💧 Which product is your favorite? Now available on our website!  #journalingsupplies #asmrjournaling #creativejournaling #scrapbooksupplies #journalinginspiration',
      url: 'https://www.instagram.com/reel/C_MM6uHRQbB/',
      embedId: 'C_MM6uHRQbB',
      category: 'product-showcase',
      tags: ['journalingsupplies', 'asmrjournaling', 'creativejournaling', 'scrapbooksupplies', 'journalinginspiration', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C_MM6uHRQbB/media/?size=m'
    },
    {
      id: 'ig-api-398',
      platform: 'instagram',
      title: 'Another American West vibe spread 🤠 I really enjoyed working with this theme...',
      description: 'Another American West vibe spread 🤠 I really enjoyed working with this theme! On the 27th we are switching to the next theme, are you ready to find out what it is? 😍  #wildwest #cowboytheme #vintage',
      url: 'https://www.instagram.com/reel/C_G3VikRvRE/',
      embedId: 'C_G3VikRvRE',
      category: 'journal-spread',
      tags: ['wildwest', 'cowboytheme', 'vintagejournal', 'asmrjournaling', 'journalingsupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C_G3VikRvRE/media/?size=m'
    },
    {
      id: 'ig-api-399',
      platform: 'instagram',
      title: 'Table Cards meet Scrapbooking 🤗 what do you think about creating some collag...',
      description: 'Table Cards meet Scrapbooking 🤗 what do you think about creating some collage table cards for your next dinner party?   I used these wonderful cards I got from @hesterandcook and my supplies from the',
      url: 'https://www.instagram.com/reel/C_Bk1hgRF2k/',
      embedId: 'C_Bk1hgRF2k',
      category: 'product-showcase',
      tags: ['scrapbooksupplies', 'tablesetting', 'journalingsupplies', 'tablecards', 'tabledecorasmr', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C_Bk1hgRF2k/media/?size=m'
    },
    {
      id: 'ig-api-400',
      platform: 'instagram',
      title: 'Minimalistic Collage Spread 🤎 I was super inspired by @rositonacostudio and ...',
      description: 'Minimalistic Collage Spread 🤎 I was super inspired by @rositonacostudio and her amazing collages and decided to make something a little different   #collagejournal #journalingsupplies #creativejourna',
      url: 'https://www.instagram.com/reel/C-8kS1YR6ZH/',
      embedId: 'C-8kS1YR6ZH',
      category: 'journal-spread',
      tags: ['collagejournal', 'journalingsupplies', 'creativejournaling', 'asmrcraft', 'scrapbooksupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C-8kS1YR6ZH/media/?size=m'
    },
    {
      id: 'ig-api-401',
      platform: 'instagram',
      title: 'Pack a big order with me from our new warehouse 😍📦',
      description: 'Pack a big order with me from our new warehouse 😍📦  #orderpacking #asmrjournaling #journalingkit #journalingsupplies',
      url: 'https://www.instagram.com/reel/C-5ZnylRZnU/',
      embedId: 'C-5ZnylRZnU',
      category: 'behind-the-scenes',
      tags: ['orderpacking', 'asmrjournaling', 'journalingkit', 'journalingsupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C-5ZnylRZnU/media/?size=m'
    },
    {
      id: 'ig-api-402',
      platform: 'instagram',
      title: 'I love using my inkpads, pastes and paints 🤎',
      description: 'I love using my inkpads, pastes and paints 🤎  #mixedmediaart #journalinspiration #artjournaling #creativejournal',
      url: 'https://www.instagram.com/reel/C-3hW-YR3ub/',
      embedId: 'C-3hW-YR3ub',
      category: 'product-showcase',
      tags: ['mixedmediaart', 'journalinspiration', 'artjournaling', 'creativejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C-3hW-YR3ub/media/?size=m'
    },
    {
      id: 'ig-api-403',
      platform: 'instagram',
      title: 'Which one is your favorite',
      description: 'Which one is your favorite?  #2 is my favorite and you can sign up for restock notification on the website 😊  #washitapelove #pettape #asmrjournaling #journalingsupplies #journalinginspiration #creat',
      url: 'https://www.instagram.com/reel/C-1Dio3Rx1p/',
      embedId: 'C-1Dio3Rx1p',
      category: 'product-showcase',
      tags: ['2', 'washitapelove', 'pettape', 'asmrjournaling', 'journalingsupplies', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2024-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C-1Dio3Rx1p/media/?size=m'
    },
    {
      id: 'ig-api-404',
      platform: 'instagram',
      title: 'A new spread in my mini journal 🤗📖',
      description: 'A new spread in my mini journal 🤗📖   #minijournal #wildwest #asmrjournal #scrapbookingideas #journalrelaxation',
      url: 'https://www.instagram.com/reel/C-vleNQxY1s/',
      embedId: 'C-vleNQxY1s',
      category: 'journal-spread',
      tags: ['minijournal', 'wildwest', 'asmrjournal', 'scrapbookingideas', 'journalrelaxation', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C-vleNQxY1s/media/?size=m'
    },
    {
      id: 'ig-api-405',
      platform: 'instagram',
      title: 'Just showing off some of my favorite washi tapes part 1 - special cutout tape...',
      description: 'Just showing off some of my favorite washi tapes part 1 - special cutout tapes 😍 should we do a part 2 with other types of tapes?  #washitape #asmrjournaling #journalingsupplies #creativejournaling #',
      url: 'https://www.instagram.com/reel/C-st28uRfo4/',
      embedId: 'C-st28uRfo4',
      category: 'product-showcase',
      tags: ['washitape', 'asmrjournaling', 'journalingsupplies', 'creativejournaling', 'journalinginspiration', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2024-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C-st28uRfo4/media/?size=m'
    },
    {
      id: 'ig-api-406',
      platform: 'instagram',
      title: 'Wild West Vibes 🤎',
      description: 'Wild West Vibes 🤎   #journalingsupplies #wildwest #creativejournaling #journalinginspiration #asmrjournal',
      url: 'https://www.instagram.com/reel/C-k3vb2xNSI/',
      embedId: 'C-k3vb2xNSI',
      category: 'product-showcase',
      tags: ['journalingsupplies', 'wildwest', 'creativejournaling', 'journalinginspiration', 'asmrjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C-k3vb2xNSI/media/?size=m'
    },
    {
      id: 'ig-api-407',
      platform: 'instagram',
      title: 'Are you more a large spread, small or mini spread kind of gal? 📖 While I enj...',
      description: 'Are you more a large spread, small or mini spread kind of gal? 📖 While I enjoy the mini format for its practicality and ease of use, larger spreads (A5 single or double pages) is what revel in! 😍 #j',
      url: 'https://www.instagram.com/reel/C-jAHjqxEy-/',
      embedId: 'C-jAHjqxEy-',
      category: 'journal-spread',
      tags: ['journalinspiration', 'journalingtypes', 'asmrjournaling', 'flipthroughjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C-jAHjqxEy-/media/?size=m'
    },
    {
      id: 'ig-api-408',
      platform: 'instagram',
      title: 'What do you think about this unpopular opinion? 😅',
      description: 'What do you think about this unpopular opinion? 😅  #gameofthrones #houseofdragons #asmrjournaling #fantasyart #fantasyjournal #bookstagram',
      url: 'https://www.instagram.com/reel/C-dHW-oxHzH/',
      embedId: 'C-dHW-oxHzH',
      category: 'product-showcase',
      tags: ['gameofthrones', 'houseofdragons', 'asmrjournaling', 'fantasyart', 'fantasyjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C-dHW-oxHzH/media/?size=m'
    },
    {
      id: 'ig-api-409',
      platform: 'instagram',
      title: 'Vineyards Mini Journal 🍷such a beautiful spread by @suchi_journalive with th...',
      description: 'Vineyards Mini Journal 🍷such a beautiful spread by @suchi_journalive with the Vineyards & Orchards Collection ❤️ I love a good wine tasting at a vineyard in the summer!   #vintagestationery #winelove',
      url: 'https://www.instagram.com/reel/C-YMMhnRDWv/',
      embedId: 'C-YMMhnRDWv',
      category: 'journal-spread',
      tags: ['vintagestationery', 'winelovers', 'minijournal', 'journalinspiration', 'journalrelaxation', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C-YMMhnRDWv/media/?size=m'
    },
    {
      id: 'ig-api-410',
      platform: 'instagram',
      title: 'Tales of the North 🤎  You have been loving this theme just as much as I did ...',
      description: 'Tales of the North 🤎  You have been loving this theme just as much as I did I think because we only have 3 boxes left 😳  #vikings #valhalla #journalingsupplies #creativejournaling #journalinginspira',
      url: 'https://www.instagram.com/reel/C-S5PdnxQoK/',
      embedId: 'C-S5PdnxQoK',
      category: 'product-showcase',
      tags: ['vikings', 'valhalla', 'journalingsupplies', 'creativejournaling', 'journalinginspiration', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C-S5PdnxQoK/media/?size=m'
    },
    {
      id: 'ig-api-411',
      platform: 'instagram',
      title: 'The full behind the scenes of my move to Nashville 😳 The past few weeks have...',
      description: 'The full behind the scenes of my move to Nashville 😳 The past few weeks have honestly been some of the most challenging time of my life.. With the move came a lot of stress, unexpected things and iss',
      url: 'https://www.instagram.com/reel/C-Qz-ezRSz9/',
      embedId: 'C-Qz-ezRSz9',
      category: 'behind-the-scenes',
      tags: ['smallbusinessvlog', 'smallbusinessmove', 'creativejournaling', 'smallbizlife', 'movingvlog', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C-Qz-ezRSz9/media/?size=m'
    },
    {
      id: 'ig-api-412',
      platform: 'instagram',
      title: 'Watching House of Dragons has made me want to dig into the Game of Thrones bo...',
      description: 'Watching House of Dragons has made me want to dig into the Game of Thrones books again! I decided to decorate the pages of my book to personalize it a little bit 😍 using the Fantasy items from the sh',
      url: 'https://www.instagram.com/reel/C-JEG5lxPkT/',
      embedId: 'C-JEG5lxPkT',
      category: 'product-showcase',
      tags: ['houseofdragons', 'got', 'bookstagram', 'booklover', 'scrapbooking', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C-JEG5lxPkT/media/?size=m'
    },
    {
      id: 'ig-api-413',
      platform: 'instagram',
      title: 'Happy Friendship Day ❤️ so grateful for all the friendships I have found with...',
      description: 'Happy Friendship Day ❤️ so grateful for all the friendships I have found with so many of you and watching this community grow and blossom into more friendships 📦   #friendshipday #creativejournaling ',
      url: 'https://www.instagram.com/reel/C-DRzyhx9Ay/',
      embedId: 'C-DRzyhx9Ay',
      category: 'product-showcase',
      tags: ['friendshipday', 'creativejournaling', 'journalingcommunity', 'scrapbooksupplies', 'scrapbookcommunity', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C-DRzyhx9Ay/media/?size=m'
    },
    {
      id: 'ig-api-414',
      platform: 'instagram',
      title: 'Wild West Vibes 🤎',
      description: 'Wild West Vibes 🤎 The box includes: * 2x wooden stamps * 1x sticker sheet * 1x transfer stickers sheet * 1x wanted notepad * 1x PET tape * 1x cutout Washi tape * 15x papers  #wildwest #yellowstone #g',
      url: 'https://www.instagram.com/reel/C9-1gx1xwat/',
      embedId: 'C9-1gx1xwat',
      category: 'product-showcase',
      tags: ['wildwest', 'yellowstone', 'goldrush', 'asmrjournaling', 'creativejournaling', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2024-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C9-1gx1xwat/media/?size=m'
    },
    {
      id: 'ig-api-415',
      platform: 'instagram',
      title: 'Who\'s ready for a wild August',
      description: 'Who\'s ready for a wild August?  In your feedback you\'ve asked me to introduce more masculine themes to the subscription boxes, so here\'s one I hope you\'ll enjoy 😊 Orders can be placed starting tonigh',
      url: 'https://www.instagram.com/reel/C95nrf4RE02/',
      embedId: 'C95nrf4RE02',
      category: 'product-showcase',
      tags: ['wildwest', 'moodboard', 'asmrjournaling', 'creativejournaling', 'journalingsupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C95nrf4RE02/media/?size=m'
    },
    {
      id: 'ig-api-416',
      platform: 'instagram',
      title: 'I just wanted to say hello to you all who are new to this account! 👋',
      description: 'I just wanted to say hello to you all who are new to this account! 👋  I\'m Coralie, I started CoraCreaCrafts, a journaling and crafting supplies store in 2020 right at the beginning of Covid and am so',
      url: 'https://www.instagram.com/reel/C90g_X7Rr7q/',
      embedId: 'C90g_X7Rr7q',
      category: 'product-showcase',
      tags: ['smallbusinessowner', 'trendingaudio', 'taylorswift', 'marketinghumour', 'craftinginspiration', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C90g_X7Rr7q/media/?size=m'
    },
    {
      id: 'ig-api-417',
      platform: 'instagram',
      title: 'Yes the Planner Companion works for teachers too 😍',
      description: 'Yes the Planner Companion works for teachers too 😍',
      url: 'https://www.instagram.com/reel/C9xsKEmRHCZ/',
      embedId: 'C9xsKEmRHCZ',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2024-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C9xsKEmRHCZ/media/?size=m'
    },
    {
      id: 'ig-api-418',
      platform: 'instagram',
      title: 'Create an Ephemera Pocket to delight someone with little treasures 😍 @hippie...',
      description: 'Create an Ephemera Pocket to delight someone with little treasures 😍 @hippie.craft.fairy   #ephemerapocket #asmrjournaling #creativejournal #journalinginspiration #scrapbooksupplies',
      url: 'https://www.instagram.com/reel/C9tDdZFxTh5/',
      embedId: 'C9tDdZFxTh5',
      category: 'product-showcase',
      tags: ['ephemerapocket', 'asmrjournaling', 'creativejournal', 'journalinginspiration', 'scrapbooksupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C9tDdZFxTh5/media/?size=m'
    },
    {
      id: 'ig-api-419',
      platform: 'instagram',
      title: 'Take a closer look into our Tales of the North Box with me ❤️ p.s: I did say ...',
      description: 'Take a closer look into our Tales of the North Box with me ❤️ p.s: I did say wooden stamp but it\'s actually clear stamps, oops 😅 #journalingsupplies #asmrjournaling #journalinginspiration #scrapbooks',
      url: 'https://www.instagram.com/reel/C9nOVGmRxAn/',
      embedId: 'C9nOVGmRxAn',
      category: 'product-showcase',
      tags: ['journalingsupplies', 'asmrjournaling', 'journalinginspiration', 'scrapbooksupplies', 'journalingasmr', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C9nOVGmRxAn/media/?size=m'
    },
    {
      id: 'ig-api-420',
      platform: 'instagram',
      title: 'One of my favorite quotes ever from Tolkien! Did you know if',
      description: 'One of my favorite quotes ever from Tolkien! Did you know if?  #fantasyart #creativejournaling #journalingsupplies #asmrjournaling #lotr',
      url: 'https://www.instagram.com/reel/C9h0FGHRU0q/',
      embedId: 'C9h0FGHRU0q',
      category: 'product-showcase',
      tags: ['fantasyart', 'creativejournaling', 'journalingsupplies', 'asmrjournaling', 'lotr', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C9h0FGHRU0q/media/?size=m'
    },
    {
      id: 'ig-api-421',
      platform: 'instagram',
      title: 'Tales of the North 🗡️ What\'s your favorite item? Full preview coming soon',
      description: 'Tales of the North 🗡️ What\'s your favorite item? Full preview coming soon!  #journalingsupplies #scrapbooksupplies #vikings #norsemythology',
      url: 'https://www.instagram.com/reel/C9XhkyOOvLq/',
      embedId: 'C9XhkyOOvLq',
      category: 'box-reveal',
      tags: ['journalingsupplies', 'scrapbooksupplies', 'vikings', 'norsemythology', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C9XhkyOOvLq/media/?size=m'
    },
    {
      id: 'ig-api-422',
      platform: 'instagram',
      title: 'Who also loves keeping a writing journal for keep important notes and unload ...',
      description: 'Who also loves keeping a writing journal for keep important notes and unload your thoughts? 💭   #writingjournal #creativejournaling #stationerylover #stationerysupplies',
      url: 'https://www.instagram.com/reel/C9Q1RGQxGDE/',
      embedId: 'C9Q1RGQxGDE',
      category: 'product-showcase',
      tags: ['writingjournal', 'creativejournaling', 'stationerylover', 'stationerysupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C9Q1RGQxGDE/media/?size=m'
    },
    {
      id: 'ig-api-423',
      platform: 'instagram',
      title: 'The 3 Rings ⭐ I just thought that was a good name for this spread. Used the s...',
      description: 'The 3 Rings ⭐ I just thought that was a good name for this spread. Used the supplied from the new Fantasy Box 😊  #fantasyart #asmrjournaling #creativejournal #scrapbooksupplies',
      url: 'https://www.instagram.com/reel/C9LKgViRh31/',
      embedId: 'C9LKgViRh31',
      category: 'journal-spread',
      tags: ['fantasyart', 'asmrjournaling', 'creativejournal', 'scrapbooksupplies', 'journaling', 'fantasy'],
      creator: 'CoraCreaCrafts',
      date: '2024-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C9LKgViRh31/media/?size=m'
    },
    {
      id: 'ig-api-424',
      platform: 'instagram',
      title: 'We only have a few left of our Independence/Revolution themed bundle 🇺🇲❤️ a...',
      description: 'We only have a few left of our Independence/Revolution themed bundle 🇺🇲❤️ and only one day left for our sale!   #journalingsupplies #creativejournaling #journalinginspiration #scrapbooksupplies',
      url: 'https://www.instagram.com/reel/C9F_v77xP6C/',
      embedId: 'C9F_v77xP6C',
      category: 'product-showcase',
      tags: ['journalingsupplies', 'creativejournaling', 'journalinginspiration', 'scrapbooksupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C9F_v77xP6C/media/?size=m'
    },
    {
      id: 'ig-api-425',
      platform: 'instagram',
      title: 'Fantasy Box Highlights 🔮 this must be my favorite box this year so far. As a...',
      description: 'Fantasy Box Highlights 🔮 this must be my favorite box this year so far. As an avid reader of fantasy, I\'m beyond excited to bring these new supplies to other crafters and readers out there 📚 There a',
      url: 'https://www.instagram.com/reel/C9DmM_jxK2T/',
      embedId: 'C9DmM_jxK2T',
      category: 'box-reveal',
      tags: ['journalingsupplies', 'fantasyart', 'fantasybooks', 'bookstagram', 'asmrjournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C9DmM_jxK2T/media/?size=m'
    },
    {
      id: 'ig-api-426',
      platform: 'instagram',
      title: 'Happy Independence Day 🇺🇲🎉 doing a  challenge for this special day with th...',
      description: 'Happy Independence Day 🇺🇲🎉 doing a #onewordaday challenge for this special day with the Independence Bundle & Postal Stamps Tapes from the shop ✨ P.S: everything is on sale for the weekend!  #journ',
      url: 'https://www.instagram.com/reel/C9AVjlyR3Ks/',
      embedId: 'C9AVjlyR3Ks',
      category: 'product-showcase',
      tags: ['onewordaday', 'journalingsupplies', 'creativejournal', 'asmrjournaling', 'journalingasmr', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C9AVjlyR3Ks/media/?size=m'
    },
    {
      id: 'ig-api-427',
      platform: 'instagram',
      title: 'Such a fun activity to decorate a book 📚 Have you ever done that? This book ...',
      description: 'Such a fun activity to decorate a book 📚 Have you ever done that? This book bundle is available in the shop 🤗 a Revolution theme combined with the lovely historical fiction from @hannah_honegger  #j',
      url: 'https://www.instagram.com/reel/C88BW4iRtQw/',
      embedId: 'C88BW4iRtQw',
      category: 'product-showcase',
      tags: ['journalingsupplies', 'bookbundle', 'bookstagram', 'journalinginspiration', 'creativejournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C88BW4iRtQw/media/?size=m'
    },
    {
      id: 'ig-api-428',
      platform: 'instagram',
      title: 'Happy July 🤗 I\'m 1 day left because yesterday we were moving all the way to ...',
      description: 'Happy July 🤗 I\'m 1 day left because yesterday we were moving all the way to Nashville! Today is also a big because it\'s our first day in the new warehouse ❤️ this is going to be an exhausting but rew',
      url: 'https://www.instagram.com/reel/C8658RJRrzr/',
      embedId: 'C8658RJRrzr',
      category: 'journal-spread',
      tags: ['monthlyspread', 'journalingsupplies', 'planwithme', 'plannerstickers', 'monthlyplanner', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C8658RJRrzr/media/?size=m'
    },
    {
      id: 'ig-api-429',
      platform: 'instagram',
      title: 'A little overview of the Versailles Ephemera Pack 😍',
      description: 'A little overview of the Versailles Ephemera Pack 😍  #journalingsupplies #creativejournaling #scrapbooking #scrapbooksupplies',
      url: 'https://www.instagram.com/reel/C8u_twoRuyw/',
      embedId: 'C8u_twoRuyw',
      category: 'behind-the-scenes',
      tags: ['journalingsupplies', 'creativejournaling', 'scrapbooking', 'scrapbooksupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C8u_twoRuyw/media/?size=m'
    },
    {
      id: 'ig-api-430',
      platform: 'instagram',
      title: 'Fauna pages 🐐 This is the LAST call for the Fauna Box, you can order them un...',
      description: 'Fauna pages 🐐 This is the LAST call for the Fauna Box, you can order them until end of day today.  #journalingsupplies #scrapbooking #creativejournal #journalinginspiration',
      url: 'https://www.instagram.com/reel/C8sGUhFRMbb/',
      embedId: 'C8sGUhFRMbb',
      category: 'product-showcase',
      tags: ['journalingsupplies', 'scrapbooking', 'creativejournal', 'journalinginspiration', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C8sGUhFRMbb/media/?size=m'
    },
    {
      id: 'ig-api-431',
      platform: 'instagram',
      title: 'Our Very First Book Bundle is here 🤗🇺🇲 what best than to combine your favo...',
      description: 'Our Very First Book Bundle is here 🤗🇺🇲 what best than to combine your favorite hobbies? You will love this historical fiction and the matching American themed supplies ❤️ perfect to celebrate 4th o',
      url: 'https://www.instagram.com/reel/C8pQIZmRIn5/',
      embedId: 'C8pQIZmRIn5',
      category: 'product-showcase',
      tags: ['journalingsupplies', '4thofjuly', 'independenceday', 'americanrevolution', 'bookstagram', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C8pQIZmRIn5/media/?size=m'
    },
    {
      id: 'ig-api-432',
      platform: 'instagram',
      title: 'Prehistory/Geology Horizontal Spread 😍',
      description: 'Prehistory/Geology Horizontal Spread 😍  #journalingsupplies #creativejournaling #asmrjournaling #dinosaurstickers #scrapbooking #scrapbookasmr',
      url: 'https://www.instagram.com/reel/C8mkFyRR1AV/',
      embedId: 'C8mkFyRR1AV',
      category: 'journal-spread',
      tags: ['journalingsupplies', 'creativejournaling', 'asmrjournaling', 'dinosaurstickers', 'scrapbooking', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C8mkFyRR1AV/media/?size=m'
    },
    {
      id: 'ig-api-433',
      platform: 'instagram',
      title: 'Red inspired journal spread 😍 using our Versailles ephemera pack',
      description: 'Red inspired journal spread 😍 using our Versailles ephemera pack   #journalingsupplies #bridgertoncrafts #ephemera #asmrjournaling #creativejournaling',
      url: 'https://www.instagram.com/reel/C8iJj8pSVY7/',
      embedId: 'C8iJj8pSVY7',
      category: 'journal-spread',
      tags: ['journalingsupplies', 'bridgertoncrafts', 'ephemera', 'asmrjournaling', 'creativejournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C8iJj8pSVY7/media/?size=m'
    },
    {
      id: 'ig-api-434',
      platform: 'instagram',
      title: 'Summer Solstice was yesterday ☀️ Let\'s celebrate the arrival of the new seaso...',
      description: 'Summer Solstice was yesterday ☀️ Let\'s celebrate the arrival of the new season ❤️  #summersolstice #summerspread #journalingsupplies #creativejournal #asmrjournal #journalingasmr #scrapbooksupplies',
      url: 'https://www.instagram.com/reel/C8e7NQRxQ4E/',
      embedId: 'C8e7NQRxQ4E',
      category: 'journal-spread',
      tags: ['summersolstice', 'summerspread', 'journalingsupplies', 'creativejournal', 'asmrjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C8e7NQRxQ4E/media/?size=m'
    },
    {
      id: 'ig-api-435',
      platform: 'instagram',
      title: 'Our very first LIVE AUCTION ❤️ thank you to everyone who participated and mad...',
      description: 'Our very first LIVE AUCTION ❤️ thank you to everyone who participated and made it fun 😊',
      url: 'https://www.instagram.com/reel/C8dNrTQxMg_/',
      embedId: 'C8dNrTQxMg_',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C8dNrTQxMg_/media/?size=m'
    },
    {
      id: 'ig-api-436',
      platform: 'instagram',
      title: 'La Gioconda 😍 Did you know this is the original name of the famous painting ...',
      description: 'La Gioconda 😍 Did you know this is the original name of the famous painting we call Mona Lisa? This is because the painting represent Lisa del Giocondo, the wife of a wealthy silk merchant 🖼️🎨  #mo',
      url: 'https://www.instagram.com/reel/C8aZN1kx5El/',
      embedId: 'C8aZN1kx5El',
      category: 'product-showcase',
      tags: ['monalisa', 'gioconda', 'journalingsupplies', 'asmrjournaling', 'minijournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C8aZN1kx5El/media/?size=m'
    },
    {
      id: 'ig-api-437',
      platform: 'instagram',
      title: 'Our move is getting closer and closer 🙌  We are taking orders until the 23rd...',
      description: 'Our move is getting closer and closer 🙌  We are taking orders until the 23rd of June, any order placed after that will be fulfilled when we are settled in our new space on the 2nd week of July 📦☺️  ',
      url: 'https://www.instagram.com/reel/C8XV09ZRSv-/',
      embedId: 'C8XV09ZRSv-',
      category: 'product-showcase',
      tags: ['journalingsupplies', 'creativejournaling', 'asmrjournaling', 'scrapbooking', 'journalinginspiration', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C8XV09ZRSv-/media/?size=m'
    },
    {
      id: 'ig-api-438',
      platform: 'instagram',
      title: 'Middle East Trade Routes ⛺️🐪 from our Silk Road collection. Thank you @liu_j...',
      description: 'Middle East Trade Routes ⛺️🐪 from our Silk Road collection. Thank you @liu_journal for transforming our products into unique art pieces ✨️   #creativejournaling #scrapbooking #minispread #silkroad #m',
      url: 'https://www.instagram.com/reel/C8U3rehRpMp/',
      embedId: 'C8U3rehRpMp',
      category: 'journal-spread',
      tags: ['creativejournaling', 'scrapbooking', 'minispread', 'silkroad', 'middleeast', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C8U3rehRpMp/media/?size=m'
    },
    {
      id: 'ig-api-439',
      platform: 'instagram',
      title: 'Mini Prehistory Spread 🦣',
      description: 'Mini Prehistory Spread 🦣 #onewordaday #minijournal #creativejournaling #asmrjournaling',
      url: 'https://www.instagram.com/reel/C8SHvcfOWRB/',
      embedId: 'C8SHvcfOWRB',
      category: 'journal-spread',
      tags: ['onewordaday', 'minijournal', 'creativejournaling', 'asmrjournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C8SHvcfOWRB/media/?size=m'
    },
    {
      id: 'ig-api-440',
      platform: 'instagram',
      title: 'What to pair with the June Prehistory VCB 😊 Here\'s a selection of supplies f...',
      description: 'What to pair with the June Prehistory VCB 😊 Here\'s a selection of supplies from the shop we think goes great with the new Prehistory items 🖤 🌱 Floral Basics Washi Tape Set 🌱 Travel and Geology Sti',
      url: 'https://www.instagram.com/reel/C8KnkVaRhzA/',
      embedId: 'C8KnkVaRhzA',
      category: 'product-showcase',
      tags: ['journalingsupplies', 'asmrjournaling', 'creativejournaling', 'washi tape', 'journaling', 'stickers'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C8KnkVaRhzA/media/?size=m'
    },
    {
      id: 'ig-api-441',
      platform: 'instagram',
      title: 'It\'s been a while since my last upcycling video 😊 Here\'s an idea on how to r...',
      description: 'It\'s been a while since my last upcycling video 😊 Here\'s an idea on how to reuse the CCC cardboard box 📦 into little ephemera holders!   #upcycling #boxupcycling #upcyclingideas #creativejournal #jo',
      url: 'https://www.instagram.com/reel/C8Ht7U3REnc/',
      embedId: 'C8Ht7U3REnc',
      category: 'tutorial',
      tags: ['upcycling', 'boxupcycling', 'upcyclingideas', 'creativejournal', 'journalingsupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C8Ht7U3REnc/media/?size=m'
    },
    {
      id: 'ig-api-442',
      platform: 'instagram',
      title: 'Silk Road inspired spread by the talented @hippie.craft.fairy 💙 Some of the ...',
      description: 'Silk Road inspired spread by the talented @hippie.craft.fairy 💙 Some of the Silk Road collection including the Samarkand Foil Tape is available in the store   #foilwashi #journalingsupplies #creative',
      url: 'https://www.instagram.com/reel/C8FZ6H4RD3-/',
      embedId: 'C8FZ6H4RD3-',
      category: 'journal-spread',
      tags: ['foilwashi', 'journalingsupplies', 'creativejournaling', 'scrapbooking', 'journalinginspiration', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C8FZ6H4RD3-/media/?size=m'
    },
    {
      id: 'ig-api-443',
      platform: 'instagram',
      title: '📜 GIVEAWAY CLOSED 📜',
      description: '📜 GIVEAWAY CLOSED 📜 Winner: @silvia.scrapbook.passion ✨  We are hosting a special giveaway to celebrate our new Digital Journal Club 😍   ✨  Prize 🏆 Lucky winner will receive 6 months of our Digita',
      url: 'https://www.instagram.com/reel/C8CnkdPRmlS/',
      embedId: 'C8CnkdPRmlS',
      category: 'tutorial',
      tags: ['giveaway', 'journalinggiveaway', 'digitaljournal', 'journalingsupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C8CnkdPRmlS/media/?size=m'
    },
    {
      id: 'ig-api-444',
      platform: 'instagram',
      title: 'Jurassic Small Spread 🦖🌱',
      description: 'Jurassic Small Spread 🦖🌱  #journalingsupplies #asmrjournaling #creativejournal #scrapbooking #journalinginspiration #scrapbooksupplies #dinosaurstickers #jurassic',
      url: 'https://www.instagram.com/reel/C8CUQTNxVjC/',
      embedId: 'C8CUQTNxVjC',
      category: 'journal-spread',
      tags: ['journalingsupplies', 'asmrjournaling', 'creativejournal', 'scrapbooking', 'journalinginspiration', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C8CUQTNxVjC/media/?size=m'
    },
    {
      id: 'ig-api-445',
      platform: 'instagram',
      title: 'Time is the most valuable thing a man can spend ✨️ beautiful spread with our ...',
      description: 'Time is the most valuable thing a man can spend ✨️ beautiful spread with our Time collection by @liu_journal  Available on our website   #journalingsupplies #journalinspiration #journalingasmr #creati',
      url: 'https://www.instagram.com/reel/C77HZq5Rbr8/',
      embedId: 'C77HZq5Rbr8',
      category: 'journal-spread',
      tags: ['journalingsupplies', 'journalinspiration', 'journalingasmr', 'creativejournal', 'scrapbooksupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C77HZq5Rbr8/media/?size=m'
    },
    {
      id: 'ig-api-446',
      platform: 'instagram',
      title: 'How to use the Digital Journal Club Kit this month 🌊',
      description: 'How to use the Digital Journal Club Kit this month 🌊  #journalingsupplies #asmrjournaling #creativejournal #asmrjournal #journalingkit #digitalscrapbooking',
      url: 'https://www.instagram.com/reel/C74yvqiRxxq/',
      embedId: 'C74yvqiRxxq',
      category: 'tutorial',
      tags: ['journalingsupplies', 'asmrjournaling', 'creativejournal', 'asmrjournal', 'journalingkit', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C74yvqiRxxq/media/?size=m'
    },
    {
      id: 'ig-api-447',
      platform: 'instagram',
      title: 'Did you know about our new Digital Journal Club? 🖨 We just started it last m...',
      description: 'Did you know about our new Digital Journal Club? 🖨 We just started it last month and we are excited to bring a new download that follows our physical Vintage Craft Box theme every month! You can comb',
      url: 'https://www.instagram.com/reel/C71lWrsRwxE/',
      embedId: 'C71lWrsRwxE',
      category: 'box-reveal',
      tags: ['digitalsubscription', 'digitalscrapbooking', 'scrapbooksupplies', 'journalingasmr', 'journalingsupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C71lWrsRwxE/media/?size=m'
    },
    {
      id: 'ig-api-448',
      platform: 'instagram',
      title: 'Which product is your favorite from this collection? 🦒🦔🦎',
      description: 'Which product is your favorite from this collection? 🦒🦔🦎   #asmrcraft #asmrjournaling #relaxingjournal #journalingsupplies #journalingasmr #craftingsupplies',
      url: 'https://www.instagram.com/reel/C7zoaVqxf0C/',
      embedId: 'C7zoaVqxf0C',
      category: 'product-showcase',
      tags: ['asmrcraft', 'asmrjournaling', 'relaxingjournal', 'journalingsupplies', 'journalingasmr', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C7zoaVqxf0C/media/?size=m'
    },
    {
      id: 'ig-api-449',
      platform: 'instagram',
      title: 'Prehistory Spread 🦖🌱',
      description: 'Prehistory Spread 🦖🌱 With the June VCB!  #asmrjournaling #journalinspiration #creativejournaling #scrapbooksupplies #journalingkit #dinosaurs #dinosaurstickers',
      url: 'https://www.instagram.com/reel/C7xNbzox5hi/',
      embedId: 'C7xNbzox5hi',
      category: 'journal-spread',
      tags: ['asmrjournaling', 'journalinspiration', 'creativejournaling', 'scrapbooksupplies', 'journalingkit', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C7xNbzox5hi/media/?size=m'
    },
    {
      id: 'ig-api-450',
      platform: 'instagram',
      title: 'June is here 🧡 one of my favorite thing to do each month is preparing a mont...',
      description: 'June is here 🧡 one of my favorite thing to do each month is preparing a monthly overview in my planner + decorating the pages which is most of the fun 😁   #hellojune #juneoverview #planwithme #plann',
      url: 'https://www.instagram.com/reel/C7rYqA3xMu8/',
      embedId: 'C7rYqA3xMu8',
      category: 'product-showcase',
      tags: ['hellojune', 'juneoverview', 'planwithme', 'plannerstickers', 'asmrjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C7rYqA3xMu8/media/?size=m'
    },
    {
      id: 'ig-api-451',
      platform: 'instagram',
      title: 'Black & White themed Fauna Spread 🖤🤍',
      description: 'Black & White themed Fauna Spread 🖤🤍  We only have a few of the Fauna boxes left!   #fauna #asmrjournaling #craftinginspiration #panther #creativejournaling #journalingasmr',
      url: 'https://www.instagram.com/reel/C7o2NkKxSWp/',
      embedId: 'C7o2NkKxSWp',
      category: 'journal-spread',
      tags: ['fauna', 'asmrjournaling', 'craftinginspiration', 'panther', 'creativejournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C7o2NkKxSWp/media/?size=m'
    },
    {
      id: 'ig-api-452',
      platform: 'instagram',
      title: 'Prehistory Box is here 🦖🦕 link in bio',
      description: 'Prehistory Box is here 🦖🦕 link in bio  #asmrcraft #asmrjournaling #subscriptionbox #journalingsupplies #journalingkit #dinosaurstickers #prehistory #dinosaurs',
      url: 'https://www.instagram.com/reel/C7mOZngRxP-/',
      embedId: 'C7mOZngRxP-',
      category: 'box-reveal',
      tags: ['asmrcraft', 'asmrjournaling', 'subscriptionbox', 'journalingsupplies', 'journalingkit', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C7mOZngRxP-/media/?size=m'
    },
    {
      id: 'ig-api-453',
      platform: 'instagram',
      title: 'Set up my journal station with me 🤩',
      description: 'Set up my journal station with me 🤩   #journalinspiration #desksetup #deskmat #happydesk #creativespace #journalingasmr #plannerstickers',
      url: 'https://www.instagram.com/reel/C7jrOtExw_F/',
      embedId: 'C7jrOtExw_F',
      category: 'product-showcase',
      tags: ['journalinspiration', 'desksetup', 'deskmat', 'happydesk', 'creativespace', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C7jrOtExw_F/media/?size=m'
    },
    {
      id: 'ig-api-454',
      platform: 'instagram',
      title: 'Small Fauna Spread 🦩',
      description: 'Small Fauna Spread 🦩  #asmrjournal #journalingasmr #creativejournaling #scrapbookasmr #scrapbooking #Journalinginspiration',
      url: 'https://www.instagram.com/reel/C7hEClmRoVJ/',
      embedId: 'C7hEClmRoVJ',
      category: 'journal-spread',
      tags: ['asmrjournal', 'journalingasmr', 'creativejournaling', 'scrapbookasmr', 'scrapbooking', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C7hEClmRoVJ/media/?size=m'
    },
    {
      id: 'ig-api-455',
      platform: 'instagram',
      title: 'Last Day to enjoy our special Grab Bags & 25% off Sale 🙌 link in bio ♥️',
      description: 'Last Day to enjoy our special Grab Bags & 25% off Sale 🙌 link in bio ♥️   #grabbags #stationerysale #stationerygrabbags #journalingsupplies #journalwithme',
      url: 'https://www.instagram.com/reel/C7fFGuQRhYl/',
      embedId: 'C7fFGuQRhYl',
      category: 'product-showcase',
      tags: ['grabbags', 'stationerysale', 'stationerygrabbags', 'journalingsupplies', 'journalwithme', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C7fFGuQRhYl/media/?size=m'
    },
    {
      id: 'ig-api-456',
      platform: 'instagram',
      title: 'Grab Bags are back 🙌  this time we have 4 different sizes including an extra...',
      description: 'Grab Bags are back 🙌  this time we have 4 different sizes including an extra large box with 50+ items inside 😱  #grabbags #stationerygrabbags #asmrjournal #scrapbooking',
      url: 'https://www.instagram.com/reel/C7Z11eTRrNA/',
      embedId: 'C7Z11eTRrNA',
      category: 'product-showcase',
      tags: ['grabbags', 'stationerygrabbags', 'asmrjournal', 'scrapbooking', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C7Z11eTRrNA/media/?size=m'
    },
    {
      id: 'ig-api-457',
      platform: 'instagram',
      title: 'Memorial Day Sale starred today 🤩 our whole website is on sale',
      description: 'Memorial Day Sale starred today 🤩 our whole website is on sale!   #stationerystore #stationerylover #plannerstickers #planwithme #stationerysale',
      url: 'https://www.instagram.com/reel/C7XRDBqRnnK/',
      embedId: 'C7XRDBqRnnK',
      category: 'product-showcase',
      tags: ['stationerystore', 'stationerylover', 'plannerstickers', 'planwithme', 'stationerysale', 'stickers'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C7XRDBqRnnK/media/?size=m'
    },
    {
      id: 'ig-api-458',
      platform: 'instagram',
      title: 'Reptile Spread 🐊  with the Fauna Box 📦 link in bio',
      description: 'Reptile Spread 🐊  with the Fauna Box 📦 link in bio   #animallovers #asmrjournaling #creativejournaling #journalingsupplies #journalingasmr #scrapbooking #kidscrafts',
      url: 'https://www.instagram.com/reel/C7RYBDpRvHg/',
      embedId: 'C7RYBDpRvHg',
      category: 'journal-spread',
      tags: ['animallovers', 'asmrjournaling', 'creativejournaling', 'journalingsupplies', 'journalingasmr', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C7RYBDpRvHg/media/?size=m'
    },
    {
      id: 'ig-api-459',
      platform: 'instagram',
      title: 'World Bee Day 🐝  Bees play such an important role in the environment by poll...',
      description: 'World Bee Day 🐝  Bees play such an important role in the environment by pollinating plants, supporting agriculture and biodiversity! Today we celebrate bees with 20% off our Bee, Insects and Butterfl',
      url: 'https://www.instagram.com/reel/C7Mlfp9OpaH/',
      embedId: 'C7Mlfp9OpaH',
      category: 'product-showcase',
      tags: ['worldbeeday', 'bees', 'asmrjournaling', 'craftinginspiration', 'journalingsupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C7Mlfp9OpaH/media/?size=m'
    },
    {
      id: 'ig-api-460',
      platform: 'instagram',
      title: 'Art is everywhere 🤍 making a horizontal creative spread was so much fun! All...',
      description: 'Art is everywhere 🤍 making a horizontal creative spread was so much fun! All supplies used are from the current Treasure Box \'Museums\'   #artiseverywhere #artquote #creativejournal #asmrjournal #jour',
      url: 'https://www.instagram.com/reel/C7HD0c5O2Oj/',
      embedId: 'C7HD0c5O2Oj',
      category: 'journal-spread',
      tags: ['artiseverywhere', 'artquote', 'creativejournal', 'asmrjournal', 'journalingsubscription', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C7HD0c5O2Oj/media/?size=m'
    },
    {
      id: 'ig-api-461',
      platform: 'instagram',
      title: 'New \'Fauna\' Box Sneak Peek 📦 I\'ve been wanting to bring more animals into my...',
      description: 'New \'Fauna\' Box Sneak Peek 📦 I\'ve been wanting to bring more animals into my products after the mystical woodlands box from last year which I got some really positive feedback on 🦔 this one takes a ',
      url: 'https://www.instagram.com/reel/C7EpKsPRzij/',
      embedId: 'C7EpKsPRzij',
      category: 'box-reveal',
      tags: ['scrapbooksupplies', 'kidscrafts', 'animallovers', 'asmrjournaling', 'creativejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C7EpKsPRzij/media/?size=m'
    },
    {
      id: 'ig-api-462',
      platform: 'instagram',
      title: 'What to pair with the Secret Gardens Box 📦 🌿',
      description: 'What to pair with the Secret Gardens Box 📦 🌿   #gardendesign #gardeninspiration #scrapbooking #creativejournal #packingorders #subscriptionbox #journalingasmr #journalingsubscription',
      url: 'https://www.instagram.com/reel/C6_bOTdu_oP/',
      embedId: 'C6_bOTdu_oP',
      category: 'behind-the-scenes',
      tags: ['gardendesign', 'gardeninspiration', 'scrapbooking', 'creativejournal', 'packingorders', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C6_bOTdu_oP/media/?size=m'
    },
    {
      id: 'ig-api-463',
      platform: 'instagram',
      title: 'Le Jardin Oublié 🤍🌿',
      description: 'Le Jardin Oublié 🤍🌿   #smalljournal #creativejournal #collagejournal #journalingasmr #journalspread #Craft #artjournal #asmr #journaling #scrapbook',
      url: 'https://www.instagram.com/reel/C69Hs0ZxlYC/',
      embedId: 'C69Hs0ZxlYC',
      category: 'journal-spread',
      tags: ['smalljournal', 'creativejournal', 'collagejournal', 'journalingasmr', 'journalspread', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C69Hs0ZxlYC/media/?size=m'
    },
    {
      id: 'ig-api-464',
      platform: 'instagram',
      title: 'Mini Journal Spread ✨️ Secret Garden 💚',
      description: 'Mini Journal Spread ✨️ Secret Garden 💚  #onewordaday #secretgarden #asmrcraft #asmrjournaling #journalingsupplies #scrapbookasmr',
      url: 'https://www.instagram.com/reel/C662vdaRSvL/',
      embedId: 'C662vdaRSvL',
      category: 'journal-spread',
      tags: ['onewordaday', 'secretgarden', 'asmrcraft', 'asmrjournaling', 'journalingsupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C662vdaRSvL/media/?size=m'
    },
    {
      id: 'ig-api-465',
      platform: 'instagram',
      title: 'Happy Mother\'s Day ❤️💐 I\'m not a mom but I have a true appreciation for ever...',
      description: 'Happy Mother\'s Day ❤️💐 I\'m not a mom but I have a true appreciation for every single mom out there who brings so much love to this world through their hard work and for caring so deeply for others 🥲',
      url: 'https://www.instagram.com/reel/C63pi1muiRt/',
      embedId: 'C63pi1muiRt',
      category: 'product-showcase',
      tags: ['mothersday', 'motherdayidea', 'mothersdaycraft', 'motherhood', 'mothersquote', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C63pi1muiRt/media/?size=m'
    },
    {
      id: 'ig-api-466',
      platform: 'instagram',
      title: 'The Golden Gallery 🎨 with the Museum Treasure Box 📦 ✨️',
      description: 'The Golden Gallery 🎨 with the Museum Treasure Box 📦 ✨️   #asmrjournal #scrapbooking #creativejournal #museumlover #asmrjournaling #journalingisart',
      url: 'https://www.instagram.com/reel/C6w2RIaxpAO/',
      embedId: 'C6w2RIaxpAO',
      category: 'product-showcase',
      tags: ['asmrjournal', 'scrapbooking', 'creativejournal', 'museumlover', 'asmrjournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C6w2RIaxpAO/media/?size=m'
    },
    {
      id: 'ig-api-467',
      platform: 'instagram',
      title: 'Secret Garden 🌿❤️ using the supplies from thr May VCB has been a dream! Can\'...',
      description: 'Secret Garden 🌿❤️ using the supplies from thr May VCB has been a dream! Can\'t wait for more creations with it! Link in bio 🤍  #secretgarden #asmrjournaling #creativejournaling #gardenspread #scrapbo',
      url: 'https://www.instagram.com/reel/C6tZfaduRVi/',
      embedId: 'C6tZfaduRVi',
      category: 'journal-spread',
      tags: ['secretgarden', 'asmrjournaling', 'creativejournaling', 'gardenspread', 'scrapbookasmr', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C6tZfaduRVi/media/?size=m'
    },
    {
      id: 'ig-api-468',
      platform: 'instagram',
      title: 'Another fun collage card idea with our Mother\'s Day Bundle 🥰',
      description: 'Another fun collage card idea with our Mother\'s Day Bundle 🥰  #collagecard #diycards #mothersdaycard #Stickerpeel #scrapbookingideas #scrapbookasmr',
      url: 'https://www.instagram.com/reel/C6rwOriRatU/',
      embedId: 'C6rwOriRatU',
      category: 'tutorial',
      tags: ['collagecard', 'diycards', 'mothersdaycard', 'stickerpeel', 'scrapbookingideas', 'stickers'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C6rwOriRatU/media/?size=m'
    },
    {
      id: 'ig-api-469',
      platform: 'instagram',
      title: 'Pack a wholesale order with us 😍 we are always so excited to work with shops...',
      description: 'Pack a wholesale order with us 😍 we are always so excited to work with shops in other countries because that makes things so much easier for our international customers. Avoiding having to deal with ',
      url: 'https://www.instagram.com/reel/C6oMdu2uX8R/',
      embedId: 'C6oMdu2uX8R',
      category: 'behind-the-scenes',
      tags: ['wholesalestationery', 'stationerystore', 'journalinspiration', 'smallbusiness', 'packanorderwithus', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C6oMdu2uX8R/media/?size=m'
    },
    {
      id: 'ig-api-470',
      platform: 'instagram',
      title: 'Pop Up Card/Deco Display 🦋 using the Mother\'s Day Bundle in Green/Brown colo...',
      description: 'Pop Up Card/Deco Display 🦋 using the Mother\'s Day Bundle in Green/Brown color 💚🤎  #mothersdaycraft #motherdayidea #diycard #butterflycraft #asmrjournaling #creativejournal #asmrcraft #craftwithme #',
      url: 'https://www.instagram.com/reel/C6g9ohCx9tr/',
      embedId: 'C6g9ohCx9tr',
      category: 'tutorial',
      tags: ['mothersdaycraft', 'motherdayidea', 'diycard', 'butterflycraft', 'asmrjournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C6g9ohCx9tr/media/?size=m'
    },
    {
      id: 'ig-api-471',
      platform: 'instagram',
      title: 'Secret Garden Box Preview 🌿',
      description: 'Secret Garden Box Preview 🌿 Happy May 1st! This will be the theme for this month\'s Vintage Craft Box. For the first time I\'m bringing some natural paper pages, I\'m really excited about that. Colors o',
      url: 'https://www.instagram.com/reel/C6cOskIx1I8/',
      embedId: 'C6cOskIx1I8',
      category: 'box-reveal',
      tags: ['asmrjournaling', 'springjournaling', 'scrapbooking', 'journalingkit', 'journalsupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C6cOskIx1I8/media/?size=m'
    },
    {
      id: 'ig-api-472',
      platform: 'instagram',
      title: 'The British Museum 🏛',
      description: 'The British Museum 🏛  Loved making this spread with the new Museum Box items 😍 I also reused some of the previous Treasure Box Old Cities Papers for the background and they just fit perfect 👌   #th',
      url: 'https://www.instagram.com/reel/C6Z2ItWxFP7/',
      embedId: 'C6Z2ItWxFP7',
      category: 'journal-spread',
      tags: ['thebritishmuseum', 'museumlover', 'artjournaling', 'creativejournaling', 'asmrjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C6Z2ItWxFP7/media/?size=m'
    },
    {
      id: 'ig-api-473',
      platform: 'instagram',
      title: 'Super easy DIY Canvas Bag deco ✨️ with the Fabric Transfers included in the n...',
      description: 'Super easy DIY Canvas Bag deco ✨️ with the Fabric Transfers included in the new Treasure Box!   #fabrictransfer #journalingbag #journalingisart #artquotes #Journalinginspiration #Journalingsupplies #j',
      url: 'https://www.instagram.com/reel/C6R8xhTRFwm/',
      embedId: 'C6R8xhTRFwm',
      category: 'tutorial',
      tags: ['fabrictransfer', 'journalingbag', 'journalingisart', 'artquotes', 'journalinginspiration', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C6R8xhTRFwm/media/?size=m'
    },
    {
      id: 'ig-api-474',
      platform: 'instagram',
      title: 'Quote of today from Pablo Nureda 🤎 while this quote can be interpreted in ma...',
      description: 'Quote of today from Pablo Nureda 🤎 while this quote can be interpreted in many ways, this is my take on it: you can face obstacles and failures but remember there will be light and positive outcomes ',
      url: 'https://www.instagram.com/reel/C6FQQ3QRntI/',
      embedId: 'C6FQQ3QRntI',
      category: 'product-showcase',
      tags: ['springquotes', 'revival', 'asmrjournaling', 'scrapbooksupplies', 'springjournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C6FQQ3QRntI/media/?size=m'
    },
    {
      id: 'ig-api-475',
      platform: 'instagram',
      title: 'Museum Box Sneak Peek 🎨  link in bio to get yours',
      description: 'Museum Box Sneak Peek 🎨  link in bio to get yours!   #museumlover #asmrjournal #Journalinginspiration #Journalingsupplies #creativejournal #stationerylover #scrapbooking',
      url: 'https://www.instagram.com/reel/C6CJVwwRr94/',
      embedId: 'C6CJVwwRr94',
      category: 'box-reveal',
      tags: ['museumlover', 'asmrjournal', 'journalinginspiration', 'journalingsupplies', 'creativejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C6CJVwwRr94/media/?size=m'
    },
    {
      id: 'ig-api-476',
      platform: 'instagram',
      title: 'Pirate Box is back 🏴‍☠️ this was the absolute favorite of last year and we d...',
      description: 'Pirate Box is back 🏴‍☠️ this was the absolute favorite of last year and we decided to bring it back for an exciting and adventurous summer season!   #piratebox #pirates #piratesofthecaribbean #statio',
      url: 'https://www.instagram.com/reel/C5_JSZfucRu/',
      embedId: 'C5_JSZfucRu',
      category: 'box-reveal',
      tags: ['piratebox', 'pirates', 'piratesofthecaribbean', 'stationerylover', 'craftingsupplies', 'advent calendar'],
      creator: 'CoraCreaCrafts',
      date: '2024-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C5_JSZfucRu/media/?size=m'
    },
    {
      id: 'ig-api-477',
      platform: 'instagram',
      title: 'Small Revival Spring Spread ❤️ I\'m so enjoying the small format of this creat...',
      description: 'Small Revival Spring Spread ❤️ I\'m so enjoying the small format of this creative notebook!  Happy Wednesday 😊   #Springcrafts #scrapbook #Journalinginspiration #creativejournaling #scrapbookingideas ',
      url: 'https://www.instagram.com/reel/C53QbbVxX8N/',
      embedId: 'C53QbbVxX8N',
      category: 'journal-spread',
      tags: ['springcrafts', 'scrapbook', 'journalinginspiration', 'creativejournaling', 'scrapbookingideas', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C53QbbVxX8N/media/?size=m'
    },
    {
      id: 'ig-api-478',
      platform: 'instagram',
      title: 'How to use our Enchanted Forest Bundle ✨️ Find it in our boxes & kits section',
      description: 'How to use our Enchanted Forest Bundle ✨️ Find it in our boxes & kits section  #journaling #craftsupplies #stickers #washitape #scrapbook #journal #journalingstarterkit',
      url: 'https://www.instagram.com/reel/C5yYbRbOa39/',
      embedId: 'C5yYbRbOa39',
      category: 'tutorial',
      tags: ['journaling', 'craftsupplies', 'stickers', 'washitape', 'scrapbook', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2024-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C5yYbRbOa39/media/?size=m'
    },
    {
      id: 'ig-api-479',
      platform: 'instagram',
      title: '⏳️ TIME GIVEAWAY ⏳️',
      description: '⏳️ TIME GIVEAWAY ⏳️  ✨ We are hosting a special giveaway to celebrate my Time Vintage Craft Box and @thedecorativemind beautiful frames 😍 We could all use a little more time to create! ✨  There will ',
      url: 'https://www.instagram.com/reel/C5qryOYOXdi/',
      embedId: 'C5qryOYOXdi',
      category: 'tutorial',
      tags: ['giveaway', 'stationerygiveaway', 'happymail', 'creativejournaling', 'asmrjournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C5qryOYOXdi/media/?size=m'
    },
    {
      id: 'ig-api-480',
      platform: 'instagram',
      title: 'New Revival Tapes 🌸🏵 link in bio ♥️',
      description: 'New Revival Tapes 🌸🏵 link in bio ♥️   #springtime #asmrunboxing #tapeaddict #creativejournaling #Journalingsupplies #scrapbook',
      url: 'https://www.instagram.com/reel/C5mQCaXRX46/',
      embedId: 'C5mQCaXRX46',
      category: 'unboxing',
      tags: ['springtime', 'asmrunboxing', 'tapeaddict', 'creativejournaling', 'journalingsupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C5mQCaXRX46/media/?size=m'
    },
    {
      id: 'ig-api-481',
      platform: 'instagram',
      title: 'How to use our Mini Journal Bundle 😍 Find it in our new arrivals',
      description: 'How to use our Mini Journal Bundle 😍 Find it in our new arrivals!   #journalingsupplies #creativejournal #minijournal #journalingkit #handmadecrafts',
      url: 'https://www.instagram.com/reel/C5gbH8jxEBi/',
      embedId: 'C5gbH8jxEBi',
      category: 'tutorial',
      tags: ['journalingsupplies', 'creativejournal', 'minijournal', 'journalingkit', 'handmadecrafts', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C5gbH8jxEBi/media/?size=m'
    },
    {
      id: 'ig-api-482',
      platform: 'instagram',
      title: 'The Mystery of Time ⏳️',
      description: 'The Mystery of Time ⏳️  Using the products from the \'Time\' VCB ❤️ link in bio   #mysteryoftime #creativejournaling #Journalinginspiration #journalrelaxation #Journalingsupplies #asmrjournaling #hourgl',
      url: 'https://www.instagram.com/reel/C5Y3n0NxuJ2/',
      embedId: 'C5Y3n0NxuJ2',
      category: 'product-showcase',
      tags: ['mysteryoftime', 'creativejournaling', 'journalinginspiration', 'journalrelaxation', 'journalingsupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C5Y3n0NxuJ2/media/?size=m'
    },
    {
      id: 'ig-api-483',
      platform: 'instagram',
      title: 'April Vintage Craft Box Preview 📦 ✨️ \'Time\' ⏰️',
      description: 'April Vintage Craft Box Preview 📦 ✨️ \'Time\' ⏰️   #journalinspiration #journalingsubscription #Time #craftingsupplies #asmrjournaling #journalingsupplies',
      url: 'https://www.instagram.com/reel/C5T0fQiRusL/',
      embedId: 'C5T0fQiRusL',
      category: 'box-reveal',
      tags: ['journalinspiration', 'journalingsubscription', 'time', 'craftingsupplies', 'asmrjournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C5T0fQiRusL/media/?size=m'
    },
    {
      id: 'ig-api-484',
      platform: 'instagram',
      title: 'Spring Postal themed spread 📬🌸',
      description: 'Spring Postal themed spread 📬🌸 Using the new Curiosities Box supplies 📦  #asmrjournal #Journalinginspiration #creativejournaling #Journalingsupplies #journalingkit #snailmailsupplies',
      url: 'https://www.instagram.com/reel/C5R578dxEtX/',
      embedId: 'C5R578dxEtX',
      category: 'journal-spread',
      tags: ['asmrjournal', 'journalinginspiration', 'creativejournaling', 'journalingsupplies', 'journalingkit', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C5R578dxEtX/media/?size=m'
    },
    {
      id: 'ig-api-485',
      platform: 'instagram',
      title: 'Enjoy 😉 🤣🤪',
      description: 'Enjoy 😉 🤣🤪   #asmrjournal #antiasmr #satisfyingsounds #fails #aprilfools #journalingsupplies #Journalinginspiration',
      url: 'https://www.instagram.com/reel/C5OpA7FRXxi/',
      embedId: 'C5OpA7FRXxi',
      category: 'product-showcase',
      tags: ['asmrjournal', 'antiasmr', 'satisfyingsounds', 'fails', 'aprilfools', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C5OpA7FRXxi/media/?size=m'
    },
    {
      id: 'ig-api-486',
      platform: 'instagram',
      title: 'Happy Easter 🐰 🐣  to those who celebrate & to those who are just here to en...',
      description: 'Happy Easter 🐰 🐣  to those who celebrate & to those who are just here to enjoy the chocolate eggs and the hunt 🤣 This was a super simple mini spread for my #onewordaday challenge  #minijournal #min',
      url: 'https://www.instagram.com/reel/C5MGfo4xRGf/',
      embedId: 'C5MGfo4xRGf',
      category: 'journal-spread',
      tags: ['onewordaday', 'minijournal', 'minispread', 'selfchallenge', 'journalinginspiration', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C5MGfo4xRGf/media/?size=m'
    },
    {
      id: 'ig-api-487',
      platform: 'instagram',
      title: '1st EGG HUNT complete 🥚',
      description: '1st EGG HUNT complete 🥚  Come back tomorrow at 3PM for another chance to win 🏆 🙌  Congrats to our winners: Nikki, Crystal and Tracey',
      url: 'https://www.instagram.com/reel/C5HMFEvxhvp/',
      embedId: 'C5HMFEvxhvp',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2024-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C5HMFEvxhvp/media/?size=m'
    },
    {
      id: 'ig-api-488',
      platform: 'instagram',
      title: 'Easter Weekend FUN 😁 join our Egg Hunt to win special prizes. Two ways to ge...',
      description: 'Easter Weekend FUN 😁 join our Egg Hunt to win special prizes. Two ways to get lucky eggs:  1. Place an order until Monday - lucky eggs will be placed randomly amongst all orders 2. Join our IG live e',
      url: 'https://www.instagram.com/reel/C5GbPs0u0_w/',
      embedId: 'C5GbPs0u0_w',
      category: 'behind-the-scenes',
      tags: ['egghunt', 'easter', 'smallbusinesssale', 'stationerystore', 'journalinspiration', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C5GbPs0u0_w/media/?size=m'
    },
    {
      id: 'ig-api-489',
      platform: 'instagram',
      title: 'Royal Post 📫 😍  New Curiosities Box 📦',
      description: 'Royal Post 📫 😍  New Curiosities Box 📦   #postal #snailmail #penalty #snailmailsupplies #Journalingsupplies #asmrjournal #creativejournal #victorianera',
      url: 'https://www.instagram.com/reel/C5B_IqXx2cV/',
      embedId: 'C5B_IqXx2cV',
      category: 'product-showcase',
      tags: ['postal', 'snailmail', 'penalty', 'snailmailsupplies', 'journalingsupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C5B_IqXx2cV/media/?size=m'
    },
    {
      id: 'ig-api-490',
      platform: 'instagram',
      title: '1 Word a Day \'Silk Road\' ❤️ got inspired by the lovely @liu_journal to try a ...',
      description: '1 Word a Day \'Silk Road\' ❤️ got inspired by the lovely @liu_journal to try a vertical spread in my mini !  #minijournal #minicraft #creativejournal #creativechallenge #onewordaday #asmrunboxing #scrap',
      url: 'https://www.instagram.com/reel/C48PDAGOxM8/',
      embedId: 'C48PDAGOxM8',
      category: 'unboxing',
      tags: ['minijournal', 'minicraft', 'creativejournal', 'creativechallenge', 'onewordaday', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C48PDAGOxM8/media/?size=m'
    },
    {
      id: 'ig-api-491',
      platform: 'instagram',
      title: 'Easter Cards 🐣 I made these for my neighbors 🥚 which one is your favorite, ...',
      description: 'Easter Cards 🐣 I made these for my neighbors 🥚 which one is your favorite, top or bottom? 💌  #easter #eastercards #diycards #handmadecards #handmadecrafts #craftwithme',
      url: 'https://www.instagram.com/reel/C4wPECXRdsg/',
      embedId: 'C4wPECXRdsg',
      category: 'tutorial',
      tags: ['easter', 'eastercards', 'diycards', 'handmadecards', 'handmadecrafts'],
      creator: 'CoraCreaCrafts',
      date: '2024-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C4wPECXRdsg/media/?size=m'
    },
    {
      id: 'ig-api-492',
      platform: 'instagram',
      title: 'Ready to celebrate the arrival of Spring very soon 🌸🐇🥚',
      description: 'Ready to celebrate the arrival of Spring very soon 🌸🐇🥚  All items used are from our Easter Bundle, available now!  #Springcrafts #easterspread #rabbittape #eastereggs #hellospring #creativejournal ',
      url: 'https://www.instagram.com/reel/C4qq5A_xuoP/',
      embedId: 'C4qq5A_xuoP',
      category: 'journal-spread',
      tags: ['springcrafts', 'easterspread', 'rabbittape', 'eastereggs', 'hellospring', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C4qq5A_xuoP/media/?size=m'
    },
    {
      id: 'ig-api-493',
      platform: 'instagram',
      title: 'Ancient Cities Mini Book 📖',
      description: 'Ancient Cities Mini Book 📖   #mesopotamia #ancientcities #deskmat #creativejournaling #scrapbook #vintagejournal #vintagestationery',
      url: 'https://www.instagram.com/reel/C4lkqm1RRfm/',
      embedId: 'C4lkqm1RRfm',
      category: 'product-showcase',
      tags: ['mesopotamia', 'ancientcities', 'deskmat', 'creativejournaling', 'scrapbook', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C4lkqm1RRfm/media/?size=m'
    },
    {
      id: 'ig-api-494',
      platform: 'instagram',
      title: 'I found my wolf creative notebook after misplacing it months ago 😅 I really ...',
      description: 'I found my wolf creative notebook after misplacing it months ago 😅 I really love that small but not too small format.   Old Cities Spread using a mix of the March VCB and the TB 😍  #oldcities #ancie',
      url: 'https://www.instagram.com/reel/C4d3PDkxvII/',
      embedId: 'C4d3PDkxvII',
      category: 'journal-spread',
      tags: ['oldcities', 'ancienthistory', 'foil', 'papercrafts', 'scrapbooking', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C4d3PDkxvII/media/?size=m'
    },
    {
      id: 'ig-api-495',
      platform: 'instagram',
      title: 'Marco Polo inspired spread 💙',
      description: 'Marco Polo inspired spread 💙  Silk Road Box available in the shop ✨️   #marcopolo #silkroad #asmrjournal #oldcities #oriental #journalingsubscription #Journalingsupplies #scrapbookasmr',
      url: 'https://www.instagram.com/reel/C4Yw5TAxopx/',
      embedId: 'C4Yw5TAxopx',
      category: 'journal-spread',
      tags: ['marcopolo', 'silkroad', 'asmrjournal', 'oldcities', 'oriental', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C4Yw5TAxopx/media/?size=m'
    },
    {
      id: 'ig-api-496',
      platform: 'instagram',
      title: 'So this is love..? 💙',
      description: 'So this is love..? 💙  My absolute favorite of this month\'s box: the Samarkand Foil Washi Tape!   #silkroad #samarkand #foilwashi #foil #artsandcrafts #craftingsupplies #creativejournal',
      url: 'https://www.instagram.com/reel/C4QbOqEuRdN/',
      embedId: 'C4QbOqEuRdN',
      category: 'box-reveal',
      tags: ['silkroad', 'samarkand', 'foilwashi', 'foil', 'artsandcrafts', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2024-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C4QbOqEuRdN/media/?size=m'
    },
    {
      id: 'ig-api-497',
      platform: 'instagram',
      title: 'If you had to choose one favorite product, which would jt be',
      description: 'If you had to choose one favorite product, which would jt be?  Some of our new arrivals & favorite products 😍  Unboxing courtesy of @liu_journal   #asmrunboxing #stationerystore #vintagestationery #a',
      url: 'https://www.instagram.com/reel/C4PBEp1RngZ/',
      embedId: 'C4PBEp1RngZ',
      category: 'unboxing',
      tags: ['asmrunboxing', 'stationerystore', 'vintagestationery', 'asmrjournal', 'creativejournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C4PBEp1RngZ/media/?size=m'
    },
    {
      id: 'ig-api-498',
      platform: 'instagram',
      title: 'One Word A Day - Wolf 🐺 Love this lullaby to go with this theme 💕 What word...',
      description: 'One Word A Day - Wolf 🐺 Love this lullaby to go with this theme 💕 What word should I do next?  #minijournal #creativejournaling #Journalinginspiration #scrapbooking #witchyjournal #asmrjournal',
      url: 'https://www.instagram.com/reel/C4IJnUQIXmt/',
      embedId: 'C4IJnUQIXmt',
      category: 'product-showcase',
      tags: ['minijournal', 'creativejournaling', 'journalinginspiration', 'scrapbooking', 'witchyjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C4IJnUQIXmt/media/?size=m'
    },
    {
      id: 'ig-api-499',
      platform: 'instagram',
      title: 'Hello March 🤗 This month we are super excited to bring a theme which has bee...',
      description: 'Hello March 🤗 This month we are super excited to bring a theme which has been in the works for a long time: the Silk Road 🐫 Inspired by the rich tapestry of cultures along these ancient trade routes',
      url: 'https://www.instagram.com/reel/C4BJCQjuUMM/',
      embedId: 'C4BJCQjuUMM',
      category: 'box-reveal',
      tags: ['silkroad', 'journalingsupplies', 'journalingsubscription', 'scrapbooksupplies', 'crafting', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C4BJCQjuUMM/media/?size=m'
    },
    {
      id: 'ig-api-500',
      platform: 'instagram',
      title: 'Making products that fit my imagination and dreams has always been the focal ...',
      description: 'Making products that fit my imagination and dreams has always been the focal point of my small business. I\'m excited to be able to continue extending the variety of products we design and carry at CCC',
      url: 'https://www.instagram.com/reel/C37-b7RIgQZ/',
      embedId: 'C37-b7RIgQZ',
      category: 'box-reveal',
      tags: ['vintagejournal', 'ephemerabag', 'ephemerapouch', 'stationeryaddict', 'smallbusinessowner', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C37-b7RIgQZ/media/?size=m'
    },
    {
      id: 'ig-api-501',
      platform: 'instagram',
      title: 'One Word a Day ❤️ Love',
      description: 'One Word a Day ❤️ Love   #onewordaday #minijournal #vintagejournal #creativejournaling #asmrjournal #scrapbook #scrapbookasmr',
      url: 'https://www.instagram.com/reel/C32-AnSIKaN/',
      embedId: 'C32-AnSIKaN',
      category: 'product-showcase',
      tags: ['onewordaday', 'minijournal', 'vintagejournal', 'creativejournaling', 'asmrjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C32-AnSIKaN/media/?size=m'
    },
    {
      id: 'ig-api-502',
      platform: 'instagram',
      title: 'What being home feels like 🇨🇭❤️',
      description: 'What being home feels like 🇨🇭❤️  #myswitzerland #sweetzerland #stickers #plannerstickers #planwithme #plannerideas',
      url: 'https://www.instagram.com/reel/C30hU51o1s1/',
      embedId: 'C30hU51o1s1',
      category: 'product-showcase',
      tags: ['myswitzerland', 'sweetzerland', 'stickers', 'plannerstickers', 'planwithme'],
      creator: 'CoraCreaCrafts',
      date: '2024-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C30hU51o1s1/media/?size=m'
    },
    {
      id: 'ig-api-503',
      platform: 'instagram',
      title: 'Find the key to the secret library 🗝📚',
      description: 'Find the key to the secret library 🗝📚  Using the February \'Mystery\' VCB supplies!   #creativejournaling #scrapbookingideas #secretlibrary #journalrelaxation #asmrjournal #vintagejournal #journalwith',
      url: 'https://www.instagram.com/reel/C3szUgnoznB/',
      embedId: 'C3szUgnoznB',
      category: 'product-showcase',
      tags: ['creativejournaling', 'scrapbookingideas', 'secretlibrary', 'journalrelaxation', 'asmrjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C3szUgnoznB/media/?size=m'
    },
    {
      id: 'ig-api-504',
      platform: 'instagram',
      title: 'Preparing Travel Journal Pages for my trip 📖✨️ if you watched long enough yo...',
      description: 'Preparing Travel Journal Pages for my trip 📖✨️ if you watched long enough you know where I\'m going 😉  See you soon 🛬  #traveljournal #vintagejournal #traveler #Journalinginspiration #journalwithme ',
      url: 'https://www.instagram.com/reel/C3lfub4xUPq/',
      embedId: 'C3lfub4xUPq',
      category: 'journal-spread',
      tags: ['traveljournal', 'vintagejournal', 'traveler', 'journalinginspiration', 'journalwithme', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C3lfub4xUPq/media/?size=m'
    },
    {
      id: 'ig-api-505',
      platform: 'instagram',
      title: 'One Word a Day - Serenity 📖 what do you associate Serenity with? For me, it\'...',
      description: 'One Word a Day - Serenity 📖 what do you associate Serenity with? For me, it\'s the idea of an old and peaceful place like a library  #minijournal #scrapbook #Journalinginspiration #asmrjournal #relaxi',
      url: 'https://www.instagram.com/reel/C3ilNYQxPAO/',
      embedId: 'C3ilNYQxPAO',
      category: 'product-showcase',
      tags: ['minijournal', 'scrapbook', 'journalinginspiration', 'asmrjournal', 'relaxingjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C3ilNYQxPAO/media/?size=m'
    },
    {
      id: 'ig-api-506',
      platform: 'instagram',
      title: 'Ancient Cities 🌏 cutout over several pages. This was fun 😍',
      description: 'Ancient Cities 🌏 cutout over several pages. This was fun 😍  #cutoutpages #temples #oldcities #ancientcities #artjournaling #Journalinginspiration #creativejournal ##scrapbook',
      url: 'https://www.instagram.com/reel/C3d5osHxf0r/',
      embedId: 'C3d5osHxf0r',
      category: 'product-showcase',
      tags: ['cutoutpages', 'temples', 'oldcities', 'ancientcities', 'artjournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C3d5osHxf0r/media/?size=m'
    },
    {
      id: 'ig-api-507',
      platform: 'instagram',
      title: 'One Word a Day - Mystery 🔍',
      description: 'One Word a Day - Mystery 🔍  #minijournal #vintagejournal #mystery #detective #Journalingsupplies #onewordaday #scrapbook',
      url: 'https://www.instagram.com/reel/C3bBLo6RUPl/',
      embedId: 'C3bBLo6RUPl',
      category: 'product-showcase',
      tags: ['minijournal', 'vintagejournal', 'mystery', 'detective', 'journalingsupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C3bBLo6RUPl/media/?size=m'
    },
    {
      id: 'ig-api-508',
      platform: 'instagram',
      title: 'Old Cities Spread 🏙️',
      description: 'Old Cities Spread 🏙️  All items from the Treasure Box for Feb/Mar ✨  #ancienthistory #minijournal #handmadejournal #asmrjournaling #creativejournaling #journalingsupplies',
      url: 'https://www.instagram.com/reel/C3YZV8YRouY/',
      embedId: 'C3YZV8YRouY',
      category: 'journal-spread',
      tags: ['ancienthistory', 'minijournal', 'handmadejournal', 'asmrjournaling', 'creativejournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C3YZV8YRouY/media/?size=m'
    },
    {
      id: 'ig-api-509',
      platform: 'instagram',
      title: 'Happy Valentine\'s Day 💌',
      description: 'Happy Valentine\'s Day 💌  Get up to 20% off our shop ❤️ link in bio  #valentinesdaycard #diycard #victorianpuzzlepurse #victorianpuzzle #loveletter #creativejournaling',
      url: 'https://www.instagram.com/reel/C3V7AEARmGC/',
      embedId: 'C3V7AEARmGC',
      category: 'tutorial',
      tags: ['valentinesdaycard', 'diycard', 'victorianpuzzlepurse', 'victorianpuzzle', 'loveletter', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C3V7AEARmGC/media/?size=m'
    },
    {
      id: 'ig-api-510',
      platform: 'instagram',
      title: 'Treasure Box Preview ✨Old Cities',
      description: 'Treasure Box Preview ✨Old Cities  Dive into the past..  Includes desk mat, journal, washi tapes, stickers, papers scissors.. *Ephemera Pouch Bag is an Add-on*  #minijournal #leatherjournal #deskmat #v',
      url: 'https://www.instagram.com/reel/C3QLeksuTZA/',
      embedId: 'C3QLeksuTZA',
      category: 'box-reveal',
      tags: ['minijournal', 'leatherjournal', 'deskmat', 'vintagejournal', 'vintagedesk', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2024-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C3QLeksuTZA/media/?size=m'
    },
    {
      id: 'ig-api-511',
      platform: 'instagram',
      title: 'New Desk Mat 📚🖊️ So excited to bring a completely new product to the shop. ...',
      description: 'New Desk Mat 📚🖊️ So excited to bring a completely new product to the shop. It adds such a unique vintage touch our office or crafting space! Get it as part of the new Treasure Box ❤️   #deskmat #des',
      url: 'https://www.instagram.com/reel/C3LkwAjRK1N/',
      embedId: 'C3LkwAjRK1N',
      category: 'box-reveal',
      tags: ['deskmat', 'desksetup', 'craftingdesk', 'happydesk', 'vintagejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C3LkwAjRK1N/media/?size=m'
    },
    {
      id: 'ig-api-512',
      platform: 'instagram',
      title: 'Mr. Detective 🔍',
      description: 'Mr. Detective 🔍  #creativejournal #scrapbooking #junkjournaling #asmrjournaling #journalspread #sherlockholmes #detective #mystery',
      url: 'https://www.instagram.com/reel/C3Ix3_9xedv/',
      embedId: 'C3Ix3_9xedv',
      category: 'journal-spread',
      tags: ['creativejournal', 'scrapbooking', 'junkjournaling', 'asmrjournaling', 'journalspread', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C3Ix3_9xedv/media/?size=m'
    },
    {
      id: 'ig-api-513',
      platform: 'instagram',
      title: 'Mystery 🔎 VCB preview for February ✨ you can also add any InkPad to your exi...',
      description: 'Mystery 🔎 VCB preview for February ✨ you can also add any InkPad to your existing or new subscription for $3 off with the code INKPADUPGRADE (INKPADUPGRADEINT - for outside US orders)  #scrapbooking ',
      url: 'https://www.instagram.com/reel/C3AzE8eupEm/',
      embedId: 'C3AzE8eupEm',
      category: 'box-reveal',
      tags: ['scrapbooking', 'sherlockholmes', 'mysterytheme', 'mysterybox', 'journalingsupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C3AzE8eupEm/media/?size=m'
    },
    {
      id: 'ig-api-514',
      platform: 'instagram',
      title: '1 Word A Day - Forest 🌲 Gentle winter forest vibes in my mini vintage journa...',
      description: '1 Word A Day - Forest 🌲 Gentle winter forest vibes in my mini vintage journal 😍🐺 Supplies: Winter Curiosities Box 🔗 . . .  #minijournaling #journalspread #craft #artjournal #scrapbook #asmr #journ',
      url: 'https://www.instagram.com/reel/C2-neIuxZxY/',
      embedId: 'C2-neIuxZxY',
      category: 'journal-spread',
      tags: ['minijournaling', 'journalspread', 'craft', 'artjournal', 'scrapbook', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C2-neIuxZxY/media/?size=m'
    },
    {
      id: 'ig-api-515',
      platform: 'instagram',
      title: 'You do! ❤️🙏 Oftentimes we second guess ourselves and what we should be doing...',
      description: 'You do! ❤️🙏 Oftentimes we second guess ourselves and what we should be doing, for the benefit of others or because of how we would be perceived. It doesn\'t matter if others don\'t share your interests',
      url: 'https://www.instagram.com/reel/C25LOcJRWFc/',
      embedId: 'C25LOcJRWFc',
      category: 'tutorial',
      tags: ['desksetup', 'journalinspiration', 'journalingcommunity', 'smallbusinessmotivation', 'followyourdreams', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C25LOcJRWFc/media/?size=m'
    },
    {
      id: 'ig-api-516',
      platform: 'instagram',
      title: '1 Word A Day - Valor 🗡️',
      description: '1 Word A Day - Valor 🗡️  #leatherjournal #minijournaling #asmrjournal #scrapbook #collagejournal #creativejournal #medieval #stickerpeel',
      url: 'https://www.instagram.com/reel/C22pOJjRGBu/',
      embedId: 'C22pOJjRGBu',
      category: 'product-showcase',
      tags: ['leatherjournal', 'minijournaling', 'asmrjournal', 'scrapbook', 'collagejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C22pOJjRGBu/media/?size=m'
    },
    {
      id: 'ig-api-517',
      platform: 'instagram',
      title: 'Hello February ✨ Planning in the beginning of this year has been so much fun ...',
      description: 'Hello February ✨ Planning in the beginning of this year has been so much fun with our new planner 📒 Have you been decorating your monthly layouts with stickers too?   #plannerstickers #planner #month',
      url: 'https://www.instagram.com/reel/C20Wyk9x6UM/',
      embedId: 'C20Wyk9x6UM',
      category: 'product-showcase',
      tags: ['plannerstickers', 'planner', 'monthlyplanner', 'monthlylayout', 'planningasmr', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2024-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C20Wyk9x6UM/media/?size=m'
    },
    {
      id: 'ig-api-518',
      platform: 'instagram',
      title: 'One Word A Day - Fairy 🧚‍♀️ 💗',
      description: 'One Word A Day - Fairy 🧚‍♀️ 💗  #vintagejournal #minijournaling #fairyspread #enchantedforest #asmrjournaling #journalasmr',
      url: 'https://www.instagram.com/reel/C2yEyp3u0bz/',
      embedId: 'C2yEyp3u0bz',
      category: 'journal-spread',
      tags: ['vintagejournal', 'minijournaling', 'fairyspread', 'enchantedforest', 'asmrjournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C2yEyp3u0bz/media/?size=m'
    },
    {
      id: 'ig-api-519',
      platform: 'instagram',
      title: 'Winter in a Bottle ❄️ what\'s your favorite thing about winter? I grew up in S...',
      description: 'Winter in a Bottle ❄️ what\'s your favorite thing about winter? I grew up in Switzerland and for me winter is associated with skiing and snowshoeing in the mountains 🗻 you get this feeling of freedom ',
      url: 'https://www.instagram.com/reel/C2uoe80O_az/',
      embedId: 'C2uoe80O_az',
      category: 'journal-spread',
      tags: ['artjournal', 'journalspread', 'vintagejournal', 'asmr', 'journalinspiration', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C2uoe80O_az/media/?size=m'
    },
    {
      id: 'ig-api-520',
      platform: 'instagram',
      title: 'Did you know we have a Discord channel? ⭐ We host a lot of fun activities to ...',
      description: 'Did you know we have a Discord channel? ⭐ We host a lot of fun activities to foster creativity and conversation there. Daily themes to challenge yourself creatively, monthly challenges, swaps, book cl',
      url: 'https://www.instagram.com/reel/C2tKO2URQKk/',
      embedId: 'C2tKO2URQKk',
      category: 'product-showcase',
      tags: ['journalingcommunity', 'craftingcommunity', 'journalhealing', 'journalingfriends', 'asmrjournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C2tKO2URQKk/media/?size=m'
    },
    {
      id: 'ig-api-521',
      platform: 'instagram',
      title: '- making a tag reusing leftover and scrap pieces only! It\'s definitely a chal...',
      description: '#ScrapSaturday - making a tag reusing leftover and scrap pieces only! It\'s definitely a challenge for me not to use the obvious nice pieces that I have but it was so enjoyable to dig, mix and match an',
      url: 'https://www.instagram.com/reel/C2n-RfXxWBb/',
      embedId: 'C2n-RfXxWBb',
      category: 'tutorial',
      tags: ['scrapsaturday', 'scrapbooking', 'scrap', 'asmrjournaling', 'asmrscrap', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C2n-RfXxWBb/media/?size=m'
    },
    {
      id: 'ig-api-522',
      platform: 'instagram',
      title: 'One Word A Day - Serendipity ❤️🦋',
      description: 'One Word A Day - Serendipity ❤️🦋 What word would you like to see next?  #onewordaday #serendipity #relaxingsounds #asmrjournaling #vintagejournal #minijournal',
      url: 'https://www.instagram.com/reel/C2klL1aRo7n/',
      embedId: 'C2klL1aRo7n',
      category: 'product-showcase',
      tags: ['onewordaday', 'serendipity', 'relaxingsounds', 'asmrjournaling', 'vintagejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C2klL1aRo7n/media/?size=m'
    },
    {
      id: 'ig-api-523',
      platform: 'instagram',
      title: 'Winter Magic 🦊 ❄️',
      description: 'Winter Magic 🦊 ❄️  Link to the Winter Box in bio ✨  #winterspirit #wintermagic #craftywinter #winterspread #journalasmr #creativejournal #winterstickers #stickerpeel',
      url: 'https://www.instagram.com/reel/C2f3bc1x72r/',
      embedId: 'C2f3bc1x72r',
      category: 'journal-spread',
      tags: ['winterspirit', 'wintermagic', 'craftywinter', 'winterspread', 'journalasmr', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C2f3bc1x72r/media/?size=m'
    },
    {
      id: 'ig-api-524',
      platform: 'instagram',
      title: 'One Word a Day 🐉 Dragon',
      description: 'One Word a Day 🐉 Dragon Thank you for sharing your ideas and wishes with me in the comments ☺️  #dragons #minijournal #vintage #fantasyart #journalasmr #journalhealing',
      url: 'https://www.instagram.com/reel/C2dlz26x0fz/',
      embedId: 'C2dlz26x0fz',
      category: 'product-showcase',
      tags: ['dragons', 'minijournal', 'vintage', 'fantasyart', 'journalasmr', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C2dlz26x0fz/media/?size=m'
    },
    {
      id: 'ig-api-525',
      platform: 'instagram',
      title: 'Embrace the Spirit of Winter ❄️ New \'Winter\' Box, link in bio 🔗',
      description: 'Embrace the Spirit of Winter ❄️ New \'Winter\' Box, link in bio 🔗  #winterwonderland #winterspirit #craftingsupplies #creativejournal #journalhealing #journalasmr',
      url: 'https://www.instagram.com/reel/C2aKEBKucIe/',
      embedId: 'C2aKEBKucIe',
      category: 'box-reveal',
      tags: ['winterwonderland', 'winterspirit', 'craftingsupplies', 'creativejournal', 'journalhealing', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C2aKEBKucIe/media/?size=m'
    },
    {
      id: 'ig-api-526',
      platform: 'instagram',
      title: 'Testing the new Alphabet Stamps 😍 the stamp set is inspired by a 1500s font ...',
      description: 'Testing the new Alphabet Stamps 😍 the stamp set is inspired by a 1500s font from Paris and includes 26 letters + 10 numbers and 2 symbols - this stamp is part of the latest Treasure Box ❤️  #alphabet',
      url: 'https://www.instagram.com/reel/C2X8QXyx4yc/',
      embedId: 'C2X8QXyx4yc',
      category: 'box-reveal',
      tags: ['alphabet', 'alphabetstamps', 'stampingart', 'asmrjournaling', 'vintagestyle', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C2X8QXyx4yc/media/?size=m'
    },
    {
      id: 'ig-api-527',
      platform: 'instagram',
      title: '1 Word a Day Continues ✒️ What word should I do next',
      description: '1 Word a Day Continues ✒️ What word should I do next?   #onewordaday #minijournal #asmrjournaling #creativejournal #journalhealing #vintage',
      url: 'https://www.instagram.com/reel/C2V2iA_xbrf/',
      embedId: 'C2V2iA_xbrf',
      category: 'product-showcase',
      tags: ['onewordaday', 'minijournal', 'asmrjournaling', 'creativejournal', 'journalhealing', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C2V2iA_xbrf/media/?size=m'
    },
    {
      id: 'ig-api-528',
      platform: 'instagram',
      title: 'Enchanted Forest Notebook Page 1 ✨🦋 Isn\'t the first page the most scary to m...',
      description: 'Enchanted Forest Notebook Page 1 ✨🦋 Isn\'t the first page the most scary to make but also the most satisfying?   #ａｅｓｔｈｅｔｉｃ #artjournaling #creativejournal #asmrjournal #stickerpeel #calligraphy #ench',
      url: 'https://www.instagram.com/reel/C2SZB83uGy_/',
      embedId: 'C2SZB83uGy_',
      category: 'product-showcase',
      tags: ['artjournaling', 'creativejournal', 'asmrjournal', 'stickerpeel', 'calligraphy', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C2SZB83uGy_/media/?size=m'
    },
    {
      id: 'ig-api-529',
      platform: 'instagram',
      title: 'Are you celebrating Carnival in your region/country',
      description: 'Are you celebrating Carnival in your region/country?  Carnival Cards with the January VCB ✨  #vintagejournal #vintagecards #vintagecarnival #carnival #ephemera #asmrjournaling',
      url: 'https://www.instagram.com/reel/C2N75m4Rttc/',
      embedId: 'C2N75m4Rttc',
      category: 'product-showcase',
      tags: ['vintagejournal', 'vintagecards', 'vintagecarnival', 'carnival', 'ephemera', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C2N75m4Rttc/media/?size=m'
    },
    {
      id: 'ig-api-530',
      platform: 'instagram',
      title: 'It\'s a chunky journal 😍 I was only able to add part 1 for this reel, would y...',
      description: 'It\'s a chunky journal 😍 I was only able to add part 1 for this reel, would you like to see the second half?   #junkjournaling #asmrjournaling #flipthroughvideo #vintagejournal #creativejournal #menta',
      url: 'https://www.instagram.com/reel/C2LUJtVxxWJ/',
      embedId: 'C2LUJtVxxWJ',
      category: 'product-showcase',
      tags: ['junkjournaling', 'asmrjournaling', 'flipthroughvideo', 'vintagejournal', 'creativejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C2LUJtVxxWJ/media/?size=m'
    },
    {
      id: 'ig-api-531',
      platform: 'instagram',
      title: 'A little bit of nostalgia for a time past or one of your imagination ✨🐉 Scri...',
      description: 'A little bit of nostalgia for a time past or one of your imagination ✨🐉 Scribe Treasure Box Highlights ✨ Link in bio 🔗',
      url: 'https://www.instagram.com/reel/C2IFGMNOSLg/',
      embedId: 'C2IFGMNOSLg',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2024-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C2IFGMNOSLg/media/?size=m'
    },
    {
      id: 'ig-api-532',
      platform: 'instagram',
      title: 'Vintage Carnival ❤️ Using the 5 stamps upgrade! Last call to get it before we...',
      description: 'Vintage Carnival ❤️ Using the 5 stamps upgrade! Last call to get it before we start shipping 😊',
      url: 'https://www.instagram.com/reel/C2DhTOmRcE1/',
      embedId: 'C2DhTOmRcE1',
      category: 'product-showcase',
      tags: ['vintage'],
      creator: 'CoraCreaCrafts',
      date: '2024-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C2DhTOmRcE1/media/?size=m'
    },
    {
      id: 'ig-api-533',
      platform: 'instagram',
      title: 'Ink Swatched 🎨 Another way to use the new Vintage Minis Journals ❤️',
      description: 'Ink Swatched 🎨 Another way to use the new Vintage Minis Journals ❤️',
      url: 'https://www.instagram.com/reel/C1-KavUo3ed/',
      embedId: 'C1-KavUo3ed',
      category: 'product-showcase',
      tags: ['journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2024-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C1-KavUo3ed/media/?size=m'
    },
    {
      id: 'ig-api-534',
      platform: 'instagram',
      title: 'One Word A Day - Beginning 🌞 ♾️ using the new Vintage Mini Journal + Happy A...',
      description: 'One Word A Day - Beginning 🌞 ♾️ using the new Vintage Mini Journal + Happy Alchemist Tape',
      url: 'https://www.instagram.com/reel/C1zfMmmoCes/',
      embedId: 'C1zfMmmoCes',
      category: 'product-showcase',
      tags: ['journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2024-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C1zfMmmoCes/media/?size=m'
    },
    {
      id: 'ig-api-535',
      platform: 'instagram',
      title: 'Carnivals & Fairs 🎡 VCB preview for January 😍 we also have a special set of...',
      description: 'Carnivals & Fairs 🎡 VCB preview for January 😍 we also have a special set of 5 stamps available to add to your box as an upgrade!',
      url: 'https://www.instagram.com/reel/C1sVSOORFVq/',
      embedId: 'C1sVSOORFVq',
      category: 'box-reveal',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2024-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C1sVSOORFVq/media/?size=m'
    },
    {
      id: 'ig-api-536',
      platform: 'instagram',
      title: 'New Mini Journals - One word a day for 2024 🌟',
      description: 'New Mini Journals - One word a day for 2024 🌟',
      url: 'https://www.instagram.com/reel/C1mqoeEuDRb/',
      embedId: 'C1mqoeEuDRb',
      category: 'product-showcase',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C1mqoeEuDRb/media/?size=m'
    },
    {
      id: 'ig-api-537',
      platform: 'instagram',
      title: 'The Planning Companions are here 🥲 what a lovely way to end this year 💕 if ...',
      description: 'The Planning Companions are here 🥲 what a lovely way to end this year 💕 if you have pre ordered one, it will make your way to you this week! Link is in bio to order yours 😍🌟',
      url: 'https://www.instagram.com/reel/C1XsMiJRXPd/',
      embedId: 'C1XsMiJRXPd',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2023-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C1XsMiJRXPd/media/?size=m'
    },
    {
      id: 'ig-api-538',
      platform: 'instagram',
      title: '🎄 CLOSED 🎄winner is @smnmarina',
      description: '🎄 CLOSED 🎄winner is @smnmarina  ✨  Merry Christmas Flash Giveaway 🌟 24 hours only! Ends on the 25th of December  ✨  One lucky winner will receive a CoraCreaCrafts full 24-day Advent Calendar worth ',
      url: 'https://www.instagram.com/reel/C1PZ8Peu21c/',
      embedId: 'C1PZ8Peu21c',
      category: 'tutorial',
      tags: ['giveaway', 'stationerygiveaway', 'vintagestationery', 'outgoingmail', 'outgoinghappymail', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C1PZ8Peu21c/media/?size=m'
    },
    {
      id: 'ig-api-539',
      platform: 'instagram',
      title: 'Don\'t forget to take deep breaths ✨🥰',
      description: 'Don\'t forget to take deep breaths ✨🥰 Featuring our Victorian Scribe Transparent Tape',
      url: 'https://www.instagram.com/reel/C1DKo7cxcew/',
      embedId: 'C1DKo7cxcew',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2023-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C1DKo7cxcew/media/?size=m'
    },
    {
      id: 'ig-api-540',
      platform: 'instagram',
      title: 'Which one\'s your favorite? Wax Seal Highlighting Creams ✨ brand new on the we...',
      description: 'Which one\'s your favorite? Wax Seal Highlighting Creams ✨ brand new on the website!',
      url: 'https://www.instagram.com/reel/C0-YKMsRRTa/',
      embedId: 'C0-YKMsRRTa',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2023-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C0-YKMsRRTa/media/?size=m'
    },
    {
      id: 'ig-api-541',
      platform: 'instagram',
      title: 'Free Christmas Wrapping with any order placed today ⭐😍 check out the link in...',
      description: 'Free Christmas Wrapping with any order placed today ⭐😍 check out the link in bio for new special offers and surprises every day until Christmas 🎄',
      url: 'https://www.instagram.com/reel/C02bzQhR5xg/',
      embedId: 'C02bzQhR5xg',
      category: 'product-showcase',
      tags: ['christmas'],
      creator: 'CoraCreaCrafts',
      date: '2023-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C02bzQhR5xg/media/?size=m'
    },
    {
      id: 'ig-api-542',
      platform: 'instagram',
      title: 'Christmas Apothecary Style Gift Tags ⭐❤️ these were SO much fun to make! All ...',
      description: 'Christmas Apothecary Style Gift Tags ⭐❤️ these were SO much fun to make! All supplies from the shop: - Apothecary foil washi - Curiosities Box - Christmas Tapes Box - Apothecary stamps - Curiosities B',
      url: 'https://www.instagram.com/reel/C0xZGQBRbDA/',
      embedId: 'C0xZGQBRbDA',
      category: 'product-showcase',
      tags: ['washi tape', 'christmas'],
      creator: 'CoraCreaCrafts',
      date: '2023-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C0xZGQBRbDA/media/?size=m'
    },
    {
      id: 'ig-api-543',
      platform: 'instagram',
      title: 'Vintage Cross Wax Seal ✨ this is our newest \'rare finds\' addition to the webs...',
      description: 'Vintage Cross Wax Seal ✨ this is our newest \'rare finds\' addition to the website, we only have 5 available! Also featuring our new wax seal highlighting creams ⭐',
      url: 'https://www.instagram.com/reel/C0upXfVRFSR/',
      embedId: 'C0upXfVRFSR',
      category: 'product-showcase',
      tags: ['vintage'],
      creator: 'CoraCreaCrafts',
      date: '2023-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C0upXfVRFSR/media/?size=m'
    },
    {
      id: 'ig-api-544',
      platform: 'instagram',
      title: 'Vintage Stores & Bookshops 📚 You voted, I deliver 😍 Such a fun theme to end...',
      description: 'Vintage Stores & Bookshops 📚 You voted, I deliver 😍 Such a fun theme to end the year 🤗 hope you all enjoy it. You can subscribe in my bio 🔗',
      url: 'https://www.instagram.com/reel/C0pdc-WxYCE/',
      embedId: 'C0pdc-WxYCE',
      category: 'product-showcase',
      tags: ['vintage'],
      creator: 'CoraCreaCrafts',
      date: '2023-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C0pdc-WxYCE/media/?size=m'
    },
    {
      id: 'ig-api-545',
      platform: 'instagram',
      title: 'These are the most stunning tissue papers ⭐✨ and they work great for gift wra...',
      description: 'These are the most stunning tissue papers ⭐✨ and they work great for gift wrapping!',
      url: 'https://www.instagram.com/reel/C0mYj4gxYjv/',
      embedId: 'C0mYj4gxYjv',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2023-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C0mYj4gxYjv/media/?size=m'
    },
    {
      id: 'ig-api-546',
      platform: 'instagram',
      title: 'Vintage Shop Vibe ✨ First peek into the December Vintage Craft Box 🥰 Full Pr...',
      description: 'Vintage Shop Vibe ✨ First peek into the December Vintage Craft Box 🥰 Full Preview coming soon!',
      url: 'https://www.instagram.com/reel/C0hFQ1Eujk1/',
      embedId: 'C0hFQ1Eujk1',
      category: 'box-reveal',
      tags: ['vintage'],
      creator: 'CoraCreaCrafts',
      date: '2023-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C0hFQ1Eujk1/media/?size=m'
    },
    {
      id: 'ig-api-547',
      platform: 'instagram',
      title: 'Just loving the magical, dark, secret, powerful, fantastic vibes journaling c...',
      description: 'Just loving the magical, dark, secret, powerful, fantastic vibes journaling creates around you ✨⭐ #creativejournal #dragons #darkacademia #fantastic #vintagejournal',
      url: 'https://www.instagram.com/reel/C0fkwnrx_CR/',
      embedId: 'C0fkwnrx_CR',
      category: 'product-showcase',
      tags: ['creativejournal', 'dragons', 'darkacademia', 'fantastic', 'vintagejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C0fkwnrx_CR/media/?size=m'
    },
    {
      id: 'ig-api-548',
      platform: 'instagram',
      title: 'Magical Apothecary Vibes 🌱',
      description: 'Magical Apothecary Vibes 🌱  #specimen #apothecary #apothecarycabinet #creativejournal #journalingasmr #asmrsounds',
      url: 'https://www.instagram.com/reel/C0aEUoNRnQx/',
      embedId: 'C0aEUoNRnQx',
      category: 'product-showcase',
      tags: ['specimen', 'apothecary', 'apothecarycabinet', 'creativejournal', 'journalingasmr', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C0aEUoNRnQx/media/?size=m'
    },
    {
      id: 'ig-api-549',
      platform: 'instagram',
      title: 'A dream come true 🥲 making my own planner has been such a long project (and ...',
      description: 'A dream come true 🥲 making my own planner has been such a long project (and pretty difficult to be honest), there were times I thought it would never end. But here it is, vintage style and unique, th',
      url: 'https://www.instagram.com/reel/C0Sd9z5RHhj/',
      embedId: 'C0Sd9z5RHhj',
      category: 'product-showcase',
      tags: ['planner', 'planningtime', 'vintageplanner', 'plannerstickers', 'stationeryaddict', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C0Sd9z5RHhj/media/?size=m'
    },
    {
      id: 'ig-api-550',
      platform: 'instagram',
      title: 'Feeling the magic of Christmas 🎄 I love making unconventional Christmas cards',
      description: 'Feeling the magic of Christmas 🎄 I love making unconventional Christmas cards!',
      url: 'https://www.instagram.com/reel/C0P4yYGx9Pl/',
      embedId: 'C0P4yYGx9Pl',
      category: 'product-showcase',
      tags: ['christmas'],
      creator: 'CoraCreaCrafts',
      date: '2023-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C0P4yYGx9Pl/media/?size=m'
    },
    {
      id: 'ig-api-551',
      platform: 'instagram',
      title: 'Apothecary Box Highlights 🌿',
      description: 'Apothecary Box Highlights 🌿 Do you sometimes wish you could live in another time?',
      url: 'https://www.instagram.com/reel/C0Mt3yYxAHI/',
      embedId: 'C0Mt3yYxAHI',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2023-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C0Mt3yYxAHI/media/?size=m'
    },
    {
      id: 'ig-api-552',
      platform: 'instagram',
      title: 'Can you? 🤔',
      description: 'Can you? 🤔  The world is just better with stickers ❤️ By the way today is our last day of sale already.. All our sticker sheets are Buy 2 get 1 Free!',
      url: 'https://www.instagram.com/reel/C0KxKwnxbyE/',
      embedId: 'C0KxKwnxbyE',
      category: 'product-showcase',
      tags: ['stickers'],
      creator: 'CoraCreaCrafts',
      date: '2023-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C0KxKwnxbyE/media/?size=m'
    },
    {
      id: 'ig-api-553',
      platform: 'instagram',
      title: 'Hello, I\'m Coralie 👋',
      description: 'Hello, I\'m Coralie 👋 It\'s been a while since I shared a few words of introduction & said hi to the new joiners 🤗 Talking about myself doesn\'t come easily and here I am wondering what is interesting ',
      url: 'https://www.instagram.com/reel/C0H6rykxqrc/',
      embedId: 'C0H6rykxqrc',
      category: 'box-reveal',
      tags: ['journaling', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2023-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C0H6rykxqrc/media/?size=m'
    },
    {
      id: 'ig-api-554',
      platform: 'instagram',
      title: 'A little early Winter magic with this blue themed woodlands spread 💙',
      description: 'A little early Winter magic with this blue themed woodlands spread 💙 Only 2 days left to order the woodlands box 🎁 #wintermagic #winter #woodlands #forest #asmrjournaling #creativejournal #anxietyre',
      url: 'https://www.instagram.com/reel/C0FMQIcxBs4/',
      embedId: 'C0FMQIcxBs4',
      category: 'journal-spread',
      tags: ['wintermagic', 'winter', 'woodlands', 'forest', 'asmrjournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C0FMQIcxBs4/media/?size=m'
    },
    {
      id: 'ig-api-555',
      platform: 'instagram',
      title: 'A lot of people wonder what this is all about ✂️ trying it out is the best wa...',
      description: 'A lot of people wonder what this is all about ✂️ trying it out is the best way to understand why so many people find enjoyment and satisfaction in journaling.  ✨ It\'s a form of creativity and self exp',
      url: 'https://www.instagram.com/reel/C0CQFjPOPaV/',
      embedId: 'C0CQFjPOPaV',
      category: 'product-showcase',
      tags: ['junkjournal', 'journalingasmr', 'asmrjournaling', 'journalingjourney', 'benefitsofjournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/C0CQFjPOPaV/media/?size=m'
    },
    {
      id: 'ig-api-556',
      platform: 'instagram',
      title: 'Grab Bags ✨ this is the most discounted item of our Black Friday Sale 🤩 and ...',
      description: 'Grab Bags ✨ this is the most discounted item of our Black Friday Sale 🤩 and the most popular! We already had to increase our stock last night because it sold out. We only have a few left so grab your',
      url: 'https://www.instagram.com/reel/Cz9qIygxdPN/',
      embedId: 'Cz9qIygxdPN',
      category: 'product-showcase',
      tags: ['grabbags', 'blackfridaysale', 'bundles', 'stationerysale', 'stationerystore', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cz9qIygxdPN/media/?size=m'
    },
    {
      id: 'ig-api-557',
      platform: 'instagram',
      title: 'Packing Mystical Woodlands Boxes 🌲🐿️',
      description: 'Packing Mystical Woodlands Boxes 🌲🐿️  #vintagejournaling #creativejournal #journalingasmr #creativejournal #journalingsupplies #asmrpacking #asmrjournaling #wolf #mystical #woodlands',
      url: 'https://www.instagram.com/reel/CzzZjxrSi0z/',
      embedId: 'CzzZjxrSi0z',
      category: 'behind-the-scenes',
      tags: ['vintagejournaling', 'creativejournal', 'journalingasmr', 'creativejournal', 'journalingsupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CzzZjxrSi0z/media/?size=m'
    },
    {
      id: 'ig-api-558',
      platform: 'instagram',
      title: 'The Wolf\'s Lair 🐺 Woodlands Journal Page in the small creative notebook 😊 A...',
      description: 'The Wolf\'s Lair 🐺 Woodlands Journal Page in the small creative notebook 😊 All supplies are part of the Treasure \'Woodlands\' Box (except metal corners and brad)  #wolf #woodlands #mystical #forest #c',
      url: 'https://www.instagram.com/reel/Czt6Q-OPXYC/',
      embedId: 'Czt6Q-OPXYC',
      category: 'journal-spread',
      tags: ['wolf', 'woodlands', 'mystical', 'forest', 'creativejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Czt6Q-OPXYC/media/?size=m'
    },
    {
      id: 'ig-api-559',
      platform: 'instagram',
      title: 'Academia Christmas Card ⭐ Just wanted to try something a little different! Us...',
      description: 'Academia Christmas Card ⭐ Just wanted to try something a little different! Used the Christmas Tapes Box from the shop 🎁  #christmastape #christmascard #diycard #cardmaking #darkacademia #academiaaest',
      url: 'https://www.instagram.com/reel/CzpRkDBPVuc/',
      embedId: 'CzpRkDBPVuc',
      category: 'tutorial',
      tags: ['christmastape', 'christmascard', 'diycard', 'cardmaking', 'darkacademia', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CzpRkDBPVuc/media/?size=m'
    },
    {
      id: 'ig-api-560',
      platform: 'instagram',
      title: 'Pack some monthly subscription boxes with us 📦 we have a new theme every mon...',
      description: 'Pack some monthly subscription boxes with us 📦 we have a new theme every month and this month is On Stage with theater, music, dance and opera elemenfs 🎵',
      url: 'https://www.instagram.com/reel/Czm1H0oRpDT/',
      embedId: 'Czm1H0oRpDT',
      category: 'behind-the-scenes',
      tags: ['subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2023-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Czm1H0oRpDT/media/?size=m'
    },
    {
      id: 'ig-api-561',
      platform: 'instagram',
      title: 'Happy Theater Pages in my small creative binder notebook 😍',
      description: 'Happy Theater Pages in my small creative binder notebook 😍  Supplies: Vintage Craft Box Creative Notebook: Treasure Box   #theater #creativejournal #creativenotebook #asmrstationery #asmrjournal #jou',
      url: 'https://www.instagram.com/reel/Czca7VJxQZi/',
      embedId: 'Czca7VJxQZi',
      category: 'box-reveal',
      tags: ['theater', 'creativejournal', 'creativenotebook', 'asmrstationery', 'asmrjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Czca7VJxQZi/media/?size=m'
    },
    {
      id: 'ig-api-562',
      platform: 'instagram',
      title: 'On Stage 🩰🎶 Vintage Craft Box theme for November 🎭 so excited to finally s...',
      description: 'On Stage 🩰🎶 Vintage Craft Box theme for November 🎭 so excited to finally share some supplies related to theater, ballet, and music!   #stationerybox #oldtheater #phantomoftheopera #stickerpeel #mus',
      url: 'https://www.instagram.com/reel/CzZsbx4xLO1/',
      embedId: 'CzZsbx4xLO1',
      category: 'box-reveal',
      tags: ['stationerybox', 'oldtheater', 'phantomoftheopera', 'stickerpeel', 'musicpages', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CzZsbx4xLO1/media/?size=m'
    },
    {
      id: 'ig-api-563',
      platform: 'instagram',
      title: 'Christmas vibes 🌟 so excited to share our new collection of festive tapes 🎄...',
      description: 'Christmas vibes 🌟 so excited to share our new collection of festive tapes 🎄 Let the season begin!  Link in bio 🌟  #christmasdeco #christmascrafts #christmasgift #christmastape #washilove #pettape #',
      url: 'https://www.instagram.com/reel/CzZI7TDRebA/',
      embedId: 'CzZI7TDRebA',
      category: 'box-reveal',
      tags: ['christmasdeco', 'christmascrafts', 'christmasgift', 'christmastape', 'washilove', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CzZI7TDRebA/media/?size=m'
    },
    {
      id: 'ig-api-564',
      platform: 'instagram',
      title: 'We had a blast for our first time at the @chicagoplannerconference ✒️📖 this ...',
      description: 'We had a blast for our first time at the @chicagoplannerconference ✒️📖 this is a little overview of setting up our vending and sponsor table 😊 I think we had the most fun with putting together the s',
      url: 'https://www.instagram.com/reel/CzT6D5ZOJrU/',
      embedId: 'CzT6D5ZOJrU',
      category: 'product-showcase',
      tags: ['planneraddict', 'plannercommunity', 'chicagocrafters', 'chicagoplannerconference', 'plannerconference', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CzT6D5ZOJrU/media/?size=m'
    },
    {
      id: 'ig-api-565',
      platform: 'instagram',
      title: 'The Phantom of the Opera is here 🎭 So excited that the new Vintage Craft Box...',
      description: 'The Phantom of the Opera is here 🎭 So excited that the new Vintage Craft Box new theme fits with my favorite musical of all times 😍   #phantomoftheopera #musicpages #theater #oldtheater #onstage #cr',
      url: 'https://www.instagram.com/reel/CzMXuBpxXvm/',
      embedId: 'CzMXuBpxXvm',
      category: 'box-reveal',
      tags: ['phantomoftheopera', 'musicpages', 'theater', 'oldtheater', 'onstage', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CzMXuBpxXvm/media/?size=m'
    },
    {
      id: 'ig-api-566',
      platform: 'instagram',
      title: 'Woodlands Box Highlights 🐺 Here are some of my favorites from this new Treas...',
      description: 'Woodlands Box Highlights 🐺 Here are some of my favorites from this new Treasure Box! Link in bio 💚  #woodlands #mystical #forest #animals #stationerybox #stickerbook #stickerpeel #asmrjournaling #cr',
      url: 'https://www.instagram.com/reel/CzJxv5DRk7n/',
      embedId: 'CzJxv5DRk7n',
      category: 'product-showcase',
      tags: ['woodlands', 'mystical', 'forest', 'animals', 'stationerybox', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CzJxv5DRk7n/media/?size=m'
    },
    {
      id: 'ig-api-567',
      platform: 'instagram',
      title: 'Happy Halloween everyone 🕷️🎃',
      description: 'Happy Halloween everyone 🕷️🎃  #halloweenjournal #halloweencrafts #halloweenbundle #asmrstationery #asmrjournalsupplies #witchyjournal #witchyseason #happyhalloween #creativejournal',
      url: 'https://www.instagram.com/reel/CzFTKGoRe4O/',
      embedId: 'CzFTKGoRe4O',
      category: 'product-showcase',
      tags: ['halloweenjournal', 'halloweencrafts', 'halloweenbundle', 'asmrstationery', 'asmrjournalsupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CzFTKGoRe4O/media/?size=m'
    },
    {
      id: 'ig-api-568',
      platform: 'instagram',
      title: 'There\'s a place that I dreamed of, where I can free my mind.. 💚✒️📖 Creative...',
      description: 'There\'s a place that I dreamed of, where I can free my mind.. 💚✒️📖 Creative journaling is my happy place and has helped me in so many ways since I started!   #creativejournal #creativeescape #journa',
      url: 'https://www.instagram.com/reel/CzChDSJxNio/',
      embedId: 'CzChDSJxNio',
      category: 'product-showcase',
      tags: ['creativejournal', 'creativeescape', 'journalasmr', 'asmrjournaling', 'journalingjourney', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CzChDSJxNio/media/?size=m'
    },
    {
      id: 'ig-api-569',
      platform: 'instagram',
      title: 'We started shipping our Advent Calendars 😍❤️ it was such moment for us, to s...',
      description: 'We started shipping our Advent Calendars 😍❤️ it was such moment for us, to see all this hard work finally come together 🙏 for those asking, we are shipping international orders, then pre-orders, and',
      url: 'https://www.instagram.com/reel/Cy8mU9xu1RA/',
      embedId: 'Cy8mU9xu1RA',
      category: 'behind-the-scenes',
      tags: ['adventcalendar', 'adventgifts', 'advent', 'craftingideas', 'journalingsupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cy8mU9xu1RA/media/?size=m'
    },
    {
      id: 'ig-api-570',
      platform: 'instagram',
      title: 'Last Call 🖤✒️📖 This has been my favorite box so far this year together with...',
      description: 'Last Call 🖤✒️📖 This has been my favorite box so far this year together with the Pirates Box in June 🤎 Get it before it\'s gone as we are switching to next month\'s theme tonight!  #academiajournal #a',
      url: 'https://www.instagram.com/reel/Cy4F_DQR603/',
      embedId: 'Cy4F_DQR603',
      category: 'product-showcase',
      tags: ['academiajournal', 'aestheticjournal', 'harrypotterjournal', 'gothicaesthetics', 'gothicjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cy4F_DQR603/media/?size=m'
    },
    {
      id: 'ig-api-571',
      platform: 'instagram',
      title: 'Special Mail is coming for Christmas 💌 I started decorating envelopes for Ch...',
      description: 'Special Mail is coming for Christmas 💌 I started decorating envelopes for Christmas cards which will go to my family 💖   #christmascrafts #christmasgift #christmasenvelopes #diyenvelope #diycards #c',
      url: 'https://www.instagram.com/reel/Cy1j65Sx_5g/',
      embedId: 'Cy1j65Sx_5g',
      category: 'tutorial',
      tags: ['christmascrafts', 'christmasgift', 'christmasenvelopes', 'diyenvelope', 'diycards', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cy1j65Sx_5g/media/?size=m'
    },
    {
      id: 'ig-api-572',
      platform: 'instagram',
      title: 'The Dark Academic 📖🖤✒️',
      description: 'The Dark Academic 📖🖤✒️ Made with the Vintage Craft Box supplies 📦 this box is almost sold out and there are only a few days left to get it at the subscription price! 🔗 in my bio   #darkaesthetics ',
      url: 'https://www.instagram.com/reel/CyzA2yRxK_3/',
      embedId: 'CyzA2yRxK_3',
      category: 'box-reveal',
      tags: ['darkaesthetics', 'darkacademia', 'vintagejournaling', 'asmrjournaling', 'creativejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CyzA2yRxK_3/media/?size=m'
    },
    {
      id: 'ig-api-573',
      platform: 'instagram',
      title: 'One of the pleasures of colder, darker days 📖 Reading new books & writing ab...',
      description: 'One of the pleasures of colder, darker days 📖 Reading new books & writing about them in my journal 🤎 I just started reading Fourth Wing and have been having a hard time putting it down 😂  Leather J',
      url: 'https://www.instagram.com/reel/CywZeuER67E/',
      embedId: 'CywZeuER67E',
      category: 'product-showcase',
      tags: ['zipperpouch', 'leathernotebook', 'bookreview', 'bookjournaling', 'asmrjournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CywZeuER67E/media/?size=m'
    },
    {
      id: 'ig-api-574',
      platform: 'instagram',
      title: 'The girls filmed me opening the first full 2023 Advent Box 😍 It was so much ...',
      description: 'The girls filmed me opening the first full 2023 Advent Box 😍 It was so much fun 😂🥰  #adventcalendar #adventbox #christmasgift #journalingasmr #journalingsupplies #craftingsupplies #christmascrafts ',
      url: 'https://www.instagram.com/reel/CyrLaacRp_q/',
      embedId: 'CyrLaacRp_q',
      category: 'unboxing',
      tags: ['adventcalendar', 'adventbox', 'christmasgift', 'journalingasmr', 'journalingsupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CyrLaacRp_q/media/?size=m'
    },
    {
      id: 'ig-api-575',
      platform: 'instagram',
      title: 'We just restocked our Halloween Bundles 👻 we will ship all new bundle orders...',
      description: 'We just restocked our Halloween Bundles 👻 we will ship all new bundle orders placed between today and Sunday on Monday so you will get them before Halloween 🎃   #halloweenjournal #halloweencrafts #h',
      url: 'https://www.instagram.com/reel/CylztpLRhYy/',
      embedId: 'CylztpLRhYy',
      category: 'product-showcase',
      tags: ['halloweenjournal', 'halloweencrafts', 'halloweenbundle', 'asmrjournaling', 'creativejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CylztpLRhYy/media/?size=m'
    },
    {
      id: 'ig-api-576',
      platform: 'instagram',
      title: 'Mushroom 🍄 I love them on paper but can\'t eat them 😅 this spread is a mix o...',
      description: 'Mushroom 🍄 I love them on paper but can\'t eat them 😅 this spread is a mix of supplies from my shop (Autumn Curiosities Box) and some lovely supplies I got from @laurenphelpsdesigns 🥰   #autumnbox #',
      url: 'https://www.instagram.com/reel/CyjFCRLxfi3/',
      embedId: 'CyjFCRLxfi3',
      category: 'journal-spread',
      tags: ['autumnbox', 'autumnmagic', 'mushrooms', 'journalingasmr', 'journalspread', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CyjFCRLxfi3/media/?size=m'
    },
    {
      id: 'ig-api-577',
      platform: 'instagram',
      title: 'Riding the Wings of Ancient Powers 🐉 with the October \'Dark Academia\' suppli...',
      description: 'Riding the Wings of Ancient Powers 🐉 with the October \'Dark Academia\' supplies + Dragons Paper & Sticker Set (in Advent Calendar 🤫)   #journalingmagic #fantasyfeels #dragonsdreams #fourthwing #drago',
      url: 'https://www.instagram.com/reel/CyehGq_R12L/',
      embedId: 'CyehGq_R12L',
      category: 'product-showcase',
      tags: ['journalingmagic', 'fantasyfeels', 'dragonsdreams', 'fourthwing', 'dragons', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CyehGq_R12L/media/?size=m'
    },
    {
      id: 'ig-api-578',
      platform: 'instagram',
      title: 'Ars Longa Vita Brevis - \'skillfulness takes time and life is short\'. Mini Dar...',
      description: 'Ars Longa Vita Brevis - \'skillfulness takes time and life is short\'. Mini Dark Academia Pages 🖤  #darkacademia #darkaesthetics #gothicjournal #gothicaesthetics #academiajournal #asmrjournaling #creat',
      url: 'https://www.instagram.com/reel/CyWpwESRa8L/',
      embedId: 'CyWpwESRa8L',
      category: 'product-showcase',
      tags: ['darkacademia', 'darkaesthetics', 'gothicjournal', 'gothicaesthetics', 'academiajournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CyWpwESRa8L/media/?size=m'
    },
    {
      id: 'ig-api-579',
      platform: 'instagram',
      title: 'I was so excited and happy to discover the packaging box I made for the Adven...',
      description: 'I was so excited and happy to discover the packaging box I made for the Advent Calendars 😍 and I got to pack the first Advent Calendar of the year as a test run making sure everything fits in the box',
      url: 'https://www.instagram.com/reel/CyUPK5kRzh6/',
      embedId: 'CyUPK5kRzh6',
      category: 'behind-the-scenes',
      tags: ['craftingasmr', 'adventcalendar', 'adventgifts', 'adventbox', 'journalingsupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CyUPK5kRzh6/media/?size=m'
    },
    {
      id: 'ig-api-580',
      platform: 'instagram',
      title: 'The Beauty of Autumn 🍁🍂 in my new little creative notebook (notebook will b...',
      description: 'The Beauty of Autumn 🍁🍂 in my new little creative notebook (notebook will be part of the upcoming Treasure Box)  #autumnvibes #autumnbox #enchantedforest #creativejournal #asmrjournal #asmrjournalin',
      url: 'https://www.instagram.com/reel/CyRe-KdxZla/',
      embedId: 'CyRe-KdxZla',
      category: 'product-showcase',
      tags: ['autumnvibes', 'autumnbox', 'enchantedforest', 'creativejournal', 'asmrjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CyRe-KdxZla/media/?size=m'
    },
    {
      id: 'ig-api-581',
      platform: 'instagram',
      title: 'This Dark Academia theme is giving me Harry Potter vibes 🪄✨',
      description: 'This Dark Academia theme is giving me Harry Potter vibes 🪄✨   #harrypotterjournal #asmrjournaling #creativejournal #journalingsupplies #darkaesthetics #darkacademia #stickerpeel #gothicjournal',
      url: 'https://www.instagram.com/reel/CyO8y5oRJVh/',
      embedId: 'CyO8y5oRJVh',
      category: 'product-showcase',
      tags: ['harrypotterjournal', 'asmrjournaling', 'creativejournal', 'journalingsupplies', 'darkaesthetics', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CyO8y5oRJVh/media/?size=m'
    },
    {
      id: 'ig-api-582',
      platform: 'instagram',
      title: 'Double Trouble? These two leather pieces just belong with each other 😜 We ju...',
      description: 'Double Trouble? These two leather pieces just belong with each other 😜 We just received these brand new colors of zipper pouches and traveler\'s journals ❤️🤎💙💚 which one is your favorite?   #zipper',
      url: 'https://www.instagram.com/reel/CyMYrwRR4Nw/',
      embedId: 'CyMYrwRR4Nw',
      category: 'product-showcase',
      tags: ['zipperpouch', 'traveljournal', 'travelersnotebook', 'asmrjournaling', 'journalingsupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CyMYrwRR4Nw/media/?size=m'
    },
    {
      id: 'ig-api-583',
      platform: 'instagram',
      title: 'Dark Academia 🖤 Vintage Craft Box theme for October 📚 So excited for this b...',
      description: 'Dark Academia 🖤 Vintage Craft Box theme for October 📚 So excited for this box! Link in bio 🔗  P.s: do you recognize the music? I thought it sets the perfect mood!  #stationerybox #darkacademia #dar',
      url: 'https://www.instagram.com/reel/CyJ4-2pxcxE/',
      embedId: 'CyJ4-2pxcxE',
      category: 'box-reveal',
      tags: ['stationerybox', 'darkacademia', 'darkpages', 'stickerpeel', 'asmrjournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CyJ4-2pxcxE/media/?size=m'
    },
    {
      id: 'ig-api-584',
      platform: 'instagram',
      title: '🍂 AUTUMN BDAY GIVEAWAY 🍂',
      description: '🍂 AUTUMN BDAY GIVEAWAY 🍂  ✨To celebrate my Bday and the beginning of the most beautiful season I am hosting a giveaway full of wonderful surprises 🎁  ✨There will be 4 winners total!   First Prize ',
      url: 'https://www.instagram.com/reel/Cx-yrlWu6JD/',
      embedId: 'Cx-yrlWu6JD',
      category: 'tutorial',
      tags: ['giveaway', 'stationerygiveaway', 'vintagestationery', 'outgoingmail', 'outgoinghappymail', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cx-yrlWu6JD/media/?size=m'
    },
    {
      id: 'ig-api-585',
      platform: 'instagram',
      title: 'Dark Academia Card 🖤 Love some moody October vibes and crafting times with t...',
      description: 'Dark Academia Card 🖤 Love some moody October vibes and crafting times with the new October Vintage Craft Box supplies 🌑 link in bio 🔗  #darkacademia #academiaaesthetic #academiajournal #creativejou',
      url: 'https://www.instagram.com/reel/Cx8RhqkuaYx/',
      embedId: 'Cx8RhqkuaYx',
      category: 'tutorial',
      tags: ['darkacademia', 'academiaaesthetic', 'academiajournal', 'creativejournal', 'diycard', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cx8RhqkuaYx/media/?size=m'
    },
    {
      id: 'ig-api-586',
      platform: 'instagram',
      title: '1st of Advent is only 2 months away!! Time just flies.. I\'m so excited to sha...',
      description: '1st of Advent is only 2 months away!! Time just flies.. I\'m so excited to share a new Advent Calendar this year with fully custom packaging 😍 24 gifts total & 1 new journaling surprise every single d',
      url: 'https://www.instagram.com/reel/Cx3Xbxcxu3v/',
      embedId: 'Cx3Xbxcxu3v',
      category: 'behind-the-scenes',
      tags: ['adventcalendar', 'journalingsupplies', 'christmasgift', 'advent', 'stationerybox', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cx3Xbxcxu3v/media/?size=m'
    },
    {
      id: 'ig-api-587',
      platform: 'instagram',
      title: 'Autumn 🍁 so much beauty in the changing of seasons 😍',
      description: 'Autumn 🍁 so much beauty in the changing of seasons 😍  #falljournal #creativejournal #autumnvibes #colorfulspread #colorfulautumn #creativeseason #relaxingjournal #mindfulmoments #asmrjournaling #jou',
      url: 'https://www.instagram.com/reel/Cxy1j1YRGjW/',
      embedId: 'Cxy1j1YRGjW',
      category: 'journal-spread',
      tags: ['falljournal', 'creativejournal', 'autumnvibes', 'colorfulspread', 'colorfulautumn', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cxy1j1YRGjW/media/?size=m'
    },
    {
      id: 'ig-api-588',
      platform: 'instagram',
      title: 'Halloween bundles getting wrapped and shipped 👻 who\'s ready for the spooky s...',
      description: 'Halloween bundles getting wrapped and shipped 👻 who\'s ready for the spooky season?   #halloweencrafts #asmrjournaling #asmrorders #creativejournal #halloweenjournal #stickerpeel #giftwrapping #spooky',
      url: 'https://www.instagram.com/reel/CxwBvKkRlWZ/',
      embedId: 'CxwBvKkRlWZ',
      category: 'product-showcase',
      tags: ['halloweencrafts', 'asmrjournaling', 'asmrorders', 'creativejournal', 'halloweenjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CxwBvKkRlWZ/media/?size=m'
    },
    {
      id: 'ig-api-589',
      platform: 'instagram',
      title: 'Greek Mythology theme ✂️💙 this is one of the spread I enjoyed the most makin...',
      description: 'Greek Mythology theme ✂️💙 this is one of the spread I enjoyed the most making this month using all the VCB supplies 📦  #greekmythology #culturaljournal #creativejournal #asmrjournaling',
      url: 'https://www.instagram.com/reel/Cxq0drGRZqW/',
      embedId: 'Cxq0drGRZqW',
      category: 'journal-spread',
      tags: ['greekmythology', 'culturaljournal', 'creativejournal', 'asmrjournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cxq0drGRZqW/media/?size=m'
    },
    {
      id: 'ig-api-590',
      platform: 'instagram',
      title: 'Packing Subscription Boxes 📦 I wanted to show you all how we pack our Vintag...',
      description: 'Packing Subscription Boxes 📦 I wanted to show you all how we pack our Vintage Craft Boxes 😊 It\'s a process from building the boxes, to putting the tissue paper, adding the products, adding cards and',
      url: 'https://www.instagram.com/reel/CxoPHt8RkFf/',
      embedId: 'CxoPHt8RkFf',
      category: 'behind-the-scenes',
      tags: ['packingboxes', 'subscriptionbox', 'asmrjournaling', 'creativejournal', 'journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2023-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CxoPHt8RkFf/media/?size=m'
    },
    {
      id: 'ig-api-591',
      platform: 'instagram',
      title: '🍂Embracing the first day of Autumn with open arms and using the Autumn \'Curi...',
      description: '🍂Embracing the first day of Autumn with open arms and using the Autumn \'Curiosities\' Box 🎨🍁 let the season of cozy vibes, colorful leaves, pumpkin & my birthday begin!  #helloautumn #creativeseason',
      url: 'https://www.instagram.com/reel/CxjIlpFRwCw/',
      embedId: 'CxjIlpFRwCw',
      category: 'product-showcase',
      tags: ['helloautumn', 'creativeseason', 'autumnmagic', 'autumn'],
      creator: 'CoraCreaCrafts',
      date: '2023-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CxjIlpFRwCw/media/?size=m'
    },
    {
      id: 'ig-api-592',
      platform: 'instagram',
      title: 'Mini Myths Pages 🤎 Creative Mini Journal with the September Vintage Craft Box',
      description: 'Mini Myths Pages 🤎 Creative Mini Journal with the September Vintage Craft Box   #mythsandlegends #mythology #creativejournal #ccclegendschallenge #asmrjournal #asmrjournaling',
      url: 'https://www.instagram.com/reel/CxgkrowRJIJ/',
      embedId: 'CxgkrowRJIJ',
      category: 'box-reveal',
      tags: ['mythsandlegends', 'mythology', 'creativejournal', 'ccclegendschallenge', 'asmrjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CxgkrowRJIJ/media/?size=m'
    },
    {
      id: 'ig-api-593',
      platform: 'instagram',
      title: 'Autumn Box Preview 🍁🤎 link in bio 🔗',
      description: 'Autumn Box Preview 🍁🤎 link in bio 🔗  #autumnbox #stationeryshop #sneakpeek #stationerybox #asmrstationery #asmrjournaling #journalingasmr #stickerpeel #unboxing #fallcrafts',
      url: 'https://www.instagram.com/reel/Cxd3n-YOHwo/',
      embedId: 'Cxd3n-YOHwo',
      category: 'unboxing',
      tags: ['autumnbox', 'stationeryshop', 'sneakpeek', 'stationerybox', 'asmrstationery', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cxd3n-YOHwo/media/?size=m'
    },
    {
      id: 'ig-api-594',
      platform: 'instagram',
      title: 'Butterfly Notebooks 💖✨',
      description: 'Butterfly Notebooks 💖✨  #magic #christmasgift #notebook #stationeryshop #leatherpouch #stationerystore #journalingasmr #journalingsupplies',
      url: 'https://www.instagram.com/reel/CxbXlQyRrRb/',
      embedId: 'CxbXlQyRrRb',
      category: 'product-showcase',
      tags: ['magic', 'christmasgift', 'notebook', 'stationeryshop', 'leatherpouch', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CxbXlQyRrRb/media/?size=m'
    },
    {
      id: 'ig-api-595',
      platform: 'instagram',
      title: 'Mini Autumn Pages 🍁 with the Sept/Oct Autumn \'Curiosities\' Box 🤎',
      description: 'Mini Autumn Pages 🍁 with the Sept/Oct Autumn \'Curiosities\' Box 🤎  #autumnvibes #minijournal #minispread #asmrjournal #creativejournal #journalingcommunity #aestheticjournal #fallcrafts #journalingsu',
      url: 'https://www.instagram.com/reel/CxYfN7dxasR/',
      embedId: 'CxYfN7dxasR',
      category: 'journal-spread',
      tags: ['autumnvibes', 'minijournal', 'minispread', 'asmrjournal', 'creativejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CxYfN7dxasR/media/?size=m'
    },
    {
      id: 'ig-api-596',
      platform: 'instagram',
      title: 'If only packing boxes was that fast 😅🙏 We had such a good time trying to cr...',
      description: 'If only packing boxes was that fast 😅🙏 We had such a good time trying to create a transition reel with @callie.plans from the team ❤️ September boxes are still shipping! We are a little delayed but ',
      url: 'https://www.instagram.com/reel/CxWLW3vxPSJ/',
      embedId: 'CxWLW3vxPSJ',
      category: 'behind-the-scenes',
      tags: ['subscriptionbox', 'packanorderwithme', 'packingboxes', 'asmrorders', 'journalingasmr', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CxWLW3vxPSJ/media/?size=m'
    },
    {
      id: 'ig-api-597',
      platform: 'instagram',
      title: 'Witchy Spread 🧹 with a special secret spell ✨ I\'m so enjoying the pre-hallow...',
      description: 'Witchy Spread 🧹 with a special secret spell ✨ I\'m so enjoying the pre-halloween season 🕷️🎃  All supplies used are part of the Halloween Bundle except the spiderweb stencil which is available separa',
      url: 'https://www.instagram.com/reel/CxQqkZXxnQg/',
      embedId: 'CxQqkZXxnQg',
      category: 'journal-spread',
      tags: ['witchyjournal', 'grimoire', 'spellbook', 'halloweenjournal', 'halloweencrafts', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CxQqkZXxnQg/media/?size=m'
    },
    {
      id: 'ig-api-598',
      platform: 'instagram',
      title: 'Myths & Legends 🖤 Check out our September VCB, link in bio 🔗',
      description: 'Myths & Legends 🖤 Check out our September VCB, link in bio 🔗  Featuring beautiful slavic myths designs by artists @lucasstaniec_ai   #asmrjournaling #journalingasmr #creativejournal #creativeescape ',
      url: 'https://www.instagram.com/reel/CxLzX_gRWgV/',
      embedId: 'CxLzX_gRWgV',
      category: 'product-showcase',
      tags: ['asmrjournaling', 'journalingasmr', 'creativejournal', 'creativeescape', 'collage', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CxLzX_gRWgV/media/?size=m'
    },
    {
      id: 'ig-api-599',
      platform: 'instagram',
      title: 'It\'s just a bunch of hocus pocus 🕷️🎃 Halloween bundles available and shippi...',
      description: 'It\'s just a bunch of hocus pocus 🕷️🎃 Halloween bundles available and shipping soon!   #journalasmr #hocuspocus #halloweenjournal #creativejournal #asmrjournal',
      url: 'https://www.instagram.com/reel/CxG1uhCx2BT/',
      embedId: 'CxG1uhCx2BT',
      category: 'product-showcase',
      tags: ['journalasmr', 'hocuspocus', 'halloweenjournal', 'creativejournal', 'asmrjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CxG1uhCx2BT/media/?size=m'
    },
    {
      id: 'ig-api-600',
      platform: 'instagram',
      title: 'Gold Sparkly Tags ✨ Is it Christmas yet? 😅',
      description: 'Gold Sparkly Tags ✨ Is it Christmas yet? 😅   #christmastags #goldtags #goldstickers #ornamentstickers #foilstickers #stickerbox #stickerpeel #diytags #christmascrafts #asmrjournaling #creativejournal',
      url: 'https://www.instagram.com/reel/CxEEsKlxx7W/',
      embedId: 'CxEEsKlxx7W',
      category: 'tutorial',
      tags: ['christmastags', 'goldtags', 'goldstickers', 'ornamentstickers', 'foilstickers', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CxEEsKlxx7W/media/?size=m'
    },
    {
      id: 'ig-api-601',
      platform: 'instagram',
      title: 'September \'Myths & Legends\' Vintage Craft Box 🐉 one of my favorite themes to...',
      description: 'September \'Myths & Legends\' Vintage Craft Box 🐉 one of my favorite themes to date! Inspired by myths and legends from all around the world 🌎   #mythsandlegends #goldpapers #legendofarthur #slavicmyt',
      url: 'https://www.instagram.com/reel/Cw-s7wCxPtz/',
      embedId: 'Cw-s7wCxPtz',
      category: 'box-reveal',
      tags: ['mythsandlegends', 'goldpapers', 'legendofarthur', 'slavicmythology', 'mythology', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cw-s7wCxPtz/media/?size=m'
    },
    {
      id: 'ig-api-602',
      platform: 'instagram',
      title: 'New Leather Product Colors 😍 Comment your favorite! 🖤🤍🤎💙🩶❤️',
      description: 'New Leather Product Colors 😍 Comment your favorite! 🖤🤍🤎💙🩶❤️  Available colors for our Ephemera Pouches, Small Pouches, Wallets, Mini Cases and Mini Journals   🔗 in bio  #minijournal #leatherpou',
      url: 'https://www.instagram.com/reel/Cw5no8JRRcE/',
      embedId: 'Cw5no8JRRcE',
      category: 'behind-the-scenes',
      tags: ['minijournal', 'leatherpouch', 'ephemera', 'handmadeleathergoods', 'wallets', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cw5no8JRRcE/media/?size=m'
    },
    {
      id: 'ig-api-603',
      platform: 'instagram',
      title: 'Reorganizing my studio 😅 It took me a good 8 hours to clean up, destash, and...',
      description: 'Reorganizing my studio 😅 It took me a good 8 hours to clean up, destash, and reorganize but it feels so good to be creating in a neat space now 🥰 the question always is for messy crafters like me.. ',
      url: 'https://www.instagram.com/reel/Cw3bTHPxikT/',
      embedId: 'Cw3bTHPxikT',
      category: 'product-showcase',
      tags: ['deskdecor', 'deskorganization', 'desksetup', 'beforeandafter', 'studiodecor', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cw3bTHPxikT/media/?size=m'
    },
    {
      id: 'ig-api-604',
      platform: 'instagram',
      title: 'Diving into a legend with my journal 🐉first sneak peak of the September Myth...',
      description: 'Diving into a legend with my journal 🐉first sneak peak of the September Myths & Legends box 🤎 available to order now on the website!   #lochnessmonster #legends #fantasyart #journalingasmr #fantasys',
      url: 'https://www.instagram.com/reel/Cw0bWmmuyec/',
      embedId: 'Cw0bWmmuyec',
      category: 'box-reveal',
      tags: ['lochnessmonster', 'legends', 'fantasyart', 'journalingasmr', 'fantasyspread', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cw0bWmmuyec/media/?size=m'
    },
    {
      id: 'ig-api-605',
      platform: 'instagram',
      title: 'Labor Day Sale is live 🎉🤗 Link in bio 🔗',
      description: 'Labor Day Sale is live 🎉🤗 Link in bio 🔗 #labordayweekend #labordaysale #stationerysale #stationeryboxes #washitape #sale #journalingsale #journalasmr',
      url: 'https://www.instagram.com/reel/CwvWAQ1ROFJ/',
      embedId: 'CwvWAQ1ROFJ',
      category: 'product-showcase',
      tags: ['labordayweekend', 'labordaysale', 'stationerysale', 'stationeryboxes', 'washitape', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CwvWAQ1ROFJ/media/?size=m'
    },
    {
      id: 'ig-api-606',
      platform: 'instagram',
      title: 'Pre- History theme 🤎 should we make pre-history one of the themes for next y...',
      description: 'Pre- History theme 🤎 should we make pre-history one of the themes for next year\'s Vintage Craft Boxes? 🦖  #prehistory #prehistorycreatures #creativejournal #creativeescape #asmrjournaling #animalssp',
      url: 'https://www.instagram.com/reel/CwqhfViRfjJ/',
      embedId: 'CwqhfViRfjJ',
      category: 'journal-spread',
      tags: ['prehistory', 'prehistorycreatures', 'creativejournal', 'creativeescape', 'asmrjournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CwqhfViRfjJ/media/?size=m'
    },
    {
      id: 'ig-api-607',
      platform: 'instagram',
      title: 'Gold Scribe ✒️ a little gold in my journal with the new Treasure Box items 🤎',
      description: 'Gold Scribe ✒️ a little gold in my journal with the new Treasure Box items 🤎  #treasurebox #goldfoil #goldstickers #scribe #washitapedesign #stickerpeel #asmrjournal #journalinginspiration #creativej',
      url: 'https://www.instagram.com/reel/CwnwPqpIfiv/',
      embedId: 'CwnwPqpIfiv',
      category: 'product-showcase',
      tags: ['treasurebox', 'goldfoil', 'goldstickers', 'scribe', 'washitapedesign', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CwnwPqpIfiv/media/?size=m'
    },
    {
      id: 'ig-api-608',
      platform: 'instagram',
      title: 'The most important part of the day 🌟 Don\'t forget to take care of yourself a...',
      description: 'The most important part of the day 🌟 Don\'t forget to take care of yourself and doing what you love, even if you can just spare a few minutes each day ❤️  #selfcarejournal #journalingjourney #mindfulm',
      url: 'https://www.instagram.com/reel/CwitJU1IBdy/',
      embedId: 'CwitJU1IBdy',
      category: 'product-showcase',
      tags: ['selfcarejournal', 'journalingjourney', 'mindfulmoments', 'relaxandreflect', 'metimemagic', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CwitJU1IBdy/media/?size=m'
    },
    {
      id: 'ig-api-609',
      platform: 'instagram',
      title: 'Victorian ASMR subscription box 📦 Treasure Box Highlights 😍 what\'s your fav...',
      description: 'Victorian ASMR subscription box 📦 Treasure Box Highlights 😍 what\'s your favorite? Link in bio to get yours!  #stickerbook #stickerpeel #goldstickers #goldfoil #asmrjournaling #asmrstationery #journa',
      url: 'https://www.instagram.com/reel/CwfmQxrId5x/',
      embedId: 'CwfmQxrId5x',
      category: 'product-showcase',
      tags: ['stickerbook', 'stickerpeel', 'goldstickers', 'goldfoil', 'asmrjournaling', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CwfmQxrId5x/media/?size=m'
    },
    {
      id: 'ig-api-610',
      platform: 'instagram',
      title: 'Last Call for the \'Vintage Games\' Box of journaling supplies 📦🤎 before we s...',
      description: 'Last Call for the \'Vintage Games\' Box of journaling supplies 📦🤎 before we switch to the next theme tomorrow! Sometimes crafting & journaling is all you need to let your imagination wander and travel',
      url: 'https://www.instagram.com/reel/CwaZq0gI93N/',
      embedId: 'CwaZq0gI93N',
      category: 'box-reveal',
      tags: ['vintagegames', '18thcentury', 'kingscourt', 'nobility', 'cardgames', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CwaZq0gI93N/media/?size=m'
    },
    {
      id: 'ig-api-611',
      platform: 'instagram',
      title: 'Colorful Victorian Theme 🩷 ASMR Creative Journal with the supplies from the ...',
      description: 'Colorful Victorian Theme 🩷 ASMR Creative Journal with the supplies from the new Treasure Box 🤗 that Victorian Writer PET tape is my favorite 😍   #asmrjournaling #asmrjournalsupplies #washidispenser',
      url: 'https://www.instagram.com/reel/CwVkrbDI_6B/',
      embedId: 'CwVkrbDI_6B',
      category: 'journal-spread',
      tags: ['asmrjournaling', 'asmrjournalsupplies', 'washidispenser', 'colorfuljournal', 'colorfulspread', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CwVkrbDI_6B/media/?size=m'
    },
    {
      id: 'ig-api-612',
      platform: 'instagram',
      title: 'ASMR Creative Journal 💙 Peaceful day at the beach.. Summer is not over yet 🌊',
      description: 'ASMR Creative Journal 💙 Peaceful day at the beach.. Summer is not over yet 🌊   You can still get the Ocean themed Curiosities Box on my website with all the supplies used here 🔗   #asmrjournal #asm',
      url: 'https://www.instagram.com/reel/CwSkhINIyaW/',
      embedId: 'CwSkhINIyaW',
      category: 'journal-spread',
      tags: ['asmrjournal', 'asmrjournaling', 'creativejournal', 'oceanlover', 'beachvibes', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CwSkhINIyaW/media/?size=m'
    },
    {
      id: 'ig-api-613',
      platform: 'instagram',
      title: 'My first fair 😍 this weekend was so much fun! Thank you to everyone who drop...',
      description: 'My first fair 😍 this weekend was so much fun! Thank you to everyone who dropped by our booth. It was amazing to meet so many of you!   #fair #market #artisanmarket #stampandscrapbookexpo #scrapbookin',
      url: 'https://www.instagram.com/reel/CwQA3l7oVU2/',
      embedId: 'CwQA3l7oVU2',
      category: 'product-showcase',
      tags: ['fair', 'market', 'artisanmarket', 'stampandscrapbookexpo', 'scrapbooking', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CwQA3l7oVU2/media/?size=m'
    },
    {
      id: 'ig-api-614',
      platform: 'instagram',
      title: 'Washi Tape Dispenser 🤎😍 Get it as part of our Treasure Box 📦 link in bio 🔗',
      description: 'Washi Tape Dispenser 🤎😍 Get it as part of our Treasure Box 📦 link in bio 🔗 #washitapes #washitapedispenser #washilove #washiroll #minicabinet #deskorganization #desksetup #journalingasmr #journali',
      url: 'https://www.instagram.com/reel/CwLIxBDIs2V/',
      embedId: 'CwLIxBDIs2V',
      category: 'product-showcase',
      tags: ['washitapes', 'washitapedispenser', 'washilove', 'washiroll', 'minicabinet', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CwLIxBDIs2V/media/?size=m'
    },
    {
      id: 'ig-api-615',
      platform: 'instagram',
      title: 'ASMR Creative Journal 🤎 Just relaxing creating a spread with the August \'Vin...',
      description: 'ASMR Creative Journal 🤎 Just relaxing creating a spread with the August \'Vintage Games\' supplies 🃏  #creativejournal #asmrjournal #vintagejournaling #vintagejournal #stickerpeel #playingcards #aesth',
      url: 'https://www.instagram.com/reel/CwD6tLOo1HK/',
      embedId: 'CwD6tLOo1HK',
      category: 'journal-spread',
      tags: ['creativejournal', 'asmrjournal', 'vintagejournaling', 'vintagejournal', 'stickerpeel', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CwD6tLOo1HK/media/?size=m'
    },
    {
      id: 'ig-api-616',
      platform: 'instagram',
      title: 'ASMR Creative Journal 🖤 Victorian Inspired Pages with the supplies from the ...',
      description: 'ASMR Creative Journal 🖤 Victorian Inspired Pages with the supplies from the Aug/Sept Treasure Box 😍 includes  supplies used to make this spread including the dispenser (except the journal itself & t',
      url: 'https://www.instagram.com/reel/CwAmqUDgu6Z/',
      embedId: 'CwAmqUDgu6Z',
      category: 'journal-spread',
      tags: ['asmrjournal', 'creativejournal', 'jourmalingasmr', 'journalspread', 'victorianspread', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CwAmqUDgu6Z/media/?size=m'
    },
    {
      id: 'ig-api-617',
      platform: 'instagram',
      title: 'How real do these playing cards look? 🃏 So happy with how the supplies for t...',
      description: 'How real do these playing cards look? 🃏 So happy with how the supplies for the August Box came out!   We are still shipping your boxes this whole week & you can still get yours on our website if you ',
      url: 'https://www.instagram.com/reel/Cv7ZNlGAOSl/',
      embedId: 'Cv7ZNlGAOSl',
      category: 'behind-the-scenes',
      tags: ['subscriptionbox', 'fulfillingorders', 'journalingasmr', 'journalingcommunity', 'smallbusinessgoal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cv7ZNlGAOSl/media/?size=m'
    },
    {
      id: 'ig-api-618',
      platform: 'instagram',
      title: '🤎 or 🖤? We just got this new antique brown color stocked for our mini woode...',
      description: '🤎 or 🖤? We just got this new antique brown color stocked for our mini wooden cabinets!   #minicabinet #deskdecor #deskorganization #desksetup #woodendecor #asmrjournaling #smallthings #stationerysho',
      url: 'https://www.instagram.com/reel/Cv2hM0BAi1W/',
      embedId: 'Cv2hM0BAi1W',
      category: 'product-showcase',
      tags: ['minicabinet', 'deskdecor', 'deskorganization', 'desksetup', 'woodendecor', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cv2hM0BAi1W/media/?size=m'
    },
    {
      id: 'ig-api-619',
      platform: 'instagram',
      title: 'ASMR Creative Journal 🖤 Gothic vibes in my Black Grimoire Journal using some...',
      description: 'ASMR Creative Journal 🖤 Gothic vibes in my Black Grimoire Journal using some of the Halloween/Gothic products newly available in my shop 💀  #gothic #creativejournal #gothicjournal #asmrjournaling #d',
      url: 'https://www.instagram.com/reel/Cv0gpooALWl/',
      embedId: 'Cv0gpooALWl',
      category: 'product-showcase',
      tags: ['gothic', 'creativejournal', 'gothicjournal', 'asmrjournaling', 'darkpages', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cv0gpooALWl/media/?size=m'
    },
    {
      id: 'ig-api-620',
      platform: 'instagram',
      title: 'Washi Tape Dispenser 😍 I\'ve been wanting to bring this tool for a while and ...',
      description: 'Washi Tape Dispenser 😍 I\'ve been wanting to bring this tool for a while and I\'m super excited that it\'s finally here! This is coming as part of the current Treasure Box which you can now order on my ',
      url: 'https://www.instagram.com/reel/CvvN96Og1pD/',
      embedId: 'CvvN96Og1pD',
      category: 'product-showcase',
      tags: ['washitapedispenser', 'washitapelove', 'stationeryshop', 'deskdecor', 'deskorganization', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CvvN96Og1pD/media/?size=m'
    },
    {
      id: 'ig-api-621',
      platform: 'instagram',
      title: 'ASMR Creative Journal ♣️ Vintage Games Paged with the new supplies from the A...',
      description: 'ASMR Creative Journal ♣️ Vintage Games Paged with the new supplies from the August VCB 🃏  #vintagejournaling #vintagecards #foilcards #stickerpeel #asmrjournaling #relaxingjournal #creativejournal #p',
      url: 'https://www.instagram.com/reel/CvswTYvgmv7/',
      embedId: 'CvswTYvgmv7',
      category: 'product-showcase',
      tags: ['vintagejournaling', 'vintagecards', 'foilcards', 'stickerpeel', 'asmrjournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CvswTYvgmv7/media/?size=m'
    },
    {
      id: 'ig-api-622',
      platform: 'instagram',
      title: 'ASMR Preview of the \'Vintage Games\' Vintage Craft Box for this month of Augus...',
      description: 'ASMR Preview of the \'Vintage Games\' Vintage Craft Box for this month of August ♟️ Link in bio 🔗  #asmrjournaling #chesslover #journalingasmr #stickerpeel #vintagejournal #aesthetic #journalingsubscri',
      url: 'https://www.instagram.com/reel/CvkSPXpA4nl/',
      embedId: 'CvkSPXpA4nl',
      category: 'box-reveal',
      tags: ['asmrjournaling', 'chesslover', 'journalingasmr', 'stickerpeel', 'vintagejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CvkSPXpA4nl/media/?size=m'
    },
    {
      id: 'ig-api-623',
      platform: 'instagram',
      title: 'So what are these cards for exactly? 🃏',
      description: 'So what are these cards for exactly? 🃏  I\'m always trying to find new and exciting product ideas for my journaling and stationery subscription boxes and I love to work around different themes every m',
      url: 'https://www.instagram.com/reel/CvkEYpWgvIp/',
      embedId: 'CvkEYpWgvIp',
      category: 'behind-the-scenes',
      tags: ['playingcards', 'foilcards', 'cardgames', 'vintagecards', 'vintagejournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CvkEYpWgvIp/media/?size=m'
    },
    {
      id: 'ig-api-624',
      platform: 'instagram',
      title: 'Mini Transparent Box Upcycled ♟️This was easy and extra fun using the items f...',
      description: 'Mini Transparent Box Upcycled ♟️This was easy and extra fun using the items from the new August VCB! I\'m in love with the foiled card set ♣️♥️ Full sneak peak coming soon! Link is in my bio to get thi',
      url: 'https://www.instagram.com/reel/Cvf1E75A7rd/',
      embedId: 'Cvf1E75A7rd',
      category: 'box-reveal',
      tags: ['upcycledbox', 'diybox', 'craftingasmr', 'stickerpeel', 'vintagejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cvf1E75A7rd/media/?size=m'
    },
    {
      id: 'ig-api-625',
      platform: 'instagram',
      title: 'ASMR Upcycled Bottle Project 💙 This project took me quite a while to realize...',
      description: 'ASMR Upcycled Bottle Project 💙 This project took me quite a while to realize, from bringing the bottle back from Spain last year, to deciding what to make and finally getting it done! I used the supp',
      url: 'https://www.instagram.com/reel/CvdSkYMgMMo/',
      embedId: 'CvdSkYMgMMo',
      category: 'box-reveal',
      tags: ['curiosities', 'upcycling', 'upcycledbottle', 'seabottle', 'marine', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CvdSkYMgMMo/media/?size=m'
    },
    {
      id: 'ig-api-626',
      platform: 'instagram',
      title: 'Curiosities Box Highlights 🌊⛵This box is all about the ocean, the beach, sum...',
      description: 'Curiosities Box Highlights 🌊⛵This box is all about the ocean, the beach, summer and blue colors 💙 Link in bio to get yours 🔗  #seashells #stickerpeel #stencil #asmrjournaling #journalingasmr #journ',
      url: 'https://www.instagram.com/reel/Cvap4D1AY72/',
      embedId: 'Cvap4D1AY72',
      category: 'product-showcase',
      tags: ['seashells', 'stickerpeel', 'stencil', 'asmrjournaling', 'journalingasmr', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cvap4D1AY72/media/?size=m'
    },
    {
      id: 'ig-api-627',
      platform: 'instagram',
      title: 'ASMR Creative Journal 💙💛❤️ Floral Frames using the new notepads from my sho...',
      description: 'ASMR Creative Journal 💙💛❤️ Floral Frames using the new notepads from my shop 🥰 Each frame has its own color, like a landscaped garden of sorts.  #creativejournal #journalspread #journalimgasmr #asm',
      url: 'https://www.instagram.com/reel/CvYQgSxgZel/',
      embedId: 'CvYQgSxgZel',
      category: 'journal-spread',
      tags: ['creativejournal', 'journalspread', 'journalimgasmr', 'asmrjournal', 'stickerpeel', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CvYQgSxgZel/media/?size=m'
    },
    {
      id: 'ig-api-628',
      platform: 'instagram',
      title: 'Our brand new notepads are here 🥲😍 Special offer is available this weekend ...',
      description: 'Our brand new notepads are here 🥲😍 Special offer is available this weekend only, buy 2 get 1 free!  I\'m so excited to launch a new \'basics\' product, this is a first for my shop and I hope it\'s succe',
      url: 'https://www.instagram.com/reel/CvSMPY-AVkm/',
      embedId: 'CvSMPY-AVkm',
      category: 'product-showcase',
      tags: ['notepads', 'foilednotepads', 'foil', 'notetaking', 'asmrjournaling', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CvSMPY-AVkm/media/?size=m'
    },
    {
      id: 'ig-api-629',
      platform: 'instagram',
      title: 'ASMR writing journal 💙 about the Sea and it\'s beauty using the new supplies ...',
      description: 'ASMR writing journal 💙 about the Sea and it\'s beauty using the new supplies from the July/Aug Curiosities Box 🪸  #asmrjournaling #creativejournal #writinginspiration #beautyofthesea #seapages #ocean',
      url: 'https://www.instagram.com/reel/CvN0HKAgK18/',
      embedId: 'CvN0HKAgK18',
      category: 'product-showcase',
      tags: ['asmrjournaling', 'creativejournal', 'writinginspiration', 'beautyofthesea', 'seapages', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CvN0HKAgK18/media/?size=m'
    },
    {
      id: 'ig-api-630',
      platform: 'instagram',
      title: 'ASMR Creative Journal 📜 Floral Country Pages 🌼 with the English Countryside...',
      description: 'ASMR Creative Journal 📜 Floral Country Pages 🌼 with the English Countryside July Box. Today is the last day to subscribe to get it 💛  #flowerspread #countryside #creativejournal #journalingasmr #vi',
      url: 'https://www.instagram.com/reel/CvLKyANAQbH/',
      embedId: 'CvLKyANAQbH',
      category: 'journal-spread',
      tags: ['flowerspread', 'countryside', 'creativejournal', 'journalingasmr', 'vintagejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CvLKyANAQbH/media/?size=m'
    },
    {
      id: 'ig-api-631',
      platform: 'instagram',
      title: 'ASMR Creative Journal 💙 Ocean Vibes 🪸 This is a little sneak peak into the ...',
      description: 'ASMR Creative Journal 💙 Ocean Vibes 🪸 This is a little sneak peak into the products for the July/August Curiosities Box themed around blue colors, beach, sea and marine life! The box is available fo',
      url: 'https://www.instagram.com/reel/CvGBLSIgF0o/',
      embedId: 'CvGBLSIgF0o',
      category: 'box-reveal',
      tags: ['asmrjournaling', 'asmrjournalsupplies', 'oceanlover', 'creativejournal', 'summerspreads', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CvGBLSIgF0o/media/?size=m'
    },
    {
      id: 'ig-api-632',
      platform: 'instagram',
      title: 'Girl Boss Culture Unpopular Opinions 👸',
      description: 'Girl Boss Culture Unpopular Opinions 👸 * As women we often feel guilty putting ourselves first: you may feel like you have to justify the \'sacrifice\' of time and money to start your own business - bu',
      url: 'https://www.instagram.com/reel/CvALkAXgZph/',
      embedId: 'CvALkAXgZph',
      category: 'product-showcase',
      tags: ['unpopularopinions', 'girlboss', 'smallstationerybusiness', 'smallbusiness', 'smallbusinesssupport'],
      creator: 'CoraCreaCrafts',
      date: '2023-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CvALkAXgZph/media/?size=m'
    },
    {
      id: 'ig-api-633',
      platform: 'instagram',
      title: 'ASMR Desk Organizing 😍 Do you like the drawers stacked or unstacked? These v...',
      description: 'ASMR Desk Organizing 😍 Do you like the drawers stacked or unstacked? These vintage style drawer sets are available from 3 to 7 drawers, you can pre-order them in my shop!   #drawerorganization #deskd',
      url: 'https://www.instagram.com/reel/Cu5J-fPgW7W/',
      embedId: 'Cu5J-fPgW7W',
      category: 'product-showcase',
      tags: ['drawerorganization', 'deskdecor', 'desksetup', 'vintagejournaling', 'asmrjournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cu5J-fPgW7W/media/?size=m'
    },
    {
      id: 'ig-api-634',
      platform: 'instagram',
      title: 'Creative Journaling is also a way to Travel 🌍 There\'s no boundaries of time ...',
      description: 'Creative Journaling is also a way to Travel 🌍 There\'s no boundaries of time or space, you can go anywhere and do anything!  These pages were made with the Treasure Box \'Travel Edition\' we only have a',
      url: 'https://www.instagram.com/reel/Cu2sGwGNe7p/',
      embedId: 'Cu2sGwGNe7p',
      category: 'journal-spread',
      tags: ['travelersnotebook', 'traveljournal', 'travelersnotebook', 'timetravel', 'relaxingjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cu2sGwGNe7p/media/?size=m'
    },
    {
      id: 'ig-api-635',
      platform: 'instagram',
      title: 'ASMR Creative Journal - Cottagecore with the English Countryside Vintage Craf...',
      description: 'ASMR Creative Journal - Cottagecore with the English Countryside Vintage Craft Box 🔗 in bio  I used mainly the sticker book and the stamps from that box but also the washi tape in the background!  #a',
      url: 'https://www.instagram.com/reel/CurbTc0A7YC/',
      embedId: 'CurbTc0A7YC',
      category: 'journal-spread',
      tags: ['asmrjournal', 'journalwithme', 'scrapbooking', 'journalspread', 'creativejournal', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CurbTc0A7YC/media/?size=m'
    },
    {
      id: 'ig-api-636',
      platform: 'instagram',
      title: 'Raffle/Giveaway Winner Announcement 🎉 for the 4th of July Sale we put togeth...',
      description: 'Raffle/Giveaway Winner Announcement 🎉 for the 4th of July Sale we put together this huge pack of goodies for one of our customers to win. This is the end of the wait, our winner this time around is L',
      url: 'https://www.instagram.com/reel/CumlNUagXg9/',
      embedId: 'CumlNUagXg9',
      category: 'behind-the-scenes',
      tags: ['stationerygiveaway', 'grabbag', 'packingorders', 'asmrjournaling', 'asmrorders', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CumlNUagXg9/media/?size=m'
    },
    {
      id: 'ig-api-637',
      platform: 'instagram',
      title: 'ASMR Travel Journal 💚🌍 Who\'s ready for some more adventures this summer? I ...',
      description: 'ASMR Travel Journal 💚🌍 Who\'s ready for some more adventures this summer? I love to pre-decorate some of my travel notebook pages for when I\'m out & about. I write at least a few words every day to r',
      url: 'https://www.instagram.com/reel/Cukls1ggrgo/',
      embedId: 'Cukls1ggrgo',
      category: 'box-reveal',
      tags: ['traveling', 'traveljournal', 'travelersnotebook', 'zipperpouch', 'forestgreen', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cukls1ggrgo/media/?size=m'
    },
    {
      id: 'ig-api-638',
      platform: 'instagram',
      title: 'Pack an order with me 📦 This one was for you Tracy G 🥰 Hope you enjoyed see...',
      description: 'Pack an order with me 📦 This one was for you Tracy G 🥰 Hope you enjoyed seeing the process! You can request your order to be filmed in the comments or by email ✉️ This also gives you a little overvi',
      url: 'https://www.instagram.com/reel/CuiIZKfA9sW/',
      embedId: 'CuiIZKfA9sW',
      category: 'behind-the-scenes',
      tags: ['packingorders', 'smallbusinesssupport', 'stationerystore', 'subscriptionbox', 'asmrjournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CuiIZKfA9sW/media/?size=m'
    },
    {
      id: 'ig-api-639',
      platform: 'instagram',
      title: 'ASMR Creative Envelopes 🖤 Just a relaxing few seconds decorating envelopes f...',
      description: 'ASMR Creative Envelopes 🖤 Just a relaxing few seconds decorating envelopes for a little snail mail package 😊   #snailmailbundle #envelopedesign #envelopeart #vellumpaper #minicabinet #stamping #crea',
      url: 'https://www.instagram.com/reel/Cuf3kKYgvC4/',
      embedId: 'Cuf3kKYgvC4',
      category: 'behind-the-scenes',
      tags: ['snailmailbundle', 'envelopedesign', 'envelopeart', 'vellumpaper', 'minicabinet', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cuf3kKYgvC4/media/?size=m'
    },
    {
      id: 'ig-api-640',
      platform: 'instagram',
      title: 'Treasure Box Travel Edition 🌍 I\'m ready for the next adventure with this new...',
      description: 'Treasure Box Travel Edition 🌍 I\'m ready for the next adventure with this new journal & pouch set - and of course now you all know what my favorite color is 😊  Link in bio to watch the full box previ',
      url: 'https://www.instagram.com/reel/CuaoXsEAPm5/',
      embedId: 'CuaoXsEAPm5',
      category: 'box-reveal',
      tags: ['leathernotebook', 'zipperpouch', 'leatherpouch', 'traveljournal', 'journalimgasmr', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CuaoXsEAPm5/media/?size=m'
    },
    {
      id: 'ig-api-641',
      platform: 'instagram',
      title: 'ASMR Creative Journaling 🏴󠁧󠁢󠁥󠁮󠁧󠁿 Which House are you? Entire spread us...',
      description: 'ASMR Creative Journaling 🏴󠁧󠁢󠁥󠁮󠁧󠁿 Which House are you? Entire spread using the July \'English Countryside\' Vintage Craft Box subscription ❤️   #creativejournal #journalimgasmr #asmrjournaling #co',
      url: 'https://www.instagram.com/reel/CuXxtKfAESZ/',
      embedId: 'CuXxtKfAESZ',
      category: 'journal-spread',
      tags: ['creativejournal', 'journalimgasmr', 'asmrjournaling', 'countryside', 'washitape', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CuXxtKfAESZ/media/?size=m'
    },
    {
      id: 'ig-api-642',
      platform: 'instagram',
      title: 'ASMR Preview of the \'English Countryside\' Vintage Craft Box preview for this ...',
      description: 'ASMR Preview of the \'English Countryside\' Vintage Craft Box preview for this month of July 💚 link in my bio 🔗  #asmrjournaling #journalingcommunity #journalimgasmr  #creativejournal #aesthetic #stic',
      url: 'https://www.instagram.com/reel/CuU4Ig9gum4/',
      embedId: 'CuU4Ig9gum4',
      category: 'box-reveal',
      tags: ['asmrjournaling', 'journalingcommunity', 'journalimgasmr', 'creativejournal', 'aesthetic', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CuU4Ig9gum4/media/?size=m'
    },
    {
      id: 'ig-api-643',
      platform: 'instagram',
      title: 'A little preview of what comes in the grab bags! We only have a few left so d...',
      description: 'A little preview of what comes in the grab bags! We only have a few left so don\'t forget to grab yours before they\'re gone ❤️✂️  #grabbags #specialsale #4thofjuly #stationerystore #smallbusinesssuppor',
      url: 'https://www.instagram.com/reel/CuPlAjDAx0Y/',
      embedId: 'CuPlAjDAx0Y',
      category: 'box-reveal',
      tags: ['grabbags', 'specialsale', '4thofjuly', 'stationerystore', 'smallbusinesssupport', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CuPlAjDAx0Y/media/?size=m'
    },
    {
      id: 'ig-api-644',
      platform: 'instagram',
      title: 'Special Sale 🇺🇸✂️ New GRAB BAGS, sticker bundles and washi tape bundles ava...',
      description: 'Special Sale 🇺🇸✂️ New GRAB BAGS, sticker bundles and washi tape bundles available in the shop 🥰 Also up to 25% store items. Link in bio  #stationerystore #stationerylovers #smallbusinesssupport #sa',
      url: 'https://www.instagram.com/reel/CuM3Z76Az6c/',
      embedId: 'CuM3Z76Az6c',
      category: 'product-showcase',
      tags: ['stationerystore', 'stationerylovers', 'smallbusinesssupport', 'sale', 'stationerysale', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CuM3Z76Az6c/media/?size=m'
    },
    {
      id: 'ig-api-645',
      platform: 'instagram',
      title: 'ASMR Creative Journaling 💚 Green Garden Vibes & a lot of satisfying sticker ...',
      description: 'ASMR Creative Journaling 💚 Green Garden Vibes & a lot of satisfying sticker peeling sounds 😄 Stickers used:  * Insects Sticker Sheet  * Specimen Bottles Sticker Sheet * NEW Gold Foil Sticker Book Ot',
      url: 'https://www.instagram.com/reel/CuIgO45A1Wr/',
      embedId: 'CuIgO45A1Wr',
      category: 'product-showcase',
      tags: ['asmrjournaling', 'vintagejournaling', 'gardenjournal', 'journalingcommunity', 'stickerpeel', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CuIgO45A1Wr/media/?size=m'
    },
    {
      id: 'ig-api-646',
      platform: 'instagram',
      title: 'ASMR Creative Journal 💚 Countryside Garden using the new supplies from the E...',
      description: 'ASMR Creative Journal 💚 Countryside Garden using the new supplies from the English Countryside July Box 🤫 Full box preview coming up next week! The box is already available, get it in my website 🔗 ',
      url: 'https://www.instagram.com/reel/CuF69ZQg9_g/',
      embedId: 'CuF69ZQg9_g',
      category: 'box-reveal',
      tags: ['asmrjournaling', 'creativejournal', 'journalimgasmr', 'vintage', 'countryside', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CuF69ZQg9_g/media/?size=m'
    },
    {
      id: 'ig-api-647',
      platform: 'instagram',
      title: 'New Journaling Starter Kits 🤩 Link in my bio 🔗 Fun package of supplies to g...',
      description: 'New Journaling Starter Kits 🤩 Link in my bio 🔗 Fun package of supplies to get started with this hobby. Includes: journal, stickers, papers, PET tape, stamp, tools (✂️, inkpad, stamping block, tweeze',
      url: 'https://www.instagram.com/reel/CuDGxjzgAsD/',
      embedId: 'CuDGxjzgAsD',
      category: 'behind-the-scenes',
      tags: ['journalingstarterkit', 'journalingsupplies', 'asmrjournaling', 'stationerystore', 'creativejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CuDGxjzgAsD/media/?size=m'
    },
    {
      id: 'ig-api-648',
      platform: 'instagram',
      title: 'ASMR Creative Journal - The Astronomer ⭐ This was such a fun spread to create...',
      description: 'ASMR Creative Journal - The Astronomer ⭐ This was such a fun spread to create, I really enjoyed making the frame in 4 different layers and play with the foiling and transparency of the washi tapes 🖤 ',
      url: 'https://www.instagram.com/reel/CuAlPjfAgyf/',
      embedId: 'CuAlPjfAgyf',
      category: 'journal-spread',
      tags: ['creativejournal', 'astronomy', 'foilwashitape', 'asmrjournaling', 'journalingasmr', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CuAlPjfAgyf/media/?size=m'
    },
    {
      id: 'ig-api-649',
      platform: 'instagram',
      title: 'Last Call for the Pirates Box 🏴‍☠️ We are switching to the next theme \'Engli...',
      description: 'Last Call for the Pirates Box 🏴‍☠️ We are switching to the next theme \'English Countryside\' tonight! What\'s in the Pirates Box? Link in bio 🔗 🖤 Pirates Transparent Tape 🖤 Pirates Thin Tape 🖤 2x W',
      url: 'https://www.instagram.com/reel/Ct9S2yAgIl5/',
      embedId: 'Ct9S2yAgIl5',
      category: 'product-showcase',
      tags: ['pirateship', 'journalasmr', 'journalingsupplies', 'subscriptionbox', 'stickerpeel', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Ct9S2yAgIl5/media/?size=m'
    },
    {
      id: 'ig-api-650',
      platform: 'instagram',
      title: 'Meet & Greet Event @atlasstationers in Chicago yesterday 🥰 Had such an amazi...',
      description: 'Meet & Greet Event @atlasstationers in Chicago yesterday 🥰 Had such an amazing time meeting new people from the area and the wonderful Atlas team! Atlas is THE place to go for all stationery lovers i',
      url: 'https://www.instagram.com/reel/Ct659SMg07o/',
      embedId: 'Ct659SMg07o',
      category: 'box-reveal',
      tags: ['stationerystore', 'chicagoevents', 'stationerylovers', 'smallbusiness', 'smallstationerybusiness', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Ct659SMg07o/media/?size=m'
    },
    {
      id: 'ig-api-651',
      platform: 'instagram',
      title: 'ASMR Creative Journal 🥀 No Rose without Thorn 🖤 Loved creating this spread ...',
      description: 'ASMR Creative Journal 🥀 No Rose without Thorn 🖤 Loved creating this spread with an air of fairytale, using the Enchanted Forest Foil washi tape and the Flowers Sticker Pack  #asmrjournaling #creativ',
      url: 'https://www.instagram.com/reel/Ct2jpq8AD44/',
      embedId: 'Ct2jpq8AD44',
      category: 'journal-spread',
      tags: ['asmrjournaling', 'creativejournal', 'vintagejournaling', 'flowerstickers', 'rosethorns', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Ct2jpq8AD44/media/?size=m'
    },
    {
      id: 'ig-api-652',
      platform: 'instagram',
      title: 'Steampunk Creative from products to final pages 🖤 which version is your favo...',
      description: 'Steampunk Creative from products to final pages 🖤 which version is your favorite? Gold Industrial Steampunk or Black Steampunk?  #steampunk #creativejournal #asmrjournaling #journalingcommunity #jour',
      url: 'https://www.instagram.com/reel/Ctz5uKSgFd9/',
      embedId: 'Ctz5uKSgFd9',
      category: 'box-reveal',
      tags: ['steampunk', 'creativejournal', 'asmrjournaling', 'journalingcommunity', 'journalingsupplies', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Ctz5uKSgFd9/media/?size=m'
    },
    {
      id: 'ig-api-653',
      platform: 'instagram',
      title: 'ASMR Creative Journal 💜 First Day of Summer and celebrating the beauty of th...',
      description: 'ASMR Creative Journal 💜 First Day of Summer and celebrating the beauty of this season with a spread full of colors 💛  Enjoy this Summer Solstice everyone!   #asmrjournaling #summerspread #colorfuljo',
      url: 'https://www.instagram.com/reel/CtxN5_atssh/',
      embedId: 'CtxN5_atssh',
      category: 'journal-spread',
      tags: ['asmrjournaling', 'summerspread', 'colorfuljournal', 'creativejournal', 'journalasmr', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CtxN5_atssh/media/?size=m'
    },
    {
      id: 'ig-api-654',
      platform: 'instagram',
      title: 'ASMR Creative Journal 🖤 Mini Journal Cabinet of Curiosities pages 😊 it\'s be...',
      description: 'ASMR Creative Journal 🖤 Mini Journal Cabinet of Curiosities pages 😊 it\'s been a while since I last made a mini spread so this was a lot of fun. I forgot how quickly you run out of space on this smal',
      url: 'https://www.instagram.com/reel/CtuX3X5ghsR/',
      embedId: 'CtuX3X5ghsR',
      category: 'journal-spread',
      tags: ['minijournal', 'cabinetofcuriosities', 'stickerbook', 'stickerpeel', 'journalasmr', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CtuX3X5ghsR/media/?size=m'
    },
    {
      id: 'ig-api-655',
      platform: 'instagram',
      title: 'Creative Journal 🖤 Black & White Steampunk Pages',
      description: 'Creative Journal 🖤 Black & White Steampunk Pages These were so satisfying to make on my last live. I especially liked using the fake photo stickers. I used the supplies from the Curiosities \'Steampun',
      url: 'https://www.instagram.com/reel/CtsAgiPAKCG/',
      embedId: 'CtsAgiPAKCG',
      category: 'journal-spread',
      tags: ['asmrjournaling', 'steampunk', 'creativejournal', 'journalwithme', 'blackandwhitespread', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CtsAgiPAKCG/media/?size=m'
    },
    {
      id: 'ig-api-656',
      platform: 'instagram',
      title: 'Travel Journal Pages 🌍 Pirates theme. Do you ever pre decorate your travel j...',
      description: 'Travel Journal Pages 🌍 Pirates theme. Do you ever pre decorate your travel journal pages before you leave on a trip? This is one of the only journals where I actually write so I\'m always leaving plen',
      url: 'https://www.instagram.com/reel/Ctpa6dXAsZ8/',
      embedId: 'Ctpa6dXAsZ8',
      category: 'journal-spread',
      tags: ['traveljournal', 'pirates', 'asmrjournaling', 'flipthrough', 'journalingcommunity', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Ctpa6dXAsZ8/media/?size=m'
    },
    {
      id: 'ig-api-657',
      platform: 'instagram',
      title: 'ASMR Creative Journal - Cabinet of Curiosities sticker book is now available ...',
      description: 'ASMR Creative Journal - Cabinet of Curiosities sticker book is now available in the shop 🖤  this is a short and satisfying version of the cabinet of curiosities journal spread I made a couple of week',
      url: 'https://www.instagram.com/reel/CtktScAAd4z/',
      embedId: 'CtktScAAd4z',
      category: 'journal-spread',
      tags: ['asmrjournaling', 'asmrstickersounds', 'stickerpeel', 'cabinetofcuriosities', 'minicabinet', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CtktScAAd4z/media/?size=m'
    },
    {
      id: 'ig-api-658',
      platform: 'instagram',
      title: 'ASMR quotes journal 💙 after brown, green and pink, I had to make a blue page...',
      description: 'ASMR quotes journal 💙 after brown, green and pink, I had to make a blue page. This one is dedicated to the power of water 💦 \'Nothing is softer or more flexible than water, yet nothing can resist it\'',
      url: 'https://www.instagram.com/reel/Cthr5xMr2vo/',
      embedId: 'Cthr5xMr2vo',
      category: 'product-showcase',
      tags: ['asmrjournaling', 'vintagejournal', 'calligraphy', 'naturequotes', 'quotejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cthr5xMr2vo/media/?size=m'
    },
    {
      id: 'ig-api-659',
      platform: 'instagram',
      title: 'This box is like a dream come true 🖤 I\'ve been wanting to create a Pirates B...',
      description: 'This box is like a dream come true 🖤 I\'ve been wanting to create a Pirates Box for the past year and I\'m so excited that this has come to realize. I could not have hoped for a better result, I\'m so h',
      url: 'https://www.instagram.com/reel/CtfLgVegaME/',
      embedId: 'CtfLgVegaME',
      category: 'behind-the-scenes',
      tags: ['subscriptionbox', 'fulfillingorders', 'journalingasmr', 'journalingcommunity', 'smallbusinessgoal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CtfLgVegaME/media/?size=m'
    },
    {
      id: 'ig-api-660',
      platform: 'instagram',
      title: 'ASMR organize supplies 🖤 Easy way to organize your journaling supplies! Upcy...',
      description: 'ASMR organize supplies 🖤 Easy way to organize your journaling supplies! Upcycle and decorate old boxes, and organize your items by size and shape. I love pre-stamping and cutting to have some themed ',
      url: 'https://www.instagram.com/reel/CtcmIIEMHCg/',
      embedId: 'CtcmIIEMHCg',
      category: 'tutorial',
      tags: ['deskorganization', 'journalingasmr', 'journalsupplies', 'diycrafts', 'boxdecoration', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CtcmIIEMHCg/media/?size=m'
    },
    {
      id: 'ig-api-661',
      platform: 'instagram',
      title: 'ASMR Faux Vintage Photo Album in my creative journal 🖼️ I used some vintage ...',
      description: 'ASMR Faux Vintage Photo Album in my creative journal 🖼️ I used some vintage postcards of paintings together with the Film Overlay Stickers & Background Stickers from the Steampunk Curiosities Box 🖤 ',
      url: 'https://www.instagram.com/reel/CtZN1aIAU4B/',
      embedId: 'CtZN1aIAU4B',
      category: 'journal-spread',
      tags: ['asmrjournal', 'stickerpeel', 'photojournal', 'photoalbum', 'journalspread', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CtZN1aIAU4B/media/?size=m'
    },
    {
      id: 'ig-api-662',
      platform: 'instagram',
      title: 'ASMR Creative Journal 🖤🏴‍☠️ Making some Pirate Tag with the Pirates Theme V...',
      description: 'ASMR Creative Journal 🖤🏴‍☠️ Making some Pirate Tag with the Pirates Theme Vintage Craft Box 📦 This is a super simple and fun project for gifting, or just to use in your creative journal!   #asmrjou',
      url: 'https://www.instagram.com/reel/CtUrSJfgfoN/',
      embedId: 'CtUrSJfgfoN',
      category: 'journal-spread',
      tags: ['asmrjournal', 'scrapbooking', 'creativejournal', 'stickerpeel', 'vintage', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CtUrSJfgfoN/media/?size=m'
    },
    {
      id: 'ig-api-663',
      platform: 'instagram',
      title: 'Cabinet of Curiosities Pre-Orders 🖤📦 Due to high demand after we sold out t...',
      description: 'Cabinet of Curiosities Pre-Orders 🖤📦 Due to high demand after we sold out the last Treasure Boxes, we wanted to bring this cute product back for those who did not get a chance to grab one in time or',
      url: 'https://www.instagram.com/reel/CtSY5dDg-n-/',
      embedId: 'CtSY5dDg-n-',
      category: 'product-showcase',
      tags: ['minicabinet', 'cabinetofcuriosities', 'deskdecor', 'deskorganization', 'journalingcommunity', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CtSY5dDg-n-/media/?size=m'
    },
    {
      id: 'ig-api-664',
      platform: 'instagram',
      title: 'Creative Pirates Upcycled Box 🖤 Reuse any box you have and make it unique wi...',
      description: 'Creative Pirates Upcycled Box 🖤 Reuse any box you have and make it unique with stickers, stamps and paper supplies 📦 For this project I used products from the June Vintage Craft Box on the theme Pir',
      url: 'https://www.instagram.com/reel/CtNDSnng_C3/',
      embedId: 'CtNDSnng_C3',
      category: 'box-reveal',
      tags: ['pirates', 'creativejournaling', 'boxdecoration', 'upcycledbox', 'upcycledart', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CtNDSnng_C3/media/?size=m'
    },
    {
      id: 'ig-api-665',
      platform: 'instagram',
      title: 'Curiosities Box Preview 🖤 Steampunk inspired box for the third edition! Soft...',
      description: 'Curiosities Box Preview 🖤 Steampunk inspired box for the third edition! Soft industrial vibes, black & white, gears, patterns and fun victorian steampunk designs to play with 📦 Available now in my s',
      url: 'https://www.instagram.com/reel/CtJ0O_bApDg/',
      embedId: 'CtJ0O_bApDg',
      category: 'box-reveal',
      tags: ['steampunkbox', 'journalingsubscription', 'asmrjournaling', 'vintagejournaling', 'asmrstationery', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CtJ0O_bApDg/media/?size=m'
    },
    {
      id: 'ig-api-666',
      platform: 'instagram',
      title: 'Pros & Cons of having a small business 🙈 I\'m sure a lot of those who have a ...',
      description: 'Pros & Cons of having a small business 🙈 I\'m sure a lot of those who have a small business or side hussle can relate to this, and for those of you getting started or considering it, these are a few t',
      url: 'https://www.instagram.com/reel/CtIA_juAsbn/',
      embedId: 'CtIA_juAsbn',
      category: 'behind-the-scenes',
      tags: ['smallbusinessowner', 'packingordersasmr', 'prosandcons'],
      creator: 'CoraCreaCrafts',
      date: '2023-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CtIA_juAsbn/media/?size=m'
    },
    {
      id: 'ig-api-667',
      platform: 'instagram',
      title: 'Creative Journal Specimen Card Part 2 💚 After the blue side on the right, I ...',
      description: 'Creative Journal Specimen Card Part 2 💚 After the blue side on the right, I decided this journal spread would not be complete without it\'s green/yellow counterpart. Making these cards is not difficul',
      url: 'https://www.instagram.com/reel/CtDB7I8g5vB/',
      embedId: 'CtDB7I8g5vB',
      category: 'journal-spread',
      tags: ['summercrafts', 'specimencards', 'journalingcommunity', 'insectlovers', 'vintagejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CtDB7I8g5vB/media/?size=m'
    },
    {
      id: 'ig-api-668',
      platform: 'instagram',
      title: 'Spoiler Alert Matey❗June \'Pirates\' Box Previews us here 📦 Embark in a thrill...',
      description: 'Spoiler Alert Matey❗June \'Pirates\' Box Previews us here 📦 Embark in a thrilling journey that will transport you to the Golden Age of Pirates! Come aboard and let the pirate within you ser sail on an ',
      url: 'https://www.instagram.com/reel/CtATg2NgfXP/',
      embedId: 'CtATg2NgfXP',
      category: 'box-reveal',
      tags: ['pirateship', 'subscriptionbox', 'asmrjournalsupplies', 'journaling', 'advent calendar', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2023-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CtATg2NgfXP/media/?size=m'
    },
    {
      id: 'ig-api-669',
      platform: 'instagram',
      title: 'Packing the largest order we received this year 🎉🙏 This order included a to...',
      description: 'Packing the largest order we received this year 🎉🙏 This order included a ton of supplies and boxes worth $900! This is all for just ONE person who decided to treat themselves big time 🤗  #packingor',
      url: 'https://www.instagram.com/reel/Cs9zueNAVDu/',
      embedId: 'Cs9zueNAVDu',
      category: 'behind-the-scenes',
      tags: ['packingordersasmr', 'smallsuccesses', 'journalingcommunity', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cs9zueNAVDu/media/?size=m'
    },
    {
      id: 'ig-api-670',
      platform: 'instagram',
      title: 'ASMR Pirates Journal Pages 🏴‍☠️ Creative Journaling with the new June Vintag...',
      description: 'ASMR Pirates Journal Pages 🏴‍☠️ Creative Journaling with the new June Vintage Craft Box supplies This is a first sneak peak of some of the products you\'ll be getting in the subscription box this mont',
      url: 'https://www.instagram.com/reel/Cs6vkk1gUgp/',
      embedId: 'Cs6vkk1gUgp',
      category: 'box-reveal',
      tags: ['pirateship', 'pirates', 'creativejournaling', 'asmrjournaling', 'journalingasmr', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cs6vkk1gUgp/media/?size=m'
    },
    {
      id: 'ig-api-671',
      platform: 'instagram',
      title: 'Creative Journal Pop Up Pages 🖤 Cabinet of Curiosities Edition',
      description: 'Creative Journal Pop Up Pages 🖤 Cabinet of Curiosities Edition  I\'m blown away by everyone\'s support and love for this Treasure Box Cabinet of Curiosities Edition, we are almost sold out 🥲 We have l',
      url: 'https://www.instagram.com/reel/Cs4k3vwJbBY/',
      embedId: 'Cs4k3vwJbBY',
      category: 'box-reveal',
      tags: ['cabinetofcuriosities', 'deskdecor', 'stickerbook', 'darkjournal', 'grimoire', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cs4k3vwJbBY/media/?size=m'
    },
    {
      id: 'ig-api-672',
      platform: 'instagram',
      title: 'Grab Bags are almost gone! Grab yours before it\'s too late. Our Memorial Day ...',
      description: 'Grab Bags are almost gone! Grab yours before it\'s too late. Our Memorial Day Sale is ending tomorrow night 💙 #stationeryshop #grabbag #stationerygrabbag #journalingasmr #asmrjournalsupplies #packingo',
      url: 'https://www.instagram.com/reel/Cs2YFe0AfEB/',
      embedId: 'Cs2YFe0AfEB',
      category: 'behind-the-scenes',
      tags: ['stationeryshop', 'grabbag', 'stationerygrabbag', 'journalingasmr', 'asmrjournalsupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cs2YFe0AfEB/media/?size=m'
    },
    {
      id: 'ig-api-673',
      platform: 'instagram',
      title: 'Memorial Day sale is here! We have some really exciting offers available unti...',
      description: 'Memorial Day sale is here! We have some really exciting offers available until Tuesday and that includes some special grab bags, mystery wax seal packs and up to 20% off our washi tapes, stamps and pa',
      url: 'https://www.instagram.com/reel/Csyd0NbAsDo/',
      embedId: 'Csyd0NbAsDo',
      category: 'behind-the-scenes',
      tags: ['memorialdaysale', 'stationeryshop', 'asmrjournaling', 'journalingsupplies', 'grabbagsale', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Csyd0NbAsDo/media/?size=m'
    },
    {
      id: 'ig-api-674',
      platform: 'instagram',
      title: 'ASMR Vintage Journal Pages 🖤 Cabinet of Curiosities',
      description: 'ASMR Vintage Journal Pages 🖤 Cabinet of Curiosities  Just love the mysterious vibes these new products give me! All the products used are from the Treasure Box (Cabinet of Curiosities theme) aside fr',
      url: 'https://www.instagram.com/reel/CstmuWygIuv/',
      embedId: 'CstmuWygIuv',
      category: 'journal-spread',
      tags: ['cabinetofcuriosities', 'deskdecor', 'stickerbook', 'stickerpeel', 'asmrjournaling', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CstmuWygIuv/media/?size=m'
    },
    {
      id: 'ig-api-675',
      platform: 'instagram',
      title: 'Announcing raffle winners 🏆 DM us if you have one of the winning numbers! We...',
      description: 'Announcing raffle winners 🏆 DM us if you have one of the winning numbers! We really enjoyed making these 10 special prizes to reward our customers and to have something fun for Mother\'s Day ❤️ #raffl',
      url: 'https://www.instagram.com/reel/Csrm5ftAzwz/',
      embedId: 'Csrm5ftAzwz',
      category: 'product-showcase',
      tags: ['raffletickets', 'specialsale', 'stationerygiveaway', 'asmrjournaling', 'journalingsupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Csrm5ftAzwz/media/?size=m'
    },
    {
      id: 'ig-api-676',
      platform: 'instagram',
      title: 'What can you fit in the Cabinet of Curiosities? 🖤 Well a whole lot more than...',
      description: 'What can you fit in the Cabinet of Curiosities? 🖤 Well a whole lot more than it looks like! I used mine for my mini journals (5), ink bottles (2), wooden stamps (2), brass clips, washi tapes (5), met',
      url: 'https://www.instagram.com/reel/CspjxoFAq3a/',
      embedId: 'CspjxoFAq3a',
      category: 'product-showcase',
      tags: ['minicabinet', 'cabinetofcuriosities', 'deskdecor', 'deskorganization', 'journalingcommunity', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CspjxoFAq3a/media/?size=m'
    },
    {
      id: 'ig-api-677',
      platform: 'instagram',
      title: 'Creative Journal ASMR Pop Up Pages 💚🩷 A green and pink floral combination f...',
      description: 'Creative Journal ASMR Pop Up Pages 💚🩷 A green and pink floral combination for Spring! I used the Floral Basics Washi Tapes, Botanist Stamps and Floral Nostalgia Transparent Tape, all part of the new',
      url: 'https://www.instagram.com/reel/CslxrDugtRX/',
      embedId: 'CslxrDugtRX',
      category: 'product-showcase',
      tags: ['creativejournaling', 'popupjournal', 'popupart', 'asmrjournaling', 'springjournal', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CslxrDugtRX/media/?size=m'
    },
    {
      id: 'ig-api-678',
      platform: 'instagram',
      title: 'Cabinet of Curiosities ASMR Subscription Box Highlights 🖤 Here are some of m...',
      description: 'Cabinet of Curiosities ASMR Subscription Box Highlights 🖤 Here are some of my favorite items that are part of this Treasure Box 📦 Link in bio to get yours 🔗 Also this is the first time I add my own',
      url: 'https://www.instagram.com/reel/CsjDplsAmzE/',
      embedId: 'CsjDplsAmzE',
      category: 'box-reveal',
      tags: ['cabinetofcuriosities', 'stickerbook', 'journalingsubscription', 'deskdecor', 'minicabinet', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CsjDplsAmzE/media/?size=m'
    },
    {
      id: 'ig-api-679',
      platform: 'instagram',
      title: 'Creative Journal Specimen Pages insect🪲 This project is finally finished! Wh...',
      description: 'Creative Journal Specimen Pages insect🪲 This project is finally finished! Which side is your favorite? Green or Blue? I used the Insect Paper Cards that I fussy cut along with some rice papers for th',
      url: 'https://www.instagram.com/reel/CsemCShA7up/',
      embedId: 'CsemCShA7up',
      category: 'product-showcase',
      tags: ['specimencards', 'specimen', 'vintagejournal', 'finishedproject', 'insectjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CsemCShA7up/media/?size=m'
    },
    {
      id: 'ig-api-680',
      platform: 'instagram',
      title: 'ASMR Accordion Journal Quote Page n°5 🖋️',
      description: 'ASMR Accordion Journal Quote Page n°5 🖋️  I love making these color themed nature frames on these pages and add my favorite quotes in the middle 🥰 #naturequotes #asmrjournaling #calligrafriends #cre',
      url: 'https://www.instagram.com/reel/CscdRhxApTh/',
      embedId: 'CscdRhxApTh',
      category: 'product-showcase',
      tags: ['naturequotes', 'asmrjournaling', 'calligrafriends', 'creativejournal', 'relaxingsounds', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CscdRhxApTh/media/?size=m'
    },
    {
      id: 'ig-api-681',
      platform: 'instagram',
      title: 'The new wooden Cabinets of Curiosities arrived 😱 I\'m so excited and relieved...',
      description: 'The new wooden Cabinets of Curiosities arrived 😱 I\'m so excited and relieved to see these finally come in after such a long time designing, planning, finalizing and dealing with shipping and customs ',
      url: 'https://www.instagram.com/reel/CsZgE9yAJ2A/',
      embedId: 'CsZgE9yAJ2A',
      category: 'product-showcase',
      tags: ['washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CsZgE9yAJ2A/media/?size=m'
    },
    {
      id: 'ig-api-682',
      platform: 'instagram',
      title: 'Creative Journal 💛 Butterflies & Bees Yellow Spread - did you notice the lit...',
      description: 'Creative Journal 💛 Butterflies & Bees Yellow Spread - did you notice the little pop up bees in the middle?  Don\'t forget to participate in this month\'s challenge #cccinsectschallenge with the May Vin',
      url: 'https://www.instagram.com/reel/CsXXMPLgTAn/',
      embedId: 'CsXXMPLgTAn',
      category: 'journal-spread',
      tags: ['cccinsectschallenge', 'vintagejournal', 'creativejournal', 'popupjournal', 'popupart', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CsXXMPLgTAn/media/?size=m'
    },
    {
      id: 'ig-api-683',
      platform: 'instagram',
      title: 'Simple Butterfly Tag 🦋 these are some of the simplest tags I\'ve ever made:',
      description: 'Simple Butterfly Tag 🦋 these are some of the simplest tags I\'ve ever made:  * start with pieces of cardstock, they can be of different sizes and shapes, I used some left over from another project * U',
      url: 'https://www.instagram.com/reel/CsUTV24AS36/',
      embedId: 'CsUTV24AS36',
      category: 'tutorial',
      tags: ['diytags', 'butterflytags', 'butterfliescrafts', 'craftingideas', 'stamping', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CsUTV24AS36/media/?size=m'
    },
    {
      id: 'ig-api-684',
      platform: 'instagram',
      title: 'Creative Journal - Make a Specimen Card in a few easy steps 🦋 I decided to g...',
      description: 'Creative Journal - Make a Specimen Card in a few easy steps 🦋 I decided to go for a blue romantic theme for this card. Join me tonight live to complete the second part of this spread, I\'m thinking gr',
      url: 'https://www.instagram.com/reel/CsRphkwgOO7/',
      embedId: 'CsRphkwgOO7',
      category: 'journal-spread',
      tags: ['journalwithme', 'journaltutorial', 'specimencard', 'journalspread', 'creativejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CsRphkwgOO7/media/?size=m'
    },
    {
      id: 'ig-api-685',
      platform: 'instagram',
      title: 'Last call for out Buy 2 Get 1 Free Stickers and our special Mother\'s Day Sale...',
      description: 'Last call for out Buy 2 Get 1 Free Stickers and our special Mother\'s Day Sale 💐 It ends tonight at midnight CST. Also, we added this beautiful sets of drawers to the website! They can be pre-ordered ',
      url: 'https://www.instagram.com/reel/CsPLslOguUB/',
      embedId: 'CsPLslOguUB',
      category: 'product-showcase',
      tags: ['deskdecoration', 'vintagedeskorganizer', 'vintagecabinet', 'vintagedrawers', 'asmrjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CsPLslOguUB/media/?size=m'
    },
    {
      id: 'ig-api-686',
      platform: 'instagram',
      title: 'Celebrating all Mothers this weekend 🎉 whether you are a mom, daughter, fur ...',
      description: 'Celebrating all Mothers this weekend 🎉 whether you are a mom, daughter, fur mom or just feel like you deserve a treat, this special weekend sale is for you 😍  Here are the exciting things going on: ',
      url: 'https://www.instagram.com/reel/CsMdQcUg_ls/',
      embedId: 'CsMdQcUg_ls',
      category: 'behind-the-scenes',
      tags: ['stationeryshop', 'asmrpacking', 'asmrjournal', 'vintagejournal', 'sale', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CsMdQcUg_ls/media/?size=m'
    },
    {
      id: 'ig-api-687',
      platform: 'instagram',
      title: 'Packing an order for Nicole 📦🤗',
      description: 'Packing an order for Nicole 📦🤗  She ordered a Folklore & Witchcraft Box (Throwback), the Enchanted Forest Sticker Book, and a few washi tapes and papers 🙏 Thank you Nicole for choosing our statione',
      url: 'https://www.instagram.com/reel/CsKgqlaAeZD/',
      embedId: 'CsKgqlaAeZD',
      category: 'behind-the-scenes',
      tags: ['asmrorderpacking', 'journalasmr', 'asmrpapersounds', 'packingorders', 'deskdecor', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CsKgqlaAeZD/media/?size=m'
    },
    {
      id: 'ig-api-688',
      platform: 'instagram',
      title: 'ASMR Creative Nature Quotes Journal 🌲',
      description: 'ASMR Creative Nature Quotes Journal 🌲  This special journal opens like an accordion and I am dedicating each page to a color and theme within nature that inspires me. My goal is to have the pages tel',
      url: 'https://www.instagram.com/reel/CsHp0DAAIUs/',
      embedId: 'CsHp0DAAIUs',
      category: 'journal-spread',
      tags: ['asmrjournal', 'journalwithme', 'scrapbooking', 'journalspread', 'naturequotes', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CsHp0DAAIUs/media/?size=m'
    },
    {
      id: 'ig-api-689',
      platform: 'instagram',
      title: 'Asking our team what their favorite product is 😮 tell us in the comments if ...',
      description: 'Asking our team what their favorite product is 😮 tell us in the comments if you have a favorite too? We had so much fun making this video and especially involving my husband for the last part 🤣 she ',
      url: 'https://www.instagram.com/reel/CsE9jxNANnD/',
      embedId: 'CsE9jxNANnD',
      category: 'product-showcase',
      tags: ['favoriteproduct', 'behindthescene', 'smallbusiness', 'stationeryshop', 'stationeryproducts', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CsE9jxNANnD/media/?size=m'
    },
    {
      id: 'ig-api-690',
      platform: 'instagram',
      title: 'DIY Pocket with the Bees & Insects stamps 🐝',
      description: 'DIY Pocket with the Bees & Insects stamps 🐝  #diycrafts #diypocket #stamping #bees #insects #butterflies #folder #cardstockcrafts #paperartist #craftideas #paperart #cccinsectschallenge',
      url: 'https://www.instagram.com/reel/Cr_GrBMgrjN/',
      embedId: 'Cr_GrBMgrjN',
      category: 'tutorial',
      tags: ['diycrafts', 'diypocket', 'stamping', 'bees', 'insects'],
      creator: 'CoraCreaCrafts',
      date: '2023-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cr_GrBMgrjN/media/?size=m'
    },
    {
      id: 'ig-api-691',
      platform: 'instagram',
      title: 'Receiving 22 packages when you\'re running a small business from home 🏡😅 Def...',
      description: 'Receiving 22 packages when you\'re running a small business from home 🏡😅 Definitely getting a workout with every new shipment coming in!   The May boxes are available on my website now 🐝  #smallbusi',
      url: 'https://www.instagram.com/reel/Cr6necnAS1y/',
      embedId: 'Cr6necnAS1y',
      category: 'behind-the-scenes',
      tags: ['smallbusinessowner', 'shopsmall', 'stationeryshop', 'homebusiness', 'subscriptionboxes', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cr6necnAS1y/media/?size=m'
    },
    {
      id: 'ig-api-692',
      platform: 'instagram',
      title: 'Bees pages 🐝📄 Did you know that bees & other insects play a vital role in o...',
      description: 'Bees pages 🐝📄 Did you know that bees & other insects play a vital role in our ecosystem? They pollinate many of the plants that we rely on for food and they also maintain biodiversity 🌼 All supplie',
      url: 'https://www.instagram.com/reel/Cr3S4WtgAgV/',
      embedId: 'Cr3S4WtgAgV',
      category: 'journal-spread',
      tags: ['bees', 'insects', 'asmrjournaling', 'journalingcommunity', 'vintagejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cr3S4WtgAgV/media/?size=m'
    },
    {
      id: 'ig-api-693',
      platform: 'instagram',
      title: 'The wooden drawers finally arrived and we were able to complete this order fo...',
      description: 'The wooden drawers finally arrived and we were able to complete this order for @cam341255 📦😍 #orderpackingreels #orderpacking #packwithme #smallbusiness #woodendrawers #deskdecor #stationerystore #v',
      url: 'https://www.instagram.com/reel/Cr1FXzugBxc/',
      embedId: 'Cr1FXzugBxc',
      category: 'behind-the-scenes',
      tags: ['orderpackingreels', 'orderpacking', 'packwithme', 'smallbusiness', 'woodendrawers', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2023-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cr1FXzugBxc/media/?size=m'
    },
    {
      id: 'ig-api-694',
      platform: 'instagram',
      title: 'Spoiler Alert ❗May \'Bees & Insects\' Box Preview is here 📦 This box includes ...',
      description: 'Spoiler Alert ❗May \'Bees & Insects\' Box Preview is here 📦 This box includes clear stamps, a PET tape, a set of scrapbooking cards, a sticker roll, 2 wooden stamps and 2 sticker sheets 🤎💛 available ',
      url: 'https://www.instagram.com/reel/Crx__UHgErH/',
      embedId: 'Crx__UHgErH',
      category: 'box-reveal',
      tags: ['journalingsubscription', 'journalingasmr', 'stationerysubscription', 'beesofinstagram', 'insectsworld', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Crx__UHgErH/media/?size=m'
    },
    {
      id: 'ig-api-695',
      platform: 'instagram',
      title: 'Sometimes it\'s just the little things 🤗😍',
      description: 'Sometimes it\'s just the little things 🤗😍  #motivation #smallbusinessquote #findhappinesswithin #stationerylover #washitapeaddict #journalinspiration',
      url: 'https://www.instagram.com/reel/Crt01GPATfk/',
      embedId: 'Crt01GPATfk',
      category: 'product-showcase',
      tags: ['motivation', 'smallbusinessquote', 'findhappinesswithin', 'stationerylover', 'washitapeaddict', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Crt01GPATfk/media/?size=m'
    },
    {
      id: 'ig-api-696',
      platform: 'instagram',
      title: 'Vintage Gardens Foam Stamps now available in the shop 🎍 I used them for a ve...',
      description: 'Vintage Gardens Foam Stamps now available in the shop 🎍 I used them for a very simple handmade gift wrapping 🤎  #giftwrapping #creativereels #journalspreadinspo #journalstamps #foamstamps #artjourna',
      url: 'https://www.instagram.com/reel/CrqbAG_A_rf/',
      embedId: 'CrqbAG_A_rf',
      category: 'journal-spread',
      tags: ['giftwrapping', 'creativereels', 'journalspreadinspo', 'journalstamps', 'foamstamps', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CrqbAG_A_rf/media/?size=m'
    },
    {
      id: 'ig-api-697',
      platform: 'instagram',
      title: 'Happy Desk Moments 🖋️📖',
      description: 'Happy Desk Moments 🖋️📖 #deskgoals #stationerylover #deskorganization #woodenstamps #coracreacrafts #igtutorials #reelsinsta #craftingcommunity #happycrafter',
      url: 'https://www.instagram.com/reel/Crn2CHoAu2l/',
      embedId: 'Crn2CHoAu2l',
      category: 'tutorial',
      tags: ['deskgoals', 'stationerylover', 'deskorganization', 'woodenstamps', 'coracreacrafts'],
      creator: 'CoraCreaCrafts',
      date: '2023-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Crn2CHoAu2l/media/?size=m'
    },
    {
      id: 'ig-api-698',
      platform: 'instagram',
      title: 'First pages in the new accordion journal 🪗🖋️ these journals are available i...',
      description: 'First pages in the new accordion journal 🪗🖋️ these journals are available in 2 sizes in my shop 🥰 #accordionbook #accordionjournal #naturejournal #naturequotes #coracreacrafts #asmrjournaling #rela',
      url: 'https://www.instagram.com/reel/Cri0LG7gLyO/',
      embedId: 'Cri0LG7gLyO',
      category: 'journal-spread',
      tags: ['accordionbook', 'accordionjournal', 'naturejournal', 'naturequotes', 'coracreacrafts', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cri0LG7gLyO/media/?size=m'
    },
    {
      id: 'ig-api-699',
      platform: 'instagram',
      title: 'Specialty Paper Packs are finally here ☺️❤️',
      description: 'Specialty Paper Packs are finally here ☺️❤️ #papercrafts #paperaddicts #asmrpaper #packingdesign #coracreacrafts #specialtypaper #smallbusinesslove',
      url: 'https://www.instagram.com/reel/CrgO2d8gvVr/',
      embedId: 'CrgO2d8gvVr',
      category: 'behind-the-scenes',
      tags: ['papercrafts', 'paperaddicts', 'asmrpaper', 'packingdesign', 'coracreacrafts'],
      creator: 'CoraCreaCrafts',
      date: '2023-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CrgO2d8gvVr/media/?size=m'
    },
    {
      id: 'ig-api-700',
      platform: 'instagram',
      title: '5min Gift Wrapping 🎁 Here\'s how to make a unique and easy handmade gift wrap...',
      description: '5min Gift Wrapping 🎁 Here\'s how to make a unique and easy handmade gift wrap! 😊 You can get free gift wrapping on your orders until Mother\'s Day by using the code MOTHER on my website 🎀 #handmadegi',
      url: 'https://www.instagram.com/reel/CrbB19CgCqw/',
      embedId: 'CrbB19CgCqw',
      category: 'tutorial',
      tags: ['handmadegiftwrapping', 'giftwrapping', 'coracreacrafts', 'washitapeart', 'wrappingtutorial', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CrbB19CgCqw/media/?size=m'
    },
    {
      id: 'ig-api-701',
      platform: 'instagram',
      title: 'Compilation of the best ASMR sounds from the last few months 😍       #ａｅｓｔｈｅｔｉｃ',
      description: 'Compilation of the best ASMR sounds from the last few months 😍 #asmrjournal #scrapbooking #papersounds #stickerpeel #asmrsounds #vintage #ａｅｓｔｈｅｔｉｃ #coracreacrafts',
      url: 'https://www.instagram.com/reel/CrUQBsKgjmV/',
      embedId: 'CrUQBsKgjmV',
      category: 'product-showcase',
      tags: ['asmrjournal', 'scrapbooking', 'papersounds', 'stickerpeel', 'asmrsounds', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CrUQBsKgjmV/media/?size=m'
    },
    {
      id: 'ig-api-702',
      platform: 'instagram',
      title: '🌸 GIVEAWAY CLOSED 🔐',
      description: '🌸 GIVEAWAY CLOSED 🔐  @chloe_grepper congratulations 👏 ✨ Collaboration Giveaway hosted by @coracreacrafts and @journal.w.Amber ✨ There will be 1 winner who will receive the prizes below: - 5 Texture',
      url: 'https://www.instagram.com/reel/CrQ91dfg56G/',
      embedId: 'CrQ91dfg56G',
      category: 'tutorial',
      tags: ['stationerygiveaway', 'handmadejournal', 'outgoinghappymail', 'packanorderwithme', 'orderpacking', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CrQ91dfg56G/media/?size=m'
    },
    {
      id: 'ig-api-703',
      platform: 'instagram',
      title: 'Curiosities Box Botanical Pages 😍 Box is still available in the shop ❤️',
      description: 'Curiosities Box Botanical Pages 😍 Box is still available in the shop ❤️  #creativejournaling #journal #relaxing #journalcommunity #coracreacrafts',
      url: 'https://www.instagram.com/reel/CrLjc0LgTiN/',
      embedId: 'CrLjc0LgTiN',
      category: 'box-reveal',
      tags: ['creativejournaling', 'journal', 'relaxing', 'journalcommunity', 'coracreacrafts', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CrLjc0LgTiN/media/?size=m'
    },
    {
      id: 'ig-api-704',
      platform: 'instagram',
      title: 'Packing order 37102 📦😍',
      description: 'Packing order 37102 📦😍  #packingorders #smallbusiness #asmrpacking #packanorderwithme #coracreacrafts',
      url: 'https://www.instagram.com/reel/CrBYWqQA7E6/',
      embedId: 'CrBYWqQA7E6',
      category: 'behind-the-scenes',
      tags: ['packingorders', 'smallbusiness', 'asmrpacking', 'packanorderwithme', 'coracreacrafts'],
      creator: 'CoraCreaCrafts',
      date: '2023-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CrBYWqQA7E6/media/?size=m'
    },
    {
      id: 'ig-api-705',
      platform: 'instagram',
      title: 'Witchy Cards with transparent sleeves ✂️ use them as decorative pieces, to gi...',
      description: 'Witchy Cards with transparent sleeves ✂️ use them as decorative pieces, to gift with a message in the back or to store stickers and ephemera! All the elements used to make these are part of the curren',
      url: 'https://www.instagram.com/reel/Cq-qw-HA8h1/',
      embedId: 'Cq-qw-HA8h1',
      category: 'box-reveal',
      tags: ['cccwitchycallenge', 'journalingsubscription', 'craftingasmr', 'witchystickers', 'coracreacrafts', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cq-qw-HA8h1/media/?size=m'
    },
    {
      id: 'ig-api-706',
      platform: 'instagram',
      title: 'Washi Tape Love ❤️ Reorganizing my washi tape display 😍 Share your love for ...',
      description: 'Washi Tape Love ❤️ Reorganizing my washi tape display 😍 Share your love for stationery too by clicking on \'Afd yours\'  #journalingcommunity #journalasmr #journalingaddict #desksetup #deskgoals #desko',
      url: 'https://www.instagram.com/reel/Cq8G6MmAImt/',
      embedId: 'Cq8G6MmAImt',
      category: 'product-showcase',
      tags: ['journalingcommunity', 'journalasmr', 'journalingaddict', 'desksetup', 'deskgoals', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cq8G6MmAImt/media/?size=m'
    },
    {
      id: 'ig-api-707',
      platform: 'instagram',
      title: 'Nature Spread with the  🪷🌸 We still have a few left available 📦🥰 link in bio',
      description: 'Nature Spread with the #curiositiesbox 🪷🌸 We still have a few left available 📦🥰 link in bio #naturetheme #journalingshop #journals #leatherjournal #curiosities #washitapes #coracreacrafts #flowers',
      url: 'https://www.instagram.com/reel/Cq6CE8_gnU7/',
      embedId: 'Cq6CE8_gnU7',
      category: 'journal-spread',
      tags: ['curiositiesbox', 'naturetheme', 'journalingshop', 'journals', 'leatherjournal', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cq6CE8_gnU7/media/?size=m'
    },
    {
      id: 'ig-api-708',
      platform: 'instagram',
      title: 'Pack a wholesale order with us 😍📦 We\'re so excited to start this partnershi...',
      description: 'Pack a wholesale order with us 😍📦 We\'re so excited to start this partnership with @atlasstationers. Do you want to see what it looks like in the store? #wholesale #packanorderwithme #order #coracrea',
      url: 'https://www.instagram.com/reel/CqyqOR1okV0/',
      embedId: 'CqyqOR1okV0',
      category: 'behind-the-scenes',
      tags: ['wholesale', 'packanorderwithme', 'order', 'coracreacrafts', 'packaging', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CqyqOR1okV0/media/?size=m'
    },
    {
      id: 'ig-api-709',
      platform: 'instagram',
      title: 'Curiosities Box Preview 💗 Nature inspired box for the second edition of this...',
      description: 'Curiosities Box Preview 💗 Nature inspired box for the second edition of this year!  #curiositiesbox #subscriptionbox #craftysubscription #journalingsubscription #pettape #washitapelove #botanicalart ',
      url: 'https://www.instagram.com/reel/CqswprCgLIw/',
      embedId: 'CqswprCgLIw',
      category: 'box-reveal',
      tags: ['curiositiesbox', 'subscriptionbox', 'craftysubscription', 'journalingsubscription', 'pettape', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CqswprCgLIw/media/?size=m'
    },
    {
      id: 'ig-api-710',
      platform: 'instagram',
      title: 'Comment your order # to see your order packed in a video! 📦❤️ April Fools Sa...',
      description: 'Comment your order # to see your order packed in a video! 📦❤️ April Fools Sale orders going out 🤗 #packanorderwithme #packingorders #orderpacking #smallbusiness #smallbusinesssale #saleorders #stati',
      url: 'https://www.instagram.com/reel/CqqUHQUAsYo/',
      embedId: 'CqqUHQUAsYo',
      category: 'behind-the-scenes',
      tags: ['packanorderwithme', 'packingorders', 'orderpacking', 'smallbusiness', 'smallbusinesssale', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CqqUHQUAsYo/media/?size=m'
    },
    {
      id: 'ig-api-711',
      platform: 'instagram',
      title: 'Spoiler Alert ❗April \'Folklore & Witchcraft\' Box Preview is here 📦  This box...',
      description: 'Spoiler Alert ❗April \'Folklore & Witchcraft\' Box Preview is here 📦  This box includes 3 washi tapes, 2 wooden stamps, a box of 55 stickers, a sticker sheet and 3 background cards ❤️ available in my s',
      url: 'https://www.instagram.com/reel/CqnhJ13ABmW/',
      embedId: 'CqnhJ13ABmW',
      category: 'box-reveal',
      tags: ['vintagecraftbox', 'subscriptionbox', 'craftysubscription', 'journalingsubscription', 'magic', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CqnhJ13ABmW/media/?size=m'
    },
    {
      id: 'ig-api-712',
      platform: 'instagram',
      title: 'Raffle Giveaway Winner 🏆 Watch till the end to see if you won 🎉🥰 Thank you...',
      description: 'Raffle Giveaway Winner 🏆 Watch till the end to see if you won 🎉🥰 Thank you everyone for participating in the raffle and placing your orders for the April Fools Sale this weekend ❤️  #luckywinner #g',
      url: 'https://www.instagram.com/reel/Cqlb-x2Jqur/',
      embedId: 'Cqlb-x2Jqur',
      category: 'behind-the-scenes',
      tags: ['luckywinner', 'giveaway', 'stationerygiveaway', 'outgoingmail', 'vintagemail', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cqlb-x2Jqur/media/?size=m'
    },
    {
      id: 'ig-api-713',
      platform: 'instagram',
      title: 'We\'re making a v.2 of the grab bags as they already sold out! What\'s in the g...',
      description: 'We\'re making a v.2 of the grab bags as they already sold out! What\'s in the grab bags? Here are a few of the items you may receive in the grab bags. These are surprise bags so you can receive anything',
      url: 'https://www.instagram.com/reel/CqgQoPzpcen/',
      embedId: 'CqgQoPzpcen',
      category: 'behind-the-scenes',
      tags: ['grabbags', 'surprisebags', 'sale', 'stationerydiscount', 'stationery', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CqgQoPzpcen/media/?size=m'
    },
    {
      id: 'ig-api-714',
      platform: 'instagram',
      title: 'New World Record 😱 The longest washi tape in the world 🌎 thank you to every...',
      description: 'New World Record 😱 The longest washi tape in the world 🌎 thank you to everyone at CoraCreaCrafts who helped achieve this fun goal 😍😂 we had so much fun making this!  #worldrecord #washitape #longe',
      url: 'https://www.instagram.com/reel/CqfzmWsg-di/',
      embedId: 'CqfzmWsg-di',
      category: 'product-showcase',
      tags: ['worldrecord', 'washitape', 'longesttape', 'coracreacrafts', 'aprilfools', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CqfzmWsg-di/media/?size=m'
    },
    {
      id: 'ig-api-715',
      platform: 'instagram',
      title: 'April Fool\'s Weekend Sale is ON 😍 I curated this special box ($150) to give ...',
      description: 'April Fool\'s Weekend Sale is ON 😍 I curated this special box ($150) to give away to 1 lucky customer this weekend! Anyone ordering for $50+ will get automatically entered for the draw 🎁 Winner will ',
      url: 'https://www.instagram.com/reel/Cqdm4fgAD2g/',
      embedId: 'Cqdm4fgAD2g',
      category: 'behind-the-scenes',
      tags: ['flashsale', 'saleweekend', 'curatedbox', 'giveaway', 'stationerystore', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cqdm4fgAD2g/media/?size=m'
    },
    {
      id: 'ig-api-716',
      platform: 'instagram',
      title: 'Washi tape swatch book 📖💙 it is so much fun to make and so satisfying to ha...',
      description: 'Washi tape swatch book 📖💙 it is so much fun to make and so satisfying to have a little summary of all the washi tapes I\'ve ever produced 😍 #washitapeaddict #washi #washitapelove #coracreacrafts #st',
      url: 'https://www.instagram.com/reel/CqbACgggjvI/',
      embedId: 'CqbACgggjvI',
      category: 'product-showcase',
      tags: ['washitapeaddict', 'washi', 'washitapelove', 'coracreacrafts', 'stationerystore', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CqbACgggjvI/media/?size=m'
    },
    {
      id: 'ig-api-717',
      platform: 'instagram',
      title: 'Blue Heritage Pages using 100% of the the Treasure Box items 🪻💙',
      description: 'Blue Heritage Pages using 100% of the the Treasure Box items 🪻💙  #treasurebox #ricepaper #royaltheme #heritage #crowns #coracreacrafts #bluepages #journalinspiration #journalaesthetics #handmadejour',
      url: 'https://www.instagram.com/reel/CqV2dRtAyQ9/',
      embedId: 'CqV2dRtAyQ9',
      category: 'journal-spread',
      tags: ['treasurebox', 'ricepaper', 'royaltheme', 'heritage', 'crowns', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CqV2dRtAyQ9/media/?size=m'
    },
    {
      id: 'ig-api-718',
      platform: 'instagram',
      title: 'This is something I have to remind myself every single day 😅 sometimes socia...',
      description: 'This is something I have to remind myself every single day 😅 sometimes social media can make having your own business look so easy! You see all these other people succeeding and going viral on TikTok',
      url: 'https://www.instagram.com/reel/CqS-pv-AJDL/',
      embedId: 'CqS-pv-AJDL',
      category: 'product-showcase',
      tags: ['smallbiz', 'motivationalquotes', 'entrepreneur', 'artist', 'creativentrepreneur'],
      creator: 'CoraCreaCrafts',
      date: '2023-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CqS-pv-AJDL/media/?size=m'
    },
    {
      id: 'ig-api-719',
      platform: 'instagram',
      title: 'Vintage Gardens in the Spring Midi Journal & Spring Small Case 😍 a few piece...',
      description: 'Vintage Gardens in the Spring Midi Journal & Spring Small Case 😍 a few pieces of the Spring Leather Collection is still available in the shop & other pieces will be restocked in a few weeks! Also las',
      url: 'https://www.instagram.com/reel/CqQQAooAv1s/',
      embedId: 'CqQQAooAv1s',
      category: 'journal-spread',
      tags: ['minijournaling', 'minileatherbag', 'springcollection', 'floralleather', 'leatherpouch', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CqQQAooAv1s/media/?size=m'
    },
    {
      id: 'ig-api-720',
      platform: 'instagram',
      title: 'Vintage Gardens Theme 🌱 Don\'t forget to enter this month\'s challenge  with y...',
      description: 'Vintage Gardens Theme 🌱 Don\'t forget to enter this month\'s challenge #cccgardenschallenge with your creations 😍  #vintagegardens #gardendesign #gardeninspirations #landscaping #vintagejournal #journ',
      url: 'https://www.instagram.com/reel/CqLkZo8g4r-/',
      embedId: 'CqLkZo8g4r-',
      category: 'box-reveal',
      tags: ['cccgardenschallenge', 'vintagegardens', 'gardendesign', 'gardeninspirations', 'landscaping', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CqLkZo8g4r-/media/?size=m'
    },
    {
      id: 'ig-api-721',
      platform: 'instagram',
      title: 'Reorganized all my washi tapes 😍❤️ you can never have too many right? All or...',
      description: 'Reorganized all my washi tapes 😍❤️ you can never have too many right? All original designs you can find in my shop! #washitapesupplier #washitapelover #hoarder #washitapehoarder #washitapedesign #des',
      url: 'https://www.instagram.com/reel/CqIjU2ag7eE/',
      embedId: 'CqIjU2ag7eE',
      category: 'product-showcase',
      tags: ['washitapesupplier', 'washitapelover', 'hoarder', 'washitapehoarder', 'washitapedesign', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CqIjU2ag7eE/media/?size=m'
    },
    {
      id: 'ig-api-722',
      platform: 'instagram',
      title: 'Strolling through 18th century french castles\' gardens 📖✂️ Who\'s in? Used th...',
      description: 'Strolling through 18th century french castles\' gardens 📖✂️ Who\'s in? Used the Vintage Gardens supplies from the March Vintage Craft Box - link in bio 🔗 There\'s a part 2 to this spread coming soon!  ',
      url: 'https://www.instagram.com/reel/CqDuJt9gPx2/',
      embedId: 'CqDuJt9gPx2',
      category: 'journal-spread',
      tags: ['vintagejournal', 'vintagegardens', 'strolling', 'gardens', 'landscape', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CqDuJt9gPx2/media/?size=m'
    },
    {
      id: 'ig-api-723',
      platform: 'instagram',
      title: 'Who else? 🙋‍♀️',
      description: 'Who else? 🙋‍♀️ #littlethings #happysaturday #craftylife #todolists #desksetup #organization #planner #planneraddict #plannerlove #woodenboard #clipboard #quoteoftheday #motivationalquotes #starttheda',
      url: 'https://www.instagram.com/reel/Cp7uXKZgTAY/',
      embedId: 'Cp7uXKZgTAY',
      category: 'product-showcase',
      tags: ['littlethings', 'happysaturday', 'craftylife', 'todolists', 'desksetup'],
      creator: 'CoraCreaCrafts',
      date: '2023-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cp7uXKZgTAY/media/?size=m'
    },
    {
      id: 'ig-api-724',
      platform: 'instagram',
      title: 'Romantic Heritage Pages 🌷 Used supplies from the Treasure Box including the ...',
      description: 'Romantic Heritage Pages 🌷 Used supplies from the Treasure Box including the journal 📖 ✂️ available in my shop 🥰 #treasurebox #handmadejournal #leatherjournal #roses #heritage #vintagejournal #rosej',
      url: 'https://www.instagram.com/reel/Cp5aoAWILnb/',
      embedId: 'Cp5aoAWILnb',
      category: 'journal-spread',
      tags: ['treasurebox', 'handmadejournal', 'leatherjournal', 'roses', 'heritage', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cp5aoAWILnb/media/?size=m'
    },
    {
      id: 'ig-api-725',
      platform: 'instagram',
      title: 'March \'Vintage Gardens\' Preview is finally here 📦❤️ Inspired by country esta...',
      description: 'March \'Vintage Gardens\' Preview is finally here 📦❤️ Inspired by country estates, mansions and castles, this theme focuses on the beauty of their gardens 🌱 Landscaping maps, statues, gates, fountains',
      url: 'https://www.instagram.com/reel/Cp02mXGAnRV/',
      embedId: 'Cp02mXGAnRV',
      category: 'unboxing',
      tags: ['vintagecraftbox', 'subscriptionbox', 'craftysubscription', 'journalingsubscription', 'foutains', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cp02mXGAnRV/media/?size=m'
    },
    {
      id: 'ig-api-726',
      platform: 'instagram',
      title: 'In this chaotic world, be your own peace of mind 💚 stamping spread using the...',
      description: 'In this chaotic world, be your own peace of mind 💚 stamping spread using the gate wooden stamp, round floral pattern stamp set, boho stencil & boho stickers from my shop!  #relaxtime #relaxingactivit',
      url: 'https://www.instagram.com/reel/Cpxg0imok_G/',
      embedId: 'Cpxg0imok_G',
      category: 'journal-spread',
      tags: ['relaxtime', 'relaxingactivity', 'journalinspiration', 'relaxingjournal', 'bohospread', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cpxg0imok_G/media/?size=m'
    },
    {
      id: 'ig-api-727',
      platform: 'instagram',
      title: 'A work day at CoraCreaCrafts 📦❤️ it\'s so much fun to create these behind the...',
      description: 'A work day at CoraCreaCrafts 📦❤️ it\'s so much fun to create these behind the scenes reels! #smallbusiness #smallbizsquad #smallbusinessowner #smallbusinesssupport #reelsinstagram #womanempowerment #s',
      url: 'https://www.instagram.com/reel/Cpp9j0bAG4e/',
      embedId: 'Cpp9j0bAG4e',
      category: 'behind-the-scenes',
      tags: ['smallbusiness', 'smallbizsquad', 'smallbusinessowner', 'smallbusinesssupport', 'reelsinstagram', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cpp9j0bAG4e/media/?size=m'
    },
    {
      id: 'ig-api-728',
      platform: 'instagram',
      title: 'Molds Decorative Frames 🖤 Had so much fun with this craft project! The frame...',
      description: 'Molds Decorative Frames 🖤 Had so much fun with this craft project! The frames ended up being perfect to decorate my bathroom 😊  You can find the molds & rice paper in my shop!  #craftingsupplies #cr',
      url: 'https://www.instagram.com/reel/CpfgaH8gQio/',
      embedId: 'CpfgaH8gQio',
      category: 'tutorial',
      tags: ['craftingsupplies', 'craftingproject', 'molds', 'decorativeframes', 'frames', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CpfgaH8gQio/media/?size=m'
    },
    {
      id: 'ig-api-729',
      platform: 'instagram',
      title: 'Gift Wrap an order with me 🎁 For this gift wrap I decided to be super creati...',
      description: 'Gift Wrap an order with me 🎁 For this gift wrap I decided to be super creative and used some mesh like paper with kraft paper and washi tape! What do you think?  #giftwrap #gifrwrapideaa #theartofgif',
      url: 'https://www.instagram.com/reel/CpQA3bsALQd/',
      embedId: 'CpQA3bsALQd',
      category: 'behind-the-scenes',
      tags: ['giftwrap', 'gifrwrapideaa', 'theartofgifting', 'gifting', 'packanorderwithme', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CpQA3bsALQd/media/?size=m'
    },
    {
      id: 'ig-api-730',
      platform: 'instagram',
      title: 'You don\'t find a happy life, you make it 💞 So grateful for the past 2 weeks ...',
      description: 'You don\'t find a happy life, you make it 💞 So grateful for the past 2 weeks spent with all my loved ones who came from all over the world to attend my wedding celebrations 🌎🙏 Followed by one week o',
      url: 'https://www.instagram.com/reel/CpI0uo8JHa4/',
      embedId: 'CpI0uo8JHa4',
      category: 'product-showcase',
      tags: ['journalasmr', 'journalinglife', 'traveljournal', 'travel', 'travelwithme', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CpI0uo8JHa4/media/?size=m'
    },
    {
      id: 'ig-api-731',
      platform: 'instagram',
      title: 'Small businesses = a lot of ups and downs and a lot of work 📦 but also a lot...',
      description: 'Small businesses = a lot of ups and downs and a lot of work 📦 but also a lot of learning, self growth and the rewards of achieving something on your own ✨  #smallbusinessowner #smallbiz #smallbizquot',
      url: 'https://www.instagram.com/reel/Co_h5wfAU3_/',
      embedId: 'Co_h5wfAU3_',
      category: 'behind-the-scenes',
      tags: ['smallbusinessowner', 'smallbiz', 'smallbizquotes', 'motivationalquotes', 'quotesdaily', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Co_h5wfAU3_/media/?size=m'
    },
    {
      id: 'ig-api-732',
      platform: 'instagram',
      title: 'Instagram Video 732',
      description: '',
      url: 'https://www.instagram.com/reel/Co7h2UMA9Ta/',
      embedId: 'Co7h2UMA9Ta',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2023-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Co7h2UMA9Ta/media/?size=m'
    },
    {
      id: 'ig-api-733',
      platform: 'instagram',
      title: 'Egypt Tags 🏷️ So inspired by this amazing civilization! Boxes are shipping e...',
      description: 'Egypt Tags 🏷️ So inspired by this amazing civilization! Boxes are shipping early next week 📦 Also announcing the first Vintage Craft Box challenge with the February Ancient Egypt Box. Participate by',
      url: 'https://www.instagram.com/reel/CozyB4sg_pO/',
      embedId: 'CozyB4sg_pO',
      category: 'box-reveal',
      tags: ['cccegyptchallenge', 'vintagejournal', 'vintagecraftbox', 'coracreacraftbox', 'collage', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CozyB4sg_pO/media/?size=m'
    },
    {
      id: 'ig-api-734',
      platform: 'instagram',
      title: 'Astronomy Study 🔭🌙 Using the washi tapes from the Curiosities Box 💕 added ...',
      description: 'Astronomy Study 🔭🌙 Using the washi tapes from the Curiosities Box 💕 added some foam squares for the 3D effect. Box is available in my shop 📦 #cabinetofcuriosities #astronomy #vintageastronomy #ast',
      url: 'https://www.instagram.com/reel/CosD_GQg33e/',
      embedId: 'CosD_GQg33e',
      category: 'box-reveal',
      tags: ['cabinetofcuriosities', 'astronomy', 'vintageastronomy', 'astronomyjournal', 'celestial', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CosD_GQg33e/media/?size=m'
    },
    {
      id: 'ig-api-735',
      platform: 'instagram',
      title: 'Comment your favorite, 1, 2 or 3? 🥰 love mini journaling in the red leather ...',
      description: 'Comment your favorite, 1, 2 or 3? 🥰 love mini journaling in the red leather mini ❤️  #minijournaling #minijournal #leatherjournal #vintagejournal #artjournal #journalingcommunity #journalasmr #artree',
      url: 'https://www.instagram.com/reel/CooHp0JgfDu/',
      embedId: 'CooHp0JgfDu',
      category: 'product-showcase',
      tags: ['minijournaling', 'minijournal', 'leatherjournal', 'vintagejournal', 'artjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CooHp0JgfDu/media/?size=m'
    },
    {
      id: 'ig-api-736',
      platform: 'instagram',
      title: 'Let\'s celebrate LOVE this upcoming week 💖 Love to others and to yourself thr...',
      description: 'Let\'s celebrate LOVE this upcoming week 💖 Love to others and to yourself through little acts of gratefulness and self appreciation! I for one am so thankful for this beautiful community and for all o',
      url: 'https://www.instagram.com/reel/CoizHtWA5Bg/',
      embedId: 'CoizHtWA5Bg',
      category: 'product-showcase',
      tags: ['cleartagchallenge', 'journalingchallenge', 'cleartag', 'transparenttag', 'tagmaking', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CoizHtWA5Bg/media/?size=m'
    },
    {
      id: 'ig-api-737',
      platform: 'instagram',
      title: 'February Planner Pages 💞 I themed this month around the big event coming up ...',
      description: 'February Planner Pages 💞 I themed this month around the big event coming up for me next week 👰‍♀️💍 I\'m so excited 😊  #planner #plannercommunity #plannerpages #weddingpages #brideplanning #weddings',
      url: 'https://www.instagram.com/reel/CofOmXOgtqt/',
      embedId: 'CofOmXOgtqt',
      category: 'journal-spread',
      tags: ['planner', 'plannercommunity', 'plannerpages', 'weddingpages', 'brideplanning', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CofOmXOgtqt/media/?size=m'
    },
    {
      id: 'ig-api-738',
      platform: 'instagram',
      title: 'Cabinet of Curiosities 🪲Group Collab with the talented @amillennialsjournal ...',
      description: 'Cabinet of Curiosities 🪲Group Collab with the talented @amillennialsjournal @_junkjournal__ and @danis.journal using supplies from my shop 🥰 Check out their posts!  #cabinetofcuriosities #curiositie',
      url: 'https://www.instagram.com/reel/CobPvN_g-zE/',
      embedId: 'CobPvN_g-zE',
      category: 'product-showcase',
      tags: ['cabinetofcuriosities', 'curiosities', 'journalingshop', 'asmrjournaling', 'anxietyrelief', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CobPvN_g-zE/media/?size=m'
    },
    {
      id: 'ig-api-739',
      platform: 'instagram',
      title: 'Spoiler Alert ❗ February Ancient Egypt Box Preview is here 📦📸🖤 this theme ...',
      description: 'Spoiler Alert ❗ February Ancient Egypt Box Preview is here 📦📸🖤 this theme was suggested by many of you over the past year and it is also a fascinating topic for me personally, so here it is! Hope y',
      url: 'https://www.instagram.com/reel/CoXcA9OAi-X/',
      embedId: 'CoXcA9OAi-X',
      category: 'box-reveal',
      tags: ['vintagecraftbox', 'subscriptionbox', 'craftysubscription', 'journalingsubscription', 'ancientegypt', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CoXcA9OAi-X/media/?size=m'
    },
    {
      id: 'ig-api-740',
      platform: 'instagram',
      title: 'Day in the life of a Shop Owner Part 2 📸👩‍💻 Every day is different and the...',
      description: 'Day in the life of a Shop Owner Part 2 📸👩‍💻 Every day is different and the reality is that it\'s not all super exciting Instagramable moments, I spend a lot of time sitting at my desk working on all',
      url: 'https://www.instagram.com/reel/CoU2-Efgn5t/',
      embedId: 'CoU2-Efgn5t',
      category: 'behind-the-scenes',
      tags: ['smallbusiness', 'smallbizowner', 'smallbizsquad', 'dayinthelife', 'femaleentrepreneur', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CoU2-Efgn5t/media/?size=m'
    },
    {
      id: 'ig-api-741',
      platform: 'instagram',
      title: 'First Ancient Egypt Pages 🖤 Join me on TikTok later today for a live journal...',
      description: 'First Ancient Egypt Pages 🖤 Join me on TikTok later today for a live journaling session on this theme! All products used are part of the February Vintage Craft Box 📦 #vintagejournal #vintagecraftbox',
      url: 'https://www.instagram.com/reel/CoP65L4g027/',
      embedId: 'CoP65L4g027',
      category: 'journal-spread',
      tags: ['vintagejournal', 'vintagecraftbox', 'coracreacraftbox', 'collage', 'collagejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CoP65L4g027/media/?size=m'
    },
    {
      id: 'ig-api-742',
      platform: 'instagram',
      title: 'Framed Scenes of the Great Wall 🧱 I made this one for CNY and forgot to post...',
      description: 'Framed Scenes of the Great Wall 🧱 I made this one for CNY and forgot to post it. I always enjoy using the super wide Chinese Landscape Washi Tape 😍 Here I coupled it with the Chinese Coins wooden st',
      url: 'https://www.instagram.com/reel/CoNI5XAgbID/',
      embedId: 'CoNI5XAgbID',
      category: 'product-showcase',
      tags: ['journalingasmr', 'vintagejournal', 'anxietyrelief', 'journalcommunity', 'cnyjournal', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CoNI5XAgbID/media/?size=m'
    },
    {
      id: 'ig-api-743',
      platform: 'instagram',
      title: 'Anyone also "doesn\'t" have a hobby? 🤣',
      description: 'Anyone also "doesn\'t" have a hobby? 🤣  #crafting #journaladdict #stationaryaddict #vintagejournal #anxietyrelief #smallbusiness #quoteoftheday #journalsupplies #ａｅｓｔｈｅｔｉｃ #bujocommunity #washitapes #',
      url: 'https://www.instagram.com/reel/CoItr2IJynU/',
      embedId: 'CoItr2IJynU',
      category: 'product-showcase',
      tags: ['crafting', 'journaladdict', 'stationaryaddict', 'vintagejournal', 'anxietyrelief', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CoItr2IJynU/media/?size=m'
    },
    {
      id: 'ig-api-744',
      platform: 'instagram',
      title: 'Ever wanted to reuse a box you received in the mail but it\'s got this ugly sh...',
      description: 'Ever wanted to reuse a box you received in the mail but it\'s got this ugly shipping label on it? 🏷️ You can still do it with these few things everyone has at home: a knife, detergent, a little bit of',
      url: 'https://www.instagram.com/reel/CoFZm_VAzza/',
      embedId: 'CoFZm_VAzza',
      category: 'tutorial',
      tags: ['smallbizsquad', 'upcycledbox', 'upcycling', 'upcylcingideas', 'reuserecycle', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CoFZm_VAzza/media/?size=m'
    },
    {
      id: 'ig-api-745',
      platform: 'instagram',
      title: 'Curiosities Box Preview 📦✂️',
      description: 'Curiosities Box Preview 📦✂️  #astronomy #papersupplies #subscriptionbox #curiositiesbox #journalingasmr #asmrstationery #collagejournal #asmrreels #anxietyawareness #vintagejournal #artreels #journal',
      url: 'https://www.instagram.com/reel/CoApYwYJcNf/',
      embedId: 'CoApYwYJcNf',
      category: 'box-reveal',
      tags: ['astronomy', 'papersupplies', 'subscriptionbox', 'curiositiesbox', 'journalingasmr', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CoApYwYJcNf/media/?size=m'
    },
    {
      id: 'ig-api-746',
      platform: 'instagram',
      title: 'Symmetric Enchanted Forest Page 📜✂️ this is one i made on my first TikTok li...',
      description: 'Symmetric Enchanted Forest Page 📜✂️ this is one i made on my first TikTok live and it was so much fun 😊 #journalingcommunity #vintagejournal #enchantedforest #anxietyrelief #coracreacrafts',
      url: 'https://www.instagram.com/reel/Cn7Hx6Jg4rs/',
      embedId: 'Cn7Hx6Jg4rs',
      category: 'product-showcase',
      tags: ['journalingcommunity', 'vintagejournal', 'enchantedforest', 'anxietyrelief', 'coracreacrafts', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cn7Hx6Jg4rs/media/?size=m'
    },
    {
      id: 'ig-api-747',
      platform: 'instagram',
      title: 'Day in the life of a Shop Owner 📦✂️😍 Should I make more like this',
      description: 'Day in the life of a Shop Owner 📦✂️😍 Should I make more like this?  #smallbizsquad #smallbusiness #smallbusinessowner #femaleentrepreneur #dayinthelife #reelsinstagram #packanorderwithme #packingord',
      url: 'https://www.instagram.com/reel/Cn2K_uALYzg/',
      embedId: 'Cn2K_uALYzg',
      category: 'unboxing',
      tags: ['smallbizsquad', 'smallbusiness', 'smallbusinessowner', 'femaleentrepreneur', 'dayinthelife', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cn2K_uALYzg/media/?size=m'
    },
    {
      id: 'ig-api-748',
      platform: 'instagram',
      title: 'Happy Lunar New Year 🌙 to all of those who celebrate it. Although I know ver...',
      description: 'Happy Lunar New Year 🌙 to all of those who celebrate it. Although I know very little about it I am fascinated by Chinese culture, architecture and art! China is at the top of my list for 2024 travels',
      url: 'https://www.instagram.com/reel/CnupeAKogfR/',
      embedId: 'CnupeAKogfR',
      category: 'product-showcase',
      tags: ['cny', 'chinesenewyear', 'lunarnewyear', 'ancienthistory', 'ancientchina', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CnupeAKogfR/media/?size=m'
    },
    {
      id: 'ig-api-749',
      platform: 'instagram',
      title: 'Collage with background tissue paper ✂️📖',
      description: 'Collage with background tissue paper ✂️📖   #artjournal #artreels #collageart #collagejournal #anxietyawareness #journalingasmr #journalcommunity #vintagejournal #coracreacrafts',
      url: 'https://www.instagram.com/reel/CnrurlVofDI/',
      embedId: 'CnrurlVofDI',
      category: 'product-showcase',
      tags: ['artjournal', 'artreels', 'collageart', 'collagejournal', 'anxietyawareness', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CnrurlVofDI/media/?size=m'
    },
    {
      id: 'ig-api-750',
      platform: 'instagram',
      title: 'Vintage Astronomy Collage 📜✂️ I so enjoyed making a collage after a long tim...',
      description: 'Vintage Astronomy Collage 📜✂️ I so enjoyed making a collage after a long time! I used the Astronomy Washi Tape Set which will come inside the brand new Curiosities Box 📦 Order the box before the end',
      url: 'https://www.instagram.com/reel/CnpDfEQo4V4/',
      embedId: 'CnpDfEQo4V4',
      category: 'box-reveal',
      tags: ['astronomy', 'collage', 'collagejournal', 'collageart', 'artwork', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CnpDfEQo4V4/media/?size=m'
    },
    {
      id: 'ig-api-751',
      platform: 'instagram',
      title: 'Tiny tin box redecorated ✂️📜 Do you remember the tin sticker box that came i...',
      description: 'Tiny tin box redecorated ✂️📜 Do you remember the tin sticker box that came inside the October Box? Here\'s an idea on how to reuse it 😊 #enchantedforest #stationeryshop #woodwitch #magicwoods #diybox',
      url: 'https://www.instagram.com/reel/Cnj5j3NoHNl/',
      embedId: 'Cnj5j3NoHNl',
      category: 'tutorial',
      tags: ['enchantedforest', 'stationeryshop', 'woodwitch', 'magicwoods', 'diybox', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cnj5j3NoHNl/media/?size=m'
    },
    {
      id: 'ig-api-752',
      platform: 'instagram',
      title: 'Cutting papers, cards and notes 😅✂️',
      description: 'Cutting papers, cards and notes 😅✂️  #smallbusinessowner #smallbusiness #smallbizsquad #smallbiz #supportsmallbusiness #tediouswork #papercutting #papercutter #asmrpaper #asmrcommunity #asmrcutting #',
      url: 'https://www.instagram.com/reel/Cneu6e-ohjz/',
      embedId: 'Cneu6e-ohjz',
      category: 'box-reveal',
      tags: ['smallbusinessowner', 'smallbusiness', 'smallbizsquad', 'smallbiz', 'supportsmallbusiness', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cneu6e-ohjz/media/?size=m'
    },
    {
      id: 'ig-api-753',
      platform: 'instagram',
      title: 'Enchanted Forest Pages to escape on this gloomy Saturday 📖✂️🌲💚',
      description: 'Enchanted Forest Pages to escape on this gloomy Saturday 📖✂️🌲💚  #journalingsubscription #enchantedforest #magical #stickerbook #coracreacrafts #journaladdict #journalpages #asmrjournaling #anxietys',
      url: 'https://www.instagram.com/reel/CnZqtWsIJsp/',
      embedId: 'CnZqtWsIJsp',
      category: 'box-reveal',
      tags: ['journalingsubscription', 'enchantedforest', 'magical', 'stickerbook', 'coracreacrafts', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CnZqtWsIJsp/media/?size=m'
    },
    {
      id: 'ig-api-754',
      platform: 'instagram',
      title: 'Wax Seal Friday 📜 it\'s been a while since I\'ve made some wax seals and it wa...',
      description: 'Wax Seal Friday 📜 it\'s been a while since I\'ve made some wax seals and it was fun to get back into it!  #waxseal #waxsealstamp #waxsealing #craftingideas #crafting #waxsealart #paperlover #scrapbooki',
      url: 'https://www.instagram.com/reel/CnXDo13IqSx/',
      embedId: 'CnXDo13IqSx',
      category: 'product-showcase',
      tags: ['waxseal', 'waxsealstamp', 'waxsealing', 'craftingideas', 'crafting', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CnXDo13IqSx/media/?size=m'
    },
    {
      id: 'ig-api-755',
      platform: 'instagram',
      title: 'It\'s not too late to plan January 😅📖 Monthly overview with the Enchanted Fo...',
      description: 'It\'s not too late to plan January 😅📖 Monthly overview with the Enchanted Forest supplies from the January Vintage Craft Box 🌲💚 #planner #planningcommunity #monthlyplannerspread #vintagecraftbox #p',
      url: 'https://www.instagram.com/reel/CnU6rNIoS13/',
      embedId: 'CnU6rNIoS13',
      category: 'journal-spread',
      tags: ['planner', 'planningcommunity', 'monthlyplannerspread', 'vintagecraftbox', 'planneraddict', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CnU6rNIoS13/media/?size=m'
    },
    {
      id: 'ig-api-756',
      platform: 'instagram',
      title: 'Spoiler Alert ❗January Enchanted Forest Box Preview is here 🌲🦋✨',
      description: 'Spoiler Alert ❗January Enchanted Forest Box Preview is here 🌲🦋✨ This theme was the fan vote of 2022, after several rounds of suggestions and votes on IG and newsletter! Immerse yourself in a world f',
      url: 'https://www.instagram.com/reel/CnPWkLMIeKd/',
      embedId: 'CnPWkLMIeKd',
      category: 'box-reveal',
      tags: ['vintagecraftbox', 'subscriptionbox', 'craftysubscription', 'journalingsubscription', 'enchantedforest', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CnPWkLMIeKd/media/?size=m'
    },
    {
      id: 'ig-api-757',
      platform: 'instagram',
      title: 'Journaling is the best hobby I ever got into 📖 From allowing me to express m...',
      description: 'Journaling is the best hobby I ever got into 📖 From allowing me to express my thoughts and feelings, to sparking the creativity inside me and to clearing my head and releasing my anxiety, journaling ',
      url: 'https://www.instagram.com/reel/CnKTeUZI8M5/',
      embedId: 'CnKTeUZI8M5',
      category: 'product-showcase',
      tags: ['journalingcommunity', 'journalasmr', 'journalingaddict', 'desksetup', 'deskgoals', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CnKTeUZI8M5/media/?size=m'
    },
    {
      id: 'ig-api-758',
      platform: 'instagram',
      title: 'Vintage Botanical Pages ✒️ Happy Weekend to everyone 💚',
      description: 'Vintage Botanical Pages ✒️ Happy Weekend to everyone 💚  For this spread I used the new Botanical tea dyed Papers just added to my shop, as well as the Burnt Edges Washi Tape and the Writers & Poets N',
      url: 'https://www.instagram.com/reel/CnHrak7K_3m/',
      embedId: 'CnHrak7K_3m',
      category: 'journal-spread',
      tags: ['vintagejournal', 'vintagestationery', 'vintagebotanical', 'botanicalspread', 'teadyedpapers', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2023-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CnHrak7K_3m/media/?size=m'
    },
    {
      id: 'ig-api-759',
      platform: 'instagram',
      title: 'Enchanted Forest DIY Box 🤎🌲🦋 Upcycled the box from a stamp with two of the...',
      description: 'Enchanted Forest DIY Box 🤎🌲🦋 Upcycled the box from a stamp with two of the products from the upcoming January \'Enchanted Forest\' Box 📦🥰 so excited to show you this exclusive sticker book I made f',
      url: 'https://www.instagram.com/reel/CnAcw_doJkx/',
      embedId: 'CnAcw_doJkx',
      category: 'tutorial',
      tags: ['enchantedforest', 'stationeryshop', 'woodwitch', 'magicwoods', 'diybox', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CnAcw_doJkx/media/?size=m'
    },
    {
      id: 'ig-api-760',
      platform: 'instagram',
      title: 'Inkpad Sampling 😍 As promised, I tested the new inkpads. Here are 8 colors o...',
      description: 'Inkpad Sampling 😍 As promised, I tested the new inkpads. Here are 8 colors out of the 18 available 🤎💙💚💜 What\'s your favorite? From top to bottom and left to right: Primrose, Mulberry Bush, Oceani',
      url: 'https://www.instagram.com/reel/Cm6pZOtorZx/',
      embedId: 'Cm6pZOtorZx',
      category: 'product-showcase',
      tags: ['inkpads', 'colorsample', 'colorsampling', 'ink', 'craftingideas', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cm6pZOtorZx/media/?size=m'
    },
    {
      id: 'ig-api-761',
      platform: 'instagram',
      title: 'Dear 2022, thank you for the memories and for an incredible year ✨ there have...',
      description: 'Dear 2022, thank you for the memories and for an incredible year ✨ there have been moments of joy and moments of sadness, challenges and achievements, loneliness and love 💕 Most of all I\'m grateful f',
      url: 'https://www.instagram.com/reel/Cm12181or6H/',
      embedId: 'Cm12181or6H',
      category: 'behind-the-scenes',
      tags: ['2022recap', '2022endingsoon', 'videoedits', 'reels', 'igreels', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cm12181or6H/media/?size=m'
    },
    {
      id: 'ig-api-762',
      platform: 'instagram',
      title: 'Playing with moulds and papers to make these little decorated pieces ✨Watch o...',
      description: 'Playing with moulds and papers to make these little decorated pieces ✨Watch out for moulds coming to the shop in 2023 🤩 I used these for my gift wrapping for Christmas!  #craftingideas #journalpages ',
      url: 'https://www.instagram.com/reel/Cmr2FSWopBu/',
      embedId: 'Cmr2FSWopBu',
      category: 'tutorial',
      tags: ['craftingideas', 'journalpages', 'anxietyawareness', 'anxietyrelief', 'relaxingtime', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cmr2FSWopBu/media/?size=m'
    },
    {
      id: 'ig-api-763',
      platform: 'instagram',
      title: '🎄 CLOSED 🎁 winners have been contacted',
      description: '🎄 CLOSED 🎁 winners have been contacted  ✨ To celebrate Christmas and the end of this beautiful year I am hosting a giveaway full of wonderful surprise 🎁 ✨ There will be 4 winners total!   First Pri',
      url: 'https://www.instagram.com/reel/Cmmw9vJow_M/',
      embedId: 'Cmmw9vJow_M',
      category: 'tutorial',
      tags: ['giveaway', 'stationerygiveaway', 'vintagestationery', 'outgoingmail', 'outgoinghappymail', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cmmw9vJow_M/media/?size=m'
    },
    {
      id: 'ig-api-764',
      platform: 'instagram',
      title: 'Writing Journal Pages ✒️🖤 I\'ve been doing a lot of art journaling and collag...',
      description: 'Writing Journal Pages ✒️🖤 I\'ve been doing a lot of art journaling and collage but I wanted to get back into the more traditional form of journaling expression with writing 🙏 it feels good to lay thi',
      url: 'https://www.instagram.com/reel/CmhJ2FMo8Wm/',
      embedId: 'CmhJ2FMo8Wm',
      category: 'journal-spread',
      tags: ['writingjournal', 'writtenexpression', 'journalpages', 'anxietyawareness', 'anxietyrelief', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CmhJ2FMo8Wm/media/?size=m'
    },
    {
      id: 'ig-api-765',
      platform: 'instagram',
      title: 'Dreamer Scribe Pages ✍️✒️ Used the supplies from the current Treasure Box as ...',
      description: 'Dreamer Scribe Pages ✍️✒️ Used the supplies from the current Treasure Box as well as the stamps from the December Vintage Craft Box 🖤 #vintagejournal #vintagestationery #vintagestamps #scribe #vintag',
      url: 'https://www.instagram.com/reel/CmcBHLII-sQ/',
      embedId: 'CmcBHLII-sQ',
      category: 'journal-spread',
      tags: ['vintagejournal', 'vintagestationery', 'vintagestamps', 'scribe', 'vintagewashitape', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CmcBHLII-sQ/media/?size=m'
    },
    {
      id: 'ig-api-766',
      platform: 'instagram',
      title: 'Gift wrapping one of the Vintage Craft Box 🎁 Did you know you can add a gift...',
      description: 'Gift wrapping one of the Vintage Craft Box 🎁 Did you know you can add a gift wrap to any item you purchase from the shop when you check out? 🎄✂️ #journalinginspiration #journalasmr #asmrjournaling #',
      url: 'https://www.instagram.com/reel/CmWrOwggi6O/',
      embedId: 'CmWrOwggi6O',
      category: 'tutorial',
      tags: ['journalinginspiration', 'journalasmr', 'asmrjournaling', 'asmrpaper', 'journalcommunity', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CmWrOwggi6O/media/?size=m'
    },
    {
      id: 'ig-api-767',
      platform: 'instagram',
      title: 'Small Business = A lot of work and struggles but I would not want to do anyth...',
      description: 'Small Business = A lot of work and struggles but I would not want to do anything else ever again 🤯🥰  #smallbusiness #smallbusinesssaturday #smallbusinessowner #smallbusinesssupport #reelsinstagram #',
      url: 'https://www.instagram.com/reel/CmRxwuJARpW/',
      embedId: 'CmRxwuJARpW',
      category: 'behind-the-scenes',
      tags: ['smallbusiness', 'smallbusinesssaturday', 'smallbusinessowner', 'smallbusinesssupport', 'reelsinstagram', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CmRxwuJARpW/media/?size=m'
    },
    {
      id: 'ig-api-768',
      platform: 'instagram',
      title: 'How to use the Pocket Watches from the Treasure Box 🤗 Here\'s an idea! I made...',
      description: 'How to use the Pocket Watches from the Treasure Box 🤗 Here\'s an idea! I made them into little Impressionist paintings ornaments and covered them with epoxy resin to make it last forever ❤️ #pocketwat',
      url: 'https://www.instagram.com/reel/CmO9sJkAyz1/',
      embedId: 'CmO9sJkAyz1',
      category: 'tutorial',
      tags: ['pocketwatch', 'pocketwatchcraft', 'craftingproject', 'craftingideas', 'craftwithme', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CmO9sJkAyz1/media/?size=m'
    },
    {
      id: 'ig-api-769',
      platform: 'instagram',
      title: 'Treasure Box Project from this weekend! ✂️😍 I created a cover for a notebook...',
      description: 'Treasure Box Project from this weekend! ✂️😍 I created a cover for a notebook from scratch using the product from the Dec/Jan Treasure Box ! This was so much fun 😊 I might remove the insert and add s',
      url: 'https://www.instagram.com/reel/CmJ51ZWgOMU/',
      embedId: 'CmJ51ZWgOMU',
      category: 'tutorial',
      tags: ['journalcover', 'journalinspiration', 'journalingideas', 'diyjournal', 'coverart', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CmJ51ZWgOMU/media/?size=m'
    },
    {
      id: 'ig-api-770',
      platform: 'instagram',
      title: 'Decorate a journal cover with me! ✂️✒️ using the supplies from the December V...',
      description: 'Decorate a journal cover with me! ✂️✒️ using the supplies from the December Vintage Craft Box available in my shop now 😊📜 #journalcover #journalcollage #journalinginspiration #journalasmr #asmrjourn',
      url: 'https://www.instagram.com/reel/CmHQJbtgR_I/',
      embedId: 'CmHQJbtgR_I',
      category: 'box-reveal',
      tags: ['journalcover', 'journalcollage', 'journalinginspiration', 'journalasmr', 'asmrjournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CmHQJbtgR_I/media/?size=m'
    },
    {
      id: 'ig-api-771',
      platform: 'instagram',
      title: 'Little card with the products from the December Vintage Craft Box 📜✒️ will u...',
      description: 'Little card with the products from the December Vintage Craft Box 📜✒️ will use it for a gift with a little message inside!  #vintagecard #vintagejournal #cardmaking #diycard #diyideas #craftingideas ',
      url: 'https://www.instagram.com/reel/Cl9NhQiJQao/',
      embedId: 'Cl9NhQiJQao',
      category: 'tutorial',
      tags: ['vintagecard', 'vintagejournal', 'cardmaking', 'diycard', 'diyideas', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cl9NhQiJQao/media/?size=m'
    },
    {
      id: 'ig-api-772',
      platform: 'instagram',
      title: 'Another Christmas Card ready to go 🎄📜 Love these new embroidery style Chris...',
      description: 'Another Christmas Card ready to go 🎄📜 Love these new embroidery style Christmas stamps & plaid stencils, just added to the shop and available in limited quantity ❤️ #christmassupplies #christmascraf',
      url: 'https://www.instagram.com/reel/Cl4D8YsJY87/',
      embedId: 'Cl4D8YsJY87',
      category: 'tutorial',
      tags: ['christmassupplies', 'christmascrafts', 'christmascards', 'diycard', 'diyideas', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cl4D8YsJY87/media/?size=m'
    },
    {
      id: 'ig-api-773',
      platform: 'instagram',
      title: 'Spoiler Alert ❗December \'Writers & Artists\' Vintage Craft Box Preview is here...',
      description: 'Spoiler Alert ❗December \'Writers & Artists\' Vintage Craft Box Preview is here ❤️ Don\'t watch if you want the surprise 🫢 This month is all about the nostalgia of written and artistic expression 📜✒️ A',
      url: 'https://www.instagram.com/reel/Cl1KorVAKMh/',
      embedId: 'Cl1KorVAKMh',
      category: 'box-reveal',
      tags: ['journalinspiration', 'journalasmr', 'journalingaddict', 'asmrstationery', 'asmrjournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cl1KorVAKMh/media/?size=m'
    },
    {
      id: 'ig-api-774',
      platform: 'instagram',
      title: 'Christmas Tag or Ornament 🎄 I haven\'t quite decided yet but I had so much fu...',
      description: 'Christmas Tag or Ornament 🎄 I haven\'t quite decided yet but I had so much fun making this one for the #3dchristmastag challenge hosted by @dream.n.journal and @sophie.mari.d 💞 thank you for this won',
      url: 'https://www.instagram.com/reel/ClyrVRaAZZX/',
      embedId: 'ClyrVRaAZZX',
      category: 'tutorial',
      tags: ['3dchristmastag', 'christmastag', 'christmasornaments', 'diyornaments', 'diychristmasornaments', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/ClyrVRaAZZX/media/?size=m'
    },
    {
      id: 'ig-api-775',
      platform: 'instagram',
      title: 'I lied and I said I was busy 🫣 does this ever happen to you? More and more I...',
      description: 'I lied and I said I was busy 🫣 does this ever happen to you? More and more I\'m reminded how precious time is and that spending these moments with myself, just doing what I love, is invaluable!  #self',
      url: 'https://www.instagram.com/reel/Clw4DICpO1q/',
      embedId: 'Clw4DICpO1q',
      category: 'product-showcase',
      tags: ['selflove', 'selfcare', 'journalingasmr', 'journaling', 'studiospace'],
      creator: 'CoraCreaCrafts',
      date: '2022-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Clw4DICpO1q/media/?size=m'
    },
    {
      id: 'ig-api-776',
      platform: 'instagram',
      title: 'DIY Christmas Cards 🎄 Using the Christmas Postcards Rice Paper and the Washi...',
      description: 'DIY Christmas Cards 🎄 Using the Christmas Postcards Rice Paper and the Washi & Stamps from the Winter & Holidays Box ❤️  #christmassupplies #christmastape #christmasstationery #christmascrafts #chris',
      url: 'https://www.instagram.com/reel/ClobuWUgbWs/',
      embedId: 'ClobuWUgbWs',
      category: 'tutorial',
      tags: ['christmassupplies', 'christmastape', 'christmasstationery', 'christmascrafts', 'christmascards', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/ClobuWUgbWs/media/?size=m'
    },
    {
      id: 'ig-api-777',
      platform: 'instagram',
      title: 'Vintage Fashion Pages 🖤 For those of you who missed the September box, these...',
      description: 'Vintage Fashion Pages 🖤 For those of you who missed the September box, these fashion themed products are now available in the shop!  #fashion #vintagefashion #fashionjournal #fashionpages #measuringt',
      url: 'https://www.instagram.com/reel/ClgqYP9gZTo/',
      embedId: 'ClgqYP9gZTo',
      category: 'box-reveal',
      tags: ['fashion', 'vintagefashion', 'fashionjournal', 'fashionpages', 'measuringtape', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/ClgqYP9gZTo/media/?size=m'
    },
    {
      id: 'ig-api-778',
      platform: 'instagram',
      title: 'Today is Black Friday! 💥 This is the largest Sale we\'ve hosted to date: up t...',
      description: 'Today is Black Friday! 💥 This is the largest Sale we\'ve hosted to date: up to 40% off our shop products and 10% off subscription boxes 📦😆 This is an exceptional sale which will not be happening aga',
      url: 'https://www.instagram.com/reel/ClZLmv5JGaS/',
      embedId: 'ClZLmv5JGaS',
      category: 'box-reveal',
      tags: ['blackfridaysale', 'blackfriday', 'leatherjournal', 'leatherpouches', 'leathersale', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/ClZLmv5JGaS/media/?size=m'
    },
    {
      id: 'ig-api-779',
      platform: 'instagram',
      title: 'Something different today 😃 I still have to pinch myself every single day be...',
      description: 'Something different today 😃 I still have to pinch myself every single day because I can\'t believe I made it happen: turned my passion into a full time occupation 📦🥰✒️ I\'m so grateful I can live thi',
      url: 'https://www.instagram.com/reel/ClUpCfUJ0bA/',
      embedId: 'ClUpCfUJ0bA',
      category: 'product-showcase',
      tags: ['reel', 'photoreel', 'photocollage', 'reelcollage', 'smallbusiness', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/ClUpCfUJ0bA/media/?size=m'
    },
    {
      id: 'ig-api-780',
      platform: 'instagram',
      title: 'Gratitude Pages with dried leaves & the Transparent Autumn Washi Tape just ad...',
      description: 'Gratitude Pages with dried leaves & the Transparent Autumn Washi Tape just added to my shop today! 🍂 Sometimes we\'re so deep into our own world and problems that it\'s hard to see how lucky we are. It',
      url: 'https://www.instagram.com/reel/ClRWE-Vgg7G/',
      embedId: 'ClRWE-Vgg7G',
      category: 'journal-spread',
      tags: ['gratefullness', 'thanksgiving', 'thanksgivingspread', 'happythanksgiving', 'gratitudepages', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/ClRWE-Vgg7G/media/?size=m'
    },
    {
      id: 'ig-api-781',
      platform: 'instagram',
      title: '🖤 The exciting BF week is here! Enjoy an early bird discount throughout the ...',
      description: '🖤 The exciting BF week is here! Enjoy an early bird discount throughout the shop by using the code EARLYSOCIALBIRDS15 🖤 Time to get your hands on these pieces you\'ve been eyeing 👀  #earlybird #earl',
      url: 'https://www.instagram.com/reel/ClOkRy9g8vO/',
      embedId: 'ClOkRy9g8vO',
      category: 'product-showcase',
      tags: ['earlybird', 'earlybirddiscount', 'discount', 'blackfriday', 'specialsale', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/ClOkRy9g8vO/media/?size=m'
    },
    {
      id: 'ig-api-782',
      platform: 'instagram',
      title: 'Pop Up Card 💞 I made this one for a special little girl who was just born! A...',
      description: 'Pop Up Card 💞 I made this one for a special little girl who was just born! After my last pop up card video you asked for a longer tutorial, it\'s now available on my YT channel, check it out 🤗 Idea f',
      url: 'https://www.instagram.com/reel/ClCO_RlJkS0/',
      embedId: 'ClCO_RlJkS0',
      category: 'tutorial',
      tags: ['cardmaking', 'card', 'diycard', 'birthcard', 'carddesign', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/ClCO_RlJkS0/media/?size=m'
    },
    {
      id: 'ig-api-783',
      platform: 'instagram',
      title: 'Dark Alchemy/Anatomy Pages for the  🖤 it\'s been a while since I created some...',
      description: 'Dark Alchemy/Anatomy Pages for the #slidespreadchallenge 🖤 it\'s been a while since I created something in that style and I missed it! I used (in my shop): Magic Manuscript Paper Set, Anatomy Washi Ta',
      url: 'https://www.instagram.com/reel/Ck8rjpBADAE/',
      embedId: 'Ck8rjpBADAE',
      category: 'journal-spread',
      tags: ['slidespreadchallenge', 'magicjournal', 'magicmanuscript', 'magocwashitape', 'magicpapers', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Ck8rjpBADAE/media/?size=m'
    },
    {
      id: 'ig-api-784',
      platform: 'instagram',
      title: 'Winter landscape ❄️ featuring the Great Wall of China and some other elements...',
      description: 'Winter landscape ❄️ featuring the Great Wall of China and some other elements from the November \'Ancient China\' Box 📦  This box is still available, link in my bio 😊 #ancientchina #antiquewashitape #',
      url: 'https://www.instagram.com/reel/Ck1PFFopI4M/',
      embedId: 'Ck1PFFopI4M',
      category: 'box-reveal',
      tags: ['ancientchina', 'antiquewashitape', 'anxietysupport', 'asmrjournaling', 'asmrjournal', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Ck1PFFopI4M/media/?size=m'
    },
    {
      id: 'ig-api-785',
      platform: 'instagram',
      title: 'Gift wrap an order with the Winter & Holidays Box 🎁❤️',
      description: 'Gift wrap an order with the Winter & Holidays Box 🎁❤️ #gift #giftwrappingideas #giftideas #giftwrapping #diygiftideas #diygiftwrap #wrappingpaper #diywrappingpaper #christmassupplies #christmaswrappi',
      url: 'https://www.instagram.com/reel/CkvtFhrAlo9/',
      embedId: 'CkvtFhrAlo9',
      category: 'tutorial',
      tags: ['gift', 'giftwrappingideas', 'giftideas', 'giftwrapping', 'diygiftideas', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CkvtFhrAlo9/media/?size=m'
    },
    {
      id: 'ig-api-786',
      platform: 'instagram',
      title: 'Vintage Craft Box Preview: Ancient China ⛩️ This month will transport you acr...',
      description: 'Vintage Craft Box Preview: Ancient China ⛩️ This month will transport you across the world and back in time to discover the beauty of Ancient China:  landscaped mountains, dragons, shrines, coins, sym',
      url: 'https://www.instagram.com/reel/CkqbQpmAP_F/',
      embedId: 'CkqbQpmAP_F',
      category: 'box-reveal',
      tags: ['ancientchina', 'china', 'chineseart', 'chinafantasy', 'chinesearchitecture', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CkqbQpmAP_F/media/?size=m'
    },
    {
      id: 'ig-api-787',
      platform: 'instagram',
      title: 'Christmas Shop Update ✨🎄 Head over to my shop to see all the new products av...',
      description: 'Christmas Shop Update ✨🎄 Head over to my shop to see all the new products available 🎁❤️ I\'m particularly excited about the Winter & Holidays Box which is my very first stationery box with its own de',
      url: 'https://www.instagram.com/reel/CklgRU3AShw/',
      embedId: 'CklgRU3AShw',
      category: 'behind-the-scenes',
      tags: ['christmas', 'christmasstationery', 'christmasjournal', 'christmascrafts', 'christmasshop', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CklgRU3AShw/media/?size=m'
    },
    {
      id: 'ig-api-788',
      platform: 'instagram',
      title: 'Autumn vibes 🍂🤎 Experimenting with textures and paste in my art journal wit...',
      description: 'Autumn vibes 🍂🤎 Experimenting with textures and paste in my art journal with the supplies from the Oct/Nov Treasure Box 📦 The boxes are shipping today, tomorrow and next week!  #autumnpages #autumn',
      url: 'https://www.instagram.com/reel/CkiqXZHglGS/',
      embedId: 'CkiqXZHglGS',
      category: 'box-reveal',
      tags: ['autumnpages', 'autumnwashitape', 'autumnjournaling', 'autumnstationery', 'autumnvibes', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CkiqXZHglGS/media/?size=m'
    },
    {
      id: 'ig-api-789',
      platform: 'instagram',
      title: 'Hello November 📖 Fantasy vibes for my monthly planner overview 🐉 Using the ...',
      description: 'Hello November 📖 Fantasy vibes for my monthly planner overview 🐉 Using the November \'Ancient China\' Box products 🐲 #fantasy #fantasyart #fantasyplanner #fantasyjournal #ancientchina #china #timetra',
      url: 'https://www.instagram.com/reel/Ckdgh2NAqlR/',
      embedId: 'Ckdgh2NAqlR',
      category: 'box-reveal',
      tags: ['fantasy', 'fantasyart', 'fantasyplanner', 'fantasyjournal', 'ancientchina', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Ckdgh2NAqlR/media/?size=m'
    },
    {
      id: 'ig-api-790',
      platform: 'instagram',
      title: 'Happy Halloween 🎃🖤 a little spooky spread to celebrate! My favorite part: s...',
      description: 'Happy Halloween 🎃🖤 a little spooky spread to celebrate! My favorite part: staining the lace with my inkpad to give it a spooky look! P.s cover your hears when I start embossing, i forgot to turn the',
      url: 'https://www.instagram.com/reel/CkYY_eaAEwG/',
      embedId: 'CkYY_eaAEwG',
      category: 'journal-spread',
      tags: ['halloweenjournaling', 'halloweenjournal', 'halloweencrafts', 'halloweenbundle', 'halloween', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CkYY_eaAEwG/media/?size=m'
    },
    {
      id: 'ig-api-791',
      platform: 'instagram',
      title: 'Blue Tags 💙 Sneak Peek of the November Box on the theme Ancient China ⛩️ I\'m...',
      description: 'Blue Tags 💙 Sneak Peek of the November Box on the theme Ancient China ⛩️ I\'m obsessed with these stamps 😆 The box is now available to order on my website! #ancientchina #chinesedynasty #chineseart #',
      url: 'https://www.instagram.com/reel/CkQol1ug2Tt/',
      embedId: 'CkQol1ug2Tt',
      category: 'box-reveal',
      tags: ['ancientchina', 'chinesedynasty', 'chineseart', 'chinesearchitecture', 'arttags', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CkQol1ug2Tt/media/?size=m'
    },
    {
      id: 'ig-api-792',
      platform: 'instagram',
      title: 'Holo Foil Baroque Tape 🖤 this is an exclusive product coming in the Advent C...',
      description: 'Holo Foil Baroque Tape 🖤 this is an exclusive product coming in the Advent Calendars 🎁 #foiladdict #foilwashi #foilwashitape #foil #holographic #holofoil #holowashi #decorativetape #washitapeshop #w',
      url: 'https://www.instagram.com/reel/CkLkG_RJGYg/',
      embedId: 'CkLkG_RJGYg',
      category: 'product-showcase',
      tags: ['foiladdict', 'foilwashi', 'foilwashitape', 'foil', 'holographic', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CkLkG_RJGYg/media/?size=m'
    },
    {
      id: 'ig-api-793',
      platform: 'instagram',
      title: 'Pop Up Card Vintage Theme 🖤🌖 this was so much fun to make, thank you @sola....',
      description: 'Pop Up Card Vintage Theme 🖤🌖 this was so much fun to make, thank you @sola.journaling for the idea 💡Should I make a longer tutorial video for this?  From my shop I used some of the new \'Antique Fou',
      url: 'https://www.instagram.com/reel/CkGY4G0grg_/',
      embedId: 'CkGY4G0grg_',
      category: 'tutorial',
      tags: ['popupart', 'popupcard', 'popupcards', 'vintagecards', 'cardmaking', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CkGY4G0grg_/media/?size=m'
    },
    {
      id: 'ig-api-794',
      platform: 'instagram',
      title: 'Spooky Talk with Homespun Haints 🖤 Becky and Diana from Homespun Haints join...',
      description: 'Spooky Talk with Homespun Haints 🖤 Becky and Diana from Homespun Haints joined me to talk about spooky experiences  crafts and their podcast!  #halloweenjournaling #halloweencrafts #ghoststories #spo',
      url: 'https://www.instagram.com/tv/CkES-2Yp5MY/',
      embedId: '',
      category: 'product-showcase',
      tags: ['halloweenjournaling', 'halloweencrafts', 'ghoststories', 'spookycrafts', 'spooky', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-795',
      platform: 'instagram',
      title: 'Trifold Pages about books 📖 Used the Vellum Foil Papers and the Art Nouveau ...',
      description: 'Trifold Pages about books 📖 Used the Vellum Foil Papers and the Art Nouveau washi tape 😊  I so enjoyed writing these pages in my journal. I\'ve been so taken by the \'art\' side of joirnaling that I so',
      url: 'https://www.instagram.com/reel/Cj-wfMRAFDQ/',
      embedId: 'Cj-wfMRAFDQ',
      category: 'product-showcase',
      tags: ['trifold', 'triplepages', 'vellumpapers', 'foilpapers', 'foiladdict', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cj-wfMRAFDQ/media/?size=m'
    },
    {
      id: 'ig-api-796',
      platform: 'instagram',
      title: 'Spooky Journal Pages 🕸📖 Do you know of any haunted libraries? I\'ve been jou...',
      description: 'Spooky Journal Pages 🕸📖 Do you know of any haunted libraries? I\'ve been journaling while listening to @homespunhaints spooky stories and it\'s so much fun 👻 I used the new \'Black Grimoire\' Journal a',
      url: 'https://www.instagram.com/reel/Cj8RrhBgATO/',
      embedId: 'Cj8RrhBgATO',
      category: 'journal-spread',
      tags: ['halloween', 'halloweenjournal', 'spookyjournal', 'spookyseason', 'spookypages', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cj8RrhBgATO/media/?size=m'
    },
    {
      id: 'ig-api-797',
      platform: 'instagram',
      title: 'Washi Background Page 📖 Using the new collection of \'Antique Foundation Wash...',
      description: 'Washi Background Page 📖 Using the new collection of \'Antique Foundation Washi Tapes\', the Forest Clear Stamps from this month\'s box and also a few of the new Handles Stamps all available in my shop ',
      url: 'https://www.instagram.com/reel/Cj5gkLPAX4K/',
      embedId: 'Cj5gkLPAX4K',
      category: 'box-reveal',
      tags: ['washibgchallengebyhazel', 'backgroundjournal', 'journal', 'journaling', 'artjournal', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cj5gkLPAX4K/media/?size=m'
    },
    {
      id: 'ig-api-798',
      platform: 'instagram',
      title: 'Giveaway is Closed - Winners are @salemsloft',
      description: 'Giveaway is Closed - Winners are @salemsloft And @bohotree 🏆  October Birth Month Giveaway! This giveaway is in collaboration with @project.sundays as we both celebrate our birthdays this month. We a',
      url: 'https://www.instagram.com/reel/Cj0Xi7hg8tS/',
      embedId: 'Cj0Xi7hg8tS',
      category: 'tutorial',
      tags: ['giveaway', 'stationerylove', 'stationerygiveaway', 'journalingaddict', 'journalinggiveaway', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cj0Xi7hg8tS/media/?size=m'
    },
    {
      id: 'ig-api-799',
      platform: 'instagram',
      title: 'Autumn Pages 🍂  Super simple, before the pen, using 2 products only (Forest ...',
      description: 'Autumn Pages 🍂  Super simple, before the pen, using 2 products only (Forest Frames Foam Stamps and Autumn PET washi) 🍂  Vintage Craft Box with these products available in my shop! #autumn #autumnpag',
      url: 'https://www.instagram.com/reel/Cjv1Huzpwqt/',
      embedId: 'Cjv1Huzpwqt',
      category: 'box-reveal',
      tags: ['autumn', 'autumnpages', 'autumncrafts', 'autumnvibes', 'autumnjournaling', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cjv1Huzpwqt/media/?size=m'
    },
    {
      id: 'ig-api-800',
      platform: 'instagram',
      title: 'Gift wrap with me 🎁 This time I used a green craft paper as the base and the...',
      description: 'Gift wrap with me 🎁 This time I used a green craft paper as the base and the Forest Frames foam stamps from my October \'Forest\' Vintage Craft Box 📦 ❤️  #gift #giftideas #giftwrappingideas #giftwrapp',
      url: 'https://www.instagram.com/reel/CjqFlUTAe-f/',
      embedId: 'CjqFlUTAe-f',
      category: 'tutorial',
      tags: ['gift', 'giftideas', 'giftwrappingideas', 'giftwrapping', 'diygiftideas', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2022-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CjqFlUTAe-f/media/?size=m'
    },
    {
      id: 'ig-api-801',
      platform: 'instagram',
      title: 'What\'s in your secret garden? 🌱 Botanical pages in my new \'Old Book\'s Journa...',
      description: 'What\'s in your secret garden? 🌱 Botanical pages in my new \'Old Book\'s Journal, using the Botanical Background rice paper and the Botanical Foam Stamps - all available in my shop 💚 #secretgarden #jar',
      url: 'https://www.instagram.com/reel/Cjk8dGbgVSz/',
      embedId: 'Cjk8dGbgVSz',
      category: 'journal-spread',
      tags: ['secretgarden', 'jardinsecret', 'oldbookjournal', 'journalspread', 'journalinginspiration', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cjk8dGbgVSz/media/?size=m'
    },
    {
      id: 'ig-api-802',
      platform: 'instagram',
      title: 'October \'Forest\' Box sneak peak 🌳🍁🐿️ boxes will start shipping this week ...',
      description: 'October \'Forest\' Box sneak peak 🌳🍁🐿️ boxes will start shipping this week 📦 you can still place your order on our website, link in bio!  #subscriptionbox #subbox #journalsubscription #journalcommun',
      url: 'https://www.instagram.com/reel/CjiSKHLgf5E/',
      embedId: 'CjiSKHLgf5E',
      category: 'box-reveal',
      tags: ['subscriptionbox', 'subbox', 'journalsubscription', 'journalcommunity', 'journalingshop', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CjiSKHLgf5E/media/?size=m'
    },
    {
      id: 'ig-api-803',
      platform: 'instagram',
      title: 'Advent Calendar ✨ Revealing a few days, don\'t watch if you want the full surp...',
      description: 'Advent Calendar ✨ Revealing a few days, don\'t watch if you want the full surprise 🫢 The Advent Calendar includes  washi tape sets, PET washi tapes, foil washi tape, wooden stamp, clear stamps, paper ',
      url: 'https://www.instagram.com/reel/CjanEYLAyVx/',
      embedId: 'CjanEYLAyVx',
      category: 'box-reveal',
      tags: ['adventcalendar', 'stationerystore', 'stationerycalendar', 'advent', 'christmas', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CjanEYLAyVx/media/?size=m'
    },
    {
      id: 'ig-api-804',
      platform: 'instagram',
      title: 'Autumn Magic ✨🍁 Tags made using the supplies from the upcoming October \'Fore...',
      description: 'Autumn Magic ✨🍁 Tags made using the supplies from the upcoming October \'Forest\' Vintage Craft Box 📦 * Postal Forest Clear Stamps * Autumn Transparent Washi Tape (* Forest Frames Labels - extra gift ',
      url: 'https://www.instagram.com/reel/CjVctBUg5FS/',
      embedId: 'CjVctBUg5FS',
      category: 'tutorial',
      tags: ['autumnjournal', 'autumnstickers', 'autumnstamps', 'autumnleaves', 'autumn', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CjVctBUg5FS/media/?size=m'
    },
    {
      id: 'ig-api-805',
      platform: 'instagram',
      title: 'Make a Shadow Box with me 🖤🕸️ Long version with walkthrough available on my...',
      description: 'Make a Shadow Box with me 🖤🕸️ Long version with walkthrough available on my YouTube channel, link in bio!  #shadowbox #shadowboxing #halloween #halloweenshadowbox #halloweenjournaling #halloweencraf',
      url: 'https://www.instagram.com/reel/CjQQjeWAdan/',
      embedId: 'CjQQjeWAdan',
      category: 'tutorial',
      tags: ['shadowbox', 'shadowboxing', 'halloween', 'halloweenshadowbox', 'halloweenjournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CjQQjeWAdan/media/?size=m'
    },
    {
      id: 'ig-api-806',
      platform: 'instagram',
      title: 'Halloween Bundles Presentation! Thank you for joining us on this live 🖤🙏🕸️',
      description: 'Halloween Bundles Presentation! Thank you for joining us on this live 🖤🙏🕸️',
      url: 'https://www.instagram.com/tv/CjLi5Z6IdFd/',
      embedId: '',
      category: 'product-showcase',
      tags: ['halloween'],
      creator: 'CoraCreaCrafts',
      date: '2022-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-807',
      platform: 'instagram',
      title: 'Who\'s ready for October? 🍁🌰',
      description: 'Who\'s ready for October? 🍁🌰 Finished my monthly planner pages just in time ☺️ I used the Autumn Forest transfer stickers to decorate, and the stunning Ephemera and Small Pouches in Burgundy by @via.',
      url: 'https://www.instagram.com/reel/CjIii3ig2WK/',
      embedId: 'CjIii3ig2WK',
      category: 'journal-spread',
      tags: ['hellooctober', 'octoberjournal', 'octoberplanner', 'monthlyplanner', 'planner', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CjIii3ig2WK/media/?size=m'
    },
    {
      id: 'ig-api-808',
      platform: 'instagram',
      title: 'Provence Lavender Pages 💜 They are BACK on my website! PET washi, background...',
      description: 'Provence Lavender Pages 💜 They are BACK on my website! PET washi, background washi + some stamps, transfer stickers and post cards!  This is my entry to the beautiful #washibgchallengebyhazel @thehaz',
      url: 'https://www.instagram.com/reel/CjDWOtvgwxi/',
      embedId: 'CjDWOtvgwxi',
      category: 'journal-spread',
      tags: ['washibgchallengebyhazel', 'journalinginspiration', 'journaling', 'journalingshop', 'journalcommunity', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CjDWOtvgwxi/media/?size=m'
    },
    {
      id: 'ig-api-809',
      platform: 'instagram',
      title: 'Stamping your own wrapping paper is so satisfying 🌲So simple and gives a rea...',
      description: 'Stamping your own wrapping paper is so satisfying 🌲So simple and gives a really personal touch to gifts! I used the Line Trees Clear Stamps and the B&W Labels Roll to decorate it 🎁 Both products wer',
      url: 'https://www.instagram.com/reel/Ci-Ndqxgn3R/',
      embedId: 'Ci-Ndqxgn3R',
      category: 'tutorial',
      tags: ['craftideas', 'autumncrafts', 'autumn', 'autumnstamps', 'stampingart', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2022-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Ci-Ndqxgn3R/media/?size=m'
    },
    {
      id: 'ig-api-810',
      platform: 'instagram',
      title: 'In the mood for spooky journaling pages 🖤🕸️ anyone else',
      description: 'In the mood for spooky journaling pages 🖤🕸️ anyone else?  All the products used are part of the Vintage Halloween Bundle available on the website 🎃 link in bio  #halloween #halloweenjournal #spooky',
      url: 'https://www.instagram.com/reel/Ci5MWzcgwP4/',
      embedId: 'Ci5MWzcgwP4',
      category: 'journal-spread',
      tags: ['halloween', 'halloweenjournal', 'spookyjournal', 'spookypages', 'halloweenjournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Ci5MWzcgwP4/media/?size=m'
    },
    {
      id: 'ig-api-811',
      platform: 'instagram',
      title: 'Medieval Headdress Styles 🖤 Simple pages in my Mini Red Leather Journal - So...',
      description: 'Medieval Headdress Styles 🖤 Simple pages in my Mini Red Leather Journal - Sometimes a few minutes with stamp + piece of paper is enough to make satisfying spreads!  #medievalstamp #medievalheaddress ',
      url: 'https://www.instagram.com/reel/CizzFaPAqe6/',
      embedId: 'CizzFaPAqe6',
      category: 'journal-spread',
      tags: ['medievalstamp', 'medievalheaddress', 'headdress', 'medievalpages', 'medievaljournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CizzFaPAqe6/media/?size=m'
    },
    {
      id: 'ig-api-812',
      platform: 'instagram',
      title: 'Wax Seal Love ❤️',
      description: 'Wax Seal Love ❤️ #waxseal #waxsealstamp #waxsealart #waxsealshop #autumn #autumnwaxseal #autumncrafts #craftingshop #vintagecraftideas #snailmailideas #vintagemail #vintagecraftshop #waxsealtags #vint',
      url: 'https://www.instagram.com/reel/CisuHtKp7VU/',
      embedId: 'CisuHtKp7VU',
      category: 'product-showcase',
      tags: ['waxseal', 'waxsealstamp', 'waxsealart', 'waxsealshop', 'autumn', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2022-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CisuHtKp7VU/media/?size=m'
    },
    {
      id: 'ig-api-813',
      platform: 'instagram',
      title: 'A little taste of Spain 🍋 Journaling with my Vintage Notebook & the stunning...',
      description: 'A little taste of Spain 🍋 Journaling with my Vintage Notebook & the stunning Travel Companion Bag from @skybambi! Tried something different for this video, hope you enjoy the summer vibes 💚 #journal',
      url: 'https://www.instagram.com/reel/CimvjzRolFL/',
      embedId: 'CimvjzRolFL',
      category: 'product-showcase',
      tags: ['journalinspiration', 'journaling', 'journalingsupplies', 'summerjournal', 'summerstationery', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CimvjzRolFL/media/?size=m'
    },
    {
      id: 'ig-api-814',
      platform: 'instagram',
      title: 'Upcycled Box 🖤 Using Chess Rice Paper and Clear Stamps to give a new life to...',
      description: 'Upcycled Box 🖤 Using Chess Rice Paper and Clear Stamps to give a new life to my @scribble_and_dot planner box! Perfect size to keep my papers!  #upcycling #upcycledbox #diycrafts #diybox #diy #crafti',
      url: 'https://www.instagram.com/reel/Ciht4yEIXhh/',
      embedId: 'Ciht4yEIXhh',
      category: 'tutorial',
      tags: ['upcycling', 'upcycledbox', 'diycrafts', 'diybox', 'diy', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2022-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Ciht4yEIXhh/media/?size=m'
    },
    {
      id: 'ig-api-815',
      platform: 'instagram',
      title: 'Restocked! Vintage Fashion Box ✂️Link in bio. Journal Pages 🖤 Loved to play ...',
      description: 'Restocked! Vintage Fashion Box ✂️Link in bio. Journal Pages 🖤 Loved to play with the frames papers and haute couture ladies stickers ✂️  #vintagecraftshop #vintagecraftsupplies #craftingsubscription ',
      url: 'https://www.instagram.com/reel/Ciard-eoSSw/',
      embedId: 'Ciard-eoSSw',
      category: 'journal-spread',
      tags: ['vintagecraftshop', 'vintagecraftsupplies', 'craftingsubscription', 'crafting', 'journalinspiration', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Ciard-eoSSw/media/?size=m'
    },
    {
      id: 'ig-api-816',
      platform: 'instagram',
      title: 'Fashion Art Journal Pages 🖤✂️ These were such a pleasure to make! Link in bi...',
      description: 'Fashion Art Journal Pages 🖤✂️ These were such a pleasure to make! Link in bio to sign up for this box!  #fashionstickers #fashion #vintagefashion #fashionart #fashionwashitape #foilwashitape #hauteco',
      url: 'https://www.instagram.com/reel/CiU7TJUoHbv/',
      embedId: 'CiU7TJUoHbv',
      category: 'journal-spread',
      tags: ['fashionstickers', 'fashion', 'vintagefashion', 'fashionart', 'fashionwashitape', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CiU7TJUoHbv/media/?size=m'
    },
    {
      id: 'ig-api-817',
      platform: 'instagram',
      title: 'September Vintage Fashion 🧷✂️ Box Preview 🖤 For all of you who enjoy the el...',
      description: 'September Vintage Fashion 🧷✂️ Box Preview 🖤 For all of you who enjoy the elegance of past fashion and Victorian style sewing accessories! This month, we have a limited number of boxes left for new s',
      url: 'https://www.instagram.com/reel/CiQDMs8IiF5/',
      embedId: 'CiQDMs8IiF5',
      category: 'box-reveal',
      tags: ['victorianfashion', 'vintagefashion', 'fashion', 'foilwashitape', 'hautecouturesketches', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CiQDMs8IiF5/media/?size=m'
    },
    {
      id: 'ig-api-818',
      platform: 'instagram',
      title: '📖 GIVEAWAY CLOSED 📖 for Read A Book Day',
      description: '📖 GIVEAWAY CLOSED 📖 for Read A Book Day  Spend some time enjoying a good book today for National Read A Book Day 🤗 This giveaway is a collaboration with @rubberstamps_com and we are bringing some e',
      url: 'https://www.instagram.com/reel/CiKqp2Voa0H/',
      embedId: 'CiKqp2Voa0H',
      category: 'tutorial',
      tags: ['giveaway', 'stationerygiveaway', 'stamping', 'exclusivestamps', 'stampgiveaway', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2022-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CiKqp2Voa0H/media/?size=m'
    },
    {
      id: 'ig-api-819',
      platform: 'instagram',
      title: 'Mushroom Season is starting 🍄 fun fact, I absolutely hate mushrooms (to eat)...',
      description: 'Mushroom Season is starting 🍄 fun fact, I absolutely hate mushrooms (to eat) but love using them in my crafts and journals ✂️😂 I have some new Autumn arrivals in my shop! Check it out!  #autumnpages',
      url: 'https://www.instagram.com/reel/CiAfcmjgEHM/',
      embedId: 'CiAfcmjgEHM',
      category: 'journal-spread',
      tags: ['autumnpages', 'autumnvibes', 'autumn', 'autumnjournal', 'fallspread', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CiAfcmjgEHM/media/?size=m'
    },
    {
      id: 'ig-api-820',
      platform: 'instagram',
      title: 'Vintage Fashion Tags 🧵🧷 Created with Measuring Washi Tape + Clear Stamps fr...',
      description: 'Vintage Fashion Tags 🧵🧷 Created with Measuring Washi Tape + Clear Stamps from the September Vintage Fashion Box! Limited availability, you can get yours in the link in my bio 😉 #fashion #vintagefas',
      url: 'https://www.instagram.com/reel/Ch7VBHyApDJ/',
      embedId: 'Ch7VBHyApDJ',
      category: 'tutorial',
      tags: ['fashion', 'vintagefashion', 'victorianfashiom', 'sewing', 'vintagesewing', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Ch7VBHyApDJ/media/?size=m'
    },
    {
      id: 'ig-api-821',
      platform: 'instagram',
      title: 'Because it\'s almost September 🍂 Something a little different today! 🥰 I use...',
      description: 'Because it\'s almost September 🍂 Something a little different today! 🥰 I used to be into planning and bullet journaling much more and I\'m trying to get back into it. This is my first page in my new @',
      url: 'https://www.instagram.com/reel/Ch2K6szAndq/',
      embedId: 'Ch2K6szAndq',
      category: 'product-showcase',
      tags: ['planner', 'planneraddict', 'plannercommunity', 'planning', 'planners', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Ch2K6szAndq/media/?size=m'
    },
    {
      id: 'ig-api-822',
      platform: 'instagram',
      title: 'Wax Seal Clusters with the latest additions to the shop 🖤 Which one is your ...',
      description: 'Wax Seal Clusters with the latest additions to the shop 🖤 Which one is your favorite? I love the white Honey Bee on the transparent background 🐝 #waxseals #waxseal #waxsealstamp #beewaxseal #waxseal',
      url: 'https://www.instagram.com/reel/ChpTnPmgrSI/',
      embedId: 'ChpTnPmgrSI',
      category: 'product-showcase',
      tags: ['waxseals', 'waxseal', 'waxsealstamp', 'beewaxseal', 'waxsealart', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/ChpTnPmgrSI/media/?size=m'
    },
    {
      id: 'ig-api-823',
      platform: 'instagram',
      title: 'My entry for the  hosted by @dream.n.journal 🖤 these little tags were so muc...',
      description: 'My entry for the #3dtagchallenge hosted by @dream.n.journal 🖤 these little tags were so much fun to make! I used the new Frames Notepads as a base for the tags, the Transparent Frames & Foil Papers f',
      url: 'https://www.instagram.com/reel/ChkIjhpAvKl/',
      embedId: 'ChkIjhpAvKl',
      category: 'tutorial',
      tags: ['3dtagchallenge', 'tagmaking', 'tags', 'diytags', 'diycrafts', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/ChkIjhpAvKl/media/?size=m'
    },
    {
      id: 'ig-api-824',
      platform: 'instagram',
      title: 'New Challenge 🌟 🌟 I teamed up with @peaceofpapershop to create this fun cha...',
      description: 'New Challenge 🌟#slideopenjournalchallenge 🌟 I teamed up with @peaceofpapershop to create this fun challenge! Check out her post to see another version of it. The challenge consists in creating a spr',
      url: 'https://www.instagram.com/reel/Chciu_zAlQv/',
      embedId: 'Chciu_zAlQv',
      category: 'journal-spread',
      tags: ['slideopenjournalchallenge', 'slideopenjournalchallenge', 'journalinspiration', 'journaling', 'journalingsupplies', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Chciu_zAlQv/media/?size=m'
    },
    {
      id: 'ig-api-825',
      platform: 'instagram',
      title: '🖤 GIVEAWAY CLOSED 🖤',
      description: '🖤 GIVEAWAY CLOSED 🖤 ✨ To celebrate our long term design collaboration @storm_sparrow & @coracreacrafts we are hosting a giveaway with some of our store products and/or shared designs! ✨ There will b',
      url: 'https://www.instagram.com/reel/ChZ0uqKAfS8/',
      embedId: 'ChZ0uqKAfS8',
      category: 'tutorial',
      tags: ['giveaway', 'stationeryshop', 'stationerygiveaway', 'washi tape', 'stickers'],
      creator: 'CoraCreaCrafts',
      date: '2022-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/ChZ0uqKAfS8/media/?size=m'
    },
    {
      id: 'ig-api-826',
      platform: 'instagram',
      title: 'My entry for the  🌲🍁🐇 Went for some Fall vibes with the Red Floral Pattern...',
      description: 'My entry for the #patchworkclusterchallenge 🌲🍁🐇 Went for some Fall vibes with the Red Floral Pattern Papers from my shop and some cute animals from the @johnderiancompany sticker book + little stam',
      url: 'https://www.instagram.com/reel/ChSMGDogXF8/',
      embedId: 'ChSMGDogXF8',
      category: 'journal-spread',
      tags: ['patchworkclusterchallenge', 'patchwork', 'paperpatchwork', 'papercrafts', 'collage', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/ChSMGDogXF8/media/?size=m'
    },
    {
      id: 'ig-api-827',
      platform: 'instagram',
      title: 'Organize my Desk with Me 😆 This morning I spent some time cleaning and reorg...',
      description: 'Organize my Desk with Me 😆 This morning I spent some time cleaning and reorganizing my desk! It was much needed and I can\'t wait to be creative in my space again ❤️  #desksetup #deskdecor #deskorgani',
      url: 'https://www.instagram.com/reel/ChNAtNIg7-R/',
      embedId: 'ChNAtNIg7-R',
      category: 'tutorial',
      tags: ['desksetup', 'deskdecor', 'deskorganization', 'deak', 'deskgoals', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/ChNAtNIg7-R/media/?size=m'
    },
    {
      id: 'ig-api-828',
      platform: 'instagram',
      title: 'Mini Boho Cards 🦋 Loved to create little packs of them with the brads holdin...',
      description: 'Mini Boho Cards 🦋 Loved to create little packs of them with the brads holding them together! All supplies are part of the \'Boho\' Vintage Craft Box, available in my shop 😍 #minicards #miniclusters #c',
      url: 'https://www.instagram.com/reel/ChKTufAg-fW/',
      embedId: 'ChKTufAg-fW',
      category: 'behind-the-scenes',
      tags: ['minicards', 'miniclusters', 'coracreacrafts', 'minijournaling', 'pins', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/ChKTufAg-fW/media/?size=m'
    },
    {
      id: 'ig-api-829',
      platform: 'instagram',
      title: 'Secret Collage in my Book 🦋📖 Let\'s make this a challenge! ! To participate,...',
      description: 'Secret Collage in my Book 🦋📖 Let\'s make this a challenge! #tunnelbookchallenge! To participate, just find an old book you\'re not using anymore and create layers through it, then decorate! I was so i',
      url: 'https://www.instagram.com/reel/ChCsGE0g53W/',
      embedId: 'ChCsGE0g53W',
      category: 'product-showcase',
      tags: ['tunnelbookchallenge', 'tunnelbook', 'tunnelbookchallenge', 'coracreacrafts', 'craftingideas', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/ChCsGE0g53W/media/?size=m'
    },
    {
      id: 'ig-api-830',
      platform: 'instagram',
      title: 'August Boho Box Preview 🦋 Link in bio to get yours',
      description: 'August Boho Box Preview 🦋 Link in bio to get yours!   #bohostamps #bohostationery #bohowashitape #bohostyle #bohopapers #boho #augustbox #augusttheme #vintagecraftbox #statiomerybox #stationerysubscr',
      url: 'https://www.instagram.com/reel/ChAElurAznr/',
      embedId: 'ChAElurAznr',
      category: 'box-reveal',
      tags: ['bohostamps', 'bohostationery', 'bohowashitape', 'bohostyle', 'bohopapers', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/ChAElurAznr/media/?size=m'
    },
    {
      id: 'ig-api-831',
      platform: 'instagram',
      title: 'One Window Two Views 🖤📖 Another fun challenge to end the week',
      description: 'One Window Two Views 🖤📖 Another fun challenge to end the week #onewindowtwoviewschallenge  This spread was quite a lot of work, more than it looks but it was so much fun to make! You can see the lon',
      url: 'https://www.instagram.com/reel/Cg4poLpA856/',
      embedId: 'Cg4poLpA856',
      category: 'journal-spread',
      tags: ['onewindowtwoviewschallenge', 'journalingsupplies', 'journalingshop', 'journalinspiration', 'journaling', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cg4poLpA856/media/?size=m'
    },
    {
      id: 'ig-api-832',
      platform: 'instagram',
      title: 'My entry to the  🖤🌺 Secret Gate in my journal! I enjoyed making this so muc...',
      description: 'My entry to the #gatefoldspreadchallenge 🖤🌺 Secret Gate in my journal! I enjoyed making this so much 🥰 hosted by @fra.key02 and @bkpaperstories  #gatefold #journalinspiration #journalgate #journald',
      url: 'https://www.instagram.com/reel/CgwqtecARCV/',
      embedId: 'CgwqtecARCV',
      category: 'journal-spread',
      tags: ['gatefoldspreadchallenge', 'gatefold', 'journalinspiration', 'journalgate', 'journaldoors', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CgwqtecARCV/media/?size=m'
    },
    {
      id: 'ig-api-833',
      platform: 'instagram',
      title: 'Medieval Pages 🖤 I haven\'t done a Medieval themed spread in a while, I misse...',
      description: 'Medieval Pages 🖤 I haven\'t done a Medieval themed spread in a while, I missed it! I used 3 products only for this spread *Fleur de Lys Foil Washi Tape *Medieval Oddities Washi Tape * Medieval Stamp S',
      url: 'https://www.instagram.com/reel/Cgt-6lfAXfo/',
      embedId: 'Cgt-6lfAXfo',
      category: 'journal-spread',
      tags: ['medieval', 'medievaltimes', 'medievaltheme', 'medievalpages', 'medievalspread', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cgt-6lfAXfo/media/?size=m'
    },
    {
      id: 'ig-api-834',
      platform: 'instagram',
      title: 'Boho inspired journal page 💞 I used papers, stamps and washi tape from the u...',
      description: 'Boho inspired journal page 💞 I used papers, stamps and washi tape from the upcoming August Boho Vintage Craft Box 🌺 Link in bio! This spread style was inspired by the beautiful journal pages from @p',
      url: 'https://www.instagram.com/reel/CgpCyoCgzqq/',
      embedId: 'CgpCyoCgzqq',
      category: 'journal-spread',
      tags: ['collageart', 'collagejournal', 'artjournaling', 'artjournal', 'clearstamps', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CgpCyoCgzqq/media/?size=m'
    },
    {
      id: 'ig-api-835',
      platform: 'instagram',
      title: 'Wax Seal Live Session with @kathrynhastingsco thank you so much for joining! ...',
      description: 'Wax Seal Live Session with @kathrynhastingsco thank you so much for joining! We talked about Antique Wax Seals and techniques for making wax seals. #waxsealvideo #waxsealart #waxsealing #waxsealstamp ',
      url: 'https://www.instagram.com/tv/CgnBw4Jpxf4/',
      embedId: '',
      category: 'product-showcase',
      tags: ['waxsealvideo', 'waxsealart', 'waxsealing', 'waxsealstamp', 'antiquewaxseal'],
      creator: 'CoraCreaCrafts',
      date: '2022-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-836',
      platform: 'instagram',
      title: 'Travel Journal Flip Through of my early July Road Trip with my family 🚙🧭⚓ W...',
      description: 'Travel Journal Flip Through of my early July Road Trip with my family 🚙🧭⚓ We left from Chicago and drove all the way North to Lake Superior along Lake Michigan coast and then back down through Wisco',
      url: 'https://www.instagram.com/reel/Cgj7w53gfdR/',
      embedId: 'Cgj7w53gfdR',
      category: 'journal-spread',
      tags: ['traveljournal', 'traveljournaling', 'flipthrough', 'travelersnotebook', 'travelcompanion', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cgj7w53gfdR/media/?size=m'
    },
    {
      id: 'ig-api-837',
      platform: 'instagram',
      title: 'Boho Envelopes 🌼 Here\'s a little preview of two products that are part of th...',
      description: 'Boho Envelopes 🌼 Here\'s a little preview of two products that are part of the August Boho Box: Pattern Stencil and Mandala Transparent Washi Tape 🌺🙊 More sneak peaks coming soon! The boxes are now ',
      url: 'https://www.instagram.com/reel/CghKqergxtp/',
      embedId: 'CghKqergxtp',
      category: 'box-reveal',
      tags: ['boho', 'bohostyle', 'worldcultures', 'mandala', 'mandalawashi', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CghKqergxtp/media/?size=m'
    },
    {
      id: 'ig-api-838',
      platform: 'instagram',
      title: 'Rice Paper Tags 💙 Made these blue tags with the Blue Matchbox Textured Paper...',
      description: 'Rice Paper Tags 💙 Made these blue tags with the Blue Matchbox Textured Papers and the Blue Garden Rice Paper 🤗 #ricepapercrafts #ricepapertags #tags #tagmaking #vintagestyle #vintagetags #diytags #d',
      url: 'https://www.instagram.com/reel/Cgen8uKgkem/',
      embedId: 'Cgen8uKgkem',
      category: 'tutorial',
      tags: ['ricepapercrafts', 'ricepapertags', 'tags', 'tagmaking', 'vintagestyle', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2022-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cgen8uKgkem/media/?size=m'
    },
    {
      id: 'ig-api-839',
      platform: 'instagram',
      title: 'Wax Seal making with the Queen Bee Wax Seal 🐝 just added today, it\'s the new...',
      description: 'Wax Seal making with the Queen Bee Wax Seal 🐝 just added today, it\'s the newest addition to the wax seal collection in the shop! 🥰 Which one is your favorite? Mine is the light gray and brown 🤎 #wa',
      url: 'https://www.instagram.com/reel/CgUucxNg2zD/',
      embedId: 'CgUucxNg2zD',
      category: 'product-showcase',
      tags: ['waxseals', 'waxseal', 'waxsealstamp', 'beewaxseal', 'waxsealart', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CgUucxNg2zD/media/?size=m'
    },
    {
      id: 'ig-api-840',
      platform: 'instagram',
      title: 'Provence Pages in my Vintage Leather Journal 💙💠 I used the wooden stamp set...',
      description: 'Provence Pages in my Vintage Leather Journal 💙💠 I used the wooden stamp sets, stickers and washi tapes from the July Vintage Craft Box. There are a few boxes left in my shop, link in bio 📖 #journal',
      url: 'https://www.instagram.com/reel/CgPKT8Dgz4k/',
      embedId: 'CgPKT8Dgz4k',
      category: 'journal-spread',
      tags: ['journalinspiration', 'journalpages', 'journalingcommunity', 'journalspread', 'provencewashitape', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CgPKT8Dgz4k/media/?size=m'
    },
    {
      id: 'ig-api-841',
      platform: 'instagram',
      title: 'Forest themed spread for National Forest Week 🌲 I used a whole bunch of stic...',
      description: 'Forest themed spread for National Forest Week 🌲 I used a whole bunch of stickers from the green/blue transparent sticker book, green floral pattern papers and winter forest stamps 📖  #greenpages #gr',
      url: 'https://www.instagram.com/reel/CgJ4lrAAGJ_/',
      embedId: 'CgJ4lrAAGJ_',
      category: 'journal-spread',
      tags: ['greenpages', 'greentheme', 'greenjournal', 'forestpages', 'forest', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CgJ4lrAAGJ_/media/?size=m'
    },
    {
      id: 'ig-api-842',
      platform: 'instagram',
      title: 'Last call for the June/July Treasure Boxes! We got a restock of 10 so if you ...',
      description: 'Last call for the June/July Treasure Boxes! We got a restock of 10 so if you want to get a hold of the Summer Mini Leather Set this is your last chance 🧡✂️ #treasurebox #leatherbag #minileatherbag #m',
      url: 'https://www.instagram.com/reel/CgCiQIhA3TP/',
      embedId: 'CgCiQIhA3TP',
      category: 'journal-spread',
      tags: ['treasurebox', 'leatherbag', 'minileatherbag', 'minileatherset', 'minipouch', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CgCiQIhA3TP/media/?size=m'
    },
    {
      id: 'ig-api-843',
      platform: 'instagram',
      title: 'Lavender Transparent Tags 💜 Used all 3 washi tapes that come in the July \'So...',
      description: 'Lavender Transparent Tags 💜 Used all 3 washi tapes that come in the July \'South of France\' Box 🤗 Boxes will start shipping end of the week. You can still order yours on my website!  #transparentwash',
      url: 'https://www.instagram.com/reel/Cf6nt-ZAYst/',
      embedId: 'Cf6nt-ZAYst',
      category: 'tutorial',
      tags: ['transparentwashitape', 'transparenttags', 'tags', 'diytags', 'tagmaking', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cf6nt-ZAYst/media/?size=m'
    },
    {
      id: 'ig-api-844',
      platform: 'instagram',
      title: 'Nautical Pages ⛵ This is such a good fit for my current trip around the Great...',
      description: 'Nautical Pages ⛵ This is such a good fit for my current trip around the Great Lakes! I used the At Sea Rice Paper, Da Vinci Clear Stamp and Nautical Clear Stamp and Nautical Sticker Sheet from my shop',
      url: 'https://www.instagram.com/reel/CfyyPt8gv3U/',
      embedId: 'CfyyPt8gv3U',
      category: 'journal-spread',
      tags: ['ricepaper', 'ricepapercrafts', 'ricepaperjournal', 'journalpages', 'nauticalpages', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CfyyPt8gv3U/media/?size=m'
    },
    {
      id: 'ig-api-845',
      platform: 'instagram',
      title: 'My entry for the  🥰 I really enjoyed playing with layers of stamps and ink c...',
      description: 'My entry for the #StampOnlyChallenge 🥰 I really enjoyed playing with layers of stamps and ink colors! This was so much fun.  Stamps used from my shop: 🧡 Pattern Clear Stamp 🧡 Toppers Clear Stamps ',
      url: 'https://www.instagram.com/reel/CftovoyAbVA/',
      embedId: 'CftovoyAbVA',
      category: 'journal-spread',
      tags: ['stamponlychallenge', 'stampshop', 'stampinup', 'stamping', 'stampcollection', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CftovoyAbVA/media/?size=m'
    },
    {
      id: 'ig-api-846',
      platform: 'instagram',
      title: 'July Box \'South of France\' sneak peak 💜🇫🇷 I\'m really excited about this th...',
      description: 'July Box \'South of France\' sneak peak 💜🇫🇷 I\'m really excited about this theme. The transparent washi tape is my favorite! Box is available for order now on my website ☺️ #julybox #southoffrance #pr',
      url: 'https://www.instagram.com/reel/CfpHMvpphMW/',
      embedId: 'CfpHMvpphMW',
      category: 'box-reveal',
      tags: ['julybox', 'southoffrance', 'provence', 'provencetheme', 'lavender', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CfpHMvpphMW/media/?size=m'
    },
    {
      id: 'ig-api-847',
      platform: 'instagram',
      title: 'Early Happy 4th of July to all 🇺🇲 out here 🎉 Special Sale is now on in my ...',
      description: 'Early Happy 4th of July to all 🇺🇲 out here 🎉 Special Sale is now on in my shop until Monday! #4thofjuly #4th #4thofjulysale #sale #specialsale #smallbusiness #smallbusinesssale #supportsmallbusines',
      url: 'https://www.instagram.com/reel/Cfg5kDngNnk/',
      embedId: 'Cfg5kDngNnk',
      category: 'product-showcase',
      tags: ['4thofjuly', '4th', '4thofjulysale', 'sale', 'specialsale', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cfg5kDngNnk/media/?size=m'
    },
    {
      id: 'ig-api-848',
      platform: 'instagram',
      title: 'Ready for vacations ☀️🧭✂️ But vacation is not right without some essential c...',
      description: 'Ready for vacations ☀️🧭✂️ But vacation is not right without some essential crafting supplies. Here\'s how I packed my new @skybambi_stationery companion bag 🖤 I\'m so happy with this purchase and can\'',
      url: 'https://www.instagram.com/reel/CfZffeCgEIA/',
      embedId: 'CfZffeCgEIA',
      category: 'behind-the-scenes',
      tags: ['travelbag', 'craftingstorage', 'craftbag', 'traveljournal', 'traveler', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CfZffeCgEIA/media/?size=m'
    },
    {
      id: 'ig-api-849',
      platform: 'instagram',
      title: 'July Box Theme Reveal 💜💙 This month\'s Vintage Craft Box will take you to So...',
      description: 'July Box Theme Reveal 💜💙 This month\'s Vintage Craft Box will take you to South of France 🇫🇷 I created these Provence inspired pages in my little art journal with the supplies from the box ☺️ New o',
      url: 'https://www.instagram.com/reel/CfTzQlagNwV/',
      embedId: 'CfTzQlagNwV',
      category: 'box-reveal',
      tags: ['southoffrance', 'suddelafrance', 'provence', 'provencal', 'provencetheme', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CfTzQlagNwV/media/?size=m'
    },
    {
      id: 'ig-api-850',
      platform: 'instagram',
      title: 'Preparing a few more pages for my upcoming trip 🧭 they were made with the Ju...',
      description: 'Preparing a few more pages for my upcoming trip 🧭 they were made with the June Explorer Box 🖤 there\'s only a few days left to subscribe and get the June Box! Link in my bio ✂️😊 #junebox #explorerth',
      url: 'https://www.instagram.com/reel/CfJrhuSgwB7/',
      embedId: 'CfJrhuSgwB7',
      category: 'box-reveal',
      tags: ['junebox', 'explorertheme', 'exploration', 'explorerstamp', 'explorerwashitape', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CfJrhuSgwB7/media/?size=m'
    },
    {
      id: 'ig-api-851',
      platform: 'instagram',
      title: 'Secret Window ✂️ NEW Keys Raised Foil Washi Tape just released in my shop thi...',
      description: 'Secret Window ✂️ NEW Keys Raised Foil Washi Tape just released in my shop this morning 🗝️ All supplies can be found in my shop: * Garden Window Wooden Stamp * Gothic Architecture Clear Stamps  * Rais',
      url: 'https://www.instagram.com/reel/CfEdW3_gwen/',
      embedId: 'CfEdW3_gwen',
      category: 'product-showcase',
      tags: ['secretgarden', 'secretwindow', 'gardenwindow', 'gardenwindowstamp', 'woodenstamps', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CfEdW3_gwen/media/?size=m'
    },
    {
      id: 'ig-api-852',
      platform: 'instagram',
      title: 'Let\'s make a Flower Bag 🌺🌸 Using old grocery paper bags! This is a super ea...',
      description: 'Let\'s make a Flower Bag 🌺🌸 Using old grocery paper bags! This is a super easy upcycling craft for the Summer 🌞 I used very few things to make it: grocery paper bag, glue, cards and washi tape (from',
      url: 'https://www.instagram.com/reel/CfB2b9zgAKO/',
      embedId: 'CfB2b9zgAKO',
      category: 'tutorial',
      tags: ['upcycled', 'upcyclingbox', 'upcyclingbag', 'upcyclingidea', 'grocerybag', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CfB2b9zgAKO/media/?size=m'
    },
    {
      id: 'ig-api-853',
      platform: 'instagram',
      title: 'Travel Journaling Live Session with @me_and_my_happydesk',
      description: 'Travel Journaling Live Session with @me_and_my_happydesk  Thank you so much for watching 🖤✂️ Here you can watch the whole session again. We are talking about our travel journals, supplies, and more! ',
      url: 'https://www.instagram.com/tv/Ce_hRSEoyf0/',
      embedId: '',
      category: 'product-showcase',
      tags: ['traveljournal', 'travelersnotebook', 'travelsupplies', 'travelsizejournal', 'traveljournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-854',
      platform: 'instagram',
      title: 'Travel Journal Pages 🖤 Join us this Sunday at 9.30AM US Central Time (or 4.3...',
      description: 'Travel Journal Pages 🖤 Join us this Sunday at 9.30AM US Central Time (or 4.30 Central European Time) to talk about travel journaling with @me_and_my_happydesk 🌎 We\'ll talk about how we got started w',
      url: 'https://www.instagram.com/reel/Ce6QjP8gtvX/',
      embedId: 'Ce6QjP8gtvX',
      category: 'journal-spread',
      tags: ['traveljournal', 'travelsupplies', 'traveljournalsupplies', 'traveljournaling', 'travelfolder', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Ce6QjP8gtvX/media/?size=m'
    },
    {
      id: 'ig-api-855',
      platform: 'instagram',
      title: 'Mini Pages in the Treasure Box Orange Mini 🧡 Journal, Paper Setand Sticker B...',
      description: 'Mini Pages in the Treasure Box Orange Mini 🧡 Journal, Paper Setand Sticker Book all from the Treasure Box. Preview is now available to watch on my YouTube channel (link in bio) 🎥.The song doesn\'t fi',
      url: 'https://www.instagram.com/reel/Ce31QyeAn6_/',
      embedId: 'Ce31QyeAn6_',
      category: 'box-reveal',
      tags: ['orangespread', 'orangejournal', 'orangepages', 'orangetheme', 'treasurebox', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Ce31QyeAn6_/media/?size=m'
    },
    {
      id: 'ig-api-856',
      platform: 'instagram',
      title: 'Specimen Ephemera 🧭🐛🪲 created with my own very first Specimen Tear Off Not...',
      description: 'Specimen Ephemera 🧭🐛🪲 created with my own very first Specimen Tear Off Notepad and Egg Sticker Strips ☺️ craft inspired by @skybambi_stationery recent specimen tags! This product is part of the Jun',
      url: 'https://www.instagram.com/reel/CezDhlHpnJD/',
      embedId: 'CezDhlHpnJD',
      category: 'tutorial',
      tags: ['specimennotepad', 'specimen', 'specimentags', 'specimens', 'specimenephemera', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CezDhlHpnJD/media/?size=m'
    },
    {
      id: 'ig-api-857',
      platform: 'instagram',
      title: 'Yellow 💛 🧡 Double window on triple pages. This is my entry for the fun chal...',
      description: 'Yellow 💛 🧡 Double window on triple pages. This is my entry for the fun challenge #journalingalldaychallenge hosted by @journalingallday ✂️ Unfortunately my camera didn\'t film any of the process 😣 s',
      url: 'https://www.instagram.com/reel/Cev-n08AAin/',
      embedId: 'Cev-n08AAin',
      category: 'journal-spread',
      tags: ['journalingalldaychallenge', 'yellowpages', 'yellowspread', 'orangepages', 'orangespread', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cev-n08AAin/media/?size=m'
    },
    {
      id: 'ig-api-858',
      platform: 'instagram',
      title: 'June "Explorer" Vintage Craft Box Preview 🖤🧭 This box is a little different...',
      description: 'June "Explorer" Vintage Craft Box Preview 🖤🧭 This box is a little different than usual: no big papers but this beautiful large tear off notepad with specimen designs, 2 extra foam stamps and 2 long ',
      url: 'https://www.instagram.com/reel/CeoQwoAAhlP/',
      embedId: 'CeoQwoAAhlP',
      category: 'box-reveal',
      tags: ['junebox', 'explorerwoodenstamp', 'exploration', 'explorerstamp', 'adventurestamp', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CeoQwoAAhlP/media/?size=m'
    },
    {
      id: 'ig-api-859',
      platform: 'instagram',
      title: 'Sneak Peek into the Summer Treasure Box (June/July Edition) 🧡💛 with these s...',
      description: 'Sneak Peek into the Summer Treasure Box (June/July Edition) 🧡💛 with these stunning handmade mini leather pieces created by @via.vee.world exclusively for CoraCreaCrafts! Each piece is full of beauti',
      url: 'https://www.instagram.com/reel/CelpFrJA5Mo/',
      embedId: 'CelpFrJA5Mo',
      category: 'box-reveal',
      tags: ['treasurebox', 'minijournal', 'miniworld', 'minileatherjournal', 'minileatherset', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CelpFrJA5Mo/media/?size=m'
    },
    {
      id: 'ig-api-860',
      platform: 'instagram',
      title: 'Easy and fun Paper Dress Collage in my journal 👗 I still need to complete th...',
      description: 'Easy and fun Paper Dress Collage in my journal 👗 I still need to complete these pages but I just couldn\'t wait to show you my version with the Gold Art Deco Box & the Art Deco Ladies Stickers ✨✨ than',
      url: 'https://www.instagram.com/reel/CeeC5JWgnTb/',
      embedId: 'CeeC5JWgnTb',
      category: 'journal-spread',
      tags: ['easypapercraft', 'funpapercrafts', 'dresspapercraft', 'dresspaper', 'paperdressvintage', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CeeC5JWgnTb/media/?size=m'
    },
    {
      id: 'ig-api-861',
      platform: 'instagram',
      title: 'Thank you for all your orders last week for the Snail Mail Special ✂️💌 I fin...',
      description: 'Thank you for all your orders last week for the Snail Mail Special ✂️💌 I finished all the envelopes with your goodies and they are being shipped your way!  #snailmailsupplies #snailmailpost #snailmai',
      url: 'https://www.instagram.com/reel/CeYwBUDgiRN/',
      embedId: 'CeYwBUDgiRN',
      category: 'behind-the-scenes',
      tags: ['snailmailsupplies', 'snailmailpost', 'snailmail', 'happymail', 'happymailsupplies', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2022-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CeYwBUDgiRN/media/?size=m'
    },
    {
      id: 'ig-api-862',
      platform: 'instagram',
      title: 'Presenting the Explorer Box Stamps (and one of the washi tapes) 🧭 The June V...',
      description: 'Presenting the Explorer Box Stamps (and one of the washi tapes) 🧭 The June Vintage Craft Box is special because it has more stamps than usual! You will get the Airship Wooden Stamp and the Exploratio',
      url: 'https://www.instagram.com/reel/CeTmv66AtCh/',
      embedId: 'CeTmv66AtCh',
      category: 'box-reveal',
      tags: ['explorer', 'explorerwoodenstamp', 'explorerstamp', 'airshipstamp', 'vintagestamps', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CeTmv66AtCh/media/?size=m'
    },
    {
      id: 'ig-api-863',
      platform: 'instagram',
      title: 'A different kind of video 😁🎥 I had so much fun trying a new style! What do ...',
      description: 'A different kind of video 😁🎥 I had so much fun trying a new style! What do you think? Should I make more like this? I\'m still improving on the editing front so ignore to blurry parts ☺️ These cute w',
      url: 'https://www.instagram.com/reel/CeOcVBYAvrk/',
      embedId: 'CeOcVBYAvrk',
      category: 'tutorial',
      tags: ['videographer', 'videoediting', 'videostyle', 'windowtags', 'transparentpaper', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CeOcVBYAvrk/media/?size=m'
    },
    {
      id: 'ig-api-864',
      platform: 'instagram',
      title: 'Vintage Slides ✂️🖤 Tried some transparent and some regular ones! Which do yo...',
      description: 'Vintage Slides ✂️🖤 Tried some transparent and some regular ones! Which do you like more? All decorated with the Stickers & Paper Book available in my shop ✨ #stickerlover #stickershop #stickerbook #s',
      url: 'https://www.instagram.com/reel/CeEp3w_pNmr/',
      embedId: 'CeEp3w_pNmr',
      category: 'tutorial',
      tags: ['stickerlover', 'stickershop', 'stickerbook', 'stickers', 'butterflies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CeEp3w_pNmr/media/?size=m'
    },
    {
      id: 'ig-api-865',
      platform: 'instagram',
      title: 'Today is the last day to subscribe and get the May Vintage Craft Box \'Art Dec...',
      description: 'Today is the last day to subscribe and get the May Vintage Craft Box \'Art Deco & Art Nouveau\' ❤️ I wasn\'t sure about posting this video as I am not satisfied with the result. But then I thought, IG is',
      url: 'https://www.instagram.com/reel/CeB56cMgtRu/',
      embedId: 'CeB56cMgtRu',
      category: 'journal-spread',
      tags: ['coracreacrafts', 'coracreacraftbox', 'vintagesupplies', 'vintagecraftstore', 'vintagecraftbox', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CeB56cMgtRu/media/?size=m'
    },
    {
      id: 'ig-api-866',
      platform: 'instagram',
      title: 'Upcycling an @eyebuydirect box 👓 using products from my shop:',
      description: 'Upcycling an @eyebuydirect box 👓 using products from my shop: ✂️ Transparent & Foil Papers ✂️ Wax Seals ✂️ Constellations Washi Tape (Black) ✂️ Unicorn Washi Tape  Thank you @glampaperstuff for the i',
      url: 'https://www.instagram.com/reel/Cd-9SNOAyDS/',
      embedId: 'Cd-9SNOAyDS',
      category: 'tutorial',
      tags: ['upcycled', 'upcycle', 'upcyclingidea', 'upcycling', 'upcyclingbox', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cd-9SNOAyDS/media/?size=m'
    },
    {
      id: 'ig-api-867',
      platform: 'instagram',
      title: '🌼 GIVEAWAY CLOSED 🌼',
      description: '🌼 GIVEAWAY CLOSED 🌼  ✨ @oliveandsunco and I have teamed up to gift you some beautiful new stationary products in celebration of Olive & Sun Co’s new bullet journal collection!  ✨ What You’ll Win fro',
      url: 'https://www.instagram.com/reel/Cd6BMS3gtCT/',
      embedId: 'Cd6BMS3gtCT',
      category: 'tutorial',
      tags: ['washi tape', 'journaling', 'stickers'],
      creator: 'CoraCreaCrafts',
      date: '2022-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cd6BMS3gtCT/media/?size=m'
    },
    {
      id: 'ig-api-868',
      platform: 'instagram',
      title: 'Don\'t throw away the back cards of the Clear Stamps! I just decided to reuse ...',
      description: 'Don\'t throw away the back cards of the Clear Stamps! I just decided to reuse one and something fun came out of it 😍 Multi-layered Window 🪟  #upcycled #upcyclingidea #backcard #clearstamps #reusable ',
      url: 'https://www.instagram.com/reel/Cdx-wEHAEO-/',
      embedId: 'Cdx-wEHAEO-',
      category: 'unboxing',
      tags: ['upcycled', 'upcyclingidea', 'backcard', 'clearstamps', 'reusable', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cdx-wEHAEO-/media/?size=m'
    },
    {
      id: 'ig-api-869',
      platform: 'instagram',
      title: 'My version of doilies clusters! I wanted to combine it with a journal spread....',
      description: 'My version of doilies clusters! I wanted to combine it with a journal spread. I used the translucent papers and newspaper from the May Art Deco/Art Nouveau Box 🥰 #doiliesclusters challenge by @fra.ke',
      url: 'https://www.instagram.com/reel/Cds8ilcAacF/',
      embedId: 'Cds8ilcAacF',
      category: 'journal-spread',
      tags: ['doiliesclusters', 'doiliescluster', 'doiliesclusterchallenge', 'journalchallenge', 'doilies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cds8ilcAacF/media/?size=m'
    },
    {
      id: 'ig-api-870',
      platform: 'instagram',
      title: 'Flower Tickets 🌷🌺 something super simple and fast to have ready pieces for ...',
      description: 'Flower Tickets 🌷🌺 something super simple and fast to have ready pieces for journals & crafts later on! Used the new Flowers Journal transfer stickers and the Gold Sticker Strips available in my shop',
      url: 'https://www.instagram.com/reel/CdnsfNiAu9C/',
      embedId: 'CdnsfNiAu9C',
      category: 'tutorial',
      tags: ['tickets', 'ephemeradiy', 'ephemerajournal', 'ephemerapieces', 'ephemera', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CdnsfNiAu9C/media/?size=m'
    },
    {
      id: 'ig-api-871',
      platform: 'instagram',
      title: 'Flip Journal Pages 🖤 After a few tries I made it 😃 Long version of the vide...',
      description: 'Flip Journal Pages 🖤 After a few tries I made it 😃 Long version of the video available on YouTube! Thank you @letras.literarias.mx for this genius ideas 😍 #flipjournal #flipjournalpages #journaleph',
      url: 'https://www.instagram.com/reel/CdgD2nAgtBp/',
      embedId: 'CdgD2nAgtBp',
      category: 'journal-spread',
      tags: ['flipjournal', 'flipjournalpages', 'journalephemera', 'journalprocess', 'journalingshop', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CdgD2nAgtBp/media/?size=m'
    },
    {
      id: 'ig-api-872',
      platform: 'instagram',
      title: 'May Box Preview 🖤 Art Nouveau & Art Deco Theme with some gold, some curves, ...',
      description: 'May Box Preview 🖤 Art Nouveau & Art Deco Theme with some gold, some curves, florals and frames!  #artnouveaucrafts #artdeco #artjournal #journalsubscription #journalephemera #journalingsupplies #jour',
      url: 'https://www.instagram.com/reel/CdbM-FCgSKi/',
      embedId: 'CdbM-FCgSKi',
      category: 'box-reveal',
      tags: ['artnouveaucrafts', 'artdeco', 'artjournal', 'journalsubscription', 'journalephemera', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CdbM-FCgSKi/media/?size=m'
    },
    {
      id: 'ig-api-873',
      platform: 'instagram',
      title: 'Tags made with the Art Deco/Art Nouveau May box supplies 🤗 I love how differ...',
      description: 'Tags made with the Art Deco/Art Nouveau May box supplies 🤗 I love how different they are from each other and beautiful in their own way. Which ones do you prefer?  Tomorrow the sneak peak of the full',
      url: 'https://www.instagram.com/reel/CdYau8wgezd/',
      embedId: 'CdYau8wgezd',
      category: 'box-reveal',
      tags: ['artdeco', 'artnouveau', 'artjournal', 'tags', 'vintagesupplies', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CdYau8wgezd/media/?size=m'
    },
    {
      id: 'ig-api-874',
      platform: 'instagram',
      title: 'New version of an upcycled box because it\'s been a while 🤗 I made this one u...',
      description: 'New version of an upcycled box because it\'s been a while 🤗 I made this one using the supplies from the current Treasure Box and filed it with casts from the molds so I have some ready to go at any ti',
      url: 'https://www.instagram.com/reel/CdVy39EApcH/',
      embedId: 'CdVy39EApcH',
      category: 'tutorial',
      tags: ['upcycled', 'upcyclingbox', 'upcycling', 'upcyclingidea', 'diycraftsupplies', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2022-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CdVy39EApcH/media/?size=m'
    },
    {
      id: 'ig-api-875',
      platform: 'instagram',
      title: 'Sneak Peek into the May Box with these mini spreads featuring one of the wash...',
      description: 'Sneak Peek into the May Box with these mini spreads featuring one of the washi tapes and the clear stamps from the Art Deco/Art Nouveau Box ⭐ #minijournal #minileatherjournal #journaling #journalingsu',
      url: 'https://www.instagram.com/reel/CdLfUxSg1pm/',
      embedId: 'CdLfUxSg1pm',
      category: 'box-reveal',
      tags: ['minijournal', 'minileatherjournal', 'journaling', 'journalingsupplies', 'miniworld', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CdLfUxSg1pm/media/?size=m'
    },
    {
      id: 'ig-api-876',
      platform: 'instagram',
      title: 'Vintage Gothic Tags 🖤 I used:',
      description: 'Vintage Gothic Tags 🖤 I used: 🌹 Cardstock Paper 🌹Artsy Paper Set from Treasure Box 🌹Vintage Gothic Clear Stamp (shop) 🌹Dark Gardens Transfer Stickers (shop) 🌹Victorian Gothic Sticker Sheet (shop',
      url: 'https://www.instagram.com/reel/CdD2qcRgNAN/',
      embedId: 'CdD2qcRgNAN',
      category: 'tutorial',
      tags: ['vintagegothic', 'victorianstickers', 'victoriantags', 'victoriangothic', 'gothicstyle', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2022-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CdD2qcRgNAN/media/?size=m'
    },
    {
      id: 'ig-api-877',
      platform: 'instagram',
      title: 'CLOSED',
      description: 'CLOSED Winners are: @bayan_planner @knittinmar_reads @bunteule @mrs_scraps This is my opportunity to say a big THANK YOU to all of you who have been supporting my creative journey and my shop. About a',
      url: 'https://www.instagram.com/reel/Cc8CSpzAH4y/',
      embedId: 'Cc8CSpzAH4y',
      category: 'tutorial',
      tags: ['journaling', 'vintage', 'stickers'],
      creator: 'CoraCreaCrafts',
      date: '2022-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cc8CSpzAH4y/media/?size=m'
    },
    {
      id: 'ig-api-878',
      platform: 'instagram',
      title: 'Forest Faux stamps 🖤 This was about 2 hours of work shortened in 45 seconds ...',
      description: 'Forest Faux stamps 🖤 This was about 2 hours of work shortened in 45 seconds 😅 All supplies used are from the current Treasure Box! Inspired by @lize.creates  recent post 🙏 #fauxstamps #stamps #post',
      url: 'https://www.instagram.com/reel/Cc5clb2gQ6k/',
      embedId: 'Cc5clb2gQ6k',
      category: 'box-reveal',
      tags: ['fauxstamps', 'stamps', 'postalstamps', 'stamping', 'stampset', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2022-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cc5clb2gQ6k/media/?size=m'
    },
    {
      id: 'ig-api-879',
      platform: 'instagram',
      title: 'First spread in the blue/green pages of my multicolor journal 💚 Retro Romant...',
      description: 'First spread in the blue/green pages of my multicolor journal 💚 Retro Romantic vibes with the products from the April/May Treasure Box 😍 #retroromance #retroromantic #romanticspread #romanticsticker',
      url: 'https://www.instagram.com/reel/CcxpXJ0AE_x/',
      embedId: 'CcxpXJ0AE_x',
      category: 'journal-spread',
      tags: ['retroromance', 'retroromantic', 'romanticspread', 'romanticstickers', 'romanticjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CcxpXJ0AE_x/media/?size=m'
    },
    {
      id: 'ig-api-880',
      platform: 'instagram',
      title: 'Decorate an envelope with me 💞 Using the April "Botanical" box supplies (flo...',
      description: 'Decorate an envelope with me 💞 Using the April "Botanical" box supplies (floral labels roll, floral patterns stickers and botanical stamps) 🌿 #envelopedecoration #envelopeart #envelopes #envelopesea',
      url: 'https://www.instagram.com/reel/CcnZE2SAo9-/',
      embedId: 'CcnZE2SAo9-',
      category: 'box-reveal',
      tags: ['envelopedecoration', 'envelopeart', 'envelopes', 'envelopeseals', 'decorateenvelope', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2022-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CcnZE2SAo9-/media/?size=m'
    },
    {
      id: 'ig-api-881',
      platform: 'instagram',
      title: 'Upcycling storage attempt n°4 🖤 I used an empty olive can to make this "King...',
      description: 'Upcycling storage attempt n°4 🖤 I used an empty olive can to make this "Kings of England" storage box! I loved using the bronze acrylic paint to finish the top and bottom on top of grit paste!  The K',
      url: 'https://www.instagram.com/reel/CciQjq1Au7A/',
      embedId: 'CciQjq1Au7A',
      category: 'box-reveal',
      tags: ['upcycled', 'upcyclingbox', 'upcycle', 'upcycledcan', 'penholder', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CciQjq1Au7A/media/?size=m'
    },
    {
      id: 'ig-api-882',
      platform: 'instagram',
      title: 'Look for the Magic ✨ Mini Pages in my Mini Brown Journal, stored in Mini Case...',
      description: 'Look for the Magic ✨ Mini Pages in my Mini Brown Journal, stored in Mini Case and Ephemera Pouch 🤎 Used some Astronomy and Celestial elements from my shop 😊 #lookforthemagic #minijournal #minileathe',
      url: 'https://www.instagram.com/reel/CcajzohANF0/',
      embedId: 'CcajzohANF0',
      category: 'journal-spread',
      tags: ['lookforthemagic', 'minijournal', 'minileatherjournal', 'minicase', 'ephemerafolder', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CcajzohANF0/media/?size=m'
    },
    {
      id: 'ig-api-883',
      platform: 'instagram',
      title: 'April Botanical Box Preview 🌿 What\'s your favorite piece? Mine is definitely...',
      description: 'April Botanical Box Preview 🌿 What\'s your favorite piece? Mine is definitely the Vintage Botanical Foam Stamps! Boxes are shipping this week if you already ordered and are still available for order o',
      url: 'https://www.instagram.com/reel/CcQOH1bABB5/',
      embedId: 'CcQOH1bABB5',
      category: 'box-reveal',
      tags: ['aprilbox', 'apriltheme', 'botanical', 'vintagebotanical', 'botanicalwashitape', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CcQOH1bABB5/media/?size=m'
    },
    {
      id: 'ig-api-884',
      platform: 'instagram',
      title: 'Spring and Summer vibes for this Garden Journal spread inspired by my visit o...',
      description: 'Spring and Summer vibes for this Garden Journal spread inspired by my visit of the beautiful gardens at Biltmore estate 🌿🌷🍅 @biltmoreestate  Using supplies from my shop: * Mixed Rustic Leather Jour',
      url: 'https://www.instagram.com/reel/CcNq7ODgs10/',
      embedId: 'CcNq7ODgs10',
      category: 'journal-spread',
      tags: ['gardenvibes', 'gardenjournal', 'floraljournal', 'journalsubscription', 'journalephemera', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CcNq7ODgs10/media/?size=m'
    },
    {
      id: 'ig-api-885',
      platform: 'instagram',
      title: 'Upcycled box attempt  using products from the April Box 🖤 featuring a transp...',
      description: 'Upcycled box attempt #3 using products from the April Box 🖤 featuring a transparent window - could also make a shadow box!  #windowbox #window #gardenwindow #gardenwindowstamp #oldgarden #ancient #bo',
      url: 'https://www.instagram.com/reel/CcF-va6gZt6/',
      embedId: 'CcF-va6gZt6',
      category: 'tutorial',
      tags: ['3', 'windowbox', 'window', 'gardenwindow', 'gardenwindowstamp', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CcF-va6gZt6/media/?size=m'
    },
    {
      id: 'ig-api-886',
      platform: 'instagram',
      title: '❤️ GIVEAWAY CLOSED ❤️',
      description: '❤️ GIVEAWAY CLOSED ❤️   Winners    @skippy_chaugunee @creativelylisamarie  I teamed up with @rubberstamps_com to bring you this big giveaway combining various stamps from both our shops! If you\'re a s',
      url: 'https://www.instagram.com/reel/Cb-OekQgdJy/',
      embedId: 'Cb-OekQgdJy',
      category: 'tutorial',
      tags: ['stationerygiveaway', 'woodenstamps', 'stampshop', 'vintage', 'giveaway'],
      creator: 'CoraCreaCrafts',
      date: '2022-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cb-OekQgdJy/media/?size=m'
    },
    {
      id: 'ig-api-887',
      platform: 'instagram',
      title: 'Vintage Botanical Mini Pages 🌿 to give you a taste of the upcoming April Bot...',
      description: 'Vintage Botanical Mini Pages 🌿 to give you a taste of the upcoming April Botanical Vintage Craft Box. This beautiful washi tape designed by @storm_sparrow will be part of the box!  * Botanical Washi ',
      url: 'https://www.instagram.com/reel/Cb7tk30A04N/',
      embedId: 'Cb7tk30A04N',
      category: 'box-reveal',
      tags: ['minijournal', 'mixedmediaart', 'minileatherjournal', 'miniredjournal', 'handmadejournal', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cb7tk30A04N/media/?size=m'
    },
    {
      id: 'ig-api-888',
      platform: 'instagram',
      title: 'Black & White Envelope Decoration 🖤',
      description: 'Black & White Envelope Decoration 🖤 I received a beautiful custom embosser from @rubberstamps_com as well as the ex libris stamp used on the left of the envelope ✉️ Can\'t wait to make more envelopes ',
      url: 'https://www.instagram.com/reel/Cbz9QQhA6sT/',
      embedId: 'Cbz9QQhA6sT',
      category: 'tutorial',
      tags: ['metalornaments', 'envelopeseals', 'envelopedecoration', 'envelopeart', 'envelopes', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Cbz9QQhA6sT/media/?size=m'
    },
    {
      id: 'ig-api-889',
      platform: 'instagram',
      title: 'Special Birthday Card using the new Transparent Foil Frames Papers from my sh...',
      description: 'Special Birthday Card using the new Transparent Foil Frames Papers from my shop 🌟 I stored them in the Vintage Brown Ephemera Pouch - this new color is also available for pre-order in my shop!  #foil',
      url: 'https://www.instagram.com/reel/CbutvDaA5s8/',
      embedId: 'CbutvDaA5s8',
      category: 'product-showcase',
      tags: ['foilpaper', 'transparentpaper', 'foil', 'holo', 'frames', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2022-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CbutvDaA5s8/media/?size=m'
    },
    {
      id: 'ig-api-890',
      platform: 'instagram',
      title: 'Unboxed my new @makeidofficial label printer and reused the box it came in to...',
      description: 'Unboxed my new @makeidofficial label printer and reused the box it came in to create a storage container for it! 🖤  #upcycled #upcyclingbox #storageideas #deskspace #deskstorage #desksetup #vintagebo',
      url: 'https://www.instagram.com/reel/CbpolaEgfNg/',
      embedId: 'CbpolaEgfNg',
      category: 'unboxing',
      tags: ['upcycled', 'upcyclingbox', 'storageideas', 'deskspace', 'deskstorage', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2022-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CbpolaEgfNg/media/?size=m'
    },
    {
      id: 'ig-api-891',
      platform: 'instagram',
      title: 'Old City Map Pages ⚜️🖤 Used a napkin showing a map of the city of Bern, the ...',
      description: 'Old City Map Pages ⚜️🖤 Used a napkin showing a map of the city of Bern, the capital of Switzerland, from 1607. Loved putting a spread together representing my home country 🇨🇭 Also used the Fleur de',
      url: 'https://www.instagram.com/reel/CbXkJoPgCU2/',
      embedId: 'CbXkJoPgCU2',
      category: 'journal-spread',
      tags: ['medievaljournal', 'medievalart', 'bern', 'bernmap', 'oldcityofbern', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CbXkJoPgCU2/media/?size=m'
    },
    {
      id: 'ig-api-892',
      platform: 'instagram',
      title: 'March "Roman & Greek Antiquity" Box Preview 🖤 it\'s finally here! Shipping st...',
      description: 'March "Roman & Greek Antiquity" Box Preview 🖤 it\'s finally here! Shipping starts today and continues into next week! Thank you for your patience ❤️ Special collaboration with@storm_sparrow who design',
      url: 'https://www.instagram.com/reel/CbNYodmAk7t/',
      embedId: 'CbNYodmAk7t',
      category: 'box-reveal',
      tags: ['vintagesupplies', 'marchsubscriptionbox', 'subboxaddict', 'subscriptionbox', 'vintagecraftsupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CbNYodmAk7t/media/?size=m'
    },
    {
      id: 'ig-api-893',
      platform: 'instagram',
      title: 'Roman Tube Boxes 🌿 More DIY upcycling storage for my desk ☺️ This time using...',
      description: 'Roman Tube Boxes 🌿 More DIY upcycling storage for my desk ☺️ This time using little tubes decorated with the March Vintage Craft Box supplies: stickers, washi tapes, papers and stamps all in the box!',
      url: 'https://www.instagram.com/reel/CbIIfLVAs6h/',
      embedId: 'CbIIfLVAs6h',
      category: 'tutorial',
      tags: ['upcyclingbox', 'upcycled', 'diycraftsupplies', 'diystorage', 'diyideas', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CbIIfLVAs6h/media/?size=m'
    },
    {
      id: 'ig-api-894',
      platform: 'instagram',
      title: 'Bug pages decorated with transfer stickers 🌿',
      description: 'Bug pages decorated with transfer stickers 🌿   From my shop: ✨ 6*9 multicolor leather journal ✨ Mystical Nature Transfer Stickers ✨ Grey Ephemera Pouch  @modest_goods @via.vee.world   #transfersticke',
      url: 'https://www.instagram.com/reel/Ca-AsyVADHu/',
      embedId: 'Ca-AsyVADHu',
      category: 'product-showcase',
      tags: ['transferstickers', 'transparentstickers', 'journalephemera', 'ephemerafolder', 'ephemerapouch', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Ca-AsyVADHu/media/?size=m'
    },
    {
      id: 'ig-api-895',
      platform: 'instagram',
      title: 'Happy Women\'s Day 💗 Be kind to yourself and remember you are unique and beau...',
      description: 'Happy Women\'s Day 💗 Be kind to yourself and remember you are unique and beautiful the way you are! To celebrate all of us today, get 15% off your order in my shop with the code WOMAN 💌 This exceptio',
      url: 'https://www.instagram.com/reel/Ca2gvzdgkzc/',
      embedId: 'Ca2gvzdgkzc',
      category: 'product-showcase',
      tags: ['happywomensday', 'celebratewomen', 'celebrate', 'craftingsubscription', 'craftingstore', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Ca2gvzdgkzc/media/?size=m'
    },
    {
      id: 'ig-api-896',
      platform: 'instagram',
      title: 'Big DIY Stationery Box 🖤 Such an easy way to embellish and organize my desk ...',
      description: 'Big DIY Stationery Box 🖤 Such an easy way to embellish and organize my desk space!  #diycraftsupplies #diybox #diyephemera #diyideas #diycrafts #organizers #diyorganizer #ephemerapieces #ephemeracraf',
      url: 'https://www.instagram.com/reel/CasDGxEg6Fp/',
      embedId: 'CasDGxEg6Fp',
      category: 'tutorial',
      tags: ['diycraftsupplies', 'diybox', 'diyephemera', 'diyideas', 'diycrafts', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CasDGxEg6Fp/media/?size=m'
    },
    {
      id: 'ig-api-897',
      platform: 'instagram',
      title: 'New mini journal pages 🖤📜',
      description: 'New mini journal pages 🖤📜 #vintagesupplies #vintagegothic #miniredjournal #redplanner #minileatherjournal #minipages #miniworld #minijournal #redjournal #minihardware #decorativemetal #decopages #vi',
      url: 'https://www.instagram.com/reel/CamwWsSFhKx/',
      embedId: 'CamwWsSFhKx',
      category: 'journal-spread',
      tags: ['vintagesupplies', 'vintagegothic', 'miniredjournal', 'redplanner', 'minileatherjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CamwWsSFhKx/media/?size=m'
    },
    {
      id: 'ig-api-898',
      platform: 'instagram',
      title: 'Antique Post 💌 I used the transparent papers and washi tape from the March V...',
      description: 'Antique Post 💌 I used the transparent papers and washi tape from the March Vintage Craft Box ✂️📜 #envelopedecoration #envelopeart #envelopes #diyenvelope #diyephemera #antiquepost #ephemerapieces #s',
      url: 'https://www.instagram.com/reel/CahkZw8A2y_/',
      embedId: 'CahkZw8A2y_',
      category: 'tutorial',
      tags: ['envelopedecoration', 'envelopeart', 'envelopes', 'diyenvelope', 'diyephemera', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CahkZw8A2y_/media/?size=m'
    },
    {
      id: 'ig-api-899',
      platform: 'instagram',
      title: 'Upcycling old jewelry boxes 🖤☺️ perfect finish with decorative metal pieces ...',
      description: 'Upcycling old jewelry boxes 🖤☺️ perfect finish with decorative metal pieces from my shop 🖤 #diybox #diycrafts #diycraftsupplies #craftstorage #craftbox #smallbox #upcycled #upcyclingbox #miniboxes #',
      url: 'https://www.instagram.com/reel/CaU8PkuAdOF/',
      embedId: 'CaU8PkuAdOF',
      category: 'tutorial',
      tags: ['diybox', 'diycrafts', 'diycraftsupplies', 'craftstorage', 'craftbox', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2022-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CaU8PkuAdOF/media/?size=m'
    },
    {
      id: 'ig-api-900',
      platform: 'instagram',
      title: 'Inspiration Pages in my journal 🖤 using the Roman Architecture stickers for ...',
      description: 'Inspiration Pages in my journal 🖤 using the Roman Architecture stickers for the upcoming March box and: * New Constellation Stamp Washi Tapes * Alchemy Manuscript Washi Tape * Vintage Text Washi Tape',
      url: 'https://www.instagram.com/reel/CaPnZdnAsuQ/',
      embedId: 'CaPnZdnAsuQ',
      category: 'box-reveal',
      tags: ['stampwashi', 'stampwashitapes', 'constellationswashitape', 'constellations', 'mythology', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CaPnZdnAsuQ/media/?size=m'
    },
    {
      id: 'ig-api-901',
      platform: 'instagram',
      title: 'First pages in my new Red Leather Mini Journal ♥️ This was so fun to make! I ...',
      description: 'First pages in my new Red Leather Mini Journal ♥️ This was so fun to make! I used, all on website: * Red Leather Journal and Lion Head from Feb/March Treasure Box * Medieval Stamp Set from January Box',
      url: 'https://www.instagram.com/reel/CaH_4H7gIs2/',
      embedId: 'CaH_4H7gIs2',
      category: 'box-reveal',
      tags: ['minilabels', 'miniworld', 'minijournal', 'minijournaling', 'miniredjournal', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CaH_4H7gIs2/media/?size=m'
    },
    {
      id: 'ig-api-902',
      platform: 'instagram',
      title: 'Planner Pages with the Victorian Gothic Washi Tape, Stickers and Chandelier S...',
      description: 'Planner Pages with the Victorian Gothic Washi Tape, Stickers and Chandelier Stamp combined with the new Green Nature Transfer Stickers 🖤 All in my shop ☺️ #planning2022 #plannerstickers #transferstic',
      url: 'https://www.instagram.com/reel/CaAPb6ygQcL/',
      embedId: 'CaAPb6ygQcL',
      category: 'product-showcase',
      tags: ['planning2022', 'plannerstickers', 'transferstickers', 'rubonstickers', 'planner', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CaAPb6ygQcL/media/?size=m'
    },
    {
      id: 'ig-api-903',
      platform: 'instagram',
      title: 'Waxperimenting ✴️🖤 Combination of wax seals and metal brads - I love the res...',
      description: 'Waxperimenting ✴️🖤 Combination of wax seals and metal brads - I love the result! Metal brads are part of the Feb/March Treasure Box 🖤 #coracreacrafts #waxperiments #waxsealshop #waxsealcrafts #waxse',
      url: 'https://www.instagram.com/reel/CZ7iOhQJBxB/',
      embedId: 'CZ7iOhQJBxB',
      category: 'tutorial',
      tags: ['coracreacrafts', 'waxperiments', 'waxsealshop', 'waxsealcrafts', 'waxsealmagic', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2022-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CZ7iOhQJBxB/media/?size=m'
    },
    {
      id: 'ig-api-904',
      platform: 'instagram',
      title: 'Victorian Gothic Pages in my journal 🖤 used various products from the Februa...',
      description: 'Victorian Gothic Pages in my journal 🖤 used various products from the February Vintage Craft Box ✴️ available in my shop 😊 #victoriankeys #vintagecards #victorianjournal #victorianephemera #victoria',
      url: 'https://www.instagram.com/reel/CZ1_so1goZZ/',
      embedId: 'CZ1_so1goZZ',
      category: 'box-reveal',
      tags: ['victoriankeys', 'vintagecards', 'victorianjournal', 'victorianephemera', 'victoriangothic', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CZ1_so1goZZ/media/?size=m'
    },
    {
      id: 'ig-api-905',
      platform: 'instagram',
      title: 'Giant Ephemera Card 🖤✴️',
      description: 'Giant Ephemera Card 🖤✴️ #ephemerafolder #ephemerapieces #ephemera #ephemeracrafts #ephemeracards #coracreacrafts #snailmailsupplies #snailmailideas #happymailideas #happymailsupplies #outgoingmail #o',
      url: 'https://www.instagram.com/reel/CZw5RGTAco0/',
      embedId: 'CZw5RGTAco0',
      category: 'tutorial',
      tags: ['ephemerafolder', 'ephemerapieces', 'ephemera', 'ephemeracrafts', 'ephemeracards', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CZw5RGTAco0/media/?size=m'
    },
    {
      id: 'ig-api-906',
      platform: 'instagram',
      title: '✴️ GIVEAWAY CLOSED ✴️ winner is @artistinresidence487',
      description: '✴️ GIVEAWAY CLOSED ✴️ winner is @artistinresidence487 @moon.mountain.pottery and I are so excited to share this giveaway collaboration as a thank you to all of you supportive people ❤️ We wanted to sh',
      url: 'https://www.instagram.com/reel/CZrirpKgrXJ/',
      embedId: 'CZrirpKgrXJ',
      category: 'tutorial',
      tags: ['stationerygiveaway', 'vintagestationery', 'giveawaygoodies', 'giveaway', 'pottery', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2022-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CZrirpKgrXJ/media/?size=m'
    },
    {
      id: 'ig-api-907',
      platform: 'instagram',
      title: 'New Mini Pages 🥰 Still trying to get better at this and this is one of my le...',
      description: 'New Mini Pages 🥰 Still trying to get better at this and this is one of my learning pages. From my shop: 🪄 Mini Journal 🪄 Alchemy Washi Tape 🪄 Floral Labels Roll NEW 🪄 Chandelier stamp (February V',
      url: 'https://www.instagram.com/reel/CZj5HcTgkro/',
      embedId: 'CZj5HcTgkro',
      category: 'box-reveal',
      tags: ['minijournal', 'mini', 'minileatherjournal', 'minipages', 'chandelierstamp', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CZj5HcTgkro/media/?size=m'
    },
    {
      id: 'ig-api-908',
      platform: 'instagram',
      title: 'February Box Preview 🖤 Victorian Gothic theme - this theme was voted for by ...',
      description: 'February Box Preview 🖤 Victorian Gothic theme - this theme was voted for by you through my stories and newsletter a few months back! Hope you enjoy the vibes of this new box 📦🧛‍♀️ #subscriptionbox ',
      url: 'https://www.instagram.com/reel/CZfIpCHIt8v/',
      embedId: 'CZfIpCHIt8v',
      category: 'box-reveal',
      tags: ['subscriptionbox', 'vintagecraftsupplies', 'vintagecraftbox', 'vintagecraftstore', 'vintagegothic', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CZfIpCHIt8v/media/?size=m'
    },
    {
      id: 'ig-api-909',
      platform: 'instagram',
      title: 'Turquoise is my favorite color 💚 Decided to play with the tissue paper from ...',
      description: 'Turquoise is my favorite color 💚 Decided to play with the tissue paper from the January Medieval Box, chalk paint AND the new transparent washi from the upcoming February Box 😍 #turquoise #turquoise',
      url: 'https://www.instagram.com/reel/CZcBD8MI-S_/',
      embedId: 'CZcBD8MI-S_',
      category: 'journal-spread',
      tags: ['turquoise', 'turquoisepaint', 'turquoisespread', 'journalspread', 'journalephemera', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CZcBD8MI-S_/media/?size=m'
    },
    {
      id: 'ig-api-910',
      platform: 'instagram',
      title: 'Wax Seal Magic 🪄✨',
      description: 'Wax Seal Magic 🪄✨ Really enjoyed creating this stop motion animation 🤩 #waxsealshop #waxsealcrafts #waxsealsaturday #waxsealmagic #pastelwax #pastelwaxseal #springwaxseal #waxseals #waxsealclip #sto',
      url: 'https://www.instagram.com/reel/CZU5e8poSuX/',
      embedId: 'CZU5e8poSuX',
      category: 'product-showcase',
      tags: ['waxsealshop', 'waxsealcrafts', 'waxsealsaturday', 'waxsealmagic', 'pastelwax'],
      creator: 'CoraCreaCrafts',
      date: '2022-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CZU5e8poSuX/media/?size=m'
    },
    {
      id: 'ig-api-911',
      platform: 'instagram',
      title: 'DIY Accordion Case 🖤 can\'t wait to store my ephemera in there! Love to be ab...',
      description: 'DIY Accordion Case 🖤 can\'t wait to store my ephemera in there! Love to be able to see what\'s in there through the "window" #accordioncase #diycraftsupplies #diyfolder #ephemerafolder #ephemerapieces ',
      url: 'https://www.instagram.com/reel/CZPXEY_o3gO/',
      embedId: 'CZPXEY_o3gO',
      category: 'tutorial',
      tags: ['accordioncase', 'diycraftsupplies', 'diyfolder', 'ephemerafolder', 'ephemerapieces', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2022-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CZPXEY_o3gO/media/?size=m'
    },
    {
      id: 'ig-api-912',
      platform: 'instagram',
      title: 'Apothecary Tags/Bookmarks with Windows 😍 I will show a demo on how to make t...',
      description: 'Apothecary Tags/Bookmarks with Windows 😍 I will show a demo on how to make these Friday on @thecraftstoretv and you\'ll also be able to purchase the products used 🖤 I so enjoyed making these after wa',
      url: 'https://www.instagram.com/reel/CZKDwnVouGO/',
      embedId: 'CZKDwnVouGO',
      category: 'tutorial',
      tags: ['bookmark', 'tags', 'vintagetags', 'vintagebookmarks', 'papers', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CZKDwnVouGO/media/?size=m'
    },
    {
      id: 'ig-api-913',
      platform: 'instagram',
      title: 'Mini Journal Pages 🖤 I\'m still a beginner at making things on such a tiny sc...',
      description: 'Mini Journal Pages 🖤 I\'m still a beginner at making things on such a tiny scale but I\'m really enjoying the process! I also love this size to travel, it makes it so much easier because I only need bi',
      url: 'https://www.instagram.com/reel/CY4MUGIImSL/',
      embedId: 'CY4MUGIImSL',
      category: 'journal-spread',
      tags: ['minijournal', 'minipages', 'vintagejournal', 'vintagesupplies', 'vintagecraftsupplies', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CY4MUGIImSL/media/?size=m'
    },
    {
      id: 'ig-api-914',
      platform: 'instagram',
      title: 'Medieval Pages ⚜️🖤 I used supplies from the January Vintage Craft Box availa...',
      description: 'Medieval Pages ⚜️🖤 I used supplies from the January Vintage Craft Box available in my shop. These sample pages are visual and written memorabilia of my ideas and inspiration for the washi tapes and s',
      url: 'https://www.instagram.com/reel/CYw3o21oXxq/',
      embedId: 'CYw3o21oXxq',
      category: 'box-reveal',
      tags: ['januarypages', 'medievaljournal', 'coracreacrafts', 'medievaltheme', 'scrapbookingideas', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CYw3o21oXxq/media/?size=m'
    },
    {
      id: 'ig-api-915',
      platform: 'instagram',
      title: 'January \'Medieval\' Box Preview 🖤🗡️ I was finally able to shoot this overvie...',
      description: 'January \'Medieval\' Box Preview 🖤🗡️ I was finally able to shoot this overview after receiving the wooden stamp set! This is the Vintage Craft box and is now available on my website ⚜️ #vintagecraftbo',
      url: 'https://www.instagram.com/reel/CYrSYSvIA7O/',
      embedId: 'CYrSYSvIA7O',
      category: 'box-reveal',
      tags: ['vintagecraftbox', 'coracreacraftbox', 'coracreacrafts', 'subboxaddict', 'journalsubscription', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CYrSYSvIA7O/media/?size=m'
    },
    {
      id: 'ig-api-916',
      platform: 'instagram',
      title: 'My wheel of themes is complete! This wheel is such a nice reminder of all the...',
      description: 'My wheel of themes is complete! This wheel is such a nice reminder of all the Vintage Craft Box themes I had for 2021! ✂️ Which one was your favorite?  ✨ Jan: Old Cuties ✨ Feb: Library ✨ Mar: Butterfl',
      url: 'https://www.instagram.com/reel/CYmK-fzoyP2/',
      embedId: 'CYmK-fzoyP2',
      category: 'box-reveal',
      tags: ['wheelofcrafts', 'craftwheel', 'vintagemail', 'outgoingvintagemail', 'wheelofthemes', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2022-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CYmK-fzoyP2/media/?size=m'
    },
    {
      id: 'ig-api-917',
      platform: 'instagram',
      title: 'Envelope decoration from this morning ✂️💌 I was inspired by the beautiful il...',
      description: 'Envelope decoration from this morning ✂️💌 I was inspired by the beautiful illustrations from the @johnderiancompany sticker book and by @skybambi recent journal post using small windows. My little wi',
      url: 'https://www.instagram.com/reel/CYb4CUToFO5/',
      embedId: 'CYb4CUToFO5',
      category: 'box-reveal',
      tags: ['envelopeart', 'envelopes', 'envelopedecoration', 'snailmailideas', 'snailmail', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2022-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CYb4CUToFO5/media/?size=m'
    },
    {
      id: 'ig-api-918',
      platform: 'instagram',
      title: 'Nostalgia Pages for the end of era and the beginning of a new one ✨',
      description: 'Nostalgia Pages for the end of era and the beginning of a new one ✨  Using the Flower Tag Stickers and the tissue paper from the previous vintage craft box in the multicolored leather journal @modest_',
      url: 'https://www.instagram.com/reel/CYEcOZmI8U9/',
      embedId: 'CYEcOZmI8U9',
      category: 'journal-spread',
      tags: ['nostalgia', 'nostalgiajournal', 'journalingsupplies', 'journalingideas', 'journalingshop', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CYEcOZmI8U9/media/?size=m'
    },
    {
      id: 'ig-api-919',
      platform: 'instagram',
      title: 'My last gift wrap of the year 🎁🎀 It\'s already that time 🎄 Merry Christmas ...',
      description: 'My last gift wrap of the year 🎁🎀 It\'s already that time 🎄 Merry Christmas everyone!  #giftwrap #giftwrapping #christmaswaxseal #christmascrafts #christmasgift #wrapping #wrappingtechnique #wrapping',
      url: 'https://www.instagram.com/reel/CX3pm83opjf/',
      embedId: 'CX3pm83opjf',
      category: 'product-showcase',
      tags: ['giftwrap', 'giftwrapping', 'christmaswaxseal', 'christmascrafts', 'christmasgift', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CX3pm83opjf/media/?size=m'
    },
    {
      id: 'ig-api-920',
      platform: 'instagram',
      title: 'Making these journal pages brought me peace ❤️',
      description: 'Making these journal pages brought me peace ❤️  Used from my shop:  * Lace washi tape * Ink bottles clear stamp * Scribe clear stamp * Vintage Sticker Book * Romantic Transfer stickers (coming soon!) ',
      url: 'https://www.instagram.com/reel/CXyiZRYA0mW/',
      embedId: 'CXyiZRYA0mW',
      category: 'journal-spread',
      tags: ['lace', 'lacewashi', 'victorianwashi', 'victorian', 'transferstickers', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CXyiZRYA0mW/media/?size=m'
    },
    {
      id: 'ig-api-921',
      platform: 'instagram',
      title: 'Chocolate Wax Seals 🍫 this was such a fun experiment to make',
      description: 'Chocolate Wax Seals 🍫 this was such a fun experiment to make!  A few observations: * Cool the metal seals with ice before each use * Put the finished seals in freezer to a while to make sure they kee',
      url: 'https://www.instagram.com/reel/CXlwsaxAWCO/',
      embedId: 'CXlwsaxAWCO',
      category: 'tutorial',
      tags: ['christmaswaxseal', 'christmasgifts', 'christmascrafts', 'christmascraftideas', 'xmasideas', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CXlwsaxAWCO/media/?size=m'
    },
    {
      id: 'ig-api-922',
      platform: 'instagram',
      title: 'Medieval inspired journal pages 🖤 Using the Gothic Architecture Clear Stamp ...',
      description: 'Medieval inspired journal pages 🖤 Using the Gothic Architecture Clear Stamp available in my shop, the Renaissance washi tape from the Treasure Box and.. sneak peak.. the Fleur de Lys Foil washi tape ',
      url: 'https://www.instagram.com/reel/CXgjYszAfM9/',
      embedId: 'CXgjYszAfM9',
      category: 'box-reveal',
      tags: ['vintagecraftsupplies', 'vintagejournal', 'vintagejournaling', 'vintagespread', 'leatherjournal', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CXgjYszAfM9/media/?size=m'
    },
    {
      id: 'ig-api-923',
      platform: 'instagram',
      title: 'Christmas Card Making 🎄🎀 I loved using my little @sizzix die cut machine ag...',
      description: 'Christmas Card Making 🎄🎀 I loved using my little @sizzix die cut machine again after such a long time and do some embossing!  #cardmaking #diycards #diychristmascards #christmascrafts #christmascard',
      url: 'https://www.instagram.com/tv/CXba9-rgj7J/',
      embedId: '',
      category: 'tutorial',
      tags: ['cardmaking', 'diycards', 'diychristmascards', 'christmascrafts', 'christmascards', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-924',
      platform: 'instagram',
      title: 'Wax Seal Twine Ribbon 🎀 to decorate my Christmas gifts 🎄 I used the new Chr...',
      description: 'Wax Seal Twine Ribbon 🎀 to decorate my Christmas gifts 🎄 I used the new Christmas Wax Seals from my shop and the Forest Wax Seal and wax pen from the current Treasure Box 📦 #waxsealshop #waxsealcra',
      url: 'https://www.instagram.com/reel/CXWVtmYgM_O/',
      embedId: 'CXWVtmYgM_O',
      category: 'box-reveal',
      tags: ['waxsealshop', 'waxsealcrafts', 'waxsealstamp', 'waxseals', 'waxsealribbons', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CXWVtmYgM_O/media/?size=m'
    },
    {
      id: 'ig-api-925',
      platform: 'instagram',
      title: 'Gift wrapping and order 🌟🎄 I enjoyed mixing two very different styles betwe...',
      description: 'Gift wrapping and order 🌟🎄 I enjoyed mixing two very different styles between the paper and the vintage washi tape I used as a ribbon 🎀 You can get gift wrapping when placing any order on my websit',
      url: 'https://www.instagram.com/reel/CXRFRWdg2GM/',
      embedId: 'CXRFRWdg2GM',
      category: 'behind-the-scenes',
      tags: ['giftideas', 'giftpackaging', 'giftwrap', 'giftwrapping', 'theartofgifting', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CXRFRWdg2GM/media/?size=m'
    },
    {
      id: 'ig-api-926',
      platform: 'instagram',
      title: 'Getting a little ahead of myself and started decorating my planner for Januar...',
      description: 'Getting a little ahead of myself and started decorating my planner for January 😁🌟 loved using the new Winter Months transfer stickers available in my shop 💙 This weekly planner is undated, which is',
      url: 'https://www.instagram.com/reel/CXL6jkTAYq_/',
      embedId: 'CXL6jkTAYq_',
      category: 'journal-spread',
      tags: ['vintagecraftsupplies', 'vintagecraftstore', 'craftingsupplies', 'craftingshop', 'transferstickers', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CXL6jkTAYq_/media/?size=m'
    },
    {
      id: 'ig-api-927',
      platform: 'instagram',
      title: 'A little Christmas folder/card 🎄 it will include a letter and go out to some...',
      description: 'A little Christmas folder/card 🎄 it will include a letter and go out to someone special 💌 I used one of my order packaging bags, and two clear stamps I added to the shop today! Starting to feel the ',
      url: 'https://www.instagram.com/reel/CXFGabpp0mC/',
      embedId: 'CXFGabpp0mC',
      category: 'tutorial',
      tags: ['christmasmood', 'christmasstickers', 'christmascrafts', 'christmascards', 'xmascards', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CXFGabpp0mC/media/?size=m'
    },
    {
      id: 'ig-api-928',
      platform: 'instagram',
      title: 'Hello December 🖤 Where has this year gone?! Playing with contrasts on my bla...',
      description: 'Hello December 🖤 Where has this year gone?! Playing with contrasts on my black pages again using the Celestial theme items from the December Vintage Craft Box: stencil, transparent washi tape and moo',
      url: 'https://www.instagram.com/reel/CW_GkIoJLOG/',
      embedId: 'CW_GkIoJLOG',
      category: 'journal-spread',
      tags: ['hellodecember', 'decemberbox', 'decemverpages', 'blackpaperjournal', 'vintagecraftsupplies', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CW_GkIoJLOG/media/?size=m'
    },
    {
      id: 'ig-api-929',
      platform: 'instagram',
      title: 'December Box Preview 🌘 I can\'t decide what my favorite piece is 🙈 This mont...',
      description: 'December Box Preview 🌘 I can\'t decide what my favorite piece is 🙈 This month is all about Astronomy with items inspired by the sky, stars and constellations 🪐🌟 Available in my shop now. #decemberb',
      url: 'https://www.instagram.com/reel/CW6Cx65J_v0/',
      embedId: 'CW6Cx65J_v0',
      category: 'box-reveal',
      tags: ['decemberbox', 'decembersubscription', 'subscriptionbox', 'craftysubscription', 'journalephemera', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CW6Cx65J_v0/media/?size=m'
    },
    {
      id: 'ig-api-930',
      platform: 'instagram',
      title: 'Transparent Christmas Tags 🎄Now that Thanksgiving is over I\'m ready to dive ...',
      description: 'Transparent Christmas Tags 🎄Now that Thanksgiving is over I\'m ready to dive in full Christmas mode! For these tags I used:  * Some transparent plastic slides * Chalk Paint * Gesso * Silver foil (part',
      url: 'https://www.instagram.com/tv/CWyfEYbAQvz/',
      embedId: '',
      category: 'tutorial',
      tags: ['christmastags', 'christmascrafts', 'diytags', 'diychristmas', 'xmasdecor', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-931',
      platform: 'instagram',
      title: 'Who/What are you thankful for? I created these little leaves cards for tomorr...',
      description: 'Who/What are you thankful for? I created these little leaves cards for tomorrow 🍁 to share a thank you message to those I\'m grateful to have around me. I used dried colorful leaves from my garden lay',
      url: 'https://www.instagram.com/reel/CWqqTLhAcGr/',
      embedId: 'CWqqTLhAcGr',
      category: 'tutorial',
      tags: ['happymailideas', 'thanksgiving', 'thanksgivingideas', 'leavescrafts', 'driedleaves', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CWqqTLhAcGr/media/?size=m'
    },
    {
      id: 'ig-api-932',
      platform: 'instagram',
      title: '🖤 Early BF Sale 🖤 starts today! Sign up to my newsletter to receive special...',
      description: '🖤 Early BF Sale 🖤 starts today! Sign up to my newsletter to receive special discount codes to access the BF Sale early. Regular sale will be from Fri 26-Mo 29 🎄📦 #blackfriday #blackfridaysale #cor',
      url: 'https://www.instagram.com/reel/CWlbSKep9QX/',
      embedId: 'CWlbSKep9QX',
      category: 'product-showcase',
      tags: ['blackfriday', 'blackfridaysale', 'coracreacrafts', 'stationeryshop', 'stationerysale', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CWlbSKep9QX/media/?size=m'
    },
    {
      id: 'ig-api-933',
      platform: 'instagram',
      title: 'Happy Weekend ☺️ Here is a short video of making the wax seal paper clips 🪶 ...',
      description: 'Happy Weekend ☺️ Here is a short video of making the wax seal paper clips 🪶 I used the Feather seal which can be found inside the November Vintage Craft Box and the Portrait Wax Seal from my shop 🖤 ',
      url: 'https://www.instagram.com/reel/CWhIGTcp0Ih/',
      embedId: 'CWhIGTcp0Ih',
      category: 'tutorial',
      tags: ['paperclips', 'waxsealclip', 'waxsealshop', 'waxsealcrafts', 'waxseals', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CWhIGTcp0Ih/media/?size=m'
    },
    {
      id: 'ig-api-934',
      platform: 'instagram',
      title: 'Letters List spread with November Vintage Craft Box items 🖤🪶 Did you notice...',
      description: 'Letters List spread with November Vintage Craft Box items 🖤🪶 Did you notice the bookmark made with the feather pen wax seal? ☺️ There are still a few November boxes available on my website 👉 link i',
      url: 'https://www.instagram.com/reel/CWTNddfgkKi/',
      embedId: 'CWTNddfgkKi',
      category: 'journal-spread',
      tags: ['blackpaper', 'blackpaperjournal', 'blackjournal', 'vintagejournal', 'vintagejournaling', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CWTNddfgkKi/media/?size=m'
    },
    {
      id: 'ig-api-935',
      platform: 'instagram',
      title: 'November "Letter" Box Preview 🖤 it\'s finally here!! Spot my favorite item, m...',
      description: 'November "Letter" Box Preview 🖤 it\'s finally here!! Spot my favorite item, my very first rectangular wax seal with a quill 🪶 I hope you like it! Available in my shop ☺️ #coracreacraftbox #coracreacr',
      url: 'https://www.instagram.com/reel/CWGU2fLg2Fs/',
      embedId: 'CWGU2fLg2Fs',
      category: 'box-reveal',
      tags: ['coracreacraftbox', 'coracreacrafts', 'novembersubscriptionbox', 'subbox', 'subscriptionbox', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CWGU2fLg2Fs/media/?size=m'
    },
    {
      id: 'ig-api-936',
      platform: 'instagram',
      title: 'My first attempt at making a shadow box 🖤 I love how it turned out with the ...',
      description: 'My first attempt at making a shadow box 🖤 I love how it turned out with the colors but I need to work on creating more depth and hiding the foam squares. Any tips? I\'m going to hang it in my studio b',
      url: 'https://www.instagram.com/reel/CWBN6vMp8oI/',
      embedId: 'CWBN6vMp8oI',
      category: 'tutorial',
      tags: ['shadowbox', 'butterflies', 'butterflypapers', 'butterflyart', 'butterflyephemera', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CWBN6vMp8oI/media/?size=m'
    },
    {
      id: 'ig-api-937',
      platform: 'instagram',
      title: 'November Box Inspirational Spread mixing different styles 🖤 Used items from ...',
      description: 'November Box Inspirational Spread mixing different styles 🖤 Used items from the November Letters Box to create these pages. The box and the leather "Mixed Rustic" Journal are available in my shop 😊 ',
      url: 'https://www.instagram.com/reel/CV5gA6RpKUG/',
      embedId: 'CV5gA6RpKUG',
      category: 'journal-spread',
      tags: ['letterstamps', 'stampwashi', 'coracreacraftbox', 'coracreacrafts', 'leatherjournal', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CV5gA6RpKUG/media/?size=m'
    },
    {
      id: 'ig-api-938',
      platform: 'instagram',
      title: 'Make your own layered paper butterflies 🦋🖤 A lot of you asked about the but...',
      description: 'Make your own layered paper butterflies 🦋🖤 A lot of you asked about the butterflies in my recent journal post so I decided to show you how to make your own very easily 🦋 not sure they\'re as pretty ',
      url: 'https://www.instagram.com/reel/CVvHPdegqRR/',
      embedId: 'CVvHPdegqRR',
      category: 'tutorial',
      tags: ['layeredbutterflies', 'paperbutterflies', 'papercrafts', 'craftingvideo', 'craftingsupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CVvHPdegqRR/media/?size=m'
    },
    {
      id: 'ig-api-939',
      platform: 'instagram',
      title: 'Spooky Vibes 🕸️🕷️ Happy Halloween',
      description: 'Spooky Vibes 🕸️🕷️ Happy Halloween!  #vintagecraftsupplies #vintagecraftstore #vintagestamps #vintagehalloween #halloweenvintage #halloween #halloweenspread #halloweenjournal #vintagejournal #vintage',
      url: 'https://www.instagram.com/reel/CVs4uAUJk6n/',
      embedId: 'CVs4uAUJk6n',
      category: 'journal-spread',
      tags: ['vintagecraftsupplies', 'vintagecraftstore', 'vintagestamps', 'vintagehalloween', 'halloweenvintage', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CVs4uAUJk6n/media/?size=m'
    },
    {
      id: 'ig-api-940',
      platform: 'instagram',
      title: 'Glazed Ephemera Pieces 🖤 The embossing glazes from @ranger_ink & @tim_holtz ...',
      description: 'Glazed Ephemera Pieces 🖤 The embossing glazes from @ranger_ink & @tim_holtz give an amazing shine to these stamped papers. Stamp set is available as part of the Oct/Nov Treasure Box 🎁  #embossingpow',
      url: 'https://www.instagram.com/reel/CVnlbw4pkjZ/',
      embedId: 'CVnlbw4pkjZ',
      category: 'tutorial',
      tags: ['embossingpowder', 'embossingglaze', 'glazedpaper', 'glazedephemera', 'ephemerapieces', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CVnlbw4pkjZ/media/?size=m'
    },
    {
      id: 'ig-api-941',
      platform: 'instagram',
      title: 'Ephemera Folders Tutorial 🖤',
      description: 'Ephemera Folders Tutorial 🖤 Using:  * 8*8in papers of Designer Paper Pad (current Treasure Box) * Distress Ink * Glue Roller  * Pieces of cardboard to reinforce the pages * Little stickers * Elastic ',
      url: 'https://www.instagram.com/tv/CViPRiEA3KJ/',
      embedId: '',
      category: 'tutorial',
      tags: ['craftingvideo', 'craftwithme', 'craftpaper', 'craftingsupplies', 'craftingshop', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-942',
      platform: 'instagram',
      title: 'DIY seasonal gift wrap 🌲 using craft paper and the pines & cones clear stamp...',
      description: 'DIY seasonal gift wrap 🌲 using craft paper and the pines & cones clear stamp from my shop 🖤  #giftideas #giftwrap #giftwrapping #diygiftsandcrafts #diywrappingpaper #wrappingpaper #wrapping #diypape',
      url: 'https://www.instagram.com/reel/CVdEFI_AZuI/',
      embedId: 'CVdEFI_AZuI',
      category: 'tutorial',
      tags: ['giftideas', 'giftwrap', 'giftwrapping', 'diygiftsandcrafts', 'diywrappingpaper', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CVdEFI_AZuI/media/?size=m'
    },
    {
      id: 'ig-api-943',
      platform: 'instagram',
      title: 'Transparent Tags 🖤 All you really need is a laminator ☺️ the piece of equipm...',
      description: 'Transparent Tags 🖤 All you really need is a laminator ☺️ the piece of equipment I use the most in my crafts. All supplies used are available in my shop Including this new Portrait Wax Seal 💞 #transp',
      url: 'https://www.instagram.com/reel/CVS-BGQpt20/',
      embedId: 'CVS-BGQpt20',
      category: 'tutorial',
      tags: ['transparenttags', 'diytags', 'ephemerapieces', 'ephemeratags', 'ephemera', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CVS-BGQpt20/media/?size=m'
    },
    {
      id: 'ig-api-944',
      platform: 'instagram',
      title: 'New spread on my black pages 🖤 I loved to work with these cute paper layered...',
      description: 'New spread on my black pages 🖤 I loved to work with these cute paper layered butterflies and with new materials: gesso to create the textured middle part with tissue paper and chalk paint to create t',
      url: 'https://www.instagram.com/reel/CVNoiQyA26l/',
      embedId: 'CVNoiQyA26l',
      category: 'journal-spread',
      tags: ['blackpaperjournal', 'blackpaper', 'blackjournal', 'blackandwhiteart', 'blackandwhitejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CVNoiQyA26l/media/?size=m'
    },
    {
      id: 'ig-api-945',
      platform: 'instagram',
      title: 'Pack a Vintage Craft Box with me 🍁 it\'s been ages since my last  video so he...',
      description: 'Pack a Vintage Craft Box with me 🍁 it\'s been ages since my last #packanorderwithme video so here\'s one again! It\'s also the last call to get your hands on this October Box if you haven\'t done so yet ',
      url: 'https://www.instagram.com/reel/CVDc2x7gGHU/',
      embedId: 'CVDc2x7gGHU',
      category: 'behind-the-scenes',
      tags: ['packanorderwithme', 'packingorders', 'packaging', 'vintagecraftbox', 'vintagecraftsupplies', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CVDc2x7gGHU/media/?size=m'
    },
    {
      id: 'ig-api-946',
      platform: 'instagram',
      title: 'Autumn Pages 🍁 I started these pages with no idea of what I wanted to do and...',
      description: 'Autumn Pages 🍁 I started these pages with no idea of what I wanted to do and just went on with it piece by piece. I\'m usually one to think about layouts and materials, and picture things in my head b',
      url: 'https://www.instagram.com/reel/CU77_veptE7/',
      embedId: 'CU77_veptE7',
      category: 'journal-spread',
      tags: ['autumncrafting', 'autumnjournal', 'journalingephemera', 'journalingideas', 'journalfall', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CU77_veptE7/media/?size=m'
    },
    {
      id: 'ig-api-947',
      platform: 'instagram',
      title: 'Autumn Tags/Bookmarks 🦌🍁These are  my favorite ephemera pieces I made this ...',
      description: 'Autumn Tags/Bookmarks 🦌🍁These are  my favorite ephemera pieces I made this month so far ❤️ Stickers and Stamps are part of the October Vintage Craft Box and Seal is the Leaf Wax Seal from my shop 🍁',
      url: 'https://www.instagram.com/reel/CU2ls72gn86/',
      embedId: 'CU2ls72gn86',
      category: 'tutorial',
      tags: ['octoberbox', 'vintagecraftbox', 'vintagecraftsupplies', 'craftingsubscriptionbox', 'craftingsupplies', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CU2ls72gn86/media/?size=m'
    },
    {
      id: 'ig-api-948',
      platform: 'instagram',
      title: 'My very first collage card ��� This was so enjoyable to make! 100% Autumn vib...',
      description: 'My very first collage card ��� This was so enjoyable to make! 100% Autumn vibes with the mushroom washi tape (October Box) and the Creepy Mushrooms Clear Stamps. I got inspired by @lize.creates who ma',
      url: 'https://www.instagram.com/reel/CUxUZRzAm7S/',
      embedId: 'CUxUZRzAm7S',
      category: 'tutorial',
      tags: ['collagecraft', 'collage', 'collagecard', 'autumncollage', 'mushroomstickers', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CUxUZRzAm7S/media/?size=m'
    },
    {
      id: 'ig-api-949',
      platform: 'instagram',
      title: 'Gift packing season is starting 🎁 I truly love packing gifts and decorating ...',
      description: 'Gift packing season is starting 🎁 I truly love packing gifts and decorating them in a special way ❤️ wrapped this Halloween box in an Autumn theme for someone\'s birthday with some of the October Box ',
      url: 'https://www.instagram.com/reel/CUsJk4BAzpF/',
      embedId: 'CUsJk4BAzpF',
      category: 'behind-the-scenes',
      tags: ['giftideas', 'giftwrapping', 'giftwrap', 'autumncrafting', 'autumngiftwrap', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CUsJk4BAzpF/media/?size=m'
    },
    {
      id: 'ig-api-950',
      platform: 'instagram',
      title: 'Study of round stamps 🟤 After deciding what to do with this new beautiful  @...',
      description: 'Study of round stamps 🟤 After deciding what to do with this new beautiful  @modest_goods journal, I started filling the pages with ideas, samples and small "studies" of products and objects I like fo',
      url: 'https://www.instagram.com/reel/CUnD4IIAowC/',
      embedId: 'CUnD4IIAowC',
      category: 'product-showcase',
      tags: ['vintagecraftstore', 'vintagecraftsupplies', 'vintagejournal', 'leatherjournal', 'studyjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CUnD4IIAowC/media/?size=m'
    },
    {
      id: 'ig-api-951',
      platform: 'instagram',
      title: 'Hello October 🍁 best month of the year 😀 Made an Autumn themed box for all ...',
      description: 'Hello October 🍁 best month of the year 😀 Made an Autumn themed box for all my stickers and ephemera #autumncrafting #autumnwaxseals #autumncraftbox #autumnstationery #autumnsupplies #fallcolors #fal',
      url: 'https://www.instagram.com/reel/CUfkWuxAYf0/',
      embedId: 'CUfkWuxAYf0',
      category: 'tutorial',
      tags: ['autumncrafting', 'autumnwaxseals', 'autumncraftbox', 'autumnstationery', 'autumnsupplies', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CUfkWuxAYf0/media/?size=m'
    },
    {
      id: 'ig-api-952',
      platform: 'instagram',
      title: 'Try n°2 of the Cloth Tags this time with Autumn vibes 🍁 I experimented woode...',
      description: 'Try n°2 of the Cloth Tags this time with Autumn vibes 🍁 I experimented wooden stamps as wax seals with mixed results, some work really well, some not so much! Used the two new wax seals: pumpkin cuto',
      url: 'https://www.instagram.com/reel/CUaOnOwgujd/',
      embedId: 'CUaOnOwgujd',
      category: 'tutorial',
      tags: ['clothtags', 'fabriccrafts', 'fabrictags', 'diytags', 'ephemerapieces', 'autumn'],
      creator: 'CoraCreaCrafts',
      date: '2021-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CUaOnOwgujd/media/?size=m'
    },
    {
      id: 'ig-api-953',
      platform: 'instagram',
      title: 'First Page of my new @modest_goods journal 🖤 I decided to make this journal ...',
      description: 'First Page of my new @modest_goods journal 🖤 I decided to make this journal a collage/inspiration/theme ideas and sampling journal. Couldn\'t really decide so it will be a mix of things 😂 Love the di',
      url: 'https://www.instagram.com/reel/CUVAW7lgaoM/',
      embedId: 'CUVAW7lgaoM',
      category: 'journal-spread',
      tags: ['vintagejournal', 'journalspread', 'newjournal', 'firstpage', 'vintagepages', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CUVAW7lgaoM/media/?size=m'
    },
    {
      id: 'ig-api-954',
      platform: 'instagram',
      title: 'Distressed Alchemy Cards with the Alchemy Stickers I created for the Septembe...',
      description: 'Distressed Alchemy Cards with the Alchemy Stickers I created for the September Box 🖤 You can now get these stickers by themselves in my shop, as well as the Clear Alchemy Stamps!  #alchemyjournal #al',
      url: 'https://www.instagram.com/reel/CUQXkkwJDD4/',
      embedId: 'CUQXkkwJDD4',
      category: 'tutorial',
      tags: ['alchemyjournal', 'alchemycards', 'alchemysymbols', 'alchemyephemera', 'ephemeracards', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CUQXkkwJDD4/media/?size=m'
    },
    {
      id: 'ig-api-955',
      platform: 'instagram',
      title: 'Video process of the black journal pages from last week 🖤 loved to work with...',
      description: 'Video process of the black journal pages from last week 🖤 loved to work with grit paste and white paint to create these beautiful contrasts! Multicolor journal from @modest_goods Grit paste @ranger_i',
      url: 'https://www.instagram.com/reel/CUK8gvkJ6ZQ/',
      embedId: 'CUK8gvkJ6ZQ',
      category: 'journal-spread',
      tags: ['journalspread', 'blackpaperjournal', 'blackpaper', 'blackjournal', 'blackandwhiteart', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CUK8gvkJ6ZQ/media/?size=m'
    },
    {
      id: 'ig-api-956',
      platform: 'instagram',
      title: 'October Box Sneak Peek - this month it\'s all about Autumn 🍄🍁 October is my ...',
      description: 'October Box Sneak Peek - this month it\'s all about Autumn 🍄🍁 October is my bday month and Autumn my favorite season of the year so it\'s a "Feeling Good" box, hence the choice of song! 😆 Hope you li',
      url: 'https://www.instagram.com/reel/CUIJ6KiAYHM/',
      embedId: 'CUIJ6KiAYHM',
      category: 'box-reveal',
      tags: ['autumncrafts', 'autumnbox', 'autumncrafting', 'autumnstationery', 'autumnsupplies', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CUIJ6KiAYHM/media/?size=m'
    },
    {
      id: 'ig-api-957',
      platform: 'instagram',
      title: 'Little Halloween Folding Cards 🖤🕸️ I made these with textured off-white car...',
      description: 'Little Halloween Folding Cards 🖤🕸️ I made these with textured off-white cardstock paper and all the supplies from the Halloween Box in my shop! It\'s a bit early for Halloween but I was just so excit',
      url: 'https://www.instagram.com/reel/CUC8QrZANsK/',
      embedId: 'CUC8QrZANsK',
      category: 'box-reveal',
      tags: ['halloween', 'halloweenbox', 'craftingsupplies', 'craftshop', 'halloweencrafts', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CUC8QrZANsK/media/?size=m'
    },
    {
      id: 'ig-api-958',
      platform: 'instagram',
      title: 'Extra Wide Ephemera Pocket 🖤 This Friday you will have a chance to win it al...',
      description: 'Extra Wide Ephemera Pocket 🖤 This Friday you will have a chance to win it along with a ton of goodies which are inside for the new giveaway I\'m hosting with @albumlovehandmade 🎉 Stay tuned to discov',
      url: 'https://www.instagram.com/reel/CTw9na6gSMD/',
      embedId: 'CTw9na6gSMD',
      category: 'tutorial',
      tags: ['ephemerapieces', 'easypapercraft', 'ephemera', 'ephemerapocket', 'ephemerafolder', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CTw9na6gSMD/media/?size=m'
    },
    {
      id: 'ig-api-959',
      platform: 'instagram',
      title: 'New Wax Seals 🖤 These 8 cutout wax seals just found their way to my shop alo...',
      description: 'New Wax Seals 🖤 These 8 cutout wax seals just found their way to my shop along with these pretty pastel wax colors! Link in bio!  #waxsealshop #waxsealart #waxsealstamp #waxseals #pastelwax #coracrea',
      url: 'https://www.instagram.com/reel/CTrwZ4PAxk0/',
      embedId: 'CTrwZ4PAxk0',
      category: 'product-showcase',
      tags: ['waxsealshop', 'waxsealart', 'waxsealstamp', 'waxseals', 'pastelwax', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CTrwZ4PAxk0/media/?size=m'
    },
    {
      id: 'ig-api-960',
      platform: 'instagram',
      title: 'Fabric Tags made out of an old curtain 🖤 Thank you @letras.literarias.mx to ...',
      description: 'Fabric Tags made out of an old curtain 🖤 Thank you @letras.literarias.mx to inspire me to create something with fabric!  #clothtags #recycledart #recycledcurtains #curtainart #waxsealstamp #waxseals ',
      url: 'https://www.instagram.com/reel/CTkHnjlgmFf/',
      embedId: 'CTkHnjlgmFf',
      category: 'tutorial',
      tags: ['clothtags', 'recycledart', 'recycledcurtains', 'curtainart', 'waxsealstamp', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CTkHnjlgmFf/media/?size=m'
    },
    {
      id: 'ig-api-961',
      platform: 'instagram',
      title: 'My very first time using grit paste by @tim_holtz @ranger_ink and I absolutel...',
      description: 'My very first time using grit paste by @tim_holtz @ranger_ink and I absolutely loved it 🖤 Created these two mixed media pieces which will go and decorate my office wall 🥰  I used: * Grit paste * Flo',
      url: 'https://www.instagram.com/tv/CTfCXwAg2IU/',
      embedId: '',
      category: 'tutorial',
      tags: ['wallart', 'mixedmediaart', 'mixedmedia', 'mixedmediawallart', 'timholz', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-962',
      platform: 'instagram',
      title: 'Pattern Bookmarks with Paperclip 🖤  Used:',
      description: 'Pattern Bookmarks with Paperclip 🖤  Used: * Round Hole punch of different sizes * Cardboard Paper * Pattern washi tapes from my shop * Dreamcatcher stickers from my shop  * Laminator * Paperclip and ',
      url: 'https://www.instagram.com/reel/CTaBJlQq0Qc/',
      embedId: 'CTaBJlQq0Qc',
      category: 'tutorial',
      tags: ['bookmark', 'patternwashi', 'washitape', 'washitapeart', 'papercrafts', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CTaBJlQq0Qc/media/?size=m'
    },
    {
      id: 'ig-api-963',
      platform: 'instagram',
      title: 'Alchemy Journal Spread 🐍 my favorite pages in this journal so far. All the i...',
      description: 'Alchemy Journal Spread 🐍 my favorite pages in this journal so far. All the items used are part of the September Vintage Craft Box 🖤 Link in bio 😍 @storm_sparrow #vintagecrafts #vintagejournal #vint',
      url: 'https://www.instagram.com/reel/CTSGdGVgcol/',
      embedId: 'CTSGdGVgcol',
      category: 'journal-spread',
      tags: ['vintagecrafts', 'vintagejournal', 'vintagecraftbox', 'coracreacrafts', 'coracreacraftbox', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CTSGdGVgcol/media/?size=m'
    },
    {
      id: 'ig-api-964',
      platform: 'instagram',
      title: 'Simple Ephemera Frames 🖤 I will use these in my journals or to decorate othe...',
      description: 'Simple Ephemera Frames 🖤 I will use these in my journals or to decorate other crafting pieces! Featuring the super cute stamps  from the new Plants Large Stamp Set and Manuscript Papers from my shop ',
      url: 'https://www.instagram.com/reel/CTNHj9tAKPe/',
      embedId: 'CTNHj9tAKPe',
      category: 'tutorial',
      tags: ['ephemera', 'journalingephemera', 'journalingideas', 'journalingpieces', 'ephemerapieces', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CTNHj9tAKPe/media/?size=m'
    },
    {
      id: 'ig-api-965',
      platform: 'instagram',
      title: 'Embellished Frames Labels 🖤 Which one is your favorite',
      description: 'Embellished Frames Labels 🖤 Which one is your favorite?  Used: Frames Labels Notepads, Scribe Clear Stamp, Butterfly Strip, Romantic Sticker Sheets, Green Wreath Sticker Sheets - all from my shop 😊 ',
      url: 'https://www.instagram.com/reel/CTH8-LfAqwj/',
      embedId: 'CTH8-LfAqwj',
      category: 'tutorial',
      tags: ['frameslabels', 'embellishedephemera', 'embellishedtags', 'diylabels', 'snailmaillabels', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CTH8-LfAqwj/media/?size=m'
    },
    {
      id: 'ig-api-966',
      platform: 'instagram',
      title: 'Surprise Box 📦 This new batch started going out a few days ago and has a ver...',
      description: 'Surprise Box 📦 This new batch started going out a few days ago and has a very limited stock! If you\'re interested in getting this specific Surprise Box, head to my website today 😍 #surprisecraftbox ',
      url: 'https://www.instagram.com/reel/CS9gjZjghs0/',
      embedId: 'CS9gjZjghs0',
      category: 'box-reveal',
      tags: ['surprisecraftbox', 'surprisegift', 'surprisebox', 'coracreacrafts', 'coracreacraftbox', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CS9gjZjghs0/media/?size=m'
    },
    {
      id: 'ig-api-967',
      platform: 'instagram',
      title: 'Orders for the September Box are open 🖤 This Alchemy theme will bring you ba...',
      description: 'Orders for the September Box are open 🖤 This Alchemy theme will bring you back to Medieval times through elixirs and symbolism. This box is special for many reasons: - Alchemy is the theme you chose ',
      url: 'https://www.instagram.com/reel/CSzXXNYgGs5/',
      embedId: 'CSzXXNYgGs5',
      category: 'box-reveal',
      tags: ['septemberbox', 'alchemytheme', 'alchemist', 'vintagescience', 'vintagealchemy', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CSzXXNYgGs5/media/?size=m'
    },
    {
      id: 'ig-api-968',
      platform: 'instagram',
      title: '19 août 🖤 What\'s special about today? I finally had time to journal again 😃...',
      description: '19 août 🖤 What\'s special about today? I finally had time to journal again 😃 A bit of romance and nostalgia for this spread with the decorative corners stamps (part of the new batch of surprise boxes',
      url: 'https://www.instagram.com/reel/CSxL4KtpEBA/',
      embedId: 'CSxL4KtpEBA',
      category: 'journal-spread',
      tags: ['washilover', 'washitapeshop', 'washitape', 'foilwashitape', 'journaladdict', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CSxL4KtpEBA/media/?size=m'
    },
    {
      id: 'ig-api-969',
      platform: 'instagram',
      title: 'Here is the tutorial for the glazed tags I made last week and shared in my re...',
      description: 'Here is the tutorial for the glazed tags I made last week and shared in my reels ❤️  I used: * cardstock paper for the tags * botanical dictionary papers * distress ink and embossing glaze by @ranger_',
      url: 'https://www.instagram.com/tv/CSuFD3oAegY/',
      embedId: '',
      category: 'tutorial',
      tags: ['embossingglaze', 'glazetags', 'botanicaltags', 'botanicalillustration', 'diytags', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-970',
      platform: 'instagram',
      title: 'So excited to announce that I collaborated with the talented @storm_sparrow f...',
      description: 'So excited to announce that I collaborated with the talented @storm_sparrow for the upcoming September Vintage Craft Box - Alchemy 🖤 The box will include these two gorgeous washi tapes which we co-de',
      url: 'https://www.instagram.com/reel/CSozTUTgHqh/',
      embedId: 'CSozTUTgHqh',
      category: 'box-reveal',
      tags: ['vintagecraftbox', 'coracreacrafts', 'coracreacraftbox', 'stormsparrow', 'journalsubscription', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CSozTUTgHqh/media/?size=m'
    },
    {
      id: 'ig-api-971',
      platform: 'instagram',
      title: 'Envelope Decorating with the Flowers and Insects Foil Washi Tape and the Tran...',
      description: 'Envelope Decorating with the Flowers and Insects Foil Washi Tape and the Transparent Blue Flower Washi Sticker from my shop  🌸🌼 These two and all my Nature/Flora/Insect section is 15% off for this w',
      url: 'https://www.instagram.com/reel/CSkFuR5gKO8/',
      embedId: 'CSkFuR5gKO8',
      category: 'tutorial',
      tags: ['envelopedesign', 'envelopedecoration', 'envelopeart', 'envelopes', 'diyenvelope', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CSkFuR5gKO8/media/?size=m'
    },
    {
      id: 'ig-api-972',
      platform: 'instagram',
      title: 'Nautical Card ⛵ Nautical stamps, stickers and washi tapes now available in my...',
      description: 'Nautical Card ⛵ Nautical stamps, stickers and washi tapes now available in my shop 🖤 A lot of you have shown an interest in getting some of the June Nautical Box items so here they are! Link in bio. ',
      url: 'https://www.instagram.com/reel/CSeyII5gqkI/',
      embedId: 'CSeyII5gqkI',
      category: 'tutorial',
      tags: ['nauticalillustration', 'nauticalstickers', 'nauticaljournal', 'nauticalcard', 'marinelife', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CSeyII5gqkI/media/?size=m'
    },
    {
      id: 'ig-api-973',
      platform: 'instagram',
      title: 'Old Maps Tags with stamped windows 🏷️ Here\'s a fun idea on how to use the it...',
      description: 'Old Maps Tags with stamped windows 🏷️ Here\'s a fun idea on how to use the items from the Old Maps August Craft Box 🖤 Share your own creations with the box and use the #coracreacrafts  #vintagecraftb',
      url: 'https://www.instagram.com/reel/CSW1PEagVLi/',
      embedId: 'CSW1PEagVLi',
      category: 'tutorial',
      tags: ['coracreacrafts', 'vintagecraftbox', 'coracreacraftbox', 'vintagebox', 'subscriptionboxes', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CSW1PEagVLi/media/?size=m'
    },
    {
      id: 'ig-api-974',
      platform: 'instagram',
      title: 'Pack an order with me 🖤✉️ haven\'t posted one of these in a while! This one i...',
      description: 'Pack an order with me 🖤✉️ haven\'t posted one of these in a while! This one is special because I\'m packing two custom vintage craft boxes.  @doodledots_29 had a special request for boxes with a Music ',
      url: 'https://www.instagram.com/reel/CSRw7AIA6B9/',
      embedId: 'CSRw7AIA6B9',
      category: 'behind-the-scenes',
      tags: ['vintagecraftbox', 'subscription', 'subscriptionboxes', 'subbox', 'subboxaddict', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CSRw7AIA6B9/media/?size=m'
    },
    {
      id: 'ig-api-975',
      platform: 'instagram',
      title: 'Making Ephemera Inserts for the Travel Journal Kits 🌍🖤 Thank you for your p...',
      description: 'Making Ephemera Inserts for the Travel Journal Kits 🌍🖤 Thank you for your patience I\'m receiving these kits 🤗 I will be shipping all backorders tomorrow 🙏 #ephemerafolder #traveljournal #traveljou',
      url: 'https://www.instagram.com/reel/CSMsj1ygMah/',
      embedId: 'CSMsj1ygMah',
      category: 'product-showcase',
      tags: ['ephemerafolder', 'traveljournal', 'traveljournalkit', 'ephemerakit', 'ephemerapockets', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CSMsj1ygMah/media/?size=m'
    },
    {
      id: 'ig-api-976',
      platform: 'instagram',
      title: 'Glaze Botanical Tags 🌿 I had so much fun experimenting with these Embossing ...',
      description: 'Glaze Botanical Tags 🌿 I had so much fun experimenting with these Embossing Glaze Powders from @ranger_ink @tim_holtz  Also used the ultra wide transparent lace tape from this new edition of the Trea',
      url: 'https://www.instagram.com/reel/CSKCg_TAioF/',
      embedId: 'CSKCg_TAioF',
      category: 'tutorial',
      tags: ['embossingglaze', 'distressink', 'distressembossingglaze', 'botanicalillustration', 'botanicalpaper', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CSKCg_TAioF/media/?size=m'
    },
    {
      id: 'ig-api-977',
      platform: 'instagram',
      title: 'Mini Postcards 💌 I used the new "Classicism" and "Impressionism" Gold Postal...',
      description: 'Mini Postcards 💌 I used the new "Classicism" and "Impressionism" Gold Postal Stamp Stickers from my shop 🖤 and the Downtown Abbey music was just a perfect fit for this video 🎶 #minipostcards #posta',
      url: 'https://www.instagram.com/reel/CSE1DtfgV9W/',
      embedId: 'CSE1DtfgV9W',
      category: 'tutorial',
      tags: ['minipostcards', 'postalstamps', 'diypost', 'diypostcard', 'diymail', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CSE1DtfgV9W/media/?size=m'
    },
    {
      id: 'ig-api-978',
      platform: 'instagram',
      title: 'Decided to make some pads of tear-off tickets using some cute little insects ...',
      description: 'Decided to make some pads of tear-off tickets using some cute little insects 🐝 I realized how much you all loved the March Box Insect and Butterfly theme after the recent vote in my stories and thoug',
      url: 'https://www.instagram.com/tv/CR9QN7_gvaa/',
      embedId: '',
      category: 'tutorial',
      tags: ['tearofftickets', 'diytickets', 'ephemeratickets', 'vintagetickets', 'insectstamps', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-979',
      platform: 'instagram',
      title: 'Creative Wheel of Themes ⚙️ I decided to make a visual overview of my Vintage...',
      description: 'Creative Wheel of Themes ⚙️ I decided to make a visual overview of my Vintage Craft Box themes so far this year 🖤 Do you recognize all the themes up to September? There are still 3 blank spots on my ',
      url: 'https://www.instagram.com/reel/CR36isdpA0O/',
      embedId: 'CR36isdpA0O',
      category: 'tutorial',
      tags: ['creativewheel', 'rouecr', 'roundephemera', 'coracreacrafts', 'coracreacraftbox', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CR36isdpA0O/media/?size=m'
    },
    {
      id: 'ig-api-980',
      platform: 'instagram',
      title: 'Highlights from the August/September Treasure Box 🖤 All pre-orders will be s...',
      description: 'Highlights from the August/September Treasure Box 🖤 All pre-orders will be shipped in a few days 🤗 This edition is still available for order in limited quantity!  #treasurebox #coracreacraftbox #cor',
      url: 'https://www.instagram.com/reel/CRy60bhp8ii/',
      embedId: 'CRy60bhp8ii',
      category: 'box-reveal',
      tags: ['treasurebox', 'coracreacraftbox', 'coracreacrafts', 'bimonthlybox', 'stationerybox', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CRy60bhp8ii/media/?size=m'
    },
    {
      id: 'ig-api-981',
      platform: 'instagram',
      title: 'Travel Journal pages with the August Old Maps Box 🖤 I used:',
      description: 'Travel Journal pages with the August Old Maps Box 🖤 I used: *Ancient Map Clear Stamp *Large Background Sticker *Map Scale Wooden Stamp *Compass Wooden Stamp The Vintage Craft Box is available in my s',
      url: 'https://www.instagram.com/reel/CRvkW_mJ2N9/',
      embedId: 'CRvkW_mJ2N9',
      category: 'journal-spread',
      tags: ['coracreacrafts', 'coracreacraftbox', 'oldmaps', 'ancientmap', 'vintagemaps', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CRvkW_mJ2N9/media/?size=m'
    },
    {
      id: 'ig-api-982',
      platform: 'instagram',
      title: 'Dreams Create Purpose 🦋 Don\'t forget to dream and follow your passions! This...',
      description: 'Dreams Create Purpose 🦋 Don\'t forget to dream and follow your passions! This is my entry for the #magicalmothweek Dreamland Polenators by @kristinheldtart @sandra.staub  #butterflies #butterflyepheme',
      url: 'https://www.instagram.com/reel/CRrAZo0Jdaj/',
      embedId: 'CRrAZo0Jdaj',
      category: 'tutorial',
      tags: ['magicalmothweek', 'butterflies', 'butterflyephemera', 'butterflytags', 'butterflycrafts', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CRrAZo0Jdaj/media/?size=m'
    },
    {
      id: 'ig-api-983',
      platform: 'instagram',
      title: '❗GIVEAWAY CLOSED❗',
      description: '❗GIVEAWAY CLOSED❗   @loveforpapergoodies and I are so excited to bring you this stunning giveaway! The 4 lucky winners will receive vintage stationery and ephemera from either one of us.   The most am',
      url: 'https://www.instagram.com/reel/CRjI1FlpElB/',
      embedId: 'CRjI1FlpElB',
      category: 'tutorial',
      tags: ['vintage', 'stickers'],
      creator: 'CoraCreaCrafts',
      date: '2021-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CRjI1FlpElB/media/?size=m'
    },
    {
      id: 'ig-api-984',
      platform: 'instagram',
      title: 'I had so much fun creating something special for the  hosted by the lovely an...',
      description: 'I had so much fun creating something special for the #magicalmothweek hosted by the lovely and talented @kristinheldtart and @sandra.staub 🦋  Today\'s prompt is "Your Spirit Moth" and I thought mine s',
      url: 'https://www.instagram.com/tv/CRhOUgWJ2az/',
      embedId: '',
      category: 'tutorial',
      tags: ['magicalmothweek', 'vintagetags', 'ephemerawindows', 'snailmailideas', 'snailmail', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-985',
      platform: 'instagram',
      title: 'August Box Sneak peek 🌏 Old Maps: this theme is extra special to me as I\'ve ...',
      description: 'August Box Sneak peek 🌏 Old Maps: this theme is extra special to me as I\'ve always been fascinated by ancient maps and been collecting them since childhood! The August boxes are available for sale st',
      url: 'https://www.instagram.com/p/CRbdp7IJwkZ/',
      embedId: 'CRbdp7IJwkZ',
      category: 'box-reveal',
      tags: ['vintagecraftbox', 'vintagecraftsupplies', 'vintagecraftstore', 'augustbox', 'oldmaps', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CRbdp7IJwkZ/media/?size=m'
    },
    {
      id: 'ig-api-986',
      platform: 'instagram',
      title: 'Pre-travel journal preparation ✈️ do you ever prepare some pages in advance? ...',
      description: 'Pre-travel journal preparation ✈️ do you ever prepare some pages in advance? I like stamping a few pages before I go to avoid carrying too many supplies with me 🖤 Today I\'m off to the airport for a t',
      url: 'https://www.instagram.com/reel/CRWeh9vpplr/',
      embedId: 'CRWeh9vpplr',
      category: 'product-showcase',
      tags: ['traveljournal', 'traveljournalkit', 'travelthroughcrafts', 'travelnotebook', 'travelkit', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CRWeh9vpplr/media/?size=m'
    },
    {
      id: 'ig-api-987',
      platform: 'instagram',
      title: 'Butterfly Sticker Strips & Some projects I created with them 🦋 These are now...',
      description: 'Butterfly Sticker Strips & Some projects I created with them 🦋 These are now available in 8 different colors on my website in limited quantities 💛💚💙 #craftingvideo #craftingstore #craftingsupplies',
      url: 'https://www.instagram.com/reel/CROw-CgJvu8/',
      embedId: 'CROw-CgJvu8',
      category: 'product-showcase',
      tags: ['craftingvideo', 'craftingstore', 'craftingsupplies', 'craftingshop', 'stickershop', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CROw-CgJvu8/media/?size=m'
    },
    {
      id: 'ig-api-988',
      platform: 'instagram',
      title: 'Journaling in the beautiful @modest_goods scrapbooking journal using the bras...',
      description: 'Journaling in the beautiful @modest_goods scrapbooking journal using the brass hardware stickers curated by @boston.notebk check out her page to get this *free* printable! ⚜️  #journaladdict #journalf',
      url: 'https://www.instagram.com/reel/CRJzhyipkz6/',
      embedId: 'CRJzhyipkz6',
      category: 'journal-spread',
      tags: ['journaladdict', 'journalflow', 'journalingsupplies', 'journalingideas', 'journalspread', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CRJzhyipkz6/media/?size=m'
    },
    {
      id: 'ig-api-989',
      platform: 'instagram',
      title: 'Folder Bag made with the July Animal Box stamps 🖤🦒 These little pockets are...',
      description: 'Folder Bag made with the July Animal Box stamps 🖤🦒 These little pockets are so easy to make. Thank you to @madebytanja for the inspiration!  #mbt_folderbag #folderbag #diyfolder #diypocket #diycraft',
      url: 'https://www.instagram.com/reel/CRG4-XkJAOA/',
      embedId: 'CRG4-XkJAOA',
      category: 'tutorial',
      tags: ['mbt_folderbag', 'folderbag', 'diyfolder', 'diypocket', 'diycrafts', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CRG4-XkJAOA/media/?size=m'
    },
    {
      id: 'ig-api-990',
      platform: 'instagram',
      title: 'Here is the long version of the medieval style vellum envelope I posted in my...',
      description: 'Here is the long version of the medieval style vellum envelope I posted in my reels a few weeks ago 🖤 this will show you the step by step process and details 🖤 I used: * Vellum envelope * Medieval S',
      url: 'https://www.instagram.com/tv/CRCGwBJJcw_/',
      embedId: '',
      category: 'tutorial',
      tags: ['craftingvideo', 'craftwithme', 'craftingtutorial', 'envelopedesign', 'envelopedecoration', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-991',
      platform: 'instagram',
      title: 'From blank pages to travel journal inserts 🌍 I wanted to show you the 1st pa...',
      description: 'From blank pages to travel journal inserts 🌍 I wanted to show you the 1st part of the process in making these inserts for the new *Travel Journal Kits* on my website! Thank you for all your orders, t',
      url: 'https://www.instagram.com/reel/CQ8pahfJmOU/',
      embedId: 'CQ8pahfJmOU',
      category: 'product-showcase',
      tags: ['traveljournal', 'travelthroughcrafts', 'oldpaper', 'oldmaps', 'journaladdict', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CQ8pahfJmOU/media/?size=m'
    },
    {
      id: 'ig-api-992',
      platform: 'instagram',
      title: '4th of July Weekend Sale started in my shop 🎆 If you missed the newsletter s...',
      description: '4th of July Weekend Sale started in my shop 🎆 If you missed the newsletter secret sale, this is your chance!  This is a packing video I filmed for a very sweet customer @doodledots_29 who has been am',
      url: 'https://www.instagram.com/reel/CQ3jVWCJD53/',
      embedId: 'CQ3jVWCJD53',
      category: 'box-reveal',
      tags: ['asmrpapersounds', 'weekendsale', 'flashsale', 'stationerysale', 'washisale', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CQ3jVWCJD53/media/?size=m'
    },
    {
      id: 'ig-api-993',
      platform: 'instagram',
      title: 'Journaling the end of June using the Bird transparent Washi Tape and the myth...',
      description: 'Journaling the end of June using the Bird transparent Washi Tape and the mythical creatures stickers from the July box 🖤 I\'m excited to share a new journal post using the beautiful scrapbooking journ',
      url: 'https://www.instagram.com/reel/CQyh7lzpSSO/',
      embedId: 'CQyh7lzpSSO',
      category: 'journal-spread',
      tags: ['asmrstationery', 'asmrjournal', 'asmrjournaling', 'asmrcraft', 'asmr', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CQyh7lzpSSO/media/?size=m'
    },
    {
      id: 'ig-api-994',
      platform: 'instagram',
      title: 'July Box Packing 🦒🦚 with  boxes available in my shop, link in bio 🖤',
      description: 'July Box Packing 🦒🦚 with #asmrpapersounds boxes available in my shop, link in bio 🖤 #julysubscriptionbox #animalwashi #animalstickers #animaltheme #vintageanimals #animalillustrations #animalstamps',
      url: 'https://www.instagram.com/reel/CQqplD1JZ3B/',
      embedId: 'CQqplD1JZ3B',
      category: 'behind-the-scenes',
      tags: ['asmrpapersounds', 'julysubscriptionbox', 'animalwashi', 'animalstickers', 'animaltheme', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CQqplD1JZ3B/media/?size=m'
    },
    {
      id: 'ig-api-995',
      platform: 'instagram',
      title: 'I made these for on of my demo on @thecraftstoretv and just had to make even ...',
      description: 'I made these for on of my demo on @thecraftstoretv and just had to make even more because I just love how they ended up looking 😍 Hope you enjoy watching!  - I cut the slides with my @silhouette.inc ',
      url: 'https://www.instagram.com/tv/CQl0BD7q4vz/',
      embedId: '',
      category: 'tutorial',
      tags: ['vintageslides', 'vintagetags', 'diycrafts', 'diytags', 'diyslides', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-996',
      platform: 'instagram',
      title: 'Surprise Box packing! This is what is inside of the new batch 🖤😍 Limited av...',
      description: 'Surprise Box packing! This is what is inside of the new batch 🖤😍 Limited availability on this specific batch, order now through the link in my bio 📦 #surprisebox #surprisegift #surprisecraftbox #su',
      url: 'https://www.instagram.com/reel/CQjA7ump7X0/',
      embedId: 'CQjA7ump7X0',
      category: 'behind-the-scenes',
      tags: ['surprisebox', 'surprisegift', 'surprisecraftbox', 'subscription', 'subscriptionboxes', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CQjA7ump7X0/media/?size=m'
    },
    {
      id: 'ig-api-997',
      platform: 'instagram',
      title: 'I don\'t paint a lot but I really enjoyed watercoloring these floral pattern s...',
      description: 'I don\'t paint a lot but I really enjoyed watercoloring these floral pattern stamped cards 🌸🌼 which pattern do you prefer? Vote in my stories 😊  These stamps were just added to my shop today ❤️ the ',
      url: 'https://www.instagram.com/reel/CQd9PetpQGE/',
      embedId: 'CQd9PetpQGE',
      category: 'tutorial',
      tags: ['flora', 'floralstationery', 'floralstamps', 'flowerstamps', 'floralpatterns'],
      creator: 'CoraCreaCrafts',
      date: '2021-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CQd9PetpQGE/media/?size=m'
    },
    {
      id: 'ig-api-998',
      platform: 'instagram',
      title: 'July Box Sneak Peek 🦉🐂 July is all about wildlife and the animal world! It ...',
      description: 'July Box Sneak Peek 🦉🐂 July is all about wildlife and the animal world! It will include two postal wooden stamps, a forest clear stamp, a bird washi sticker, a vintage animal washi tape and more tha',
      url: 'https://www.instagram.com/p/CQYzRCBp9k0/',
      embedId: 'CQYzRCBp9k0',
      category: 'box-reveal',
      tags: ['coracreacrafts', 'coracreacraftbox', 'craftingsupplies', 'craftingsubscriptionbox', 'craftysubscription', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CQYzRCBp9k0/media/?size=m'
    },
    {
      id: 'ig-api-999',
      platform: 'instagram',
      title: 'Decorating a vellum envelope 💌 These are so fun for happy mail! All I used c...',
      description: 'Decorating a vellum envelope 💌 These are so fun for happy mail! All I used came from my shop aside from the envelope, link in bio 🖤 Medieval Washi Tapes, Book Series Stamp, Romantic Stickers (key), ',
      url: 'https://www.instagram.com/reel/CQUDvFHJL6C/',
      embedId: 'CQUDvFHJL6C',
      category: 'tutorial',
      tags: ['coracreacrafts', 'coracreacraftswashi', 'coracreawaxseals', 'craftingsupplies', 'craftingshop', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CQUDvFHJL6C/media/?size=m'
    },
    {
      id: 'ig-api-1000',
      platform: 'instagram',
      title: 'July Box Mini Theme Reveal 🖤🐺 A lot of you have been asking me about the Ju...',
      description: 'July Box Mini Theme Reveal 🖤🐺 A lot of you have been asking me about the July Box and I\'ve decided to share a pre-theme reveal with just 2 elements that will be part of the box! Can you guess the th',
      url: 'https://www.instagram.com/reel/CQOmqM_JNgh/',
      embedId: 'CQOmqM_JNgh',
      category: 'box-reveal',
      tags: ['coracreacrafts', 'coracreacraftbox', 'coracreacraftswashi', 'woodenstamps', 'animals', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CQOmqM_JNgh/media/?size=m'
    },
    {
      id: 'ig-api-1001',
      platform: 'instagram',
      title: 'About a year ago I received a custom order on Etsy for a Game of Thrones them...',
      description: 'About a year ago I received a custom order on Etsy for a Game of Thrones themed bookmark 🐺 since then I\'ve been making many of those, each different and unique. This time, i decided to use a wax seal',
      url: 'https://www.instagram.com/tv/CQLv6-iptMV/',
      embedId: '',
      category: 'tutorial',
      tags: ['bookmark', 'handmadebookmark', 'booklover', 'diybookmark', 'diycrafts', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-1002',
      platform: 'instagram',
      title: 'Wax Seal Samples 🤩 with the new latest additions to my shop: Happy Mail, Anc...',
      description: 'Wax Seal Samples 🤩 with the new latest additions to my shop: Happy Mail, Anchor, Swords and Clock ⏰ It\'s always so satisfying to make!  #waxsealshop #coracreacrafts #waxseals #waxcolor #waxstamp #wax',
      url: 'https://www.instagram.com/reel/CQGr1lfJYVD/',
      embedId: 'CQGr1lfJYVD',
      category: 'product-showcase',
      tags: ['waxsealshop', 'coracreacrafts', 'waxseals', 'waxcolor', 'waxstamp', 'medieval'],
      creator: 'CoraCreaCrafts',
      date: '2021-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CQGr1lfJYVD/media/?size=m'
    },
    {
      id: 'ig-api-1003',
      platform: 'instagram',
      title: 'ASMR Saturday with this short video of the Treasure Box prep! 📦🖤 This box i...',
      description: 'ASMR Saturday with this short video of the Treasure Box prep! 📦🖤 This box is now sold out but you can pre-order for the next Edition (Aug/Sep) 🤗 #asmrcraft #asmrpaper #asmrstationery #coracreacraft',
      url: 'https://www.instagram.com/reel/CQB0GIzJhpS/',
      embedId: 'CQB0GIzJhpS',
      category: 'behind-the-scenes',
      tags: ['asmrcraft', 'asmrpaper', 'asmrstationery', 'coracreacrafts', 'coracreacraftbox', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CQB0GIzJhpS/media/?size=m'
    },
    {
      id: 'ig-api-1004',
      platform: 'instagram',
      title: 'I\'ve been wanting to make more use of my laminator lately so I decided to mak...',
      description: 'I\'ve been wanting to make more use of my laminator lately so I decided to make some new simple pieces. I called them cards but they could really be finished and used for a number of purposes!  💡Punch',
      url: 'https://www.instagram.com/tv/CP3c1TIJ_Rm/',
      embedId: '',
      category: 'tutorial',
      tags: ['tutorial', 'craftwithme', 'coracreacrafts', 'craftingtutorial', 'craftingideas', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-1005',
      platform: 'instagram',
      title: 'First page of my new journal 📖✂️ Isn\'t the start of a new journal always exc...',
      description: 'First page of my new journal 📖✂️ Isn\'t the start of a new journal always exciting? 😁 For this first page I used my very new Medieval Series Washi Tapes and some of the Knight Clear Stamps from my sh',
      url: 'https://www.instagram.com/reel/CPqn2fHJDBH/',
      embedId: 'CPqn2fHJDBH',
      category: 'journal-spread',
      tags: ['medievaldesign', 'journaling', 'coracreacrafts', 'medievalwashi', 'knights', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CPqn2fHJDBH/media/?size=m'
    },
    {
      id: 'ig-api-1006',
      platform: 'instagram',
      title: 'I wanted to create tags with a twist, used pre-made tags with a fun shape and...',
      description: 'I wanted to create tags with a twist, used pre-made tags with a fun shape and cut some windows on the inside with my @sizzix cutting machine ✂️ Love how different those look with the pressed flowers a',
      url: 'https://www.instagram.com/tv/CPnqG-IJ-v0/',
      embedId: '',
      category: 'tutorial',
      tags: ['coracreacrafts', 'diycrafts', 'diytags', 'tagswithwindows', 'windows', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-1007',
      platform: 'instagram',
      title: 'Flash Sale for Memorial Day in my shop 🖤📖 Today only 🤗 Link in bio',
      description: 'Flash Sale for Memorial Day in my shop 🖤📖 Today only 🤗 Link in bio #memorialday #memorialdaysale #flashsale #coracreacrafts #craftingsupplies #craftingshop #washitapeshop #washitape #stickershop #s',
      url: 'https://www.instagram.com/reel/CPieiVVJt-N/',
      embedId: 'CPieiVVJt-N',
      category: 'behind-the-scenes',
      tags: ['memorialday', 'memorialdaysale', 'flashsale', 'coracreacrafts', 'craftingsupplies', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CPieiVVJt-N/media/?size=m'
    },
    {
      id: 'ig-api-1008',
      platform: 'instagram',
      title: 'Treasure Craft Box Highlights and Packing with some satisfying  sounds 📦🖤 T...',
      description: 'Treasure Craft Box Highlights and Packing with some satisfying #asmr sounds 📦🖤 This edition of the box is available for sale on my website (June-July Edition) 🎁 #coracreacraftbox #coracreacrafts #s',
      url: 'https://www.instagram.com/reel/CPa4mARp24f/',
      embedId: 'CPa4mARp24f',
      category: 'behind-the-scenes',
      tags: ['asmr', 'coracreacraftbox', 'coracreacrafts', 'surprisegift', 'subscription', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CPa4mARp24f/media/?size=m'
    },
    {
      id: 'ig-api-1009',
      platform: 'instagram',
      title: 'This is my very first attempt at making a pop up: I didn\'t follow the pop up ...',
      description: 'This is my very first attempt at making a pop up: I didn\'t follow the pop up technique to make it and just "winged" it 😅🙈 Either way I really enjoyed the process of putting each piece up and creatin',
      url: 'https://www.instagram.com/tv/CPVuSSiJf3U/',
      embedId: '',
      category: 'box-reveal',
      tags: ['scribeepopupchallenge', 'coracreacrafts', 'craftwithme', 'journalingcommunity', 'vintagejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-1010',
      platform: 'instagram',
      title: 'A new episode of order packing 📦🖤 This one had lots of washi, stamps and st...',
      description: 'A new episode of order packing 📦🖤 This one had lots of washi, stamps and stickers from my shop and everything fit perfectly in the box 😁 #coracreacrafts #packanorderwithme #packagingdesign #packedw',
      url: 'https://www.instagram.com/reel/CPTVr-LJXHj/',
      embedId: 'CPTVr-LJXHj',
      category: 'behind-the-scenes',
      tags: ['coracreacrafts', 'packanorderwithme', 'packagingdesign', 'packedwithlove', 'packingorders', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CPTVr-LJXHj/media/?size=m'
    },
    {
      id: 'ig-api-1011',
      platform: 'instagram',
      title: 'Nautical Journal Spread created with the June Vintage Craft Box supplies ⛵📖 ...',
      description: 'Nautical Journal Spread created with the June Vintage Craft Box supplies ⛵📖 I haven\'t used my journal in so long, I really missed it. Thank you to the talented people who inspired me to get back to i',
      url: 'https://www.instagram.com/reel/CPQip3cpMWq/',
      embedId: 'CPQip3cpMWq',
      category: 'journal-spread',
      tags: ['nauticalstickers', 'nauticalstyle', 'nautical', 'nauticaljournal', 'boatspread', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CPQip3cpMWq/media/?size=m'
    },
    {
      id: 'ig-api-1012',
      platform: 'instagram',
      title: 'Mini booklets 📖 I built on the idea from one of my previous reels - from the...',
      description: 'Mini booklets 📖 I built on the idea from one of my previous reels - from the simple folder I added various papers to create booklets 🖤 Using the Large Frame Stamps and Antique Maps Background Sticke',
      url: 'https://www.instagram.com/reel/CPLh6w1J3Wl/',
      embedId: 'CPLh6w1J3Wl',
      category: 'tutorial',
      tags: ['coracreacrafts', 'minibook', 'minibooklet', 'bookletcover', 'bookstamps', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CPLh6w1J3Wl/media/?size=m'
    },
    {
      id: 'ig-api-1013',
      platform: 'instagram',
      title: 'Tried something a little different for these stickers with sea/wave like patt...',
      description: 'Tried something a little different for these stickers with sea/wave like patterns 🌊 🐡 these are also part of the June Vintage Craft Box ⛵ #nauticalstyle #nautical #fishstickers #nauticalstickers #co',
      url: 'https://www.instagram.com/reel/CPGhXPFJtxT/',
      embedId: 'CPGhXPFJtxT',
      category: 'box-reveal',
      tags: ['nauticalstyle', 'nautical', 'fishstickers', 'nauticalstickers', 'coracreacrafts', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CPGhXPFJtxT/media/?size=m'
    },
    {
      id: 'ig-api-1014',
      platform: 'instagram',
      title: 'I created these 3 postcards to remember what traveling is like, and maybe one...',
      description: 'I created these 3 postcards to remember what traveling is like, and maybe one day I can see these places again and send my postcards 🙏😁 Which one is your favorite? Prague, Switzerland or Thailand?  ',
      url: 'https://www.instagram.com/tv/CPDqtZepLh1/',
      embedId: '',
      category: 'tutorial',
      tags: ['coracreacrafts', 'diycrafts', 'diypostcardswap', 'postcardstamp', 'postcard', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-1015',
      platform: 'instagram',
      title: 'June Box Theme Reveal and Sneak Peek of some of the items you will get ⛵🐡🐙 ...',
      description: 'June Box Theme Reveal and Sneak Peek of some of the items you will get ⛵🐡🐙 This month is "Nautical" with lots of boats and marine life 🖤 summer holiday at the sea feeling ⚓ #vintagebox #coracreacra',
      url: 'https://www.instagram.com/p/CO-ixX5pI9j/',
      embedId: 'CO-ixX5pI9j',
      category: 'box-reveal',
      tags: ['vintagebox', 'coracreacraftbox', 'coracreacrafts', 'vintagecraftbox', 'vintagesupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CO-ixX5pI9j/media/?size=m'
    },
    {
      id: 'ig-api-1016',
      platform: 'instagram',
      title: 'Packing a double order with a Surprise box and some other goodies 🖤 This was...',
      description: 'Packing a double order with a Surprise box and some other goodies 🖤 This was another customer request to film their packing video 📦 Enjoy @cheyjournaler  #packanorderwithme #packedwithlove #packingo',
      url: 'https://www.instagram.com/reel/CO0UlppJJtj/',
      embedId: 'CO0UlppJJtj',
      category: 'behind-the-scenes',
      tags: ['packanorderwithme', 'packedwithlove', 'packingorders', 'packingdesign', 'packingvideo', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CO0UlppJJtj/media/?size=m'
    },
    {
      id: 'ig-api-1017',
      platform: 'instagram',
      title: 'I haven\'t made a card in a long time but not long ago was my best friend\'s bi...',
      description: 'I haven\'t made a card in a long time but not long ago was my best friend\'s birthday and I thought it would be a great occasion to try something new. I\'ve had these oxyde sprays from ranger for some ti',
      url: 'https://www.instagram.com/tv/COyDhjYJObG/',
      embedId: '',
      category: 'product-showcase',
      tags: ['cardmaking', 'carddesign', 'cardmakingday', 'cardmakingideas', 'coffeestain', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-1018',
      platform: 'instagram',
      title: 'Card, little pocket, booklet cover.. I don\'t know yet but I so enjoyed making...',
      description: 'Card, little pocket, booklet cover.. I don\'t know yet but I so enjoyed making it ☺️📜 Using the Explorer Frame Stamp and the Antique Maps Background Stickers from my shop 🖤  #coracreacrafts #diyfolde',
      url: 'https://www.instagram.com/reel/COvC1KUJ5Fr/',
      embedId: 'COvC1KUJ5Fr',
      category: 'tutorial',
      tags: ['coracreacrafts', 'diyfolder', 'ephemerafolder', 'ephemerapocket', 'bookletcover', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/COvC1KUJ5Fr/media/?size=m'
    },
    {
      id: 'ig-api-1019',
      platform: 'instagram',
      title: 'Tricolor wax seal for this special Mother\'s Day gift ❤️ Celebrating all mothe...',
      description: 'Tricolor wax seal for this special Mother\'s Day gift ❤️ Celebrating all mothers, strong women and crafty souls this weekend with a special sale on my website 🎉 This includes this beautiful heart wax ',
      url: 'https://www.instagram.com/reel/COnjYwbpOiV/',
      embedId: 'COnjYwbpOiV',
      category: 'behind-the-scenes',
      tags: ['heartwaxseal', 'waxsealshop', 'waxseals', 'waxstamp', 'giftwrapping', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/COnjYwbpOiV/media/?size=m'
    },
    {
      id: 'ig-api-1020',
      platform: 'instagram',
      title: 'I\'ve been wanting to make some wrapping paper for a while and here\'s the resu...',
      description: 'I\'ve been wanting to make some wrapping paper for a while and here\'s the result. Super simple to make and you can really personalize it any way you want ✂️  For mine I used: * Plain Craft Wrapping Pap',
      url: 'https://www.instagram.com/tv/COiWsTzp5D4/',
      embedId: '',
      category: 'tutorial',
      tags: ['coracreacrafts', 'diywrappingpaper', 'wrappingpaper', 'packagingdesign', 'giftideas', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-1021',
      platform: 'instagram',
      title: 'Prepared some sets of Runes Foil Stickers to add to a May Vintage Craft Box o...',
      description: 'Prepared some sets of Runes Foil Stickers to add to a May Vintage Craft Box order 💚💛💙 Should I create more foil sticker designs for my shop?  #coracreacrafts #coracreastickers #coracreacraftbox #fo',
      url: 'https://www.instagram.com/reel/COfoQt7pZvv/',
      embedId: 'COfoQt7pZvv',
      category: 'behind-the-scenes',
      tags: ['coracreacrafts', 'coracreastickers', 'coracreacraftbox', 'foilingprocess', 'foilpaperart', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/COfoQt7pZvv/media/?size=m'
    },
    {
      id: 'ig-api-1022',
      platform: 'instagram',
      title: 'Sneak peek into the Surprise Box for this month 🤗❤️ My favorite part: the li...',
      description: 'Sneak peek into the Surprise Box for this month 🤗❤️ My favorite part: the little book with 50+ floral stickers 🌼 Check out my website to find out about my 3 types of boxes 📦 #coracreacrafts #coracr',
      url: 'https://www.instagram.com/reel/COadzsyp8c0/',
      embedId: 'COadzsyp8c0',
      category: 'box-reveal',
      tags: ['coracreacrafts', 'coracreacraftbox', 'surprisebox', 'surprisegift', 'vintagebox', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/COadzsyp8c0/media/?size=m'
    },
    {
      id: 'ig-api-1023',
      platform: 'instagram',
      title: 'New Frames Stamps 🖤🖼️ so beautiful and intricate! Can\'t tell which of the 6...',
      description: 'New Frames Stamps 🖤🖼️ so beautiful and intricate! Can\'t tell which of the 6 is my favorite 🤷🏻‍♀️ Available in my shop now 👉 link in bio #newarrivals #stamping #stampvideo #stampshop #stampdesign ',
      url: 'https://www.instagram.com/reel/COQViOPpUR4/',
      embedId: 'COQViOPpUR4',
      category: 'product-showcase',
      tags: ['newarrivals', 'stamping', 'stampvideo', 'stampshop', 'stampdesign'],
      creator: 'CoraCreaCrafts',
      date: '2021-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/COQViOPpUR4/media/?size=m'
    },
    {
      id: 'ig-api-1024',
      platform: 'instagram',
      title: 'As I\'m currently packing the May boxes and shipping them out soon, I wanted t...',
      description: 'As I\'m currently packing the May boxes and shipping them out soon, I wanted to share a little tutorial using the items from the box. I got inspired to create with trading card sleeves by @lucyofthedea',
      url: 'https://www.instagram.com/tv/CONoQ3Mp16l/',
      embedId: '',
      category: 'tutorial',
      tags: ['coracreacrafts', 'creativedays', 'maybox', 'vintagecraftbox', 'journalsubscription', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-1025',
      platform: 'instagram',
      title: 'Special gift wrapping for a Mother\'s Day Gift order ❤️ I added a new option o...',
      description: 'Special gift wrapping for a Mother\'s Day Gift order ❤️ I added a new option on my shop to get your order gift wrapped with beautiful details and a hand written note 🖋️ #giftideas #giftwrapping #surpr',
      url: 'https://www.instagram.com/reel/COIsWi9ptuC/',
      embedId: 'COIsWi9ptuC',
      category: 'behind-the-scenes',
      tags: ['giftideas', 'giftwrapping', 'surprisegift', 'surprisebox', 'stationerystore', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/COIsWi9ptuC/media/?size=m'
    },
    {
      id: 'ig-api-1026',
      platform: 'instagram',
      title: 'Paper clips revisited 🖤 Adding a wax stamp on top! Wax seals and wax colors ...',
      description: 'Paper clips revisited 🖤 Adding a wax stamp on top! Wax seals and wax colors from my shop #waxsealshop #waxseals #waxstamp #coracreacrafts #craftingsupplies #craftingideas #paperclips #clips #diypaper',
      url: 'https://www.instagram.com/reel/COA65YGpvQK/',
      embedId: 'COA65YGpvQK',
      category: 'tutorial',
      tags: ['waxsealshop', 'waxseals', 'waxstamp', 'coracreacrafts', 'craftingsupplies', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/COA65YGpvQK/media/?size=m'
    },
    {
      id: 'ig-api-1027',
      platform: 'instagram',
      title: 'I wanted to create something to hold samples of these beautiful papers and la...',
      description: 'I wanted to create something to hold samples of these beautiful papers and labels for my snail mail or to use when I\'m on the go 📜  Created using: ✨ Cardboard Paper ✨ Papers from the Ephemera Paper B',
      url: 'https://www.instagram.com/tv/CN-UMK3J2sU/',
      embedId: '',
      category: 'tutorial',
      tags: ['coracreacrafts', 'dyifolder', 'ephemera', 'ephemerapocket', 'ephemerapapers', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-1028',
      platform: 'instagram',
      title: 'Its been a while since I last decorated an envelope but I have a few long ove...',
      description: 'Its been a while since I last decorated an envelope but I have a few long overdue #snailmail packs to prepare so this was the perfect opportunity!   I used various papers, stickers and washi tapes fro',
      url: 'https://www.instagram.com/tv/CN5SrqApbdK/',
      embedId: '',
      category: 'tutorial',
      tags: ['snailmail', 'envelopedesign', 'envelopedecoration', 'craftwithme', 'craftingsupplies', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-1029',
      platform: 'instagram',
      title: 'Treasure Craft Box packing 📦🖤 The lovely person who ordered this asked me t...',
      description: 'Treasure Craft Box packing 📦🖤 The lovely person who ordered this asked me to make a special video of her order, so here it is! 😃 Enjoy all the #asmr satisfying sounds! This edition of the box is so',
      url: 'https://www.instagram.com/reel/CN2ZmbPJxnK/',
      embedId: 'CN2ZmbPJxnK',
      category: 'behind-the-scenes',
      tags: ['asmr', 'coracreacraftbox', 'coracreacrafts', 'surprisegift', 'subscription', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CN2ZmbPJxnK/media/?size=m'
    },
    {
      id: 'ig-api-1030',
      platform: 'instagram',
      title: 'Ever wondered how foil washi tape is made? Here\'s a quick video showing you h...',
      description: 'Ever wondered how foil washi tape is made? Here\'s a quick video showing you how! 🖤📦 These beautiful tapes are part of the May "Flora" Vintage Craft Box 🌼🌿 #washilover #washitapeshop #washitape #fo',
      url: 'https://www.instagram.com/reel/CNvAaTiJ1Cw/',
      embedId: 'CNvAaTiJ1Cw',
      category: 'box-reveal',
      tags: ['washilover', 'washitapeshop', 'washitape', 'foilingprocess', 'foilwashitape', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CNvAaTiJ1Cw/media/?size=m'
    },
    {
      id: 'ig-api-1031',
      platform: 'instagram',
      title: 'May Box Theme Reveal and Sneak Peek of some of the items you will get 🌿🌷🌼 ...',
      description: 'May Box Theme Reveal and Sneak Peek of some of the items you will get 🌿🌷🌼 This month is all about Flora! Orders are now open - link in bio ♥️ #vintagebox #coracreacraftbox #coracreacrafts #vintagec',
      url: 'https://www.instagram.com/p/CNpoHwbJ8K1/',
      embedId: 'CNpoHwbJ8K1',
      category: 'box-reveal',
      tags: ['vintagebox', 'coracreacraftbox', 'coracreacrafts', 'vintagecraftbox', 'vintagesupplies', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CNpoHwbJ8K1/media/?size=m'
    },
    {
      id: 'ig-api-1032',
      platform: 'instagram',
      title: 'This is another super easy tutorial on how to make faux-stamps pieces/spring ...',
      description: 'This is another super easy tutorial on how to make faux-stamps pieces/spring ephemera for snail mail, journal decorating etc.  I\'m using:  - 2 different types of papers including one vellum paper shee',
      url: 'https://www.instagram.com/tv/CNm9zG1pKL8/',
      embedId: '',
      category: 'tutorial',
      tags: ['dyistamps', 'fauxstamps', 'tutorialsvideos', 'craftwithme', 'craftingsupplies', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-1033',
      platform: 'instagram',
      title: 'A few days more to participate in this giveaway collaboration with @dojo_scra...',
      description: 'A few days more to participate in this giveaway collaboration with @dojo_scrap 🖤 check our accounts for more ☺️ #happymail #snailmail #outgoingvintagemail #vintagejournalinggiveaway #vintagejournal #',
      url: 'https://www.instagram.com/reel/CNm3MLmpbIz/',
      embedId: 'CNm3MLmpbIz',
      category: 'product-showcase',
      tags: ['happymail', 'snailmail', 'outgoingvintagemail', 'vintagejournalinggiveaway', 'vintagejournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CNm3MLmpbIz/media/?size=m'
    },
    {
      id: 'ig-api-1034',
      platform: 'instagram',
      title: 'Foiling 💚💙💛 Valheim Rune Stickers. What\'s the best color',
      description: 'Foiling 💚💙💛 Valheim Rune Stickers. What\'s the best color?#satisfyingvideos #satisfying #foiling #foilpaperart #foilpaper #foilstickers #runes #runestickers #valheimgame #valheim #pagandesign #stick',
      url: 'https://www.instagram.com/reel/CNkdvXAJiIH/',
      embedId: 'CNkdvXAJiIH',
      category: 'product-showcase',
      tags: ['satisfyingvideos', 'satisfying', 'foiling', 'foilpaperart', 'foilpaper', 'stickers'],
      creator: 'CoraCreaCrafts',
      date: '2021-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CNkdvXAJiIH/media/?size=m'
    },
    {
      id: 'ig-api-1035',
      platform: 'instagram',
      title: 'on a rainy Saturday 🌧️ Love love the "Old Things" Craft Part Washi Tape 🖤',
      description: '#packanorderwithme on a rainy Saturday 🌧️ Love love the "Old Things" Craft Part Washi Tape 🖤 #orderprocess #coracreacrafts #stationerystore #washilover #stickershop #washitapeshop #orderpacking #let',
      url: 'https://www.instagram.com/reel/CNfrYQlpMIE/',
      embedId: 'CNfrYQlpMIE',
      category: 'behind-the-scenes',
      tags: ['packanorderwithme', 'orderprocess', 'coracreacrafts', 'stationerystore', 'washilover', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CNfrYQlpMIE/media/?size=m'
    },
    {
      id: 'ig-api-1036',
      platform: 'instagram',
      title: 'Going over all the items that are part of the Giveaway hosted by @dojo_scrap ...',
      description: 'Going over all the items that are part of the Giveaway hosted by @dojo_scrap and myself. This is what you and one of your friends could win 🖤 Participate by visiting the main Giveaway post in my feed',
      url: 'https://www.instagram.com/tv/CNcpwGCp2QD/',
      embedId: '',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2021-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-1037',
      platform: 'instagram',
      title: 'This is a short tutorial on how to make very easy small tags for snail mail a...',
      description: 'This is a short tutorial on how to make very easy small tags for snail mail and other crafts in a vintage style 🖤🏷️ I\'m using: - Small Sizzix machine and die-cuts (also the option to cut tags by han',
      url: 'https://www.instagram.com/tv/CNaN85ApkLc/',
      embedId: '',
      category: 'tutorial',
      tags: ['diytags', 'tagmaking', 'tags', 'vintagetags', 'vintagesupplies', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-1038',
      platform: 'instagram',
      title: 'Highlights from the Treasure Box 🖤🤗 All pre-orders for the April/May editio...',
      description: 'Highlights from the Treasure Box 🖤🤗 All pre-orders for the April/May edition were shipped 💌 this edition is still available for order until end of April or when sold out!  #treasurebox #coracreacra',
      url: 'https://www.instagram.com/reel/CNNWwKMJ5Oq/',
      embedId: 'CNNWwKMJ5Oq',
      category: 'box-reveal',
      tags: ['treasurebox', 'coracreacraftbox', 'coracreacrafts', 'bimonthlybox', 'stationerybox', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CNNWwKMJ5Oq/media/?size=m'
    },
    {
      id: 'ig-api-1039',
      platform: 'instagram',
      title: 'Medieval Oddities Washi Tape 🖤 This fun heraldry vintage design will be part...',
      description: 'Medieval Oddities Washi Tape 🖤 This fun heraldry vintage design will be part of the April Vintage Craft Box ☺️ #aprilbox #vintagebox #vintagesupplies #vintagecraftsupplies #craftingsupplies #washitap',
      url: 'https://www.instagram.com/reel/CNLYCj0peiD/',
      embedId: 'CNLYCj0peiD',
      category: 'box-reveal',
      tags: ['aprilbox', 'vintagebox', 'vintagesupplies', 'vintagecraftsupplies', 'craftingsupplies', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CNLYCj0peiD/media/?size=m'
    },
    {
      id: 'ig-api-1040',
      platform: 'instagram',
      title: 'Time for another  episode 🤗 they\'re always so fun to make - and I experiment...',
      description: 'Time for another #packanorderwithme episode 🤗 they\'re always so fun to make - and I experimented something new with the little arm transition at the end 🙈🎶  #packingorders #packagingdesign #paperad',
      url: 'https://www.instagram.com/reel/CNFtvMNp6x0/',
      embedId: 'CNFtvMNp6x0',
      category: 'behind-the-scenes',
      tags: ['packanorderwithme', 'packingorders', 'packagingdesign', 'paperaddict', 'coracreacrafts', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CNFtvMNp6x0/media/?size=m'
    },
    {
      id: 'ig-api-1041',
      platform: 'instagram',
      title: 'Ephemera Pocket DIY using the April Box Anatomy items 🖤 full tutorial to cre...',
      description: 'Ephemera Pocket DIY using the April Box Anatomy items 🖤 full tutorial to create a large pocket like this is available on my IGTV ✂️   Some of the items included in the April Box:  - Ephemera Paper Bu',
      url: 'https://www.instagram.com/reel/CNDmE12Jw5x/',
      embedId: 'CNDmE12Jw5x',
      category: 'tutorial',
      tags: ['vintagecraftstore', 'coracreacraftbox', 'coracreacrafts', 'vintagebox', 'subscription', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CNDmE12Jw5x/media/?size=m'
    },
    {
      id: 'ig-api-1042',
      platform: 'instagram',
      title: 'New Wax Seals and Wax colors just arrived in my shop 🧡🖤💜💙 I tested all of...',
      description: 'New Wax Seals and Wax colors just arrived in my shop 🧡🖤💜💙 I tested all of them by making wax seal samples 🤗 so enjoyed this super satisfying process!  P.s: special discount on all new wax seals j',
      url: 'https://www.instagram.com/reel/CNAkZQoJCHO/',
      embedId: 'CNAkZQoJCHO',
      category: 'product-showcase',
      tags: ['waxseal', 'wax', 'waxsealstamp', 'waxsealshop', 'coracreacrafts', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CNAkZQoJCHO/media/?size=m'
    },
    {
      id: 'ig-api-1043',
      platform: 'instagram',
      title: 'Made this quick sticker pocket for something exciting coming soon ✂️🖤 hint: ...',
      description: 'Made this quick sticker pocket for something exciting coming soon ✂️🖤 hint: you may be able to win this 😛  #stickershop #stickerpockets #stickeraddict #stickers #coracreastickers #coracreacrafts #mi',
      url: 'https://www.instagram.com/reel/CM4lfAMJghT/',
      embedId: 'CM4lfAMJghT',
      category: 'product-showcase',
      tags: ['stickershop', 'stickerpockets', 'stickeraddict', 'stickers', 'coracreastickers', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CM4lfAMJghT/media/?size=m'
    },
    {
      id: 'ig-api-1044',
      platform: 'instagram',
      title: 'Making paper bundles for the April boxes 🖤 And the sound of shuffling papers...',
      description: 'Making paper bundles for the April boxes 🖤 And the sound of shuffling papers 📜 Link in bio!  #aprilbox #coracreacraftbox #coracreacrafts #packingorders #p',
      url: 'https://www.instagram.com/reel/CMz80ETJcZj/',
      embedId: 'CMz80ETJcZj',
      category: 'behind-the-scenes',
      tags: ['aprilbox', 'coracreacraftbox', 'coracreacrafts', 'packingorders', 'p'],
      creator: 'CoraCreaCrafts',
      date: '2021-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CMz80ETJcZj/media/?size=m'
    },
    {
      id: 'ig-api-1045',
      platform: 'instagram',
      title: 'This is a short tutorial on how to make a big pocket out of empty notepad cov...',
      description: 'This is a short tutorial on how to make a big pocket out of empty notepad covers ✂️📃   I had so much fun making this pocket ☺️ from putting the frame together to decorating it and adding all the litt',
      url: 'https://www.instagram.com/tv/CMw469GJGXR/',
      embedId: '',
      category: 'tutorial',
      tags: ['diypocket', 'diyephemerapocket', 'snailmail', 'snailmailideas', 'snailmailpocket', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-1046',
      platform: 'instagram',
      title: 'April Box Sneak Peek - Anatomy theme 🖤🩺 here are some of the items you will...',
      description: 'April Box Sneak Peek - Anatomy theme 🖤🩺 here are some of the items you will get in this Vintage Craft Supplies box! I\'m very excited about this theme as it\'s different from everything I\'ve done so f',
      url: 'https://www.instagram.com/p/CMuPhc4Jd5p/',
      embedId: 'CMuPhc4Jd5p',
      category: 'box-reveal',
      tags: ['coracreacraftbox', 'coracreacrafts', 'vintagesupplies', 'craftingsupplies', 'subboxaddict', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CMuPhc4Jd5p/media/?size=m'
    },
    {
      id: 'ig-api-1047',
      platform: 'instagram',
      title: 'Are you more roll or sample when it comes to washi tapes? 🖤',
      description: 'Are you more roll or sample when it comes to washi tapes? 🖤  Should I offer samples in my shop? Vote in my stories! 🤗 #washilover #washitapeshop #washitape #coracreacrafts #coracreacraftswashi #wash',
      url: 'https://www.instagram.com/reel/CMpksN4pWo-/',
      embedId: 'CMpksN4pWo-',
      category: 'product-showcase',
      tags: ['washilover', 'washitapeshop', 'washitape', 'coracreacrafts', 'coracreacraftswashi', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CMpksN4pWo-/media/?size=m'
    },
    {
      id: 'ig-api-1048',
      platform: 'instagram',
      title: 'Here\'s a little intro & sneak peek into my new Treasure box 🖤 this is the la...',
      description: 'Here\'s a little intro & sneak peek into my new Treasure box 🖤 this is the largest of all my subscription boxes with LOTS of items inside including some stationery and deco pieces - visit my website f',
      url: 'https://www.instagram.com/tv/CMmsbJVphz3/',
      embedId: '',
      category: 'box-reveal',
      tags: ['coracreacrafts', 'coracreacraftbox', 'treasurebox', 'vintagesupplies', 'stationerybox', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-1049',
      platform: 'instagram',
      title: 'It\'s been a while since I last showed my Encyclopedia stickers 🖤 They come i...',
      description: 'It\'s been a while since I last showed my Encyclopedia stickers 🖤 They come in three styles - Botanical (the one in this video), Geology, and Natural History 🦇 These large sheets are available in my ',
      url: 'https://www.instagram.com/reel/CMkJ43vprsW/',
      embedId: 'CMkJ43vprsW',
      category: 'product-showcase',
      tags: ['coracreacrafts', 'coracreastickers', 'encyclopedia', 'encyclopediastickers', 'vintagesupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CMkJ43vprsW/media/?size=m'
    },
    {
      id: 'ig-api-1050',
      platform: 'instagram',
      title: 'Vellum Papers 🖤 This video also serves as a theme reveal for the April Vinta...',
      description: 'Vellum Papers 🖤 This video also serves as a theme reveal for the April Vintage Craft Box: Anatomy 🩺 vintage medical illustration, ancient remedies, bodies and a few mythological creatures #coracreac',
      url: 'https://www.instagram.com/reel/CMe3-ZPJmsb/',
      embedId: 'CMe3-ZPJmsb',
      category: 'box-reveal',
      tags: ['coracreacraftbox', 'coracreacrafts', 'vintagesupplies', 'vintagecraftbox', 'vintagecraftstore', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CMe3-ZPJmsb/media/?size=m'
    },
    {
      id: 'ig-api-1051',
      platform: 'instagram',
      title: 'Here\'s a quick and simple idea using some of the supplies from the butterfly ...',
      description: 'Here\'s a quick and simple idea using some of the supplies from the butterfly March Box 🦋 I wanted to treat my neighbors with a little something and wish them a Happy Spring 🌺 (although believe it or',
      url: 'https://www.instagram.com/tv/CMcVSXnpiUW/',
      embedId: '',
      category: 'box-reveal',
      tags: ['whattomakewith', 'marchbox', 'subboxaddict', 'subscription', 'coracreacraftbox', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-1052',
      platform: 'instagram',
      title: 'Pack an order with me in 30 seconds! If only it was so quick in real life 😅...',
      description: 'Pack an order with me in 30 seconds! If only it was so quick in real life 😅😉 I always finish the process by writing the name of the person on the box for a little personalized touch 🖤 #packanorderw',
      url: 'https://www.instagram.com/reel/CMUu1MDJJ3h/',
      embedId: 'CMUu1MDJJ3h',
      category: 'behind-the-scenes',
      tags: ['packanorderwithme', 'orderprocess', 'coracreacrafts', 'stationerystore', 'washilover', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CMUu1MDJJ3h/media/?size=m'
    },
    {
      id: 'ig-api-1053',
      platform: 'instagram',
      title: '✨REP ANNOUNCEMENT✨ Happy to announce my very first Reps for this season ☺️🌺 ...',
      description: '✨REP ANNOUNCEMENT✨ Happy to announce my very first Reps for this season ☺️🌺 I\'m very excited to welcome these talented and lovely ladies:  🖤 @canary.diaries  🖤 @chumichangaaa  🖤 @greetingsbyg  🖤 ',
      url: 'https://www.instagram.com/p/CMPhokSpz7O/',
      embedId: 'CMPhokSpz7O',
      category: 'box-reveal',
      tags: ['coracreacrafts', 'repsearch', 'repannouncement', 'brandrep', 'brandrepannouncement', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CMPhokSpz7O/media/?size=m'
    },
    {
      id: 'ig-api-1054',
      platform: 'instagram',
      title: 'Yesterday we finally reached temperatures worthy of Spring 🌸☀️ So I got very...',
      description: 'Yesterday we finally reached temperatures worthy of Spring 🌸☀️ So I got very inspired and created this short envelope tutorial with some new stardust paper I got and the new Spring Flowers Wooden Sta',
      url: 'https://www.instagram.com/tv/CMM98fmJMcj/',
      embedId: '',
      category: 'tutorial',
      tags: ['spring', 'springflowers', 'woodenstamps', 'coracreacrafts', 'flowerstamps'],
      creator: 'CoraCreaCrafts',
      date: '2021-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-1055',
      platform: 'instagram',
      title: 'Peek into today\'s preparation of the March Boxes 🦋 A lot of time goes into t...',
      description: 'Peek into today\'s preparation of the March Boxes 🦋 A lot of time goes into the details like rolling the newsprint papers and wrapping them with natural twine. There\'s also a little reveal of new inse',
      url: 'https://www.instagram.com/reel/CMIIhQuAlji/',
      embedId: 'CMIIhQuAlji',
      category: 'box-reveal',
      tags: ['vintagesupplies', 'vintagecraftstore', 'craftingsupplies', 'cratejoybox', 'vintagecraftbox', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CMIIhQuAlji/media/?size=m'
    },
    {
      id: 'ig-api-1056',
      platform: 'instagram',
      title: '✨ REP SEARCH ✨ Closed! But I\'ll be reopening the program every 2 months',
      description: '✨ REP SEARCH ✨ Closed! But I\'ll be reopening the program every 2 months!  Excited to announce I\'m looking for a few reps for my shop 🖤 Apply if you love crafting, journaling, scrapbooking, enjoy my p',
      url: 'https://www.instagram.com/reel/CMClsfQJ95L/',
      embedId: 'CMClsfQJ95L',
      category: 'tutorial',
      tags: ['repsearch', 'coracreacrafts', 'vintagesupplies', 'vintagecraftstore', 'stationerystore', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CMClsfQJ95L/media/?size=m'
    },
    {
      id: 'ig-api-1057',
      platform: 'instagram',
      title: 'This is a short tutorial on how to decorate extra wide tags in a vintage styl...',
      description: 'This is a short tutorial on how to decorate extra wide tags in a vintage style 🖤🏷️ I\'m using: - Large Book Series Stamps - Monogram Stamps - Vintage Label Stickers - Wax Seals All available in my sh',
      url: 'https://www.instagram.com/tv/CMAJSmhpS3c/',
      embedId: '',
      category: 'tutorial',
      tags: ['diytags', 'tagmaking', 'vintagesupplies', 'vintagetags', 'tag', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-1058',
      platform: 'instagram',
      title: 'March Box Details 🦋',
      description: 'March Box Details 🦋 And the sound of shuffling paper 📃 Available in my shop #marchbox #coracreacraftbox #coracreacrafts #packingorders #packagingdesign #papershuffle #satisfyingvideos #details #vint',
      url: 'https://www.instagram.com/reel/CL9nE5JrsT7/',
      embedId: 'CL9nE5JrsT7',
      category: 'behind-the-scenes',
      tags: ['marchbox', 'coracreacraftbox', 'coracreacrafts', 'packingorders', 'packagingdesign', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CL9nE5JrsT7/media/?size=m'
    },
    {
      id: 'ig-api-1059',
      platform: 'instagram',
      title: 'New Stickers inspired by Norse Mythology and the video game  🌿🏔️ I love the...',
      description: 'New Stickers inspired by Norse Mythology and the video game #valheim 🌿🏔️ I love the magic touch the foil brings ✨ Available in my shop in green (video), gold or silver 🖤 #valheimgame #vikings #rune',
      url: 'https://www.instagram.com/reel/CL241oTJA55/',
      embedId: 'CL241oTJA55',
      category: 'product-showcase',
      tags: ['valheim', 'valheimgame', 'vikings', 'runes', 'norsemythology', 'stickers'],
      creator: 'CoraCreaCrafts',
      date: '2021-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CL241oTJA55/media/?size=m'
    },
    {
      id: 'ig-api-1060',
      platform: 'instagram',
      title: 'Stickers for the March Vintage Craft Box in the making this weekend 🦋',
      description: 'Stickers for the March Vintage Craft Box in the making this weekend 🦋 #stickershop #stickers #coracreacraftbox #coracreastickers #coracreacrafts #subboxaddict #subscription #subbox #vintagesupplies #',
      url: 'https://www.instagram.com/reel/CLz4K8WpuCO/',
      embedId: 'CLz4K8WpuCO',
      category: 'box-reveal',
      tags: ['stickershop', 'stickers', 'coracreacraftbox', 'coracreastickers', 'coracreacrafts', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CLz4K8WpuCO/media/?size=m'
    },
    {
      id: 'ig-api-1061',
      platform: 'instagram',
      title: 'Here\'s another "pack an order with me" episode 💌 I just never get tired of t...',
      description: 'Here\'s another "pack an order with me" episode 💌 I just never get tired of them, they\'re so satisfying to watch ☺️ #packanorderwithme #packingorders #packwithme #packagingdesign #coracreacrafts #vint',
      url: 'https://www.instagram.com/reel/CLuMAfcp_Ck/',
      embedId: 'CLuMAfcp_Ck',
      category: 'behind-the-scenes',
      tags: ['packanorderwithme', 'packingorders', 'packwithme', 'packagingdesign', 'coracreacrafts', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CLuMAfcp_Ck/media/?size=m'
    },
    {
      id: 'ig-api-1062',
      platform: 'instagram',
      title: 'Test a product with me part 1 💌 The Postcard Stamp is such a nice way to add...',
      description: 'Test a product with me part 1 💌 The Postcard Stamp is such a nice way to add details to snail mail, journals and other vintage inspired crafts 🖤 #testaproductwithme #coracreacrafts #stamping #stamps',
      url: 'https://www.instagram.com/reel/CLpL4kRJbiM/',
      embedId: 'CLpL4kRJbiM',
      category: 'product-showcase',
      tags: ['testaproductwithme', 'coracreacrafts', 'stamping', 'stampshop', 'vintagesupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CLpL4kRJbiM/media/?size=m'
    },
    {
      id: 'ig-api-1063',
      platform: 'instagram',
      title: 'Ever wondered how washi tapes are made🤔? Here\'s a quick video showing you how',
      description: 'Ever wondered how washi tapes are made🤔? Here\'s a quick video showing you how!  #washitape #coracreacraftswashi #coracreacrafts #washitapeshop #washilover #washi #washihowto #howto #washiproject #was',
      url: 'https://www.instagram.com/reel/CLhfoGIJ_oK/',
      embedId: 'CLhfoGIJ_oK',
      category: 'product-showcase',
      tags: ['washitape', 'coracreacraftswashi', 'coracreacrafts', 'washitapeshop', 'washilover', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CLhfoGIJ_oK/media/?size=m'
    },
    {
      id: 'ig-api-1064',
      platform: 'instagram',
      title: 'Prepared a Little Mermaid themed cover page for June 🧜‍♀️ I messed up the le...',
      description: 'Prepared a Little Mermaid themed cover page for June 🧜‍♀️ I messed up the left page but managed to save it by creating some water/waves effect with the beautiful watercolors from @reneeissancecolours',
      url: 'https://www.instagram.com/tv/CLe2Dj9J3bH/',
      embedId: '',
      category: 'journal-spread',
      tags: ['bulletjournal', 'journalingstickers', 'journaladdict', 'bujovideo', 'bujogoals', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-1065',
      platform: 'instagram',
      title: 'Fulfilling orders in this winter wonderland 🥶',
      description: 'Fulfilling orders in this winter wonderland 🥶 #winterwonderland❄️ #fulfillment #fullfillingorders #winter #snow #snowyday #plowing #plowwithme #morningroutine #wintertime #chicagoweather #smallbusine',
      url: 'https://www.instagram.com/reel/CLXiQ2mJUFP/',
      embedId: 'CLXiQ2mJUFP',
      category: 'product-showcase',
      tags: ['winterwonderland', 'fulfillment', 'fullfillingorders', 'winter', 'snow'],
      creator: 'CoraCreaCrafts',
      date: '2021-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CLXiQ2mJUFP/media/?size=m'
    },
    {
      id: 'ig-api-1066',
      platform: 'instagram',
      title: 'Sending love to all of you on this Valentine\'s Day. I\'m so grateful for this ...',
      description: 'Sending love to all of you on this Valentine\'s Day. I\'m so grateful for this community. Thank you for your ongoing love and support 🤗 this is where I get the strength and motivation to continue on my',
      url: 'https://www.instagram.com/p/CLSM20zpl2X/',
      embedId: 'CLSM20zpl2X',
      category: 'product-showcase',
      tags: ['iloveyou', 'happyvalentinesday', 'loveday', 'sharethelove', 'artcommunity', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CLSM20zpl2X/media/?size=m'
    },
    {
      id: 'ig-api-1067',
      platform: 'instagram',
      title: 'Tested stickers on holographic paper 🗞️ I love how the color shifts from pur...',
      description: 'Tested stickers on holographic paper 🗞️ I love how the color shifts from purple to blue and green!  #stickershop #stickertestdrive #holographic #holostickers #holographicstickers #coracreacrafts #cor',
      url: 'https://www.instagram.com/p/CLM05gWpfkx/',
      embedId: 'CLM05gWpfkx',
      category: 'product-showcase',
      tags: ['stickershop', 'stickertestdrive', 'holographic', 'holostickers', 'holographicstickers', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CLM05gWpfkx/media/?size=m'
    },
    {
      id: 'ig-api-1068',
      platform: 'instagram',
      title: 'So happy to share my new "Constellations" washi tapes 🖤 These designs were i...',
      description: 'So happy to share my new "Constellations" washi tapes 🖤 These designs were inspired by 19th century illustrations of a celestial atlas 🌚🌛🌟 5 designs available: check out the link in bio for more d',
      url: 'https://www.instagram.com/reel/CLI-io_pi7t/',
      embedId: 'CLI-io_pi7t',
      category: 'product-showcase',
      tags: ['coracreacrafts', 'coracreacraftswashi', 'washitapeshop', 'washilover', 'washiaddict', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CLI-io_pi7t/media/?size=m'
    },
    {
      id: 'ig-api-1069',
      platform: 'instagram',
      title: 'Pack an order with me 💌 another episode of a pretty satisfying order packing 😄',
      description: 'Pack an order with me 💌 another episode of a pretty satisfying order packing 😄 #coracreacrafts #orderprocess #orderpacking #packanorderwithme #packaging #fingersnap #washitapeshop #washitape #sticke',
      url: 'https://www.instagram.com/reel/CLE8nVmpKcn/',
      embedId: 'CLE8nVmpKcn',
      category: 'behind-the-scenes',
      tags: ['coracreacrafts', 'orderprocess', 'orderpacking', 'packanorderwithme', 'packaging', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CLE8nVmpKcn/media/?size=m'
    },
    {
      id: 'ig-api-1070',
      platform: 'instagram',
      title: 'What\'s your journaling ritual? 📖 I\'m grateful for these dark snowy days that...',
      description: 'What\'s your journaling ritual? 📖 I\'m grateful for these dark snowy days that push me to open this journal and be more creative!  #journalingritual #ritual #craftingritual #ritualforcreatives #creativ',
      url: 'https://www.instagram.com/reel/CK68MJWJF6C/',
      embedId: 'CK68MJWJF6C',
      category: 'product-showcase',
      tags: ['journalingritual', 'ritual', 'craftingritual', 'ritualforcreatives', 'creativeritual', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CK68MJWJF6C/media/?size=m'
    },
    {
      id: 'ig-api-1071',
      platform: 'instagram',
      title: 'Restocking on the February Boxes 🚚 Time to grab yours:  taking orders until ...',
      description: 'Restocking on the February Boxes 🚚 Time to grab yours:  taking orders until sold out/15th of February 📖 Library Theme 📖 #vintagecrafts #vintagepapers #vintagecraftbox #coracreacraftbox #coracreacra',
      url: 'https://www.instagram.com/p/CK2HGJupRz2/',
      embedId: 'CK2HGJupRz2',
      category: 'box-reveal',
      tags: ['vintagecrafts', 'vintagepapers', 'vintagecraftbox', 'coracreacraftbox', 'coracreacrafts', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CK2HGJupRz2/media/?size=m'
    },
    {
      id: 'ig-api-1072',
      platform: 'instagram',
      title: 'Ready to roll? 😜 I\'ve been wanting to use this song for a while and finally ...',
      description: 'Ready to roll? 😜 I\'ve been wanting to use this song for a while and finally got the video for it. Sorry there\'s no dancing, only washi tapes rolling 😂 #rollingwashi #washilovers #washitapes #washita',
      url: 'https://www.instagram.com/reel/CKzUPzHpLXs/',
      embedId: 'CKzUPzHpLXs',
      category: 'behind-the-scenes',
      tags: ['rollingwashi', 'washilovers', 'washitapes', 'washitapeshop', 'craftstore', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CKzUPzHpLXs/media/?size=m'
    },
    {
      id: 'ig-api-1073',
      platform: 'instagram',
      title: 'Tried foiling for the first time today and I already love it 😍',
      description: 'Tried foiling for the first time today and I already love it 😍  #foil #foilpaper #foilpaperart #coracreacrafts #minc #mincfoil #smallbusiness #greenfoil #blackpaperart #blackcards #papercrafts #paper',
      url: 'https://www.instagram.com/reel/CKugO1fpRSf/',
      embedId: 'CKugO1fpRSf',
      category: 'product-showcase',
      tags: ['foil', 'foilpaper', 'foilpaperart', 'coracreacrafts', 'minc', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CKugO1fpRSf/media/?size=m'
    },
    {
      id: 'ig-api-1074',
      platform: 'instagram',
      title: 'How to create old paper/parchment effects 📜🖤 New Parchment Stamp in my shop',
      description: 'How to create old paper/parchment effects 📜🖤 New Parchment Stamp in my shop #coracreacrafts #paperaddict #papercrafts #oldpaper #parchment #stamping #stamps #stationerystore #journalsupplies #crafti',
      url: 'https://www.instagram.com/reel/CKrq30hpVKO/',
      embedId: 'CKrq30hpVKO',
      category: 'tutorial',
      tags: ['coracreacrafts', 'paperaddict', 'papercrafts', 'oldpaper', 'parchment', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CKrq30hpVKO/media/?size=m'
    },
    {
      id: 'ig-api-1075',
      platform: 'instagram',
      title: 'Prepared a Snow White themed cover page and tracker for February 🍎 with rela...',
      description: 'Prepared a Snow White themed cover page and tracker for February 🍎 with relaxing music 🎶 #february #februaryjournal #februarycoverpage #coverpage #bulletjournal #bujogoals #bujovideo #bujoideas #sno',
      url: 'https://www.instagram.com/tv/CKpfdF-JP-j/',
      embedId: '',
      category: 'journal-spread',
      tags: ['february', 'februaryjournal', 'februarycoverpage', 'coverpage', 'bulletjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-1076',
      platform: 'instagram',
      title: 'A little process video packing the February Craft boxes ☺️ so fun to watch ho...',
      description: 'A little process video packing the February Craft boxes ☺️ so fun to watch how quickly they pile up in the video when it took more than a day 😂  #craftingsupplies #coracreacraftbox #coracreacrafts #s',
      url: 'https://www.instagram.com/reel/CKl-SKRJRum/',
      embedId: 'CKl-SKRJRum',
      category: 'behind-the-scenes',
      tags: ['craftingsupplies', 'coracreacraftbox', 'coracreacrafts', 'subboxaddict', 'subscriptionbox', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CKl-SKRJRum/media/?size=m'
    },
    {
      id: 'ig-api-1077',
      platform: 'instagram',
      title: 'The February Vintage Craft Box is set around the theme Library and includes 2...',
      description: 'The February Vintage Craft Box is set around the theme Library and includes 2 beautiful washi tapes, a wooden handle stamp, a clear stamp sheet and much more.. 📜 Set around a different theme every mo',
      url: 'https://www.instagram.com/tv/CKhG7z7hpcP/',
      embedId: '',
      category: 'box-reveal',
      tags: ['subboxaddict', 'subscriptionbox', 'subscription', 'journalsupplies', 'journalingsubscription', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-1078',
      platform: 'instagram',
      title: 'Can you guess the 6 countries displayed on these beautiful washis?Giving away...',
      description: 'Can you guess the 6 countries displayed on these beautiful washis?Giving away one tape of your choice to the first 3 people with the correct answers 🌍 To enter: - Follow me - Share which washi/countr',
      url: 'https://www.instagram.com/reel/CKb-wOAJwI-/',
      embedId: 'CKb-wOAJwI-',
      category: 'product-showcase',
      tags: ['giveaway', 'stationerystore', 'stationerygiveaway', 'washitape', 'washi', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CKb-wOAJwI-/media/?size=m'
    },
    {
      id: 'ig-api-1079',
      platform: 'instagram',
      title: 'Cathedral stamp 🖤 I moved the stamp slightly on the first take in my journal...',
      description: 'Cathedral stamp 🖤 I moved the stamp slightly on the first take in my journal so it came out smudged.. did a second take because this stamp is so beautiful! Now available in my shop 💌 #cathedral #cat',
      url: 'https://www.instagram.com/reel/CKZwIYAJfWN/',
      embedId: 'CKZwIYAJfWN',
      category: 'product-showcase',
      tags: ['cathedral', 'cathedralstamp', 'notredame', 'stamping', 'stampvideo', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CKZwIYAJfWN/media/?size=m'
    },
    {
      id: 'ig-api-1080',
      platform: 'instagram',
      title: 'My day in 30 seconds - in reality it took me almost all day yesterday 😅 Cat ...',
      description: 'My day in 30 seconds - in reality it took me almost all day yesterday 😅 Cat was chilling and watching me the whole time 🐈 Reorganizing part 2 coming soon to see the full office (as soon as I get the',
      url: 'https://www.instagram.com/reel/CKWyD24pflC/',
      embedId: 'CKWyD24pflC',
      category: 'behind-the-scenes',
      tags: ['reorganizing', 'officedecor', 'officeorganization', 'storageideas', 'storagesolutions'],
      creator: 'CoraCreaCrafts',
      date: '2021-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CKWyD24pflC/media/?size=m'
    },
    {
      id: 'ig-api-1081',
      platform: 'instagram',
      title: 'Pack an order with me 😉 some lucky person out there will receive all these w...',
      description: 'Pack an order with me 😉 some lucky person out there will receive all these washi tapes, January box and other goodies😍 Reusing a beautiful Liberty box from a Christmas gift 🎁 #packanorderwithme #pa',
      url: 'https://www.instagram.com/reel/CKUFXrZp6aT/',
      embedId: 'CKUFXrZp6aT',
      category: 'behind-the-scenes',
      tags: ['packanorderwithme', 'packagingdesign', 'coracreacraftbox', 'coracreacrafts', 'craftstore', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CKUFXrZp6aT/media/?size=m'
    },
    {
      id: 'ig-api-1082',
      platform: 'instagram',
      title: 'My first few days back after holidays 😊 feeling refreshed and energized and ...',
      description: 'My first few days back after holidays 😊 feeling refreshed and energized and ready for what the next weeks will bring ❄️💕 This is what I spent the last few days doing !  #etsyseller #etsyorders #stat',
      url: 'https://www.instagram.com/reel/CKMp2y7JLop/',
      embedId: 'CKMp2y7JLop',
      category: 'behind-the-scenes',
      tags: ['etsyseller', 'etsyorders', 'stationerystore', 'smallshop', 'whatididtoday'],
      creator: 'CoraCreaCrafts',
      date: '2021-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CKMp2y7JLop/media/?size=m'
    },
    {
      id: 'ig-api-1083',
      platform: 'instagram',
      title: 'Insects stamping 🐝🦋 Who doesn\'t love them when they\'re on paper? 😂this new...',
      description: 'Insects stamping 🐝🦋 Who doesn\'t love them when they\'re on paper? 😂this new stamp sheet is available in my shop 👉 link in bio  #stationerystore #stamps #clearstamps #etsyseller #stamping #insects #',
      url: 'https://www.instagram.com/reel/CKHveH2J6-t/',
      embedId: 'CKHveH2J6-t',
      category: 'tutorial',
      tags: ['stationerystore', 'stamps', 'clearstamps', 'etsyseller', 'stamping', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CKHveH2J6-t/media/?size=m'
    },
    {
      id: 'ig-api-1084',
      platform: 'instagram',
      title: 'If it only took two snaps of the fingers ✌️😊',
      description: 'If it only took two snaps of the fingers ✌️😊 #packingorders #coracreacrafts #packingorders #packanorderwithme #vintagesupplies #vintagecrafts #vintagecraftstore #craftingsupplies #craftstore #journal',
      url: 'https://www.instagram.com/reel/CKAhZOnptOV/',
      embedId: 'CKAhZOnptOV',
      category: 'behind-the-scenes',
      tags: ['packingorders', 'coracreacrafts', 'packingorders', 'packanorderwithme', 'vintagesupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CKAhZOnptOV/media/?size=m'
    },
    {
      id: 'ig-api-1085',
      platform: 'instagram',
      title: 'How long can a line of shipping labels be 😅📦? I thought I\'d take mine throu...',
      description: 'How long can a line of shipping labels be 😅📦? I thought I\'d take mine throughout the house to see 🙈 #shippingorders #shippinglabels #shippinglabelcompetition #coracreacrafts',
      url: 'https://www.instagram.com/reel/CJ7GjsapKEO/',
      embedId: 'CJ7GjsapKEO',
      category: 'product-showcase',
      tags: ['shippingorders', 'shippinglabels', 'shippinglabelcompetition', 'coracreacrafts'],
      creator: 'CoraCreaCrafts',
      date: '2021-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CJ7GjsapKEO/media/?size=m'
    },
    {
      id: 'ig-api-1086',
      platform: 'instagram',
      title: 'Order packing:',
      description: 'Order packing: *Pre-perforated flower stamps *Van Gogh Large Washi x2 *Giant Tape Christmas *Natural Paper Notebook x6 *Gothic Letter Wooden Stamps - Full Alphabet  ❤️ Link in bio ❤️ #coracreacrafts #',
      url: 'https://www.instagram.com/reel/CJwXcTNpWI2/',
      embedId: 'CJwXcTNpWI2',
      category: 'behind-the-scenes',
      tags: ['coracreacrafts', 'stationerystore', 'vintagestyle', 'vintagecrafts', 'vintagesupplies', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CJwXcTNpWI2/media/?size=m'
    },
    {
      id: 'ig-api-1087',
      platform: 'instagram',
      title: 'Spot the weird thing in this video 😆 Cover page for a full year planner I ju...',
      description: 'Spot the weird thing in this video 😆 Cover page for a full year planner I just started working on !  #fullyearspread #fullyearplanner #planner #plannercommunity #bulletjournal #bujocoverpage #coverpa',
      url: 'https://www.instagram.com/p/CJt-2eRpc-y/',
      embedId: 'CJt-2eRpc-y',
      category: 'journal-spread',
      tags: ['fullyearspread', 'fullyearplanner', 'planner', 'plannercommunity', 'bulletjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CJt-2eRpc-y/media/?size=m'
    },
    {
      id: 'ig-api-1088',
      platform: 'instagram',
      title: 'Packing orders 📦💕',
      description: 'Packing orders 📦💕 #coracreacrafts #coracreacraftbox #packagingdesign #packingorders #etsyseller #etsyorders #stickers #washitapes #craftingsupplies #journalsupplies #journalingsubscription',
      url: 'https://www.instagram.com/reel/CJmVsD0pylq/',
      embedId: 'CJmVsD0pylq',
      category: 'behind-the-scenes',
      tags: ['coracreacrafts', 'coracreacraftbox', 'packagingdesign', 'packingorders', 'etsyseller', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2021-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CJmVsD0pylq/media/?size=m'
    },
    {
      id: 'ig-api-1089',
      platform: 'instagram',
      title: 'Setting a new full year spread 🎇🤩🌟 It\'s so nice to have a full & empty ove...',
      description: 'Setting a new full year spread 🎇🤩🌟 It\'s so nice to have a full & empty overview of 2021 - anything is possible !  #bulletjournal #custombujo #fullyearspread #yearlyspread #yearoverview #2021planner',
      url: 'https://www.instagram.com/reel/CJjsC-6psCB/',
      embedId: 'CJjsC-6psCB',
      category: 'journal-spread',
      tags: ['bulletjournal', 'custombujo', 'fullyearspread', 'yearlyspread', 'yearoverview', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CJjsC-6psCB/media/?size=m'
    },
    {
      id: 'ig-api-1090',
      platform: 'instagram',
      title: 'Shipping by all weather 🌡️🥶😂 I thought I\'d post something a bit different ...',
      description: 'Shipping by all weather 🌡️🥶😂 I thought I\'d post something a bit different to end 2020 ! Happy New Year 🌟 #etsyseller #etsyorders #shippingorders #processingorders #orderprocess #chicagoweather #go',
      url: 'https://www.instagram.com/reel/CJejgzQJnM0/',
      embedId: 'CJejgzQJnM0',
      category: 'product-showcase',
      tags: ['etsyseller', 'etsyorders', 'shippingorders', 'processingorders', 'orderprocess'],
      creator: 'CoraCreaCrafts',
      date: '2020-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CJejgzQJnM0/media/?size=m'
    },
    {
      id: 'ig-api-1091',
      platform: 'instagram',
      title: 'Who\'s setting goals for the new year? ✍️ This is a quick & easy idea for a go...',
      description: 'Who\'s setting goals for the new year? ✍️ This is a quick & easy idea for a goals spread in your journal 📖 #goals #2021goals #2021journal #bujogoals #bujospread #goalsspread #journalsupplies #bulletjo',
      url: 'https://www.instagram.com/reel/CJbdyu5piSn/',
      embedId: 'CJbdyu5piSn',
      category: 'journal-spread',
      tags: ['goals', '2021goals', '2021journal', 'bujogoals', 'bujospread', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CJbdyu5piSn/media/?size=m'
    },
    {
      id: 'ig-api-1092',
      platform: 'instagram',
      title: 'Sneak Peek into the January Box! Get yours in my  shop. Theme for the month i...',
      description: 'Sneak Peek into the January Box! Get yours in my #etsy shop. Theme for the month is Old Cities with a beautiful cathedrals washi tape, a landmark clear stamp, a wide selection of stickers & papers and',
      url: 'https://www.instagram.com/reel/CJT80DYJY82/',
      embedId: 'CJT80DYJY82',
      category: 'box-reveal',
      tags: ['etsy', 'subscription', 'box', 'crafting', 'stickers', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2020-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CJT80DYJY82/media/?size=m'
    },
    {
      id: 'ig-api-1093',
      platform: 'instagram',
      title: 'Wax sealing 🕯️',
      description: 'Wax sealing 🕯️#waxseal #wrapping #giftideas #giftwrapping #brassseal',
      url: 'https://www.instagram.com/reel/CJSDNfDpjEk/',
      embedId: 'CJSDNfDpjEk',
      category: 'product-showcase',
      tags: ['waxseal', 'wrapping', 'giftideas', 'giftwrapping', 'brassseal'],
      creator: 'CoraCreaCrafts',
      date: '2020-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CJSDNfDpjEk/media/?size=m'
    },
    {
      id: 'ig-api-1094',
      platform: 'instagram',
      title: 'Super easy Christmas cards for all the last-minute people like me 😅 using so...',
      description: 'Super easy Christmas cards for all the last-minute people like me 😅 using some of the vintage style Christmas stickers from my shop, letter size cardstock cut in 4, and some green, red and golden pen',
      url: 'https://www.instagram.com/reel/CI_uM9Gpanm/',
      embedId: 'CI_uM9Gpanm',
      category: 'tutorial',
      tags: ['christmas', 'christmascards', 'vintagestyle', 'vintagechristmas', 'lastminutecards', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2020-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CI_uM9Gpanm/media/?size=m'
    },
    {
      id: 'ig-api-1095',
      platform: 'instagram',
      title: 'Can you guess the theme from the music? 🎼 way too easy 😛 I had so much fun ...',
      description: 'Can you guess the theme from the music? 🎼 way too easy 😛 I had so much fun making those !! This was an order from my Etsy shop for two Christmas presents 🎄🎁',
      url: 'https://www.instagram.com/tv/CI4Fwk6puCQ/',
      embedId: '',
      category: 'product-showcase',
      tags: ['christmas'],
      creator: 'CoraCreaCrafts',
      date: '2020-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-1096',
      platform: 'instagram',
      title: 'Holidays vibes for this journal spread 🎄',
      description: 'Holidays vibes for this journal spread 🎄 #journalingaddict #journaldecember #christmaswashitape #christmascrafts #christmasvibes #journaling #bulletjournaltracker #bulletjournalsupplies #custombujo #',
      url: 'https://www.instagram.com/reel/CIytWXPpUrM/',
      embedId: 'CIytWXPpUrM',
      category: 'journal-spread',
      tags: ['journalingaddict', 'journaldecember', 'christmaswashitape', 'christmascrafts', 'christmasvibes', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2020-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CIytWXPpUrM/media/?size=m'
    },
    {
      id: 'ig-api-1097',
      platform: 'instagram',
      title: 'New in my shop 🖤 Vintage style wooden stamps with handles 💌 love them to de...',
      description: 'New in my shop 🖤 Vintage style wooden stamps with handles 💌 love them to decorate my mail & also a great idea for last minute Christmas gifts !  #woodenstamp #woodenstamps #stationeryshop #stampitup',
      url: 'https://www.instagram.com/reel/CItMKblJAH4/',
      embedId: 'CItMKblJAH4',
      category: 'product-showcase',
      tags: ['woodenstamp', 'woodenstamps', 'stationeryshop', 'stampitup', 'stampingart', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2020-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CItMKblJAH4/media/?size=m'
    },
    {
      id: 'ig-api-1098',
      platform: 'instagram',
      title: 'Gift wrapping season 🎁❤️ My very favorite thing to do ! And always with a wa...',
      description: 'Gift wrapping season 🎁❤️ My very favorite thing to do ! And always with a wax seal to add a final touch 🤗 #orderpacking #giftpacking #giftwrapping #wrappingvideo #wrappingideas #christmasgift #chris',
      url: 'https://www.instagram.com/p/CIjELJUJZlK/',
      embedId: 'CIjELJUJZlK',
      category: 'behind-the-scenes',
      tags: ['orderpacking', 'giftpacking', 'giftwrapping', 'wrappingvideo', 'wrappingideas', 'christmas'],
      creator: 'CoraCreaCrafts',
      date: '2020-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CIjELJUJZlK/media/?size=m'
    },
    {
      id: 'ig-api-1099',
      platform: 'instagram',
      title: 'Reorganizing: building shelves, counting and organizing inventory, making spa...',
      description: 'Reorganizing: building shelves, counting and organizing inventory, making space for order packing, moving furniture around 😅 a hell of a weekend but so worth it !! #satisfying  #officeorganization #r',
      url: 'https://www.instagram.com/reel/CIgQfK-pKaN/',
      embedId: 'CIgQfK-pKaN',
      category: 'behind-the-scenes',
      tags: ['satisfying', 'officeorganization', 'reorganize', 'craftingsupplies', 'inventory'],
      creator: 'CoraCreaCrafts',
      date: '2020-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CIgQfK-pKaN/media/?size=m'
    },
    {
      id: 'ig-api-1100',
      platform: 'instagram',
      title: 'Pack December boxes with me 📦🖤 All boxes come with an extra Christmasy stic...',
      description: 'Pack December boxes with me 📦🖤 All boxes come with an extra Christmasy sticker sheet 🎅 #coracreacrafts #coracreacraftbox #subscriptionbox #craftingsupplies #journalsubscription #journalingsupplies ',
      url: 'https://www.instagram.com/reel/CIRFqIzJBqS/',
      embedId: 'CIRFqIzJBqS',
      category: 'behind-the-scenes',
      tags: ['coracreacrafts', 'coracreacraftbox', 'subscriptionbox', 'craftingsupplies', 'journalsubscription', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2020-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CIRFqIzJBqS/media/?size=m'
    },
    {
      id: 'ig-api-1101',
      platform: 'instagram',
      title: 'Making some more Christmas Burlap Bags this weekend 🎁🌲 Love making these su...',
      description: 'Making some more Christmas Burlap Bags this weekend 🎁🌲 Love making these super large ones. Burlap bags make it so easy to wrap presents + they\'re eco-friendly and reusable !  #christmaasack #christm',
      url: 'https://www.instagram.com/p/CIL--JJpvna/',
      embedId: 'CIL--JJpvna',
      category: 'tutorial',
      tags: ['christmaasack', 'christmasgift', 'christmaspresent', 'wrappingvideo', 'wrappingideas', 'christmas'],
      creator: 'CoraCreaCrafts',
      date: '2020-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CIL--JJpvna/media/?size=m'
    },
    {
      id: 'ig-api-1102',
      platform: 'instagram',
      title: 'Happy Thanksgiving 🦃 my very first Thanksgiving in the US ! Definitely not t...',
      description: 'Happy Thanksgiving 🦃 my very first Thanksgiving in the US ! Definitely not the day to be counting calories 😂',
      url: 'https://www.instagram.com/reel/CIEHpbqJItz/',
      embedId: 'CIEHpbqJItz',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2020-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CIEHpbqJItz/media/?size=m'
    },
    {
      id: 'ig-api-1103',
      platform: 'instagram',
      title: 'Vintage Label Stickers 🖤 Music & Poetry 🎼 available in my shop ✨',
      description: 'Vintage Label Stickers 🖤 Music & Poetry 🎼 available in my shop ✨ #stickersaddict #stickers #vintagestickers #labelstickers #vellumstickers #musicstickers #poetrystickers #scrapbooksupplies #junkjour',
      url: 'https://www.instagram.com/reel/CH3pljkprM2/',
      embedId: 'CH3pljkprM2',
      category: 'product-showcase',
      tags: ['stickersaddict', 'stickers', 'vintagestickers', 'labelstickers', 'vellumstickers', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CH3pljkprM2/media/?size=m'
    },
    {
      id: 'ig-api-1104',
      platform: 'instagram',
      title: 'Last call yo get your Advent calendar on time for the first of December 🕚',
      description: 'Last call yo get your Advent calendar on time for the first of December 🕚 #coracreacrafts #coracreacraftsadvent #adventcalendars #craftysupplies #craftingsupplies #stickers #washitape #stamps #statio',
      url: 'https://www.instagram.com/reel/CH1D39IJLdw/',
      embedId: 'CH1D39IJLdw',
      category: 'product-showcase',
      tags: ['coracreacrafts', 'coracreacraftsadvent', 'adventcalendars', 'craftysupplies', 'craftingsupplies', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2020-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CH1D39IJLdw/media/?size=m'
    },
    {
      id: 'ig-api-1105',
      platform: 'instagram',
      title: 'December Craft Box Sneak Peek 🌘 this month is all about Space, Planets, Gala...',
      description: 'December Craft Box Sneak Peek 🌘 this month is all about Space, Planets, Galaxies ✨ You will receive a selection of two washis tapes, stickers, a beautiful handmade ruler by @alanajanelle, a moon phas',
      url: 'https://www.instagram.com/reel/CHyFbHVJIlb/',
      embedId: 'CHyFbHVJIlb',
      category: 'box-reveal',
      tags: ['coracreacraftbox', 'coracreacrafts', 'christmasgift', 'christmaspresents', 'subscriptionbox', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2020-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CHyFbHVJIlb/media/?size=m'
    },
    {
      id: 'ig-api-1106',
      platform: 'instagram',
      title: 'What I\'m working on - Day 1',
      description: 'What I\'m working on - Day 1 Custom logo & sticker creation for a small apothecary shop 🌿 I decided to start a series of reels with sneak peeks into my daily projects 😊 hope you like it !  #coracreac',
      url: 'https://www.instagram.com/reel/CHwVX1vpiO2/',
      embedId: 'CHwVX1vpiO2',
      category: 'box-reveal',
      tags: ['coracreacrafts', 'whatimworkingon', 'stickersaddict', 'customstickers', 'stickerdesign', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2020-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CHwVX1vpiO2/media/?size=m'
    },
    {
      id: 'ig-api-1107',
      platform: 'instagram',
      title: 'Advent Calendar bags 🎁🎄Peeling off that transfer paper is SO satisfying 😄 ...',
      description: 'Advent Calendar bags 🎁🎄Peeling off that transfer paper is SO satisfying 😄 Burlap bags with Advent calendar & other Christmas designs are on sale on my Etsy  #adventcalendar #advent #burlapbags #bur',
      url: 'https://www.instagram.com/reel/CHqSwz2pyo9/',
      embedId: 'CHqSwz2pyo9',
      category: 'tutorial',
      tags: ['adventcalendar', 'advent', 'burlapbags', 'burlapdecor', 'diybags', 'christmas'],
      creator: 'CoraCreaCrafts',
      date: '2020-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CHqSwz2pyo9/media/?size=m'
    },
    {
      id: 'ig-api-1108',
      platform: 'instagram',
      title: 'Christmas Craft Boxes packing 🎄📦 two variations available. This is a sneak ...',
      description: 'Christmas Craft Boxes packing 🎄📦 two variations available. This is a sneak peek of the stickers that come with the box 🎁 #christmasjournaling #christmasgift #christmascrafts #christmasstickers #cra',
      url: 'https://www.instagram.com/reel/CHoZKE9pmYs/',
      embedId: 'CHoZKE9pmYs',
      category: 'box-reveal',
      tags: ['christmasjournaling', 'christmasgift', 'christmascrafts', 'christmasstickers', 'craftingtools', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CHoZKE9pmYs/media/?size=m'
    },
    {
      id: 'ig-api-1109',
      platform: 'instagram',
      title: 'Super easy Holidays card & envelope ❄️🦌 it\'s not too early to start with Chr...',
      description: 'Super easy Holidays card & envelope ❄️🦌 it\'s not too early to start with Christmas cards I promise 🤣  Find the Winter Transfer stickers I\'m using in my shop - link in bio 💙 I love to use transfer s',
      url: 'https://www.instagram.com/p/CHlXFylpWvd/',
      embedId: 'CHlXFylpWvd',
      category: 'tutorial',
      tags: ['diycrafts', 'diycards', 'winterforest', 'wintercards', 'winterstickers', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CHlXFylpWvd/media/?size=m'
    },
    {
      id: 'ig-api-1110',
      platform: 'instagram',
      title: 'My Crafting Supplies Advent Calendar packing continues 2️⃣🎄📦 very few calen...',
      description: 'My Crafting Supplies Advent Calendar packing continues 2️⃣🎄📦 very few calendars left for sale in my shop 💌 link in bio 😉 #adventcalendar #coracreacraftsadvent #coracreacrafts #advent #packagingord',
      url: 'https://www.instagram.com/reel/CHfk7g7Jwbz/',
      embedId: 'CHfk7g7Jwbz',
      category: 'behind-the-scenes',
      tags: ['adventcalendar', 'coracreacraftsadvent', 'coracreacrafts', 'advent', 'packagingorder', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2020-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CHfk7g7Jwbz/media/?size=m'
    },
    {
      id: 'ig-api-1111',
      platform: 'instagram',
      title: 'One of the last request I got was to create medieval style place cards for a ...',
      description: 'One of the last request I got was to create medieval style place cards for a wedding. I had SO much fun making these 👌🖤 from the gothic calligraphy to the flourishing and the illuminated stickers ! ',
      url: 'https://www.instagram.com/tv/CHdtLN1pN5_/',
      embedId: '',
      category: 'product-showcase',
      tags: ['coracreacrafts', 'coracreacustomlettering', 'customplacecards', 'weddingcalligraphy', 'weddingplacecards', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-1112',
      platform: 'instagram',
      title: 'Encyclopedia Stickers in the making 🖤 all sets of this collection are on sal...',
      description: 'Encyclopedia Stickers in the making 🖤 all sets of this collection are on sale in my shop at the moment 💌 themes are: Geology, Natural History and Botany 🌿 #stickershop #stickeretsy #stickersaddict ',
      url: 'https://www.instagram.com/reel/CHQVx51JA1Y/',
      embedId: 'CHQVx51JA1Y',
      category: 'product-showcase',
      tags: ['stickershop', 'stickeretsy', 'stickersaddict', 'stickershop', 'stickerset', 'stickers'],
      creator: 'CoraCreaCrafts',
      date: '2020-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CHQVx51JA1Y/media/?size=m'
    },
    {
      id: 'ig-api-1113',
      platform: 'instagram',
      title: 'A remake of my Forest theme from last year but different ! This was super fun...',
      description: 'A remake of my Forest theme from last year but different ! This was super fun to make 🌲💌',
      url: 'https://www.instagram.com/tv/CHOoxUEp66l/',
      embedId: '',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2020-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-1114',
      platform: 'instagram',
      title: 'Christmas Burlap Bags 🖤🌲short DIY video since some of you asked ! 5 differe...',
      description: 'Christmas Burlap Bags 🖤🌲short DIY video since some of you asked ! 5 different designs and sizes are also available for sale on my Etsy shop 🦌 so excited for Christmas !! #christmasgift #christmaasa',
      url: 'https://www.instagram.com/p/CHJOCuLJl4i/',
      embedId: 'CHJOCuLJl4i',
      category: 'tutorial',
      tags: ['christmasgift', 'christmaasack', 'burlapdecor', 'burlapbags', 'diydecor', 'christmas'],
      creator: 'CoraCreaCrafts',
      date: '2020-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CHJOCuLJl4i/media/?size=m'
    },
    {
      id: 'ig-api-1115',
      platform: 'instagram',
      title: 'My journal spread for November last year 🌲 I loved that pine theme ! I\'m run...',
      description: 'My journal spread for November last year 🌲 I loved that pine theme ! I\'m running late in my journal this year and haven\'t started November yet. This inspires me to get back into it !  #novembernight ',
      url: 'https://www.instagram.com/p/CHGXd19pB_c/',
      embedId: 'CHGXd19pB_c',
      category: 'journal-spread',
      tags: ['novembernight', 'hellonovember', 'novemberspread', 'novemberbujovideo', 'novemberplanner', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CHGXd19pB_c/media/?size=m'
    },
    {
      id: 'ig-api-1116',
      platform: 'instagram',
      title: 'It\'s that time of the month again 📦 Getting the first batch of the subscript...',
      description: 'It\'s that time of the month again 📦 Getting the first batch of the subscription boxes ready to ship in the next few days 💌 This month\'s theme is "Victorian" - still available for order until 14 Nove',
      url: 'https://www.instagram.com/tv/CHDX-zxJ4lR/',
      embedId: '',
      category: 'box-reveal',
      tags: ['subscriptionbox', 'craftsuppliesbox', 'craftingsupplies', 'novemberbox', 'journalingsupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-1117',
      platform: 'instagram',
      title: 'Full Autumn mode with this stationery set 🍁 I\'m going to use those cute enve...',
      description: 'Full Autumn mode with this stationery set 🍁 I\'m going to use those cute envelopes, tags and stickers for Thanksgiving 🥰 this year\'s going to be my first "real" Thanksgiving celebration in the US 💌',
      url: 'https://www.instagram.com/reel/CG8U6dcJTTG/',
      embedId: 'CG8U6dcJTTG',
      category: 'behind-the-scenes',
      tags: ['stationeryshop', 'stationeryaddict', 'stationerypack', 'envelopemaking', 'envelopeart', 'autumn'],
      creator: 'CoraCreaCrafts',
      date: '2020-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CG8U6dcJTTG/media/?size=m'
    },
    {
      id: 'ig-api-1118',
      platform: 'instagram',
      title: 'My Halloween inspired spread for this last week of October 🎃🍂 I created the...',
      description: 'My Halloween inspired spread for this last week of October 🎃🍂 I created these Halloween stickers a bit too late this year so they will be available for Halloween next year 😂  #halloweenweek #hallow',
      url: 'https://www.instagram.com/p/CG3BMNaJ5Ue/',
      embedId: 'CG3BMNaJ5Ue',
      category: 'journal-spread',
      tags: ['halloweenweek', 'halloween', 'halloweenspread', 'halloweenstickers', 'journalingoctober', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CG3BMNaJ5Ue/media/?size=m'
    },
    {
      id: 'ig-api-1119',
      platform: 'instagram',
      title: 'November Vintage Craft Box 🖤 this month it\'s all about the Victorian Era 🧷 ...',
      description: 'November Vintage Craft Box 🖤 this month it\'s all about the Victorian Era 🧷 with lots of that time\'s fashion & decorative details. Order now on my Etsy or Cratejoy - available until sold out 🖤🖤 #vi',
      url: 'https://www.instagram.com/p/CGpkKIunJ68/',
      embedId: 'CGpkKIunJ68',
      category: 'box-reveal',
      tags: ['vintagesupplies', 'craftingtools', 'craftsuppliesbox', 'craftingsupplies', 'vintagecrafts', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CGpkKIunJ68/media/?size=m'
    },
    {
      id: 'ig-api-1120',
      platform: 'instagram',
      title: 'Testing various @ranger_ink distress inks on some usps registered mail receip...',
      description: 'Testing various @ranger_ink distress inks on some usps registered mail receipts ☺️🖤 I can\'t tell which I prefer... From top to bottom at the end: antique linen, vintage photo, brushed corduroy, walnu',
      url: 'https://www.instagram.com/reel/CGlHyHanNiP/',
      embedId: 'CGlHyHanNiP',
      category: 'product-showcase',
      tags: ['rangerik', 'inkpads', 'agingpaper', 'paperaddict', 'receipt', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2020-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CGlHyHanNiP/media/?size=m'
    },
    {
      id: 'ig-api-1121',
      platform: 'instagram',
      title: 'Enjoyed packaging this little gift so much ! I used:',
      description: 'Enjoyed packaging this little gift so much ! I used: - Kraft wrapping paper - Natural leaves from outside - Laminator  - Leaf wax seal & 2 wax colors from my shop - Note paper from my shop - Natural t',
      url: 'https://www.instagram.com/tv/CGaRo0vHBh2/',
      embedId: '',
      category: 'tutorial',
      tags: ['giftwrapping', 'giftwrap', 'craftwrap', 'craftingtools', 'waxstamp', 'autumn'],
      creator: 'CoraCreaCrafts',
      date: '2020-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-1122',
      platform: 'instagram',
      title: '\'Victorian\' is the November theme for the Craft Supplies box 🖤 really enjoye...',
      description: '\'Victorian\' is the November theme for the Craft Supplies box 🖤 really enjoyed filming this quick snapshot & finding music to fit the theme 🎶 Orders now open in my shop ! #craftsuppliesbox #craftings',
      url: 'https://www.instagram.com/reel/CGV8qmMnp9W/',
      embedId: 'CGV8qmMnp9W',
      category: 'box-reveal',
      tags: ['craftsuppliesbox', 'craftingsupplies', 'craftingshop', 'craftjournal', 'coracreacraftbox', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2020-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CGV8qmMnp9W/media/?size=m'
    },
    {
      id: 'ig-api-1123',
      platform: 'instagram',
      title: 'These new washi tapes inspired me to get music with mysterious mystical vibes...',
      description: 'These new washi tapes inspired me to get music with mysterious mystical vibes 🐍⏳ Available in my shop !  #washitapeaddict #washishop #washitapeshop #washitape #magictheme #magicjournal #magicwashitap',
      url: 'https://www.instagram.com/reel/CF5OunkHG9k/',
      embedId: 'CF5OunkHG9k',
      category: 'box-reveal',
      tags: ['washitapeaddict', 'washishop', 'washitapeshop', 'washitape', 'magictheme', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2020-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CF5OunkHG9k/media/?size=m'
    },
    {
      id: 'ig-api-1124',
      platform: 'instagram',
      title: 'Hello October 🍄🍁My favorite month just started, beautiful colors, crisp air...',
      description: 'Hello October 🍄🍁My favorite month just started, beautiful colors, crisp air and earthy smells ! What\'s your favorite month? And how cute are these mushrooms 😍  #hellooctober #octobercalligraphy #oc',
      url: 'https://www.instagram.com/p/CFz1GxmHQnT/',
      embedId: 'CFz1GxmHQnT',
      category: 'journal-spread',
      tags: ['hellooctober', 'octobercalligraphy', 'octoberlettering', 'pointedpen', 'pointedpencallihraphy', 'autumn'],
      creator: 'CoraCreaCrafts',
      date: '2020-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CFz1GxmHQnT/media/?size=m'
    },
    {
      id: 'ig-api-1125',
      platform: 'instagram',
      title: 'October Vintage Craft Box 🍄🍁 Only a couple boxes available left on Etsy and...',
      description: 'October Vintage Craft Box 🍄🍁 Only a couple boxes available left on Etsy and Cratejoy - link in bio 🖤  Some of the items:  🍁 Unique handmade watercolors from @reneeissancecolours 🍁Large mushroom s',
      url: 'https://www.instagram.com/tv/CFht5XmHJ02/',
      embedId: '',
      category: 'box-reveal',
      tags: ['autumnwatercolors', 'handmadewatercolors', 'autumncrafts', 'fallcrafts', 'fallcraftsupplies', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2020-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-1126',
      platform: 'instagram',
      title: 'My first entry for the  challenge hosted by these 3 wonderful ladies @greetin...',
      description: 'My first entry for the #babyyodaletters challenge hosted by these 3 wonderful ladies @greetingsfromkirsten @justwriteawayrenee @stuffmynephewsays I finally took out my inkpen for some practice today a',
      url: 'https://www.instagram.com/reel/CFamFrGHd-W/',
      embedId: 'CFamFrGHd-W',
      category: 'tutorial',
      tags: ['babyyodaletters', 'lettering', 'watercolorlettering', 'letteringchallenge', 'calligraphy'],
      creator: 'CoraCreaCrafts',
      date: '2020-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CFamFrGHd-W/media/?size=m'
    },
    {
      id: 'ig-api-1127',
      platform: 'instagram',
      title: 'New Craft Supplies Box available in my shop for all the witchy souls and/or H...',
      description: 'New Craft Supplies Box available in my shop for all the witchy souls and/or Halloween addicts 🧹🕷️🐍🕯️ If you are more interested in one theme or another, they can be purchased separately !  Also is',
      url: 'https://www.instagram.com/reel/CFXP-FnHnry/',
      embedId: 'CFXP-FnHnry',
      category: 'box-reveal',
      tags: ['magictheme', 'magicjournal', 'magiccraft', 'magiccraftbox', 'witchcrafts', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2020-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CFXP-FnHnry/media/?size=m'
    },
    {
      id: 'ig-api-1128',
      platform: 'instagram',
      title: 'Happy Birthday to one of the coolest person I\'ve met through the crafty insta...',
      description: 'Happy Birthday to one of the coolest person I\'ve met through the crafty instagram community 🥳🎈We have not yet met in real life but I consider you a true friend 😘 Thank you for being so supportive a',
      url: 'https://www.instagram.com/p/CFSQQLUn0ZP/',
      embedId: 'CFSQQLUn0ZP',
      category: 'journal-spread',
      tags: ['birthdaycard', 'birthdaygift', 'happybirthday', 'dyibirthday', 'handmadecards', 'autumn'],
      creator: 'CoraCreaCrafts',
      date: '2020-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CFSQQLUn0ZP/media/?size=m'
    },
    {
      id: 'ig-api-1129',
      platform: 'instagram',
      title: 'October Vintage Craft Box sneek peek of some of the items included 🍄🍁 I\'m s...',
      description: 'October Vintage Craft Box sneek peek of some of the items included 🍄🍁 I\'m so excited about this box because Autumn is my favorite season and I really enjoyed putting all the cute seasonal and vintag',
      url: 'https://www.instagram.com/reel/CFQ8d4En-fi/',
      embedId: 'CFQ8d4En-fi',
      category: 'box-reveal',
      tags: ['autumnwatercolors', 'handmadewatercolors', 'autumncrafts', 'fallcrafts', 'fallcraftsupplies', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2020-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CFQ8d4En-fi/media/?size=m'
    },
    {
      id: 'ig-api-1130',
      platform: 'instagram',
      title: 'Got inspired by the wonderful @letras.literarias.mx to make my own version of...',
      description: 'Got inspired by the wonderful @letras.literarias.mx to make my own version of DYI stamps with an Autumnal theme 🍄 Using the Autumn Sticker Sheets from my shop - link in bio ! #autumnspread #autumnsta',
      url: 'https://www.instagram.com/tv/CFIputZnah2/',
      embedId: '',
      category: 'journal-spread',
      tags: ['autumnspread', 'autumnstamps', 'autumnstickers', 'dyistamps', 'dyivideo', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2020-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-1131',
      platform: 'instagram',
      title: 'Some new Autumnal stickers in a dark vintage style, great for Fall & Hallowee...',
      description: 'Some new Autumnal stickers in a dark vintage style, great for Fall & Halloween crafts 🖤 These will exclusively be sold as part of the October Vintage Craft Box ! Link in bio - sold on Etsy & Cratejoy',
      url: 'https://www.instagram.com/reel/CFDMFbbnlsR/',
      embedId: 'CFDMFbbnlsR',
      category: 'box-reveal',
      tags: ['coracreacraftbox', 'craftingsupplies', 'craftingshop', 'vintagecraftbox', 'vintagebrochures', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CFDMFbbnlsR/media/?size=m'
    },
    {
      id: 'ig-api-1132',
      platform: 'instagram',
      title: 'Little mountain doodle in music 🏞️🎼 Page created for a trip to Yellowstone ...',
      description: 'Little mountain doodle in music 🏞️🎼 Page created for a trip to Yellowstone NP in my last journal. Have a nice weekend ❤️ #yellowstone #jacksonhole #traveljournal #travelersnotebook #travelmemories #',
      url: 'https://www.instagram.com/p/CFCrzh0HEaW/',
      embedId: 'CFCrzh0HEaW',
      category: 'journal-spread',
      tags: ['yellowstone', 'jacksonhole', 'traveljournal', 'travelersnotebook', 'travelmemories', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CFCrzh0HEaW/media/?size=m'
    },
    {
      id: 'ig-api-1133',
      platform: 'instagram',
      title: 'Sneak peek: 2 of the items that you\'ll receive as part of the Vintage Craft B...',
      description: 'Sneak peek: 2 of the items that you\'ll receive as part of the Vintage Craft Box for the month of October 🍁 - Mushroom stamps 🍄 must of have for the season - Beautiful handmade watercolors in seasona',
      url: 'https://www.instagram.com/reel/CE7niAbnKtN/',
      embedId: 'CE7niAbnKtN',
      category: 'box-reveal',
      tags: ['craftingtools', 'craftingsupplies', 'sneakpeeks', 'vintagecard', 'vintagemushroom', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CE7niAbnKtN/media/?size=m'
    },
    {
      id: 'ig-api-1134',
      platform: 'instagram',
      title: 'One of my bucket list places for 2021 is Wyoming and specifically  Yellowston...',
      description: 'One of my bucket list places for 2021 is Wyoming and specifically  Yellowstone & Grand Teton NP 🍁🏔️ I made this Autumnal vibes spread for my most recent travel journal order - the happy couple decid',
      url: 'https://www.instagram.com/p/CE4082-nr-Z/',
      embedId: 'CE4082-nr-Z',
      category: 'journal-spread',
      tags: ['wyoming', 'visittheusa', 'visitwyoming', 'grandteton', 'yellowstone', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2020-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CE4082-nr-Z/media/?size=m'
    },
    {
      id: 'ig-api-1135',
      platform: 'instagram',
      title: 'New Wax Seal available in my shop ❤️💌 Here wrapping my Illuminated Letter St...',
      description: 'New Wax Seal available in my shop ❤️💌 Here wrapping my Illuminated Letter Stickers with a vellum cover, twine and the seal  #waxseals #wax #waxstamp #sealingwax #stampitup #stampingart #stampcollecti',
      url: 'https://www.instagram.com/reel/CExVk2_nBys/',
      embedId: 'CExVk2_nBys',
      category: 'behind-the-scenes',
      tags: ['waxseals', 'wax', 'waxstamp', 'sealingwax', 'stampitup', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2020-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CExVk2_nBys/media/?size=m'
    },
    {
      id: 'ig-api-1136',
      platform: 'instagram',
      title: 'September Monthly cover & overview 🖤 for this cover I made a travel & vintag...',
      description: 'September Monthly cover & overview 🖤 for this cover I made a travel & vintage inspired spread as a small collaboration with the lovely @zepeto_26 as we both dream about being able to travel again 🗺️',
      url: 'https://www.instagram.com/p/CEm-IyNHE_-/',
      embedId: 'CEm-IyNHE_-',
      category: 'journal-spread',
      tags: ['helloseptember', 'september', 'septemberdpread', 'deptembercoverpage', 'coverpagevideo', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2020-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CEm-IyNHE_-/media/?size=m'
    },
    {
      id: 'ig-api-1137',
      platform: 'instagram',
      title: 'September Box Sneak Peek Video 🎥📦 still a few available in my store ! Link ...',
      description: 'September Box Sneak Peek Video 🎥📦 still a few available in my store ! Link in bio 🖤 #coracreacraftbox #coracreacrafts #craftingtools #craftingsupplies #craftingshop #craftbox #vintagesupplies #jour',
      url: 'https://www.instagram.com/p/CEe7IVDHfMC/',
      embedId: 'CEe7IVDHfMC',
      category: 'box-reveal',
      tags: ['coracreacraftbox', 'coracreacrafts', 'craftingtools', 'craftingsupplies', 'craftingshop', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2020-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CEe7IVDHfMC/media/?size=m'
    },
    {
      id: 'ig-api-1138',
      platform: 'instagram',
      title: 'Finally had a chance to edit my map making process from a few days ago 🗺️ It...',
      description: 'Finally had a chance to edit my map making process from a few days ago 🗺️ It\'s been a while since I\'ve made one and it was really fun to get back to it ! This will be part of a custom Travel Journal ',
      url: 'https://www.instagram.com/p/CEU2AFwH0yp/',
      embedId: 'CEU2AFwH0yp',
      category: 'product-showcase',
      tags: ['traveljournal', 'customjournal', 'customjournal', 'customtraveljournals', 'coracreatraveljournals', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CEU2AFwH0yp/media/?size=m'
    },
    {
      id: 'ig-api-1139',
      platform: 'instagram',
      title: 'Quick and simple spread for the next few days 🌿 still following the vintage ...',
      description: 'Quick and simple spread for the next few days 🌿 still following the vintage plant theme. I\'m using the Botany Encyclopedia stickers from my shop to highlight each day 💚 Happy weekend ! #coracreacraf',
      url: 'https://www.instagram.com/p/CD6ubdmHtHh/',
      embedId: 'CD6ubdmHtHh',
      category: 'journal-spread',
      tags: ['coracreacrafts', 'etsyshop', 'etsystickers', 'handmadestickers', 'stickershop', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CD6ubdmHtHh/media/?size=m'
    },
    {
      id: 'ig-api-1140',
      platform: 'instagram',
      title: 'Snapshot of the August Vintage Craft Box 🌿 Orders for September now open ! T...',
      description: 'Snapshot of the August Vintage Craft Box 🌿 Orders for September now open ! The theme will be Renaissance & Inventors ⚙️ available both in my Etsy shop and on Cratejoy 🖤  #coracreacraftbox #coracreas',
      url: 'https://www.instagram.com/p/CDuNBsDHn2A/',
      embedId: 'CDuNBsDHn2A',
      category: 'box-reveal',
      tags: ['coracreacraftbox', 'coracreastickers', 'coracrea', 'stickersaddict', 'subscriptionbox', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2020-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CDuNBsDHn2A/media/?size=m'
    },
    {
      id: 'ig-api-1141',
      platform: 'instagram',
      title: 'Personalized birthday gift card that was shipped together with a vintage craf...',
      description: 'Personalized birthday gift card that was shipped together with a vintage craft box 🖤📦 Such a good idea to order one as a gift 🤗 I hope the recipient enjoyed it !  You can order as a gift on Etsy or',
      url: 'https://www.instagram.com/p/CDlxKaknJS-/',
      embedId: 'CDlxKaknJS-',
      category: 'behind-the-scenes',
      tags: ['giftbox', 'craftgifts', 'giftideas', 'etsygifts', 'craftingtools', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CDlxKaknJS-/media/?size=m'
    },
    {
      id: 'ig-api-1142',
      platform: 'instagram',
      title: 'New travel stamps ✈️🧳Comment your favorite 🖤',
      description: 'New travel stamps ✈️🧳Comment your favorite 🖤  I love stamping on vellum paper 🤗 Happy Sunday ! #woodenstamps #stamps #stampingart #stationaryaddict #stampshop #stampcollection #etsyshop #stationery',
      url: 'https://www.instagram.com/p/CDY8qRYHzIr/',
      embedId: 'CDY8qRYHzIr',
      category: 'product-showcase',
      tags: ['woodenstamps', 'stamps', 'stampingart', 'stationaryaddict', 'stampshop', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CDY8qRYHzIr/media/?size=m'
    },
    {
      id: 'ig-api-1143',
      platform: 'instagram',
      title: 'Fun & easy vellum paper package for my calendar stickers 🖤💌 Ready to ship 📦',
      description: 'Fun & easy vellum paper package for my calendar stickers 🖤💌 Ready to ship 📦  Stickers available in Etsy - link in bio or by tapping the video  #calendarstickers #monthstickers #handmadestickers #mo',
      url: 'https://www.instagram.com/p/CDBwv_oHsGo/',
      embedId: 'CDBwv_oHsGo',
      category: 'journal-spread',
      tags: ['calendarstickers', 'monthstickers', 'handmadestickers', 'monthlystickers', 'bulletjournalstickers', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CDBwv_oHsGo/media/?size=m'
    },
    {
      id: 'ig-api-1144',
      platform: 'instagram',
      title: 'Encyclopedia 🖤💌 Spread to illustrate some of my research & to celebrate my ...',
      description: 'Encyclopedia 🖤💌 Spread to illustrate some of my research & to celebrate my new sticker collection "Vintage Encyclopedia" with various themes related to nature, science and art 🧪🔭 The stickers used',
      url: 'https://www.instagram.com/p/CCt8mxKn_ed/',
      embedId: 'CCt8mxKn_ed',
      category: 'journal-spread',
      tags: ['stickersaddict', 'stickerset', 'stickershop', 'handmadestickers', 'naturalhistorystickers', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CCt8mxKn_ed/media/?size=m'
    },
    {
      id: 'ig-api-1145',
      platform: 'instagram',
      title: 'Orders for the August Box are now open 💌📦 (both on Etsy and Cratejoy - link...',
      description: 'Orders for the August Box are now open 💌📦 (both on Etsy and Cratejoy - link in bio) This is a snapshot of the July Craft Box which was on the theme of Vintage Travel 🌍 p.s. boxes can vary slightly ',
      url: 'https://www.instagram.com/p/CCeMuUanDlw/',
      embedId: 'CCeMuUanDlw',
      category: 'unboxing',
      tags: ['craftingtools', 'craftingsupplies', 'craftbox', 'subscriptionbox', 'vintagecrafts', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2020-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CCeMuUanDlw/media/?size=m'
    },
    {
      id: 'ig-api-1146',
      platform: 'instagram',
      title: 'Vintage Calendar Stickers 📜 Used the July one to keep myself organized with ...',
      description: 'Vintage Calendar Stickers 📜 Used the July one to keep myself organized with my list of To Dos and Tasks 📝 These unique stickers are now available in my shop 🖤⁠ #stickershop #stickerlover #etsyshop ',
      url: 'https://www.instagram.com/p/CCL93tdp00J/',
      embedId: 'CCL93tdp00J',
      category: 'product-showcase',
      tags: ['stickershop', 'stickerlover', 'etsyshop', 'etsyseller', 'etsycommunity', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CCL93tdp00J/media/?size=m'
    },
    {
      id: 'ig-api-1147',
      platform: 'instagram',
      title: 'July is around the corner ☀️ Here is a video of a July spread I\'ve made for a...',
      description: 'July is around the corner ☀️ Here is a video of a July spread I\'ve made for a #customplanner 📖 on a "vintage travel" theme ✈️ July usually is the typical month to travel on holiday but this year is d',
      url: 'https://www.instagram.com/p/CCBtm47HLiA/',
      embedId: 'CCBtm47HLiA',
      category: 'journal-spread',
      tags: ['customplanner', 'hellojuly', 'julyspread', 'julybujo', 'travelplans', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CCBtm47HLiA/media/?size=m'
    },
    {
      id: 'ig-api-1148',
      platform: 'instagram',
      title: 'Already the last prompt of our  challenge ! It went by way too fast. Thank yo...',
      description: 'Already the last prompt of our #makeityourwaysummer challenge ! It went by way too fast. Thank you to all of you who have participated in this challenge in your OWN way. It was so inspiring to see eve',
      url: 'https://www.instagram.com/p/CB8-1wvHP5W/',
      embedId: 'CB8-1wvHP5W',
      category: 'product-showcase',
      tags: ['makeityourwaysummer', 'summerchallenge', 'letteringchallenge', 'summercrafts', 'craftingcommunity', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CB8-1wvHP5W/media/?size=m'
    },
    {
      id: 'ig-api-1149',
      platform: 'instagram',
      title: 'Long Summer Nights... reminded me of this song 🎵 Who also loves the music fr...',
      description: 'Long Summer Nights... reminded me of this song 🎵 Who also loves the music from Grease? I enjoyed making this but realized i really need more practice with brush lettering !⁠ My entry for the #makeity',
      url: 'https://www.instagram.com/p/CB3tGMbJM-M/',
      embedId: 'CB3tGMbJM-M',
      category: 'tutorial',
      tags: ['makeityourwaysummer', 'summerchallenge', 'letteringchallenge', 'letteringtutorial', 'letteringvideo'],
      creator: 'CoraCreaCrafts',
      date: '2020-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CB3tGMbJM-M/media/?size=m'
    },
    {
      id: 'ig-api-1150',
      platform: 'instagram',
      title: 'Celebration means... Good Drinks 🍸 Or do Good Drinks mean Celebration? 🤔😂 ...',
      description: 'Celebration means... Good Drinks 🍸 Or do Good Drinks mean Celebration? 🤔😂 This is a special entry for the #makeityourwaysummer challenge because today\'s the birthday of my wonderful co-host and fri',
      url: 'https://www.instagram.com/p/CByAMOEH-lS/',
      embedId: 'CByAMOEH-lS',
      category: 'tutorial',
      tags: ['makeityourwaysummer', 'makeityourwaychallenge', 'summerchallenge', 'celebrationmeans', 'celebratingsummer'],
      creator: 'CoraCreaCrafts',
      date: '2020-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CByAMOEH-lS/media/?size=m'
    },
    {
      id: 'ig-api-1151',
      platform: 'instagram',
      title: 'Summer Solstice ☀️ Longest day of the year. I made this tag to use as a bookm...',
      description: 'Summer Solstice ☀️ Longest day of the year. I made this tag to use as a bookmark 🔖 using the beautiful @stamperia paper as a base. Part of the #makeityourwaysummer challenge⁠ hosted with the lovelies',
      url: 'https://www.instagram.com/p/CBqwYkMHf2K/',
      embedId: 'CBqwYkMHf2K',
      category: 'product-showcase',
      tags: ['makeityourwaysummer', 'summersolstice', 'longestday', 'celebrationofsummer', 'summertime', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2020-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CBqwYkMHf2K/media/?size=m'
    },
    {
      id: 'ig-api-1152',
      platform: 'instagram',
      title: 'I had so much fun putting together the second batch of my new Crafting Suppli...',
      description: 'I had so much fun putting together the second batch of my new Crafting Supplies Subscription Box 📦 This is the packing video for the June Box ❤️ Orders for the July Box are now open on Etsy until the',
      url: 'https://www.instagram.com/p/CBlZItzHdeF/',
      embedId: 'CBlZItzHdeF',
      category: 'behind-the-scenes',
      tags: ['subscriptionbox', 'craftingsupplies', 'craftingbox', 'suprisebox', 'craftingtools', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CBlZItzHdeF/media/?size=m'
    },
    {
      id: 'ig-api-1153',
      platform: 'instagram',
      title: 'My Garden is full of.... Butterflies 🦋🦋 Prompt 8 of the  challenge - DYI st...',
      description: 'My Garden is full of.... Butterflies 🦋🦋 Prompt 8 of the #makeityourwaysummer challenge - DYI stickers with rubber stamps & glittery pens (inspired by the wonderful @emadam.de) ⁠ Don\'t forget you can',
      url: 'https://www.instagram.com/p/CBiwQ6MBGAo/',
      embedId: 'CBiwQ6MBGAo',
      category: 'tutorial',
      tags: ['makeityourwaysummer', 'stationery', 'tutorial', 'dyi', 'sticker', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CBiwQ6MBGAo/media/?size=m'
    },
    {
      id: 'ig-api-1154',
      platform: 'instagram',
      title: 'Favorite Summer Food for the  challenge ☀️ Can you guess what mine is? 🍓🍓 I...',
      description: 'Favorite Summer Food for the #makeityourwaysummer challenge ☀️ Can you guess what mine is? 🍓🍓 I used a beautiful strawberry sticker tape which is now available in my shop 😍 Tap on the video for the',
      url: 'https://www.instagram.com/p/CBgCWzql-Mr/',
      embedId: 'CBgCWzql-Mr',
      category: 'tutorial',
      tags: ['makeityourwaysummer', 'summerfood', 'foodie', 'strawberryaddict', 'berries', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2020-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CBgCWzql-Mr/media/?size=m'
    },
    {
      id: 'ig-api-1155',
      platform: 'instagram',
      title: 'Heads in the Clouds, Feet... in the stars ⭐ I give you my Ancient Greece styl...',
      description: 'Heads in the Clouds, Feet... in the stars ⭐ I give you my Ancient Greece style foot constellation 😂This is about a dream that sky is not the limit: there is a whole universe out there for us to disco',
      url: 'https://www.instagram.com/p/CBYNXt5niQt/',
      embedId: 'CBYNXt5niQt',
      category: 'tutorial',
      tags: ['makeityourwaysummer', 'nightskypainting', 'nightsky', 'constellation', 'constellationart', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CBYNXt5niQt/media/?size=m'
    },
    {
      id: 'ig-api-1156',
      platform: 'instagram',
      title: 'Favorite Summer Travel Spot - for this  prompt I didn\'t hesitate one second. ...',
      description: 'Favorite Summer Travel Spot - for this #makeityourwaysummer prompt I didn\'t hesitate one second. Lakes remind me of countless summers in Switzerland, swimming in the lakes. To me, Summer = Lake 🏞️ Si',
      url: 'https://www.instagram.com/p/CBTI--Al6GJ/',
      embedId: 'CBTI--Al6GJ',
      category: 'tutorial',
      tags: ['makeityourwaysummer', 'lifeatthelake', 'summeratthelake', 'lakes', 'lakequotes', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CBTI--Al6GJ/media/?size=m'
    },
    {
      id: 'ig-api-1157',
      platform: 'instagram',
      title: 'Favorite Summer Song - Prompt 2 of the  Challenge ☀️ Few people probably know...',
      description: 'Favorite Summer Song - Prompt 2 of the #makeityourwaysummer Challenge ☀️ Few people probably know this French song from Polo & Pan called La Canopée. I\'ve discovered it recently and find love its rela',
      url: 'https://www.instagram.com/p/CA-3LmNnQeZ/',
      embedId: 'CA-3LmNnQeZ',
      category: 'product-showcase',
      tags: ['makeityourwaysummer', 'makeityourwaychallenge', 'artchallenge', 'letteringchallenge', 'craftchallenge', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CA-3LmNnQeZ/media/?size=m'
    },
    {
      id: 'ig-api-1158',
      platform: 'instagram',
      title: 'A Celebration of Summer - Day 1 of the  Challenge ☀️ For this prompt, I\'ve de...',
      description: 'A Celebration of Summer - Day 1 of the #makeityourwaysummer Challenge ☀️ For this prompt, I\'ve decided to create my June bullet journal spread on the theme of summer and insert all these little things',
      url: 'https://www.instagram.com/p/CA5pFl6HOsR/',
      embedId: 'CA5pFl6HOsR',
      category: 'journal-spread',
      tags: ['makeityourwaysummer', 'makeityourwaychallenge', 'artchallenge', 'letteringchallenge', 'craftchallenge', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CA5pFl6HOsR/media/?size=m'
    },
    {
      id: 'ig-api-1159',
      platform: 'instagram',
      title: '🌵 Who\'s also a cactus lover? 🌵 Enjoyed making this custom birthday card usi...',
      description: '🌵 Who\'s also a cactus lover? 🌵 Enjoyed making this custom birthday card using my @cricutofficial ⁠ #cricut #cricutprojects #birthdaycard #handmadecard #handmadebdaycard #customcard #custombday ⁠ #tu',
      url: 'https://www.instagram.com/p/CAvUr71nWxK/',
      embedId: 'CAvUr71nWxK',
      category: 'tutorial',
      tags: ['cricut', 'cricutprojects', 'birthdaycard', 'handmadecard', 'handmadebdaycard', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CAvUr71nWxK/media/?size=m'
    },
    {
      id: 'ig-api-1160',
      platform: 'instagram',
      title: 'New Plant Stamps 🌿🍁 Just love using stamps on slightly transparent vellum p...',
      description: 'New Plant Stamps 🌿🍁 Just love using stamps on slightly transparent vellum paper 😍⁠ Fond them on Etsy ! #stamps #plantstamps #stampshop #stamplover #vellumpaper #vellumtags #stamptags #tutorialvideo',
      url: 'https://www.instagram.com/p/CAno7msHLJs/',
      embedId: 'CAno7msHLJs',
      category: 'tutorial',
      tags: ['stamps', 'plantstamps', 'stampshop', 'stamplover', 'vellumpaper', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CAno7msHLJs/media/?size=m'
    },
    {
      id: 'ig-api-1161',
      platform: 'instagram',
      title: 'Who\'s been there? 🤚 That\'s another destination on my "America" bucket list ...',
      description: 'Who\'s been there? 🤚 That\'s another destination on my "America" bucket list 🇺🇸 So enjoyed making this little map for this custom travel journal 😍⁠ #traveljournal #travelersjournal #travelersnoteboo',
      url: 'https://www.instagram.com/p/CAa3BuHARRA/',
      embedId: 'CAa3BuHARRA',
      category: 'journal-spread',
      tags: ['traveljournal', 'travelersjournal', 'travelersnotebook', 'customjournal', 'customtraveljournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CAa3BuHARRA/media/?size=m'
    },
    {
      id: 'ig-api-1162',
      platform: 'instagram',
      title: 'What I miss the most from my home country is the mountains 🏔️🇨🇭 Making thi...',
      description: 'What I miss the most from my home country is the mountains 🏔️🇨🇭 Making this spread inspired me to go discover the Rocky Mountains in the US - I will take a trip to Glacier National Park and Yellows',
      url: 'https://www.instagram.com/p/CAVtWfwg57x/',
      embedId: 'CAVtWfwg57x',
      category: 'journal-spread',
      tags: ['journalingmay', 'mountains', 'switzerland', 'visittheusa', 'visitamerica', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2020-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CAVtWfwg57x/media/?size=m'
    },
    {
      id: 'ig-api-1163',
      platform: 'instagram',
      title: 'I had so much fun putting together the very first batch of my new Crafting Su...',
      description: 'I had so much fun putting together the very first batch of my new Crafting Supplies Subscription Box 📦 This is the packing video for the May Box which was a special Plants edition 🌱 Orders for the J',
      url: 'https://www.instagram.com/p/CALHpnpnyxZ/',
      embedId: 'CALHpnpnyxZ',
      category: 'box-reveal',
      tags: ['subscriptionbox', 'craftingsupplies', 'craftingbox', 'suprisebox', 'craftingtools', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CALHpnpnyxZ/media/?size=m'
    },
    {
      id: 'ig-api-1164',
      platform: 'instagram',
      title: 'It\'s never too early to start making travel plans again 😁✈️Here is a simple ...',
      description: 'It\'s never too early to start making travel plans again 😁✈️Here is a simple way to make a travel bucket list spread in your journal 🌎⁠ #bucketlist #travelbucketlist #travelplans #timetoplan #travelp',
      url: 'https://www.instagram.com/p/CAGH8eHFXSY/',
      embedId: 'CAGH8eHFXSY',
      category: 'journal-spread',
      tags: ['bucketlist', 'travelbucketlist', 'travelplans', 'timetoplan', 'travelplanning', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/CAGH8eHFXSY/media/?size=m'
    },
    {
      id: 'ig-api-1165',
      platform: 'instagram',
      title: '"It\'s not about the destination, it\'s about the journey" 🥾🌍',
      description: '"It\'s not about the destination, it\'s about the journey" 🥾🌍 #traveljournal #travelersjournal #travelersnotebook #customjournal #customtraveljournal #coracrea #etsycustom #estygifts #personalizedjour',
      url: 'https://www.instagram.com/p/B_-U1Ptn_gg/',
      embedId: 'B_-U1Ptn_gg',
      category: 'journal-spread',
      tags: ['traveljournal', 'travelersjournal', 'travelersnotebook', 'customjournal', 'customtraveljournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B_-U1Ptn_gg/media/?size=m'
    },
    {
      id: 'ig-api-1166',
      platform: 'instagram',
      title: 'What\'s the first A word that comes to your mind? Mine is ASPIRE 🗯️ Lately, a...',
      description: 'What\'s the first A word that comes to your mind? Mine is ASPIRE 🗯️ Lately, after moving and the current situation, I\'ve been aspiring to change my lifestyle but more specifically to strive to develop',
      url: 'https://www.instagram.com/p/B_2w_Jll14q/',
      embedId: 'B_2w_Jll14q',
      category: 'product-showcase',
      tags: ['aspire', 'aspiration', 'lettering', 'letteringa', 'lettera', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B_2w_Jll14q/media/?size=m'
    },
    {
      id: 'ig-api-1167',
      platform: 'instagram',
      title: 'May Bullet Journal Spread 🕊️ It was time for a change in how I organize myse...',
      description: 'May Bullet Journal Spread 🕊️ It was time for a change in how I organize myself - this month I decided to plan things differently so no more of the usual weekly spreads: I haven\'t used my journal much',
      url: 'https://www.instagram.com/p/B_xmyo5AnL6/',
      embedId: 'B_xmyo5AnL6',
      category: 'journal-spread',
      tags: ['mayjournal', 'coverpage', 'mayspread', 'maybujo', 'monthlycover', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B_xmyo5AnL6/media/?size=m'
    },
    {
      id: 'ig-api-1168',
      platform: 'instagram',
      title: 'Beautiful Spring days required this very flowery spread 🌸🌼 For a slower ver...',
      description: 'Beautiful Spring days required this very flowery spread 🌸🌼 For a slower version of this spread, please check out my Youtube channel "Cora Crea" 🎬⁠ #springdays #hellospring #hellomay #beautifuldays ',
      url: 'https://www.instagram.com/p/B_se-70joe-/',
      embedId: 'B_se-70joe-',
      category: 'journal-spread',
      tags: ['springdays', 'hellospring', 'hellomay', 'beautifuldays', 'floweryspread', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B_se-70joe-/media/?size=m'
    },
    {
      id: 'ig-api-1169',
      platform: 'instagram',
      title: 'What do you wish for in May? 🙏🏻 I wish for things to get better, so interna...',
      description: 'What do you wish for in May? 🙏🏻 I wish for things to get better, so international travel can reopen, and so I can finally start planning my July wedding 😅👰✈️Maybe too optimistic but one can hope !',
      url: 'https://www.instagram.com/p/B_nOtoMn7AX/',
      embedId: 'B_nOtoMn7AX',
      category: 'product-showcase',
      tags: ['mayoverview', 'mayjournal', 'hellomay', 'wishformay', 'may', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B_nOtoMn7AX/media/?size=m'
    },
    {
      id: 'ig-api-1170',
      platform: 'instagram',
      title: 'I love to decorate journal inserts 😍 It\'s fun and easy and makes the insert ...',
      description: 'I love to decorate journal inserts 😍 It\'s fun and easy and makes the insert look like something completely different 📔⁠ #journalingmay #coverpage #scrapbooking #scrapookinglover #scrapbookingvideo #',
      url: 'https://www.instagram.com/p/B_k3XeFHELt/',
      embedId: 'B_k3XeFHELt',
      category: 'journal-spread',
      tags: ['journalingmay', 'coverpage', 'scrapbooking', 'scrapookinglover', 'scrapbookingvideo', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2020-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B_k3XeFHELt/media/?size=m'
    },
    {
      id: 'ig-api-1171',
      platform: 'instagram',
      title: 'On its way to its recipient 😍 These packing up videos are the most satisfyin...',
      description: 'On its way to its recipient 😍 These packing up videos are the most satisfying of all ! ⁠ #traveljournal #customtraveljournal #customjournal #customtraveljournal #customgift #etsycustom #estygifts #pe',
      url: 'https://www.instagram.com/p/B_fjyM9ncHJ/',
      embedId: 'B_fjyM9ncHJ',
      category: 'journal-spread',
      tags: ['traveljournal', 'customtraveljournal', 'customjournal', 'customtraveljournal', 'customgift', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B_fjyM9ncHJ/media/?size=m'
    },
    {
      id: 'ig-api-1172',
      platform: 'instagram',
      title: 'Spring Vintage Birthday Card Idea 💌✂️ Using the Retro Botanical Sticker Pack...',
      description: 'Spring Vintage Birthday Card Idea 💌✂️ Using the Retro Botanical Sticker Pack available in my shop 🌺🌿 It was my best friend\'s birthday yesterday and she just got it in the mail 🧡⁠ #birthdaycard #ha',
      url: 'https://www.instagram.com/p/B_VO1gZnIfy/',
      embedId: 'B_VO1gZnIfy',
      category: 'behind-the-scenes',
      tags: ['birthdaycard', 'handmadecard', 'customcard', 'retrocard', 'retrobdaycard', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B_VO1gZnIfy/media/?size=m'
    },
    {
      id: 'ig-api-1173',
      platform: 'instagram',
      title: 'Have you been to the Yellowstone National Park? If so, you might recognize th...',
      description: 'Have you been to the Yellowstone National Park? If so, you might recognize this wonder of nature 🤩 I absolutely want to visit and see these dazzling colors with my own eyes 💚💜🧡 ⁠ #yellowstone #vis',
      url: 'https://www.instagram.com/p/B_QQqYEnhtx/',
      embedId: 'B_QQqYEnhtx',
      category: 'tutorial',
      tags: ['yellowstone', 'visityellowstone', 'grandprismatic', 'grandprismaticdrawing', 'grandprismaticpainting', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B_QQqYEnhtx/media/?size=m'
    },
    {
      id: 'ig-api-1174',
      platform: 'instagram',
      title: 'Crafty supplies packing 🏷️ One of my favorite activities ❤️⁠',
      description: 'Crafty supplies packing 🏷️ One of my favorite activities ❤️⁠ Here is what I packaged in this video:⁠ 1. Scholar Retro Stickers ⁠ 2. Retro Botanic Stickers ⁠ 3. Vintage Newspaper Stickers⁠ 4. \'Portrai',
      url: 'https://www.instagram.com/p/B_LHF7xHbIE/',
      embedId: 'B_LHF7xHbIE',
      category: 'behind-the-scenes',
      tags: ['crafting', 'craftingsupplies', 'craftysupplies', 'journalingsupplies', 'craftsupplies', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2020-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B_LHF7xHbIE/media/?size=m'
    },
    {
      id: 'ig-api-1175',
      platform: 'instagram',
      title: 'My favorite part of visiting a new city is exploring the Old Town. I\'ve heard...',
      description: 'My favorite part of visiting a new city is exploring the Old Town. I\'ve heard Wichita\'s Old Town has some wonderful antique shops 🖤 Definitely one more destination for my ever growing "To See in the ',
      url: 'https://www.instagram.com/p/B_Ikg8xHt6S/',
      embedId: 'B_Ikg8xHt6S',
      category: 'tutorial',
      tags: ['wichita', 'wichitaoldtown', 'visittheusa', 'usroadtrip', 'ustour', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B_Ikg8xHt6S/media/?size=m'
    },
    {
      id: 'ig-api-1176',
      platform: 'instagram',
      title: 'Packing some Illuminated Letter Stickers 💌 These are my favorite to use on d...',
      description: 'Packing some Illuminated Letter Stickers 💌 These are my favorite to use on decorating snail mail and they make me travel back in time 🕙 Available on Etsy ⁠ #etsyseller #etsyshop #etsystickers #etsys',
      url: 'https://www.instagram.com/p/B_DpG4eJ0Tr/',
      embedId: 'B_DpG4eJ0Tr',
      category: 'behind-the-scenes',
      tags: ['etsyseller', 'etsyshop', 'etsystickers', 'etsystickershop', 'stickeraddicts', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2020-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B_DpG4eJ0Tr/media/?size=m'
    },
    {
      id: 'ig-api-1177',
      platform: 'instagram',
      title: 'Lettering Close-up 🖋️ for my custom travel journal 🖤 In which state is Wich...',
      description: 'Lettering Close-up 🖋️ for my custom travel journal 🖤 In which state is Wichita? Test your geography skills in my story 😄 (I definitely wouldn\'t have known If I hadn\'t been making this USA road trip',
      url: 'https://www.instagram.com/p/B_BA2SLHyiX/',
      embedId: 'B_BA2SLHyiX',
      category: 'product-showcase',
      tags: ['letteringcloseup', 'letteringvideo', 'letteringlove', 'letteringjournal', 'letteringchallenge', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B_BA2SLHyiX/media/?size=m'
    },
    {
      id: 'ig-api-1178',
      platform: 'instagram',
      title: 'Have you ever been to Colorado? ⛰️ It\'s been on top of my bucket list for des...',
      description: 'Have you ever been to Colorado? ⛰️ It\'s been on top of my bucket list for destinations in the US for a while and I\'m hoping to get to go at the end of the year or next year ! I created this vintage st',
      url: 'https://www.instagram.com/tv/B--NQYJnOBN/',
      embedId: '',
      category: 'product-showcase',
      tags: ['usroadtrip', 'roadtrip', 'poster', 'vintageposter', 'cityposter', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-1179',
      platform: 'instagram',
      title: 'Itinerary Page for my latest Travel Journal order ❤️🌏 Organized freak as I a...',
      description: 'Itinerary Page for my latest Travel Journal order ❤️🌏 Organized freak as I am, I find this page essential for any trip ! It gives an overview of where the trip is taking you and what the plan is for ',
      url: 'https://www.instagram.com/p/B-5SbD4DV-J/',
      embedId: 'B-5SbD4DV-J',
      category: 'journal-spread',
      tags: ['traveljournal', 'customjournal', 'travelersnotebook', 'notebook', 'journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2020-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B-5SbD4DV-J/media/?size=m'
    },
    {
      id: 'ig-api-1180',
      platform: 'instagram',
      title: 'Keep Smiling 💙 Every day I\'m writing a few things I\'m grateful about & reaso...',
      description: 'Keep Smiling 💙 Every day I\'m writing a few things I\'m grateful about & reasons to keep smiling 😄 ⁠ 1. The weather is beautiful⁠ 2. There\'s so much nature to explore around my new house⁠ 3. Tomorrow ',
      url: 'https://www.instagram.com/p/B-sF4bqD9dg/',
      embedId: 'B-sF4bqD9dg',
      category: 'journal-spread',
      tags: ['keepsmiling', 'vandallettering', 'positivequotes', 'sidewalkquotes', 'chalklettering'],
      creator: 'CoraCreaCrafts',
      date: '2020-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B-sF4bqD9dg/media/?size=m'
    },
    {
      id: 'ig-api-1181',
      platform: 'instagram',
      title: 'Little Castle Drawing Tutorial 🏰 Check my Youtube Channel (Cora Crea) to see...',
      description: 'Little Castle Drawing Tutorial 🏰 Check my Youtube Channel (Cora Crea) to see a slower version - Have a beautiful and creative weekend everyone 💜⁠ #castledrawing #drawingtutorial #drawingvideo #castl',
      url: 'https://www.instagram.com/p/B-kpNx4jImr/',
      embedId: 'B-kpNx4jImr',
      category: 'tutorial',
      tags: ['castledrawing', 'drawingtutorial', 'drawingvideo', 'castle', 'europeancastle', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B-kpNx4jImr/media/?size=m'
    },
    {
      id: 'ig-api-1182',
      platform: 'instagram',
      title: 'Some thoughts for all the families that are away from each other at the momen...',
      description: 'Some thoughts for all the families that are away from each other at the moment 🧡👨‍👩‍👧‍👦 My family is 6000 miles away in Europe and I don\'t know when we will next be able to travel to see each oth',
      url: 'https://www.instagram.com/p/B-c9u_pnWBB/',
      embedId: 'B-c9u_pnWBB',
      category: 'tutorial',
      tags: ['familyquote', 'familytravel', 'family', 'quote', 'positivethoughts', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-04',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B-c9u_pnWBB/media/?size=m'
    },
    {
      id: 'ig-api-1183',
      platform: 'instagram',
      title: 'Wrapping up is the best part of the process 😋 Custom Travel Journals 📔⁠',
      description: 'Wrapping up is the best part of the process 😋 Custom Travel Journals 📔⁠ #customtraveljournal #etsyshop #etsyseller #etsyjournal #customjournal #handmadejournal #calligraphyjournal #wrappingup #packa',
      url: 'https://www.instagram.com/p/B-YIryoJb1j/',
      embedId: 'B-YIryoJb1j',
      category: 'behind-the-scenes',
      tags: ['customtraveljournal', 'etsyshop', 'etsyseller', 'etsyjournal', 'customjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B-YIryoJb1j/media/?size=m'
    },
    {
      id: 'ig-api-1184',
      platform: 'instagram',
      title: '⁠',
      description: '⁠ A for April 🖤 What\'s your favorite letter to write? A must be my favorite and K is my pet peeve !⁠ #aforapril #aprillettering #apriljournal #springjournal #coverpage #aprilcoverpage #aprilspread #j',
      url: 'https://www.instagram.com/p/B-K7YjngwQ5/',
      embedId: 'B-K7YjngwQ5',
      category: 'journal-spread',
      tags: ['aforapril', 'aprillettering', 'apriljournal', 'springjournal', 'coverpage', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B-K7YjngwQ5/media/?size=m'
    },
    {
      id: 'ig-api-1185',
      platform: 'instagram',
      title: 'A lot of us are sitting home and dreaming of the next places we want to visit...',
      description: 'A lot of us are sitting home and dreaming of the next places we want to visit, whether that\'s the dream destination across the world or our favorite restaurant around the corner 😅 Let\'s keep our hope',
      url: 'https://www.instagram.com/p/B9zionen4ra/',
      embedId: 'B9zionen4ra',
      category: 'behind-the-scenes',
      tags: ['coracreaprojecttravel', 'coracreatraveljournals', 'journaling', 'stickers'],
      creator: 'CoraCreaCrafts',
      date: '2020-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B9zionen4ra/media/?size=m'
    },
    {
      id: 'ig-api-1186',
      platform: 'instagram',
      title: 'Easy little pockets for your Journal 📘 For those, who like me, like to colle...',
      description: 'Easy little pockets for your Journal 📘 For those, who like me, like to collect all kinds of papers, tickets and random vintage things on their travels 🤩🖤 I\'m making this journal for a family who pl',
      url: 'https://www.instagram.com/p/B9uiqytp9Qk/',
      embedId: 'B9uiqytp9Qk',
      category: 'product-showcase',
      tags: ['coracreatraveljournals', 'journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2020-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B9uiqytp9Qk/media/?size=m'
    },
    {
      id: 'ig-api-1187',
      platform: 'instagram',
      title: 'Ireland 💚 Who here will celebrate St Patrick\'s Day next week? 🍀 Used this b...',
      description: 'Ireland 💚 Who here will celebrate St Patrick\'s Day next week? 🍀 Used this beautiful Lime watercolor from @brillanzacolours that was gifted to me by my dear friend @thelatenightletterer back in Londo',
      url: 'https://www.instagram.com/p/B9pRzYcHguX/',
      embedId: 'B9pRzYcHguX',
      category: 'product-showcase',
      tags: ['coracreaprojectlettering', 'coracreatraveljournals', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B9pRzYcHguX/media/?size=m'
    },
    {
      id: 'ig-api-1188',
      platform: 'instagram',
      title: 'Running a little travel journal factory 👩‍🏭 Decorating the first page of th...',
      description: 'Running a little travel journal factory 👩‍🏭 Decorating the first page of these 3 custom travel journals 🖤🌎 Who\'s ready to travel? ⁠ #traveljournal #travelersjournal #travelersnotebook #customjourn',
      url: 'https://www.instagram.com/p/B9VMEWkgZot/',
      embedId: 'B9VMEWkgZot',
      category: 'journal-spread',
      tags: ['traveljournal', 'travelersjournal', 'travelersnotebook', 'customjournal', 'customtraveljournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B9VMEWkgZot/media/?size=m'
    },
    {
      id: 'ig-api-1189',
      platform: 'instagram',
      title: 'With March comes Spring 🌺💮 watch the full video of the March spreads on my ...',
      description: 'With March comes Spring 🌺💮 watch the full video of the March spreads on my YouTube channel: Cora Crea or on Pinterest: Cora Crea Boutique 😊 This was part of a yearly reading planner I made  for an ',
      url: 'https://www.instagram.com/p/B9J9oWdH59l/',
      embedId: 'B9J9oWdH59l',
      category: 'journal-spread',
      tags: ['etsycustom', 'etsyshop', 'etsyseller', 'etsyjournals', 'customjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B9J9oWdH59l/media/?size=m'
    },
    {
      id: 'ig-api-1190',
      platform: 'instagram',
      title: 'Can\'t believe it is the end of February and I\'ve already been in this new cou...',
      description: 'Can\'t believe it is the end of February and I\'ve already been in this new country for 2 weeks and a half ! 🇺🇸 I\'m really enjoying it so far 👍 Also I can now go to all the big craft stores everyone ',
      url: 'https://www.instagram.com/p/B9HcS8pn7NJ/',
      embedId: 'B9HcS8pn7NJ',
      category: 'journal-spread',
      tags: ['februaryjournal', 'februaryspread', 'februarycrafts', 'endofmonthjournal', 'journaladdict', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B9HcS8pn7NJ/media/?size=m'
    },
    {
      id: 'ig-api-1191',
      platform: 'instagram',
      title: 'Instagram Video 1191',
      description: '',
      url: 'https://www.instagram.com/tv/B9AC1Q-H-iP/',
      embedId: '',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2020-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-1192',
      platform: 'instagram',
      title: 'What do you do when it\'s cold outside? ❄️ Crafting, journaling, lettering 🖌️...',
      description: 'What do you do when it\'s cold outside? ❄️ Crafting, journaling, lettering 🖌️ You\'ve just got to love the freezing Chicago temperatures 🥶 Third week of February and my "magic theme" took a very medie',
      url: 'https://www.instagram.com/p/B8say7jH5u7/',
      embedId: 'B8say7jH5u7',
      category: 'product-showcase',
      tags: ['chicagolettering', 'chicagoletterers', 'wintercrafts', 'winterlettering', 'winterjournaling', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B8say7jH5u7/media/?size=m'
    },
    {
      id: 'ig-api-1193',
      platform: 'instagram',
      title: 'First post in my new city: Chicago ✈️ the first week of February is well over...',
      description: 'First post in my new city: Chicago ✈️ the first week of February is well over but I\'ve only managed to post this now 😅 I\'ve been very busy with packing and preparing for my big departure to Chicago. ',
      url: 'https://www.instagram.com/p/B8hU4WPnOAh/',
      embedId: 'B8hU4WPnOAh',
      category: 'journal-spread',
      tags: ['weeklyspreadvideo', 'weeklybujo', 'februaryjournal', 'februaryspread', 'movingtoamerica', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B8hU4WPnOAh/media/?size=m'
    },
    {
      id: 'ig-api-1194',
      platform: 'instagram',
      title: 'A couple recent additions to my shop for washi tape lovers ♥️ which one is yo...',
      description: 'A couple recent additions to my shop for washi tape lovers ♥️ which one is your favorite? Comment 1,2,3 or 4 !  #washishop #washitape #washitapeaddict #washitapeshop #etsyshop #etsymail #etsyuk #etsyg',
      url: 'https://www.instagram.com/p/B8HXBSAncW_/',
      embedId: 'B8HXBSAncW_',
      category: 'product-showcase',
      tags: ['washishop', 'washitape', 'washitapeaddict', 'washitapeshop', 'etsyshop', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2020-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B8HXBSAncW_/media/?size=m'
    },
    {
      id: 'ig-api-1195',
      platform: 'instagram',
      title: 'February will be MAGIC 🍃✨This month I\'m moving to the other end of the world...',
      description: 'February will be MAGIC 🍃✨This month I\'m moving to the other end of the world and starting a new life ! While this is very scary, I don\'t doubt that this is what I\'m meant to do and I trust in the wor',
      url: 'https://www.instagram.com/p/B8BykU5HSco/',
      embedId: 'B8BykU5HSco',
      category: 'journal-spread',
      tags: ['februaryjournal', 'februaryspread', 'newmonth', 'monthlycoverpage', 'magicjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B8BykU5HSco/media/?size=m'
    },
    {
      id: 'ig-api-1196',
      platform: 'instagram',
      title: 'Celestial January Flip Through 🔭🌑 I enjoyed starting my journal and creatin...',
      description: 'Celestial January Flip Through 🔭🌑 I enjoyed starting my journal and creating beginning of the year goals around this theme 🌓 Tomorrow is the beginning of another month, I\'m ready for this new start',
      url: 'https://www.instagram.com/p/B7_mq6-HDgs/',
      embedId: 'B7_mq6-HDgs',
      category: 'journal-spread',
      tags: ['endofmonth', 'endofmonthflipthrough', 'flipthroughvideo', 'januarycover', 'januaryflipthrough', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B7_mq6-HDgs/media/?size=m'
    },
    {
      id: 'ig-api-1197',
      platform: 'instagram',
      title: 'In a few days it will be February 🙀 time flies ⏱️ tick tock - 2 weeks and 2 ...',
      description: 'In a few days it will be February 🙀 time flies ⏱️ tick tock - 2 weeks and 2 days before my big departure ! This is part of the reading journal I\'m making for the lovely @skh_lettering 🖤  #readingjou',
      url: 'https://www.instagram.com/p/B71SkWinHNo/',
      embedId: 'B71SkWinHNo',
      category: 'journal-spread',
      tags: ['readingjournal', 'readingbujo', 'februaryspread', 'februarybujo', 'february2020', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B71SkWinHNo/media/?size=m'
    },
    {
      id: 'ig-api-1198',
      platform: 'instagram',
      title: 'Thursday Night Crafty Night 🖤 Tonight I\'m showing you how to make a full yea...',
      description: 'Thursday Night Crafty Night 🖤 Tonight I\'m showing you how to make a full year overview in your journal 🎥 These 2 pages are SO useful to me, I use them to highlight my holiday plans, travels, and big',
      url: 'https://www.instagram.com/p/B7q-JEzn7zg/',
      embedId: 'B7q-JEzn7zg',
      category: 'journal-spread',
      tags: ['fullyearbujo', 'fullyearjournal', 'fullyearspread', 'yearlyspread', 'yearoverview', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B7q-JEzn7zg/media/?size=m'
    },
    {
      id: 'ig-api-1199',
      platform: 'instagram',
      title: 'Book Shelf 📚 for the piles of books to be read this year ! Do you have a 202...',
      description: 'Book Shelf 📚 for the piles of books to be read this year ! Do you have a 2020 reading list? Any good recommendations? 😊📗Slower video available on my YouTube channel 🎥 #bookshelf #tbrpile #tbrshelf',
      url: 'https://www.instagram.com/p/B7jQ-2anAF2/',
      embedId: 'B7jQ-2anAF2',
      category: 'journal-spread',
      tags: ['bookshelf', 'tbrpile', 'tbrshelf', 'tbrlist', 'tbrspread', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B7jQ-2anAF2/media/?size=m'
    },
    {
      id: 'ig-api-1200',
      platform: 'instagram',
      title: 'Working on a new custom journal 🌺 📖 This journal will be all about reading:...',
      description: 'Working on a new custom journal 🌺 📖 This journal will be all about reading: reading trackers, to be read shelf, series to read, list of books read each month, book reviews 📚 Who here is also a book',
      url: 'https://www.instagram.com/p/B7dQ6qOnJ1j/',
      embedId: 'B7dQ6qOnJ1j',
      category: 'product-showcase',
      tags: ['readingjournal', 'bookjournal', 'flyleafdeco', 'bookworm', 'readingtracker', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B7dQ6qOnJ1j/media/?size=m'
    },
    {
      id: 'ig-api-1201',
      platform: 'instagram',
      title: 'The second week of January is already gone 😱 it\'s not only one month until m...',
      description: 'The second week of January is already gone 😱 it\'s not only one month until my big move to the US 🧳🙈 Here is last week\'s journal spread 😊 #journalinspiration #journalingvideo #journalingvideo #bujo',
      url: 'https://www.instagram.com/p/B7RSbXRnNn5/',
      embedId: 'B7RSbXRnNn5',
      category: 'journal-spread',
      tags: ['journalinspiration', 'journalingvideo', 'journalingvideo', 'bujolettering', 'bulletjournalvideo', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B7RSbXRnNn5/media/?size=m'
    },
    {
      id: 'ig-api-1202',
      platform: 'instagram',
      title: 'Back to my little Etsy orders envelopes with a new design 💌 After the Christ...',
      description: 'Back to my little Etsy orders envelopes with a new design 💌 After the Christmas edition I wanted something fresh for the New Year so I decided to go for florals 🌺🌷 I\'m using *pink and red Tombow du',
      url: 'https://www.instagram.com/p/B7G-Nz4naYf/',
      embedId: 'B7G-Nz4naYf',
      category: 'product-showcase',
      tags: ['etsyorders', 'etsyshop', 'etsymail', 'etsyuk', 'snailmail', 'christmas'],
      creator: 'CoraCreaCrafts',
      date: '2020-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B7G-Nz4naYf/media/?size=m'
    },
    {
      id: 'ig-api-1203',
      platform: 'instagram',
      title: 'January Planning 📖  video following my celestial theme ✨🌘 I did some mistak...',
      description: 'January Planning 📖 #weeklyspread video following my celestial theme ✨🌘 I did some mistake solving too: the watercolors bled through the masking tape on the right - I decided to leave it as is and cr',
      url: 'https://www.instagram.com/p/B7Bu3SInuYs/',
      embedId: 'B7Bu3SInuYs',
      category: 'journal-spread',
      tags: ['weeklyspread', 'januaryjournal', 'weeklyspreadvideo', 'januarytheme', 'bujojanuary', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B7Bu3SInuYs/media/?size=m'
    },
    {
      id: 'ig-api-1204',
      platform: 'instagram',
      title: 'Finishing up my January cover page 🖤✨ This month my theme will be all about ...',
      description: 'Finishing up my January cover page 🖤✨ This month my theme will be all about the universe with creative celestial bodies & galaxies 🌘🔭 #januaryjournal #januarycover #januarycoverpage #galaxydrawing ',
      url: 'https://www.instagram.com/p/B60tLJ6Hzbs/',
      embedId: 'B60tLJ6Hzbs',
      category: 'product-showcase',
      tags: ['januaryjournal', 'januarycover', 'januarycoverpage', 'galaxydrawing', 'galaxytheme', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B60tLJ6Hzbs/media/?size=m'
    },
    {
      id: 'ig-api-1205',
      platform: 'instagram',
      title: '2020 🖤 Starting this new decade with a flip through video of a special custo...',
      description: '2020 🖤 Starting this new decade with a flip through video of a special custom made 2020 planner 📖 I wish you all to start the new year like this empty planner, full of possibilities, with each page ',
      url: 'https://www.instagram.com/p/B6xXiskn30C/',
      embedId: 'B6xXiskn30C',
      category: 'journal-spread',
      tags: ['startingfresh', 'emptyplanner', 'newplanner', 'newyearnewjournal', 'newyearplanning', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-01',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B6xXiskn30C/media/?size=m'
    },
    {
      id: 'ig-api-1206',
      platform: 'instagram',
      title: 'Celestial January 🌌 Cover page using dark blue watercolors and a golden @uni...',
      description: 'Celestial January 🌌 Cover page using dark blue watercolors and a golden @uniball_uk pen in my @scribblesthatmatter B6 journal 📖 This cover page was inspired by the amazing work of @hannahmariaplans ',
      url: 'https://www.instagram.com/p/B6qWliKHau3/',
      embedId: 'B6qWliKHau3',
      category: 'journal-spread',
      tags: ['celestialtheme', 'galaxyjanuary', 'celestial', 'galaxydrawing', 'galaxypainting', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B6qWliKHau3/media/?size=m'
    },
    {
      id: 'ig-api-1207',
      platform: 'instagram',
      title: 'The beginning of a new decade is about to start 😲🎇 this prompted some caref...',
      description: 'The beginning of a new decade is about to start 😲🎇 this prompted some careful thinking about what I want to change and improve in my life in the future 🧘  in order to do that properly I followed th',
      url: 'https://www.instagram.com/p/B6keZ_inPU-/',
      embedId: 'B6keZ_inPU-',
      category: 'journal-spread',
      tags: ['newyearplanning', 'newyearresolutions', '2020planner', '2020journal', '2020journalselection', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B6keZ_inPU-/media/?size=m'
    },
    {
      id: 'ig-api-1208',
      platform: 'instagram',
      title: 'Merry Christmas 🎄🎁 Did you also make some handmade gifts this year? This is...',
      description: 'Merry Christmas 🎄🎁 Did you also make some handmade gifts this year? This is a little box fully decorated by hand and that I filled with several vouchers for activities and little goodies 🎀 I find p',
      url: 'https://www.instagram.com/p/B6fcSBcH_hs/',
      embedId: 'B6fcSBcH_hs',
      category: 'box-reveal',
      tags: ['merrychristmascard', 'merrychristmasgift', 'christmasgift', 'handmadegift', 'handcraftedgift', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2019-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B6fcSBcH_hs/media/?size=m'
    },
    {
      id: 'ig-api-1209',
      platform: 'instagram',
      title: 'Last orders before Holidays 😁🎄🎀 I decorated these craft paper envelopes wi...',
      description: 'Last orders before Holidays 😁🎄🎀 I decorated these craft paper envelopes with some Christmas tree doodles, washi tapes and shiny lettering ✍️ Now I\'m off to Switzerland for the next two weeks 🏔️🇨',
      url: 'https://www.instagram.com/p/B6bHRXSnjDM/',
      embedId: 'B6bHRXSnjDM',
      category: 'product-showcase',
      tags: ['envelopecalligraphy', 'envelopemaking', 'chrustmasdrawing', 'christmassupplies', 'christmasjournaling', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2019-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B6bHRXSnjDM/media/?size=m'
    },
    {
      id: 'ig-api-1210',
      platform: 'instagram',
      title: 'Who\'s already looking ahead to the New Year? ✨✍️ I just couldn\'t wait to star...',
      description: 'Who\'s already looking ahead to the New Year? ✨✍️ I just couldn\'t wait to start my new 2020 journal and decorated the very first page using some @tombow_uk Dual Brush Pens and a water brush pen to dilu',
      url: 'https://www.instagram.com/p/B6Vr5fIno2t/',
      embedId: 'B6Vr5fIno2t',
      category: 'product-showcase',
      tags: ['newyearjournal', 'newyearplanner', 'newyearjournaling', 'journalinspiration', 'journalingvideo', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B6Vr5fIno2t/media/?size=m'
    },
    {
      id: 'ig-api-1211',
      platform: 'instagram',
      title: 'Last minute with Christmas cards prep?✨ Here\'s a quick and easy way of making...',
      description: 'Last minute with Christmas cards prep?✨ Here\'s a quick and easy way of making some simple and natural end of the year cards for all last minute people like me 🙈🌲✨ I used: * @sizzix sidekick machine ',
      url: 'https://www.instagram.com/p/B6TaHDanYNq/',
      embedId: 'B6TaHDanYNq',
      category: 'product-showcase',
      tags: ['christmasjournaling', 'christmassupplies', 'christmascards', 'easyxmascards', 'dyichristmas', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B6TaHDanYNq/media/?size=m'
    },
    {
      id: 'ig-api-1212',
      platform: 'instagram',
      title: 'Do you also make lots of Christmas list to not forget anyone or anything? 🙈...',
      description: 'Do you also make lots of Christmas list to not forget anyone or anything? 🙈🎁 I definitely need lists for everything that I do - my journal is the second part of my brain 😁 #christmasjournaling #chr',
      url: 'https://www.instagram.com/p/B6P2f1Ono2P/',
      embedId: 'B6P2f1Ono2P',
      category: 'journal-spread',
      tags: ['christmasjournaling', 'christmassupplies', 'christmasjournal', 'christmascardlist', 'chrustmasgiftlist', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2019-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B6P2f1Ono2P/media/?size=m'
    },
    {
      id: 'ig-api-1213',
      platform: 'instagram',
      title: 'Another batch of the Christmas Envelopes is on its way 💌🎀 These must be my ...',
      description: 'Another batch of the Christmas Envelopes is on its way 💌🎀 These must be my favorite envelopes of the year, easy doodles for great results 😀 Are you decorating/using decorated envelopes or do you li',
      url: 'https://www.instagram.com/p/B6KoE9qnO2L/',
      embedId: 'B6KoE9qnO2L',
      category: 'product-showcase',
      tags: ['christmasenvelopes', 'christmasdeco', 'christmasembellishments', 'envelopecalligraphy', 'envelopemaking', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B6KoE9qnO2L/media/?size=m'
    },
    {
      id: 'ig-api-1214',
      platform: 'instagram',
      title: 'In preparation for the new year ahead I\'ve ordered this new journal and gave ...',
      description: 'In preparation for the new year ahead I\'ve ordered this new journal and gave it the ultimate pen, ink & watercolor test ✍️🎥 I\'m really happy with both the format and the quality of the paper and defi',
      url: 'https://www.instagram.com/tv/B6IcDf5nyZk/',
      embedId: '',
      category: 'product-showcase',
      tags: ['scribblesthatmatter', 'scribblesjournal', 'journalreview', '2020planner', '2020journalselection', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-1215',
      platform: 'instagram',
      title: 'Celebrating the most festive month of the year 🎄✨ with this December journal...',
      description: 'Celebrating the most festive month of the year 🎄✨ with this December journal spread. Definitely one of my favorite times of the year !  #christmasgift #christmaswashi  #christmascrafts #christmasjour',
      url: 'https://www.instagram.com/p/B6Bbo4BnUmo/',
      embedId: 'B6Bbo4BnUmo',
      category: 'journal-spread',
      tags: ['christmasgift', 'christmaswashi', 'christmascrafts', 'christmasjournal', 'christmasjournaling', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2019-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B6Bbo4BnUmo/media/?size=m'
    },
    {
      id: 'ig-api-1216',
      platform: 'instagram',
      title: 'Off season post 🦋 this is an extract of a custom 2020 planner order that wil...',
      description: 'Off season post 🦋 this is an extract of a custom 2020 planner order that will be delivered just before Christmas 🎀 After making this journal I\'m feeling ready to dive into the year !  I used: * @mol',
      url: 'https://www.instagram.com/p/B58THFinwUY/',
      embedId: 'B58THFinwUY',
      category: 'product-showcase',
      tags: ['2020planner', 'newyearplanner', '2020vision', '2020journal', '2020bujo', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2019-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B58THFinwUY/media/?size=m'
    },
    {
      id: 'ig-api-1217',
      platform: 'instagram',
      title: 'Little Tag Factory 🏷️🎄 Created some more of these fun Christmas tags for my...',
      description: 'Little Tag Factory 🏷️🎄 Created some more of these fun Christmas tags for my Etsy orders 💌  To make those tags I used: ✨ Sizzix Sidekick machine & tag thinlit @sizzix ✨ Craft Card paper (200gsm) ✨ V',
      url: 'https://www.instagram.com/p/B55syzoH1Ym/',
      embedId: 'B55syzoH1Ym',
      category: 'product-showcase',
      tags: ['diecut', 'sizzix', 'tagdiecut', 'thinlits', 'christmasembellishments', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B55syzoH1Ym/media/?size=m'
    },
    {
      id: 'ig-api-1218',
      platform: 'instagram',
      title: 'Envelopes Winter Edition 💌🌲 for my Etsy orders ✨ Loved to use this tree woo...',
      description: 'Envelopes Winter Edition 💌🌲 for my Etsy orders ✨ Loved to use this tree wooden stamp with different inks & pressure to create the illusion of a deep forest 🌲🌲 #christmasdoodles #christmasstamps #c',
      url: 'https://www.instagram.com/p/B5xnVHgHbON/',
      embedId: 'B5xnVHgHbON',
      category: 'product-showcase',
      tags: ['christmasdoodles', 'christmasstamps', 'christmasenvelopes', 'winterforest', 'winterenvelopes', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B5xnVHgHbON/media/?size=m'
    },
    {
      id: 'ig-api-1219',
      platform: 'instagram',
      title: 'Doodling through the first week of December 🎁✍️ Finally managed to create a ...',
      description: 'Doodling through the first week of December 🎁✍️ Finally managed to create a December cover page in my journal using: * brown craft paper washi tape * @tombow_uk black, red and grey dual brush pens  #',
      url: 'https://www.instagram.com/p/B5vUApnnrsO/',
      embedId: 'B5vUApnnrsO',
      category: 'product-showcase',
      tags: ['christmasdoodles', 'champagnedoodle', 'christmasdeco', 'christmasgift', 'christmasembellishments', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2019-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B5vUApnnrsO/media/?size=m'
    },
    {
      id: 'ig-api-1220',
      platform: 'instagram',
      title: 'December is here ✨ A season for excitement, especially when it comes to craft...',
      description: 'December is here ✨ A season for excitement, especially when it comes to crafting 🎄🎁 Here is a simple way to decorate your Christmas envelopes or cards 💌 These are now also available for order on Et',
      url: 'https://www.instagram.com/p/B5lH4MUnE4Q/',
      embedId: 'B5lH4MUnE4Q',
      category: 'product-showcase',
      tags: ['christmasenvelopes', 'christmasdeco', 'christmasembellishments', 'envelopecalligraphy', 'envelopeart', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-12',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B5lH4MUnE4Q/media/?size=m'
    },
    {
      id: 'ig-api-1221',
      platform: 'instagram',
      title: 'Christmas Tags On SALE on Etsy🏷️🌲 Here\'s an easy and quick way of making so...',
      description: 'Christmas Tags On SALE on Etsy🏷️🌲 Here\'s an easy and quick way of making some nice tags - because everyone\'s busy before Christmas right? I made those tags as part of my Advent Calendars, really lov',
      url: 'https://www.instagram.com/p/B5a1TVan3sI/',
      embedId: 'B5a1TVan3sI',
      category: 'product-showcase',
      tags: ['diecut', 'sizzix', 'tagdiecut', 'christmasembellishments', 'christmastags', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B5a1TVan3sI/media/?size=m'
    },
    {
      id: 'ig-api-1222',
      platform: 'instagram',
      title: 'November is almost over 🌲 Another very simple and fast design for my bullet ...',
      description: 'November is almost over 🌲 Another very simple and fast design for my bullet journal weekly spread, using beautiful misty forest & sky stickers, my dymo home embossing tool for the days, and a very li',
      url: 'https://www.instagram.com/p/B5R_HxXn89R/',
      embedId: 'B5R_HxXn89R',
      category: 'journal-spread',
      tags: ['novemberplanner', 'novemberbujo', 'novemberbujovideo', 'novemberdarksky', 'novembernight', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B5R_HxXn89R/media/?size=m'
    },
    {
      id: 'ig-api-1223',
      platform: 'instagram',
      title: 'Travel stickers pack in the making ✈️🌍 I always have so much fun making thos...',
      description: 'Travel stickers pack in the making ✈️🌍 I always have so much fun making those 🥰 #etsygifts #etsyuk #etsyshop #etsyorders #etsystickershop #stickershop #stickeraddict #travelstickers #travelsupplies ',
      url: 'https://www.instagram.com/p/B5HyOSNnZB5/',
      embedId: 'B5HyOSNnZB5',
      category: 'behind-the-scenes',
      tags: ['etsygifts', 'etsyuk', 'etsyshop', 'etsyorders', 'etsystickershop', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B5HyOSNnZB5/media/?size=m'
    },
    {
      id: 'ig-api-1224',
      platform: 'instagram',
      title: 'November Week 47 💫🔭 Another  of my "Night Sky" theme 🌌 Another way of usin...',
      description: 'November Week 47 💫🔭 Another #weeklyspreadvideo of my "Night Sky" theme 🌌 Another way of using distress ink and stamps to create a fun background. Also used the transparent Moon washi tape available',
      url: 'https://www.instagram.com/p/B5BB-aeHT6Z/',
      embedId: 'B5BB-aeHT6Z',
      category: 'journal-spread',
      tags: ['weeklyspreadvideo', 'weeklyspread', 'novemberplanner', 'novemberbujo', 'novemberbujovideo', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2019-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B5BB-aeHT6Z/media/?size=m'
    },
    {
      id: 'ig-api-1225',
      platform: 'instagram',
      title: 'Loved packaging all these orders including some washi tapes and Christmas sti...',
      description: 'Loved packaging all these orders including some washi tapes and Christmas stickers ✨🎄🎁 Feeling so ready for end of the year festivities !  #packaging #packingvideo #packagingvideo #etsyorders #etsys',
      url: 'https://www.instagram.com/p/B40MWzanWqF/',
      embedId: 'B40MWzanWqF',
      category: 'behind-the-scenes',
      tags: ['packaging', 'packingvideo', 'packagingvideo', 'etsyorders', 'etsyshop', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2019-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B40MWzanWqF/media/?size=m'
    },
    {
      id: 'ig-api-1226',
      platform: 'instagram',
      title: 'Second Week of November 🌲 showing you how I\'m making my "November Skies" wee...',
      description: 'Second Week of November 🌲 showing you how I\'m making my "November Skies" weekly spread 🎥 using distress ink and pine tree stamps 💙 #nightskytheme #novemberplanner #novemberbujo #novemberdarksky #no',
      url: 'https://www.instagram.com/p/B4vBCKSng2i/',
      embedId: 'B4vBCKSng2i',
      category: 'journal-spread',
      tags: ['nightskytheme', 'novemberplanner', 'novemberbujo', 'novemberdarksky', 'novembernight', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B4vBCKSng2i/media/?size=m'
    },
    {
      id: 'ig-api-1227',
      platform: 'instagram',
      title: 'Hello November 🌲🌌 For those who were wondering what my theme is in the poll...',
      description: 'Hello November 🌲🌌 For those who were wondering what my theme is in the poll yesterday, here it is: November Night Skies (foggy sky in this video!) - for those who voted for winter forest, I admit th',
      url: 'https://www.instagram.com/p/B4UPLjtnvGY/',
      embedId: 'B4UPLjtnvGY',
      category: 'product-showcase',
      tags: ['coracreaprojectbujo', 'coracrea', 'coverpage', 'coverpagedesign', 'coverpagevideo', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-11',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B4UPLjtnvGY/media/?size=m'
    },
    {
      id: 'ig-api-1228',
      platform: 'instagram',
      title: 'Happy Halloween 🕸️ and happy end of October 🍂 this month went too fast ! He...',
      description: 'Happy Halloween 🕸️ and happy end of October 🍂 this month went too fast ! Here is a flip through of my journal for the past month. I\'m realizing that there wasn\'t a great consistency between my weekl',
      url: 'https://www.instagram.com/p/B4Srv9DH5nf/',
      embedId: 'B4Srv9DH5nf',
      category: 'journal-spread',
      tags: ['weeklyspread', 'weeklyplanner', 'octoberweekly', 'octoberspread', 'octoberplanner', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B4Srv9DH5nf/media/?size=m'
    },
    {
      id: 'ig-api-1229',
      platform: 'instagram',
      title: 'What a Corker 🍾 for the  This time my definition is not very original but I ...',
      description: 'What a Corker 🍾 for the #oldschoolenglishchallenge This time my definition is not very original but I still don\'t think it\'s right 😂 Cork makes me think wine & champagne so what a Corker is an expre',
      url: 'https://www.instagram.com/p/B4Nih3LHs7H/',
      embedId: 'B4Nih3LHs7H',
      category: 'product-showcase',
      tags: ['oldschoolenglishchallenge', 'letteringchallenges', 'letteringfriendship', 'letterchallenge', 'craftingchallenge', 'christmas'],
      creator: 'CoraCreaCrafts',
      date: '2019-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B4Nih3LHs7H/media/?size=m'
    },
    {
      id: 'ig-api-1230',
      platform: 'instagram',
      title: 'My last  for October is obviously on the theme of Halloween 🎃🕸️🕷️Who\'s goi...',
      description: 'My last #weeklyspread for October is obviously on the theme of Halloween 🎃🕸️🕷️Who\'s going to do something special on Thursday night? #weeklyplanner #weeklyplanning #halloweenspread #halloweenweek #',
      url: 'https://www.instagram.com/p/B4J255HnGr1/',
      embedId: 'B4J255HnGr1',
      category: 'journal-spread',
      tags: ['weeklyspread', 'weeklyplanner', 'weeklyplanning', 'halloweenspread', 'halloweenweek', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B4J255HnGr1/media/?size=m'
    },
    {
      id: 'ig-api-1231',
      platform: 'instagram',
      title: 'Process video of this month\'s habit tracker 🍂 I\'ll share the filled tracker ...',
      description: 'Process video of this month\'s habit tracker 🍂 I\'ll share the filled tracker at the end of the month so you can get an idea how it looks like when it\'s not empty ! These are the 4 things I like to tra',
      url: 'https://www.instagram.com/p/B4COwXWHq7x/',
      embedId: 'B4COwXWHq7x',
      category: 'journal-spread',
      tags: ['habittracker', 'habittrackerspread', 'processvideo', 'bujovideo', 'bujosupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B4COwXWHq7x/media/?size=m'
    },
    {
      id: 'ig-api-1232',
      platform: 'instagram',
      title: 'Watch on IGTV to see text 🏴 For those who wished to see what I discovered in...',
      description: 'Watch on IGTV to see text 🏴 For those who wished to see what I discovered in the beautiful antique/vintage shop #armazemporto here is a short video of my findings 🖤🏷️🧾📙📜 #thriftshopping #vintage',
      url: 'https://www.instagram.com/tv/B3znYvwHJSI/',
      embedId: '',
      category: 'behind-the-scenes',
      tags: ['armazemporto', 'thriftshopping', 'vintageshop', 'antiqueshop', 'artshop', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-1233',
      platform: 'instagram',
      title: 'Sent some of those transparent washi tapes today ☺️🖋️ Inspired myself from @...',
      description: 'Sent some of those transparent washi tapes today ☺️🖋️ Inspired myself from @thehappyevercrafter to embellish my envelopes 💌 and got a little visitor at the end of the video 🐈😂 Still a few washis a',
      url: 'https://www.instagram.com/p/B3xFYHAHQhf/',
      embedId: 'B3xFYHAHQhf',
      category: 'behind-the-scenes',
      tags: ['embellishments', 'envelopeart', 'envelopeembellishment', 'happymail', 'etsymail', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2019-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B3xFYHAHQhf/media/?size=m'
    },
    {
      id: 'ig-api-1234',
      platform: 'instagram',
      title: 'Happy Monday 🖋️📙 No matter how difficult Mondays always are, it\'s nice to s...',
      description: 'Happy Monday 🖋️📙 No matter how difficult Mondays always are, it\'s nice to start a new page in my planner every week 🧡 For this page I used a lot of orange and green tones and an illustration of a M',
      url: 'https://www.instagram.com/p/B3lx8zuHyDI/',
      embedId: 'B3lx8zuHyDI',
      category: 'journal-spread',
      tags: ['vintagearttheme', 'vintagesupplies', 'vintageart', 'vintagejournal', 'vintagebulletjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B3lx8zuHyDI/media/?size=m'
    },
    {
      id: 'ig-api-1235',
      platform: 'instagram',
      title: 'Cuppa Builder\'s for the  @kitcalligraphy @thelondonscrivener 🖋️🖤 This time ...',
      description: 'Cuppa Builder\'s for the #oldschoolenglishchallenge @kitcalligraphy @thelondonscrivener 🖋️🖤 This time I\'m going for a less sophisticated definition: the Cuppa Builder\'s is the one who accumulates the',
      url: 'https://www.instagram.com/p/B3cj65hHaGJ/',
      embedId: 'B3cj65hHaGJ',
      category: 'product-showcase',
      tags: ['oldschoolenglishchallenge', 'oldschoolenglish', 'calligraphylove', 'calligraphycommunity', 'calligraphyvideo'],
      creator: 'CoraCreaCrafts',
      date: '2019-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B3cj65hHaGJ/media/?size=m'
    },
    {
      id: 'ig-api-1236',
      platform: 'instagram',
      title: 'Packaging video of my Illuminated Letter Stickers 🖤❤️🖤 My favorites ! Avail...',
      description: 'Packaging video of my Illuminated Letter Stickers 🖤❤️🖤 My favorites ! Available on Etsy. #illuminatedletters #illuminatedart #illuminatedstickers #stickershop #letterstickers #lettering #gothiclette',
      url: 'https://www.instagram.com/p/B3Z8pGiBp67/',
      embedId: 'B3Z8pGiBp67',
      category: 'behind-the-scenes',
      tags: ['illuminatedletters', 'illuminatedart', 'illuminatedstickers', 'stickershop', 'letterstickers', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B3Z8pGiBp67/media/?size=m'
    },
    {
      id: 'ig-api-1237',
      platform: 'instagram',
      title: 'October Spread Process Video 🍁',
      description: 'October Spread Process Video 🍁 My October planning video is now online 🎥 Check out the full version on Youtube with habit tracker and weekly spreads - link in bio ! #processvideo #youtubevideo #mont',
      url: 'https://www.instagram.com/p/B3NH7z2nBLI/',
      embedId: 'B3NH7z2nBLI',
      category: 'journal-spread',
      tags: ['processvideo', 'youtubevideo', 'monthlyplanning', 'octoberplanning', 'planningvideo', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-10',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B3NH7z2nBLI/media/?size=m'
    },
    {
      id: 'ig-api-1238',
      platform: 'instagram',
      title: 'Flip through & packing video of this custom journal prepared for a couple goi...',
      description: 'Flip through & packing video of this custom journal prepared for a couple going to New Zealand on their honeymoon 👰 How cool is that?  #vintagepaper #newzealandadventures #newzealandmap #newzealandtr',
      url: 'https://www.instagram.com/tv/B3Ct7jvnvFe/',
      embedId: '',
      category: 'journal-spread',
      tags: ['vintagepaper', 'newzealandadventures', 'newzealandmap', 'newzealandtrip', 'newzealandjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-1239',
      platform: 'instagram',
      title: 'Autumn is here 🍁 Finished packing these two orders of Fall doodle stickers ...',
      description: 'Autumn is here 🍁 Finished packing these two orders of Fall doodle stickers 🕸️ They\'re so fun to make and Autumn is my favorite season ! What\'s yours?  ____________________________________ #autumnsti',
      url: 'https://www.instagram.com/p/B2_lyfyHegJ/',
      embedId: 'B2_lyfyHegJ',
      category: 'behind-the-scenes',
      tags: ['autumnstickers', 'autumnvibes', 'fallstickers', 'stickeraddicts', 'stickershop', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B2_lyfyHegJ/media/?size=m'
    },
    {
      id: 'ig-api-1240',
      platform: 'instagram',
      title: 'Do you also need a packing list in your travel journal to not forget anything...',
      description: 'Do you also need a packing list in your travel journal to not forget anything? 🎒📜 I definitely do !  I also had a little visitor sneaking in on my desk 🐈😅 ____________________________________ #tra',
      url: 'https://www.instagram.com/p/B2qwzqbHWdC/',
      embedId: 'B2qwzqbHWdC',
      category: 'box-reveal',
      tags: ['traveljournal', 'travelersnotebooks', 'customgifts', 'customweddinggift', 'customhoneymoongift', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B2qwzqbHWdC/media/?size=m'
    },
    {
      id: 'ig-api-1241',
      platform: 'instagram',
      title: 'Some of  top places to see in New Zealand 🌏 For this spread I made a vintage...',
      description: 'Some of  top places to see in New Zealand 🌏 For this spread I made a vintage tag & handlettered each location ✒️ ------------------------------------------------------------ Custom made travel journa',
      url: 'https://www.instagram.com/p/B2duEamnMji/',
      embedId: 'B2duEamnMji',
      category: 'journal-spread',
      tags: ['weddingjournal', 'weddinggiftideas', 'honeymoonjournal', 'honeymoonmemories', 'weddinggifts', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B2duEamnMji/media/?size=m'
    },
    {
      id: 'ig-api-1242',
      platform: 'instagram',
      title: 'Is New Zealand a good destination for a honeymoon? 🌕 this journal is going t...',
      description: 'Is New Zealand a good destination for a honeymoon? 🌕 this journal is going to a special couple looking for a real adventure 🏇🌲⛵🏞️ ____________________________________ #customgifts #customweddinggi',
      url: 'https://www.instagram.com/p/B2Y2YQvHJRH/',
      embedId: 'B2Y2YQvHJRH',
      category: 'product-showcase',
      tags: ['customgifts', 'customweddinggift', 'customhoneymoongift', 'honeymoonjournal', 'weddingjournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B2Y2YQvHJRH/media/?size=m'
    },
    {
      id: 'ig-api-1243',
      platform: 'instagram',
      title: 'Starting a new travel journal that was ordered for a wedding gift 📜🖤 Thinki...',
      description: 'Starting a new travel journal that was ordered for a wedding gift 📜🖤 Thinking about how I will customize the content of the journal and shaping my thoughts into the first page is my favorite moment ',
      url: 'https://www.instagram.com/p/B2KAt5ln2pJ/',
      embedId: 'B2KAt5ln2pJ',
      category: 'journal-spread',
      tags: ['customjournal', 'weddingjournal', 'customweddingjournal', 'honeymoonjournal', 'customhoneymoongift', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B2KAt5ln2pJ/media/?size=m'
    },
    {
      id: 'ig-api-1244',
      platform: 'instagram',
      title: 'September begins 🦉🍂 and my theme is "Owls" - here is the process video for ...',
      description: 'September begins 🦉🍂 and my theme is "Owls" - here is the process video for my monthly cover page 🎥 Hope you like it 🖤 ____________________________________ #coverpage #coverpageinspiration #septemb',
      url: 'https://www.instagram.com/p/B14COaNHNQY/',
      embedId: 'B14COaNHNQY',
      category: 'product-showcase',
      tags: ['coverpage', 'coverpageinspiration', 'septembercoverpage', 'septembercover', 'septembertheme', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-09',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B14COaNHNQY/media/?size=m'
    },
    {
      id: 'ig-api-1245',
      platform: 'instagram',
      title: 'August is over 🙈📖 Flip through of the whole month - vintage theme 🎥 And fe...',
      description: 'August is over 🙈📖 Flip through of the whole month - vintage theme 🎥 And feeling ready for a new one 📜🕰️ #monthlyspread #monthlyvideo #monthlybujo #bujovideo #bujolettering #bujofavorites #monthly',
      url: 'https://www.instagram.com/p/B113KpNH_a9/',
      embedId: 'B113KpNH_a9',
      category: 'journal-spread',
      tags: ['monthlyspread', 'monthlyvideo', 'monthlybujo', 'bujovideo', 'bujolettering', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B113KpNH_a9/media/?size=m'
    },
    {
      id: 'ig-api-1246',
      platform: 'instagram',
      title: 'At one with Nature 🌱🌿 For this prompt of the  I wanted to do something diff...',
      description: 'At one with Nature 🌱🌿 For this prompt of the #makeityourwaychallenge I wanted to do something different ! I just went with the flow and created these two vintage style cards on the theme 💮 Nature i',
      url: 'https://www.instagram.com/p/B1Mb2VZFjA-/',
      embedId: 'B1Mb2VZFjA-',
      category: 'product-showcase',
      tags: ['makeityourwaychallenge', 'paperlover', 'paperaddict', 'handmadecards', 'vintagecards', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B1Mb2VZFjA-/media/?size=m'
    },
    {
      id: 'ig-api-1247',
      platform: 'instagram',
      title: 'A special post created for @theenchantedlairmagazine who kindly asked me to b...',
      description: 'A special post created for @theenchantedlairmagazine who kindly asked me to be part of their Autumn publication 😍 Feeling very grateful and excited to see it 🎉 The theme will be Magic 🎲 You can pre',
      url: 'https://www.instagram.com/p/B1JwqoanDvL/',
      embedId: 'B1JwqoanDvL',
      category: 'journal-spread',
      tags: ['enchantedlair', 'magicjournal', 'coloringbook', 'coloringforest', 'enchantedforest', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B1JwqoanDvL/media/?size=m'
    },
    {
      id: 'ig-api-1248',
      platform: 'instagram',
      title: 'Sundays are snail mail days 💌📬 So happy with these new vellum papers I made...',
      description: 'Sundays are snail mail days 💌📬 So happy with these new vellum papers I made 📜 Who also loves to send and receive happy mail?  ____________________________________ #snailmailessentials #snailmail #s',
      url: 'https://www.instagram.com/p/B1CB3e7H4BU/',
      embedId: 'B1CB3e7H4BU',
      category: 'behind-the-scenes',
      tags: ['snailmailessentials', 'snailmail', 'snailmailsupplies', 'happymail', 'happymailideas', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B1CB3e7H4BU/media/?size=m'
    },
    {
      id: 'ig-api-1249',
      platform: 'instagram',
      title: 'Packing a full alphabet of Illuminated Letter stickers from my Etsy shop 🖤 I...',
      description: 'Packing a full alphabet of Illuminated Letter stickers from my Etsy shop 🖤 Isn\'t packing the best part of the process? 📜📬 ____________________________________ #illuminations #illuminatedmanuscripts',
      url: 'https://www.instagram.com/p/B0-teV_HfVn/',
      embedId: 'B0-teV_HfVn',
      category: 'behind-the-scenes',
      tags: ['illuminations', 'illuminatedmanuscripts', 'illuminatedstickers', 'illuminatedletters', 'parchmentpaper', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B0-teV_HfVn/media/?size=m'
    },
    {
      id: 'ig-api-1250',
      platform: 'instagram',
      title: 'Good morning August 🌞 this is the process video of my new cover page🎥 I\'m n...',
      description: 'Good morning August 🌞 this is the process video of my new cover page🎥 I\'m not sure what my theme is or if I have one for the month but so far my pages look very vintage 🔑📖 ________________________',
      url: 'https://www.instagram.com/p/B0nTCTIHQTW/',
      embedId: 'B0nTCTIHQTW',
      category: 'journal-spread',
      tags: ['augustcreativity', 'augustcoverpage', 'augustspread', 'augustbujo', 'bujocoverpage', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-08',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B0nTCTIHQTW/media/?size=m'
    },
    {
      id: 'ig-api-1251',
      platform: 'instagram',
      title: 'July Flip Through 📖🌿 I really loved this theme ! I think my favorite pages ...',
      description: 'July Flip Through 📖🌿 I really loved this theme ! I think my favorite pages of the month were the cover pages and the first weekly spread 😊 ____________________________________ #julyflipthrough #buj',
      url: 'https://www.instagram.com/p/B0knt2qnFmX/',
      embedId: 'B0knt2qnFmX',
      category: 'journal-spread',
      tags: ['julyflipthrough', 'bujoflipthrough', 'bujovideo', 'flipthroughvideo', 'julyspread', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B0knt2qnFmX/media/?size=m'
    },
    {
      id: 'ig-api-1252',
      platform: 'instagram',
      title: 'Can you ever own too many? 📖🙈 I\'ve been wanting to make an index of all my ...',
      description: 'Can you ever own too many? 📖🙈 I\'ve been wanting to make an index of all my washi and finally had time to do it 😋 ____________________________________ #washi #washiindex #washilibrary #washitapes #w',
      url: 'https://www.instagram.com/p/B0RE6aVnWME/',
      embedId: 'B0RE6aVnWME',
      category: 'journal-spread',
      tags: ['washi', 'washiindex', 'washilibrary', 'washitapes', 'washitapelove', 'washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2019-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B0RE6aVnWME/media/?size=m'
    },
    {
      id: 'ig-api-1253',
      platform: 'instagram',
      title: 'Floral pack 🦋 full of cute supplies for crafting/scrapbooking/journaling 🌿...',
      description: 'Floral pack 🦋 full of cute supplies for crafting/scrapbooking/journaling 🌿📖 includes: 🌺 Floral tags & numbers (18) 🌺 Floral wooden stamp (1) 🌺 Large stickers (14) 🌺 Small stickers (24) now avai',
      url: 'https://www.instagram.com/p/B0Gus1UHQ_-/',
      embedId: 'B0Gus1UHQ_-',
      category: 'behind-the-scenes',
      tags: ['floraljournal', 'floralstickers', 'stickerspack', 'stickeraddicts', 'journalsupplies', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/B0Gus1UHQ_-/media/?size=m'
    },
    {
      id: 'ig-api-1254',
      platform: 'instagram',
      title: 'Process video of the gothic bday card I posted a few days ago 🎥 Really happy...',
      description: 'Process video of the gothic bday card I posted a few days ago 🎥 Really happy with the result ☺️ although ironically, my friend never received this card 😅 I used some of my illuminated letters sticke',
      url: 'https://www.instagram.com/p/Bz8d6pQHwRK/',
      embedId: 'Bz8d6pQHwRK',
      category: 'product-showcase',
      tags: ['letteringvideo', 'letteringgift', 'lettereveryday', 'letteringcommunity', 'gothiccalligraphy', 'gothic'],
      creator: 'CoraCreaCrafts',
      date: '2019-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Bz8d6pQHwRK/media/?size=m'
    },
    {
      id: 'ig-api-1255',
      platform: 'instagram',
      title: 'Bridesmaids gift wrapping 🎀 Process video of a special treat for a special f...',
      description: 'Bridesmaids gift wrapping 🎀 Process video of a special treat for a special friend 💕 @coach ____________________________________ #giftwrapping #giftdecorating #peocessvideo #letteringvideo #lettering',
      url: 'https://www.instagram.com/p/Bz2_M5kHDPq/',
      embedId: 'Bz2_M5kHDPq',
      category: 'behind-the-scenes',
      tags: ['giftwrapping', 'giftdecorating', 'peocessvideo', 'letteringvideo', 'letteringbirthdays'],
      creator: 'CoraCreaCrafts',
      date: '2019-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Bz2_M5kHDPq/media/?size=m'
    },
    {
      id: 'ig-api-1256',
      platform: 'instagram',
      title: 'Finally posting a full flip through of the last custom travel journal I made ...',
      description: 'Finally posting a full flip through of the last custom travel journal I made 🌏✈️ ______________________________________ #flipthroughvideo #customtraveljournal #traveljournal #customjournal #travelers',
      url: 'https://www.instagram.com/tv/Bzujn97nmAx/',
      embedId: '',
      category: 'journal-spread',
      tags: ['flipthroughvideo', 'customtraveljournal', 'traveljournal', 'customjournal', 'travelersnotebook', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p//media/?size=m'
    },
    {
      id: 'ig-api-1257',
      platform: 'instagram',
      title: 'Mailing small orders is the best 💌 sending some of the Illuminated letter st...',
      description: 'Mailing small orders is the best 💌 sending some of the Illuminated letter stickers & adding a fun "snail mail essentials" sticker pack 📮🐌 I hope you like it @claudiasebastianycalligraphy 🧡 _______',
      url: 'https://www.instagram.com/p/Bzhm4crnyYS/',
      embedId: 'Bzhm4crnyYS',
      category: 'behind-the-scenes',
      tags: ['stickeraddicts', 'stickershop', 'stickerpack', 'coracreaprojectstickers', 'snailmailessentials', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2019-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Bzhm4crnyYS/media/?size=m'
    },
    {
      id: 'ig-api-1258',
      platform: 'instagram',
      title: '▪️Enjoy The Little Things ▪️Process video of my "Vintage Botanics" motivation...',
      description: '▪️Enjoy The Little Things ▪️Process video of my "Vintage Botanics" motivational spread for July 🌿For this spread I used a lot of amazing floral stickers & stamps 🎟️ I\'m thinking of creating some "vi',
      url: 'https://www.instagram.com/p/BzcbBneHIM8/',
      embedId: 'BzcbBneHIM8',
      category: 'journal-spread',
      tags: ['vintagebotanicstyle', 'vintagebotanical', 'vintageflowers', 'vintageflowerstickers', 'stickeraddiction', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2019-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/BzcbBneHIM8/media/?size=m'
    },
    {
      id: 'ig-api-1259',
      platform: 'instagram',
      title: '1st of July 🔥🌞 Here is the process video for my cover page 📖 My goal this ...',
      description: '1st of July 🔥🌞 Here is the process video for my cover page 📖 My goal this month is to "Enjoy the Little Things" in life - to which I dedicated the next pages of my journal 🙌 to be shared later thi',
      url: 'https://www.instagram.com/p/BzXWuadHcYj/',
      embedId: 'BzXWuadHcYj',
      category: 'journal-spread',
      tags: ['processvideo', 'bujoprocess', 'bujovideo', 'bujogoal', 'bujomotto', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-07',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/BzXWuadHcYj/media/?size=m'
    },
    {
      id: 'ig-api-1260',
      platform: 'instagram',
      title: 'The satisfaction when wrapping up a journal 📦😍',
      description: 'The satisfaction when wrapping up a journal 📦😍 #journalgoals #journaling #customleathergoods #customjournal #traveljournal #traveltracker #travellerstory #travellersnotebook #customgifts #bujoletter',
      url: 'https://www.instagram.com/p/By0WS5dHR69/',
      embedId: 'By0WS5dHR69',
      category: 'journal-spread',
      tags: ['journalgoals', 'journaling', 'customleathergoods', 'customjournal', 'traveljournal'],
      creator: 'CoraCreaCrafts',
      date: '2019-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/By0WS5dHR69/media/?size=m'
    },
    {
      id: 'ig-api-1261',
      platform: 'instagram',
      title: '"The gladdest moment in human life is a departure into unknown lands" 🏷️',
      description: '"The gladdest moment in human life is a departure into unknown lands" 🏷️ Sharing the process video for the first page of this custom travel journal 🖋️🌏 although it was the very last page I made 🙈',
      url: 'https://www.instagram.com/p/ByqVJdFHWd7/',
      embedId: 'ByqVJdFHWd7',
      category: 'product-showcase',
      tags: ['customleathergoods', 'customnotebook', 'customcalligraphy', 'customjournal', 'customtraveljournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/ByqVJdFHWd7/media/?size=m'
    },
    {
      id: 'ig-api-1262',
      platform: 'instagram',
      title: 'Process Video of a World Map I made this weekend for a travel journal 🌏🖋️ 1...',
      description: 'Process Video of a World Map I made this weekend for a travel journal 🌏🖋️ 1h of work in 1 minute 🙈 Happy Monday everyone _____________________ #worldmap #handmademap #vintagemap #custommap #travelm',
      url: 'https://www.instagram.com/p/ByhZzsbHXB1/',
      embedId: 'ByhZzsbHXB1',
      category: 'product-showcase',
      tags: ['worldmap', 'handmademap', 'vintagemap', 'custommap', 'travelmap', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-06',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/ByhZzsbHXB1/media/?size=m'
    },
    {
      id: 'ig-api-1263',
      platform: 'instagram',
      title: 'Travel Journal Flip Through 📖 So happy with the result 💞- this order was a ...',
      description: 'Travel Journal Flip Through 📖 So happy with the result 💞- this order was a "memory" custom journal for a couple to fill in after their trip with pictures and memories of each place they visited 🌏 #',
      url: 'https://www.instagram.com/p/ByHqUHQHzwj/',
      embedId: 'ByHqUHQHzwj',
      category: 'product-showcase',
      tags: ['traveljournal', 'traveltracker', 'vietnamjournal', 'customjournal', 'customtraveljournal', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-05',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/ByHqUHQHzwj/media/?size=m'
    },
    {
      id: 'ig-api-1264',
      platform: 'instagram',
      title: 'We can never say it enough❣️ Personalised little love letter 💌',
      description: 'We can never say it enough❣️ Personalised little love letter 💌  #iloveyou #loveletter #littleloveletter #lettering #calligraphedletters #callihraphylover #calligraphysunday #waxseal #lovestamp #waxst',
      url: 'https://www.instagram.com/p/BujU_XdDeSE/',
      embedId: 'BujU_XdDeSE',
      category: 'product-showcase',
      tags: ['iloveyou', 'loveletter', 'littleloveletter', 'lettering', 'calligraphedletters'],
      creator: 'CoraCreaCrafts',
      date: '2019-03',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/BujU_XdDeSE/media/?size=m'
    },
    {
      id: 'ig-api-1265',
      platform: 'instagram',
      title: 'Prepared something special for my lovely neighbor 😍 birthday card on natural...',
      description: 'Prepared something special for my lovely neighbor 😍 birthday card on natural paper and vellum envelope 📜 #birthdaycard #happybirthdaycard #specialgift #vellum #vellumpaper #vellumenvelopes #homemade',
      url: 'https://www.instagram.com/p/Bt_EV4kjS1T/',
      embedId: 'Bt_EV4kjS1T',
      category: 'product-showcase',
      tags: ['birthdaycard', 'happybirthdaycard', 'specialgift', 'vellum', 'vellumpaper'],
      creator: 'CoraCreaCrafts',
      date: '2019-02',
      thumbnail: 'https://wsrv.nl/?url=www.instagram.com/p/Bt_EV4kjS1T/media/?size=m'
    },

    // === YouTube Videos (537 entries) ===
    {
      id: 'yt-api-1',
      platform: 'youtube',
      title: 'Masquerade Box Long Preview - Treasure Box Oct/Nov 2026 - Junk Journal & Crafting Box',
      description: '',
      url: 'https://www.youtube.com/watch?v=2epLrDaYHHU',
      embedId: '2epLrDaYHHU',
      category: 'box-reveal',
      tags: ['journaling', 'junk journal'],
      creator: 'CoraCreaCrafts',
      date: '2026-10',
      thumbnail: 'https://img.youtube.com/vi/2epLrDaYHHU/maxresdefault.jpg'
    },
    {
      id: 'yt-api-2',
      platform: 'youtube',
      title: 'Candlelit Remembrance Long Preview - Vintage Craft Box October 2026 - Junk Journal & Crafting Box',
      description: '',
      url: 'https://www.youtube.com/watch?v=buQO1-Ls1Xw',
      embedId: 'buQO1-Ls1Xw',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'junk journal'],
      creator: 'CoraCreaCrafts',
      date: '2026-10',
      thumbnail: 'https://img.youtube.com/vi/buQO1-Ls1Xw/maxresdefault.jpg'
    },
    {
      id: 'yt-api-3',
      platform: 'youtube',
      title: 'Autumn Magic Box Long Preview - Curiosities Box Sept/Oct 2026 - Junk Journal & Crafting Box',
      description: '',
      url: 'https://www.youtube.com/watch?v=yF4JBxrYomQ',
      embedId: 'yF4JBxrYomQ',
      category: 'box-reveal',
      tags: ['journaling', 'autumn', 'junk journal'],
      creator: 'CoraCreaCrafts',
      date: '2026-09',
      thumbnail: 'https://img.youtube.com/vi/yF4JBxrYomQ/maxresdefault.jpg'
    },
    {
      id: 'yt-api-4',
      platform: 'youtube',
      title: 'Witches & Vampires Box Long Preview - Vintage Craft Box September 2026 - Junk Journal & Crafting Box',
      description: '',
      url: 'https://www.youtube.com/watch?v=sakNEjwqIE0',
      embedId: 'sakNEjwqIE0',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'junk journal'],
      creator: 'CoraCreaCrafts',
      date: '2026-09',
      thumbnail: 'https://img.youtube.com/vi/sakNEjwqIE0/maxresdefault.jpg'
    },
    {
      id: 'yt-api-5',
      platform: 'youtube',
      title: 'Sherlock Mystery Box Long Preview - Treasure Box Aug/Sept 2026 - Junk Journal & Crafting Box',
      description: '',
      url: 'https://www.youtube.com/watch?v=LtwnsP6Ku8U',
      embedId: 'LtwnsP6Ku8U',
      category: 'box-reveal',
      tags: ['journaling', 'junk journal'],
      creator: 'CoraCreaCrafts',
      date: '2026-08',
      thumbnail: 'https://img.youtube.com/vi/LtwnsP6Ku8U/maxresdefault.jpg'
    },
    {
      id: 'yt-api-6',
      platform: 'youtube',
      title: 'Arabian Nights Box Long Preview - Vintage Craft Box August 2026 - Junk Journal & Crafting Box',
      description: '',
      url: 'https://www.youtube.com/watch?v=FaWKKKNH37U',
      embedId: 'FaWKKKNH37U',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'junk journal'],
      creator: 'CoraCreaCrafts',
      date: '2026-08',
      thumbnail: 'https://img.youtube.com/vi/FaWKKKNH37U/maxresdefault.jpg'
    },
    {
      id: 'yt-api-7',
      platform: 'youtube',
      title: 'What\'s in my journaling bag ✨ All my everyday craft essentials 🤎',
      description: '#junkjournal #creativejournaling #whatsinmybag #craftbag #creativejournal',
      url: 'https://www.youtube.com/watch?v=CPMqF3Mb1m0',
      embedId: 'CPMqF3Mb1m0',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-07',
      thumbnail: 'https://img.youtube.com/vi/CPMqF3Mb1m0/maxresdefault.jpg'
    },
    {
      id: 'yt-api-8',
      platform: 'youtube',
      title: 'A little writing inspiration ✨ Featuring our new Esterbrook pen & Ferris Wheel Press ink 👀',
      description: '#pen #fountainpen #fancywriting #newarrivals #esterbrook #penhaul',
      url: 'https://www.youtube.com/watch?v=VB0YLYhxiK0',
      embedId: 'VB0YLYhxiK0',
      category: 'unboxing',
      tags: ['haul'],
      creator: 'CoraCreaCrafts',
      date: '2026-07',
      thumbnail: 'https://img.youtube.com/vi/VB0YLYhxiK0/maxresdefault.jpg'
    },
    {
      id: 'yt-api-9',
      platform: 'youtube',
      title: 'Looking back at my past spreads is always so special ✨ Do you go back through your old journals? 🤎',
      description: '#flip #flipthrough #creativejournaling #scrapbooking #junkjournal #junkjournaling',
      url: 'https://www.youtube.com/watch?v=y8NbSbOmydQ',
      embedId: 'y8NbSbOmydQ',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-07',
      thumbnail: 'https://img.youtube.com/vi/y8NbSbOmydQ/maxresdefault.jpg'
    },
    {
      id: 'yt-api-10',
      platform: 'youtube',
      title: 'A cosy farm spread from our Garden, Farm & Cottage Life box 🌾',
      description: '#gardencrafts  #farmcraft #creativejournaling #scrapbooking #junkjournal #stationery #mindfulliving',
      url: 'https://www.youtube.com/watch?v=Wj8c_92bUz0',
      embedId: 'Wj8c_92bUz0',
      category: 'box-reveal',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-07',
      thumbnail: 'https://img.youtube.com/vi/Wj8c_92bUz0/maxresdefault.jpg'
    },
    {
      id: 'yt-api-11',
      platform: 'youtube',
      title: 'Setting up my July habit tracker with our Mesoamerican pieces 💀',
      description: '#habittracker #habit #productivity #productiveliving #productivityhacks #journaling #creativejournaling',
      url: 'https://www.youtube.com/watch?v=z0W7EEDfroY',
      embedId: 'z0W7EEDfroY',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-07',
      thumbnail: 'https://img.youtube.com/vi/z0W7EEDfroY/maxresdefault.jpg'
    },
    {
      id: 'yt-api-12',
      platform: 'youtube',
      title: 'Garden, Farm & Cottage Life,  a craft box for quiet moments and slower days 🌿',
      description: '#gardenlife #gardencrafts #farmcraft #farmlife #craft #unboxing #stationeryhaul #stationeryaddict',
      url: 'https://www.youtube.com/watch?v=W5ZbVgJsTdY',
      embedId: 'W5ZbVgJsTdY',
      category: 'unboxing',
      tags: ['unboxing', 'haul'],
      creator: 'CoraCreaCrafts',
      date: '2026-07',
      thumbnail: 'https://img.youtube.com/vi/W5ZbVgJsTdY/maxresdefault.jpg'
    },
    {
      id: 'yt-api-13',
      platform: 'youtube',
      title: 'Summer Dream Box Long Preview - Curiosities Box July/August 2026 - Junk Journal & Crafting Box',
      description: '',
      url: 'https://www.youtube.com/watch?v=vmzSMLXd460',
      embedId: 'vmzSMLXd460',
      category: 'box-reveal',
      tags: ['journaling', 'junk journal'],
      creator: 'CoraCreaCrafts',
      date: '2026-07',
      thumbnail: 'https://img.youtube.com/vi/vmzSMLXd460/maxresdefault.jpg'
    },
    {
      id: 'yt-api-14',
      platform: 'youtube',
      title: 'My best friend got married, so of course I had to make all of her wedding stationery ✨',
      description: '#wedding #wedddingdecor #weddinginspiration #weddingseason #europe #eurosummer #trending #travelvlog',
      url: 'https://www.youtube.com/watch?v=i-ElLrdShuU',
      embedId: 'i-ElLrdShuU',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2026-06',
      thumbnail: 'https://img.youtube.com/vi/i-ElLrdShuU/maxresdefault.jpg'
    },
    {
      id: 'yt-api-15',
      platform: 'youtube',
      title: 'This is your sign to add a little magic to your reading with our Swords & Stories bookmarks 🗡️📚',
      description: '#fantasy #fantasybooks #craftingcommunity #booktok #journaling #stationeryaddict #junkjournal',
      url: 'https://www.youtube.com/watch?v=pazB58t0_E4',
      embedId: 'pazB58t0_E4',
      category: 'journal-spread',
      tags: ['journaling', 'fantasy'],
      creator: 'CoraCreaCrafts',
      date: '2026-06',
      thumbnail: 'https://img.youtube.com/vi/pazB58t0_E4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-16',
      platform: 'youtube',
      title: 'Ancient Civilizations Treasure Box ✨ New Theme Reveal',
      description: '#scrapbooking #ancientcivilizations #junkjournal #junkjournalcommunity #stationeryaddict',
      url: 'https://www.youtube.com/watch?v=KkRC4OjObUc',
      embedId: 'KkRC4OjObUc',
      category: 'box-reveal',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-06',
      thumbnail: 'https://img.youtube.com/vi/KkRC4OjObUc/maxresdefault.jpg'
    },
    {
      id: 'yt-api-17',
      platform: 'youtube',
      title: 'You Have to See Our New Tapes ✨ Holographic & Celestial Designs',
      description: '#washington #pettape #transparenttape #tapeloops #stationeryaddict #new',
      url: 'https://www.youtube.com/watch?v=cQ04V66V2V4',
      embedId: 'cQ04V66V2V4',
      category: 'product-showcase',
      tags: ['washi tape'],
      creator: 'CoraCreaCrafts',
      date: '2026-06',
      thumbnail: 'https://img.youtube.com/vi/cQ04V66V2V4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-18',
      platform: 'youtube',
      title: 'Sellotape Transfer Technique ✨ Junk Journal Hack for Vintage Text',
      description: '#scrapbooking #junkjournal #craftingcommunity #journal #journalhaul',
      url: 'https://www.youtube.com/watch?v=gxkIzlz9GLA',
      embedId: 'gxkIzlz9GLA',
      category: 'unboxing',
      tags: ['journaling', 'vintage', 'haul', 'junk journal'],
      creator: 'CoraCreaCrafts',
      date: '2026-06',
      thumbnail: 'https://img.youtube.com/vi/gxkIzlz9GLA/maxresdefault.jpg'
    },
    {
      id: 'yt-api-19',
      platform: 'youtube',
      title: 'For the ones who hoard pretty stickers ✨ Sticker Lover Club',
      description: '#sticker #stickeraddict #stickers #stationeryaddict #crafting',
      url: 'https://www.youtube.com/watch?v=Y7UT8ql9-nk',
      embedId: 'Y7UT8ql9-nk',
      category: 'product-showcase',
      tags: ['stickers'],
      creator: 'CoraCreaCrafts',
      date: '2026-06',
      thumbnail: 'https://img.youtube.com/vi/Y7UT8ql9-nk/maxresdefault.jpg'
    },
    {
      id: 'yt-api-20',
      platform: 'youtube',
      title: 'Say ciao to our Italian Summer Vintage Craft Box 🇮🇹🍋',
      description: '#scrapbooking #italy #italiansummer #italianadventures #craftingcommunity #craftboxx #unboxing #stationeryaddict',
      url: 'https://www.youtube.com/watch?v=NwBpRJ8HMeU',
      embedId: 'NwBpRJ8HMeU',
      category: 'unboxing',
      tags: ['vintage', 'advent calendar', 'unboxing'],
      creator: 'CoraCreaCrafts',
      date: '2026-06',
      thumbnail: 'https://img.youtube.com/vi/NwBpRJ8HMeU/maxresdefault.jpg'
    },
    {
      id: 'yt-api-21',
      platform: 'youtube',
      title: 'Unboxing Poetic Spring ✨ Vintage Floral Junk Journal Craft Box',
      description: '#stationeryaddict #springaesthetic #springcraft #springtime #unboxing #craftingcommunity',
      url: 'https://www.youtube.com/watch?v=LFW-Q5tX6D0',
      embedId: 'LFW-Q5tX6D0',
      category: 'unboxing',
      tags: ['journaling', 'vintage', 'unboxing', 'junk journal'],
      creator: 'CoraCreaCrafts',
      date: '2026-06',
      thumbnail: 'https://img.youtube.com/vi/LFW-Q5tX6D0/maxresdefault.jpg'
    },
    {
      id: 'yt-api-22',
      platform: 'youtube',
      title: 'Spring Waterfall Page Tutorial 🌸 Easy junk journal fold for vintage spring spreads',
      description: '#scrapbooking #waterfall #waterfallpage #springaesthetic #craftingcommunity #craftideas',
      url: 'https://www.youtube.com/watch?v=KjA2r2JdV8o',
      embedId: 'KjA2r2JdV8o',
      category: 'tutorial',
      tags: ['journaling', 'vintage', 'tutorial', 'junk journal'],
      creator: 'CoraCreaCrafts',
      date: '2026-06',
      thumbnail: 'https://img.youtube.com/vi/KjA2r2JdV8o/maxresdefault.jpg'
    },
    {
      id: 'yt-api-23',
      platform: 'youtube',
      title: 'Creative Spring Junk Journal Spread 🌸 Vintage Floral Journaling Inspiration',
      description: '#springaesthetic #spring #springcraft #journal #junkjournalcommunity #journaling #scrapbooking',
      url: 'https://www.youtube.com/watch?v=guJOCbOED6s',
      embedId: 'guJOCbOED6s',
      category: 'journal-spread',
      tags: ['journaling', 'vintage', 'junk journal'],
      creator: 'CoraCreaCrafts',
      date: '2026-06',
      thumbnail: 'https://img.youtube.com/vi/guJOCbOED6s/maxresdefault.jpg'
    },
    {
      id: 'yt-api-24',
      platform: 'youtube',
      title: 'And just like that the story continues... Part 2 of our Treasure Box unboxing is here!',
      description: '#mindfuljournaling #scrapbooking #fablestories #fairytales #fairytale #journal #stationeryaddict',
      url: 'https://www.youtube.com/watch?v=IjMhjRd86Ps',
      embedId: 'IjMhjRd86Ps',
      category: 'unboxing',
      tags: ['journaling', 'unboxing'],
      creator: 'CoraCreaCrafts',
      date: '2026-05',
      thumbnail: 'https://img.youtube.com/vi/IjMhjRd86Ps/maxresdefault.jpg'
    },
    {
      id: 'yt-api-25',
      platform: 'youtube',
      title: 'Little Craft Fest 2026 Vlog 🌸 A Day in the Life of a Craft Maker',
      description: '#scrapbooking #littlecraftfest #crafty #market #craftingcommunity',
      url: 'https://www.youtube.com/watch?v=c1hKiuB34cs',
      embedId: 'c1hKiuB34cs',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2026-05',
      thumbnail: 'https://img.youtube.com/vi/c1hKiuB34cs/maxresdefault.jpg'
    },
    {
      id: 'yt-api-26',
      platform: 'youtube',
      title: 'Unboxing the Tiny Wee Things Treasure Box 🌿 Vintage miniature craft haul',
      description: '#scrapbooking #journaling #junkjournal #stationeryaddict #tinyworld',
      url: 'https://www.youtube.com/watch?v=J92pD2tp-_w',
      embedId: 'J92pD2tp-_w',
      category: 'unboxing',
      tags: ['journaling', 'vintage', 'unboxing', 'haul'],
      creator: 'CoraCreaCrafts',
      date: '2026-05',
      thumbnail: 'https://img.youtube.com/vi/J92pD2tp-_w/maxresdefault.jpg'
    },
    {
      id: 'yt-api-27',
      platform: 'youtube',
      title: 'Packing Memorial Day Grab Bags 🇺🇸 Thank You For The Incredible Support',
      description: '#grabbag #luckybag #craftingcommunity',
      url: 'https://www.youtube.com/watch?v=yWJZGx90Op4',
      embedId: 'yWJZGx90Op4',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2026-05',
      thumbnail: 'https://img.youtube.com/vi/yWJZGx90Op4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-28',
      platform: 'youtube',
      title: 'Once upon a time there was a Treasure Box for the ones who never stopped believing in Fairytales...',
      description: '#scrapbooking #fablestories #fairtale #fairytales #onceuponatime #journaling #craftingcommunity #junkjournal #stationeryaddict',
      url: 'https://www.youtube.com/watch?v=-UFUG0xvrXI',
      embedId: '-UFUG0xvrXI',
      category: 'box-reveal',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-05',
      thumbnail: 'https://img.youtube.com/vi/-UFUG0xvrXI/maxresdefault.jpg'
    },
    {
      id: 'yt-api-29',
      platform: 'youtube',
      title: 'Memorial Day weekend is here ✨ And so are our grab bags and members get to shop them first today!',
      description: 'If there are any left over, they\'ll be available for non-members on Saturday.  Just make sure you\'re logged into your account before you have a look.   Not a member yet? You can make an account anytim',
      url: 'https://www.youtube.com/watch?v=Mkp4PqAtoLM',
      embedId: 'Mkp4PqAtoLM',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2026-05',
      thumbnail: 'https://img.youtube.com/vi/Mkp4PqAtoLM/maxresdefault.jpg'
    },
    {
      id: 'yt-api-30',
      platform: 'youtube',
      title: 'POV: you made an everything journal and it holds your whole life ✨',
      description: '#mentalhealth #mentalhealthawareness #positivevibes #gratitudejournal #positiveenergy #todolist #flipthrough #craftingcommunity #journaling #mindfuljournaling',
      url: 'https://www.youtube.com/watch?v=xf3R28kwY2c',
      embedId: 'xf3R28kwY2c',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-05',
      thumbnail: 'https://img.youtube.com/vi/xf3R28kwY2c/maxresdefault.jpg'
    },
    {
      id: 'yt-api-31',
      platform: 'youtube',
      title: 'POV: the prettiest sticker tin on your desk is one you made yourself 🌸',
      description: '#craftingcommunity #diycrafts #diy #scrapbooking #junkjournal #vintagecrafts #stationeryaddict #stickeraddict #sticker',
      url: 'https://www.youtube.com/watch?v=1wPh1yKXoZ4',
      embedId: '1wPh1yKXoZ4',
      category: 'tutorial',
      tags: ['journaling', 'vintage', 'stickers', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2026-05',
      thumbnail: 'https://img.youtube.com/vi/1wPh1yKXoZ4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-32',
      platform: 'youtube',
      title: 'How to Make a Pressed Flower Wood Panel | Handmade Mother\'s Day Gift Idea',
      description: 'The sweetest Mother\'s Day gifts are the handmade ones! 🌷  In this tutorial, I\'m walking you through exactly how to make a beautiful pressed flower decoupage panel at home. It\'s beginner-friendly, bud',
      url: 'https://www.youtube.com/watch?v=TtEWAmDF6Ts',
      embedId: 'TtEWAmDF6Ts',
      category: 'tutorial',
      tags: ['tutorial'],
      creator: 'CoraCreaCrafts',
      date: '2026-05',
      thumbnail: 'https://img.youtube.com/vi/TtEWAmDF6Ts/maxresdefault.jpg'
    },
    {
      id: 'yt-api-33',
      platform: 'youtube',
      title: 'Not me making a whole subscription just because I couldn\'t stop designing pretty vintage stickers 🌸',
      description: '#sticker #stickeraddict #stickers #stickershop #stickerbook #fyp #subscriber #craftingcommunity #craft #vintagecrafts #junkjournal #journal',
      url: 'https://www.youtube.com/watch?v=iW2Nh_4I5nw',
      embedId: 'iW2Nh_4I5nw',
      category: 'product-showcase',
      tags: ['journaling', 'vintage', 'subscription box', 'stickers'],
      creator: 'CoraCreaCrafts',
      date: '2026-04',
      thumbnail: 'https://img.youtube.com/vi/iW2Nh_4I5nw/maxresdefault.jpg'
    },
    {
      id: 'yt-api-34',
      platform: 'youtube',
      title: 'Sun was out, windows were open, and I had the Poetic Spring Box spread across my craft table 🌷',
      description: '#spring #springaesthetic #mindfuljournaling #journaling #craftingcommunity #stationeryaddict #craftboxx #journal #junkjournal',
      url: 'https://www.youtube.com/watch?v=vt2uTP7lbfo',
      embedId: 'vt2uTP7lbfo',
      category: 'box-reveal',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-04',
      thumbnail: 'https://img.youtube.com/vi/vt2uTP7lbfo/maxresdefault.jpg'
    },
    {
      id: 'yt-api-35',
      platform: 'youtube',
      title: 'Consider this your gentle nudge to start that habit tracker you\'ve been thinking about 🌷',
      description: '',
      url: 'https://www.youtube.com/watch?v=JVl8IDLKfk4',
      embedId: 'JVl8IDLKfk4',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2026-04',
      thumbnail: 'https://img.youtube.com/vi/JVl8IDLKfk4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-36',
      platform: 'youtube',
      title: 'Cozy setup for april\'s gratitude log, our 365 days of stickers are the secret to keeping up with one',
      description: '#gratitude #gratitudejournal #positivevibes #mentalhealthmatters #slowcraft #slowcrafting #junkjournalcommunity #scrapbooking',
      url: 'https://www.youtube.com/watch?v=UyCnvnODbkk',
      embedId: 'UyCnvnODbkk',
      category: 'product-showcase',
      tags: ['journaling', 'stickers'],
      creator: 'CoraCreaCrafts',
      date: '2026-04',
      thumbnail: 'https://img.youtube.com/vi/UyCnvnODbkk/maxresdefault.jpg'
    },
    {
      id: 'yt-api-37',
      platform: 'youtube',
      title: 'This one\'s for the crafters who romanticise everything ✨',
      description: '#springaesthetic #spring #springcrafts #springcraft #craftboxx #unboxing #haulvideo #craftholic #junkjournal',
      url: 'https://www.youtube.com/watch?v=x2LXzMkOmg8',
      embedId: 'x2LXzMkOmg8',
      category: 'unboxing',
      tags: ['journaling', 'unboxing', 'haul'],
      creator: 'CoraCreaCrafts',
      date: '2026-04',
      thumbnail: 'https://img.youtube.com/vi/x2LXzMkOmg8/maxresdefault.jpg'
    },
    {
      id: 'yt-api-38',
      platform: 'youtube',
      title: 'My husband built something SO special for our community 🥹',
      description: 'All our videos,  tutorials, unboxings, spreads and more, now live in one place on our website, searchable by product so you can find exactly what you need! Let me know what you think 🔍✨  Link in bio!',
      url: 'https://www.youtube.com/watch?v=1Chpzjb0XH8',
      embedId: '1Chpzjb0XH8',
      category: 'unboxing',
      tags: ['tutorial', 'unboxing'],
      creator: 'CoraCreaCrafts',
      date: '2026-04',
      thumbnail: 'https://img.youtube.com/vi/1Chpzjb0XH8/maxresdefault.jpg'
    },
    {
      id: 'yt-api-39',
      platform: 'youtube',
      title: 'Secrets of the Pharoahs vintage journal spread 💫 Should we bring back more Egyptian themed items?',
      description: '#egyptian #egyptianmythology #egyptianvibes #junkjournal #journaling #journalspreads #scrapbooking',
      url: 'https://www.youtube.com/watch?v=YQM9RhOETcI',
      embedId: 'YQM9RhOETcI',
      category: 'journal-spread',
      tags: ['journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2026-04',
      thumbnail: 'https://img.youtube.com/vi/YQM9RhOETcI/maxresdefault.jpg'
    },
    {
      id: 'yt-api-40',
      platform: 'youtube',
      title: 'One splash of water and our Alchemy Burst Powders do THIS! 🔮',
      description: '#junkjournal #fortunetelling #fortune #fortuneteller #fyp #mixedmedia #mixedmediaart #powderart',
      url: 'https://www.youtube.com/watch?v=YtVzkYKOWQw',
      embedId: 'YtVzkYKOWQw',
      category: 'product-showcase',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-04',
      thumbnail: 'https://img.youtube.com/vi/YtVzkYKOWQw/maxresdefault.jpg'
    },
    {
      id: 'yt-api-41',
      platform: 'youtube',
      title: 'Just a couple of crafty girls on Channel 5 news living their best life!😂',
      description: '#nashville #nashvillestore #junkjournal #onthenews #news #channel5',
      url: 'https://www.youtube.com/watch?v=1RA2AbuLD_A',
      embedId: '1RA2AbuLD_A',
      category: 'product-showcase',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-04',
      thumbnail: 'https://img.youtube.com/vi/1RA2AbuLD_A/maxresdefault.jpg'
    },
    {
      id: 'yt-api-42',
      platform: 'youtube',
      title: 'POV: You open your new vintage craft box and spring falls out 🌸',
      description: '#spring #springaesthetic #scrapbooking #stationeryaddict #journaling #junkjournal #vintagecrafts',
      url: 'https://www.youtube.com/watch?v=CYa_etiHV3g',
      embedId: 'CYa_etiHV3g',
      category: 'box-reveal',
      tags: ['journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2026-04',
      thumbnail: 'https://img.youtube.com/vi/CYa_etiHV3g/maxresdefault.jpg'
    },
    {
      id: 'yt-api-43',
      platform: 'youtube',
      title: 'I\'ve wanted my own perfume for years and today it\'s finally here introducing Craft Room N°5!',
      description: '#perfume #perfumes #perfumecollection  #craftingcommunity  #craft #junkjournal #junkjournalcommunity  #vintagecrafts',
      url: 'https://www.youtube.com/watch?v=QbZKeUZM9Cg',
      embedId: 'QbZKeUZM9Cg',
      category: 'product-showcase',
      tags: ['journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2026-04',
      thumbnail: 'https://img.youtube.com/vi/QbZKeUZM9Cg/maxresdefault.jpg'
    },
    {
      id: 'yt-api-44',
      platform: 'youtube',
      title: 'We have some really cool events coming up at our store! Join us at Creative Corner Nashville ✨',
      description: 'Get your tickets here! https://coracreacrafts.com/pages/event-schedule',
      url: 'https://www.youtube.com/watch?v=asnn72mXkKQ',
      embedId: 'asnn72mXkKQ',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2026-03',
      thumbnail: 'https://img.youtube.com/vi/asnn72mXkKQ/maxresdefault.jpg'
    },
    {
      id: 'yt-api-45',
      platform: 'youtube',
      title: 'Sometimes the most special spreads are the ones that were totally unplanned ❤️',
      description: '#craftingcommunity #journaling #mindfuljournaling #journal #bulletjournalling #junkjournal #junkjournalcommunity',
      url: 'https://www.youtube.com/watch?v=UXrJzXcoUsM',
      embedId: 'UXrJzXcoUsM',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-03',
      thumbnail: 'https://img.youtube.com/vi/UXrJzXcoUsM/maxresdefault.jpg'
    },
    {
      id: 'yt-api-46',
      platform: 'youtube',
      title: 'Okay, so I might be a little obsessed with our Ireland-themed box… 💚',
      description: '#craftingcommunity #ireland #irish #stationeryaddict #stpatricksday #journaling #vintagecrafts',
      url: 'https://www.youtube.com/watch?v=bW1xMfmL3b8',
      embedId: 'bW1xMfmL3b8',
      category: 'box-reveal',
      tags: ['journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2026-03',
      thumbnail: 'https://img.youtube.com/vi/bW1xMfmL3b8/maxresdefault.jpg'
    },
    {
      id: 'yt-api-47',
      platform: 'youtube',
      title: 'March needed a little intention setting so, I made this gratitude log with our Fortune Teller items!',
      description: '#fortunetelling #fortune #craftboxx #habittracker #gratitudejournal #gratitude #mindfuljournaling',
      url: 'https://www.youtube.com/watch?v=adOpKA-PSW0',
      embedId: 'adOpKA-PSW0',
      category: 'box-reveal',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-03',
      thumbnail: 'https://img.youtube.com/vi/adOpKA-PSW0/maxresdefault.jpg'
    },
    {
      id: 'yt-api-48',
      platform: 'youtube',
      title: 'I may have gone a little overboard with the new arrivals in store… 👀',
      description: '#nashville #smallbusiness #journal #newarrivals #stationeryaddict #craftboxx',
      url: 'https://www.youtube.com/watch?v=G7AP3CLbRUw',
      embedId: 'G7AP3CLbRUw',
      category: 'box-reveal',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-03',
      thumbnail: 'https://img.youtube.com/vi/G7AP3CLbRUw/maxresdefault.jpg'
    },
    {
      id: 'yt-api-49',
      platform: 'youtube',
      title: 'Looking back at my March habit tracker I made ✨ Did you keep up with your habits this month?',
      description: '#craftingcommunity #habittracker #habits #journal #stationeryaddict #mindfuljournaling #resetyourmindset #wellnessjourney #wellnesstips',
      url: 'https://www.youtube.com/watch?v=Ao60TH-2fJI',
      embedId: 'Ao60TH-2fJI',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-03',
      thumbnail: 'https://img.youtube.com/vi/Ao60TH-2fJI/maxresdefault.jpg'
    },
    {
      id: 'yt-api-50',
      platform: 'youtube',
      title: 'Bringing a touch of Ireland to my journal! 🍀💚',
      description: '#craftingcommunity #ireland #irish #stationeryaddict #stpatricksday #journaling #vintagecrafts #journalwithme',
      url: 'https://www.youtube.com/watch?v=s4QTmnvBePY',
      embedId: 's4QTmnvBePY',
      category: 'journal-spread',
      tags: ['journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2026-03',
      thumbnail: 'https://img.youtube.com/vi/s4QTmnvBePY/maxresdefault.jpg'
    },
    {
      id: 'yt-api-51',
      platform: 'youtube',
      title: 'The universe called and it left you a box...🔮',
      description: '#journaling #fortuneteller #fortunetelling #stationeryaddict #unboxing #craftboxx',
      url: 'https://www.youtube.com/watch?v=5iMBcRy44Vg',
      embedId: '5iMBcRy44Vg',
      category: 'unboxing',
      tags: ['journaling', 'unboxing'],
      creator: 'CoraCreaCrafts',
      date: '2026-03',
      thumbnail: 'https://img.youtube.com/vi/5iMBcRy44Vg/maxresdefault.jpg'
    },
    {
      id: 'yt-api-52',
      platform: 'youtube',
      title: 'Who’s obsessed with Wuthering Heights like we are?',
      description: '#wutheringheights #margotrobbie #jacobelordi #wutheringheightsmovie #craftingcommunity #craft #nashville #nashvillestore',
      url: 'https://www.youtube.com/watch?v=wuuZhooO5AY',
      embedId: 'wuuZhooO5AY',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2026-03',
      thumbnail: 'https://img.youtube.com/vi/wuuZhooO5AY/maxresdefault.jpg'
    },
    {
      id: 'yt-api-53',
      platform: 'youtube',
      title: 'Love glitter, Polaroids & sparkle? Come to our store Friday! Tickets in the description ✨',
      description: 'https://coracreacrafts.com/pages/event-schedule ^ Get your tickets!',
      url: 'https://www.youtube.com/watch?v=qpV-qsqtB3A',
      embedId: 'qpV-qsqtB3A',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2026-03',
      thumbnail: 'https://img.youtube.com/vi/qpV-qsqtB3A/maxresdefault.jpg'
    },
    {
      id: 'yt-api-54',
      platform: 'youtube',
      title: 'Our Ireland-themed Vintage Craft Box has arrived ☘️',
      description: '#ireland #stpatricksday #craftingcommunity #craftboxx #journaling #journal #bulletjournalling',
      url: 'https://www.youtube.com/watch?v=EDYVVCG0wCo',
      embedId: 'EDYVVCG0wCo',
      category: 'box-reveal',
      tags: ['journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2026-03',
      thumbnail: 'https://img.youtube.com/vi/EDYVVCG0wCo/maxresdefault.jpg'
    },
    {
      id: 'yt-api-55',
      platform: 'youtube',
      title: 'See how we pack the Legends & Lore Craft Box inspired by Game of Thrones fantasy supplies!',
      description: '#craftingcommunity   #gameofthrones #craftkit #aknightofthesevenkingdoms #fantasy #fantasycraft #medieval   In collaboration with the very talented Storm Sparrows Inn!',
      url: 'https://www.youtube.com/watch?v=cGEGXq4kzC4',
      embedId: 'cGEGXq4kzC4',
      category: 'box-reveal',
      tags: ['fantasy', 'medieval'],
      creator: 'CoraCreaCrafts',
      date: '2026-02',
      thumbnail: 'https://img.youtube.com/vi/cGEGXq4kzC4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-56',
      platform: 'youtube',
      title: 'Legends & Lore Craft Box | Game of Thrones-inspired fantasy journaling & scrapbooking supplies',
      description: '#gameofthrones #aknightofthesevenkingdoms #fantasy #fantasycraft #medieval #booktok #craftingcommunity #craftbox',
      url: 'https://www.youtube.com/watch?v=FF-idqVeKPM',
      embedId: 'FF-idqVeKPM',
      category: 'box-reveal',
      tags: ['journaling', 'fantasy', 'medieval'],
      creator: 'CoraCreaCrafts',
      date: '2026-02',
      thumbnail: 'https://img.youtube.com/vi/FF-idqVeKPM/maxresdefault.jpg'
    },
    {
      id: 'yt-api-57',
      platform: 'youtube',
      title: 'Our brand new bundle with Laura Frantz for The Belle of Chatham is here 🤎📖✂️',
      description: '#booktok #bookreview #booktube  #romancebooks #historical #independenceday #independence #bookish #bookcraft #laurafranz',
      url: 'https://www.youtube.com/watch?v=H7lXYH4S-kc',
      embedId: 'H7lXYH4S-kc',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2026-02',
      thumbnail: 'https://img.youtube.com/vi/H7lXYH4S-kc/maxresdefault.jpg'
    },
    {
      id: 'yt-api-58',
      platform: 'youtube',
      title: 'Our sticker sheets are to die for! Come visit us in store and get your sticker fix! ❤️',
      description: '#craftingcommunity #stickers #stickeraddict #stickerhaul #stickershop #smallbusiness #nashville #craftshopping',
      url: 'https://www.youtube.com/watch?v=_RZn5qoHdeE',
      embedId: '_RZn5qoHdeE',
      category: 'unboxing',
      tags: ['stickers', 'haul'],
      creator: 'CoraCreaCrafts',
      date: '2026-02',
      thumbnail: 'https://img.youtube.com/vi/_RZn5qoHdeE/maxresdefault.jpg'
    },
    {
      id: 'yt-api-59',
      platform: 'youtube',
      title: 'This is what relaxation looks like for me: sunshine, no rush, and my journal ☀️',
      description: '#smallbusiness #craftingcommunity #mindfuljournaling #journaling #craftsupplies #habittracker',
      url: 'https://www.youtube.com/watch?v=pB1ctSPNSf0',
      embedId: 'pB1ctSPNSf0',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-02',
      thumbnail: 'https://img.youtube.com/vi/pB1ctSPNSf0/maxresdefault.jpg'
    },
    {
      id: 'yt-api-60',
      platform: 'youtube',
      title: 'Still sticking to my 2026 habits and wasn\'t about to let a trip stop me!  Also made a little friend🥹',
      description: '#habittracker #habits #journaling #mindfuljournaling #scrapbooking #craftsupplies #reset #resetyourmindset',
      url: 'https://www.youtube.com/watch?v=_Signmfjr-E',
      embedId: '_Signmfjr-E',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-02',
      thumbnail: 'https://img.youtube.com/vi/_Signmfjr-E/maxresdefault.jpg'
    },
    {
      id: 'yt-api-61',
      platform: 'youtube',
      title: 'Welcome to a full Dreamscape Journal Kit unboxing and walkthrough! ✨',
      description: '#craftingcommunity #craftbox #craftsupplies #stationeryaddict #journal #scrapbooking',
      url: 'https://www.youtube.com/watch?v=xlO_Y-X4KSQ',
      embedId: 'xlO_Y-X4KSQ',
      category: 'unboxing',
      tags: ['journaling', 'unboxing'],
      creator: 'CoraCreaCrafts',
      date: '2026-02',
      thumbnail: 'https://img.youtube.com/vi/xlO_Y-X4KSQ/maxresdefault.jpg'
    },
    {
      id: 'yt-api-62',
      platform: 'youtube',
      title: 'A snowy day called for a Dreamscape Journal Spread ❄️',
      description: '#craftingcommunity #journaling #dreamscape #dream #stationeryaddict #journal #junkjournal',
      url: 'https://www.youtube.com/watch?v=TXaOBNFR96c',
      embedId: 'TXaOBNFR96c',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-02',
      thumbnail: 'https://img.youtube.com/vi/TXaOBNFR96c/maxresdefault.jpg'
    },
    {
      id: 'yt-api-63',
      platform: 'youtube',
      title: 'Still need a Valentine\'s gift? Here are some ideas for the stationery lover in your life 🤍',
      description: '#valentinesday #valentine #journaling #smallbusiness #nashville #giftideas #gifts #love #craftingcommunity',
      url: 'https://www.youtube.com/watch?v=_KTNk8730HA',
      embedId: '_KTNk8730HA',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-02',
      thumbnail: 'https://img.youtube.com/vi/_KTNk8730HA/maxresdefault.jpg'
    },
    {
      id: 'yt-api-64',
      platform: 'youtube',
      title: 'Make a positivity journal page with me! ❤️',
      description: '#smallbusiness #mindfuljournaling #habittracker #positivity #positivevibes #journaling #stationeryaddict #scrapbooking #junkjournal',
      url: 'https://www.youtube.com/watch?v=utNcGUzMo_s',
      embedId: 'utNcGUzMo_s',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-02',
      thumbnail: 'https://img.youtube.com/vi/utNcGUzMo_s/maxresdefault.jpg'
    },
    {
      id: 'yt-api-65',
      platform: 'youtube',
      title: 'Come pack our 365 Days of Stickers box with us! ✨',
      description: '#stickers #stickeraddict #stickerhaul #scrapbooking #scrapbookingideas #diary #creativejournaling #junkjournal',
      url: 'https://www.youtube.com/watch?v=5gWxcL8vHMc',
      embedId: '5gWxcL8vHMc',
      category: 'unboxing',
      tags: ['journaling', 'stickers', 'haul'],
      creator: 'CoraCreaCrafts',
      date: '2026-02',
      thumbnail: 'https://img.youtube.com/vi/5gWxcL8vHMc/maxresdefault.jpg'
    },
    {
      id: 'yt-api-66',
      platform: 'youtube',
      title: 'Ever wonder what your journal would look like if it wondered through a dream?✨',
      description: '#craftingcommunity #dreamscape #dream #journaling #craftkit #craftbox #stationeryaddict',
      url: 'https://www.youtube.com/watch?v=Et9R8PV5UqE',
      embedId: 'Et9R8PV5UqE',
      category: 'box-reveal',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-02',
      thumbnail: 'https://img.youtube.com/vi/Et9R8PV5UqE/maxresdefault.jpg'
    },
    {
      id: 'yt-api-67',
      platform: 'youtube',
      title: 'Pack an Order with Me: Behind the Scenes at CoraCreaCrafts ✨📦',
      description: '#packanorderwithme #behindthescenes #smallbusiness #orderpacking',
      url: 'https://www.youtube.com/watch?v=tnSxyFCc9cY',
      embedId: 'tnSxyFCc9cY',
      category: 'behind-the-scenes',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2026-02',
      thumbnail: 'https://img.youtube.com/vi/tnSxyFCc9cY/maxresdefault.jpg'
    },
    {
      id: 'yt-api-68',
      platform: 'youtube',
      title: 'Come Inside Our Vision Board Workshop ✨',
      description: 'Step inside our recent Vision Board Workshop at CoraCreaCrafts! Watch as participants get creative, set intentions, and bring their goals to life with art and inspiration. Perfect for anyone looking t',
      url: 'https://www.youtube.com/watch?v=Pp0fTcT0_ko',
      embedId: 'Pp0fTcT0_ko',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2026-01',
      thumbnail: 'https://img.youtube.com/vi/Pp0fTcT0_ko/maxresdefault.jpg'
    },
    {
      id: 'yt-api-69',
      platform: 'youtube',
      title: 'How to Make a Vision Board for Your Journal ✨ DIY Goal Planning',
      description: 'Join us as we create a beautiful vision board for your journal! 🌿 In this video, I’ll guide you step-by-step through designing a vision board that inspires your goals, intentions, and creativity. Per',
      url: 'https://www.youtube.com/watch?v=74dNf_8rZH0',
      embedId: '74dNf_8rZH0',
      category: 'tutorial',
      tags: ['journaling', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2026-01',
      thumbnail: 'https://img.youtube.com/vi/74dNf_8rZH0/maxresdefault.jpg'
    },
    {
      id: 'yt-api-70',
      platform: 'youtube',
      title: 'CoraCreaCrafts Featured on News! Channel 5 Talk of The Town Stopped by Our Nashville Store!',
      description: 'We’re still pinching ourselves! 🎉 News Channel 5 stopped by CoraCreaCrafts for their Talk of The Town segment to see our Nashville store and all the latest craft, stationery, and junk journaling supp',
      url: 'https://www.youtube.com/watch?v=jC_gf68a8Ec',
      embedId: 'jC_gf68a8Ec',
      category: 'journal-spread',
      tags: ['journaling', 'junk journal'],
      creator: 'CoraCreaCrafts',
      date: '2026-01',
      thumbnail: 'https://img.youtube.com/vi/jC_gf68a8Ec/maxresdefault.jpg'
    },
    {
      id: 'yt-api-71',
      platform: 'youtube',
      title: 'New In-Store Arrivals – Junk Journaling Kits & Craft Supplies | Creative Corner Nashville',
      description: 'Check out the latest in-store arrivals at CoraCreaCrafts! From junk journaling kits and scrapbooking supplies to stationery and craft tools, these new products are perfect for crafters, planners, and ',
      url: 'https://www.youtube.com/watch?v=wv0vKnGNsME',
      embedId: 'wv0vKnGNsME',
      category: 'journal-spread',
      tags: ['journaling', 'junk journal'],
      creator: 'CoraCreaCrafts',
      date: '2026-01',
      thumbnail: 'https://img.youtube.com/vi/wv0vKnGNsME/maxresdefault.jpg'
    },
    {
      id: 'yt-api-72',
      platform: 'youtube',
      title: 'Build Daily Habits & Gratitude Journaling Tips ��️',
      description: 'In this video, I’m setting up a gratitude log in my new Bird Journal and sharing simple ways to track one small thing each day. Perfect for journaling beginners or anyone looking to cultivate positivi',
      url: 'https://www.youtube.com/watch?v=i2CG_1dwwPg',
      embedId: 'i2CG_1dwwPg',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-01',
      thumbnail: 'https://img.youtube.com/vi/i2CG_1dwwPg/maxresdefault.jpg'
    },
    {
      id: 'yt-api-73',
      platform: 'youtube',
      title: 'Celtic Treasure Box Unboxing: Nature & Mythology Inspired Crafts',
      description: 'Step into the world of the Celtic Treasure Box, inspired by ancient landscapes, rolling green hills, misty forests, and sacred symbols. This unique collection blends mythology, nature, and timeless tr',
      url: 'https://www.youtube.com/watch?v=TArBlMnU5uY',
      embedId: 'TArBlMnU5uY',
      category: 'unboxing',
      tags: ['unboxing'],
      creator: 'CoraCreaCrafts',
      date: '2026-01',
      thumbnail: 'https://img.youtube.com/vi/TArBlMnU5uY/maxresdefault.jpg'
    },
    {
      id: 'yt-api-74',
      platform: 'youtube',
      title: 'January 2026 New Arrivals: Junk Journaling Kits, Craft & Stationery | CoraCreaCrafts',
      description: '#newarrivals #stationeryaddict #stationeryhaul #craftingcommunity #junkjournal #craftsupplies #scrapbooking',
      url: 'https://www.youtube.com/watch?v=_leyHfm6Ing',
      embedId: '_leyHfm6Ing',
      category: 'unboxing',
      tags: ['journaling', 'haul', 'junk journal'],
      creator: 'CoraCreaCrafts',
      date: '2026-01',
      thumbnail: 'https://img.youtube.com/vi/_leyHfm6Ing/maxresdefault.jpg'
    },
    {
      id: 'yt-api-75',
      platform: 'youtube',
      title: 'Step inside Creative Corner Nashville! See our opening hours and explore our cozy world of crafts 🎨✨',
      description: 'Welcome to Creative Corner Nashville! In this video, we share our store opening hours and give you a glimpse of our charming shop filled with craft supplies, junk journaling kits, stationery, and DIY ',
      url: 'https://www.youtube.com/watch?v=r9aD3M7QUx4',
      embedId: 'r9aD3M7QUx4',
      category: 'tutorial',
      tags: ['journaling', 'diy', 'junk journal'],
      creator: 'CoraCreaCrafts',
      date: '2026-01',
      thumbnail: 'https://img.youtube.com/vi/r9aD3M7QUx4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-76',
      platform: 'youtube',
      title: 'So excited to finally show you what\'s inside our New Look VCB! Inspired by postwar 1940\'s - 1950\'s❤️',
      description: '#1950s #postwarera #fashion #fashioncrafting #newlookfashion #craftingcommunity #junkjournal #bulletjournalling #scrapbooking #craftbox #mindfuljournaling',
      url: 'https://www.youtube.com/watch?v=HhoN2NEAQwM',
      embedId: 'HhoN2NEAQwM',
      category: 'box-reveal',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-01',
      thumbnail: 'https://img.youtube.com/vi/HhoN2NEAQwM/maxresdefault.jpg'
    },
    {
      id: 'yt-api-77',
      platform: 'youtube',
      title: 'Brand new pieces, now in store 🤍 I\'m obsessed with these,  come see if you’re nearby!',
      description: '#nashville #nashvillestore #smallbusiness #tennessee #newarrivals #restock #scrapbooking #craftingcommunity #stationeryaddict #craftsupplies',
      url: 'https://www.youtube.com/watch?v=theiffvUIy0',
      embedId: 'theiffvUIy0',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2026-01',
      thumbnail: 'https://img.youtube.com/vi/theiffvUIy0/maxresdefault.jpg'
    },
    {
      id: 'yt-api-78',
      platform: 'youtube',
      title: 'I’ll be honest, my self-control didn’t stand a chance once these were released 🫣',
      description: '#newarrivals #craftsupplies #craftingcommunity #junkjournal #stationeryaddict #journal #stickers #tape #craftinglove',
      url: 'https://www.youtube.com/watch?v=30K7r8koeZg',
      embedId: '30K7r8koeZg',
      category: 'product-showcase',
      tags: ['journaling', 'stickers'],
      creator: 'CoraCreaCrafts',
      date: '2026-01',
      thumbnail: 'https://img.youtube.com/vi/30K7r8koeZg/maxresdefault.jpg'
    },
    {
      id: 'yt-api-79',
      platform: 'youtube',
      title: 'Owl & Fox clips & matching scissors are back in stock! Come and visit us in Nashville! 🤗',
      description: 'Creative Corner Nashville 501 Metroplex Drive #117  Nashville Tennessee   Open:  Wed-Thu: 11am-6pm  Fri - Sat: 10am-6pm  Sun: 11am-6pm',
      url: 'https://www.youtube.com/watch?v=j1rWmXEw7dE',
      embedId: 'j1rWmXEw7dE',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2026-01',
      thumbnail: 'https://img.youtube.com/vi/j1rWmXEw7dE/maxresdefault.jpg'
    },
    {
      id: 'yt-api-80',
      platform: 'youtube',
      title: 'This year, one of my resolutions is to have more relaxing moments just me and my journal 🤗',
      description: '#scrapbooking #junkjournal #mindfuljournaling #craftingcommunity #journaling #junkjournalcommunity  #craftsupplies #resolution2026 #newyearnewme',
      url: 'https://www.youtube.com/watch?v=EzaAoXEHPGQ',
      embedId: 'EzaAoXEHPGQ',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-01',
      thumbnail: 'https://img.youtube.com/vi/EzaAoXEHPGQ/maxresdefault.jpg'
    },
    {
      id: 'yt-api-81',
      platform: 'youtube',
      title: 'Our Celtic box is now available to order and will ship within the next week! 🍀',
      description: '#celtics #celtic #craftingcommunity #craftkit #craftbox #junkjournal #creativejournaling #mindfuljournaling #unboxing #coffeeandcraft',
      url: 'https://www.youtube.com/watch?v=VLEuIxgjoVQ',
      embedId: 'VLEuIxgjoVQ',
      category: 'unboxing',
      tags: ['journaling', 'unboxing'],
      creator: 'CoraCreaCrafts',
      date: '2026-01',
      thumbnail: 'https://img.youtube.com/vi/VLEuIxgjoVQ/maxresdefault.jpg'
    },
    {
      id: 'yt-api-82',
      platform: 'youtube',
      title: 'Mini Argentina Vlog! Spending the holidays here was a dream 🎄',
      description: '#argentina #minivlog #vloggerlife #travelvlog #smallbusiness #creativejournaling #craftingcommunity',
      url: 'https://www.youtube.com/watch?v=k3APCeyCH7E',
      embedId: 'k3APCeyCH7E',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-01',
      thumbnail: 'https://img.youtube.com/vi/k3APCeyCH7E/maxresdefault.jpg'
    },
    {
      id: 'yt-api-83',
      platform: 'youtube',
      title: 'New year, new habits. Still in my pj\'s choosing to start my mornings with gratitude! 🤍',
      description: '#gratitude #2026 #newyear2026 #newyearnewme #newyearreset #journaling #journalwithme #thankful #habits',
      url: 'https://www.youtube.com/watch?v=JH2RrrL4Jv4',
      embedId: 'JH2RrrL4Jv4',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2026-01',
      thumbnail: 'https://img.youtube.com/vi/JH2RrrL4Jv4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-84',
      platform: 'youtube',
      title: 'There’s something so relaxing about these ASMR sounds ✂️🎨 Which one’s your favourite?',
      description: '#asmrsounds #asmr #asmrvideo #mindfuljournaling #journaling #colouring #craftbox #crafttok #vintagecrafts #stationeryaddict',
      url: 'https://www.youtube.com/watch?v=VHTazDHr2X4',
      embedId: 'VHTazDHr2X4',
      category: 'box-reveal',
      tags: ['journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2026-01',
      thumbnail: 'https://img.youtube.com/vi/VHTazDHr2X4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-85',
      platform: 'youtube',
      title: 'Which Vintage Craft Box theme was your favourite in 2025? ✨',
      description: '#craftingcommunity #scrapbooking #fyp #mindfuljournaling #journaling #junkjournal #bulletjournalling #crafting',
      url: 'https://www.youtube.com/watch?v=ojPocVq4eIU',
      embedId: 'ojPocVq4eIU',
      category: 'box-reveal',
      tags: ['journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-12',
      thumbnail: 'https://img.youtube.com/vi/ojPocVq4eIU/maxresdefault.jpg'
    },
    {
      id: 'yt-api-86',
      platform: 'youtube',
      title: 'Looking back at some of my favourite spreads from this year ✨ Which spread is your favourite? 📖💛',
      description: '#craftingcommunity #scrapbooking #journaling #mindfuljournaling #christmasgift #junkjournal #spread #wrapped',
      url: 'https://www.youtube.com/watch?v=7Zrg4kdXIgg',
      embedId: '7Zrg4kdXIgg',
      category: 'journal-spread',
      tags: ['journaling', 'christmas'],
      creator: 'CoraCreaCrafts',
      date: '2025-12',
      thumbnail: 'https://img.youtube.com/vi/7Zrg4kdXIgg/maxresdefault.jpg'
    },
    {
      id: 'yt-api-87',
      platform: 'youtube',
      title: 'Get up to 50% off our entire site until Monday in our end of year sale! Happy Shopping!🎄✨',
      description: '#sale #craftsale #endofyearsale #craftingcommunity #creativejournaling #craftsupplies #junkjournal #craftbox #stationeryaddict #stationeryhaul',
      url: 'https://www.youtube.com/watch?v=ibTFecBxgGc',
      embedId: 'ibTFecBxgGc',
      category: 'unboxing',
      tags: ['journaling', 'haul'],
      creator: 'CoraCreaCrafts',
      date: '2025-12',
      thumbnail: 'https://img.youtube.com/vi/ibTFecBxgGc/maxresdefault.jpg'
    },
    {
      id: 'yt-api-88',
      platform: 'youtube',
      title: 'Merry Christmas to all of our wonderful supporters! 🎄Thank you for being part of our community!',
      description: '#coracreacrafts #craftingcommunity #craftlove #junkjournal #craftingtherapy #mindfuljournaling #journaling #junkjournal #stickers #stationeryaddict',
      url: 'https://www.youtube.com/watch?v=UIp21n1CGJc',
      embedId: 'UIp21n1CGJc',
      category: 'journal-spread',
      tags: ['journaling', 'christmas', 'stickers'],
      creator: 'CoraCreaCrafts',
      date: '2025-12',
      thumbnail: 'https://img.youtube.com/vi/UIp21n1CGJc/maxresdefault.jpg'
    },
    {
      id: 'yt-api-89',
      platform: 'youtube',
      title: 'You voted and we listened! This months box brings the beauty of gothic literature to your journal!',
      description: '#gothic #gothiclove #poe #gothicwriters #craftingcommunity #journaling #craftboxx #mindfuljournaling #journal #atc #stationeryhaul',
      url: 'https://www.youtube.com/watch?v=2ZH91s7hpxQ',
      embedId: '2ZH91s7hpxQ',
      category: 'unboxing',
      tags: ['journaling', 'gothic', 'haul'],
      creator: 'CoraCreaCrafts',
      date: '2025-12',
      thumbnail: 'https://img.youtube.com/vi/2ZH91s7hpxQ/maxresdefault.jpg'
    },
    {
      id: 'yt-api-90',
      platform: 'youtube',
      title: 'Nashville friends, if you love making things and meeting people our events are perfect for you!',
      description: '#craftingcommunity #nashville #nashvillestore #craftclasses #friendship #workshop #nashvilleworkshop #journaling',
      url: 'https://www.youtube.com/watch?v=PRY5vgI-toQ',
      embedId: 'PRY5vgI-toQ',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-12',
      thumbnail: 'https://img.youtube.com/vi/PRY5vgI-toQ/maxresdefault.jpg'
    },
    {
      id: 'yt-api-91',
      platform: 'youtube',
      title: 'Winter magic has arrived! Let the cozy crafting begin',
      description: '#winter #wintercollection #wintercrafts #winterwonderland #craftboxx #scrapbooking #mindfuljournaling #journaling #journal #stationeryaddict',
      url: 'https://www.youtube.com/watch?v=x_m3eYRdfAA',
      embedId: 'x_m3eYRdfAA',
      category: 'box-reveal',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-12',
      thumbnail: 'https://img.youtube.com/vi/x_m3eYRdfAA/maxresdefault.jpg'
    },
    {
      id: 'yt-api-92',
      platform: 'youtube',
      title: 'Have you started christmas crafting yet? ✨ Our new Christmas Journaling Book is here!',
      description: '#craftingcommunity #christmasgift #christmascrafts #christmasjournal #festivevibes #scrapbooking #sticker #stationeryaddict #ephemera',
      url: 'https://www.youtube.com/watch?v=bqQkxHqrZR4',
      embedId: 'bqQkxHqrZR4',
      category: 'journal-spread',
      tags: ['journaling', 'christmas', 'stickers'],
      creator: 'CoraCreaCrafts',
      date: '2025-12',
      thumbnail: 'https://img.youtube.com/vi/bqQkxHqrZR4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-93',
      platform: 'youtube',
      title: 'Here\'s a little behind the scenes peak at the making of our Dream Calendar!',
      description: '#behindthescenes #sneakpeek #craftingcommunity #adventcalendar #craftboxx #journaling #craftsupplies #scrapbooking #christmasgift',
      url: 'https://www.youtube.com/watch?v=rtfW6EPc_4M',
      embedId: 'rtfW6EPc_4M',
      category: 'box-reveal',
      tags: ['journaling', 'christmas', 'advent calendar'],
      creator: 'CoraCreaCrafts',
      date: '2025-12',
      thumbnail: 'https://img.youtube.com/vi/rtfW6EPc_4M/maxresdefault.jpg'
    },
    {
      id: 'yt-api-94',
      platform: 'youtube',
      title: 'This is your sign to pause and unwind with our special crafty Advent Calendar!',
      description: '#craftingcommunity #adventcalendar #journaling #craftboxx #craftsupplies #christmasgift #reset #mindfuljournaling #mindfulness #hobbies #craft #relaxing',
      url: 'https://www.youtube.com/watch?v=VWHqGdQLC2A',
      embedId: 'VWHqGdQLC2A',
      category: 'box-reveal',
      tags: ['journaling', 'christmas', 'advent calendar'],
      creator: 'CoraCreaCrafts',
      date: '2025-11',
      thumbnail: 'https://img.youtube.com/vi/VWHqGdQLC2A/maxresdefault.jpg'
    },
    {
      id: 'yt-api-95',
      platform: 'youtube',
      title: 'POV: You made the perfect craft box for Dragon Lovers...',
      description: '#dragon #fantasycraft #fantasyart #dragonart #craftingcommunity #journaling #craftsupplies #scrapbooking #colouring #stationeryaddict',
      url: 'https://www.youtube.com/watch?v=4ygxif0hROk',
      embedId: '4ygxif0hROk',
      category: 'box-reveal',
      tags: ['journaling', 'fantasy'],
      creator: 'CoraCreaCrafts',
      date: '2025-11',
      thumbnail: 'https://img.youtube.com/vi/4ygxif0hROk/maxresdefault.jpg'
    },
    {
      id: 'yt-api-96',
      platform: 'youtube',
      title: 'Meet me in the Library after midnight. The books know secrets. In love with our newest VCB!',
      description: '#booklover #library #booktube #journaling #scrapbooking #craftsupplies #scrapbooking #bookish #bookstagram #craftboxx',
      url: 'https://www.youtube.com/watch?v=U1YuKqx1xeg',
      embedId: 'U1YuKqx1xeg',
      category: 'box-reveal',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-11',
      thumbnail: 'https://img.youtube.com/vi/U1YuKqx1xeg/maxresdefault.jpg'
    },
    {
      id: 'yt-api-97',
      platform: 'youtube',
      title: 'Can you guess how many Advent Calendars are in these 2 boxes for our friends at Atlas Stationers...',
      description: '#craftingcommunity #adventcalendar #craftsupplies #christmasgift #christmas2025 #stationeryaddict #stationeryaddict #craftboxx',
      url: 'https://www.youtube.com/watch?v=CNLIAlIoAwI',
      embedId: 'CNLIAlIoAwI',
      category: 'box-reveal',
      tags: ['christmas', 'advent calendar'],
      creator: 'CoraCreaCrafts',
      date: '2025-11',
      thumbnail: 'https://img.youtube.com/vi/CNLIAlIoAwI/maxresdefault.jpg'
    },
    {
      id: 'yt-api-98',
      platform: 'youtube',
      title: 'Let’s journal together: Celestial Edition 💫',
      description: '#celestial #celestialadventure #scrapbooking #junkjournal #craftsupplies #journaling #journalwithme #scrapbook #subscriptionbox',
      url: 'https://www.youtube.com/watch?v=NoIqoyINpJw',
      embedId: 'NoIqoyINpJw',
      category: 'box-reveal',
      tags: ['journaling', 'advent calendar', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2025-11',
      thumbnail: 'https://img.youtube.com/vi/NoIqoyINpJw/maxresdefault.jpg'
    },
    {
      id: 'yt-api-99',
      platform: 'youtube',
      title: 'My take on an alchemy-inspired journal page ✨⚗️',
      description: '#alchemistry #alchemy #mindfuljournaling #journaling #creativejournaling #scrapbooking #craftsupplies #scrapbook #bulletjournal #junkjournal',
      url: 'https://www.youtube.com/watch?v=J7LmXh8JKwc',
      embedId: 'J7LmXh8JKwc',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-11',
      thumbnail: 'https://img.youtube.com/vi/J7LmXh8JKwc/maxresdefault.jpg'
    },
    {
      id: 'yt-api-100',
      platform: 'youtube',
      title: 'A peek behind the scenes of how our Dream Calendar came to life 💫',
      description: '#craftingcommunity #journaling #adventcalendar #gift #giftsforher #christmas #craftsupplies #stationeryaddict #scrapbooking #scrapbook',
      url: 'https://www.youtube.com/watch?v=zqt-GNK3mf0',
      embedId: 'zqt-GNK3mf0',
      category: 'journal-spread',
      tags: ['journaling', 'christmas', 'advent calendar'],
      creator: 'CoraCreaCrafts',
      date: '2025-11',
      thumbnail: 'https://img.youtube.com/vi/zqt-GNK3mf0/maxresdefault.jpg'
    },
    {
      id: 'yt-api-101',
      platform: 'youtube',
      title: '💫 Craft heaven in the cutest celestial drawers ever 🌙',
      description: '#craftingcommunity #nashville #creativejournaling #junkjournal #scrapbook #craftsupplies #washitape #sticker #paperset',
      url: 'https://www.youtube.com/watch?v=YNutC8Z4TiU',
      embedId: 'YNutC8Z4TiU',
      category: 'journal-spread',
      tags: ['washi tape', 'journaling', 'stickers'],
      creator: 'CoraCreaCrafts',
      date: '2025-11',
      thumbnail: 'https://img.youtube.com/vi/YNutC8Z4TiU/maxresdefault.jpg'
    },
    {
      id: 'yt-api-102',
      platform: 'youtube',
      title: 'Creative Corner Nashville is now open! We would love to see as many of you visit as possible!!',
      description: 'Come and visit us at: 501 Metroplex Dr, Suite 117,  Nashville, TN 37211  #nashville #nashvillelive #smallbusiness #fypシ゚ #stationerystore #junkjournal #vintagejournaling #vintagecrafts #creativeescape',
      url: 'https://www.youtube.com/watch?v=Efg8Ao7TKIM',
      embedId: 'Efg8Ao7TKIM',
      category: 'journal-spread',
      tags: ['journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-11',
      thumbnail: 'https://img.youtube.com/vi/Efg8Ao7TKIM/maxresdefault.jpg'
    },
    {
      id: 'yt-api-103',
      platform: 'youtube',
      title: 'Come with me to host our creative journaling workshop inspired by all things fantasy at Green Hills!',
      description: '#craftingcommunity #dayinthelife #fypシ゚viral #journaling #fantasycraft #journalyourway #minivlog #stationeryaddict #craftworkshops #nashvillelive #nashville',
      url: 'https://www.youtube.com/watch?v=_7ke39zyzuU',
      embedId: '_7ke39zyzuU',
      category: 'journal-spread',
      tags: ['journaling', 'fantasy'],
      creator: 'CoraCreaCrafts',
      date: '2025-11',
      thumbnail: 'https://img.youtube.com/vi/_7ke39zyzuU/maxresdefault.jpg'
    },
    {
      id: 'yt-api-104',
      platform: 'youtube',
      title: 'pov: trying not to keep a Celestial Box for myself while packing yours 💫',
      description: '#craftingcommunity #journaling #craftbox #creativejournaling #scrapbooking #craftkit #junkjournal #craftsupplies #celestial',
      url: 'https://www.youtube.com/watch?v=o47zAN3cy6A',
      embedId: 'o47zAN3cy6A',
      category: 'box-reveal',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-11',
      thumbnail: 'https://img.youtube.com/vi/o47zAN3cy6A/maxresdefault.jpg'
    },
    {
      id: 'yt-api-105',
      platform: 'youtube',
      title: 'I bet you\'ve never used a rolling pin like this before 👀',
      description: '#craftingcommunity #journaling #mindfuljournaling #funnyvideos #fypage #craftsupplies #stationeryaddict #rolling #vintagejournaling #vintagecrafts',
      url: 'https://www.youtube.com/watch?v=v0iP7IYHDnY',
      embedId: 'v0iP7IYHDnY',
      category: 'journal-spread',
      tags: ['journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-11',
      thumbnail: 'https://img.youtube.com/vi/v0iP7IYHDnY/maxresdefault.jpg'
    },
    {
      id: 'yt-api-106',
      platform: 'youtube',
      title: 'Happy Halloween, crafters! I can’t believe spooky season is almost over it’s  one of my favorites 🎃',
      description: '#craftingcommunity #halloweencraft #halloween #halloween2025 #craftsupplies #junkjournal #scrapbook #bulletjournalling #vintagejournaling #spookyseason',
      url: 'https://www.youtube.com/watch?v=RhwAK2BqLKA',
      embedId: 'RhwAK2BqLKA',
      category: 'journal-spread',
      tags: ['journaling', 'vintage', 'halloween'],
      creator: 'CoraCreaCrafts',
      date: '2025-10',
      thumbnail: 'https://img.youtube.com/vi/RhwAK2BqLKA/maxresdefault.jpg'
    },
    {
      id: 'yt-api-107',
      platform: 'youtube',
      title: 'How I Brought the Dream Calendar to Life ✨ From Idea to Finished Box 🧚🏻🤍',
      description: '#christmas #gifting #creativejournaling #craftbox #journal #junkjournal #craftsupplies #mindfuljournaling #vintagejournaling #vintagecrafts #fypシ #fypシ゚viral',
      url: 'https://www.youtube.com/watch?v=eJsmG_Bctio',
      embedId: 'eJsmG_Bctio',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'christmas'],
      creator: 'CoraCreaCrafts',
      date: '2025-10',
      thumbnail: 'https://img.youtube.com/vi/eJsmG_Bctio/maxresdefault.jpg'
    },
    {
      id: 'yt-api-108',
      platform: 'youtube',
      title: 'Wandering into Fairyworld this Autumn with these beautiful supplies!',
      description: '#fairyworld #fairytales #craftingcommunity #journaling #scrapbooking #junkjournal #atc #craftsupplies #journal #mindfuljournaling',
      url: 'https://www.youtube.com/watch?v=--VhMNEvIDU',
      embedId: '--VhMNEvIDU',
      category: 'journal-spread',
      tags: ['journaling', 'autumn'],
      creator: 'CoraCreaCrafts',
      date: '2025-10',
      thumbnail: 'https://img.youtube.com/vi/--VhMNEvIDU/maxresdefault.jpg'
    },
    {
      id: 'yt-api-109',
      platform: 'youtube',
      title: 'I\'m still obsessed with the little treats from our Dream Calendar✨',
      description: '#adventcalendar #creativegifts #gifting #fypシ #christmas #scrapbooking #journaling #craftingcommunity #craftbox #stationeryaddict',
      url: 'https://www.youtube.com/watch?v=9GmgdU3Kbcg',
      embedId: '9GmgdU3Kbcg',
      category: 'box-reveal',
      tags: ['journaling', 'christmas', 'advent calendar'],
      creator: 'CoraCreaCrafts',
      date: '2025-10',
      thumbnail: 'https://img.youtube.com/vi/9GmgdU3Kbcg/maxresdefault.jpg'
    },
    {
      id: 'yt-api-110',
      platform: 'youtube',
      title: 'A spooky little Halloween journal spread 👻✨🎃',
      description: '#craftingcommunity #journaling #halloweencraft #halloween #junkjournal #bulletjournal #journalpage #scrapbooking',
      url: 'https://www.youtube.com/watch?v=Yvf-uuq3xHE',
      embedId: 'Yvf-uuq3xHE',
      category: 'journal-spread',
      tags: ['journaling', 'halloween'],
      creator: 'CoraCreaCrafts',
      date: '2025-10',
      thumbnail: 'https://img.youtube.com/vi/Yvf-uuq3xHE/maxresdefault.jpg'
    },
    {
      id: 'yt-api-111',
      platform: 'youtube',
      title: 'This is our most asked about pen 👀 Here\'s exactly how to use it so your journaling feels effortless✨',
      description: '#fountainpen #pens #craftingcommunity #coracreacrafts #writingcommunity #writingtips #notebookaddict #stationeryaddict #creativejournaling',
      url: 'https://www.youtube.com/watch?v=ehPh2ARQLDo',
      embedId: 'ehPh2ARQLDo',
      category: 'tutorial',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-10',
      thumbnail: 'https://img.youtube.com/vi/ehPh2ARQLDo/maxresdefault.jpg'
    },
    {
      id: 'yt-api-112',
      platform: 'youtube',
      title: 'Unbox our Alchemy Curiosities Box with us! This one\'s for anyone who loves science, magic & mystery!',
      description: '#alchemy #alchemistry #craftingcommunity #journaling #craftbox #craftkit #stationeryaddict #junkjournal',
      url: 'https://www.youtube.com/watch?v=E2YB2Fnc754',
      embedId: 'E2YB2Fnc754',
      category: 'unboxing',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-10',
      thumbnail: 'https://img.youtube.com/vi/E2YB2Fnc754/maxresdefault.jpg'
    },
    {
      id: 'yt-api-113',
      platform: 'youtube',
      title: 'SPOILER ALERT:Our 2025 Advent Calendars are here! This year, they come in 2 sizes small and large! 🎄',
      description: '#craftingcommunity #christmas #adventcalendar2025 #adventcalendar #creativejournaling #stationeryaddict #vintagecrafts #craftbox #craftsupplies #atc',
      url: 'https://www.youtube.com/watch?v=Snlska-IU54',
      embedId: 'Snlska-IU54',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'christmas', 'advent calendar'],
      creator: 'CoraCreaCrafts',
      date: '2025-10',
      thumbnail: 'https://img.youtube.com/vi/Snlska-IU54/maxresdefault.jpg'
    },
    {
      id: 'yt-api-114',
      platform: 'youtube',
      title: 'The peak at our Halloween Tapes is here! What\'s your favorite?👀🎃🕸️',
      description: '#halloween #halloweencraft #halloween2025 #spookyseason #craftingcommunity #creativejournaling #scrapbooking #washitape #pettape',
      url: 'https://www.youtube.com/watch?v=CfHBOY1kI-w',
      embedId: 'CfHBOY1kI-w',
      category: 'journal-spread',
      tags: ['washi tape', 'journaling', 'halloween'],
      creator: 'CoraCreaCrafts',
      date: '2025-10',
      thumbnail: 'https://img.youtube.com/vi/CfHBOY1kI-w/maxresdefault.jpg'
    },
    {
      id: 'yt-api-115',
      platform: 'youtube',
      title: 'Calling All Nashville Crafters! Our Brand New Store Opens Oct 24 🎉✨ We can\'t wait to see you there!',
      description: 'Cora\'s Creative Corner  501 Metroplex Dr, Suite 117,  Nashville, TN 37211  Follow @creativecorner_nashville for updates & information about our grand opening!  #nashville #nashvillecrafting #nashville',
      url: 'https://www.youtube.com/watch?v=kf7Yn5azhxc',
      embedId: 'kf7Yn5azhxc',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2025-10',
      thumbnail: 'https://img.youtube.com/vi/kf7Yn5azhxc/maxresdefault.jpg'
    },
    {
      id: 'yt-api-116',
      platform: 'youtube',
      title: 'Our Founder’s Top Halloween Must-Haves for This Spooky Crafting Season 🕸️',
      description: '#halloween #halloweencraft #creativejournaling #creativeescape #scrapbooking #craftbox #journal #junkjournal #spookyseason',
      url: 'https://www.youtube.com/watch?v=gLraHNgt3LE',
      embedId: 'gLraHNgt3LE',
      category: 'box-reveal',
      tags: ['journaling', 'halloween'],
      creator: 'CoraCreaCrafts',
      date: '2025-10',
      thumbnail: 'https://img.youtube.com/vi/gLraHNgt3LE/maxresdefault.jpg'
    },
    {
      id: 'yt-api-117',
      platform: 'youtube',
      title: 'Unboxing the Celestial Vintage Craft Box, where vintage dreams meet the stars! ✨',
      description: '#celestial #celestialadventure #craftsupplies #creativejournaling #journaling #mindfuljournaling #craftbox #craftingcommunity #craftkit #scrapbooking',
      url: 'https://www.youtube.com/watch?v=UqF605Y9KfI',
      embedId: 'UqF605Y9KfI',
      category: 'unboxing',
      tags: ['journaling', 'vintage', 'advent calendar', 'unboxing'],
      creator: 'CoraCreaCrafts',
      date: '2025-10',
      thumbnail: 'https://img.youtube.com/vi/UqF605Y9KfI/maxresdefault.jpg'
    },
    {
      id: 'yt-api-118',
      platform: 'youtube',
      title: 'This spread feels like stepping into the world of French Royalty 🇫🇷 👑',
      description: '#mindfuljournaling #creativejournaling #journaling #scrapbooking #vintagecrafts #journal #spread',
      url: 'https://www.youtube.com/watch?v=hTQdnzObuIk',
      embedId: 'hTQdnzObuIk',
      category: 'journal-spread',
      tags: ['journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-09',
      thumbnail: 'https://img.youtube.com/vi/hTQdnzObuIk/maxresdefault.jpg'
    },
    {
      id: 'yt-api-119',
      platform: 'youtube',
      title: '“fairyworld is finally here 🧚✨ I’ve been so excited to share this with you all🤍',
      description: '#creativejournaling #journaling #craftbox #craftingcommunity #craftkit #vintagejournaling #journaling #plannercommunity #unboxing',
      url: 'https://www.youtube.com/watch?v=NR9i8gZIqoE',
      embedId: 'NR9i8gZIqoE',
      category: 'unboxing',
      tags: ['journaling', 'vintage', 'unboxing'],
      creator: 'CoraCreaCrafts',
      date: '2025-09',
      thumbnail: 'https://img.youtube.com/vi/NR9i8gZIqoE/maxresdefault.jpg'
    },
    {
      id: 'yt-api-120',
      platform: 'youtube',
      title: 'Ever wonder what journaling is really about? Keep watching! ✨',
      description: '#journaling #creativejournaling #creativeescape #meditation #relaxing #craftingcommunity #metime #vintagejournaling',
      url: 'https://www.youtube.com/watch?v=l1iFTyz7Nm4',
      embedId: 'l1iFTyz7Nm4',
      category: 'journal-spread',
      tags: ['journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-08',
      thumbnail: 'https://img.youtube.com/vi/l1iFTyz7Nm4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-121',
      platform: 'youtube',
      title: 'You asked and you shall receive! Here are some of my favorite pieces from the Legends & Lore box!',
      description: '#unboxingvideo #craftsupplies #fantasyart #fantasycraft #astrology #cosmicjourney #zodiacsigns #vintagejournaling #scrapbooking #stationeryaddict',
      url: 'https://www.youtube.com/watch?v=Uozad6aGQso',
      embedId: 'Uozad6aGQso',
      category: 'unboxing',
      tags: ['journaling', 'vintage', 'fantasy', 'unboxing'],
      creator: 'CoraCreaCrafts',
      date: '2025-08',
      thumbnail: 'https://img.youtube.com/vi/Uozad6aGQso/maxresdefault.jpg'
    },
    {
      id: 'yt-api-122',
      platform: 'youtube',
      title: 'Taking a little trip to Versailles with this spread I made using our Royal France Vintage Craft Box!',
      description: '#france #royalfrance #french #creativejournaling #creativeescape #craftingcommunity #journal #journalspreads #scrapbooking',
      url: 'https://www.youtube.com/watch?v=4PO88VqRDWk',
      embedId: '4PO88VqRDWk',
      category: 'box-reveal',
      tags: ['journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-08',
      thumbnail: 'https://img.youtube.com/vi/4PO88VqRDWk/maxresdefault.jpg'
    },
    {
      id: 'yt-api-123',
      platform: 'youtube',
      title: 'Mermaid Journal With Me 🧜‍♀️✨🐚🫧🐬',
      description: '#mermaid #mermaidcommunity #mermaidsticker #oceanwaves #oceanlife #ocean #junkjournaling #junkjournal #scrapbooking #journal #craftingcommunity #sea',
      url: 'https://www.youtube.com/watch?v=J6Hjld1ufF8',
      embedId: 'J6Hjld1ufF8',
      category: 'journal-spread',
      tags: ['journaling', 'stickers'],
      creator: 'CoraCreaCrafts',
      date: '2025-08',
      thumbnail: 'https://img.youtube.com/vi/J6Hjld1ufF8/maxresdefault.jpg'
    },
    {
      id: 'yt-api-124',
      platform: 'youtube',
      title: 'Have you tried our new pastes yet? Watching the colors swirl and textures pop is seriously addictive',
      description: '#paste #mixedmediaart #mixedmedia #texture #creativejournaling #artsandcrafts #journal #craftingcommunity #craftsupplies',
      url: 'https://www.youtube.com/watch?v=eaD8f272UK4',
      embedId: 'eaD8f272UK4',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-08',
      thumbnail: 'https://img.youtube.com/vi/eaD8f272UK4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-125',
      platform: 'youtube',
      title: 'Journal with me to make this ocean-inspired spread as summer slowly turns into autumn 💙',
      description: '#creativejournaling #vintagejournaling #shipart #ocean #journalingcommunity #craftwithme #stationeryaddict #artisticinspiration #nautical',
      url: 'https://www.youtube.com/watch?v=EuHrBe4E10E',
      embedId: 'EuHrBe4E10E',
      category: 'journal-spread',
      tags: ['journaling', 'vintage', 'autumn'],
      creator: 'CoraCreaCrafts',
      date: '2025-08',
      thumbnail: 'https://img.youtube.com/vi/EuHrBe4E10E/maxresdefault.jpg'
    },
    {
      id: 'yt-api-126',
      platform: 'youtube',
      title: 'Why throw away a box when you can upcycle it? Watch us transform this box with our lock & key items!',
      description: '#upcycling #box #diy #diycrafts #vintagejournaling #craftingcommunity #upcycledcrafts #vintagestyle',
      url: 'https://www.youtube.com/watch?v=C7kEF1lkJUA',
      embedId: 'C7kEF1lkJUA',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2025-08',
      thumbnail: 'https://img.youtube.com/vi/C7kEF1lkJUA/maxresdefault.jpg'
    },
    {
      id: 'yt-api-127',
      platform: 'youtube',
      title: 'Do you keep a Travel Journal on your adventures? Our new kit will now come everywhere with me ❤️',
      description: '#traveljournal #traveling #travel #adventure #memories #vintagejournaling #vintagecrafts #creativejournaling #creativeescape',
      url: 'https://www.youtube.com/watch?v=1mWTnforI-c',
      embedId: '1mWTnforI-c',
      category: 'journal-spread',
      tags: ['journaling', 'vintage', 'advent calendar'],
      creator: 'CoraCreaCrafts',
      date: '2025-08',
      thumbnail: 'https://img.youtube.com/vi/1mWTnforI-c/maxresdefault.jpg'
    },
    {
      id: 'yt-api-128',
      platform: 'youtube',
      title: 'So excited to give you a closer look at our Mid Year Dream Calendar packaging! 💗',
      description: '#adventcalendar #calendar #junkjournal  #crafts  #diy  #vintagecrafts  #surprisebox  #giftideas  #creativeinspiration',
      url: 'https://www.youtube.com/watch?v=gVfqhZ56--w',
      embedId: 'gVfqhZ56--w',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'advent calendar', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2025-08',
      thumbnail: 'https://img.youtube.com/vi/gVfqhZ56--w/maxresdefault.jpg'
    },
    {
      id: 'yt-api-129',
      platform: 'youtube',
      title: 'August’s vintage craft box is here, and it’s a special one... meet Royal France!',
      description: '#royalfrance #france #french #creativejournaling #journaling #craftingcommunity #journal #vintagejournaling #unboxing #subscription #subscriptionunboxing',
      url: 'https://www.youtube.com/watch?v=Z3Yx7CFsP8w',
      embedId: 'Z3Yx7CFsP8w',
      category: 'unboxing',
      tags: ['journaling', 'vintage', 'subscription box', 'unboxing'],
      creator: 'CoraCreaCrafts',
      date: '2025-08',
      thumbnail: 'https://img.youtube.com/vi/Z3Yx7CFsP8w/maxresdefault.jpg'
    },
    {
      id: 'yt-api-130',
      platform: 'youtube',
      title: 'Mini Journal With Me: Step-by-step Scotland-themed journaling tutorial!',
      description: '#creativejournaling #minijournal #vintagejournaling #tutorial #junkjournal #junkjournaling #scrapbooking #craftingcommunity #scotland #scottish',
      url: 'https://www.youtube.com/watch?v=wzUJreFNyBk',
      embedId: 'wzUJreFNyBk',
      category: 'tutorial',
      tags: ['journaling', 'vintage', 'tutorial'],
      creator: 'CoraCreaCrafts',
      date: '2025-08',
      thumbnail: 'https://img.youtube.com/vi/wzUJreFNyBk/maxresdefault.jpg'
    },
    {
      id: 'yt-api-131',
      platform: 'youtube',
      title: 'Cute scissors that work great and look even better, perfect for your craft projects! ✨',
      description: '#creativejournaling #journaling #scissors #vintagejournaling #junkjournal #cuttingskills #journal #craftsupplies #stationeryaddict  Evening Fall Harp by Kevin MacLeod is licensed under a Creative Comm',
      url: 'https://www.youtube.com/watch?v=AX6DELI8bvw',
      embedId: 'AX6DELI8bvw',
      category: 'journal-spread',
      tags: ['journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-08',
      thumbnail: 'https://img.youtube.com/vi/AX6DELI8bvw/maxresdefault.jpg'
    },
    {
      id: 'yt-api-132',
      platform: 'youtube',
      title: 'Create an Outer Spaced themed spread with me! 👽🚀🛸☄️🪐',
      description: '#creativejournaling #journaling #scrapbooking #craftingcommunity #journal #space #spacecraft #spaceexploration #outerspace',
      url: 'https://www.youtube.com/watch?v=YQ9xNsu9dHo',
      embedId: 'YQ9xNsu9dHo',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-08',
      thumbnail: 'https://img.youtube.com/vi/YQ9xNsu9dHo/maxresdefault.jpg'
    },
    {
      id: 'yt-api-133',
      platform: 'youtube',
      title: 'Journal With Me: Academia Aesthetic',
      description: 'Have you tried our new Academia Supplies yet? Check them out here - https://coracreacrafts.com/products/academia-box',
      url: 'https://www.youtube.com/watch?v=dU3UF7k5-W8',
      embedId: 'dU3UF7k5-W8',
      category: 'box-reveal',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-07',
      thumbnail: 'https://img.youtube.com/vi/dU3UF7k5-W8/maxresdefault.jpg'
    },
    {
      id: 'yt-api-134',
      platform: 'youtube',
      title: 'What are your favorite places to craft in? I feel so much inspiration at my local coffee shop ☕️',
      description: '#creativejournaling #scrapbooking #coffeelover #coffeecraft #journal #craftingcommunity #vintagejournaling #junkjournal',
      url: 'https://www.youtube.com/watch?v=yvrSseHl_kc',
      embedId: 'yvrSseHl_kc',
      category: 'journal-spread',
      tags: ['journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-07',
      thumbnail: 'https://img.youtube.com/vi/yvrSseHl_kc/maxresdefault.jpg'
    },
    {
      id: 'yt-api-135',
      platform: 'youtube',
      title: 'Sometimes the best spreads aren’t perfect; they’re abit messy that’s why I love our Archive Sprays!',
      description: '#junkjournaling #journaling #creativejournaling #vintagejournaling #spray #spraypaint #diycrafts #craftingcommunity',
      url: 'https://www.youtube.com/watch?v=m6BxG0V3_RQ',
      embedId: 'm6BxG0V3_RQ',
      category: 'tutorial',
      tags: ['journaling', 'vintage', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2025-07',
      thumbnail: 'https://img.youtube.com/vi/m6BxG0V3_RQ/maxresdefault.jpg'
    },
    {
      id: 'yt-api-136',
      platform: 'youtube',
      title: 'The second edition of Legends & Lore: The Fantasy Collection is here, The Astronomer’s Legacy ✨',
      description: 'We’ve once again teamed up with the wonderfully talented Storm Sparrow’s Inn to craft a box inspired by ancient star myths, quiet wonder, and the epic magic of the night sky! Who else is excited for t',
      url: 'https://www.youtube.com/watch?v=Mb46H7ULTqk',
      embedId: 'Mb46H7ULTqk',
      category: 'box-reveal',
      tags: ['fantasy'],
      creator: 'CoraCreaCrafts',
      date: '2025-07',
      thumbnail: 'https://img.youtube.com/vi/Mb46H7ULTqk/maxresdefault.jpg'
    },
    {
      id: 'yt-api-137',
      platform: 'youtube',
      title: 'Join me to make a beautiful centre fold card, perfect for summer and as a cute gift!',
      description: '#centrefoldcard #cardmaking #diycrafts #diyprojects #nautical #summercraft #creativejournaling #vintagecrafts',
      url: 'https://www.youtube.com/watch?v=Ncd_k6-pfF4',
      embedId: 'Ncd_k6-pfF4',
      category: 'tutorial',
      tags: ['journaling', 'vintage', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2025-07',
      thumbnail: 'https://img.youtube.com/vi/Ncd_k6-pfF4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-138',
      platform: 'youtube',
      title: 'Introducing our newest Curiosities Box theme: Locks & Keys, full of mystery and vintage charm 🔐🗝️',
      description: '#unboxingvideo #subscription #creativejournaling #vintagejournaling #craftingcommunity #lock #key',
      url: 'https://www.youtube.com/watch?v=qzj07ZumCFU',
      embedId: 'qzj07ZumCFU',
      category: 'unboxing',
      tags: ['journaling', 'vintage', 'subscription box', 'unboxing'],
      creator: 'CoraCreaCrafts',
      date: '2025-07',
      thumbnail: 'https://img.youtube.com/vi/qzj07ZumCFU/maxresdefault.jpg'
    },
    {
      id: 'yt-api-139',
      platform: 'youtube',
      title: 'Welcome! I\'m Coralie, founder of CoraCreaCrafts, so glad you\'re here 💛',
      description: '#creativejournaling #founder #smallbusiness #smallbusinessowner #craftingcommunity #journaling #vintagejournaling #stationeryaddict',
      url: 'https://www.youtube.com/watch?v=6XfWXjtEcVE',
      embedId: '6XfWXjtEcVE',
      category: 'journal-spread',
      tags: ['journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-07',
      thumbnail: 'https://img.youtube.com/vi/6XfWXjtEcVE/maxresdefault.jpg'
    },
    {
      id: 'yt-api-140',
      platform: 'youtube',
      title: 'Our first fan voted Vintage Craft Box is here.... meet our Outer Space Box! ☄️🚀👽🪐🛸',
      description: '#unboxing #subscription #subscriptionunboxing #vintagejournaling #creativejournaling #outerspace #spacecraft',
      url: 'https://www.youtube.com/watch?v=QOPfU8t_Q0w',
      embedId: 'QOPfU8t_Q0w',
      category: 'unboxing',
      tags: ['journaling', 'vintage', 'subscription box', 'unboxing'],
      creator: 'CoraCreaCrafts',
      date: '2025-07',
      thumbnail: 'https://img.youtube.com/vi/QOPfU8t_Q0w/maxresdefault.jpg'
    },
    {
      id: 'yt-api-141',
      platform: 'youtube',
      title: 'A quiet ritual: vintage scraps, slow journaling, and a moment to myself ✨',
      description: '#creativejournaling #journaling #journalwithme #relaxing #junkjournaling #metime #witchy',
      url: 'https://www.youtube.com/watch?v=h_hS0B6dE28',
      embedId: 'h_hS0B6dE28',
      category: 'journal-spread',
      tags: ['journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-07',
      thumbnail: 'https://img.youtube.com/vi/h_hS0B6dE28/maxresdefault.jpg'
    },
    {
      id: 'yt-api-142',
      platform: 'youtube',
      title: '4th of July Sale! Up to 30% off, Buy2get1 free on stickers & tapes! Free digital kit on $100+ orders',
      description: '#creativejournaling #4thofjuly #independenceday #starsandstripesforever #vintagejournaling #craftingcommunity #sale #holidaysale #stationeryaddict',
      url: 'https://www.youtube.com/watch?v=2isGJCq3TyY',
      embedId: '2isGJCq3TyY',
      category: 'journal-spread',
      tags: ['journaling', 'vintage', 'stickers'],
      creator: 'CoraCreaCrafts',
      date: '2025-07',
      thumbnail: 'https://img.youtube.com/vi/2isGJCq3TyY/maxresdefault.jpg'
    },
    {
      id: 'yt-api-143',
      platform: 'youtube',
      title: 'Junk Journal With Me - Deep Sea Edition 💙',
      description: '#sea #oceanwaves #ocean #creativejournaling #junkjournal #junkjournaling #scrapbooking #craftingcommunity #marinelife #craftsupplies',
      url: 'https://www.youtube.com/watch?v=HKRAyNCLsG4',
      embedId: 'HKRAyNCLsG4',
      category: 'journal-spread',
      tags: ['journaling', 'junk journal'],
      creator: 'CoraCreaCrafts',
      date: '2025-07',
      thumbnail: 'https://img.youtube.com/vi/HKRAyNCLsG4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-144',
      platform: 'youtube',
      title: 'Honestly, our Make Your Own Map Pack is way more addictive than I expected...',
      description: '#journaling #junkjournaling #map #diycrafts #creativeescape #creativejournaling #summercraft #navigation',
      url: 'https://www.youtube.com/watch?v=oVICNCN6IzI',
      embedId: 'oVICNCN6IzI',
      category: 'tutorial',
      tags: ['journaling', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2025-06',
      thumbnail: 'https://img.youtube.com/vi/oVICNCN6IzI/maxresdefault.jpg'
    },
    {
      id: 'yt-api-145',
      platform: 'youtube',
      title: 'POV: You’re unboxing our mid-year dream calendar and it feels like pure magic ✨',
      description: '#calendar #craftingcommunity #craftsupplies #stationeryaddict #papercraft #unboxing #adventcalendar #surprise #vintagejournaling #vintagecrafts',
      url: 'https://www.youtube.com/watch?v=a57C7dIzVoU',
      embedId: 'a57C7dIzVoU',
      category: 'unboxing',
      tags: ['journaling', 'vintage', 'advent calendar', 'unboxing'],
      creator: 'CoraCreaCrafts',
      date: '2025-06',
      thumbnail: 'https://img.youtube.com/vi/a57C7dIzVoU/maxresdefault.jpg'
    },
    {
      id: 'yt-api-146',
      platform: 'youtube',
      title: 'Our navigation-themed digital journal kit is here!💙',
      description: '#navigation #sea #ocean #journaling #digitalart #digitalsubscription #printable #junkjournal #creativejournaling',
      url: 'https://www.youtube.com/watch?v=eVLo9tucQL8',
      embedId: 'eVLo9tucQL8',
      category: 'journal-spread',
      tags: ['journaling', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2025-06',
      thumbnail: 'https://img.youtube.com/vi/eVLo9tucQL8/maxresdefault.jpg'
    },
    {
      id: 'yt-api-147',
      platform: 'youtube',
      title: 'Witchy supplies you didn’t know you needed… until now 🕯️🔮',
      description: '#witchy #witchyvibe #witchycommunity #moody #darkacademia #grimoire #creativejournaling #stationery #junkjournal',
      url: 'https://www.youtube.com/watch?v=0j5tfVyS-7E',
      embedId: '0j5tfVyS-7E',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-06',
      thumbnail: 'https://img.youtube.com/vi/0j5tfVyS-7E/maxresdefault.jpg'
    },
    {
      id: 'yt-api-148',
      platform: 'youtube',
      title: 'Come behind the scenes with us at our latest workshop!',
      description: '#witchy #workshop #craftingcommunity #journaling #creativejournaling #nashville #nashvilleevents #craftworkshops',
      url: 'https://www.youtube.com/watch?v=7WgBOdDrByo',
      embedId: '7WgBOdDrByo',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-06',
      thumbnail: 'https://img.youtube.com/vi/7WgBOdDrByo/maxresdefault.jpg'
    },
    {
      id: 'yt-api-149',
      platform: 'youtube',
      title: 'This Treasure Box is a special one, it’s our 1st theme chosen by the community! Introducing Academia',
      description: '#journaling #moody #springdiaries #darkacademia #academia #gothic #creativejournaling #scrapbooking #library #unboxing #subscription',
      url: 'https://www.youtube.com/watch?v=_lOgCYrS3Kc',
      embedId: '_lOgCYrS3Kc',
      category: 'unboxing',
      tags: ['journaling', 'gothic', 'subscription box', 'unboxing'],
      creator: 'CoraCreaCrafts',
      date: '2025-06',
      thumbnail: 'https://img.youtube.com/vi/_lOgCYrS3Kc/maxresdefault.jpg'
    },
    {
      id: 'yt-api-150',
      platform: 'youtube',
      title: 'Pack a wholesale order with me for my small business! ✨',
      description: '#packanorderwithme #creativejournaling #vintagecrafts #smallbusiness #founder #smallyoutubersupport #smallbusinessowner #craftingcommunity #wholesale',
      url: 'https://www.youtube.com/watch?v=dgJC3D7vqP8',
      embedId: 'dgJC3D7vqP8',
      category: 'journal-spread',
      tags: ['journaling', 'vintage', 'small business'],
      creator: 'CoraCreaCrafts',
      date: '2025-06',
      thumbnail: 'https://img.youtube.com/vi/dgJC3D7vqP8/maxresdefault.jpg'
    },
    {
      id: 'yt-api-151',
      platform: 'youtube',
      title: 'Take a peek inside our 3 unique journals, perfect for vintage lovers, crafters & memory keepers! ✨',
      description: '#journaling #scrapbooking #journal #journalwithme #writingjournals #junkjournal #junkjournaling #coracreacrafts',
      url: 'https://www.youtube.com/watch?v=TtvDw_0TtaU',
      embedId: 'TtvDw_0TtaU',
      category: 'journal-spread',
      tags: ['journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-06',
      thumbnail: 'https://img.youtube.com/vi/TtvDw_0TtaU/maxresdefault.jpg'
    },
    {
      id: 'yt-api-152',
      platform: 'youtube',
      title: 'This is my favorite thing to do when I get back home from a trip 🌍🗺️',
      description: '#journaling #traveling #traveljournal #coracreacrafts #memories #scrapbooking #diycrafts',
      url: 'https://www.youtube.com/watch?v=KSX6xh84xqQ',
      embedId: 'KSX6xh84xqQ',
      category: 'tutorial',
      tags: ['journaling', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2025-06',
      thumbnail: 'https://img.youtube.com/vi/KSX6xh84xqQ/maxresdefault.jpg'
    },
    {
      id: 'yt-api-153',
      platform: 'youtube',
      title: 'New Vintage Craft Box: Navigation theme inspired by sailing & ocean adventures 🌊🗺️',
      description: '#sea #sailing #seajourney #navigation #journaling #creativejournaling #scrapbooking #journal',
      url: 'https://www.youtube.com/watch?v=0kTyZKTUv5U',
      embedId: '0kTyZKTUv5U',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'advent calendar'],
      creator: 'CoraCreaCrafts',
      date: '2025-06',
      thumbnail: 'https://img.youtube.com/vi/0kTyZKTUv5U/maxresdefault.jpg'
    },
    {
      id: 'yt-api-154',
      platform: 'youtube',
      title: 'Took a moment to revisit old favorites—sometimes the best reset is to pause, breathe, and create✨',
      description: '#journaling #journalwithme #coracreacrafts #creativejournaling #creativeescape #relax #craftingcommunity',
      url: 'https://www.youtube.com/watch?v=i13FmC6bTME',
      embedId: 'i13FmC6bTME',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-06',
      thumbnail: 'https://img.youtube.com/vi/i13FmC6bTME/maxresdefault.jpg'
    },
    {
      id: 'yt-api-155',
      platform: 'youtube',
      title: 'Building this business changed my life. It’s been hard, beautiful & the greatest journey, thank you!',
      description: '#founder #smallbusiness #female #femalebusinessowners #entrepreneur #craftingcommunity #scrapbooking',
      url: 'https://www.youtube.com/watch?v=fkPp86CzA30',
      embedId: 'fkPp86CzA30',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2025-06',
      thumbnail: 'https://img.youtube.com/vi/fkPp86CzA30/maxresdefault.jpg'
    },
    {
      id: 'yt-api-156',
      platform: 'youtube',
      title: 'I’m definitely still in my Coquette era 🎀🌸💗',
      description: '#coquette #coquettestyle #vintagejournaling #vintagecrafts #creativejournaling #journalwithme #tutorial',
      url: 'https://www.youtube.com/watch?v=9IzNwgvynaU',
      embedId: '9IzNwgvynaU',
      category: 'tutorial',
      tags: ['journaling', 'vintage', 'tutorial'],
      creator: 'CoraCreaCrafts',
      date: '2025-06',
      thumbnail: 'https://img.youtube.com/vi/9IzNwgvynaU/maxresdefault.jpg'
    },
    {
      id: 'yt-api-157',
      platform: 'youtube',
      title: 'What’s your mermaid name? 🧜‍��️',
      description: '#sea #mermaid #seajourney #journaling #tutorial #creativejournaling #creativeescape #scrapbooking',
      url: 'https://www.youtube.com/watch?v=fzlUOgREkN4',
      embedId: 'fzlUOgREkN4',
      category: 'tutorial',
      tags: ['journaling', 'tutorial'],
      creator: 'CoraCreaCrafts',
      date: '2025-06',
      thumbnail: 'https://img.youtube.com/vi/fzlUOgREkN4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-158',
      platform: 'youtube',
      title: 'I wanted to create a double-page “frame wall” layout  and I’m really happy with how it turned out✨',
      description: '#steampunk #steampunkart #creativejournaling #scrapbooking #creativeescape #junkjournaling',
      url: 'https://www.youtube.com/watch?v=EYlRknHkySQ',
      embedId: 'EYlRknHkySQ',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-06',
      thumbnail: 'https://img.youtube.com/vi/EYlRknHkySQ/maxresdefault.jpg'
    },
    {
      id: 'yt-api-159',
      platform: 'youtube',
      title: 'My Kindle cover collection is definitely growing… this time I wanted to make a coquette-themed one 💗',
      description: '#coquette #kindle #diy #diycrafts #creativejournaling #springdiaries #scrapbooking #craftsupplies #craftingcommunity',
      url: 'https://www.youtube.com/watch?v=tPvvwqoljIE',
      embedId: 'tPvvwqoljIE',
      category: 'tutorial',
      tags: ['journaling', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2025-05',
      thumbnail: 'https://img.youtube.com/vi/tPvvwqoljIE/maxresdefault.jpg'
    },
    {
      id: 'yt-api-160',
      platform: 'youtube',
      title: 'POV: You travel to other worlds with your journal 🖤',
      description: '#journaling #vintagejournaling #vintagecrafts #fantasy #dragon #scrapbooking',
      url: 'https://www.youtube.com/watch?v=55VxP5xMBko',
      embedId: '55VxP5xMBko',
      category: 'journal-spread',
      tags: ['journaling', 'vintage', 'fantasy'],
      creator: 'CoraCreaCrafts',
      date: '2025-05',
      thumbnail: 'https://img.youtube.com/vi/55VxP5xMBko/maxresdefault.jpg'
    },
    {
      id: 'yt-api-161',
      platform: 'youtube',
      title: 'Happy Memorial Day to those of you celebrating today 💙',
      description: 'It’s been so lovely to see so many of you pick up a grab bag and shop the sale this weekend, thank you for all the support. There’s still one more day to treat yourself to a little surprise before the',
      url: 'https://www.youtube.com/watch?v=2MxOxaWaWdc',
      embedId: '2MxOxaWaWdc',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2025-05',
      thumbnail: 'https://img.youtube.com/vi/2MxOxaWaWdc/maxresdefault.jpg'
    },
    {
      id: 'yt-api-162',
      platform: 'youtube',
      title: 'Today felt like the perfect slow journaling day so I pulled out my Coquette Box 🌸 🎀 💗',
      description: '#coquette #coquettestyle #lace #floral #delicate #subscription #craftingcommunity #creativejournaling #vintagejournaling #scrapbooking',
      url: 'https://www.youtube.com/watch?v=AjLqvJuCKBA',
      embedId: 'AjLqvJuCKBA',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2025-05',
      thumbnail: 'https://img.youtube.com/vi/AjLqvJuCKBA/maxresdefault.jpg'
    },
    {
      id: 'yt-api-163',
      platform: 'youtube',
      title: 'My first big craft fest journey packing supplies, hosting a workshop & sharing the joy of crafting❤️',
      description: '#littlecraftfest #littlecraftplace #craftingcommunity #craftingcommunity #creativejournaling #scrapbooking #junkjournaling',
      url: 'https://www.youtube.com/watch?v=oMx-DoyeVE8',
      embedId: 'oMx-DoyeVE8',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-05',
      thumbnail: 'https://img.youtube.com/vi/oMx-DoyeVE8/maxresdefault.jpg'
    },
    {
      id: 'yt-api-164',
      platform: 'youtube',
      title: 'Hope you all enjoyed part 1 of my flip through, here’s part 2! Can’t wait to hear what you think ❤️',
      description: '#flipthrough #scrapbooking #journaling #creativejournaling #junkjournal',
      url: 'https://www.youtube.com/watch?v=uFozzUnMWUg',
      embedId: 'uFozzUnMWUg',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-05',
      thumbnail: 'https://img.youtube.com/vi/uFozzUnMWUg/maxresdefault.jpg'
    },
    {
      id: 'yt-api-165',
      platform: 'youtube',
      title: 'I had so much fun using our Dichotomy supplies to make this large spread ✨',
      description: '#dichotomy #journaling #scrapbooking #junkjournal #creativejournaling #vintagejournaling',
      url: 'https://www.youtube.com/watch?v=0hXsFMgvj50',
      embedId: '0hXsFMgvj50',
      category: 'journal-spread',
      tags: ['journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-05',
      thumbnail: 'https://img.youtube.com/vi/0hXsFMgvj50/maxresdefault.jpg'
    },
    {
      id: 'yt-api-166',
      platform: 'youtube',
      title: 'I\'m so excited to share our new Curiosities Box - Mermaids & Wonders of The Sea 🩵',
      description: '#mermaid #sea #journaling #subscriptionunboxing #subscription #scrapbooking #creativejournaling',
      url: 'https://www.youtube.com/watch?v=kjhNJW6RB8s',
      embedId: 'kjhNJW6RB8s',
      category: 'unboxing',
      tags: ['journaling', 'subscription box', 'unboxing'],
      creator: 'CoraCreaCrafts',
      date: '2025-05',
      thumbnail: 'https://img.youtube.com/vi/kjhNJW6RB8s/maxresdefault.jpg'
    },
    {
      id: 'yt-api-167',
      platform: 'youtube',
      title: 'It\'s been a while since I did a flip through of my journal, I hope this brings you some inspiration✨',
      description: '#journaling #scrapbooking #flipthrough #junkjournaling #junkjournal #creativejournaling',
      url: 'https://www.youtube.com/watch?v=zJSqFcbYejw',
      embedId: 'zJSqFcbYejw',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-05',
      thumbnail: 'https://img.youtube.com/vi/zJSqFcbYejw/maxresdefault.jpg'
    },
    {
      id: 'yt-api-168',
      platform: 'youtube',
      title: 'Our limited edition Mother\'s Day Bundles are now live! Less than 10 left in stock so be quick🌸',
      description: '#mothersday #journaling #giftideas #cardcreations #journaling #scrapbooking #quiettime #junkjournal #keepitcreative #mom',
      url: 'https://www.youtube.com/watch?v=oV7WbGdCmiM',
      embedId: 'oV7WbGdCmiM',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-05',
      thumbnail: 'https://img.youtube.com/vi/oV7WbGdCmiM/maxresdefault.jpg'
    },
    {
      id: 'yt-api-169',
      platform: 'youtube',
      title: 'Meet our newest Vintage Craft Box for May - Coquette 🌸 🎀 💗',
      description: '#subscriptionunboxing #craftingcommunity #coquette #coquettecore #coquettestyle #creativejournaling #junkjournal #scrapbooking',
      url: 'https://www.youtube.com/watch?v=WUsvT_oV6vM',
      embedId: 'WUsvT_oV6vM',
      category: 'unboxing',
      tags: ['journaling', 'vintage', 'subscription box', 'unboxing'],
      creator: 'CoraCreaCrafts',
      date: '2025-05',
      thumbnail: 'https://img.youtube.com/vi/WUsvT_oV6vM/maxresdefault.jpg'
    },
    {
      id: 'yt-api-170',
      platform: 'youtube',
      title: 'When someone jokingly offered to send haggis I couldn’t resist the challenge! 🏴󠁧󠁢󠁳󠁣󠁴󠁿',
      description: '#coracreacrafts #scrapbooking #vintagejournaling #journaling #scotland #haggis',
      url: 'https://www.youtube.com/watch?v=Uu-jPCSSBcQ',
      embedId: 'Uu-jPCSSBcQ',
      category: 'journal-spread',
      tags: ['journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-05',
      thumbnail: 'https://img.youtube.com/vi/Uu-jPCSSBcQ/maxresdefault.jpg'
    },
    {
      id: 'yt-api-171',
      platform: 'youtube',
      title: 'Gentle reminder: joy can be found in the simplest moments like sunshine and your favorite supplies❤️',
      description: 'Nothing beats that feeling of being fully in the moment, just you and your creativity!  #founder #smallbusiness #craftingcommunity #craftingsupplies #junkjournal #craftingcommunity #journaling',
      url: 'https://www.youtube.com/watch?v=86ebHxH5ITY',
      embedId: '86ebHxH5ITY',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-05',
      thumbnail: 'https://img.youtube.com/vi/86ebHxH5ITY/maxresdefault.jpg'
    },
    {
      id: 'yt-api-172',
      platform: 'youtube',
      title: 'Meet our Witches & Potions Treasure Box 🖤',
      description: '#subscriptionunboxing #subscription #coracreacrafts #craftingcommunity #witch #witches #magic #potions #gothic #scrapbooking',
      url: 'https://www.youtube.com/watch?v=15PEMcnMFGw',
      embedId: '15PEMcnMFGw',
      category: 'unboxing',
      tags: ['gothic', 'subscription box', 'unboxing'],
      creator: 'CoraCreaCrafts',
      date: '2025-04',
      thumbnail: 'https://img.youtube.com/vi/15PEMcnMFGw/maxresdefault.jpg'
    },
    {
      id: 'yt-api-173',
      platform: 'youtube',
      title: 'Make an Alice in Wonderland inspired kindle cover with me 🃏',
      description: '#alice #coracreacrafts #kindle #diy #diycrafts #creativeescape #aliceinwonderland #books #booklover #reading',
      url: 'https://www.youtube.com/watch?v=KnFPS4YzsmU',
      embedId: 'KnFPS4YzsmU',
      category: 'tutorial',
      tags: ['alice in wonderland', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2025-04',
      thumbnail: 'https://img.youtube.com/vi/KnFPS4YzsmU/maxresdefault.jpg'
    },
    {
      id: 'yt-api-174',
      platform: 'youtube',
      title: 'I designed this exclusive tape just for The Little Craft Place!',
      description: 'This tape is inspired by their beautiful store and the love they pour into everything they do. We named it Little Craft Love, a little tribute to their style, products and the community they’ve built.',
      url: 'https://www.youtube.com/watch?v=pGC06yySTec',
      embedId: 'pGC06yySTec',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2025-04',
      thumbnail: 'https://img.youtube.com/vi/pGC06yySTec/maxresdefault.jpg'
    },
    {
      id: 'yt-api-175',
      platform: 'youtube',
      title: 'I spotted The Loch Ness Monster! Okay, maybe it’s just a sticker from our Scotland  Box😅',
      description: '#subscriptionunboxing #subscribe #fyp #coracreacrafts #creativejournaling #scrapbooking',
      url: 'https://www.youtube.com/watch?v=S565gvNyXnY',
      embedId: 'S565gvNyXnY',
      category: 'unboxing',
      tags: ['journaling', 'subscription box', 'stickers', 'unboxing'],
      creator: 'CoraCreaCrafts',
      date: '2025-04',
      thumbnail: 'https://img.youtube.com/vi/S565gvNyXnY/maxresdefault.jpg'
    },
    {
      id: 'yt-api-176',
      platform: 'youtube',
      title: 'That moment you realize your tape is giving Chappell Roan vibes... so, a spread had to happen ✨',
      description: '#chappellroan #goodluck #washitape #fyp #scrapbooking #creativejournaling',
      url: 'https://www.youtube.com/watch?v=GljFGuvbkpQ',
      embedId: 'GljFGuvbkpQ',
      category: 'journal-spread',
      tags: ['washi tape', 'journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-04',
      thumbnail: 'https://img.youtube.com/vi/GljFGuvbkpQ/maxresdefault.jpg'
    },
    {
      id: 'yt-api-177',
      platform: 'youtube',
      title: 'I’m so excited to share my passion for mini journaling with you at The Little Craft Fest this week!',
      description: 'If you’ve ever wanted to try journaling or explore new ways to express your creativity, this workshop is for you! Date: Friday, April 25th Time: 7:30 PM - 9:00 PM Location: Little Craft Fest, Houston,',
      url: 'https://www.youtube.com/watch?v=Zf0fzvQW0wY',
      embedId: 'Zf0fzvQW0wY',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-04',
      thumbnail: 'https://img.youtube.com/vi/Zf0fzvQW0wY/maxresdefault.jpg'
    },
    {
      id: 'yt-api-178',
      platform: 'youtube',
      title: 'Spring vibes with our Easter Digital Kit – a peaceful, creative spread to welcome the season 🌸💛',
      description: '#spring #easter #sunday #creativejournaling #digitalkit #scrapbooking #coracreacrafts',
      url: 'https://www.youtube.com/watch?v=rgXgmSP_7LA',
      embedId: 'rgXgmSP_7LA',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-04',
      thumbnail: 'https://img.youtube.com/vi/rgXgmSP_7LA/maxresdefault.jpg'
    },
    {
      id: 'yt-api-179',
      platform: 'youtube',
      title: 'I decided to grab some flowers and make my own bouquets to romanticize Spring 💐',
      description: 'What’s your favorite way to slow down and enjoy the season? #springvibes  #romanticizespring #weekend #reset #fyp',
      url: 'https://www.youtube.com/watch?v=13ISP_mWniQ',
      embedId: '13ISP_mWniQ',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2025-04',
      thumbnail: 'https://img.youtube.com/vi/13ISP_mWniQ/maxresdefault.jpg'
    },
    {
      id: 'yt-api-180',
      platform: 'youtube',
      title: 'I loved creating this spread and layering textures that feel like they’ve lived a life before 🖤',
      description: 'Steampunk has always fascinated me, there’s something so captivating about the blend of history and imagination. The worn metals, mechanical details, and Victorian flair feel like a peek into an alter',
      url: 'https://www.youtube.com/watch?v=IZESvslEna4',
      embedId: 'IZESvslEna4',
      category: 'journal-spread',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2025-04',
      thumbnail: 'https://img.youtube.com/vi/IZESvslEna4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-181',
      platform: 'youtube',
      title: 'Wasn’t planning a sale but here we are! Get up to 20% off some of our most-loved pieces now❤️',
      description: 'The sale ends April 16th at 11:59 PM CST and the discount is automatically applied at checkout.  Thank you so much for your continued support, I hope this brings a little joy to your desk 💛',
      url: 'https://www.youtube.com/watch?v=EaYtvR-TMqY',
      embedId: 'EaYtvR-TMqY',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2025-04',
      thumbnail: 'https://img.youtube.com/vi/EaYtvR-TMqY/maxresdefault.jpg'
    },
    {
      id: 'yt-api-182',
      platform: 'youtube',
      title: 'I\'ve had so many questions about printing our digital kits, so I put together this video to help! 🖨️',
      description: '#coracreacrafts #digitalkit #scrapbooking #creativejournaling #digitalart',
      url: 'https://www.youtube.com/watch?v=zhsGZjEc5Uc',
      embedId: 'zhsGZjEc5Uc',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-04',
      thumbnail: 'https://img.youtube.com/vi/zhsGZjEc5Uc/maxresdefault.jpg'
    },
    {
      id: 'yt-api-183',
      platform: 'youtube',
      title: 'Decided my Kindle needed a little steampunk glow up! ⚙️📖',
      description: '#coracreacrafts #steampunk #vintagecrafts #scrapbooking #diy',
      url: 'https://www.youtube.com/watch?v=M8FKfnqq1EI',
      embedId: 'M8FKfnqq1EI',
      category: 'tutorial',
      tags: ['vintage', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2025-04',
      thumbnail: 'https://img.youtube.com/vi/M8FKfnqq1EI/maxresdefault.jpg'
    },
    {
      id: 'yt-api-184',
      platform: 'youtube',
      title: 'Spend a day with me as a small business owner prepping for our first ever Art Crawl at Hester & Cook',
      description: '#smallbusiness #founder #dayinmylife #dayinthelife #vlog #coracreacrafts',
      url: 'https://www.youtube.com/watch?v=sy_kXHdjQMw',
      embedId: 'sy_kXHdjQMw',
      category: 'behind-the-scenes',
      tags: ['small business'],
      creator: 'CoraCreaCrafts',
      date: '2025-04',
      thumbnail: 'https://img.youtube.com/vi/sy_kXHdjQMw/maxresdefault.jpg'
    },
    {
      id: 'yt-api-185',
      platform: 'youtube',
      title: 'Being able to make a Black Dragon spread with my Empyrean Dragons Digital Kit was pure magic 🐉',
      description: '#dragon #fantasyaddict #magical #coracreacrafts #digitalkit',
      url: 'https://www.youtube.com/watch?v=v9tV8nUkMtE',
      embedId: 'v9tV8nUkMtE',
      category: 'journal-spread',
      tags: ['fantasy'],
      creator: 'CoraCreaCrafts',
      date: '2025-04',
      thumbnail: 'https://img.youtube.com/vi/v9tV8nUkMtE/maxresdefault.jpg'
    },
    {
      id: 'yt-api-186',
      platform: 'youtube',
      title: 'Our April Vintage Craft Box has arrived and we’re taking you on a journey to Scotland! 🏴󠁧󠁢󠁳󠁣󠁴󠁿',
      description: '#coracreacrafts #scotland #scrapbooking #subscriptionunboxing #scottish #vintagecrafts',
      url: 'https://www.youtube.com/watch?v=B280mTKE6Io',
      embedId: 'B280mTKE6Io',
      category: 'unboxing',
      tags: ['vintage', 'subscription box', 'unboxing'],
      creator: 'CoraCreaCrafts',
      date: '2025-04',
      thumbnail: 'https://img.youtube.com/vi/B280mTKE6Io/maxresdefault.jpg'
    },
    {
      id: 'yt-api-187',
      platform: 'youtube',
      title: 'Made a cute rose specimen card ❤️ #creativejournaling #scrapbooking #journaling #coracreacrafts',
      description: '',
      url: 'https://www.youtube.com/watch?v=6E9roCZ5tmg',
      embedId: '6E9roCZ5tmg',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://img.youtube.com/vi/6E9roCZ5tmg/maxresdefault.jpg'
    },
    {
      id: 'yt-api-188',
      platform: 'youtube',
      title: 'Come pack an order with me! Today, I’m putting together Helen’s order, filled with lots of goodies!',
      description: 'I always love seeing what you all pick out ❤️ Want me to pack your order next? Drop a comment below! ✨  #coracreacrafts #packanorderwithme #scrapbooking #creativejournaling #smallbusiness #stationaryo',
      url: 'https://www.youtube.com/watch?v=Ir0FbRKTIbU',
      embedId: 'Ir0FbRKTIbU',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://img.youtube.com/vi/Ir0FbRKTIbU/maxresdefault.jpg'
    },
    {
      id: 'yt-api-189',
      platform: 'youtube',
      title: 'Coffee in hand, crafting in full swing! ☕🌸 Who else is ready to craft their way through spring?',
      description: '#springcrafting #coracreacrafts #creativejournaling #scrapbooking #junkjournal #spring',
      url: 'https://www.youtube.com/watch?v=07SaQUo4xj4',
      embedId: '07SaQUo4xj4',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://img.youtube.com/vi/07SaQUo4xj4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-190',
      platform: 'youtube',
      title: 'I’ve been wanting to create a day & night dichotomy spread for so long, and I finally did it! ☀️🌙',
      description: 'I loved playing with the contrast between light and dark! What side is your favorite?   #creativejournaling #dichotomy #opposite #coracreacrafts #scrapbooking',
      url: 'https://www.youtube.com/watch?v=JM49n76att8',
      embedId: 'JM49n76att8',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://img.youtube.com/vi/JM49n76att8/maxresdefault.jpg'
    },
    {
      id: 'yt-api-191',
      platform: 'youtube',
      title: 'Rice paper is one of my favorite materials to craft with! Here are 3 easy ways to use it✨',
      description: '#ricepaper  #coracreacrafts  #diy  #handmadewithlove  #creativejournaling  #craftingcommunity',
      url: 'https://www.youtube.com/watch?v=aPmOJcMHvZE',
      embedId: 'aPmOJcMHvZE',
      category: 'tutorial',
      tags: ['journaling', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://img.youtube.com/vi/aPmOJcMHvZE/maxresdefault.jpg'
    },
    {
      id: 'yt-api-192',
      platform: 'youtube',
      title: 'With our Buy 2 Get 1 Free sale now’s the perfect excuse to grab just a few more supplies. 👀',
      description: 'Sale ends tonight (March 18th) at 11:59pm CST  Excludes subscriptions  #creativejournaling #scrapbooking #coracreacrafts',
      url: 'https://www.youtube.com/watch?v=wKv-DjHIrUQ',
      embedId: 'wKv-DjHIrUQ',
      category: 'journal-spread',
      tags: ['journaling', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://img.youtube.com/vi/wKv-DjHIrUQ/maxresdefault.jpg'
    },
    {
      id: 'yt-api-193',
      platform: 'youtube',
      title: 'Our BUY 2, GET 1 FREE sale is now live! Stock up on your favorites now! ☘️ #creativejournaling',
      description: 'This lucky deal disappears tomorrow, March 18th at 11:59 PM CST!  Excludes Subscriptions  Happy shopping! ☘️  #coracreacrafts #sale #scrapbooking #craftingcommunity',
      url: 'https://www.youtube.com/watch?v=f_QHJRXb4CU',
      embedId: 'f_QHJRXb4CU',
      category: 'journal-spread',
      tags: ['journaling', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://img.youtube.com/vi/f_QHJRXb4CU/maxresdefault.jpg'
    },
    {
      id: 'yt-api-194',
      platform: 'youtube',
      title: 'Getting into the St. Patrick\'s Day spirit! 🍀 All the items I used are listed in the video!',
      description: '#stpatricksday #stpatricks #coracreacrafts #creativejournaling #craftingcommunity #scrapbooking',
      url: 'https://www.youtube.com/watch?v=3hyRS1rbIK0',
      embedId: '3hyRS1rbIK0',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://img.youtube.com/vi/3hyRS1rbIK0/maxresdefault.jpg'
    },
    {
      id: 'yt-api-195',
      platform: 'youtube',
      title: 'Steampunk is back! ⚙️ This has been one of our most requested themes since 2023!',
      description: 'I really wanted to capture the magic of invention and adventure with these pieces. I hope you love it as much as I do! What’s your favorite item?   #coracreacrafts  #steampunk  #steampunkart  #craftin',
      url: 'https://www.youtube.com/watch?v=pNoOPM0NP1o',
      embedId: 'pNoOPM0NP1o',
      category: 'product-showcase',
      tags: ['advent calendar'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://img.youtube.com/vi/pNoOPM0NP1o/maxresdefault.jpg'
    },
    {
      id: 'yt-api-196',
      platform: 'youtube',
      title: 'Restocked! The Alice collection is back, but it’s going quick! ✨',
      description: 'I made these Alice-inspired tags and loved how they turned out! 😍  #alice #coracreacrafts #diycrafts #handmade #junkjournal',
      url: 'https://www.youtube.com/watch?v=HGRoki3Jysg',
      embedId: 'HGRoki3Jysg',
      category: 'tutorial',
      tags: ['journaling', 'alice in wonderland', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://img.youtube.com/vi/HGRoki3Jysg/maxresdefault.jpg'
    },
    {
      id: 'yt-api-197',
      platform: 'youtube',
      title: 'I had so much fun creating these 3 new digital kits..which one is your favorite?🤭',
      description: '#creativejournaling #digitalkit #printable #coracreacrafts',
      url: 'https://www.youtube.com/watch?v=yn3gsYlDy1o',
      embedId: 'yn3gsYlDy1o',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://img.youtube.com/vi/yn3gsYlDy1o/maxresdefault.jpg'
    },
    {
      id: 'yt-api-198',
      platform: 'youtube',
      title: 'One of my favorite things to make are pop-up cards! Have you tried this before?👀',
      description: '#creativejournaling #scrapbooking #diycrafts',
      url: 'https://www.youtube.com/watch?v=FHE6pwcSoNQ',
      embedId: 'FHE6pwcSoNQ',
      category: 'tutorial',
      tags: ['journaling', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://img.youtube.com/vi/FHE6pwcSoNQ/maxresdefault.jpg'
    },
    {
      id: 'yt-api-199',
      platform: 'youtube',
      title: 'Sharing a little Alice in Wonderland magic with this mini journal spread!',
      description: 'Comment if you love mini journaling ⬇️ #junkjournal  #aliceinwonderland #creativejournaling  #minijournaling #coracreacrafts',
      url: 'https://www.youtube.com/watch?v=syTHd7c3LdE',
      embedId: 'syTHd7c3LdE',
      category: 'journal-spread',
      tags: ['journaling', 'alice in wonderland'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://img.youtube.com/vi/syTHd7c3LdE/maxresdefault.jpg'
    },
    {
      id: 'yt-api-200',
      platform: 'youtube',
      title: 'This is your sign to start upcycling in your crafts ✨',
      description: '#upcycledcrafts #upcycling #creativejournaling #scrapbooking #diy',
      url: 'https://www.youtube.com/watch?v=LjwJF_4OdEA',
      embedId: 'LjwJF_4OdEA',
      category: 'tutorial',
      tags: ['journaling', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://img.youtube.com/vi/LjwJF_4OdEA/maxresdefault.jpg'
    },
    {
      id: 'yt-api-201',
      platform: 'youtube',
      title: 'Our new Dragon Creative Notebook is an immediate add to cart....who else agrees? 🐉✨',
      description: '#coracreacrafts  #fantasyaddict  #notebook  #scrapbooking  #creativejournaling  #dragon',
      url: 'https://www.youtube.com/watch?v=Uy-5YS2Ht2Q',
      embedId: 'Uy-5YS2Ht2Q',
      category: 'journal-spread',
      tags: ['journaling', 'fantasy'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://img.youtube.com/vi/Uy-5YS2Ht2Q/maxresdefault.jpg'
    },
    {
      id: 'yt-api-202',
      platform: 'youtube',
      title: 'Our newest wax items have arrived! I\'m absolutely obsessed with the Rosewood Wax Seal Furnace Set 😍',
      description: 'Did you know you can upgrade your March VCB and add one of our wax items....Don\'t miss out! #waxseal  #coracreacrafts  #creativejournaling  #scrapbookingsupplies',
      url: 'https://www.youtube.com/watch?v=xRhsU3rNAlY',
      embedId: 'xRhsU3rNAlY',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://img.youtube.com/vi/xRhsU3rNAlY/maxresdefault.jpg'
    },
    {
      id: 'yt-api-203',
      platform: 'youtube',
      title: 'To celebrate IWD, we’ve put together bundles at 40% off and get a free gift with orders over $30!',
      description: 'Today, we’re celebrating the incredible strength, creativity, and power of women, both past and present. I’m beyond grateful for the amazing women who have supported CoraCreaCrafts, your passion and e',
      url: 'https://www.youtube.com/watch?v=12Icb5iqlVQ',
      embedId: '12Icb5iqlVQ',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://img.youtube.com/vi/12Icb5iqlVQ/maxresdefault.jpg'
    },
    {
      id: 'yt-api-204',
      platform: 'youtube',
      title: 'I forgot how much fun cut-out spreads can be until I created this one! Anyone else a fan of cutouts?',
      description: '#creativejournaling #scrapbooking #mindfuljournaling #cutouts #junkjournal',
      url: 'https://www.youtube.com/watch?v=lidgovrUQmM',
      embedId: 'lidgovrUQmM',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://img.youtube.com/vi/lidgovrUQmM/maxresdefault.jpg'
    },
    {
      id: 'yt-api-205',
      platform: 'youtube',
      title: 'This March step into the world of Dichotomy! Comment your favorite product below ⬇️',
      description: '#creativejournaling #coracreacrafts #unboxing #creativejournal #craftingcommunity',
      url: 'https://www.youtube.com/watch?v=kSQ1HmeOw4s',
      embedId: 'kSQ1HmeOw4s',
      category: 'unboxing',
      tags: ['journaling', 'unboxing'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://img.youtube.com/vi/kSQ1HmeOw4s/maxresdefault.jpg'
    },
    {
      id: 'yt-api-206',
      platform: 'youtube',
      title: 'I’ve put together some gift ideas for anyone who shares the love for our feathered friends! 🐦💖',
      description: '#birdlovers  #giftideas  #featheredfriends  #natureinspired #creativejournaling #scrapbooking #nature',
      url: 'https://www.youtube.com/watch?v=2Efw_kOz3_s',
      embedId: '2Efw_kOz3_s',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://img.youtube.com/vi/2Efw_kOz3_s/maxresdefault.jpg'
    },
    {
      id: 'yt-api-207',
      platform: 'youtube',
      title: 'Creating this Alice in Wonderland-inspired spread made me want to watch the movie again! 🎩🐇🫖🃏',
      description: '#alice #aliceinwonderland #creativejournaling #scrapbooking #journalnotebook',
      url: 'https://www.youtube.com/watch?v=Mp-5jN6Empc',
      embedId: 'Mp-5jN6Empc',
      category: 'journal-spread',
      tags: ['journaling', 'alice in wonderland'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://img.youtube.com/vi/Mp-5jN6Empc/maxresdefault.jpg'
    },
    {
      id: 'yt-api-208',
      platform: 'youtube',
      title: 'Nothing resets my mind like a little crafting. What\'s your go-to craft when you need to relax?',
      description: '#scrapbooking  #craftingtherapy #coracreacrafts  #creativeescape  #vintagejournaling #selfcare #selflove #saturdayvibes',
      url: 'https://www.youtube.com/watch?v=QHbIvrCjK98',
      embedId: 'QHbIvrCjK98',
      category: 'journal-spread',
      tags: ['journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-03',
      thumbnail: 'https://img.youtube.com/vi/QHbIvrCjK98/maxresdefault.jpg'
    },
    {
      id: 'yt-api-209',
      platform: 'youtube',
      title: 'Free wax mold? Yes, please! 🕯✨ Get yours with orders over $50—while supplies last!',
      description: '#vintagecrafts  #freebies  #waxseal  #creativejournaling  #coracreacrafts',
      url: 'https://www.youtube.com/watch?v=8MwGmjic4do',
      embedId: '8MwGmjic4do',
      category: 'journal-spread',
      tags: ['journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-02',
      thumbnail: 'https://img.youtube.com/vi/8MwGmjic4do/maxresdefault.jpg'
    },
    {
      id: 'yt-api-210',
      platform: 'youtube',
      title: 'We’re so excited to introduce our End-of-Month Live Sale starting tomorrow Friday, February 29th! 🤭',
      description: '📍 Instagram Live: 5-7 PM CST 📍 TikTok Live: 7-9 PM CST  Get ready for exclusive deals starting at $2, $5, $10, and $15—but only during the live! 👀 Don’t miss your chance to claim these limited-time',
      url: 'https://www.youtube.com/watch?v=doL6MKvmFs8',
      embedId: 'doL6MKvmFs8',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2025-02',
      thumbnail: 'https://img.youtube.com/vi/doL6MKvmFs8/maxresdefault.jpg'
    },
    {
      id: 'yt-api-211',
      platform: 'youtube',
      title: 'Who else loves that new notebook feeling? #journaling #notebook #creativejournaling',
      description: 'There’s something magical about opening a brand new notebook, especially when it’s our Ancient Arcane Foil Notebook ✨ With its dark academia design, gold foiling, and aged parchment pages, it’s the pe',
      url: 'https://www.youtube.com/watch?v=QbIaINLS8B0',
      embedId: 'QbIaINLS8B0',
      category: 'journal-spread',
      tags: ['journaling', 'dark academia'],
      creator: 'CoraCreaCrafts',
      date: '2025-02',
      thumbnail: 'https://img.youtube.com/vi/QbIaINLS8B0/maxresdefault.jpg'
    },
    {
      id: 'yt-api-212',
      platform: 'youtube',
      title: 'New Treasure Box Alert! ✨ Who\'s excited for this one? ❤️',
      description: '#red #creativejournaling #treasurebox #scrapbooking #spreadsmile  Right On Red by Audionautix is licensed under a Creative Commons Attribution 4.0 license. https://creativecommons.org/licenses/by/4.0/',
      url: 'https://www.youtube.com/watch?v=d1ioNEYvu8I',
      embedId: 'd1ioNEYvu8I',
      category: 'box-reveal',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-02',
      thumbnail: 'https://img.youtube.com/vi/d1ioNEYvu8I/maxresdefault.jpg'
    },
    {
      id: 'yt-api-213',
      platform: 'youtube',
      title: 'We want YOU to pick the next Treasure Box theme! ✨Drop a comment below with your favorite theme idea',
      description: '#creativejournaling #winner #junkjournal',
      url: 'https://www.youtube.com/watch?v=sdQ9RFtd8HU',
      embedId: 'sdQ9RFtd8HU',
      category: 'box-reveal',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-02',
      thumbnail: 'https://img.youtube.com/vi/sdQ9RFtd8HU/maxresdefault.jpg'
    },
    {
      id: 'yt-api-214',
      platform: 'youtube',
      title: 'Gothic Spread 🖤',
      description: '#gothicaesthetic #junkjournal #coracreacrafts #creativejournaling #gothicvibes',
      url: 'https://www.youtube.com/watch?v=aRD2Fc4odEk',
      embedId: 'aRD2Fc4odEk',
      category: 'journal-spread',
      tags: ['journaling', 'gothic'],
      creator: 'CoraCreaCrafts',
      date: '2025-02',
      thumbnail: 'https://img.youtube.com/vi/aRD2Fc4odEk/maxresdefault.jpg'
    },
    {
      id: 'yt-api-215',
      platform: 'youtube',
      title: 'Our Alice in Wonderland Vintage Craft Box is finally here! 🃏',
      description: 'This isn’t the Alice you know, you’ll find dark fairies, gothic details, and a whole new twist on the classic tale. I’ve been dying to share this one with you ever since I came up with the idea, and I',
      url: 'https://www.youtube.com/watch?v=k76WqbWho_8',
      embedId: 'k76WqbWho_8',
      category: 'box-reveal',
      tags: ['vintage', 'gothic', 'alice in wonderland'],
      creator: 'CoraCreaCrafts',
      date: '2025-02',
      thumbnail: 'https://img.youtube.com/vi/k76WqbWho_8/maxresdefault.jpg'
    },
    {
      id: 'yt-api-216',
      platform: 'youtube',
      title: 'Adding a touch of vintage romance to my journal with our newest collection! 💌✨',
      description: '#VintageJournaling #CreativeMoments #VintageAesthetic #JournalingLove #ValentinesVibes',
      url: 'https://www.youtube.com/watch?v=7oyv8we_j4I',
      embedId: '7oyv8we_j4I',
      category: 'journal-spread',
      tags: ['journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2025-02',
      thumbnail: 'https://img.youtube.com/vi/7oyv8we_j4I/maxresdefault.jpg'
    },
    {
      id: 'yt-api-217',
      platform: 'youtube',
      title: 'Don’t forget our Valentine’s Sale ends tomorrow at 11:59 PM CST!  ❤️',
      description: '#sale #valentinesday #valentines #creativejournaling #creativejournal',
      url: 'https://www.youtube.com/watch?v=XArUAqsbl6A',
      embedId: 'XArUAqsbl6A',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-02',
      thumbnail: 'https://img.youtube.com/vi/XArUAqsbl6A/maxresdefault.jpg'
    },
    {
      id: 'yt-api-218',
      platform: 'youtube',
      title: 'You can only pick ONE item from our Legends & Lore Box… 👀 Which one are you choosing? ✨',
      description: 'Don\'t wait too long—we have only a few of these limited edition boxes left in stock! ⏳   https://coracreacrafts.com/products/legends-lore-dragon-riders   #creativejournaling #scrapbooking #fantasybook',
      url: 'https://www.youtube.com/watch?v=gWbviwM-jPU',
      embedId: 'gWbviwM-jPU',
      category: 'box-reveal',
      tags: ['journaling', 'fantasy'],
      creator: 'CoraCreaCrafts',
      date: '2025-02',
      thumbnail: 'https://img.youtube.com/vi/gWbviwM-jPU/maxresdefault.jpg'
    },
    {
      id: 'yt-api-219',
      platform: 'youtube',
      title: 'I wanted to share a double-page gothic spread I created 🖤',
      description: 'I love how the Gothic Patterns Foil Washi Tape turned out! So grateful for all the love on the Gothic VCB—pre-orders are open now!  #scrapbooking #gothic #journaling #junkjournal',
      url: 'https://www.youtube.com/watch?v=nvm05Dw9hj0',
      embedId: 'nvm05Dw9hj0',
      category: 'journal-spread',
      tags: ['washi tape', 'journaling', 'gothic'],
      creator: 'CoraCreaCrafts',
      date: '2025-02',
      thumbnail: 'https://img.youtube.com/vi/nvm05Dw9hj0/maxresdefault.jpg'
    },
    {
      id: 'yt-api-220',
      platform: 'youtube',
      title: 'Elven magic awaits! 🧝‍♀️💫',
      description: 'Products used:  Elves Transparent Tape Faux Wax Seals Sticker Sheets Elven Ruins Sticker Box  #elvenmusic #creativejournaling #junkjournal #journal',
      url: 'https://www.youtube.com/watch?v=gugJQbLhVeo',
      embedId: 'gugJQbLhVeo',
      category: 'box-reveal',
      tags: ['journaling', 'stickers'],
      creator: 'CoraCreaCrafts',
      date: '2025-02',
      thumbnail: 'https://img.youtube.com/vi/gugJQbLhVeo/maxresdefault.jpg'
    },
    {
      id: 'yt-api-221',
      platform: 'youtube',
      title: 'I can’t wait for you to see what’s inside our Fantasy Winter Box! ❄️',
      description: 'From frosty creatures to enchanted landscapes, every detail is crafted to bring a little magic to your winter season. Let the magic begin… 🩵  Shop our Curiosities Winter Fantasy Box here - https://co',
      url: 'https://www.youtube.com/watch?v=V36fvJZFe3U',
      embedId: 'V36fvJZFe3U',
      category: 'box-reveal',
      tags: ['fantasy'],
      creator: 'CoraCreaCrafts',
      date: '2025-01',
      thumbnail: 'https://img.youtube.com/vi/V36fvJZFe3U/maxresdefault.jpg'
    },
    {
      id: 'yt-api-222',
      platform: 'youtube',
      title: 'Just a little upcycle magic to keep my crafting space fresh! ✨',
      description: 'Head over to our videos for the full step-by-step tutorial! #upcycling #upcycledcrafts #diycrafts #diyprojects #sustainablecrafts',
      url: 'https://www.youtube.com/watch?v=pcPn47KJEM4',
      embedId: 'pcPn47KJEM4',
      category: 'tutorial',
      tags: ['tutorial', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2025-01',
      thumbnail: 'https://img.youtube.com/vi/pcPn47KJEM4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-223',
      platform: 'youtube',
      title: 'Here are a few of our team\'s top picks to brighten your week! What’s on your wishlist? 🩵',
      description: 'Shop our new arrivals here - https://coracreacrafts.com/collections/new-arrivals #scrapbooking   #journaling #creativejournaling #junkjournal',
      url: 'https://www.youtube.com/watch?v=xo0zKKb5Tno',
      embedId: 'xo0zKKb5Tno',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-01',
      thumbnail: 'https://img.youtube.com/vi/xo0zKKb5Tno/maxresdefault.jpg'
    },
    {
      id: 'yt-api-224',
      platform: 'youtube',
      title: 'Dark, mysterious, and oh-so-vintage 🖤 #creativejournaling #scrapbooking #gothic #journaling',
      description: 'I couldn’t resist creating this spread with our Gothic Vintage Craft Box! It’s full of moody gothic vibes that are perfect for this time of year. Who’s excited to start crafting with this box? 👀  You',
      url: 'https://www.youtube.com/watch?v=5QF2OT76IJY',
      embedId: '5QF2OT76IJY',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'gothic'],
      creator: 'CoraCreaCrafts',
      date: '2025-01',
      thumbnail: 'https://img.youtube.com/vi/5QF2OT76IJY/maxresdefault.jpg'
    },
    {
      id: 'yt-api-225',
      platform: 'youtube',
      title: '2025 is here, and so are our new arrivals! 😍 We’ve got tons of new goodies up on the site!',
      description: 'Shop our new arrivals here - https://coracreacrafts.com/collections/new-arrivals',
      url: 'https://www.youtube.com/watch?v=2902ZqtvpEs',
      embedId: '2902ZqtvpEs',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2025-01',
      thumbnail: 'https://img.youtube.com/vi/2902ZqtvpEs/maxresdefault.jpg'
    },
    {
      id: 'yt-api-226',
      platform: 'youtube',
      title: 'I am such a lover of reading, so this collaboration with Laura Frantz is incredibly special to me! ✨',
      description: 'Bringing her beautiful story, The Indigo Heiress, to life through crafting has been such a joy 💙  You can order the bundle here - https://coracreacrafts.com/products/indigo-heiress-bundle?_pos=1&_sid',
      url: 'https://www.youtube.com/watch?v=Y_N3hkc_tiE',
      embedId: 'Y_N3hkc_tiE',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2025-01',
      thumbnail: 'https://img.youtube.com/vi/Y_N3hkc_tiE/maxresdefault.jpg'
    },
    {
      id: 'yt-api-227',
      platform: 'youtube',
      title: '✨ New Box Alert! ✨ Introducing our newest quarterly subscription -Legends & Lore Fantasy Collection!',
      description: 'In collaboration with Storm Sparrow’s Inn, this box brings your favorite stories to life with magical items crafted for every fan. If magical worlds are your escape, this box is your next adventure 💫',
      url: 'https://www.youtube.com/watch?v=yXhP-nuGEKw',
      embedId: 'yXhP-nuGEKw',
      category: 'box-reveal',
      tags: ['advent calendar', 'fantasy', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2025-01',
      thumbnail: 'https://img.youtube.com/vi/yXhP-nuGEKw/maxresdefault.jpg'
    },
    {
      id: 'yt-api-228',
      platform: 'youtube',
      title: 'Using our Elven-themed supplies, I created this magical spread and I’m loving how it turned out! ✨',
      description: 'You can shop our Elven supplies here - https://coracreacrafts.com/search?options%5Bprefix%5D=last&q=elven',
      url: 'https://www.youtube.com/watch?v=W8N8z7o1230',
      embedId: 'W8N8z7o1230',
      category: 'journal-spread',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2025-01',
      thumbnail: 'https://img.youtube.com/vi/W8N8z7o1230/maxresdefault.jpg'
    },
    {
      id: 'yt-api-229',
      platform: 'youtube',
      title: 'Craftuary Week 3 - Pop Up Journal/Card - January Creative Challenge',
      description: '♻️ Week 3 - Jan 19th: Pop Up Card/Journal  Scroll down for more information about the challenge!  Links for tools and products used in this video:  Stationery Book: https://coracreacrafts.com/products',
      url: 'https://www.youtube.com/watch?v=v8sBA3YCz4s',
      embedId: 'v8sBA3YCz4s',
      category: 'product-showcase',
      tags: ['journaling', 'craftuary'],
      creator: 'CoraCreaCrafts',
      date: '2025-01',
      thumbnail: 'https://img.youtube.com/vi/v8sBA3YCz4s/maxresdefault.jpg'
    },
    {
      id: 'yt-api-230',
      platform: 'youtube',
      title: 'Who’s excited for the first VCB of 2025? 👀 Say hello to our Gothic box 🖤 #scrapbooking #journaling',
      description: 'You can pre-order our Gothic VCB here - https://coracreacrafts.com/products/pre-order-gothic-box',
      url: 'https://www.youtube.com/watch?v=zV0SmWydvV8',
      embedId: 'zV0SmWydvV8',
      category: 'box-reveal',
      tags: ['journaling', 'gothic'],
      creator: 'CoraCreaCrafts',
      date: '2025-01',
      thumbnail: 'https://img.youtube.com/vi/zV0SmWydvV8/maxresdefault.jpg'
    },
    {
      id: 'yt-api-231',
      platform: 'youtube',
      title: 'Craftuary Week 2 - Upcycled Storage Box - January Creative Challenge',
      description: '♻️ Week 2 - Jan 12th: Upcycling Project - Create an Aesthetic Storage Box for Your Desk  Scroll down for more information about the challenge!  Links for tools and products used in this video:  White ',
      url: 'https://www.youtube.com/watch?v=Hp4V9P9yhM4',
      embedId: 'Hp4V9P9yhM4',
      category: 'box-reveal',
      tags: ['craftuary'],
      creator: 'CoraCreaCrafts',
      date: '2025-01',
      thumbnail: 'https://img.youtube.com/vi/Hp4V9P9yhM4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-232',
      platform: 'youtube',
      title: 'I finally had a chance to sit down and use the beautiful supplies from our Elven Box',
      description: 'I’m loving how these pieces brought a little bit of magic and calm to my creative space. ✨🍃The delicate greens and enchanting details are exactly what I needed to kick off some after-Christmas crafti',
      url: 'https://www.youtube.com/watch?v=6tked44olE8',
      embedId: '6tked44olE8',
      category: 'box-reveal',
      tags: ['christmas'],
      creator: 'CoraCreaCrafts',
      date: '2025-01',
      thumbnail: 'https://img.youtube.com/vi/6tked44olE8/maxresdefault.jpg'
    },
    {
      id: 'yt-api-233',
      platform: 'youtube',
      title: 'I haven’t used my mini journal lately, but creating this spread reminded me how much I’ve missed it!',
      description: 'There’s something so magical about how even the simplest layouts can shine in a smaller space. 🖋️',
      url: 'https://www.youtube.com/watch?v=TEZ-xe-XJOk',
      embedId: 'TEZ-xe-XJOk',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-01',
      thumbnail: 'https://img.youtube.com/vi/TEZ-xe-XJOk/maxresdefault.jpg'
    },
    {
      id: 'yt-api-234',
      platform: 'youtube',
      title: 'Creating our Digital Journal Kits has been one of the highlights of 2024 for me ✨',
      description: 'I love knowing that they give everyone a chance to sit down, get creative, and make something truly beautiful—no matter where you are or how much time you have. I’m excited to keep designing more and ',
      url: 'https://www.youtube.com/watch?v=M1fnfMzoPEg',
      embedId: 'M1fnfMzoPEg',
      category: 'product-showcase',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2025-01',
      thumbnail: 'https://img.youtube.com/vi/M1fnfMzoPEg/maxresdefault.jpg'
    },
    {
      id: 'yt-api-235',
      platform: 'youtube',
      title: 'I’ve been so excited to share this with you—our Elven Treasure Box is finally here! ✨',
      description: 'Every piece in this box was chosen to bring a little magic and wonder to your crafting. Unboxing it feels like stepping into a woodland dream. I’d love to hear which item is your favorite! 🧝‍♀️',
      url: 'https://www.youtube.com/watch?v=dgYAmCb05zk',
      embedId: 'dgYAmCb05zk',
      category: 'unboxing',
      tags: ['unboxing'],
      creator: 'CoraCreaCrafts',
      date: '2025-01',
      thumbnail: 'https://img.youtube.com/vi/dgYAmCb05zk/maxresdefault.jpg'
    },
    {
      id: 'yt-api-236',
      platform: 'youtube',
      title: 'Craftuary Week 1 - Make your Own 2025 Calendar - January Creative Challenge',
      description: 'I’m so excited to kick off 2025 with something special for all of you—a January Crafting Challenge that we can enjoy together!  Starting 6th January, we’ll spend 4 weeks crafting, creating, and sharin',
      url: 'https://www.youtube.com/watch?v=jXlZa5eKlds',
      embedId: 'jXlZa5eKlds',
      category: 'product-showcase',
      tags: ['craftuary'],
      creator: 'CoraCreaCrafts',
      date: '2025-01',
      thumbnail: 'https://img.youtube.com/vi/jXlZa5eKlds/maxresdefault.jpg'
    },
    {
      id: 'yt-api-237',
      platform: 'youtube',
      title: 'Exciting news, you can now grab our baking and cooking supplies individually on our website!',
      description: 'This was such a popular box and I\'m so excited for everyone to now have the chance to use these supplies!  Which items are you most excited to try? Let me know below! 💛',
      url: 'https://www.youtube.com/watch?v=YeRekLBu_yA',
      embedId: 'YeRekLBu_yA',
      category: 'box-reveal',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2025-01',
      thumbnail: 'https://img.youtube.com/vi/YeRekLBu_yA/maxresdefault.jpg'
    },
    {
      id: 'yt-api-238',
      platform: 'youtube',
      title: 'Can you believe this whole spread came from just one washi tape?',
      description: 'I used the Gothic Washi Tape, and I’m obsessed with how it turned out! Sometimes, keeping it simple works best.What’s your go-to washi tape for spreads?',
      url: 'https://www.youtube.com/watch?v=vg1ESTAIQFc',
      embedId: 'vg1ESTAIQFc',
      category: 'journal-spread',
      tags: ['washi tape', 'gothic'],
      creator: 'CoraCreaCrafts',
      date: '2025-01',
      thumbnail: 'https://img.youtube.com/vi/vg1ESTAIQFc/maxresdefault.jpg'
    },
    {
      id: 'yt-api-239',
      platform: 'youtube',
      title: 'What better way to start 2025 than by sharing how I’ve organized my Planning Companion for the year!',
      description: 'I absolutely love how versatile this planner is—it’s perfect for keeping me on track while still letting me express my creativity. Adding stickers, papers, and little touches makes it feel so personal',
      url: 'https://www.youtube.com/watch?v=-vkX-gO7i8g',
      embedId: '-vkX-gO7i8g',
      category: 'product-showcase',
      tags: ['stickers'],
      creator: 'CoraCreaCrafts',
      date: '2025-01',
      thumbnail: 'https://img.youtube.com/vi/-vkX-gO7i8g/maxresdefault.jpg'
    },
    {
      id: 'yt-api-240',
      platform: 'youtube',
      title: 'As we unwrap the last surprise, I’m thrilled to reveal our brand new Sticker Folder!',
      description: 'Thank you for joining me on this holiday countdown. I’m so excited to share this new product with you and can’t wait to see how you use it in your craft projects!  #adventcalendar #sticker #creativejo',
      url: 'https://www.youtube.com/watch?v=CLjGWnikA3M',
      embedId: 'CLjGWnikA3M',
      category: 'box-reveal',
      tags: ['advent calendar', 'stickers'],
      creator: 'CoraCreaCrafts',
      date: '2024-12',
      thumbnail: 'https://img.youtube.com/vi/CLjGWnikA3M/maxresdefault.jpg'
    },
    {
      id: 'yt-api-241',
      platform: 'youtube',
      title: 'Merry Christmas from all of us here at CoraCreaCrafts! 🎄',
      description: 'Thank you for your incredible support this year. Your passion for crafting has made everything we do possible. Wishing you a peaceful holiday season and looking forward to what’s ahead in 2025!  #adve',
      url: 'https://www.youtube.com/watch?v=gHEnk5C430o',
      embedId: 'gHEnk5C430o',
      category: 'product-showcase',
      tags: ['christmas'],
      creator: 'CoraCreaCrafts',
      date: '2024-12',
      thumbnail: 'https://img.youtube.com/vi/gHEnk5C430o/maxresdefault.jpg'
    },
    {
      id: 'yt-api-242',
      platform: 'youtube',
      title: 'Every year, I look forward to making my own Christmas tags, it adds such a special handmade touch❤️',
      description: 'Have you ever tried making your own tags? Let me know in the comments!  #christmasdecoration #christmasgift #creativejournal',
      url: 'https://www.youtube.com/watch?v=nLts7VqUqgo',
      embedId: 'nLts7VqUqgo',
      category: 'product-showcase',
      tags: ['journaling', 'christmas'],
      creator: 'CoraCreaCrafts',
      date: '2024-12',
      thumbnail: 'https://img.youtube.com/vi/nLts7VqUqgo/maxresdefault.jpg'
    },
    {
      id: 'yt-api-243',
      platform: 'youtube',
      title: 'Why buy wrapping paper when you can make your own? 👀',
      description: 'Handmade wrapping paper makes every gift extra special, because the wrapping should be just as thoughtful as what’s inside! 🎁  #christmas #wrapping #christmasgift #creativejournal',
      url: 'https://www.youtube.com/watch?v=2Awk6xF5Tss',
      embedId: '2Awk6xF5Tss',
      category: 'product-showcase',
      tags: ['journaling', 'christmas'],
      creator: 'CoraCreaCrafts',
      date: '2024-12',
      thumbnail: 'https://img.youtube.com/vi/2Awk6xF5Tss/maxresdefault.jpg'
    },
    {
      id: 'yt-api-244',
      platform: 'youtube',
      title: 'Here’s a look inside the December VCB, our Writers Box edition, chosen as a fan favorite by YOU! 📜',
      description: 'This box is so special to me, and I hope you all love it as much as I do. 🖤 Let me know which item is your favorite!  #creativejournaling #creativejournal #junkjournal',
      url: 'https://www.youtube.com/watch?v=iwRgWwCsfSY',
      embedId: 'iwRgWwCsfSY',
      category: 'box-reveal',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-12',
      thumbnail: 'https://img.youtube.com/vi/iwRgWwCsfSY/maxresdefault.jpg'
    },
    {
      id: 'yt-api-245',
      platform: 'youtube',
      title: 'Coffee as watercolor? Yes, please! ☕🎨 I used real coffee to create the background for this spread',
      description: 'Have you tried using coffee or other fun materials in your crafts? Let me know! 👇 #journaling #creativejournaling',
      url: 'https://www.youtube.com/watch?v=m6LQgh2dQCc',
      embedId: 'm6LQgh2dQCc',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-12',
      thumbnail: 'https://img.youtube.com/vi/m6LQgh2dQCc/maxresdefault.jpg'
    },
    {
      id: 'yt-api-246',
      platform: 'youtube',
      title: 'I\'ve been loving the flow of creating double-page spreads lately!',
      description: 'There\'s something so satisfying about seeing the full picture come together! Do you craft across two pages too? Let me know! 👇  #journaling #creativejournaling #scrapbooking',
      url: 'https://www.youtube.com/watch?v=KIDcRlQ5hUo',
      embedId: 'KIDcRlQ5hUo',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-12',
      thumbnail: 'https://img.youtube.com/vi/KIDcRlQ5hUo/maxresdefault.jpg'
    },
    {
      id: 'yt-api-247',
      platform: 'youtube',
      title: 'The cutest, pinkest spread I\'ve ever made! ✨',
      description: 'Playing with our new A Celebration of Women treasure box and I\'m obsessed. Did you manage to grab yours?',
      url: 'https://www.youtube.com/watch?v=q4tSVdtE_1I',
      embedId: 'q4tSVdtE_1I',
      category: 'box-reveal',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2024-12',
      thumbnail: 'https://img.youtube.com/vi/q4tSVdtE_1I/maxresdefault.jpg'
    },
    {
      id: 'yt-api-248',
      platform: 'youtube',
      title: 'SPOILER!! 🚨 Day 1 of Advent is finally here! ✨',
      description: 'I’ve been counting down the days for you to start opening your calendars and couldn’t wait to share today’s surprise! What do you think of this festive treat? 🎄',
      url: 'https://www.youtube.com/watch?v=Ghl1SYOV_3M',
      embedId: 'Ghl1SYOV_3M',
      category: 'product-showcase',
      tags: ['advent calendar'],
      creator: 'CoraCreaCrafts',
      date: '2024-12',
      thumbnail: 'https://img.youtube.com/vi/Ghl1SYOV_3M/maxresdefault.jpg'
    },
    {
      id: 'yt-api-249',
      platform: 'youtube',
      title: 'One more sleep until December 1st… who’s ready to start opening their advent calendars? 👀🎄📆',
      description: '#adventcalendar #christmas',
      url: 'https://www.youtube.com/watch?v=TO7x0RWZi7Q',
      embedId: 'TO7x0RWZi7Q',
      category: 'product-showcase',
      tags: ['christmas', 'advent calendar'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://img.youtube.com/vi/TO7x0RWZi7Q/maxresdefault.jpg'
    },
    {
      id: 'yt-api-250',
      platform: 'youtube',
      title: 'THE WAIT IS OVER! 🎉',
      description: 'Our BIGGEST surprise of the year is finally here, and it’s packed with deals you’ll LOVE:  🛍️ Up to 30% off sitewide 🎁 Spend $100+ to enter a raffle for a chance to win a mystery box full with curat',
      url: 'https://www.youtube.com/watch?v=gNXrJH8wggs',
      embedId: 'gNXrJH8wggs',
      category: 'box-reveal',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://img.youtube.com/vi/gNXrJH8wggs/maxresdefault.jpg'
    },
    {
      id: 'yt-api-251',
      platform: 'youtube',
      title: 'SPOILER ALERT! It’s time for a sneak peek inside day 12 of our advent calendar 🤭',
      description: 'Don’t miss this exclusive look at what’s waiting behind the door ✨  #creativejournaling #journaling #adventcalendar',
      url: 'https://www.youtube.com/watch?v=TB19T2hlfE0',
      embedId: 'TB19T2hlfE0',
      category: 'box-reveal',
      tags: ['journaling', 'advent calendar'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://img.youtube.com/vi/TB19T2hlfE0/maxresdefault.jpg'
    },
    {
      id: 'yt-api-252',
      platform: 'youtube',
      title: 'Have you tried our Digital Journal Club yet? 👀',
      description: '#creativejournaling #journaling #journal',
      url: 'https://www.youtube.com/watch?v=l1EVsfsZ9nQ',
      embedId: 'l1EVsfsZ9nQ',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://img.youtube.com/vi/l1EVsfsZ9nQ/maxresdefault.jpg'
    },
    {
      id: 'yt-api-253',
      platform: 'youtube',
      title: 'Exciting news! Our Grab Bags are back!  👀',
      description: 'This time, we’re offering different sizes to choose from, each packed with a wide selection of treasures you’ll love 😍 These are always a favorite and won’t last long, so grab yours before they’re go',
      url: 'https://www.youtube.com/watch?v=r-np3inCkVo',
      embedId: 'r-np3inCkVo',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://img.youtube.com/vi/r-np3inCkVo/maxresdefault.jpg'
    },
    {
      id: 'yt-api-254',
      platform: 'youtube',
      title: 'Everyone I have an announcement! 📣 Our Black Friday sale week starts now!!',
      description: 'Check back here daily for exclusive deals you won’t want to miss, leading up to a huge surprise on Friday, the 29th! 👀  Get ready to treat yourself! 🖤  #creativejournaling #journaling',
      url: 'https://www.youtube.com/watch?v=b5Kwy6e-zzc',
      embedId: 'b5Kwy6e-zzc',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://img.youtube.com/vi/b5Kwy6e-zzc/maxresdefault.jpg'
    },
    {
      id: 'yt-api-255',
      platform: 'youtube',
      title: 'I started this spread during a TikTok live and just had to share the finishing touches with you ✨',
      description: 'All created using supplies from our Celebration of Women treasure box! Have you grabbed yours yet?  #creativejournaling #journaling',
      url: 'https://www.youtube.com/watch?v=jAKMVdvxpAI',
      embedId: 'jAKMVdvxpAI',
      category: 'box-reveal',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://img.youtube.com/vi/jAKMVdvxpAI/maxresdefault.jpg'
    },
    {
      id: 'yt-api-256',
      platform: 'youtube',
      title: 'Here’s the first spread I’ve created using items from our ‘A Celebration of Women’ Treasure Box!',
      description: 'I’m so excited to see all the beautiful spreads you’ll create with it. Don’t forget to share and tag us! 💗  Shop the box here - https://coracreacrafts.com/products/treasure-craft-supplies-box-bimonth',
      url: 'https://www.youtube.com/watch?v=2HA4Zrpaztw',
      embedId: '2HA4Zrpaztw',
      category: 'box-reveal',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://img.youtube.com/vi/2HA4Zrpaztw/maxresdefault.jpg'
    },
    {
      id: 'yt-api-257',
      platform: 'youtube',
      title: 'Ready to add some flavor to your crafting? 🥐🍪☕️',
      description: 'Our newest Curiosities Box: Baking & Cooking is here, and I’ve been so excited to share it with you! This box has everything you need to add a sprinkle of creativity to your crafting collection. Take ',
      url: 'https://www.youtube.com/watch?v=nPKoXV6LzMY',
      embedId: 'nPKoXV6LzMY',
      category: 'box-reveal',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://img.youtube.com/vi/nPKoXV6LzMY/maxresdefault.jpg'
    },
    {
      id: 'yt-api-258',
      platform: 'youtube',
      title: 'Only ONE WEEK left to grab our Medieval Vintage Craft Box! 🏰',
      description: 'Don’t miss your chance to own this enchanting collection of medieval-inspired treasures ✨  Shop here - https://coracreacrafts.com/products/vintage-craft-supplies-box-monthly-subscription  #creativejou',
      url: 'https://www.youtube.com/watch?v=U4orYJ9JMhk',
      embedId: 'U4orYJ9JMhk',
      category: 'box-reveal',
      tags: ['vintage', 'medieval', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://img.youtube.com/vi/U4orYJ9JMhk/maxresdefault.jpg'
    },
    {
      id: 'yt-api-259',
      platform: 'youtube',
      title: 'Our BRAND NEW DIY Tote Bag Kits have landed! 👜 #diy #creativejournaling #totebag #crafting',
      description: 'Get creative with three styles to choose from: Witchy, Nature, and Forests. Perfect for gifting or crafting your own unique tote!   Get yours here - https://coracreacrafts.com/products/diy-bag-creativ',
      url: 'https://www.youtube.com/watch?v=F0Ypp3l1RGk',
      embedId: 'F0Ypp3l1RGk',
      category: 'tutorial',
      tags: ['journaling', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://img.youtube.com/vi/F0Ypp3l1RGk/maxresdefault.jpg'
    },
    {
      id: 'yt-api-260',
      platform: 'youtube',
      title: 'Our Medieval VCB features a special collaboration with Meggie Retrow Boutique!',
      description: 'We worked with Meggie to bring you these stunning paper dolls! ✨ I had so much fun assembling mine!! Thank you so much, Meggie, for your incredible work! 💖',
      url: 'https://www.youtube.com/watch?v=3eOjJjyPGLs',
      embedId: '3eOjJjyPGLs',
      category: 'product-showcase',
      tags: ['medieval'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://img.youtube.com/vi/3eOjJjyPGLs/maxresdefault.jpg'
    },
    {
      id: 'yt-api-261',
      platform: 'youtube',
      title: 'After 3 months of hard work, our new website is now LIVE! Get 20% off site wide today to celebrate!',
      description: '',
      url: 'https://www.youtube.com/watch?v=5rwhkgz-PPs',
      embedId: '5rwhkgz-PPs',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://img.youtube.com/vi/5rwhkgz-PPs/maxresdefault.jpg'
    },
    {
      id: 'yt-api-262',
      platform: 'youtube',
      title: 'PLEASE PLEASE PLEASE Get me a CoraCreaCrafts Advent Calendar for Christmas! 🎄 SPOILERS!🚨',
      description: '#adventcalendar #creativejournaling',
      url: 'https://www.youtube.com/watch?v=MjsuHxbpDQU',
      embedId: 'MjsuHxbpDQU',
      category: 'journal-spread',
      tags: ['journaling', 'christmas', 'advent calendar'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://img.youtube.com/vi/MjsuHxbpDQU/maxresdefault.jpg'
    },
    {
      id: 'yt-api-263',
      platform: 'youtube',
      title: 'Creating spreads using our Medieval Vintage Craft Box has been so much fun!!  #creativejournaling',
      description: 'The supplies in this box are such a joy to work with 💫  Have you got yours yet? #journaling #journal',
      url: 'https://www.youtube.com/watch?v=oQXfel3_ysE',
      embedId: 'oQXfel3_ysE',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'medieval'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://img.youtube.com/vi/oQXfel3_ysE/maxresdefault.jpg'
    },
    {
      id: 'yt-api-264',
      platform: 'youtube',
      title: 'SPOILERS🚨 I’m absolutely blown away by how many of you have already grabbed a 2024 Advent Calendar!🎄',
      description: 'If you haven’t gotten yours yet, there’s still time! Make this holiday season extra special and count down to Christmas with a little surprise each day 🎁✨ Don’t miss out!   #creativejournaling #journ',
      url: 'https://www.youtube.com/watch?v=vQ_9LJF2flI',
      embedId: 'vQ_9LJF2flI',
      category: 'journal-spread',
      tags: ['journaling', 'christmas', 'advent calendar'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://img.youtube.com/vi/vQ_9LJF2flI/maxresdefault.jpg'
    },
    {
      id: 'yt-api-265',
      platform: 'youtube',
      title: 'Introducing \'A Celebration of Women\' our newest Treasure Box! #creativejournaling',
      description: 'This box is so special to me and I\'m super excited to announce that we collaborated with 3 amazing brands  Put And Paper Am Paper & Art Co  Sidhuras Craft Studio  to bring you some exclusive items! I ',
      url: 'https://www.youtube.com/watch?v=7EevWleEvWU',
      embedId: '7EevWleEvWU',
      category: 'box-reveal',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://img.youtube.com/vi/7EevWleEvWU/maxresdefault.jpg'
    },
    {
      id: 'yt-api-266',
      platform: 'youtube',
      title: 'It’s finally time to reveal a sneak peek of our November Vintage Craft Box! ✨',
      description: 'This Medieval theme holds a special place in my heart. I can’t wait for you to experience the magic and charm of this collection! 🏰✨ Hope you adore it as much as I do!',
      url: 'https://www.youtube.com/watch?v=rudulUvPNgs',
      embedId: 'rudulUvPNgs',
      category: 'box-reveal',
      tags: ['vintage', 'medieval'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://img.youtube.com/vi/rudulUvPNgs/maxresdefault.jpg'
    },
    {
      id: 'yt-api-267',
      platform: 'youtube',
      title: 'Take a look at my new favorite journal! ✨ Meet our new Owl Grimoire Foil Notebook!',
      description: 'I hope this notebook will inspire you on your creative journey! It\'s a hardcover with beautiful foiled gold decorations on the front and inside the pages have an aged, parchment-like look. Is this on ',
      url: 'https://www.youtube.com/watch?v=5jnOZboP9Gg',
      embedId: '5jnOZboP9Gg',
      category: 'product-showcase',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://img.youtube.com/vi/5jnOZboP9Gg/maxresdefault.jpg'
    },
    {
      id: 'yt-api-268',
      platform: 'youtube',
      title: 'Make some Autumnal Tags with me using the supplies from my An Air of Autumn Box 🍂',
      description: '#creativejournaling #scrapbooking #journaling',
      url: 'https://www.youtube.com/watch?v=_BDclPWBCj0',
      embedId: '_BDclPWBCj0',
      category: 'box-reveal',
      tags: ['journaling', 'autumn'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://img.youtube.com/vi/_BDclPWBCj0/maxresdefault.jpg'
    },
    {
      id: 'yt-api-269',
      platform: 'youtube',
      title: 'SPOILER ALERT! Our 2024 Advent Calendar reveal is finally here!! I\'ve been so excited for this! 🎁',
      description: '#creativejournaling #adventcalendars #scrapbooking #journaling   We Wish You a Merry Christmas by Twin Musicom is licensed under a Creative Commons Attribution 4.0 license. https://creativecommons.org',
      url: 'https://www.youtube.com/watch?v=Sm0i4x-Vezc',
      embedId: 'Sm0i4x-Vezc',
      category: 'box-reveal',
      tags: ['journaling', 'christmas', 'advent calendar'],
      creator: 'CoraCreaCrafts',
      date: '2024-11',
      thumbnail: 'https://img.youtube.com/vi/Sm0i4x-Vezc/maxresdefault.jpg'
    },
    {
      id: 'yt-api-270',
      platform: 'youtube',
      title: 'Happy Halloween friends! This spooky spread was made by our ambassador @liu_journal  🎃',
      description: '#creativejournaling #scrapbooking #halloweenjournal #halloweencraft',
      url: 'https://www.youtube.com/watch?v=h-ZjizzcwPU',
      embedId: 'h-ZjizzcwPU',
      category: 'journal-spread',
      tags: ['journaling', 'halloween'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://img.youtube.com/vi/h-ZjizzcwPU/maxresdefault.jpg'
    },
    {
      id: 'yt-api-271',
      platform: 'youtube',
      title: 'I thought it would be fun for me to take you through a timestamped day in my life! ❤️',
      description: '#creativejournaling #dayinthelife #dayinmylife #dailyvlog #scrapbooking',
      url: 'https://www.youtube.com/watch?v=-FRr6PuoAlQ',
      embedId: '-FRr6PuoAlQ',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://img.youtube.com/vi/-FRr6PuoAlQ/maxresdefault.jpg'
    },
    {
      id: 'yt-api-272',
      platform: 'youtube',
      title: 'I absolutely loved making this small style journal out of our October Digital Journal Kit!',
      description: '#creativejournaling #scrapbooking #journaling #scrapbook',
      url: 'https://www.youtube.com/watch?v=cwNHTj4KzQ4',
      embedId: 'cwNHTj4KzQ4',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://img.youtube.com/vi/cwNHTj4KzQ4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-273',
      platform: 'youtube',
      title: 'To celebrate spooky season get 20% off our Halloween Collection using code lastmin on our website 👻',
      description: '#creativejournaling #halloweencraft #scrapbooking',
      url: 'https://www.youtube.com/watch?v=AAtcYxKC-m4',
      embedId: 'AAtcYxKC-m4',
      category: 'journal-spread',
      tags: ['journaling', 'halloween'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://img.youtube.com/vi/AAtcYxKC-m4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-274',
      platform: 'youtube',
      title: 'There\'s only a few days left to get your hands on our Vintage Pharmacy Vintage Craft Box!',
      description: '#creativejournaling #scrapbooking #junkjournaltutorialsandmore #journaling',
      url: 'https://www.youtube.com/watch?v=4zbqmRclZjU',
      embedId: '4zbqmRclZjU',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'tutorial'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://img.youtube.com/vi/4zbqmRclZjU/maxresdefault.jpg'
    },
    {
      id: 'yt-api-275',
      platform: 'youtube',
      title: 'Who\'s excited for Halloween? 👻  There\'s still time to shop our Halloween Collection 🎃',
      description: '#halloween #halloweencraft #creativejournaling #scrapbooking #journaling',
      url: 'https://www.youtube.com/watch?v=Ojem2XDYurM',
      embedId: 'Ojem2XDYurM',
      category: 'journal-spread',
      tags: ['journaling', 'halloween'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://img.youtube.com/vi/Ojem2XDYurM/maxresdefault.jpg'
    },
    {
      id: 'yt-api-276',
      platform: 'youtube',
      title: 'Have you tried our Digital Journal Kit yet? The themes of these follow our monthly Vintage Craft Box',
      description: '#creativejournaling #scrapbook #scrapbooking',
      url: 'https://www.youtube.com/watch?v=57YRE2-r_94',
      embedId: '57YRE2-r_94',
      category: 'box-reveal',
      tags: ['journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://img.youtube.com/vi/57YRE2-r_94/maxresdefault.jpg'
    },
    {
      id: 'yt-api-277',
      platform: 'youtube',
      title: 'Our workshop is less than 1 week away - I\'m so excited! 🥹 #nashville #nashvilleevents #journaling',
      description: 'Here is a tour of the beautiful space where it will be held! Don\'t forget its on 26th October 4pm-6pm at Forevermore in Nashville. We still have tickets available you can get them here - https://www.e',
      url: 'https://www.youtube.com/watch?v=ssyVvmWhVPo',
      embedId: 'ssyVvmWhVPo',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://img.youtube.com/vi/ssyVvmWhVPo/maxresdefault.jpg'
    },
    {
      id: 'yt-api-278',
      platform: 'youtube',
      title: 'Double page Ancient Pharmacy spread ✨ I\'ve really loved working with this theme so far!',
      description: '#creativejournaling #scrapbooking #journaling #scrapbook',
      url: 'https://www.youtube.com/watch?v=PS5jVdjiVrA',
      embedId: 'PS5jVdjiVrA',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://img.youtube.com/vi/PS5jVdjiVrA/maxresdefault.jpg'
    },
    {
      id: 'yt-api-279',
      platform: 'youtube',
      title: 'Harry Potter is one of my favorite things to watch this time of year 🖤 #harrypotter #scrapbooking',
      description: '',
      url: 'https://www.youtube.com/watch?v=YqQrNuCq6ms',
      embedId: 'YqQrNuCq6ms',
      category: 'product-showcase',
      tags: ['harry potter'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://img.youtube.com/vi/YqQrNuCq6ms/maxresdefault.jpg'
    },
    {
      id: 'yt-api-280',
      platform: 'youtube',
      title: 'I finally made my first spread in my new journal using our restocked Mysteries of The Night supplies',
      description: '#creativejournaling #scrapbooking #journaling #scrapbook',
      url: 'https://www.youtube.com/watch?v=70OhRwHTOzA',
      embedId: '70OhRwHTOzA',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://img.youtube.com/vi/70OhRwHTOzA/maxresdefault.jpg'
    },
    {
      id: 'yt-api-281',
      platform: 'youtube',
      title: 'Vintage Pharmacy is here! This box even comes with a special treat inside  #scrapbooking #journaling',
      description: 'Which product is your favourite? Now available on our website',
      url: 'https://www.youtube.com/watch?v=lS-k01-YOVo',
      embedId: 'lS-k01-YOVo',
      category: 'box-reveal',
      tags: ['journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://img.youtube.com/vi/lS-k01-YOVo/maxresdefault.jpg'
    },
    {
      id: 'yt-api-282',
      platform: 'youtube',
      title: 'Make a spooky card with me using some of my favorite Halloween supplies! 🖤👻',
      description: 'Have you started your halloween crafting yet? 🎃   #creativejournaling #scrapbooking #journaling #halloween',
      url: 'https://www.youtube.com/watch?v=B57blBSN5_0',
      embedId: 'B57blBSN5_0',
      category: 'journal-spread',
      tags: ['journaling', 'halloween'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://img.youtube.com/vi/B57blBSN5_0/maxresdefault.jpg'
    },
    {
      id: 'yt-api-283',
      platform: 'youtube',
      title: 'Part 2 of my quick-fire Q&A is here! I hope you all enjoyed watching as much as I did answering them',
      description: 'Let me know if you\'d love to see more of these in the future! 👀💬  #questions #creativejournaling #scrapbooking #journaling #scrapbook #quickfirequestions',
      url: 'https://www.youtube.com/watch?v=RqW1JXLgBIM',
      embedId: 'RqW1JXLgBIM',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://img.youtube.com/vi/RqW1JXLgBIM/maxresdefault.jpg'
    },
    {
      id: 'yt-api-284',
      platform: 'youtube',
      title: 'Make a Autumnal tote bag with me while I tell you about our upcoming workshop here in Nashville! 👀',
      description: 'Join us for \'Autumn Artistry\': A DIY Canvas Bag Decoration Workshop where you can transform a simple tote into your perfect fall accessory! 📅 Saturday, October 26th  🕓 4 - 6pm CDT  📍 Forevermore - ',
      url: 'https://www.youtube.com/watch?v=l_76M7KRlj4',
      embedId: 'l_76M7KRlj4',
      category: 'tutorial',
      tags: ['autumn', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://img.youtube.com/vi/l_76M7KRlj4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-285',
      platform: 'youtube',
      title: 'It’s my birthday! 🎉  To celebrate get 15% and get a free gift from me with any order placed today!',
      description: 'MUST END 11:59PM CST 4TH OCTOBER Head to our website - https://coracreacrafts.com/  #quickfirequestions #birthday #crafting #scrapbooking #journaling #creativejournaling',
      url: 'https://www.youtube.com/watch?v=wXhWSlv8M5E',
      embedId: 'wXhWSlv8M5E',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://img.youtube.com/vi/wXhWSlv8M5E/maxresdefault.jpg'
    },
    {
      id: 'yt-api-286',
      platform: 'youtube',
      title: 'You heard it here first! Me and @adriannasjournals are hosting our first ever workshop in Nashville✨',
      description: 'Join us for \'Autumn Artistry\': A DIY Canvas Bag Decoration Workshop where you can transform a simple tote into your perfect fall accessory! 📅 Saturday, October 26th  🕓 4 - 6pm CDT  📍 Forevermore - ',
      url: 'https://www.youtube.com/watch?v=ze2fnD8_y-M',
      embedId: 'ze2fnD8_y-M',
      category: 'tutorial',
      tags: ['journaling', 'autumn', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://img.youtube.com/vi/ze2fnD8_y-M/maxresdefault.jpg'
    },
    {
      id: 'yt-api-287',
      platform: 'youtube',
      title: 'TikTok Shop Live Host Wanted in Nashville! Read the caption for more information ⬇️',
      description: 'We\'re looking for a vibrant TikTok Shop Live Host to join our team!  Think you’ve got what it takes? DM us on IG for all the details!  Instagram - @coracreacrafts  #tiktok #tiktoklive #nashville',
      url: 'https://www.youtube.com/watch?v=-CCiNRzWwwc',
      embedId: '-CCiNRzWwwc',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://img.youtube.com/vi/-CCiNRzWwwc/maxresdefault.jpg'
    },
    {
      id: 'yt-api-288',
      platform: 'youtube',
      title: 'October is here! 🎃 It\'s only right to kick off spooky season with a witchy-inspired tutorial 🧙‍♀️',
      description: '#creativejournaling #halloween #halloweencraft #scrapbooking  Spooky Ride by Twin Musicom is licensed under a Creative Commons Attribution 4.0 license. https://creativecommons.org/licenses/by/4.0/',
      url: 'https://www.youtube.com/watch?v=--R3XaxTJMM',
      embedId: '--R3XaxTJMM',
      category: 'tutorial',
      tags: ['journaling', 'halloween', 'tutorial'],
      creator: 'CoraCreaCrafts',
      date: '2024-10',
      thumbnail: 'https://img.youtube.com/vi/--R3XaxTJMM/maxresdefault.jpg'
    },
    {
      id: 'yt-api-289',
      platform: 'youtube',
      title: 'Spooky Season has arrived! 🎃 Check out our newest Halloween arrivals now!  #creativejournaling',
      description: '#scrapbooking #halloween #halloweencraft #junkjournaltutorialsandmore   Darkest Child by Kevin MacLeod is licensed under a Creative Commons Attribution 4.0 license. https://creativecommons.org/license',
      url: 'https://www.youtube.com/watch?v=IzbMLv_U3ic',
      embedId: 'IzbMLv_U3ic',
      category: 'tutorial',
      tags: ['journaling', 'halloween', 'tutorial'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://img.youtube.com/vi/IzbMLv_U3ic/maxresdefault.jpg'
    },
    {
      id: 'yt-api-290',
      platform: 'youtube',
      title: 'A beginner-friendly tutorial using our Mysteries of the Night TB! ✨',
      description: '#creativejournaling #journaling #junkjournal',
      url: 'https://www.youtube.com/watch?v=CLAPQ6prBmk',
      embedId: 'CLAPQ6prBmk',
      category: 'tutorial',
      tags: ['journaling', 'tutorial'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://img.youtube.com/vi/CLAPQ6prBmk/maxresdefault.jpg'
    },
    {
      id: 'yt-api-291',
      platform: 'youtube',
      title: 'A water themed spread using our Four Elements VCB! Theres only 3 days left to get our September Box✨',
      description: '#creativejournaling #journaling #craftbox #junkjournal',
      url: 'https://www.youtube.com/watch?v=ZLnSUHG5Qs4',
      embedId: 'ZLnSUHG5Qs4',
      category: 'box-reveal',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://img.youtube.com/vi/ZLnSUHG5Qs4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-292',
      platform: 'youtube',
      title: 'Today marks the official start of fall 🍂 Heres a spread using our An Air Of Autumn Curiosities box',
      description: '#creativejournaling #scrapbooking #journaling',
      url: 'https://www.youtube.com/watch?v=16ajJNRSGTs',
      embedId: '16ajJNRSGTs',
      category: 'box-reveal',
      tags: ['journaling', 'autumn'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://img.youtube.com/vi/16ajJNRSGTs/maxresdefault.jpg'
    },
    {
      id: 'yt-api-293',
      platform: 'youtube',
      title: 'Dreams, sketched in passion, now stitched in leather 🖤 Feeling these designs in my hands is surreal',
      description: '#pov #creativejournaling #scrapbooking #craftbox #journaling',
      url: 'https://www.youtube.com/watch?v=d1hCG3HNOKw',
      embedId: 'd1hCG3HNOKw',
      category: 'box-reveal',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://img.youtube.com/vi/d1hCG3HNOKw/maxresdefault.jpg'
    },
    {
      id: 'yt-api-294',
      platform: 'youtube',
      title: 'Part 2 of our \'Mysteries Of The Night\' TB unboxing is here!  What\'s your favorite item so far?',
      description: '#creativejournaling #scrapbooking #junkjournal',
      url: 'https://www.youtube.com/watch?v=F63CIEgdrGk',
      embedId: 'F63CIEgdrGk',
      category: 'unboxing',
      tags: ['journaling', 'unboxing'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://img.youtube.com/vi/F63CIEgdrGk/maxresdefault.jpg'
    },
    {
      id: 'yt-api-295',
      platform: 'youtube',
      title: 'It\'s SCRAPTEMBER! 🍁 Head over to our IG to take part in our Scraptember post! #creativejournaling',
      description: '#scrapbooking #creativity #scraptember #scrapbooking',
      url: 'https://www.youtube.com/watch?v=V1UHxQmECa4',
      embedId: 'V1UHxQmECa4',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://img.youtube.com/vi/V1UHxQmECa4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-296',
      platform: 'youtube',
      title: 'DIY Dinner Party Hack - Make your table cards! Using Hester & Cook and our Vineyard & Orchards items',
      description: '#diy #diycrafts #dinnerparty #craftsupplies',
      url: 'https://www.youtube.com/watch?v=wUYRAzTbi-o',
      embedId: 'wUYRAzTbi-o',
      category: 'tutorial',
      tags: ['diy'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://img.youtube.com/vi/wUYRAzTbi-o/maxresdefault.jpg'
    },
    {
      id: 'yt-api-297',
      platform: 'youtube',
      title: 'Our brand new leather collection is live!! Which style and color do you prefer? ❤️',
      description: '#creativejournal #craftbox #creativejournal #leathercraft #leatherfashion',
      url: 'https://www.youtube.com/watch?v=mOUPszxdUF0',
      embedId: 'mOUPszxdUF0',
      category: 'box-reveal',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://img.youtube.com/vi/mOUPszxdUF0/maxresdefault.jpg'
    },
    {
      id: 'yt-api-298',
      platform: 'youtube',
      title: 'It was only right to do this spread today! Happy Friday the 13th friends 🖤 #creativejournaling',
      description: '#junkjournal #scrapbook #creativejournaling #asmrjournaling',
      url: 'https://www.youtube.com/watch?v=022S1PhpDn0',
      embedId: '022S1PhpDn0',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://img.youtube.com/vi/022S1PhpDn0/maxresdefault.jpg'
    },
    {
      id: 'yt-api-299',
      platform: 'youtube',
      title: 'BTS of making a wooden sign for CCC! This was so much fun and so nice to see the sign on display ❤️',
      description: '#creativejournaling #diycraft #diycrafts #craftsupplies',
      url: 'https://www.youtube.com/watch?v=dsoMi4SL5Ck',
      embedId: 'dsoMi4SL5Ck',
      category: 'tutorial',
      tags: ['journaling', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://img.youtube.com/vi/dsoMi4SL5Ck/maxresdefault.jpg'
    },
    {
      id: 'yt-api-300',
      platform: 'youtube',
      title: 'Which one do you think matches the best?👀 #creativejournaling #scrapbook #craftsupplies #creativity',
      description: '',
      url: 'https://www.youtube.com/watch?v=PcIDD6XNs-s',
      embedId: 'PcIDD6XNs-s',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://img.youtube.com/vi/PcIDD6XNs-s/maxresdefault.jpg'
    },
    {
      id: 'yt-api-301',
      platform: 'youtube',
      title: 'Highlights part 1 from our Mysteries of the Night Treasure Box! Part 2 coming soon ❤️',
      description: 'Orders yours now!  https://coracreacrafts.com/product/treasure-craft-supplies-box-bimonthly-subscription/',
      url: 'https://www.youtube.com/watch?v=y95OLcaJN_k',
      embedId: 'y95OLcaJN_k',
      category: 'box-reveal',
      tags: ['subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://img.youtube.com/vi/y95OLcaJN_k/maxresdefault.jpg'
    },
    {
      id: 'yt-api-302',
      platform: 'youtube',
      title: 'An Air of Autumn! Our new curiosities box celebrates this beautiful season! Order yours now 🍁',
      description: '',
      url: 'https://www.youtube.com/watch?v=tW5bVUGf5iE',
      embedId: 'tW5bVUGf5iE',
      category: 'box-reveal',
      tags: ['autumn'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://img.youtube.com/vi/tW5bVUGf5iE/maxresdefault.jpg'
    },
    {
      id: 'yt-api-303',
      platform: 'youtube',
      title: 'We are looking for female owned small business to collab with on an exciting project! Tag any below!',
      description: '#femalebusinessowners #femalebusinessowner #femaleentrepreneur #smallbusiness #smallbusinessowner',
      url: 'https://www.youtube.com/watch?v=H1GxN4O1R4U',
      embedId: 'H1GxN4O1R4U',
      category: 'behind-the-scenes',
      tags: ['small business'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://img.youtube.com/vi/H1GxN4O1R4U/maxresdefault.jpg'
    },
    {
      id: 'yt-api-304',
      platform: 'youtube',
      title: 'Let\'s pack a grab bag together! ✨ Our Labor Day Sale ends today! Head to our website to shop now!',
      description: '#creativity #laborday #labordaysale #creativejournaling #journalspread',
      url: 'https://www.youtube.com/watch?v=OAMpbkfzoXY',
      embedId: 'OAMpbkfzoXY',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://img.youtube.com/vi/OAMpbkfzoXY/maxresdefault.jpg'
    },
    {
      id: 'yt-api-305',
      platform: 'youtube',
      title: 'Don\'t miss out on our Labor day sale!! Must end tomorrow 11:59 CST ❤️',
      description: '#scrapbooking #junkjournal #creativejournal #creativejournaling',
      url: 'https://www.youtube.com/watch?v=R1PmUCQ6UHY',
      embedId: 'R1PmUCQ6UHY',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://img.youtube.com/vi/R1PmUCQ6UHY/maxresdefault.jpg'
    },
    {
      id: 'yt-api-306',
      platform: 'youtube',
      title: 'Our Labor Day sale is live! Our grab bags are back in not 1 but 6 different styles/sizes!!',
      description: '#creativejournaling #scrapbooking #laborday #journalspread #journaling',
      url: 'https://www.youtube.com/watch?v=jIppSbJZka4',
      embedId: 'jIppSbJZka4',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-09',
      thumbnail: 'https://img.youtube.com/vi/jIppSbJZka4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-307',
      platform: 'youtube',
      title: 'We are so excited for our 2024 advent calendars! Read more below ⬇️ #creativejournal #adventcalendar',
      description: 'While these sneak peaks are from last year this might give you a little idea of what you could receive! The packaging and products will be all new for 2024 😍 This is our biggest and most fun box of t',
      url: 'https://www.youtube.com/watch?v=KG3Kg_RnjzI',
      embedId: 'KG3Kg_RnjzI',
      category: 'box-reveal',
      tags: ['journaling', 'advent calendar'],
      creator: 'CoraCreaCrafts',
      date: '2024-08',
      thumbnail: 'https://img.youtube.com/vi/KG3Kg_RnjzI/maxresdefault.jpg'
    },
    {
      id: 'yt-api-308',
      platform: 'youtube',
      title: 'Our 4 Elements VCB Box is here! Which product is your favorite? 👀  #creativejournaling #craftbox',
      description: '',
      url: 'https://www.youtube.com/watch?v=pFraBGMXEOk',
      embedId: 'pFraBGMXEOk',
      category: 'box-reveal',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-08',
      thumbnail: 'https://img.youtube.com/vi/pFraBGMXEOk/maxresdefault.jpg'
    },
    {
      id: 'yt-api-309',
      platform: 'youtube',
      title: 'Another American Wild West Themed Spread! We switch to our next VCB theme today are you ready?👀',
      description: '#scrapbooking #creativejournal #creativejournaling #journaling #junkjournaltutorialsandmore #craftsupplies',
      url: 'https://www.youtube.com/watch?v=9lU9cGjRqHs',
      embedId: '9lU9cGjRqHs',
      category: 'tutorial',
      tags: ['journaling', 'tutorial'],
      creator: 'CoraCreaCrafts',
      date: '2024-08',
      thumbnail: 'https://img.youtube.com/vi/9lU9cGjRqHs/maxresdefault.jpg'
    },
    {
      id: 'yt-api-310',
      platform: 'youtube',
      title: 'Table cards meet scrapbooking! Decorating these table cards from Hester and Cook with CCC products!',
      description: '#diycrafts #craftbox #journalspread #scrapbooking #creativejournaling #dinnerparty #tablecards #hesterandcook #coracreacrafts',
      url: 'https://www.youtube.com/watch?v=caA9GI74Btk',
      embedId: 'caA9GI74Btk',
      category: 'box-reveal',
      tags: ['journaling', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2024-08',
      thumbnail: 'https://img.youtube.com/vi/caA9GI74Btk/maxresdefault.jpg'
    },
    {
      id: 'yt-api-311',
      platform: 'youtube',
      title: 'Minimalistic Collage Spread! I was super inspired by Rositonacostudio for this and loved making it!',
      description: '#creativejournaling #scrapbooking #junkjournalfun #junkjournal #spread #asmrjournaling #creativejournal',
      url: 'https://www.youtube.com/watch?v=_c8aItjcTL0',
      embedId: '_c8aItjcTL0',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-08',
      thumbnail: 'https://img.youtube.com/vi/_c8aItjcTL0/maxresdefault.jpg'
    },
    {
      id: 'yt-api-312',
      platform: 'youtube',
      title: 'Pack a big order with me from our new warehouse! #creativejournaling #journaling #scrapbooking',
      description: '',
      url: 'https://www.youtube.com/watch?v=g5HJUmAWGio',
      embedId: 'g5HJUmAWGio',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-08',
      thumbnail: 'https://img.youtube.com/vi/g5HJUmAWGio/maxresdefault.jpg'
    },
    {
      id: 'yt-api-313',
      platform: 'youtube',
      title: 'I love using inkpads, pastes and paints! How many of you guys use these in your spreads?',
      description: '#creativejournaling #creativejournal #scrapbooking #junkjournalfun #junkjournal',
      url: 'https://www.youtube.com/watch?v=e9zb1NHKi_Y',
      embedId: 'e9zb1NHKi_Y',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-08',
      thumbnail: 'https://img.youtube.com/vi/e9zb1NHKi_Y/maxresdefault.jpg'
    },
    {
      id: 'yt-api-314',
      platform: 'youtube',
      title: 'Washi Tape Haul - Part 2 👀 Rank your favourite tapes in order down in the comments! #washitape #',
      description: '#creativejournaling #scrapbooking #journalspread #junkjournal #craftbox',
      url: 'https://www.youtube.com/watch?v=LhxPtcfNe7k',
      embedId: 'LhxPtcfNe7k',
      category: 'unboxing',
      tags: ['washi tape', 'journaling', 'haul'],
      creator: 'CoraCreaCrafts',
      date: '2024-08',
      thumbnail: 'https://img.youtube.com/vi/LhxPtcfNe7k/maxresdefault.jpg'
    },
    {
      id: 'yt-api-315',
      platform: 'youtube',
      title: 'A new spread in my mini journal - Wild West theme 🤠 #minijournal #asmrjournaling #creativejournaling',
      description: '',
      url: 'https://www.youtube.com/watch?v=8FXIXUxLDkE',
      embedId: '8FXIXUxLDkE',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-08',
      thumbnail: 'https://img.youtube.com/vi/8FXIXUxLDkE/maxresdefault.jpg'
    },
    {
      id: 'yt-api-316',
      platform: 'youtube',
      title: 'Washi Tape Haul - Part 1 😍 Which one is your favorite? #scrapbooking #washitape #creativejournaling',
      description: '',
      url: 'https://www.youtube.com/watch?v=ABi1oEZUVCg',
      embedId: 'ABi1oEZUVCg',
      category: 'unboxing',
      tags: ['washi tape', 'journaling', 'haul'],
      creator: 'CoraCreaCrafts',
      date: '2024-08',
      thumbnail: 'https://img.youtube.com/vi/ABi1oEZUVCg/maxresdefault.jpg'
    },
    {
      id: 'yt-api-317',
      platform: 'youtube',
      title: 'Wild West Spread! Get your August Vintage Craft Box below! #creativejournaling #scrapbooking #spread',
      description: 'https://coracreacrafts.com/product/vintage-craft-supplies-box-monthly-subscription/ Get your Wild West Box now!',
      url: 'https://www.youtube.com/watch?v=5nUNfsb6OgQ',
      embedId: '5nUNfsb6OgQ',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2024-08',
      thumbnail: 'https://img.youtube.com/vi/5nUNfsb6OgQ/maxresdefault.jpg'
    },
    {
      id: 'yt-api-318',
      platform: 'youtube',
      title: 'A few people have asked for a flip through of some of my spreads so here is my large journal one ❤️',
      description: '#creativejournaling #scrapbooking #junkjournal #junkjournalfun #journalspread',
      url: 'https://www.youtube.com/watch?v=zpq9gKyk2Ls',
      embedId: 'zpq9gKyk2Ls',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-08',
      thumbnail: 'https://img.youtube.com/vi/zpq9gKyk2Ls/maxresdefault.jpg'
    },
    {
      id: 'yt-api-319',
      platform: 'youtube',
      title: 'What do you think about this unpopular opinion? 👀 #creativejournaling #scrapbooking #asmrjournaling',
      description: '',
      url: 'https://www.youtube.com/watch?v=gJd7KcD8bVw',
      embedId: 'gJd7KcD8bVw',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-08',
      thumbnail: 'https://img.youtube.com/vi/gJd7KcD8bVw/maxresdefault.jpg'
    },
    {
      id: 'yt-api-320',
      platform: 'youtube',
      title: 'Vineyards Mini Journal! Such a beautiful spread created by our ambassador - suchi_journalive (IG) ❤️',
      description: '#creativejournaling #minijournal #asmrjournaling #scrapbooking',
      url: 'https://www.youtube.com/watch?v=Y0EwK-Rf9ns',
      embedId: 'Y0EwK-Rf9ns',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-08',
      thumbnail: 'https://img.youtube.com/vi/Y0EwK-Rf9ns/maxresdefault.jpg'
    },
    {
      id: 'yt-api-321',
      platform: 'youtube',
      title: 'Tales of the North! 🤎  It\'s been the best seeing everyones love for this box! #creativejournaling',
      description: '#creativejournaling #asmrjournaling #asmrsounds #scrapbooking #spread #vikings',
      url: 'https://www.youtube.com/watch?v=dW_bj5nGZ60',
      embedId: 'dW_bj5nGZ60',
      category: 'box-reveal',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-08',
      thumbnail: 'https://img.youtube.com/vi/dW_bj5nGZ60/maxresdefault.jpg'
    },
    {
      id: 'yt-api-322',
      platform: 'youtube',
      title: 'The behind the scenes of my move to Nashville! I\'m so happy to be here & excited for what\'s to come!',
      description: 'Thingamajig by Audionautix is licensed under a Creative Commons Attribution 4.0 license. https://creativecommons.org/licenses/by/4.0/  Artist: http://audionautix.com/',
      url: 'https://www.youtube.com/watch?v=W4A5-zJVKmE',
      embedId: 'W4A5-zJVKmE',
      category: 'behind-the-scenes',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2024-08',
      thumbnail: 'https://img.youtube.com/vi/W4A5-zJVKmE/maxresdefault.jpg'
    },
    {
      id: 'yt-api-323',
      platform: 'youtube',
      title: 'Decorate my Clash of Kings book with me using my favorite craft supplies! #creativejournaling',
      description: '#gameofthrones #houseofthedragon #scrapbooking #journaling #journalspread',
      url: 'https://www.youtube.com/watch?v=aWok-Vjg50s',
      embedId: 'aWok-Vjg50s',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-08',
      thumbnail: 'https://img.youtube.com/vi/aWok-Vjg50s/maxresdefault.jpg'
    },
    {
      id: 'yt-api-324',
      platform: 'youtube',
      title: 'Happy Friendship Day! Get 10% off the store with code FRIENDS ❤️',
      description: 'A heartfelt thank you to our amazing community! Your continued support has been the driving force behind CoraCreaCrafts. It\'s been incredible watching our community grow and witnessing the beautiful f',
      url: 'https://www.youtube.com/watch?v=P7GMG3yJqLk',
      embedId: 'P7GMG3yJqLk',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2024-07',
      thumbnail: 'https://img.youtube.com/vi/P7GMG3yJqLk/maxresdefault.jpg'
    },
    {
      id: 'yt-api-325',
      platform: 'youtube',
      title: 'Unbox our August Vintage Craft Subscription Box with me! I\'m obsessed with the theme \'The Wild West\'',
      description: '#journaling #scrapbooking #junkjournalspread #journalspread #creativejournaling #unboxing #wildwest #cowboys',
      url: 'https://www.youtube.com/watch?v=Oq9Esl4O-Rg',
      embedId: 'Oq9Esl4O-Rg',
      category: 'unboxing',
      tags: ['journaling', 'vintage', 'subscription box', 'unboxing'],
      creator: 'CoraCreaCrafts',
      date: '2024-07',
      thumbnail: 'https://img.youtube.com/vi/Oq9Esl4O-Rg/maxresdefault.jpg'
    },
    {
      id: 'yt-api-326',
      platform: 'youtube',
      title: 'Our August Vintage Craft Subscription Box is here! I absolutely love this theme of The Wild West 🤠',
      description: '#scrapbooking #journalspread #creativejournaling #wildwest #asmrjournaling #junkjournal',
      url: 'https://www.youtube.com/watch?v=r9TxKV554oc',
      embedId: 'r9TxKV554oc',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2024-07',
      thumbnail: 'https://img.youtube.com/vi/r9TxKV554oc/maxresdefault.jpg'
    },
    {
      id: 'yt-api-327',
      platform: 'youtube',
      title: 'Ephemera Pouch Tutorial! Made by our lovely ambassador Teddy @hippie.craft.fairy on IG ❤️',
      description: '#creativejournaling #junkjournalling #journaling #journalspread #junkjournalspread #scrapbooking',
      url: 'https://www.youtube.com/watch?v=CL4hM03v8FQ',
      embedId: 'CL4hM03v8FQ',
      category: 'tutorial',
      tags: ['journaling', 'tutorial'],
      creator: 'CoraCreaCrafts',
      date: '2024-07',
      thumbnail: 'https://img.youtube.com/vi/CL4hM03v8FQ/maxresdefault.jpg'
    },
    {
      id: 'yt-api-328',
      platform: 'youtube',
      title: 'Take a closer look at our Tales of the North box! #scrapbooking #junkjournaling #creativejournaling',
      description: '',
      url: 'https://www.youtube.com/watch?v=F6df6FCFLDc',
      embedId: 'F6df6FCFLDc',
      category: 'box-reveal',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-07',
      thumbnail: 'https://img.youtube.com/vi/F6df6FCFLDc/maxresdefault.jpg'
    },
    {
      id: 'yt-api-329',
      platform: 'youtube',
      title: 'Fantasy Large spread using the supplies from our Treasure Box  #creativejournaling #journalspread',
      description: 'Shop our treasure box here - https://coracreacrafts.com/product/treasure-craft-supplies-box-bimonthly-subscription/',
      url: 'https://www.youtube.com/watch?v=3srJlJCukoM',
      embedId: '3srJlJCukoM',
      category: 'box-reveal',
      tags: ['journaling', 'fantasy', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2024-07',
      thumbnail: 'https://img.youtube.com/vi/3srJlJCukoM/maxresdefault.jpg'
    },
    {
      id: 'yt-api-330',
      platform: 'youtube',
      title: 'English Countryside Journal Spread #journalspread #creativejournaling #junkjournaling',
      description: 'You can shop our English Countryside Box here - https://coracreacrafts.com/product/englishcountry-box/',
      url: 'https://www.youtube.com/watch?v=Pr0uIBIEcGU',
      embedId: 'Pr0uIBIEcGU',
      category: 'box-reveal',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-07',
      thumbnail: 'https://img.youtube.com/vi/Pr0uIBIEcGU/maxresdefault.jpg'
    },
    {
      id: 'yt-api-331',
      platform: 'youtube',
      title: 'Our Tales of The North Curiosities Preview is here! Which item is your favorite? 🛡️🏰 🗡️🌲🪓',
      description: '#creativejournaling #junkjournal #journalspread #scrapbooking #vintagejournal   Crusade - Video Classica by Kevin MacLeod is licensed under a Creative Commons Attribution 4.0 license. https://creative',
      url: 'https://www.youtube.com/watch?v=nUzFZAXnpFs',
      embedId: 'nUzFZAXnpFs',
      category: 'journal-spread',
      tags: ['journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2024-07',
      thumbnail: 'https://img.youtube.com/vi/nUzFZAXnpFs/maxresdefault.jpg'
    },
    {
      id: 'yt-api-332',
      platform: 'youtube',
      title: 'I hope you all have enjoyed me posting more! Is there anything else you would like to see me post?👀',
      description: '#creativejournaling #journaling #journalspread #vintagejournal #scrapbooking #junkjournal #junkjournaling',
      url: 'https://www.youtube.com/watch?v=qsWOhy4a8Is',
      embedId: 'qsWOhy4a8Is',
      category: 'journal-spread',
      tags: ['journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2024-07',
      thumbnail: 'https://img.youtube.com/vi/qsWOhy4a8Is/maxresdefault.jpg'
    },
    {
      id: 'yt-api-333',
      platform: 'youtube',
      title: 'Small Fantasy Spread using our new Fantasy box! #creativejournaling #vintagejournal #scrapbooking',
      description: '',
      url: 'https://www.youtube.com/watch?v=3uiKpKJY2E4',
      embedId: '3uiKpKJY2E4',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'fantasy'],
      creator: 'CoraCreaCrafts',
      date: '2024-07',
      thumbnail: 'https://img.youtube.com/vi/3uiKpKJY2E4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-334',
      platform: 'youtube',
      title: 'We only have a few left of our Independence Themed Bundle! Grab yours now #scrapbooking',
      description: 'Don\'t miss out get yours now here - https://coracreacrafts.com/product/book-bundle-alady/',
      url: 'https://www.youtube.com/watch?v=X-ODdbhrVgc',
      embedId: 'X-ODdbhrVgc',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2024-07',
      thumbnail: 'https://img.youtube.com/vi/X-ODdbhrVgc/maxresdefault.jpg'
    },
    {
      id: 'yt-api-335',
      platform: 'youtube',
      title: 'Fantasy Box Highlights 🔮 This is my favorite box of the year so far! #creativejournaling',
      description: 'You can shop the fantasy box here - https://coracreacrafts.com/product/treasure-craft-supplies-box-bimonthly-subscription/',
      url: 'https://www.youtube.com/watch?v=dM9BvKr1nYo',
      embedId: 'dM9BvKr1nYo',
      category: 'box-reveal',
      tags: ['journaling', 'fantasy', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2024-07',
      thumbnail: 'https://img.youtube.com/vi/dM9BvKr1nYo/maxresdefault.jpg'
    },
    {
      id: 'yt-api-336',
      platform: 'youtube',
      title: 'Happy Independence Day! Here is a one word a day challenge for this special day! 🇺🇸',
      description: 'In this video I used our Independence Bundle and Postal Stamp Tapes from the shop! P.S everything is on sale for the weekend 😍',
      url: 'https://www.youtube.com/watch?v=Ej4vLMSfMGk',
      embedId: 'Ej4vLMSfMGk',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2024-07',
      thumbnail: 'https://img.youtube.com/vi/Ej4vLMSfMGk/maxresdefault.jpg'
    },
    {
      id: 'yt-api-337',
      platform: 'youtube',
      title: 'One of my favorite activities is decorating my books! Have you ever done this?👀 #creativejournaling',
      description: 'Shop our exclusive 4th of July book bundle now while stocks last - https://coracreacrafts.com/product/book-bundle-alady/',
      url: 'https://www.youtube.com/watch?v=8Edaw023IrA',
      embedId: '8Edaw023IrA',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-07',
      thumbnail: 'https://img.youtube.com/vi/8Edaw023IrA/maxresdefault.jpg'
    },
    {
      id: 'yt-api-338',
      platform: 'youtube',
      title: 'Happy 1st of July! I love starting the month by organizing my Planning Companion #creativejournal',
      description: 'Get our undated planning companion here - https://coracreacrafts.com/product/coracreacrafts-planner/',
      url: 'https://www.youtube.com/watch?v=XEAlQLM4UWU',
      embedId: 'XEAlQLM4UWU',
      category: 'product-showcase',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-07',
      thumbnail: 'https://img.youtube.com/vi/XEAlQLM4UWU/maxresdefault.jpg'
    },
    {
      id: 'yt-api-339',
      platform: 'youtube',
      title: 'A beautiful spread created by our talented ambassador (IG - hippie.craft.fairy) #creativejournaling',
      description: 'Using the supplies from our May Vintage Craft Subscription Box - https://coracreacrafts.com/product-category/box/subscription/',
      url: 'https://www.youtube.com/watch?v=xUXgl5EDzTk',
      embedId: 'xUXgl5EDzTk',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://img.youtube.com/vi/xUXgl5EDzTk/maxresdefault.jpg'
    },
    {
      id: 'yt-api-340',
      platform: 'youtube',
      title: 'Travel Journal Swiss Set Up #creativejournal #scrapbooking #creativejournaling #traveljournal',
      description: '',
      url: 'https://www.youtube.com/watch?v=PkCwUlVsej8',
      embedId: 'PkCwUlVsej8',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://img.youtube.com/vi/PkCwUlVsej8/maxresdefault.jpg'
    },
    {
      id: 'yt-api-341',
      platform: 'youtube',
      title: 'An overview of our beautiful Versailles Ephmera Pack 😍 #creativejournaling #scrapbooking',
      description: 'Shop the Versailles Ephmera Pack here - https://coracreacrafts.com/product/versailles-ephemera-bundle/',
      url: 'https://www.youtube.com/watch?v=1_CrgDts1fI',
      embedId: '1_CrgDts1fI',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://img.youtube.com/vi/1_CrgDts1fI/maxresdefault.jpg'
    },
    {
      id: 'yt-api-342',
      platform: 'youtube',
      title: 'Mystical Woodlands Journal Spread #creativejournaling #scrapbooking #journalspread #vintagejournal',
      description: 'Shop our Mystical Woodlands Sticker Book here - https://coracreacrafts.com/?s=Mystical+Forest+Sticker+Book',
      url: 'https://www.youtube.com/watch?v=8gwW4koV06A',
      embedId: '8gwW4koV06A',
      category: 'journal-spread',
      tags: ['journaling', 'vintage', 'stickers'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://img.youtube.com/vi/8gwW4koV06A/maxresdefault.jpg'
    },
    {
      id: 'yt-api-343',
      platform: 'youtube',
      title: 'Large Fauna Spread | Creative Journaling #creativejournal #journalspread #scrapbooking',
      description: '',
      url: 'https://www.youtube.com/watch?v=lWVvI6h4114',
      embedId: 'lWVvI6h4114',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://img.youtube.com/vi/lWVvI6h4114/maxresdefault.jpg'
    },
    {
      id: 'yt-api-344',
      platform: 'youtube',
      title: 'Our very 1st book bundle is here! Grab yours now while stock lasts! #creativejournaling #reading',
      description: 'Limited stock available so don\'t miss out! https://coracreacrafts.com/product/book-bundle-alady/',
      url: 'https://www.youtube.com/watch?v=-TkbQ82R3Vs',
      embedId: '-TkbQ82R3Vs',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://img.youtube.com/vi/-TkbQ82R3Vs/maxresdefault.jpg'
    },
    {
      id: 'yt-api-345',
      platform: 'youtube',
      title: 'Pre-history/Geology Horizontal Spread #creativejournaling #scrapbooking #asmrjournaling',
      description: '',
      url: 'https://www.youtube.com/watch?v=DVDMyuu4TMM',
      embedId: 'DVDMyuu4TMM',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://img.youtube.com/vi/DVDMyuu4TMM/maxresdefault.jpg'
    },
    {
      id: 'yt-api-346',
      platform: 'youtube',
      title: 'Bridgerton Inspired Journal Spread using our Versailles Ephemera Pack! #bridgerton #creativejournal',
      description: '',
      url: 'https://www.youtube.com/watch?v=j2ZuFQNiyPE',
      embedId: 'j2ZuFQNiyPE',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://img.youtube.com/vi/j2ZuFQNiyPE/maxresdefault.jpg'
    },
    {
      id: 'yt-api-347',
      platform: 'youtube',
      title: 'Ancient Cities Encyclopedia Mini Book #creativejournaling #junkjournaling #asmrjournaling',
      description: 'Shop our Ancient Cities collection here - https://coracreacrafts.com/?s=ancient+cities',
      url: 'https://www.youtube.com/watch?v=dOVk5pr8K4M',
      embedId: 'dOVk5pr8K4M',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://img.youtube.com/vi/dOVk5pr8K4M/maxresdefault.jpg'
    },
    {
      id: 'yt-api-348',
      platform: 'youtube',
      title: 'Happy Summer Solstice! Celebrate the arrival of the new season ☀️ #creativejournaling #journalwithme',
      description: '',
      url: 'https://www.youtube.com/watch?v=V-OLSN-18H8',
      embedId: 'V-OLSN-18H8',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://img.youtube.com/vi/V-OLSN-18H8/maxresdefault.jpg'
    },
    {
      id: 'yt-api-349',
      platform: 'youtube',
      title: 'Fairy Mini Journal Spread | Creative Journaling #creativejournaling #minijournal #fairytales',
      description: 'You can shop our Vintage Mini Journals here - https://coracreacrafts.com/product/vintage-mini-journals/',
      url: 'https://www.youtube.com/watch?v=RUH5Q7KEGzY',
      embedId: 'RUH5Q7KEGzY',
      category: 'journal-spread',
      tags: ['journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://img.youtube.com/vi/RUH5Q7KEGzY/maxresdefault.jpg'
    },
    {
      id: 'yt-api-350',
      platform: 'youtube',
      title: 'One Word A Day | La Gioconda 😍 #monalisa #gioconda #minijournal #creativejournaling #journalasmr',
      description: '',
      url: 'https://www.youtube.com/watch?v=f1Zx7OgEbE4',
      embedId: 'f1Zx7OgEbE4',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://img.youtube.com/vi/f1Zx7OgEbE4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-351',
      platform: 'youtube',
      title: 'Detective Inspired Spread | Creative Journaling #scrapbooking #creativejournaling  #junkjournal',
      description: 'You can shop our mystery inspired supplies here - https://coracreacrafts.com/?s=DETECTIVE',
      url: 'https://www.youtube.com/watch?v=OrlyzgoZBQ0',
      embedId: 'OrlyzgoZBQ0',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://img.youtube.com/vi/OrlyzgoZBQ0/maxresdefault.jpg'
    },
    {
      id: 'yt-api-352',
      platform: 'youtube',
      title: 'Carnival spread | Creative Journaling #creativejournaling #scrapbooking #journaladdict',
      description: 'Shop our gorgeous collection of vintage carnival supplies here now - https://coracreacrafts.com/?s=carnival',
      url: 'https://www.youtube.com/watch?v=xlfKMhkS0Sg',
      embedId: 'xlfKMhkS0Sg',
      category: 'journal-spread',
      tags: ['journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://img.youtube.com/vi/xlfKMhkS0Sg/maxresdefault.jpg'
    },
    {
      id: 'yt-api-353',
      platform: 'youtube',
      title: 'Mini Prehistory Spread 🦏 #minijournal #creativejournaling #asmrjournaling #creativejournal',
      description: '',
      url: 'https://www.youtube.com/watch?v=NM9gj8FD2jE',
      embedId: 'NM9gj8FD2jE',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://img.youtube.com/vi/NM9gj8FD2jE/maxresdefault.jpg'
    },
    {
      id: 'yt-api-354',
      platform: 'youtube',
      title: 'One word a day Dragon theme | Vintage Mini Journals #minijournal #asmrjournaling #creativejournaling',
      description: 'Shop our selection of Dragon themed inspired products here - https://coracreacrafts.com/product/dragons-sticker-sheets/  Dragon and Toast by Kevin MacLeod is licensed under a Creative Commons Attribut',
      url: 'https://www.youtube.com/watch?v=grOdVGSUij4',
      embedId: 'grOdVGSUij4',
      category: 'journal-spread',
      tags: ['journaling', 'vintage', 'stickers'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://img.youtube.com/vi/grOdVGSUij4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-355',
      platform: 'youtube',
      title: 'What tape is your favorite from our Revival Tapes Box?👀 #asmrsounds #asmrjournaling #creativejournal',
      description: 'Shop our Revival Tapes Box here - https://coracreacrafts.com/product/revival-tapes-box/',
      url: 'https://www.youtube.com/watch?v=Bo7TByasFO8',
      embedId: 'Bo7TByasFO8',
      category: 'box-reveal',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://img.youtube.com/vi/Bo7TByasFO8/maxresdefault.jpg'
    },
    {
      id: 'yt-api-356',
      platform: 'youtube',
      title: 'What to pair with the June Pre History VCB 😍 What\'s your favorite? #creativejournaling #asmrjournal',
      description: '',
      url: 'https://www.youtube.com/watch?v=nxf-LTznAXc',
      embedId: 'nxf-LTznAXc',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://img.youtube.com/vi/nxf-LTznAXc/maxresdefault.jpg'
    },
    {
      id: 'yt-api-357',
      platform: 'youtube',
      title: 'Upcycling time! 💡Here\'s an idea on how to reuse the CCC cardboard box! #upcycling #craftideas',
      description: '',
      url: 'https://www.youtube.com/watch?v=IcHE5hCeaXM',
      embedId: 'IcHE5hCeaXM',
      category: 'box-reveal',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://img.youtube.com/vi/IcHE5hCeaXM/maxresdefault.jpg'
    },
    {
      id: 'yt-api-358',
      platform: 'youtube',
      title: 'Jurassic Small Spread 🦖 #journalingsupplies #asmrjournaling #creativejournal #journalinspiration',
      description: 'Shop our Pre-History Vintage Craft Box now! https://coracreacrafts.com/product/vintage-craft-supplies-box-monthly-subscription/',
      url: 'https://www.youtube.com/watch?v=osz2Qpx3yrw',
      embedId: 'osz2Qpx3yrw',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://img.youtube.com/vi/osz2Qpx3yrw/maxresdefault.jpg'
    },
    {
      id: 'yt-api-359',
      platform: 'youtube',
      title: 'INSTAGRAM GIVEAWAY! ✨ Win a 6 month subscription to our Digital Journal Club! Check the description👇',
      description: '✨ DIGITAL JOURNAL SUPPLIES GIVEAWAY ✨ We are hosting a giveaway to celebrate our new Digital Journal Club! 😍  Head over to our INSTAGRAM to check it out! @coracreacrafts  Prize 🏆 Lucky winner will r',
      url: 'https://www.youtube.com/watch?v=Aea8iHADSRY',
      embedId: 'Aea8iHADSRY',
      category: 'product-showcase',
      tags: ['journaling', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://img.youtube.com/vi/Aea8iHADSRY/maxresdefault.jpg'
    },
    {
      id: 'yt-api-360',
      platform: 'youtube',
      title: 'Canvas Bag DIY | Creative Crafting #craftideas #diycrafts #creativecrafts #creativity',
      description: 'Shop our Fabric Transfers & Bags here - https://coracreacrafts.com/product/fabric-transfer-bag/',
      url: 'https://www.youtube.com/watch?v=WDlTgm07pyo',
      embedId: 'WDlTgm07pyo',
      category: 'tutorial',
      tags: ['diy'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://img.youtube.com/vi/WDlTgm07pyo/maxresdefault.jpg'
    },
    {
      id: 'yt-api-361',
      platform: 'youtube',
      title: 'Revival Tape Spread | ASMR #creativejournaling #asmrjournaling #scrapbooking  #journalspread',
      description: 'You can shop our exclusive Revival Tape Box here - https://coracreacrafts.com/product/revival-tapes-box/',
      url: 'https://www.youtube.com/watch?v=LZYeH6TBHwI',
      embedId: 'LZYeH6TBHwI',
      category: 'box-reveal',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://img.youtube.com/vi/LZYeH6TBHwI/maxresdefault.jpg'
    },
    {
      id: 'yt-api-362',
      platform: 'youtube',
      title: 'Which product is your favourite in this collection? 👀 #asmrsounds #asmrjournaling #journalspread',
      description: '',
      url: 'https://www.youtube.com/watch?v=2Mx7TKHBolU',
      embedId: '2Mx7TKHBolU',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://img.youtube.com/vi/2Mx7TKHBolU/maxresdefault.jpg'
    },
    {
      id: 'yt-api-363',
      platform: 'youtube',
      title: 'How to use the Digital Journal Club kit this month 🌊 #creativejournaling #journalspread',
      description: '',
      url: 'https://www.youtube.com/watch?v=M1_vbjcsQno',
      embedId: 'M1_vbjcsQno',
      category: 'tutorial',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://img.youtube.com/vi/M1_vbjcsQno/maxresdefault.jpg'
    },
    {
      id: 'yt-api-364',
      platform: 'youtube',
      title: 'Say hello to our brand new subscription! Meet the Digital Journal Club 🤗 #creativejournaling',
      description: 'Our newest subscription is here and this time we are digital! We are so excited to bring you a new download that follows our physical Vintage Craft Box theme every month! You can combine both as they ',
      url: 'https://www.youtube.com/watch?v=AQzzpo_RpWQ',
      embedId: 'AQzzpo_RpWQ',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://img.youtube.com/vi/AQzzpo_RpWQ/maxresdefault.jpg'
    },
    {
      id: 'yt-api-365',
      platform: 'youtube',
      title: 'One of my favourite things to craft with are mini journals! 🤗 | Creative Journaling #journalspread',
      description: '',
      url: 'https://www.youtube.com/watch?v=GncVKsLPmXY',
      embedId: 'GncVKsLPmXY',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://img.youtube.com/vi/GncVKsLPmXY/maxresdefault.jpg'
    },
    {
      id: 'yt-api-366',
      platform: 'youtube',
      title: 'Pre History Spread | ASMR Edition | Creative Journaling #journalspread #asmrjournal #asmrsounds',
      description: 'Subscribe to our Vintage Craft Subscription Box by clicking the link below!  https://coracreacrafts.com/product/vintage-craft-supplies-box-monthly-subscription/',
      url: 'https://www.youtube.com/watch?v=Rfoubyuw3S8',
      embedId: 'Rfoubyuw3S8',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://img.youtube.com/vi/Rfoubyuw3S8/maxresdefault.jpg'
    },
    {
      id: 'yt-api-367',
      platform: 'youtube',
      title: 'Postal Themed Spread 📮 | ASMR Creative Journaling #asmrsounds #asmrjournaling #creativejournaling',
      description: 'Get our Postal Supplies here! - https://coracreacrafts.com/?s=postal',
      url: 'https://www.youtube.com/watch?v=gAPBAF35WgE',
      embedId: 'gAPBAF35WgE',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://img.youtube.com/vi/gAPBAF35WgE/maxresdefault.jpg'
    },
    {
      id: 'yt-api-368',
      platform: 'youtube',
      title: 'Happy 1st of June 🧡 One of my favourite things to do each month is prepare  my planner!',
      description: 'Get yours now using the link below - https://coracreacrafts.com/product/coracreacrafts-planner/',
      url: 'https://www.youtube.com/watch?v=T17Fmjb5X_E',
      embedId: 'T17Fmjb5X_E',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2024-06',
      thumbnail: 'https://img.youtube.com/vi/T17Fmjb5X_E/maxresdefault.jpg'
    },
    {
      id: 'yt-api-369',
      platform: 'youtube',
      title: 'Black & White themed Fauna Spread 🖤🤍 | Creative Journaling  #creativejournaling #journalspread',
      description: 'We only have a few Fauna boxes left, get yours now using the link below -  https://coracreacrafts.com/product/curiosities-box-subscription/',
      url: 'https://www.youtube.com/watch?v=5ooW9SAkd-0',
      embedId: '5ooW9SAkd-0',
      category: 'box-reveal',
      tags: ['journaling', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://img.youtube.com/vi/5ooW9SAkd-0/maxresdefault.jpg'
    },
    {
      id: 'yt-api-370',
      platform: 'youtube',
      title: 'Our Prehistory Box Is Here 🦖 | ASMR Unboxing #asmrjournaling #creativejournaling #journaltime',
      description: 'Order your Vintage Craft Subscription Box here now - https://coracreacrafts.com/product/vintage-craft-supplies-box-monthly-subscription/',
      url: 'https://www.youtube.com/watch?v=4jE52ong9TM',
      embedId: '4jE52ong9TM',
      category: 'unboxing',
      tags: ['journaling', 'vintage', 'subscription box', 'unboxing'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://img.youtube.com/vi/4jE52ong9TM/maxresdefault.jpg'
    },
    {
      id: 'yt-api-371',
      platform: 'youtube',
      title: 'Set Up My Journal Station With Me 😍 | Creative Journaling  #creativejournaling #journaltime',
      description: 'You can get our undated Planning Companion (perfect for anytime of the year) by clicking the link below-  https://coracreacrafts.com/product/coracreacrafts-planner/',
      url: 'https://www.youtube.com/watch?v=QxVNDRLqI3g',
      embedId: 'QxVNDRLqI3g',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://img.youtube.com/vi/QxVNDRLqI3g/maxresdefault.jpg'
    },
    {
      id: 'yt-api-372',
      platform: 'youtube',
      title: 'Small Fauna Spread | Creative Journaling #asmrvideo #journalspread #asmrjournal #scrapbooking',
      description: 'Get our Fauna Themed Curiosities Subscription Box now by clicking the link below -  https://coracreacrafts.com/product/curiosities-box-subscription/',
      url: 'https://www.youtube.com/watch?v=euEPixGWOeg',
      embedId: 'euEPixGWOeg',
      category: 'box-reveal',
      tags: ['journaling', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://img.youtube.com/vi/euEPixGWOeg/maxresdefault.jpg'
    },
    {
      id: 'yt-api-373',
      platform: 'youtube',
      title: 'LAST CHANCE to shop our Memorial Day Sale! 🇺🇸 Don\'t miss out!  #journalspread  #vintagejournal',
      description: 'Shop here - https://coracreacrafts.com/',
      url: 'https://www.youtube.com/watch?v=EmXh8_rS5O0',
      embedId: 'EmXh8_rS5O0',
      category: 'journal-spread',
      tags: ['journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://img.youtube.com/vi/EmXh8_rS5O0/maxresdefault.jpg'
    },
    {
      id: 'yt-api-374',
      platform: 'youtube',
      title: 'Our Grab Bags are back 🙌  We have 4 different sizes including an XL box with 50+ items inside 😱',
      description: 'Shop Our Grab Bags by clicking the link below! https://coracreacrafts.com/product/grab-bags-2/',
      url: 'https://www.youtube.com/watch?v=3oKW1LosSqg',
      embedId: '3oKW1LosSqg',
      category: 'box-reveal',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://img.youtube.com/vi/3oKW1LosSqg/maxresdefault.jpg'
    },
    {
      id: 'yt-api-375',
      platform: 'youtube',
      title: 'Time Is Precious | Time Inspired Creative Spread  #journalspread #creativejournaling #asmrjournal',
      description: 'You can still shop our beautiful Time Box on our website!  Link below -  https://coracreacrafts.com/product/time-box/',
      url: 'https://www.youtube.com/watch?v=_CZRz0ZNVCM',
      embedId: '_CZRz0ZNVCM',
      category: 'box-reveal',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://img.youtube.com/vi/_CZRz0ZNVCM/maxresdefault.jpg'
    },
    {
      id: 'yt-api-376',
      platform: 'youtube',
      title: 'Our Memorial Day Sale is now LIVE! |   Exclusive offers available until  Monday 🇺🇸',
      description: '',
      url: 'https://www.youtube.com/watch?v=ubHcjJ64Tyc',
      embedId: 'ubHcjJ64Tyc',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://img.youtube.com/vi/ubHcjJ64Tyc/maxresdefault.jpg'
    },
    {
      id: 'yt-api-377',
      platform: 'youtube',
      title: 'How to use our Mini Journal Bundle | Creative Journaling #vintagejournal #minijournal #scrapbooking',
      description: '',
      url: 'https://www.youtube.com/watch?v=eFmAECKo2yk',
      embedId: 'eFmAECKo2yk',
      category: 'tutorial',
      tags: ['journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://img.youtube.com/vi/eFmAECKo2yk/maxresdefault.jpg'
    },
    {
      id: 'yt-api-378',
      platform: 'youtube',
      title: 'Reptile Spread 🐊 | Curiosities Box | ASMR Creative Journaling  #journalspread #asmrsounds',
      description: '',
      url: 'https://www.youtube.com/watch?v=6h_6BxxtH0s',
      embedId: '6h_6BxxtH0s',
      category: 'box-reveal',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://img.youtube.com/vi/6h_6BxxtH0s/maxresdefault.jpg'
    },
    {
      id: 'yt-api-379',
      platform: 'youtube',
      title: 'Take a look at my favourite mini journals | Mini Journaling | Vintage Spread  #journalspread',
      description: '',
      url: 'https://www.youtube.com/watch?v=Uzz41sePI8w',
      embedId: 'Uzz41sePI8w',
      category: 'journal-spread',
      tags: ['journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://img.youtube.com/vi/Uzz41sePI8w/maxresdefault.jpg'
    },
    {
      id: 'yt-api-380',
      platform: 'youtube',
      title: 'World Bee Day 🐝 | Creative Journaling | Tutorial  #journalspread #vintagejournal #creativejournaling',
      description: 'Today we celebrate Bee\'s with 20% off our Bee, Insects and Butterflies Category!  Don\'t Miss Out - Shop Now!',
      url: 'https://www.youtube.com/watch?v=TdGCw5269hE',
      embedId: 'TdGCw5269hE',
      category: 'tutorial',
      tags: ['journaling', 'vintage', 'tutorial'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://img.youtube.com/vi/TdGCw5269hE/maxresdefault.jpg'
    },
    {
      id: 'yt-api-381',
      platform: 'youtube',
      title: 'Happy World Bee Day 🐝 | Creative Journaling | Tutorial  #journalspread #scrapbooking #vintagejournal',
      description: 'World Bee Day! 🐝   Bees play such an important role in the environment by pollinating plants, supporting agriculture and biodiversity! Today we celebrate bees with 20% off our Bee, Insects and Butter',
      url: 'https://www.youtube.com/watch?v=SxK6TG-5UQs',
      embedId: 'SxK6TG-5UQs',
      category: 'tutorial',
      tags: ['journaling', 'vintage', 'tutorial'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://img.youtube.com/vi/SxK6TG-5UQs/maxresdefault.jpg'
    },
    {
      id: 'yt-api-382',
      platform: 'youtube',
      title: 'Art Is Everywhere | Museum Treasure Box | Vintage Journaling  #journalspread   #creativejournaling',
      description: '',
      url: 'https://www.youtube.com/watch?v=-dsHW_qK7NQ',
      embedId: '-dsHW_qK7NQ',
      category: 'box-reveal',
      tags: ['journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://img.youtube.com/vi/-dsHW_qK7NQ/maxresdefault.jpg'
    },
    {
      id: 'yt-api-383',
      platform: 'youtube',
      title: 'Secret Garden Spread | May Vintage Craft Box | Creative Journaling  #journaltime #journalspread',
      description: '',
      url: 'https://www.youtube.com/watch?v=bW-iPo-CQY4',
      embedId: 'bW-iPo-CQY4',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'secret garden'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://img.youtube.com/vi/bW-iPo-CQY4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-384',
      platform: 'youtube',
      title: 'Fauna Box Sneak Peak | Curiosities Box | ASMR Unboxing  #scrapbooking  #creativejournaling',
      description: '',
      url: 'https://www.youtube.com/watch?v=LTmEyklC7ws',
      embedId: 'LTmEyklC7ws',
      category: 'unboxing',
      tags: ['journaling', 'unboxing'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://img.youtube.com/vi/LTmEyklC7ws/maxresdefault.jpg'
    },
    {
      id: 'yt-api-385',
      platform: 'youtube',
      title: 'Le Jardin Oublié | Small Spread Idea | Creative Journaling  #creativejournaling  #journalspread',
      description: '',
      url: 'https://www.youtube.com/watch?v=RNnRkEKxcD8',
      embedId: 'RNnRkEKxcD8',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://img.youtube.com/vi/RNnRkEKxcD8/maxresdefault.jpg'
    },
    {
      id: 'yt-api-386',
      platform: 'youtube',
      title: 'What to pair with the Secret Gardens Box | Vintage Craft Subscription Box #creativejournaling',
      description: '',
      url: 'https://www.youtube.com/watch?v=cXXkbZQUVmQ',
      embedId: 'cXXkbZQUVmQ',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'secret garden', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://img.youtube.com/vi/cXXkbZQUVmQ/maxresdefault.jpg'
    },
    {
      id: 'yt-api-387',
      platform: 'youtube',
      title: 'Mini Journal | Secret Garden Edition | Vintage Journal #scrapbooking  #minijournaling #journalspread',
      description: '',
      url: 'https://www.youtube.com/watch?v=bznp12mzYuo',
      embedId: 'bznp12mzYuo',
      category: 'journal-spread',
      tags: ['journaling', 'vintage', 'secret garden'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://img.youtube.com/vi/bznp12mzYuo/maxresdefault.jpg'
    },
    {
      id: 'yt-api-388',
      platform: 'youtube',
      title: 'Golden Gallery Journal Spread | ASMR Journaling | Treasure Box #asmrjournaling  #creativejournaling',
      description: '',
      url: 'https://www.youtube.com/watch?v=pjTLsjKUN8M',
      embedId: 'pjTLsjKUN8M',
      category: 'box-reveal',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-05',
      thumbnail: 'https://img.youtube.com/vi/pjTLsjKUN8M/maxresdefault.jpg'
    },
    {
      id: 'yt-api-389',
      platform: 'youtube',
      title: 'Mini Journal Bundle | ASMR Journal | Scrapbooking #scrapbooking #minijournaling',
      description: '',
      url: 'https://www.youtube.com/watch?v=mpqEIUKw2oY',
      embedId: 'mpqEIUKw2oY',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-04',
      thumbnail: 'https://img.youtube.com/vi/mpqEIUKw2oY/maxresdefault.jpg'
    },
    {
      id: 'yt-api-390',
      platform: 'youtube',
      title: 'Revival Tapes Box | Vintage Journaling Supplies | Unboxing ASMR #spring #asmrjournal',
      description: '',
      url: 'https://www.youtube.com/watch?v=xLRPIqR-J3w',
      embedId: 'xLRPIqR-J3w',
      category: 'unboxing',
      tags: ['journaling', 'vintage', 'unboxing'],
      creator: 'CoraCreaCrafts',
      date: '2024-04',
      thumbnail: 'https://img.youtube.com/vi/xLRPIqR-J3w/maxresdefault.jpg'
    },
    {
      id: 'yt-api-391',
      platform: 'youtube',
      title: 'Egg Hunt Minj Spread | One Word A Day Challenge | Creative Journaling',
      description: '',
      url: 'https://www.youtube.com/watch?v=YZnSua11ud4',
      embedId: 'YZnSua11ud4',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-04',
      thumbnail: 'https://img.youtube.com/vi/YZnSua11ud4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-392',
      platform: 'youtube',
      title: 'Ancient Cities Spread | Creative Journaling | ASMR #folklore #ancientcities #asmrjournal',
      description: '',
      url: 'https://www.youtube.com/watch?v=bLMPOPdlHko',
      embedId: 'bLMPOPdlHko',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2024-03',
      thumbnail: 'https://img.youtube.com/vi/bLMPOPdlHko/maxresdefault.jpg'
    },
    {
      id: 'yt-api-393',
      platform: 'youtube',
      title: 'March Vintage Craft Box | Silk Road | Crafting & Journaling Subscription Box #asmrjournal',
      description: '',
      url: 'https://www.youtube.com/watch?v=94GvQ83KVzo',
      embedId: '94GvQ83KVzo',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2024-03',
      thumbnail: 'https://img.youtube.com/vi/94GvQ83KVzo/maxresdefault.jpg'
    },
    {
      id: 'yt-api-394',
      platform: 'youtube',
      title: 'Curiosities Box Preview | Winter Themed | Journaling & Crafting Subscription #asmrjournal',
      description: '',
      url: 'https://www.youtube.com/watch?v=D6tv5u7eNjQ',
      embedId: 'D6tv5u7eNjQ',
      category: 'box-reveal',
      tags: ['journaling', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2024-02',
      thumbnail: 'https://img.youtube.com/vi/D6tv5u7eNjQ/maxresdefault.jpg'
    },
    {
      id: 'yt-api-395',
      platform: 'youtube',
      title: 'January Vintage Craft Box | Carnivals & Fairs | Crafting & Journaling Subscription Box #asmrjournal',
      description: '',
      url: 'https://www.youtube.com/watch?v=kpT8QI7Ip8s',
      embedId: 'kpT8QI7Ip8s',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2024-01',
      thumbnail: 'https://img.youtube.com/vi/kpT8QI7Ip8s/maxresdefault.jpg'
    },
    {
      id: 'yt-api-396',
      platform: 'youtube',
      title: 'December Vintage Craft Box | Vintage Shops | Journaling & Crafting Subscription| ASMR #asmrjournal',
      description: '',
      url: 'https://www.youtube.com/watch?v=Ivsw83eQRKw',
      embedId: 'Ivsw83eQRKw',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2023-12',
      thumbnail: 'https://img.youtube.com/vi/Ivsw83eQRKw/maxresdefault.jpg'
    },
    {
      id: 'yt-api-397',
      platform: 'youtube',
      title: 'Apothecary Box Highlights | Curiosities Box | Journaling & Crafting Subscription #asmrjournal',
      description: '',
      url: 'https://www.youtube.com/watch?v=KF1xeiNtG4k',
      embedId: 'KF1xeiNtG4k',
      category: 'box-reveal',
      tags: ['journaling', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2023-11',
      thumbnail: 'https://img.youtube.com/vi/KF1xeiNtG4k/maxresdefault.jpg'
    },
    {
      id: 'yt-api-398',
      platform: 'youtube',
      title: 'Christmas Cards Mood | Christmas Tapes Box | Journaling ASMR',
      description: '',
      url: 'https://www.youtube.com/watch?v=-LBUWVnRI78',
      embedId: '-LBUWVnRI78',
      category: 'box-reveal',
      tags: ['journaling', 'christmas'],
      creator: 'CoraCreaCrafts',
      date: '2023-11',
      thumbnail: 'https://img.youtube.com/vi/-LBUWVnRI78/maxresdefault.jpg'
    },
    {
      id: 'yt-api-399',
      platform: 'youtube',
      title: 'Small Business Stories | Part 4 | Subscription Boxes',
      description: '',
      url: 'https://www.youtube.com/watch?v=5Yaujmt9GeE',
      embedId: '5Yaujmt9GeE',
      category: 'box-reveal',
      tags: ['subscription box', 'small business'],
      creator: 'CoraCreaCrafts',
      date: '2023-11',
      thumbnail: 'https://img.youtube.com/vi/5Yaujmt9GeE/maxresdefault.jpg'
    },
    {
      id: 'yt-api-400',
      platform: 'youtube',
      title: 'November Vintage Craft Box | On Stage | Journaling & Crafting Subscription| ASMR #asmrjournal',
      description: '',
      url: 'https://www.youtube.com/watch?v=JdmNea-f2V4',
      embedId: 'JdmNea-f2V4',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2023-11',
      thumbnail: 'https://img.youtube.com/vi/JdmNea-f2V4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-401',
      platform: 'youtube',
      title: 'Small Business Stories | Part 3 | Product Problems',
      description: '',
      url: 'https://www.youtube.com/watch?v=56YQgDx5Gjo',
      embedId: '56YQgDx5Gjo',
      category: 'behind-the-scenes',
      tags: ['small business'],
      creator: 'CoraCreaCrafts',
      date: '2023-10',
      thumbnail: 'https://img.youtube.com/vi/56YQgDx5Gjo/maxresdefault.jpg'
    },
    {
      id: 'yt-api-402',
      platform: 'youtube',
      title: 'Advent Calendar | Packing ASMR | Journaling & Crafting Supplies #asmrjournal #scrapbooking #craftbox',
      description: '',
      url: 'https://www.youtube.com/watch?v=7g98uKtGMeE',
      embedId: '7g98uKtGMeE',
      category: 'box-reveal',
      tags: ['journaling', 'advent calendar'],
      creator: 'CoraCreaCrafts',
      date: '2023-10',
      thumbnail: 'https://img.youtube.com/vi/7g98uKtGMeE/maxresdefault.jpg'
    },
    {
      id: 'yt-api-403',
      platform: 'youtube',
      title: 'Dark Academia Spread | Mini Journal | October Vintage Craft Box | ASMR #asmrjournal',
      description: '',
      url: 'https://www.youtube.com/watch?v=L9HQl6sM3gc',
      embedId: 'L9HQl6sM3gc',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'dark academia'],
      creator: 'CoraCreaCrafts',
      date: '2023-10',
      thumbnail: 'https://img.youtube.com/vi/L9HQl6sM3gc/maxresdefault.jpg'
    },
    {
      id: 'yt-api-404',
      platform: 'youtube',
      title: 'October Vintage Craft Box | Dark Academia | Journaling & Crafting Subscription | ASMR #asmrjournal',
      description: '',
      url: 'https://www.youtube.com/watch?v=z-KHIuCJ8pM',
      embedId: 'z-KHIuCJ8pM',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'dark academia', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2023-10',
      thumbnail: 'https://img.youtube.com/vi/z-KHIuCJ8pM/maxresdefault.jpg'
    },
    {
      id: 'yt-api-405',
      platform: 'youtube',
      title: 'Dark Academia Card | October Vintage Craft Box | Dark Academia Theme | ASMR Journaling #asmrjournal',
      description: '',
      url: 'https://www.youtube.com/watch?v=xpyf_m5ISyU',
      embedId: 'xpyf_m5ISyU',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'dark academia'],
      creator: 'CoraCreaCrafts',
      date: '2023-10',
      thumbnail: 'https://img.youtube.com/vi/xpyf_m5ISyU/maxresdefault.jpg'
    },
    {
      id: 'yt-api-406',
      platform: 'youtube',
      title: 'Curiosities Box Preview | Autumn Edition | Enchanted Fall 🍂 | Journaling Crafting Subscription',
      description: '',
      url: 'https://www.youtube.com/watch?v=IQdqku0JkeY',
      embedId: 'IQdqku0JkeY',
      category: 'box-reveal',
      tags: ['journaling', 'autumn', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2023-09',
      thumbnail: 'https://img.youtube.com/vi/IQdqku0JkeY/maxresdefault.jpg'
    },
    {
      id: 'yt-api-407',
      platform: 'youtube',
      title: 'Small Business Stories | Part 2 | Team & Work Space',
      description: '',
      url: 'https://www.youtube.com/watch?v=2rXg6nAlF5g',
      embedId: '2rXg6nAlF5g',
      category: 'behind-the-scenes',
      tags: ['small business'],
      creator: 'CoraCreaCrafts',
      date: '2023-09',
      thumbnail: 'https://img.youtube.com/vi/2rXg6nAlF5g/maxresdefault.jpg'
    },
    {
      id: 'yt-api-408',
      platform: 'youtube',
      title: 'Small Business Stories | Part 1 | We are back & Introduction',
      description: '',
      url: 'https://www.youtube.com/watch?v=rxkyKfW5iws',
      embedId: 'rxkyKfW5iws',
      category: 'behind-the-scenes',
      tags: ['small business'],
      creator: 'CoraCreaCrafts',
      date: '2023-09',
      thumbnail: 'https://img.youtube.com/vi/rxkyKfW5iws/maxresdefault.jpg'
    },
    {
      id: 'yt-api-409',
      platform: 'youtube',
      title: 'Halloween Minis Spread | ASMR Journaling | Halloween Bundle #asmrjournal #craftbox',
      description: '',
      url: 'https://www.youtube.com/watch?v=62TeBC9CwVA',
      embedId: '62TeBC9CwVA',
      category: 'box-reveal',
      tags: ['journaling', 'halloween'],
      creator: 'CoraCreaCrafts',
      date: '2023-09',
      thumbnail: 'https://img.youtube.com/vi/62TeBC9CwVA/maxresdefault.jpg'
    },
    {
      id: 'yt-api-410',
      platform: 'youtube',
      title: 'Vintage Craft Box Preview | September Myths & Legends | Journaling Subscription Box #asmrjournal',
      description: '',
      url: 'https://www.youtube.com/watch?v=PNwn7LEiN3g',
      embedId: 'PNwn7LEiN3g',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2023-09',
      thumbnail: 'https://img.youtube.com/vi/PNwn7LEiN3g/maxresdefault.jpg'
    },
    {
      id: 'yt-api-411',
      platform: 'youtube',
      title: 'Gold Scribe Journal Spread | ASMR journaling | Treasure Box #asmrjournal #creativejournaling',
      description: '',
      url: 'https://www.youtube.com/watch?v=f1wtmvyW3XA',
      embedId: 'f1wtmvyW3XA',
      category: 'box-reveal',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2023-09',
      thumbnail: 'https://img.youtube.com/vi/f1wtmvyW3XA/maxresdefault.jpg'
    },
    {
      id: 'yt-api-412',
      platform: 'youtube',
      title: 'Treasure Box | 15th Edition | Aug/Sept (2023) | Crafting & Journaling Subscription | Preview',
      description: 'Get it here: https://coracreacrafts.com/product/treasure-craft-supplies-box-bimonthly-subscription/',
      url: 'https://www.youtube.com/watch?v=ehIuB3y-LDw',
      embedId: 'ehIuB3y-LDw',
      category: 'box-reveal',
      tags: ['journaling', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2023-08',
      thumbnail: 'https://img.youtube.com/vi/ehIuB3y-LDw/maxresdefault.jpg'
    },
    {
      id: 'yt-api-413',
      platform: 'youtube',
      title: 'August Vintage Craft Box | Vintage Games | Journaling & Crafting Subscription | ASMR',
      description: '',
      url: 'https://www.youtube.com/watch?v=YNWW__SVCtE',
      embedId: 'YNWW__SVCtE',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2023-08',
      thumbnail: 'https://img.youtube.com/vi/YNWW__SVCtE/maxresdefault.jpg'
    },
    {
      id: 'yt-api-414',
      platform: 'youtube',
      title: 'July Vintage Craft Box | Journaling & Crafting Subscription| ASMR #creativejournaling #asmrjournal',
      description: '',
      url: 'https://www.youtube.com/watch?v=LF-FjYbQBhw',
      embedId: 'LF-FjYbQBhw',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2023-07',
      thumbnail: 'https://img.youtube.com/vi/LF-FjYbQBhw/maxresdefault.jpg'
    },
    {
      id: 'yt-api-415',
      platform: 'youtube',
      title: 'Treasure Box | 14th Edition | June/July (2023) | Crafting & Journaling Subscription | Preview',
      description: 'Get it here: https://coracreacrafts.com/product/treasure-craft-supplies-box-bimonthly-subscription/  What’s in the June/July Treasure Box? This edition of the box is centered around the idea and theme',
      url: 'https://www.youtube.com/watch?v=b6BamPxRuU0',
      embedId: 'b6BamPxRuU0',
      category: 'box-reveal',
      tags: ['journaling', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2023-07',
      thumbnail: 'https://img.youtube.com/vi/b6BamPxRuU0/maxresdefault.jpg'
    },
    {
      id: 'yt-api-416',
      platform: 'youtube',
      title: 'June Vintage Craft Box| Pirates | Journaling & Crafting Subscription ! ASMR',
      description: 'https://coracreacrafts.com/product/vintage-craft-supplies-box-monthly-subscription/',
      url: 'https://www.youtube.com/watch?v=SDUiTT4uIQQ',
      embedId: 'SDUiTT4uIQQ',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2023-06',
      thumbnail: 'https://img.youtube.com/vi/SDUiTT4uIQQ/maxresdefault.jpg'
    },
    {
      id: 'yt-api-417',
      platform: 'youtube',
      title: 'ASMR Creative Journal | Cabinet of Curiosities spread | Long Version | Journaling',
      description: 'Using the items from the Treasure Box - we have less than 10 left available: https://coracreacrafts.com/product/treasure-craft-supplies-box-bimonthly-subscription/',
      url: 'https://www.youtube.com/watch?v=RCySt-bxgEM',
      embedId: 'RCySt-bxgEM',
      category: 'box-reveal',
      tags: ['journaling', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2023-05',
      thumbnail: 'https://img.youtube.com/vi/RCySt-bxgEM/maxresdefault.jpg'
    },
    {
      id: 'yt-api-418',
      platform: 'youtube',
      title: 'Cabinet of Curiosities | Treasure Box Highlights | Journaling & Crafting Subscription',
      description: '',
      url: 'https://www.youtube.com/watch?v=ovQ3-_n0fz0',
      embedId: 'ovQ3-_n0fz0',
      category: 'box-reveal',
      tags: ['journaling', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2023-05',
      thumbnail: 'https://img.youtube.com/vi/ovQ3-_n0fz0/maxresdefault.jpg'
    },
    {
      id: 'yt-api-419',
      platform: 'youtube',
      title: 'Treasure Craft Box | 13th Edition | Apr/May (2023) | Crafting & Journaling Subscription | Preview',
      description: 'What\'s in the April/May Treasure Box? This edition of the box is centered around the idea and theme of the Cabinet of Curiosities  Cabinet of Curiosities Sticker Book by CoraCreaCrafts Cabinet of Curi',
      url: 'https://www.youtube.com/watch?v=4ttz3LWN0h4',
      embedId: '4ttz3LWN0h4',
      category: 'box-reveal',
      tags: ['journaling', 'subscription box', 'stickers'],
      creator: 'CoraCreaCrafts',
      date: '2023-05',
      thumbnail: 'https://img.youtube.com/vi/4ttz3LWN0h4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-420',
      platform: 'youtube',
      title: 'Specimen Journal Card | Journaling Tutorial | Bees & Insects | Relaxing',
      description: '',
      url: 'https://www.youtube.com/watch?v=85JR4-aFJXo',
      embedId: '85JR4-aFJXo',
      category: 'tutorial',
      tags: ['journaling', 'tutorial'],
      creator: 'CoraCreaCrafts',
      date: '2023-05',
      thumbnail: 'https://img.youtube.com/vi/85JR4-aFJXo/maxresdefault.jpg'
    },
    {
      id: 'yt-api-421',
      platform: 'youtube',
      title: 'DIY Craft Pocket | Long Version | Crafting Ideas | Tutorial | ASMR',
      description: '',
      url: 'https://www.youtube.com/watch?v=aBoQ18YYUYc',
      embedId: 'aBoQ18YYUYc',
      category: 'tutorial',
      tags: ['tutorial', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2023-05',
      thumbnail: 'https://img.youtube.com/vi/aBoQ18YYUYc/maxresdefault.jpg'
    },
    {
      id: 'yt-api-422',
      platform: 'youtube',
      title: 'May Vintage Craft Box | Bees & Insects | Journaling & Crafting Subscription| ASMR',
      description: 'Get the box here: https://coracreacrafts.com/product/vintage-craft-supplies-box-monthly-subscription/',
      url: 'https://www.youtube.com/watch?v=8y4Ug8IGQrs',
      embedId: '8y4Ug8IGQrs',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2023-05',
      thumbnail: 'https://img.youtube.com/vi/8y4Ug8IGQrs/maxresdefault.jpg'
    },
    {
      id: 'yt-api-423',
      platform: 'youtube',
      title: 'Botanical Journal Spread | Curiosities Box | Journaling Idea | Quick Tutorial',
      description: '',
      url: 'https://www.youtube.com/watch?v=oRXaymtxgEw',
      embedId: 'oRXaymtxgEw',
      category: 'box-reveal',
      tags: ['journaling', 'tutorial'],
      creator: 'CoraCreaCrafts',
      date: '2023-04',
      thumbnail: 'https://img.youtube.com/vi/oRXaymtxgEw/maxresdefault.jpg'
    },
    {
      id: 'yt-api-424',
      platform: 'youtube',
      title: 'Nature Journal Spread | Curiosities Box | Journaling Idea | Relaxing Tutorial',
      description: '',
      url: 'https://www.youtube.com/watch?v=2VJhwOpI0Ow',
      embedId: '2VJhwOpI0Ow',
      category: 'box-reveal',
      tags: ['journaling', 'tutorial'],
      creator: 'CoraCreaCrafts',
      date: '2023-04',
      thumbnail: 'https://img.youtube.com/vi/2VJhwOpI0Ow/maxresdefault.jpg'
    },
    {
      id: 'yt-api-425',
      platform: 'youtube',
      title: 'Witchy Cards w. transparent sleeves | Vintage Craft Box | Folklore & Witchcraft | ASMR | Crafting',
      description: '',
      url: 'https://www.youtube.com/watch?v=4aKjE15A8dk',
      embedId: '4aKjE15A8dk',
      category: 'box-reveal',
      tags: ['vintage'],
      creator: 'CoraCreaCrafts',
      date: '2023-04',
      thumbnail: 'https://img.youtube.com/vi/4aKjE15A8dk/maxresdefault.jpg'
    },
    {
      id: 'yt-api-426',
      platform: 'youtube',
      title: 'Curiosities Box | 2nd Edition | Mar/Apr (2023) | Preview | Journaling & Crafting Subscription Box',
      description: 'Curiosities Box Preview 💗 Nature inspired box for the second edition of this year!   https://coracreacrafts.com/product/curiosities-box-subscription/',
      url: 'https://www.youtube.com/watch?v=fZa84g3I1PA',
      embedId: 'fZa84g3I1PA',
      category: 'box-reveal',
      tags: ['journaling', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2023-04',
      thumbnail: 'https://img.youtube.com/vi/fZa84g3I1PA/maxresdefault.jpg'
    },
    {
      id: 'yt-api-427',
      platform: 'youtube',
      title: 'April Vintage Craft Box | Folklore & Witchcraft Theme | Journaling & Crafting Subscription',
      description: 'Order here: https://coracreacrafts.com/product/vintage-craft-supplies-box-monthly-subscription/',
      url: 'https://www.youtube.com/watch?v=eA1SjZzbzA8',
      embedId: 'eA1SjZzbzA8',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2023-04',
      thumbnail: 'https://img.youtube.com/vi/eA1SjZzbzA8/maxresdefault.jpg'
    },
    {
      id: 'yt-api-428',
      platform: 'youtube',
      title: 'Vintage Gardens Journal Spread | Journaling | ASMR | Vintage Craft Box',
      description: 'Using the supplies from the March Vintage Craft Box:  https://coracreacrafts.com/product/vintage-craft-supplies-box-monthly-subscription/',
      url: 'https://www.youtube.com/watch?v=6scvPBH_ZIA',
      embedId: '6scvPBH_ZIA',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2023-03',
      thumbnail: 'https://img.youtube.com/vi/6scvPBH_ZIA/maxresdefault.jpg'
    },
    {
      id: 'yt-api-429',
      platform: 'youtube',
      title: 'March Vintage Craft Box | Vintage Gardens Theme | Journaling & Crafting Subscription Box | ASMR',
      description: 'Order here: https://coracreacrafts.com/product/vintage-craft-supplies-box-monthly-subscription/  March \'Vintage Gardens\' Preview is finally here 📦❤️ Inspired by country estates, mansions and castles,',
      url: 'https://www.youtube.com/watch?v=pX0AnHe_CC8',
      embedId: 'pX0AnHe_CC8',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2023-03',
      thumbnail: 'https://img.youtube.com/vi/pX0AnHe_CC8/maxresdefault.jpg'
    },
    {
      id: 'yt-api-430',
      platform: 'youtube',
      title: 'How to use Transfer Stickers (Rub-on)? | Journaling Tip | Tutorial',
      description: 'Find a whole selection of transfer stickers here: https://coracreacrafts.com/product-category/transfer-stickers/',
      url: 'https://www.youtube.com/watch?v=ls6HUuRIX-4',
      embedId: 'ls6HUuRIX-4',
      category: 'tutorial',
      tags: ['journaling', 'stickers', 'tutorial'],
      creator: 'CoraCreaCrafts',
      date: '2023-03',
      thumbnail: 'https://img.youtube.com/vi/ls6HUuRIX-4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-431',
      platform: 'youtube',
      title: 'Treasure Craft Box | 12th Edition | Feb/March (2023) | Preview',
      description: 'https://coracreacrafts.com/product/treasure-craft-supplies-box-bimonthly-subscription/',
      url: 'https://www.youtube.com/watch?v=a70GFKhXavI',
      embedId: 'a70GFKhXavI',
      category: 'box-reveal',
      tags: ['subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2023-03',
      thumbnail: 'https://img.youtube.com/vi/a70GFKhXavI/maxresdefault.jpg'
    },
    {
      id: 'yt-api-432',
      platform: 'youtube',
      title: 'Molds Decorative Frames | Long Version | Crafting Ideas  | Tutorial | ASMR',
      description: 'Make some decorative frames using the new profile molds from my shop!  Links below  Molds: https://coracreacrafts.com/product/profiles-molds/  Resin Epoxy: https://www.amazon.com/dp/B086L1CDMT?linkCod',
      url: 'https://www.youtube.com/watch?v=VVVZKJNsaXc',
      embedId: 'VVVZKJNsaXc',
      category: 'tutorial',
      tags: ['tutorial'],
      creator: 'CoraCreaCrafts',
      date: '2023-03',
      thumbnail: 'https://img.youtube.com/vi/VVVZKJNsaXc/maxresdefault.jpg'
    },
    {
      id: 'yt-api-433',
      platform: 'youtube',
      title: 'February Vintage Craft Box | Ancient Egypt Theme | Journaling & Crafting Subscription Box',
      description: 'https://coracreacrafts.com/product/vintage-craft-supplies-box-monthly-subscription/',
      url: 'https://www.youtube.com/watch?v=gpr1gMowFP4',
      embedId: 'gpr1gMowFP4',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2023-02',
      thumbnail: 'https://img.youtube.com/vi/gpr1gMowFP4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-434',
      platform: 'youtube',
      title: 'Egypt Journal Pages | DIY Tutorial | Tutorial',
      description: 'Pages made with the Ancient Egypt supplies from the Vintage Craft Box: https://coracreacrafts.com/product/vintage-craft-supplies-box-monthly-subscription/',
      url: 'https://www.youtube.com/watch?v=qy-h14IK0ls',
      embedId: 'qy-h14IK0ls',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'subscription box', 'tutorial', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2023-02',
      thumbnail: 'https://img.youtube.com/vi/qy-h14IK0ls/maxresdefault.jpg'
    },
    {
      id: 'yt-api-435',
      platform: 'youtube',
      title: 'January Vintage Craft Box | Enchanted Forest Theme | Journaling & Crafting Subscription Box',
      description: 'Although this box is now sold out you can pre order the restock on our website: CoraCreaCrafts.com ✂️',
      url: 'https://www.youtube.com/watch?v=mOTNEf-IwNY',
      embedId: 'mOTNEf-IwNY',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2023-01',
      thumbnail: 'https://img.youtube.com/vi/mOTNEf-IwNY/maxresdefault.jpg'
    },
    {
      id: 'yt-api-436',
      platform: 'youtube',
      title: 'CNY Themed Spread | DIY Tutorial| Journaling',
      description: '',
      url: 'https://www.youtube.com/watch?v=NjuKueCStRA',
      embedId: 'NjuKueCStRA',
      category: 'tutorial',
      tags: ['journaling', 'tutorial', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2023-01',
      thumbnail: 'https://img.youtube.com/vi/NjuKueCStRA/maxresdefault.jpg'
    },
    {
      id: 'yt-api-437',
      platform: 'youtube',
      title: 'Pocket Watch Mini Shadow Box | Crafting Ideas | Tutorial',
      description: 'How to use the Pocket Watches from the Treasure Box 🤗 Here\'s an idea! I made them into little Impressionist paintings ornaments and covered them with epoxy resin to make it last forever ❤️',
      url: 'https://www.youtube.com/watch?v=evPoPjpGiq8',
      embedId: 'evPoPjpGiq8',
      category: 'box-reveal',
      tags: ['tutorial'],
      creator: 'CoraCreaCrafts',
      date: '2022-12',
      thumbnail: 'https://img.youtube.com/vi/evPoPjpGiq8/maxresdefault.jpg'
    },
    {
      id: 'yt-api-438',
      platform: 'youtube',
      title: 'DIY Cover for Journal Insert | Long Version | DIY Tutorial | Crafting',
      description: 'This cover for a journal insert or other sleeves insert was created using the products from the Treasure Box Edition 11 exclusively   https://coracreacrafts.com/product/treasure-craft-supplies-box-bim',
      url: 'https://www.youtube.com/watch?v=iLFbXwDBH6k',
      embedId: 'iLFbXwDBH6k',
      category: 'box-reveal',
      tags: ['journaling', 'tutorial', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2022-12',
      thumbnail: 'https://img.youtube.com/vi/iLFbXwDBH6k/maxresdefault.jpg'
    },
    {
      id: 'yt-api-439',
      platform: 'youtube',
      title: 'Journal Cover Vintage Deco | Long Version |  DIY Tutorial | Crafting',
      description: 'This little journal cover project was made exclusively using the products from the December Vintage Craft Box \'Writers & Artists\'  https://coracreacrafts.com/product/vintage-craft-supplies-box-monthly',
      url: 'https://www.youtube.com/watch?v=ITfwu2GXSfY',
      embedId: 'ITfwu2GXSfY',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'tutorial', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2022-12',
      thumbnail: 'https://img.youtube.com/vi/ITfwu2GXSfY/maxresdefault.jpg'
    },
    {
      id: 'yt-api-440',
      platform: 'youtube',
      title: 'Treasure Craft Box | 11th Edition | December/January (2023) | Preview',
      description: '',
      url: 'https://www.youtube.com/watch?v=y93UU4GxA1Y',
      embedId: 'y93UU4GxA1Y',
      category: 'box-reveal',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2022-12',
      thumbnail: 'https://img.youtube.com/vi/y93UU4GxA1Y/maxresdefault.jpg'
    },
    {
      id: 'yt-api-441',
      platform: 'youtube',
      title: 'December Vintage Craft Box | Writers & Artists Theme | Journaling & Crafting Subscription Box',
      description: '',
      url: 'https://www.youtube.com/watch?v=p2wbMIMQkbc',
      embedId: 'p2wbMIMQkbc',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2022-12',
      thumbnail: 'https://img.youtube.com/vi/p2wbMIMQkbc/maxresdefault.jpg'
    },
    {
      id: 'yt-api-442',
      platform: 'youtube',
      title: 'November Vintages Craft Box | Ancient China Theme | Journaling & Crafting Subscription Box',
      description: '',
      url: 'https://www.youtube.com/watch?v=J03Iwa7A-nY',
      embedId: 'J03Iwa7A-nY',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2022-12',
      thumbnail: 'https://img.youtube.com/vi/J03Iwa7A-nY/maxresdefault.jpg'
    },
    {
      id: 'yt-api-443',
      platform: 'youtube',
      title: 'October Vintages Craft Box | Forest Theme | Journaling & Crafting Subscription Box',
      description: '',
      url: 'https://www.youtube.com/watch?v=169NgdTKgN0',
      embedId: '169NgdTKgN0',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2022-12',
      thumbnail: 'https://img.youtube.com/vi/169NgdTKgN0/maxresdefault.jpg'
    },
    {
      id: 'yt-api-444',
      platform: 'youtube',
      title: 'September Vintage Craft Box | Vintage Fashion Theme | Journaling & Crafting Subscription Box',
      description: '',
      url: 'https://www.youtube.com/watch?v=5OziIb-F-cs',
      embedId: '5OziIb-F-cs',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2022-12',
      thumbnail: 'https://img.youtube.com/vi/5OziIb-F-cs/maxresdefault.jpg'
    },
    {
      id: 'yt-api-445',
      platform: 'youtube',
      title: 'Pop Up Card | Long Version Voiceover | DIY Tutorial |Crafting',
      description: 'Here is a long version of the Pop Up Card video shared on Instagram.  You can adjust the size and shape of the pop up as you wish and experiment with it. If you would like to follow the template I use',
      url: 'https://www.youtube.com/watch?v=5EyCNpa530U',
      embedId: '5EyCNpa530U',
      category: 'tutorial',
      tags: ['tutorial', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2022-11',
      thumbnail: 'https://img.youtube.com/vi/5EyCNpa530U/maxresdefault.jpg'
    },
    {
      id: 'yt-api-446',
      platform: 'youtube',
      title: 'Collage with Oct/Nov Treasure Box | Long Version Voiceover | DIY Tutorial | Crafting',
      description: '',
      url: 'https://www.youtube.com/watch?v=YfcVeu36Xc8',
      embedId: 'YfcVeu36Xc8',
      category: 'box-reveal',
      tags: ['tutorial', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2022-10',
      thumbnail: 'https://img.youtube.com/vi/YfcVeu36Xc8/maxresdefault.jpg'
    },
    {
      id: 'yt-api-447',
      platform: 'youtube',
      title: 'Treasure Craft Box | 10th Edition | October/November | Preview',
      description: 'This bimonthly box is the largest of CoraCreaCrafts’ boxes. Not only will you receive a selection of vintage style washi tapes, stamps, papers, stickers and ephemera in larger quantity than the other ',
      url: 'https://www.youtube.com/watch?v=DQIycx-Gb9Y',
      embedId: 'DQIycx-Gb9Y',
      category: 'box-reveal',
      tags: ['washi tape', 'vintage', 'stickers'],
      creator: 'CoraCreaCrafts',
      date: '2022-10',
      thumbnail: 'https://img.youtube.com/vi/DQIycx-Gb9Y/maxresdefault.jpg'
    },
    {
      id: 'yt-api-448',
      platform: 'youtube',
      title: 'Halloween Shadow Box | Long Version Voiceover | DIY Tutorial | Crafting',
      description: 'In this video I\'m explaining how to make a decorative shadow box for Halloween or simply for those who like a bit of a dark style! Products used: - Shadow Box: https://www.amazon.com/dp/B08V1VC3BN?tag',
      url: 'https://www.youtube.com/watch?v=wF1GRVIXPFU',
      embedId: 'wF1GRVIXPFU',
      category: 'box-reveal',
      tags: ['halloween', 'tutorial', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2022-10',
      thumbnail: 'https://img.youtube.com/vi/wF1GRVIXPFU/maxresdefault.jpg'
    },
    {
      id: 'yt-api-449',
      platform: 'youtube',
      title: 'Wax Seal Clusters | Plain Wax Seals + Transparent Wax | DIY tutorial | Crafting',
      description: 'All wax seals used in this video are available in my shop: Coracreacrafts.com',
      url: 'https://www.youtube.com/watch?v=J1urCOyXits',
      embedId: 'J1urCOyXits',
      category: 'tutorial',
      tags: ['tutorial', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2022-08',
      thumbnail: 'https://img.youtube.com/vi/J1urCOyXits/maxresdefault.jpg'
    },
    {
      id: 'yt-api-450',
      platform: 'youtube',
      title: 'August Vintage Craft Box | Boho Theme | Journaling & Crafting Subscription Box',
      description: '',
      url: 'https://www.youtube.com/watch?v=PkgBq1Cs9jY',
      embedId: 'PkgBq1Cs9jY',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2022-08',
      thumbnail: 'https://img.youtube.com/vi/PkgBq1Cs9jY/maxresdefault.jpg'
    },
    {
      id: 'yt-api-451',
      platform: 'youtube',
      title: 'Treasure Craft Box | 9th Edition | August/September | Preview',
      description: 'This bimonthly box is the largest of CoraCreaCrafts’ boxes. Not only will you receive a selection of vintage style washi tapes, stamps, papers, stickers and ephemera in larger quantity than the other ',
      url: 'https://www.youtube.com/watch?v=AVHXYpGHI0M',
      embedId: 'AVHXYpGHI0M',
      category: 'box-reveal',
      tags: ['washi tape', 'vintage', 'stickers'],
      creator: 'CoraCreaCrafts',
      date: '2022-08',
      thumbnail: 'https://img.youtube.com/vi/AVHXYpGHI0M/maxresdefault.jpg'
    },
    {
      id: 'yt-api-452',
      platform: 'youtube',
      title: 'Gatefold Spread | Long Version | DIY Tutorial | Journaling',
      description: '',
      url: 'https://www.youtube.com/watch?v=05yroMqov6Q',
      embedId: '05yroMqov6Q',
      category: 'tutorial',
      tags: ['journaling', 'tutorial', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2022-08',
      thumbnail: 'https://img.youtube.com/vi/05yroMqov6Q/maxresdefault.jpg'
    },
    {
      id: 'yt-api-453',
      platform: 'youtube',
      title: 'Window Journal | Long Version | DIY Tutorial | Journaling',
      description: '',
      url: 'https://www.youtube.com/watch?v=Ro2KhEPGMe4',
      embedId: 'Ro2KhEPGMe4',
      category: 'tutorial',
      tags: ['journaling', 'tutorial', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2022-07',
      thumbnail: 'https://img.youtube.com/vi/Ro2KhEPGMe4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-454',
      platform: 'youtube',
      title: 'July Vintage Craft Box | South of France Theme | Journaling & Crafting Subscription Box',
      description: '',
      url: 'https://www.youtube.com/watch?v=-ncvvIrIJKA',
      embedId: '-ncvvIrIJKA',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2022-07',
      thumbnail: 'https://img.youtube.com/vi/-ncvvIrIJKA/maxresdefault.jpg'
    },
    {
      id: 'yt-api-455',
      platform: 'youtube',
      title: 'Rice Paper Journal Page | Long Version | DIY Tutorial | Journaling',
      description: 'Here\'s a long version of the video posted on IG showing the details of how to apply rice paper for your backgrounds. Rice paper is a super versatile medium and can be used on countless of crafts and p',
      url: 'https://www.youtube.com/watch?v=54gLxxd2czk',
      embedId: '54gLxxd2czk',
      category: 'tutorial',
      tags: ['journaling', 'tutorial', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2022-07',
      thumbnail: 'https://img.youtube.com/vi/54gLxxd2czk/maxresdefault.jpg'
    },
    {
      id: 'yt-api-456',
      platform: 'youtube',
      title: 'Upcycling Paper Bags | Flower Deco Bag | DIY Tutorial | Crafting Idea',
      description: '',
      url: 'https://www.youtube.com/watch?v=0C6ss2aSA9s',
      embedId: '0C6ss2aSA9s',
      category: 'tutorial',
      tags: ['tutorial', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2022-06',
      thumbnail: 'https://img.youtube.com/vi/0C6ss2aSA9s/maxresdefault.jpg'
    },
    {
      id: 'yt-api-457',
      platform: 'youtube',
      title: 'Treasure Craft Box | 8th Edition | June/July | Preview',
      description: 'This bimonthly box is the largest of CoraCreaCrafts’ boxes. Not only will you receive a selection of vintage style washi tapes, stamps, papers, stickers and ephemera in larger quantity than the other ',
      url: 'https://www.youtube.com/watch?v=V7rtobdh7Qg',
      embedId: 'V7rtobdh7Qg',
      category: 'box-reveal',
      tags: ['washi tape', 'vintage', 'stickers'],
      creator: 'CoraCreaCrafts',
      date: '2022-06',
      thumbnail: 'https://img.youtube.com/vi/V7rtobdh7Qg/maxresdefault.jpg'
    },
    {
      id: 'yt-api-458',
      platform: 'youtube',
      title: 'Specimen Tags | Long Version | DIY Tutorial | Vintage Journaling',
      description: 'Here is a slow version of the ephemera tags posted on IG - this will show you a bit more details into how I made this but I unfortunately lost the footage showing the details (cutting and adding the 3',
      url: 'https://www.youtube.com/watch?v=E7UZfHOZ1r0',
      embedId: 'E7UZfHOZ1r0',
      category: 'tutorial',
      tags: ['journaling', 'vintage', 'tutorial', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2022-06',
      thumbnail: 'https://img.youtube.com/vi/E7UZfHOZ1r0/maxresdefault.jpg'
    },
    {
      id: 'yt-api-459',
      platform: 'youtube',
      title: 'June Vintage Craft Box | Explorer Theme | Journaling & Crafting Subscription Box',
      description: '',
      url: 'https://www.youtube.com/watch?v=WD1MXMj1Eds',
      embedId: 'WD1MXMj1Eds',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2022-06',
      thumbnail: 'https://img.youtube.com/vi/WD1MXMj1Eds/maxresdefault.jpg'
    },
    {
      id: 'yt-api-460',
      platform: 'youtube',
      title: 'DIY Vintage Slides | With Sticker & Paper Book | CoraCreaCrafts',
      description: '',
      url: 'https://www.youtube.com/watch?v=F-NYHI4iwbw',
      embedId: 'F-NYHI4iwbw',
      category: 'tutorial',
      tags: ['vintage', 'stickers', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2022-06',
      thumbnail: 'https://img.youtube.com/vi/F-NYHI4iwbw/maxresdefault.jpg'
    },
    {
      id: 'yt-api-461',
      platform: 'youtube',
      title: 'Flip Journal | Long Version | DIY Tutorial | Journaling',
      description: 'Learn how to create a special flip page in your journal!',
      url: 'https://www.youtube.com/watch?v=NDlir4SpH-8',
      embedId: 'NDlir4SpH-8',
      category: 'tutorial',
      tags: ['journaling', 'tutorial', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2022-05',
      thumbnail: 'https://img.youtube.com/vi/NDlir4SpH-8/maxresdefault.jpg'
    },
    {
      id: 'yt-api-462',
      platform: 'youtube',
      title: 'May Vintage Craft Box | Art Nouveau Art Deco Theme | Journaling & Crafting Subscription Box',
      description: '',
      url: 'https://www.youtube.com/watch?v=G0JrSgcpPrQ',
      embedId: 'G0JrSgcpPrQ',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2022-05',
      thumbnail: 'https://img.youtube.com/vi/G0JrSgcpPrQ/maxresdefault.jpg'
    },
    {
      id: 'yt-api-463',
      platform: 'youtube',
      title: 'Treasure Craft Box | 7th Edition | April/May | Preview',
      description: 'Treasure Craft Box | 7th Edition | April/May | Preview  In this video: Acrylic metallic paints used on the casts: https://www.amazon.com/shop/coracreacrafts/list/2S8GL163SXZ8X Other materials to use f',
      url: 'https://www.youtube.com/watch?v=hZzEptTABJQ',
      embedId: 'hZzEptTABJQ',
      category: 'box-reveal',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2022-04',
      thumbnail: 'https://img.youtube.com/vi/hZzEptTABJQ/maxresdefault.jpg'
    },
    {
      id: 'yt-api-464',
      platform: 'youtube',
      title: 'Upcycled DIY Box with Window | April Vintage Craft Box products | DIY Crafting Idea',
      description: '',
      url: 'https://www.youtube.com/watch?v=vWE4YfpGcWA',
      embedId: 'vWE4YfpGcWA',
      category: 'box-reveal',
      tags: ['vintage', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2022-04',
      thumbnail: 'https://img.youtube.com/vi/vWE4YfpGcWA/maxresdefault.jpg'
    },
    {
      id: 'yt-api-465',
      platform: 'youtube',
      title: 'April Vintage Craft Box | Botanical Theme | Journaling & Crafting Subscription',
      description: '',
      url: 'https://www.youtube.com/watch?v=B_ToX1uMXWs',
      embedId: 'B_ToX1uMXWs',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2022-04',
      thumbnail: 'https://img.youtube.com/vi/B_ToX1uMXWs/maxresdefault.jpg'
    },
    {
      id: 'yt-api-466',
      platform: 'youtube',
      title: 'Mini Red Leather Journal | Botanic Pages | Vintage Journal',
      description: '',
      url: 'https://www.youtube.com/watch?v=yFRKMZS2dlc',
      embedId: 'yFRKMZS2dlc',
      category: 'product-showcase',
      tags: ['journaling', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2022-04',
      thumbnail: 'https://img.youtube.com/vi/yFRKMZS2dlc/maxresdefault.jpg'
    },
    {
      id: 'yt-api-467',
      platform: 'youtube',
      title: 'March Vintage Craft Box | Roman & Greek Antiquity Theme | Journaling & Crafting Subscription',
      description: '',
      url: 'https://www.youtube.com/watch?v=EDo3gyP7rxc',
      embedId: 'EDo3gyP7rxc',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2022-03',
      thumbnail: 'https://img.youtube.com/vi/EDo3gyP7rxc/maxresdefault.jpg'
    },
    {
      id: 'yt-api-468',
      platform: 'youtube',
      title: 'Treasure Craft Box | 6th Edition | February/March - Preview',
      description: 'Treasure Craft Box - 6th Edition - February/March - Preview  This bimonthly box is the largest of CoraCreaCrafts’ boxes. Not only will you receive a selection of vintage style washi tapes, stamps, pap',
      url: 'https://www.youtube.com/watch?v=6LgehKcSL8Q',
      embedId: '6LgehKcSL8Q',
      category: 'box-reveal',
      tags: ['washi tape', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2022-02',
      thumbnail: 'https://img.youtube.com/vi/6LgehKcSL8Q/maxresdefault.jpg'
    },
    {
      id: 'yt-api-469',
      platform: 'youtube',
      title: 'February Vintage Craft Box | Victorian Gothic Theme | Preview | Journaling & Crafting Subscription',
      description: '',
      url: 'https://www.youtube.com/watch?v=gfDNWydH01I',
      embedId: 'gfDNWydH01I',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'gothic', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2022-02',
      thumbnail: 'https://img.youtube.com/vi/gfDNWydH01I/maxresdefault.jpg'
    },
    {
      id: 'yt-api-470',
      platform: 'youtube',
      title: 'January Vintage Craft Box - Medieval Theme - Subscription Box - Crafting Journaling Supplies',
      description: 'Visit my website for more info & to sign up: coracreacrafts.com',
      url: 'https://www.youtube.com/watch?v=WGNuXrPebV0',
      embedId: 'WGNuXrPebV0',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'medieval', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2022-01',
      thumbnail: 'https://img.youtube.com/vi/WGNuXrPebV0/maxresdefault.jpg'
    },
    {
      id: 'yt-api-471',
      platform: 'youtube',
      title: 'Coralie\'s Vlogs | New Work Space | 002 Life of a Crafter and Shop Owner',
      description: 'I\'m excited to share my second vlog with you! This video shows the hard work we put in moving all of the shop supplies and work space to the basement of my house as the business has grown out of the s',
      url: 'https://www.youtube.com/watch?v=AHw5pat87n0',
      embedId: 'AHw5pat87n0',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2021-12',
      thumbnail: 'https://img.youtube.com/vi/AHw5pat87n0/maxresdefault.jpg'
    },
    {
      id: 'yt-api-472',
      platform: 'youtube',
      title: 'Treasure Craft Box - 5th Edition - December/January - Preview',
      description: 'Treasure Craft Box - 5th Edition - December/January - Preview  This bimonthly box is the largest of CoraCreaCrafts’ boxes. Not only will you receive a selection of vintage style washi tapes, stamps, p',
      url: 'https://www.youtube.com/watch?v=JJgpXg_-9Z4',
      embedId: 'JJgpXg_-9Z4',
      category: 'box-reveal',
      tags: ['washi tape', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-12',
      thumbnail: 'https://img.youtube.com/vi/JJgpXg_-9Z4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-473',
      platform: 'youtube',
      title: 'December Box Preview 🌘 Celestial Themes | Vintage Craft Box | Journalungg and Crafting Subscription',
      description: '',
      url: 'https://www.youtube.com/watch?v=fQXfLiVgIH4',
      embedId: 'fQXfLiVgIH4',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2021-11',
      thumbnail: 'https://img.youtube.com/vi/fQXfLiVgIH4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-474',
      platform: 'youtube',
      title: 'November Vintage Craft Box Preview | Letter Theme | Stationery Subscription Box',
      description: '',
      url: 'https://www.youtube.com/watch?v=qUJDp4bykic',
      embedId: 'qUJDp4bykic',
      category: 'box-reveal',
      tags: ['vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2021-11',
      thumbnail: 'https://img.youtube.com/vi/qUJDp4bykic/maxresdefault.jpg'
    },
    {
      id: 'yt-api-475',
      platform: 'youtube',
      title: 'Halloween Vintage Journal Spread | Spooky Theme | Vintage Ephemera',
      description: '',
      url: 'https://www.youtube.com/watch?v=xtxpCo_hY1E',
      embedId: 'xtxpCo_hY1E',
      category: 'journal-spread',
      tags: ['journaling', 'vintage', 'halloween'],
      creator: 'CoraCreaCrafts',
      date: '2021-10',
      thumbnail: 'https://img.youtube.com/vi/xtxpCo_hY1E/maxresdefault.jpg'
    },
    {
      id: 'yt-api-476',
      platform: 'youtube',
      title: 'Transparent Tags | Portrait Wax Seal | Laminator | DIY Vintage Crafts',
      description: '',
      url: 'https://www.youtube.com/watch?v=s32EEcT3ZVc',
      embedId: 's32EEcT3ZVc',
      category: 'tutorial',
      tags: ['vintage', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2021-10',
      thumbnail: 'https://img.youtube.com/vi/s32EEcT3ZVc/maxresdefault.jpg'
    },
    {
      id: 'yt-api-477',
      platform: 'youtube',
      title: 'Coralie\'s Vlogs | Intro | Vlogs 001 Life of a Crafter and Shop Owner',
      description: 'Hi there!  I am excited to share my very first Vlog with you. For me this is a special way of showing more of the behind the scenes of my business but also about myself and the fun, unexpected, annoyi',
      url: 'https://www.youtube.com/watch?v=pg7Ks7krWrQ',
      embedId: 'pg7Ks7krWrQ',
      category: 'behind-the-scenes',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2021-10',
      thumbnail: 'https://img.youtube.com/vi/pg7Ks7krWrQ/maxresdefault.jpg'
    },
    {
      id: 'yt-api-478',
      platform: 'youtube',
      title: 'Treasure Craft Box - 4th Edition - October/November - Preview',
      description: 'Treasure Craft Box - 4th Edition - October/November - Preview  This bimonthly box is the largest of CoraCreaCrafts’ boxes. Not only will you receive a selection of vintage style washi tapes, stamps, p',
      url: 'https://www.youtube.com/watch?v=0ZVeszYrx_k',
      embedId: '0ZVeszYrx_k',
      category: 'box-reveal',
      tags: ['washi tape', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-09',
      thumbnail: 'https://img.youtube.com/vi/0ZVeszYrx_k/maxresdefault.jpg'
    },
    {
      id: 'yt-api-479',
      platform: 'youtube',
      title: 'October Vintage Craft Box - Autumn Theme - Subscription Box - Crafting Journaling Supplies',
      description: 'Visit my website for more info & to sign up: coracreacrafts.com',
      url: 'https://www.youtube.com/watch?v=ngBaZ53SfJo',
      embedId: 'ngBaZ53SfJo',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'autumn', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2021-09',
      thumbnail: 'https://img.youtube.com/vi/ngBaZ53SfJo/maxresdefault.jpg'
    },
    {
      id: 'yt-api-480',
      platform: 'youtube',
      title: 'Fabric Tags Tutorial - Recycled Curtains - Wax Seal & Stamp Ephemera Craft',
      description: 'Reusing and old sheer curtain to make some unique ephemera pieces. Use them in your journals, scrapbooks, snail mail and more!  Find products used in this tutorial here: coracreacrafts.com',
      url: 'https://www.youtube.com/watch?v=Gfrn0dnwTQY',
      embedId: 'Gfrn0dnwTQY',
      category: 'tutorial',
      tags: ['journaling', 'tutorial'],
      creator: 'CoraCreaCrafts',
      date: '2021-09',
      thumbnail: 'https://img.youtube.com/vi/Gfrn0dnwTQY/maxresdefault.jpg'
    },
    {
      id: 'yt-api-481',
      platform: 'youtube',
      title: 'Mixed Media Vintage Art Pieces for Wall Decor - Grit Paste Paper Crafts',
      description: 'My very first time using grit paste by @tim_holtz @ranger_ink and I absolutely loved it 🖤 Created these two mixed media pieces which will go and decorate my office wall 🥰 I used: * Grit paste * Flor',
      url: 'https://www.youtube.com/watch?v=Y2XygyYKrD8',
      embedId: 'Y2XygyYKrD8',
      category: 'product-showcase',
      tags: ['vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-09',
      thumbnail: 'https://img.youtube.com/vi/Y2XygyYKrD8/maxresdefault.jpg'
    },
    {
      id: 'yt-api-482',
      platform: 'youtube',
      title: 'September Vintage Craft Box - Alchemy Theme - Subscription Box - Crafting Journaling Supplies',
      description: '',
      url: 'https://www.youtube.com/watch?v=SfFvXcnci1E',
      embedId: 'SfFvXcnci1E',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2021-08',
      thumbnail: 'https://img.youtube.com/vi/SfFvXcnci1E/maxresdefault.jpg'
    },
    {
      id: 'yt-api-483',
      platform: 'youtube',
      title: 'Glazed Botanical Tags Tutorial Video - DIY Tags Vintage Style Crafts',
      description: 'Here is the tutorial for the glazed tags I made last week and shared in my reels ❤️  I used: * cardstock paper for the tags * botanical dictionary papers * distress ink and embossing glaze by @ranger_',
      url: 'https://www.youtube.com/watch?v=CXAIkc3UkBw',
      embedId: 'CXAIkc3UkBw',
      category: 'tutorial',
      tags: ['vintage', 'tutorial', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2021-08',
      thumbnail: 'https://img.youtube.com/vi/CXAIkc3UkBw/maxresdefault.jpg'
    },
    {
      id: 'yt-api-484',
      platform: 'youtube',
      title: 'Alchemy Washi Tapes - Vintage Craft Box - September',
      description: 'These washi tapes were created together with the artist Storm Sparrow for the September Vintage Craft Box. Check out my website to see all the items included in the box! coracreacrafts.com',
      url: 'https://www.youtube.com/watch?v=l_n1g6KhOzs',
      embedId: 'l_n1g6KhOzs',
      category: 'box-reveal',
      tags: ['washi tape', 'vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-08',
      thumbnail: 'https://img.youtube.com/vi/l_n1g6KhOzs/maxresdefault.jpg'
    },
    {
      id: 'yt-api-485',
      platform: 'youtube',
      title: 'Treasure Craft Box - 3rd Edition - Vintage Craft Supplies Bimonthly Subscription Box',
      description: 'Treasure Craft Box - 3rd Edition - August/September - Overview  This bimonthly box is the largest of CoraCreaCrafts’ boxes. Not only will you receive a selection of vintage style washi tapes, stamps, ',
      url: 'https://www.youtube.com/watch?v=DpYs4f_64pg',
      embedId: 'DpYs4f_64pg',
      category: 'box-reveal',
      tags: ['washi tape', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2021-08',
      thumbnail: 'https://img.youtube.com/vi/DpYs4f_64pg/maxresdefault.jpg'
    },
    {
      id: 'yt-api-486',
      platform: 'youtube',
      title: 'August Vintage Craft Box Sneak Peek - Old Maps Theme - Journaling Crafting Supplies Subscription',
      description: '',
      url: 'https://www.youtube.com/watch?v=PeONopFc5d4',
      embedId: 'PeONopFc5d4',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2021-07',
      thumbnail: 'https://img.youtube.com/vi/PeONopFc5d4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-487',
      platform: 'youtube',
      title: 'Large Ephemera Hanging Piece DIY - Butterflies, windows, snail mail, stamps, laminator',
      description: 'Short DIY tutorial using my laminator again! The first part of the video got cut so you won\'t see me put the pieces together before laminating but the process is easy: - Use a laminating sheet, open i',
      url: 'https://www.youtube.com/watch?v=jO7mQ2R577s',
      embedId: 'jO7mQ2R577s',
      category: 'tutorial',
      tags: ['tutorial', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2021-07',
      thumbnail: 'https://img.youtube.com/vi/jO7mQ2R577s/maxresdefault.jpg'
    },
    {
      id: 'yt-api-488',
      platform: 'youtube',
      title: 'Folder Bag DIY - using July Animal Box Stamps - Snail Mail Pocket Idea - Crafting Tutorial',
      description: '',
      url: 'https://www.youtube.com/watch?v=akLtXCj8XlI',
      embedId: 'akLtXCj8XlI',
      category: 'box-reveal',
      tags: ['tutorial', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2021-07',
      thumbnail: 'https://img.youtube.com/vi/akLtXCj8XlI/maxresdefault.jpg'
    },
    {
      id: 'yt-api-489',
      platform: 'youtube',
      title: 'Surprise Box Sneak Peek - June batch 2',
      description: '',
      url: 'https://www.youtube.com/watch?v=-R81So7iG2Q',
      embedId: '-R81So7iG2Q',
      category: 'box-reveal',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2021-06',
      thumbnail: 'https://img.youtube.com/vi/-R81So7iG2Q/maxresdefault.jpg'
    },
    {
      id: 'yt-api-490',
      platform: 'youtube',
      title: 'May Box Sneak Peek - Animals & Wildlife Theme - Crafting and Journaling Subscription Box Vintage',
      description: 'Monthly Vintage Craft Subscription Box Order here: https://coracreacrafts.com/product/vintage-craft-supplies-box-monthly-subscription/ Theme: Animals & Wildlife This box will ship by the 10th of July ',
      url: 'https://www.youtube.com/watch?v=3Zoq7WaFdcg',
      embedId: '3Zoq7WaFdcg',
      category: 'box-reveal',
      tags: ['journaling', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2021-06',
      thumbnail: 'https://img.youtube.com/vi/3Zoq7WaFdcg/maxresdefault.jpg'
    },
    {
      id: 'yt-api-491',
      platform: 'youtube',
      title: 'Game of Thrones Bookmark DIY Easy Tutorial Crafting Idea Book Lovers',
      description: '',
      url: 'https://www.youtube.com/watch?v=vyygXmMF9w0',
      embedId: 'vyygXmMF9w0',
      category: 'tutorial',
      tags: ['tutorial', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2021-06',
      thumbnail: 'https://img.youtube.com/vi/vyygXmMF9w0/maxresdefault.jpg'
    },
    {
      id: 'yt-api-492',
      platform: 'youtube',
      title: 'Vellum Envelope Decoration ASMR  sounds crafting video vintage Style satisfying',
      description: '',
      url: 'https://www.youtube.com/watch?v=f9QTaQtJlBM',
      embedId: 'f9QTaQtJlBM',
      category: 'product-showcase',
      tags: ['vintage'],
      creator: 'CoraCreaCrafts',
      date: '2021-06',
      thumbnail: 'https://img.youtube.com/vi/f9QTaQtJlBM/maxresdefault.jpg'
    },
    {
      id: 'yt-api-493',
      platform: 'youtube',
      title: 'ASMR sounds - Satisfying stationery order packing video',
      description: '',
      url: 'https://www.youtube.com/watch?v=Uo_ETmhrFcU',
      embedId: 'Uo_ETmhrFcU',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2021-06',
      thumbnail: 'https://img.youtube.com/vi/Uo_ETmhrFcU/maxresdefault.jpg'
    },
    {
      id: 'yt-api-494',
      platform: 'youtube',
      title: 'Treasure Craft Box - 2nd Edition',
      description: 'Bimonthly Vintage Treasure Craft Subscription Box  The upcoming box is June/July – this box will be delivered to your doorstep every second month  This bimonthly box is the largest of CoraCreaCrafts’ ',
      url: 'https://www.youtube.com/watch?v=psebFWLlEOY',
      embedId: 'psebFWLlEOY',
      category: 'box-reveal',
      tags: ['vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2021-05',
      thumbnail: 'https://img.youtube.com/vi/psebFWLlEOY/maxresdefault.jpg'
    },
    {
      id: 'yt-api-495',
      platform: 'youtube',
      title: 'June Box Sneak Peek - Nautical Theme',
      description: 'Monthly Vintage Craft Subscription Box Order here: https://coracreacrafts.com/product/vintage-craft-supplies-box-monthly-subscription/ Theme: Nautical This box will ship by the 10th of June  June foll',
      url: 'https://www.youtube.com/watch?v=6OVlD2lcdrE',
      embedId: '6OVlD2lcdrE',
      category: 'box-reveal',
      tags: ['vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2021-05',
      thumbnail: 'https://img.youtube.com/vi/6OVlD2lcdrE/maxresdefault.jpg'
    },
    {
      id: 'yt-api-496',
      platform: 'youtube',
      title: 'Labels & Paper Pad Folder Tutorial - Relaxing Easy DIY',
      description: 'I wanted to create something to hold samples of these beautiful papers and labels for my snail mail or to use when I\'m on the go 📜  Created using: ✨ Cardboard Paper ✨ Papers from the Ephemera Paper B',
      url: 'https://www.youtube.com/watch?v=VT2k6_Pn87U',
      embedId: 'VT2k6_Pn87U',
      category: 'tutorial',
      tags: ['tutorial', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2021-04',
      thumbnail: 'https://img.youtube.com/vi/VT2k6_Pn87U/maxresdefault.jpg'
    },
    {
      id: 'yt-api-497',
      platform: 'youtube',
      title: 'May Box Sneak Peek - Flora Theme',
      description: 'Monthly Vintage Craft Subscription Box  The current box is MAY – theme: Flora Shipping: by 10th of May – Mother’s Day Orders please notify me for early shipping  May is all about Flora – this box will',
      url: 'https://www.youtube.com/watch?v=kn9VmTrWH4E',
      embedId: 'kn9VmTrWH4E',
      category: 'box-reveal',
      tags: ['vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2021-04',
      thumbnail: 'https://img.youtube.com/vi/kn9VmTrWH4E/maxresdefault.jpg'
    },
    {
      id: 'yt-api-498',
      platform: 'youtube',
      title: 'April Box - Sneak Peek - Vintage Craft Subscription Box - Anatomy',
      description: 'Monthly Vintage Craft Subscription Box   Theme: Anatomy THIS BOX WILL SHIP by the 10th of April  Let yourself be surprised by this monthly subscription box. This box includes timeless vintage style ep',
      url: 'https://www.youtube.com/watch?v=XmcjkWoF3lM',
      embedId: 'XmcjkWoF3lM',
      category: 'box-reveal',
      tags: ['vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2021-03',
      thumbnail: 'https://img.youtube.com/vi/XmcjkWoF3lM/maxresdefault.jpg'
    },
    {
      id: 'yt-api-499',
      platform: 'youtube',
      title: 'Valheim Foil Stickers - Viking Norse Runes',
      description: 'These stickers were inspired by the new video game Valheim which is set in Viking world. While fighting for survival, you may come across some beautiful runes. I used one of these runes to create thes',
      url: 'https://www.youtube.com/watch?v=itvt69RezFI',
      embedId: 'itvt69RezFI',
      category: 'product-showcase',
      tags: ['stickers'],
      creator: 'CoraCreaCrafts',
      date: '2021-03',
      thumbnail: 'https://img.youtube.com/vi/itvt69RezFI/maxresdefault.jpg'
    },
    {
      id: 'yt-api-500',
      platform: 'youtube',
      title: 'Valheim Foil Stickers Norse Runes Mythology Crafting Tips Foil Stickers DIY Satisfying Video',
      description: 'These stickers were inspired by the new video game Valheim which is set in Viking world. While fighting for survival, you may come across some beautiful runes. I used one of these runes to create thes',
      url: 'https://www.youtube.com/watch?v=MT3aIZuJU0k',
      embedId: 'MT3aIZuJU0k',
      category: 'tutorial',
      tags: ['stickers', 'diy'],
      creator: 'CoraCreaCrafts',
      date: '2021-03',
      thumbnail: 'https://img.youtube.com/vi/MT3aIZuJU0k/maxresdefault.jpg'
    },
    {
      id: 'yt-api-501',
      platform: 'youtube',
      title: 'March Box - Sneak Peek - Vintage Craft Subscription Box - Butterflies and Insects',
      description: 'Monthly Vintage Craft Subscription Box -  March Theme: Butterflies and Insects THIS BOX WILL SHIP by the 15th  of March  Let yourself be surprised by this monthly subscription box. This box includes t',
      url: 'https://www.youtube.com/watch?v=id5pJ5Vauwc',
      embedId: 'id5pJ5Vauwc',
      category: 'box-reveal',
      tags: ['vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2021-02',
      thumbnail: 'https://img.youtube.com/vi/id5pJ5Vauwc/maxresdefault.jpg'
    },
    {
      id: 'yt-api-502',
      platform: 'youtube',
      title: 'Introducing the Treasure Craft Box',
      description: 'Bimonthly Vintage Treasure Craft Subscription Box  The upcoming box is April/May – this box will be delivered to your doorstep every second month  This bimonthly box is the largest of CoraCreaCrafts’ ',
      url: 'https://www.youtube.com/watch?v=8SA83CsF_9k',
      embedId: '8SA83CsF_9k',
      category: 'box-reveal',
      tags: ['vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2021-02',
      thumbnail: 'https://img.youtube.com/vi/8SA83CsF_9k/maxresdefault.jpg'
    },
    {
      id: 'yt-api-503',
      platform: 'youtube',
      title: 'Monthly Spread & Habit Tracker - Fairytale - Snow White',
      description: 'New video process for my "fairytale inspired journal" - this month was centered around Snow White.',
      url: 'https://www.youtube.com/watch?v=VY8tJKruRm0',
      embedId: 'VY8tJKruRm0',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2021-02',
      thumbnail: 'https://img.youtube.com/vi/VY8tJKruRm0/maxresdefault.jpg'
    },
    {
      id: 'yt-api-504',
      platform: 'youtube',
      title: 'February Box - Sneak Peek',
      description: 'Full Box Preview available: https://youtu.be/gmiZpJQuD28  Monthly VMonthly Vintage Craft Subscription Box -  February Theme: Library THIS BOX WILL SHIP by the 15th  of February  Let yourself be surpri',
      url: 'https://www.youtube.com/watch?v=XEhM4UTJ6Zw',
      embedId: 'XEhM4UTJ6Zw',
      category: 'box-reveal',
      tags: ['vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2021-01',
      thumbnail: 'https://img.youtube.com/vi/XEhM4UTJ6Zw/maxresdefault.jpg'
    },
    {
      id: 'yt-api-505',
      platform: 'youtube',
      title: 'February Box - Preview - Theme: Library',
      description: 'Monthly Vintage Craft Subscription Box -  February Theme: Library THIS BOX WILL SHIP by the 15th  of February  Let yourself be surprised by this monthly subscription box. This box includes timeless vi',
      url: 'https://www.youtube.com/watch?v=gmiZpJQuD28',
      embedId: 'gmiZpJQuD28',
      category: 'box-reveal',
      tags: ['vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2021-01',
      thumbnail: 'https://img.youtube.com/vi/gmiZpJQuD28/maxresdefault.jpg'
    },
    {
      id: 'yt-api-506',
      platform: 'youtube',
      title: 'January Box Sneak Peek',
      description: 'Monthly Vintage Craft Subscription Box - January Box   Let yourself be surprised by this monthly subscription box. This box includes timeless vintage style ephemera pieces for your crafts - each box w',
      url: 'https://www.youtube.com/watch?v=TaEHAUGJKO0',
      embedId: 'TaEHAUGJKO0',
      category: 'box-reveal',
      tags: ['vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2020-12',
      thumbnail: 'https://img.youtube.com/vi/TaEHAUGJKO0/maxresdefault.jpg'
    },
    {
      id: 'yt-api-507',
      platform: 'youtube',
      title: 'December Craft Box - Preview',
      description: 'Monthly Vintage Craft Subscription Box - DECEMBER  *Holiday announcement*: for the upcoming holiday season you have the option to pre-order December boxes ahead of time as there will be a limited quan',
      url: 'https://www.youtube.com/watch?v=wHg8lZZwvx4',
      embedId: 'wHg8lZZwvx4',
      category: 'box-reveal',
      tags: ['vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2020-11',
      thumbnail: 'https://img.youtube.com/vi/wHg8lZZwvx4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-508',
      platform: 'youtube',
      title: 'Christmas Burlap Bags DYI',
      description: '',
      url: 'https://www.youtube.com/watch?v=U1BpCUkyCGI',
      embedId: 'U1BpCUkyCGI',
      category: 'product-showcase',
      tags: ['christmas'],
      creator: 'CoraCreaCrafts',
      date: '2020-11',
      thumbnail: 'https://img.youtube.com/vi/U1BpCUkyCGI/maxresdefault.jpg'
    },
    {
      id: 'yt-api-509',
      platform: 'youtube',
      title: 'Yellowstone Travel Journal Spread',
      description: '',
      url: 'https://www.youtube.com/watch?v=9nd-v8jvdc4',
      embedId: '9nd-v8jvdc4',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-11',
      thumbnail: 'https://img.youtube.com/vi/9nd-v8jvdc4/maxresdefault.jpg'
    },
    {
      id: 'yt-api-510',
      platform: 'youtube',
      title: 'Halloween Week Journal Spread',
      description: '',
      url: 'https://www.youtube.com/watch?v=IIal3WMO7so',
      embedId: 'IIal3WMO7so',
      category: 'journal-spread',
      tags: ['journaling', 'halloween'],
      creator: 'CoraCreaCrafts',
      date: '2020-10',
      thumbnail: 'https://img.youtube.com/vi/IIal3WMO7so/maxresdefault.jpg'
    },
    {
      id: 'yt-api-511',
      platform: 'youtube',
      title: 'Autumn Gift Packaging DYI idea',
      description: '',
      url: 'https://www.youtube.com/watch?v=FgT_Gf4y7ns',
      embedId: 'FgT_Gf4y7ns',
      category: 'product-showcase',
      tags: ['autumn'],
      creator: 'CoraCreaCrafts',
      date: '2020-10',
      thumbnail: 'https://img.youtube.com/vi/FgT_Gf4y7ns/maxresdefault.jpg'
    },
    {
      id: 'yt-api-512',
      platform: 'youtube',
      title: 'November Box - Vintage Craft Supplies - Packing Video',
      description: 'Monthly Vintage Craft Subscription Box - NOVEMBER  *Holiday announcement*: for the upcoming holiday season you have the option to pre-order December boxes ahead of time as there will be a limited quan',
      url: 'https://www.youtube.com/watch?v=b9D55DZnfS0',
      embedId: 'b9D55DZnfS0',
      category: 'box-reveal',
      tags: ['vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2020-10',
      thumbnail: 'https://img.youtube.com/vi/b9D55DZnfS0/maxresdefault.jpg'
    },
    {
      id: 'yt-api-513',
      platform: 'youtube',
      title: 'November Vintage Craft Box Sneak Peak',
      description: 'Monthly Vintage Craft Subscription Box - NOVEMBER  *Holiday announcement*: for the upcoming holiday season you have the option to pre-order December boxes ahead of time as there will be a limited quan',
      url: 'https://www.youtube.com/watch?v=ysL0ovaiHR0',
      embedId: 'ysL0ovaiHR0',
      category: 'box-reveal',
      tags: ['vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2020-10',
      thumbnail: 'https://img.youtube.com/vi/ysL0ovaiHR0/maxresdefault.jpg'
    },
    {
      id: 'yt-api-514',
      platform: 'youtube',
      title: 'Large City Wooden Stamps',
      description: 'These beautiful stamps are available in my shop ! CoraCreaCrafts.com',
      url: 'https://www.youtube.com/watch?v=nh5j8Y_uYqY',
      embedId: 'nh5j8Y_uYqY',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2020-09',
      thumbnail: 'https://img.youtube.com/vi/nh5j8Y_uYqY/maxresdefault.jpg'
    },
    {
      id: 'yt-api-515',
      platform: 'youtube',
      title: 'Handmade Map Travel Journal Map Making USA Roadtrip',
      description: '',
      url: 'https://www.youtube.com/watch?v=td2OmsHKl08',
      embedId: 'td2OmsHKl08',
      category: 'product-showcase',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-09',
      thumbnail: 'https://img.youtube.com/vi/td2OmsHKl08/maxresdefault.jpg'
    },
    {
      id: 'yt-api-516',
      platform: 'youtube',
      title: 'Travel Journal Spread Ideas Wyoming Adventures',
      description: '',
      url: 'https://www.youtube.com/watch?v=rROkxRfz8Ss',
      embedId: 'rROkxRfz8Ss',
      category: 'journal-spread',
      tags: ['journaling', 'advent calendar'],
      creator: 'CoraCreaCrafts',
      date: '2020-09',
      thumbnail: 'https://img.youtube.com/vi/rROkxRfz8Ss/maxresdefault.jpg'
    },
    {
      id: 'yt-api-517',
      platform: 'youtube',
      title: 'September Bullet Journal Spread Monthly Cover Page',
      description: '',
      url: 'https://www.youtube.com/watch?v=ctABgSI6kNE',
      embedId: 'ctABgSI6kNE',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-09',
      thumbnail: 'https://img.youtube.com/vi/ctABgSI6kNE/maxresdefault.jpg'
    },
    {
      id: 'yt-api-518',
      platform: 'youtube',
      title: 'Jackson Hole Wyoming Travel Journal Doodle',
      description: '',
      url: 'https://www.youtube.com/watch?v=ePq7tSoWxqI',
      embedId: 'ePq7tSoWxqI',
      category: 'product-showcase',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-09',
      thumbnail: 'https://img.youtube.com/vi/ePq7tSoWxqI/maxresdefault.jpg'
    },
    {
      id: 'yt-api-519',
      platform: 'youtube',
      title: 'Illuminated Letter Stickers & Cross Wax Seal',
      description: 'Packaging video of my Illuminated Letter Stickers with a simple vellum paper sheet, natural twine and a cross wax seal with satin red wax.  Illuminated Letter Stickers and Wax Seals are available in m',
      url: 'https://www.youtube.com/watch?v=MP_iTzETwIQ',
      embedId: 'MP_iTzETwIQ',
      category: 'product-showcase',
      tags: ['stickers'],
      creator: 'CoraCreaCrafts',
      date: '2020-09',
      thumbnail: 'https://img.youtube.com/vi/MP_iTzETwIQ/maxresdefault.jpg'
    },
    {
      id: 'yt-api-520',
      platform: 'youtube',
      title: 'October Vintage Craft Box Early Sneak Peek',
      description: 'Here are some of the elements you will find in the October Vintage Craft Box - this is the first box which includes a collaboration with another artist to bring you some unique items. This month you w',
      url: 'https://www.youtube.com/watch?v=l0CY42TDd3E',
      embedId: 'l0CY42TDd3E',
      category: 'box-reveal',
      tags: ['vintage'],
      creator: 'CoraCreaCrafts',
      date: '2020-09',
      thumbnail: 'https://img.youtube.com/vi/l0CY42TDd3E/maxresdefault.jpg'
    },
    {
      id: 'yt-api-521',
      platform: 'youtube',
      title: 'Autumn Birthday Card Idea with Embossing Elements',
      description: 'DYI Autumn card with beautifully embossed leaves using: - Leaves Stamp from my shop: coracreacrafts.com - Yellow gold and red gold Embossing powder from ranger - Embossing Inkpad from ranger',
      url: 'https://www.youtube.com/watch?v=HcHp41nKTLs',
      embedId: 'HcHp41nKTLs',
      category: 'product-showcase',
      tags: ['autumn'],
      creator: 'CoraCreaCrafts',
      date: '2020-09',
      thumbnail: 'https://img.youtube.com/vi/HcHp41nKTLs/maxresdefault.jpg'
    },
    {
      id: 'yt-api-522',
      platform: 'youtube',
      title: 'DYI Autumn Stamps',
      description: 'Here\'s an easy way of making your own DYI stamps with some cute little stickers you can find in my shop !  coracreacrafts.com',
      url: 'https://www.youtube.com/watch?v=0sMVLLkYYDk',
      embedId: '0sMVLLkYYDk',
      category: 'product-showcase',
      tags: ['autumn', 'stickers'],
      creator: 'CoraCreaCrafts',
      date: '2020-09',
      thumbnail: 'https://img.youtube.com/vi/0sMVLLkYYDk/maxresdefault.jpg'
    },
    {
      id: 'yt-api-523',
      platform: 'youtube',
      title: 'October Vintage Craft Box Sneak Peek',
      description: 'Monthly Vintage Craft Subscription Box  *Holiday announcement*: for the upcoming holiday season you have the option to pre-order the November and December boxes ahead of time as there will be a limite',
      url: 'https://www.youtube.com/watch?v=RZ1BbSjAo0A',
      embedId: 'RZ1BbSjAo0A',
      category: 'box-reveal',
      tags: ['vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2020-09',
      thumbnail: 'https://img.youtube.com/vi/RZ1BbSjAo0A/maxresdefault.jpg'
    },
    {
      id: 'yt-api-524',
      platform: 'youtube',
      title: 'September Monthly Craft Box Sneak Peek - Vintage Crafts - Inventors',
      description: 'Order now: - https://www.etsy.com/listing/799232185/september-mystery-craft-supplies-box - https://www.cratejoy.com/subscription-box/cora-crea-crafts/  Monthly Vintage Craft Subscription Box   Let you',
      url: 'https://www.youtube.com/watch?v=HCiWkLoeVsA',
      embedId: 'HCiWkLoeVsA',
      category: 'box-reveal',
      tags: ['vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2020-08',
      thumbnail: 'https://img.youtube.com/vi/HCiWkLoeVsA/maxresdefault.jpg'
    },
    {
      id: 'yt-api-525',
      platform: 'youtube',
      title: 'August Monthly Craft Box Snapshot',
      description: '!! Orders for the August Mystery Craft Box open until 31st July !!  THIS BOX WILL SHIP by 10th August Orders after 31st July will be registered to start with the September subscription box  Monthly Vi',
      url: 'https://www.youtube.com/watch?v=kpnWpSbq090',
      embedId: 'kpnWpSbq090',
      category: 'box-reveal',
      tags: ['subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2020-07',
      thumbnail: 'https://img.youtube.com/vi/kpnWpSbq090/maxresdefault.jpg'
    },
    {
      id: 'yt-api-526',
      platform: 'youtube',
      title: 'August Craft Box - Sneak Peak',
      description: 'Last Chance to order the August Box - only a couple left in stock !  Monthly Vintage Craft Subscription Box   Let yourself be surprised by this monthly subscription box is a box with timeless vintage ',
      url: 'https://www.youtube.com/watch?v=KdniJjHIK2M',
      embedId: 'KdniJjHIK2M',
      category: 'box-reveal',
      tags: ['vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2020-07',
      thumbnail: 'https://img.youtube.com/vi/KdniJjHIK2M/maxresdefault.jpg'
    },
    {
      id: 'yt-api-527',
      platform: 'youtube',
      title: 'July Vintage Craft Box Packing Video Showcase',
      description: 'Monthly Vintage Craft Subscription Box  This monthly subscription box is a box with timeless vintage ephemera pieces for your crafts - each box will include a wide variety of vintage themed washi tape',
      url: 'https://www.youtube.com/watch?v=m9Vq6hjvx3I',
      embedId: 'm9Vq6hjvx3I',
      category: 'box-reveal',
      tags: ['washi tape', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2020-07',
      thumbnail: 'https://img.youtube.com/vi/m9Vq6hjvx3I/maxresdefault.jpg'
    },
    {
      id: 'yt-api-528',
      platform: 'youtube',
      title: 'June Vintage Craft Box Full Packing Video',
      description: 'Monthly Vintage Craft Subscription Box  This monthly subscription box is a box with timeless vintage ephemera pieces for your crafts - each box will include a wide variety of vintage themed washi tape',
      url: 'https://www.youtube.com/watch?v=XZG_ubDHT1M',
      embedId: 'XZG_ubDHT1M',
      category: 'box-reveal',
      tags: ['washi tape', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2020-06',
      thumbnail: 'https://img.youtube.com/vi/XZG_ubDHT1M/maxresdefault.jpg'
    },
    {
      id: 'yt-api-529',
      platform: 'youtube',
      title: 'June Vintage Craft Box Subscription Packing Video Showcase',
      description: 'Monthly Vintage Craft Subscription Box  This monthly subscription box is a box with timeless vintage ephemera pieces for your crafts - each box will include a wide variety of vintage themed washi tape',
      url: 'https://www.youtube.com/watch?v=vZogqIKGO_8',
      embedId: 'vZogqIKGO_8',
      category: 'box-reveal',
      tags: ['washi tape', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2020-06',
      thumbnail: 'https://img.youtube.com/vi/vZogqIKGO_8/maxresdefault.jpg'
    },
    {
      id: 'yt-api-530',
      platform: 'youtube',
      title: 'May Vintage Craft Box Subscription Packing Video Showcase',
      description: 'Monthly Vintage Craft Subscription Box  This monthly subscription box is a box with timeless vintage ephemera pieces for your crafts - each box will include a wide variety of vintage themed washi tape',
      url: 'https://www.youtube.com/watch?v=n_gps9G-0iE',
      embedId: 'n_gps9G-0iE',
      category: 'box-reveal',
      tags: ['washi tape', 'vintage', 'subscription box'],
      creator: 'CoraCreaCrafts',
      date: '2020-05',
      thumbnail: 'https://img.youtube.com/vi/n_gps9G-0iE/maxresdefault.jpg'
    },
    {
      id: 'yt-api-531',
      platform: 'youtube',
      title: 'Spring Flowers Bullet Journal Goals Spread Satisfying Video May Goals',
      description: 'A short video to show you how to set up a goals spread with a flowery/spring theme in your bullet journal',
      url: 'https://www.youtube.com/watch?v=QMpYiXdoo7w',
      embedId: 'QMpYiXdoo7w',
      category: 'tutorial',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-05',
      thumbnail: 'https://img.youtube.com/vi/QMpYiXdoo7w/maxresdefault.jpg'
    },
    {
      id: 'yt-api-532',
      platform: 'youtube',
      title: 'Castle Drawing Tutorial',
      description: 'Small tutorial video to show you how to draw a castle - this was made in a custom travel journal for a family traveling to Ireland.',
      url: 'https://www.youtube.com/watch?v=4v5c5qcxg3Q',
      embedId: '4v5c5qcxg3Q',
      category: 'tutorial',
      tags: ['journaling', 'tutorial'],
      creator: 'CoraCreaCrafts',
      date: '2020-04',
      thumbnail: 'https://img.youtube.com/vi/4v5c5qcxg3Q/maxresdefault.jpg'
    },
    {
      id: 'yt-api-533',
      platform: 'youtube',
      title: 'March Monthly Reading Planner Video',
      description: 'A short video to show you how I planned my March reading journal starting with a cover page and monthly overview, ending with my favorite books and reading tracker.',
      url: 'https://www.youtube.com/watch?v=QOdgG6Chapg',
      embedId: 'QOdgG6Chapg',
      category: 'product-showcase',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-02',
      thumbnail: 'https://img.youtube.com/vi/QOdgG6Chapg/maxresdefault.jpg'
    },
    {
      id: 'yt-api-534',
      platform: 'youtube',
      title: 'Reading Journal - How to make a To Be Read Book Shelf - Bullet Journal',
      description: '',
      url: 'https://www.youtube.com/watch?v=7fbve9gVxmk',
      embedId: '7fbve9gVxmk',
      category: 'tutorial',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-01',
      thumbnail: 'https://img.youtube.com/vi/7fbve9gVxmk/maxresdefault.jpg'
    },
    {
      id: 'yt-api-535',
      platform: 'youtube',
      title: 'Celestial Journal Spreads - Galaxy Theme - Bullet Journal - Plan with Me - January',
      description: 'This video is a snapshot of how I planned my January journal following a "Celestial" theme. I used dark blue and black watercolors and drew on it with a golden uniball pen. My journal is a Scribbles T',
      url: 'https://www.youtube.com/watch?v=e4HiLVPrAUs',
      embedId: 'e4HiLVPrAUs',
      category: 'journal-spread',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2020-01',
      thumbnail: 'https://img.youtube.com/vi/e4HiLVPrAUs/maxresdefault.jpg'
    },
    {
      id: 'yt-api-536',
      platform: 'youtube',
      title: 'Planning October with Cora Crea',
      description: 'Video of my monthly planning - from selecting a theme and supplies to creating each page',
      url: 'https://www.youtube.com/watch?v=4XbNQd5wmRE',
      embedId: '4XbNQd5wmRE',
      category: 'product-showcase',
      tags: ['crafting', 'creative'],
      creator: 'CoraCreaCrafts',
      date: '2019-10',
      thumbnail: 'https://img.youtube.com/vi/4XbNQd5wmRE/maxresdefault.jpg'
    },
    {
      id: 'yt-api-537',
      platform: 'youtube',
      title: 'Travel Journal Flip Through & Packing Video',
      description: 'Custom Travel Journal created as a wedding gift for a couple going to New Zealand for their honeymoon.  Flip Through of all pages & packing process video',
      url: 'https://www.youtube.com/watch?v=Pu7kgeezSHU',
      embedId: 'Pu7kgeezSHU',
      category: 'product-showcase',
      tags: ['journaling'],
      creator: 'CoraCreaCrafts',
      date: '2019-09',
      thumbnail: 'https://img.youtube.com/vi/Pu7kgeezSHU/maxresdefault.jpg'
    }
  ];

  /* ---------- State ---------- */

  /**
   * Gallery state object (reactive)
   *
   * Updated by event handlers and filter functions.
   * Drives re-render on change.
   *
   * @type {Object}
   * @property {Array} filteredVideos - videos after filter/search/sort
   * @property {string} activeFilter - 'all', 'youtube', 'tiktok', 'instagram'
   * @property {string} activeCategory - category filter, 'all' or specific
   * @property {string} searchQuery - text search query
   * @property {string} sortBy - 'newest', 'oldest', 'title'
   * @property {string} viewMode - 'cards' or 'list' (CSS class suffix)
   * @property {number} currentModalIndex - open modal video index, -1 if closed
   */
  const state = {
    filteredVideos: VIDEOS.slice(),
    activeFilter: 'all',
    activeCategory: 'all',
    searchQuery: '',
    sortBy: 'newest',
    viewMode: 'cards',
    currentModalIndex: -1,
  };

  /* ---------- Category Map ---------- */

  /**
   * Category ID to display name mapping
   * @type {Object<string, string>}
   * @constant
   */
  const CATEGORIES = {
    all: 'All',
    'box-reveal': 'Box Reveals',
    unboxing: 'Unboxings',
    'journal-spread': 'Journal Spreads',
    'product-showcase': 'Products',
    tutorial: 'Tutorials',
    'behind-the-scenes': 'Behind the Scenes',
  };

  /* ---------- Platform Labels ---------- */

  /**
   * Platform name to display label mapping
   * @type {Object<string, string>}
   * @constant
   */
  const PLATFORM_LABELS = {
    tiktok: 'TikTok',
    instagram: 'Instagram',
    youtube: 'YouTube',
  };

  /* ---------- Date Formatting ---------- */

  /**
   * Abbreviated month names for date labels
   * @type {Array<string>}
   * @constant
   */
  const MONTH_NAMES = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  /* ---------- SVG Icons ---------- */

  /**
   * SVG icon definitions for gallery metadata
   * @type {Object<string, string>}
   * @constant
   */
  const ICONS = {
    CREATOR:
      '<svg fill="currentColor" viewBox="0 0 24 24"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>',
    CALENDAR:
      '<svg fill="currentColor" viewBox="0 0 24 24"><path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11z"/></svg>',
    PLAY: '<svg fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>',
  };

  /* ---------- Initialize ---------- */

  /**
   * Initialize gallery on page load
   *
   * Called automatically when DOM is ready (via DOMContentLoaded or direct exec).
   * Sets up all event listeners, applies initial filters, updates stats.
   *
   * @returns {void}
   * @private
   */
  function init() {
    const initStart = performance.now();
    const issues = [];

    // Bind events
    try {
      bindEvents();
    } catch (e) {
      issues.push('Event binding error: ' + e.message);
    }

    // Apply initial filters and render
    try {
      applyFilters();
    } catch (e) {
      issues.push('Filter application error: ' + e.message);
    }

    // Update stats
    try {
      updateStats();
    } catch (e) {
      issues.push('Stats update error: ' + e.message);
    }

    // Log data health
    if (window.VideoGalleryMetrics) {
      const platformCounts = {
        youtube: countByPlatform('youtube'),
        tiktok: countByPlatform('tiktok'),
        instagram: countByPlatform('instagram'),
      };
      window.VideoGalleryMetrics.logDataHealth(VIDEOS.length, platformCounts, []);

      const initDuration = performance.now() - initStart;
      window.VideoGalleryMetrics.logInitialization(initDuration, issues.length === 0, issues);
    }
  }

  /* ========== HELPER: Toggle Active Button State ========== */

  /**
   * Toggle active button state in a button group
   *
   * Removes 'active' class from all siblings, adds it to clicked button.
   * Used for filter, category, and view mode buttons (mutually exclusive).
   *
   * @param {HTMLElement} clickedBtn - button element to mark active
   * @param {string} selectorClass - CSS selector for button group (e.g., '.vg-filter-btn')
   * @returns {void}
   * @private
   */
  function updateActiveButton(clickedBtn, selectorClass) {
    const siblings = document.querySelectorAll(selectorClass);
    for (let i = 0; i < siblings.length; i++) {
      siblings[i].classList.remove(CLASS_SELECTORS.ACTIVE);
    }
    clickedBtn.classList.add(CLASS_SELECTORS.ACTIVE);
  }

  /* ========== HELPER: Search Input Handler ========== */

  /**
   * Handle search input change
   *
   * Updates state.searchQuery, applies filters to grid.
   * Searches: title, description, tags, creator fields.
   *
   * @param {Event} event - input change event
   * @returns {void}
   * @private
   */
  function handleSearchInput(event) {
    state.searchQuery = event.target.value.toLowerCase().trim();
    applyFilters();
  }

  /* ========== HELPER: Platform Filter Handler ========== */

  /**
   * Handle platform filter button click
   *
   * Updates activeFilter, marks button active, re-applies filters.
   * Reads platform from data-platform attribute.
   *
   * @param {Event} event - click event from filter button
   * @returns {void}
   * @private
   */
  function handlePlatformFilter(event) {
    updateActiveButton(event.target, CLASS_SELECTORS.FILTER_BTN);
    state.activeFilter = event.target.getAttribute('data-platform');
    applyFilters();
  }

  /* ========== HELPER: Category Filter Handler ========== */

  /**
   * Handle category filter button click
   *
   * Updates activeCategory, marks button active, re-applies filters.
   * Reads category from data-category attribute.
   *
   * @param {Event} event - click event from category button
   * @returns {void}
   * @private
   */
  function handleCategoryFilter(event) {
    updateActiveButton(event.target, CLASS_SELECTORS.CAT_BTN);
    state.activeCategory = event.target.getAttribute('data-category');
    applyFilters();
  }

  /* ========== HELPER: Sort Change Handler ========== */

  /**
   * Handle sort dropdown change
   *
   * Updates sortBy, re-applies filters to resort grid.
   * Options: 'newest', 'oldest', 'title'
   *
   * @param {Event} event - change event from sort select
   * @returns {void}
   * @private
   */
  function handleSortChange(event) {
    state.sortBy = event.target.value;
    applyFilters();
  }

  /* ========== HELPER: View Mode Toggle Handler ========== */

  /**
   * Handle view mode toggle (cards vs list)
   *
   * Updates viewMode (affects grid CSS class), marks button active.
   * Reads view mode from data-view attribute.
   *
   * @param {Event} event - click event from view button
   * @returns {void}
   * @private
   */
  function handleViewToggle(event) {
    updateActiveButton(event.target, CLASS_SELECTORS.VIEW_BTN);
    state.viewMode = event.target.getAttribute('data-view');
    updateGridView();
  }

  /* ========== HELPER: Modal Backdrop Click Handler ========== */

  /**
   * Handle click on modal backdrop (outside content)
   *
   * Closes modal if click is on backdrop itself (not content).
   * Bound to modal element with 'this' context.
   *
   * @param {Event} event - click event
   * @returns {void}
   * @private
   */
  function handleModalBackdropClick(event) {
    if (event.target === this) closeModal();
  }

  /* ========== HELPER: Keyboard Navigation Handler ========== */

  /**
   * Handle keyboard navigation in modal
   *
   * - Escape: close modal
   * - ArrowLeft: previous video
   * - ArrowRight: next video
   *
   * Only active when modal is open (currentModalIndex >= 0).
   *
   * @param {KeyboardEvent} event - keydown event
   * @returns {void}
   * @private
   */
  function handleKeyboardNav(event) {
    if (state.currentModalIndex === -1) return;
    if (event.key === 'Escape') closeModal();
    if (event.key === 'ArrowLeft') navigateModal(-1);
    if (event.key === 'ArrowRight') navigateModal(1);
  }

  /* ---------- Event Binding ---------- */
  let thumbFallbackBound = false;

  /**
   * Cards used to carry onerror="handleThumbError(this,'...','...')", which put the platform and
   * the embed id inside a JS string inside an HTML attribute. Entity escaping cannot defend that
   * position: the HTML parser decodes &#39; back to an apostrophe before the JS parser ever sees
   * the string, so an embed id containing one closed the argument and the rest parsed as code.
   * The values now ride in data attributes, where escaping does hold, and the handler is reached
   * by delegation. `error` does not bubble, so this listens in the capture phase. It binds from
   * bindEvents rather than module scope so a throwing addEventListener stays inside init's error
   * isolation, and the flag keeps it to one listener if init ever runs twice.
   */
  function bindThumbFallbackDelegation() {
    if (thumbFallbackBound) return;
    thumbFallbackBound = true;
    document.addEventListener(
      'error',
      function (event) {
        const img = event.target;
        if (!img || img.tagName !== 'IMG' || !img.hasAttribute('data-vg-embed')) return;
        // _handleThumbError swaps in a data-URI placeholder as its last resort. That cannot
        // fail, but a delegated listener has no img.onerror to null out, so the attribute is
        // what stops a later error on the same element re-entering the fallback chain.
        if (img.getAttribute('data-vg-thumb-fallback') === 'done') return;
        _handleThumbError(
          img,
          img.getAttribute('data-vg-platform'),
          img.getAttribute('data-vg-embed')
        );
      },
      true
    );
  }

  function bindEvents() {
    bindThumbFallbackDelegation();

    // Search input
    const searchInput = document.getElementById(DOM_IDS.SEARCH);
    if (searchInput) {
      searchInput.addEventListener('input', handleSearchInput);
    }

    // Platform filters - event delegation
    const filterContainer = document.querySelector(CLASS_SELECTORS.FILTER_BTN);
    if (filterContainer && filterContainer.parentElement) {
      filterContainer.parentElement.addEventListener('click', function (event) {
        if (event.target.closest(CLASS_SELECTORS.FILTER_BTN)) {
          handlePlatformFilter(event);
        }
      });
    } else {
      // Fallback: direct binding for each button
      const filterBtns = document.querySelectorAll(CLASS_SELECTORS.FILTER_BTN);
      for (let i = 0; i < filterBtns.length; i++) {
        filterBtns[i].addEventListener('click', handlePlatformFilter);
      }
    }

    // Category filters - event delegation
    const catContainer = document.querySelector(CLASS_SELECTORS.CAT_BTN);
    if (catContainer && catContainer.parentElement) {
      catContainer.parentElement.addEventListener('click', function (event) {
        if (event.target.closest(CLASS_SELECTORS.CAT_BTN)) {
          handleCategoryFilter(event);
        }
      });
    } else {
      // Fallback: direct binding for each button
      const catBtns = document.querySelectorAll(CLASS_SELECTORS.CAT_BTN);
      for (let i = 0; i < catBtns.length; i++) {
        catBtns[i].addEventListener('click', handleCategoryFilter);
      }
    }

    // Sort select
    const sortSelect = document.getElementById(DOM_IDS.SORT);
    if (sortSelect) {
      sortSelect.addEventListener('change', handleSortChange);
    }

    // View toggle
    const viewContainer = document.querySelector(CLASS_SELECTORS.VIEW_BTN);
    if (viewContainer && viewContainer.parentElement) {
      viewContainer.parentElement.addEventListener('click', function (event) {
        if (event.target.closest(CLASS_SELECTORS.VIEW_BTN)) {
          handleViewToggle(event);
        }
      });
    } else {
      // Fallback: direct binding for each button
      const viewBtns = document.querySelectorAll(CLASS_SELECTORS.VIEW_BTN);
      for (let i = 0; i < viewBtns.length; i++) {
        viewBtns[i].addEventListener('click', handleViewToggle);
      }
    }

    // Modal close button
    const modalClose = document.getElementById(DOM_IDS.MODAL_CLOSE);
    if (modalClose) {
      modalClose.addEventListener('click', closeModal);
    }

    // Modal backdrop click
    const modal = document.getElementById(DOM_IDS.MODAL);
    if (modal) {
      modal.addEventListener('click', handleModalBackdropClick);
    }

    // Modal navigation
    const prevBtn = document.getElementById(DOM_IDS.MODAL_PREV);
    const nextBtn = document.getElementById(DOM_IDS.MODAL_NEXT);
    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        navigateModal(-1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        navigateModal(1);
      });
    }

    // Keyboard navigation
    document.addEventListener('keydown', handleKeyboardNav);
  }

  /* ========== HELPER: Check Platform Filter ========== */

  /**
   * Check if video matches platform filter
   *
   * @param {Object} video - video object to check
   * @param {string} activeFilter - 'all' or platform name
   * @returns {boolean} true if video matches filter
   * @private
   */
  function matchesPlatform(video, activeFilter) {
    if (activeFilter === 'all') {
      return true;
    }
    return video.platform === activeFilter;
  }

  /* ========== HELPER: Check Category Filter ========== */

  /**
   * Check if video matches category filter
   *
   * @param {Object} video - video object to check
   * @param {string} activeCategory - 'all' or category name
   * @returns {boolean} true if video matches filter
   * @private
   */
  function matchesCategory(video, activeCategory) {
    if (activeCategory === 'all') {
      return true;
    }
    return video.category === activeCategory;
  }

  /* ========== HELPER: Check Search Query ========== */

  /**
   * Check if video matches search query
   *
   * Searches: title, description, tags (space-joined), creator.
   * Case-insensitive substring match.
   *
   * @param {Object} video - video object to check
   * @param {string} searchQuery - search text (already lowercased)
   * @returns {boolean} true if video matches query
   * @private
   */
  function matchesSearchQuery(video, searchQuery) {
    if (!searchQuery) return true;
    const searchableText = (
      video.title +
      ' ' +
      video.description +
      ' ' +
      video.tags.join(' ') +
      ' ' +
      video.creator
    ).toLowerCase();
    return searchableText.includes(searchQuery);
  }

  /* ========== HELPER: Comparator for Sorting ========== */

  /**
   * Compare two videos for sorting
   *
   * Used by Array.prototype.sort().
   * Supported sort modes:
   * - 'newest': most recent first (videoB.date - videoA.date)
   * - 'oldest': least recent first (videoA.date - videoB.date)
   * - 'title': alphabetical by title
   *
   * @param {Object} videoA - first video to compare
   * @param {Object} videoB - second video to compare
   * @param {string} sortBy - sort mode ('newest', 'oldest', 'title')
   * @returns {number} negative if A < B, 0 if equal, positive if A > B
   * @private
   */
  function compareVideos(videoA, videoB, sortBy) {
    switch (sortBy) {
      case 'newest':
        return videoB.date.localeCompare(videoA.date);
      case 'oldest':
        return videoA.date.localeCompare(videoB.date);
      case 'title':
        return videoA.title.localeCompare(videoB.title);
      default:
        return 0;
    }
  }

  /* ---------- Filter & Sort ---------- */

  /**
   * Apply all filters and re-render grid
   *
   * Filters videos by: platform, category, search query.
   * Sorts results. Updates grid and results count.
   * Called by event handlers and init().
   *
   * Filter pipeline:
   * 1. All VIDEOS → filter by platform
   * 2. → filter by category
   * 3. → filter by search query
   * 4. → sort by sortBy mode
   * 5. Store in state.filteredVideos
   * 6. Render grid, update count
   *
   * @returns {void}
   * @private
   */
  function applyFilters() {
    const filterStart = performance.now();

    const results = VIDEOS.filter(function (video) {
      if (!matchesPlatform(video, state.activeFilter)) return false;
      if (!matchesCategory(video, state.activeCategory)) return false;
      if (!matchesSearchQuery(video, state.searchQuery)) return false;
      return true;
    });

    // Sort results
    results.sort(function (videoA, videoB) {
      return compareVideos(videoA, videoB, state.sortBy);
    });

    state.filteredVideos = results;

    // Track filter performance
    const filterDuration = performance.now() - filterStart;
    if (window.VideoGalleryMetrics) {
      window.VideoGalleryMetrics.trackFilterTime(filterDuration);
      window.VideoGalleryMetrics.logFilterApplied(
        state.activeFilter,
        state.activeCategory,
        state.searchQuery,
        results.length
      );
    }

    renderGrid();
    updateResultsCount();
  }

  /* ========== HELPER: Render Empty State ========== */

  /**
   * Render empty state message
   *
   * Shown when no videos match filters.
   *
   * @returns {string} HTML markup for empty state
   * @private
   */
  function renderEmptyState() {
    return (
      '<div class="vg-empty">' +
      '<div class="vg-empty__icon">&#127916;</div>' +
      '<div class="vg-empty__text">No videos match your filters</div>' +
      '<div class="vg-empty__hint">Try adjusting your search or filter criteria</div>' +
      '</div>'
    );
  }

  /* ========== HELPER: Build Cards HTML ========== */

  /**
   * Build HTML for all cards in grid
   *
   * Concatenates renderCard() output for each video.
   *
   * @param {Array} filteredVideos - videos to render
   * @returns {string} concatenated card HTML
   * @private
   */
  function buildCardsHtml(filteredVideos) {
    let html = '';
    for (let i = 0; i < filteredVideos.length; i++) {
      html += renderCard(filteredVideos[i], i);
    }
    return html;
  }

  /* ========== HELPER: Handle Card Click ========== */

  /**
   * Handle card click to open modal
   *
   * Uses event delegation: listens on grid, checks for closest .vg-card.
   * Reads video index from data-index attribute.
   *
   * @param {Event} event - click event
   * @returns {void}
   * @private
   */
  function handleCardClick(event) {
    const cardElement = event.target.closest(CLASS_SELECTORS.CARD);
    if (cardElement) {
      const videoIndex = parseInt(cardElement.getAttribute('data-index'), 10);
      openModal(videoIndex);
    }
  }

  /* ---------- Render Grid ---------- */

  /**
   * Render filtered video grid
   *
   * Hides loader, renders empty state or card HTML, then inserts it with
   * safeSetHTML. That call does no filtering; the escaping already happened in
   * buildCardsHtml, which is what keeps this safe.
   * Attaches card click listener via event delegation.
   *
   * @returns {void}
   * @private
   */
  function renderGrid() {
    const renderStart = performance.now();
    const grid = document.getElementById(DOM_IDS.GRID);
    if (!grid) return;

    // Track cache hit for grid lookup
    if (window.VideoGalleryMetrics) {
      window.VideoGalleryMetrics.logCacheHit('dom', 'vg-grid', 100);
    }

    const loader = document.getElementById(DOM_IDS.LOADER);
    if (loader) loader.style.display = 'none';

    let gridHtml = '';
    if (state.filteredVideos.length === 0) {
      gridHtml = renderEmptyState();
    } else {
      gridHtml = buildCardsHtml(state.filteredVideos);
    }

    // eslint-disable-next-line no-undef
    safeSetHTML(grid, gridHtml);

    // Use event delegation for card clicks
    grid.addEventListener('click', handleCardClick);

    // Track render performance
    const renderDuration = performance.now() - renderStart;
    if (window.VideoGalleryMetrics) {
      window.VideoGalleryMetrics.trackRenderTime(renderDuration);
    }
  }

  /* ========== HELPER: Get Badge Text by Platform ========== */

  /**
   * Get display label for platform badge
   *
   * @param {string} platform - 'youtube', 'tiktok', 'instagram'
   * @returns {string} display label
   * @private
   */
  function getBadgeText(platform) {
    switch (platform) {
      case 'tiktok':
        return 'TikTok';
      case 'instagram':
        return 'Instagram';
      case 'youtube':
      default:
        return 'YouTube';
    }
  }

  /* ========== HELPER: Build Tag HTML ========== */

  /**
   * Render tag HTML for card
   *
   * Shows max 3 tags (slice limit). Each tag escaped for XSS safety.
   *
   * @param {Array<string>} tags - tag array from video object
   * @returns {string} concatenated tag span elements
   * @private
   */
  function renderTagsHtml(tags) {
    const maxTags = 3;
    return tags
      .slice(0, maxTags)
      .map(function (tag) {
        return '<span class="vg-card__tag">' + escapeHtml(tag) + '</span>';
      })
      .join('');
  }

  /* ========== HELPER: Build Metadata HTML ========== */

  /**
   * Render metadata section (creator + date) for card
   *
   * @param {Object} video - video object
   * @param {string} dateLabel - formatted date string
   * @returns {string} HTML with creator icon, creator name, calendar icon, date
   * @private
   */
  function renderMetadataHtml(video, dateLabel) {
    return (
      '<span class="vg-card__meta-item">' +
      ICONS.CREATOR +
      escapeHtml(video.creator) +
      '</span>' +
      '<span class="vg-card__meta-item">' +
      ICONS.CALENDAR +
      dateLabel +
      '</span>'
    );
  }

  /* ========== HELPER: Build Thumbnail Section ========== */

  /**
   * Render thumbnail section (image, badges, play overlay) for card
   *
   * Includes:
   * - Thumbnail image with onerror handler
   * - Platform badge
   * - Category label
   * - Play button overlay
   *
   * @param {Object} video - video object
   * @returns {string} HTML for thumbnail section
   * @private
   */
  function renderThumbnailSection(video) {
    const thumbSrc = video.thumbnail || getDefaultThumb(video);
    const badgeClass = 'vg-badge vg-badge--' + video.platform;
    const badgeText = getBadgeText(video.platform);
    const categoryLabel = escapeHtml(CATEGORIES[video.category] || video.category);

    return (
      '<div class="vg-card__thumb">' +
      '<img src="' +
      escapeHtml(thumbSrc) +
      '" alt="' +
      escapeHtml(video.title) +
      '" loading="lazy" data-vg-platform="' +
      escapeHtml(video.platform) +
      '" data-vg-embed="' +
      escapeHtml(video.embedId) +
      '">' +
      '<div class="' +
      badgeClass +
      '">' +
      badgeText +
      '</div>' +
      '<div class="vg-card__category">' +
      categoryLabel +
      '</div>' +
      '<div class="vg-card__play-overlay"><div class="vg-card__play-btn">' +
      ICONS.PLAY +
      '</div></div>' +
      '</div>'
    );
  }

  /**
   * Render a single video card
   *
   * Assembles all card sections into HTML.
   * data-index used by click handler to identify which video was clicked.
   *
   * @param {Object} video - video object to render
   * @param {number} index - index in filtered videos array (for modal navigation)
   * @returns {string} complete card HTML
   * @private
   */
  function renderCard(video, index) {
    const dateLabel = formatDate(video.date);
    const thumbSection = renderThumbnailSection(video);
    const metadataHtml = renderMetadataHtml(video, dateLabel);
    const tagsHtml = renderTagsHtml(video.tags);

    return (
      '<div class="vg-card" data-index="' +
      index +
      '">' +
      thumbSection +
      '<div class="vg-card__body">' +
      '<div class="vg-card__title">' +
      escapeHtml(video.title) +
      '</div>' +
      '<div class="vg-card__desc">' +
      escapeHtml(video.description) +
      '</div>' +
      '<div class="vg-card__meta">' +
      metadataHtml +
      '</div>' +
      '<div class="vg-card__tags">' +
      tagsHtml +
      '</div>' +
      '</div>' +
      '</div>'
    );
  }

  /* ---------- Modal ---------- */

  /**
   * Open modal with video at index
   *
   * Sets up embed (YouTube iframe, TikTok iframe, or Instagram blockquote),
   * populates metadata, details, tags, and link.
   * Updates nav button disabled states.
   * Shows modal, hides body scroll.
   *
   * Instagram embeds: loads embed.js with retry logic.
   *
   * @param {number} index - index in state.filteredVideos
   * @returns {void}
   * @private
   */
  function openModal(index) {
    const modalStart = performance.now();
    state.currentModalIndex = index;
    const video = state.filteredVideos[index];
    if (!video) return;

    const modal = document.getElementById('vg-modal');
    const embedEl = document.getElementById('vg-modal-embed');
    const titleEl = document.getElementById('vg-modal-title');
    const descEl = document.getElementById('vg-modal-desc');
    const detailsEl = document.getElementById('vg-modal-details');
    const tagsEl = document.getElementById('vg-modal-tags');
    const linkEl = document.getElementById('vg-modal-link');

    if (!modal || !embedEl || !titleEl || !descEl || !detailsEl || !tagsEl || !linkEl) return;

    // Set embed
    embedEl.className = 'vg-modal__embed';
    if (video.platform === 'youtube') {
      embedEl.classList.add('vg-modal__embed--youtube');
      const ytFrame = document.createElement('iframe');
      ytFrame.src =
        'https://www.youtube.com/embed/' + encodeURIComponent(video.embedId) + '?autoplay=1';
      ytFrame.allow = 'autoplay; encrypted-media';
      ytFrame.allowFullscreen = true;
      ytFrame.title = video.title;
      embedEl.replaceChildren(ytFrame);
    } else if (video.platform === 'tiktok') {
      const ttFrame = document.createElement('iframe');
      ttFrame.src = 'https://www.tiktok.com/embed/v2/' + encodeURIComponent(video.embedId);
      ttFrame.allow = 'autoplay';
      ttFrame.allowFullscreen = true;
      ttFrame.title = video.title;
      embedEl.replaceChildren(ttFrame);
    } else if (video.platform === 'instagram') {
      embedEl.classList.add('vg-modal__embed--instagram');
      embedEl.innerHTML =
        '<div class="vg-ig-embed-wrap">' +
        '<div class="vg-ig-loading" id="vg-ig-loading">' +
        '<div class="vg-ig-spinner"></div>' +
        '<span>Loading Instagram video&hellip;</span>' +
        '</div>' +
        '<blockquote class="instagram-media" ' +
        'data-instgrm-permalink="' +
        escapeHtml(video.url) +
        '?utm_source=ig_embed" ' +
        'data-instgrm-version="14" ' +
        'style="background:#FFF; border:0; border-radius:3px; box-shadow:0 0 1px 0 rgba(0,0,0,0.5),0 1px 10px 0 rgba(0,0,0,0.15); margin:0 auto; max-width:540px; min-width:326px; padding:0; width:calc(100% - 2px);">' +
        '<div style="padding:16px;"><a href="' +
        escapeHtml(video.url) +
        '" target="_blank" rel="noopener" style="color:#262626; text-decoration:none;">View this post on Instagram</a></div>' +
        '</blockquote>' +
        '<a href="' +
        escapeHtml(video.url) +
        '" target="_blank" rel="noopener noreferrer" class="vg-ig-direct-link">' +
        '<svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069z"/></svg>' +
        ' Watch directly on Instagram' +
        '</a>' +
        '</div>';
      processInstagramEmbed();
    }

    titleEl.textContent = video.title;
    descEl.textContent = video.description;

    detailsEl.innerHTML =
      '<span class="vg-modal__detail"><svg fill="currentColor" viewBox="0 0 24 24"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>' +
      escapeHtml(video.creator) +
      '</span>' +
      '<span class="vg-modal__detail"><svg fill="currentColor" viewBox="0 0 24 24"><path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11z"/></svg>' +
      formatDate(video.date) +
      '</span>' +
      '<span class="vg-modal__detail"><svg fill="currentColor" viewBox="0 0 24 24"><path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/></svg>' +
      platformLabel(video.platform) +
      '</span>';

    tagsEl.innerHTML = video.tags
      .map(function (t) {
        return '<span class="vg-modal__tag">' + escapeHtml(t) + '</span>';
      })
      .join('');

    linkEl.href = video.url;
    linkEl.textContent = 'Watch on ' + platformLabel(video.platform);

    // Update nav
    const prevBtn = document.getElementById('vg-modal-prev');
    const nextBtn = document.getElementById('vg-modal-next');
    if (prevBtn) prevBtn.disabled = index === 0;
    if (nextBtn) nextBtn.disabled = index >= state.filteredVideos.length - 1;

    modal.classList.add('open');
    document.body.style.overflow = 'hidden';

    // Track modal load performance
    const modalDuration = performance.now() - modalStart;
    if (window.VideoGalleryMetrics) {
      window.VideoGalleryMetrics.trackModalLoad(modalDuration);
    }
  }

  /**
   * Close the modal and reset state
   *
   * Removes 'open' class, clears embed element, restores body scroll,
   * and resets currentModalIndex to -1.
   *
   * @returns {void}
   *
   * @example
   * closeModal();
   */
  function closeModal() {
    const modal = document.getElementById('vg-modal');
    if (!modal) return;
    modal.classList.remove('open');
    const embedEl = document.getElementById('vg-modal-embed');
    if (embedEl) embedEl.replaceChildren();
    document.body.style.overflow = '';
    state.currentModalIndex = -1;
  }

  /**
   * Navigate modal to adjacent video
   *
   * Opens the modal at (currentModalIndex + direction) if in valid range.
   * Direction +1 moves to next video, -1 moves to previous.
   *
   * @param {number} direction - delta to apply to currentModalIndex (+1 or -1)
   * @returns {void}
   *
   * @example
   * navigateModal(1);  // Next video
   * navigateModal(-1); // Previous video
   */
  function navigateModal(direction) {
    const newIdx = state.currentModalIndex + direction;
    if (newIdx >= 0 && newIdx < state.filteredVideos.length) {
      openModal(newIdx);
    }
  }

  /* ---------- View Mode ---------- */

  /**
   * Update grid CSS class based on current view mode
   *
   * Sets grid className to 'vg-grid vg-grid--{viewMode}' where viewMode
   * is 'grid', 'list', or 'compact'. Triggered on view mode change.
   *
   * @returns {void}
   *
   * @example
   * state.viewMode = 'list';
   * updateGridView(); // Grid now has class 'vg-grid vg-grid--list'
   */
  function updateGridView() {
    const grid = document.getElementById('vg-grid');
    if (!grid) return;
    grid.className = 'vg-grid vg-grid--' + state.viewMode;
  }

  /* ========== HELPER: Count Videos by Platform ========== */

  /**
   * Count videos matching a platform
   *
   * @param {string} platform - platform to filter by ('youtube', 'tiktok', 'instagram')
   * @returns {number} count of videos from the specified platform
   *
   * @example
   * countByPlatform('youtube') // → 45
   */
  function countByPlatform(platform) {
    return VIDEOS.filter(function (video) {
      return video.platform === platform;
    }).length;
  }

  /* ========== HELPER: Format Number with Locale ========== */

  /**
   * Format number with locale-specific thousands separator
   *
   * @param {number} num - number to format
   * @returns {string} formatted number string (e.g., "1,234" in en-US)
   *
   * @example
   * formatNumber(1234) // → "1,234"
   */
  function formatNumber(num) {
    return num.toLocaleString();
  }

  /* ========== HELPER: Update Stat Element ========== */

  /**
   * Update a stat display element with formatted number
   *
   * Sets element.textContent to formatNumber(value). Safe to call if element
   * doesn't exist — silently skips if element is not found.
   *
   * @param {string} elementId - DOM element ID to update
   * @param {number} value - numeric value to format and display
   * @returns {void}
   *
   * @example
   * updateStatElement('stat-total', 125) // Sets #stat-total to "125"
   */
  function updateStatElement(elementId, value) {
    const element = document.getElementById(elementId);
    if (element) {
      element.textContent = formatNumber(value);
    }
  }

  /* ---------- Stats ---------- */

  /**
   * Update all stats elements (total and per-platform counts)
   *
   * Updates 4 stat elements: total video count, TikTok count, Instagram count,
   * YouTube count. Called during init and when data changes.
   *
   * @returns {void}
   *
   * @example
   * updateStats(); // Updates #vg-stat-total, #vg-stat-tiktok, etc.
   */
  function updateStats() {
    updateStatElement(DOM_IDS.STAT_TOTAL, VIDEOS.length);
    updateStatElement(DOM_IDS.STAT_TIKTOK, countByPlatform('tiktok'));
    updateStatElement(DOM_IDS.STAT_INSTAGRAM, countByPlatform('instagram'));
    updateStatElement(DOM_IDS.STAT_YOUTUBE, countByPlatform('youtube'));
  }

  /**
   * Update the results count display (filtered count / total count)
   *
   * Updates element with text like "42 of 125 videos". Called after filtering.
   *
   * @returns {void}
   *
   * @example
   * updateResultsCount(); // Updates #vg-results-count to "42 of 125 videos"
   */
  function updateResultsCount() {
    const element = document.getElementById(DOM_IDS.RESULTS_COUNT);
    if (element) {
      element.textContent =
        formatNumber(state.filteredVideos.length) +
        ' of ' +
        formatNumber(VIDEOS.length) +
        ' videos';
    }
  }

  /* ---------- Helpers ---------- */

  /**
   * Get thumbnail URL for a video or fallback to default placeholder
   *
   * YouTube videos use img.youtube.com CDN with maxresdefault quality.
   * TikTok and Instagram don't have reliable static thumbnails, so falls back
   * to DEFAULT_PLACEHOLDER_SVG (gradient placeholder).
   *
   * @param {Object} video - video object with platform and embedId properties
   * @returns {string} thumbnail URL or SVG data URL
   *
   * @example
   * getDefaultThumb({platform: 'youtube', embedId: 'abc123'})
   * // → "https://img.youtube.com/vi/abc123/maxresdefault.jpg"
   */
  function getDefaultThumb(video) {
    if (video.platform === 'youtube') {
      return 'https://img.youtube.com/vi/' + video.embedId + '/maxresdefault.jpg';
    }
    // TikTok and Instagram don't have reliable static thumbnail URLs
    // Use a gradient placeholder with platform color
    return DEFAULT_PLACEHOLDER_SVG;
  }

  /**
   * Format ISO YYYY-MM date string to "Month Year"
   *
   * Parses ISO date format (e.g., "2026-04") and returns human-readable format
   * (e.g., "Apr 2026"). Returns empty string if input is falsy.
   *
   * @param {string} dateStr - ISO date in YYYY-MM format
   * @returns {string} formatted date (e.g., "Apr 2026") or empty string
   *
   * @example
   * formatDate('2026-04') // → "Apr 2026"
   */
  function formatDate(dateStr) {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    const monthIndex = parseInt(parts[1], 10) - 1;
    return MONTH_NAMES[monthIndex] + ' ' + parts[0];
  }

  /**
   * Get human-readable label for a platform
   *
   * @param {string} platform - platform key ('youtube', 'tiktok', 'instagram')
   * @returns {string} platform label (e.g., "YouTube", "TikTok", "Instagram") or "YouTube" if not found
   *
   * @example
   * platformLabel('tiktok') // → "TikTok"
   */
  function platformLabel(platform) {
    return PLATFORM_LABELS[platform] || 'YouTube';
  }

  /* ---------- Instagram Embed Processing ---------- */

  /**
   * Configuration for Instagram embed.js retry logic
   *
   * embed.js loads asynchronously and may not be ready when processInstagramEmbed()
   * is called. This config controls retry behavior.
   *
   * @type {Object}
   * @constant
   * @property {number} MAX_ATTEMPTS - maximum retry attempts (default: 15, ~7.5s total wait)
   * @property {number} RETRY_INTERVAL - ms between retries (default: 500)
   * @property {number} LOADER_HIDE_DELAY - ms to wait after embed.js processes before hiding loader (default: 1500)
   */
  const INSTAGRAM_EMBED_CONFIG = {
    MAX_ATTEMPTS: 15,
    RETRY_INTERVAL: 500,
    LOADER_HIDE_DELAY: 1500,
  };

  /**
   * Hide the Instagram loading indicator
   *
   * Sets #vg-ig-loading to display: none. Safe to call if element doesn't exist.
   *
   * @returns {void}
   *
   * @example
   * hideInstagramLoader();
   */
  function hideInstagramLoader() {
    const loader = document.getElementById('vg-ig-loading');
    if (loader) loader.style.display = 'none';
  }

  /**
   * Process Instagram embed blockquotes via embed.js
   *
   * Instagram embed.js must process <blockquote> elements to render them as embeds.
   * This function:
   * 1. Loads embed.js if not already present
   * 2. Retries window.instgrm.Embeds.process() up to MAX_ATTEMPTS times
   * 3. Hides loading indicator after successful processing
   *
   * Fallback link (from <a> inside blockquote) remains visible if embed.js fails.
   *
   * @returns {void}
   *
   * @example
   * processInstagramEmbed(); // Loads embed.js and processes blockquotes
   */
  function processInstagramEmbed() {
    let attempts = 0;

    function tryProcess() {
      attempts++;
      if (window.instgrm && window.instgrm.Embeds) {
        window.instgrm.Embeds.process();
        // Hide loading after embed.js processes (give it a moment to render)
        setTimeout(hideInstagramLoader, INSTAGRAM_EMBED_CONFIG.LOADER_HIDE_DELAY);
      } else if (attempts < INSTAGRAM_EMBED_CONFIG.MAX_ATTEMPTS) {
        setTimeout(tryProcess, INSTAGRAM_EMBED_CONFIG.RETRY_INTERVAL);
      } else {
        // embed.js never loaded - hide loading, the blockquote fallback link remains visible
        hideInstagramLoader();
      }
    }

    // Load embed.js if not already present
    if (!document.querySelector('script[src*="instagram.com/embed.js"]')) {
      const s = document.createElement('script');
      s.src = '//www.instagram.com/embed.js';
      s.async = true;
      document.body.appendChild(s);
    }

    tryProcess();
  }

  /* ---------- Thumbnail Error Handler ---------- */

  /**
   * Public thumbnail error handler for inline onerror attributes
   *
   * Delegates to _handleThumbError() to handle image load failures with fallbacks:
   * - YouTube: tries hqdefault if maxresdefault failed
   * - All platforms: uses default SVG placeholder if all fallbacks exhausted
   *
   * Validates embedId before constructing fallback URLs to prevent XSS.
   *
   * @param {HTMLImageElement} img - image element with failed src
   * @param {string} platform - 'youtube', 'tiktok', or 'instagram'
   * @param {string} embedId - platform-specific video ID (validated before use)
   * @returns {void}
   *
   * Retained as a public entry point for callers outside this file. Cards no longer use it
   * directly; they carry data-vg-platform and data-vg-embed and are served by the delegated
   * capture listener installed at the bottom of this module.
   *
   * @example
   * window.handleThumbError(document.querySelector('img'), 'youtube', 'abc123');
   */
  window.handleThumbError = function (img, platform, embedId) {
    _handleThumbError(img, platform, embedId);
  };

  /**
   * Escape HTML special characters to prevent XSS
   *
   * Uses browser DOM API to safely escape: &, <, >, ", '.
   * Preferred over string.replace() for correctness.
   *
   * @param {string} str - string to escape
   * @returns {string} HTML-escaped string, or empty string if input falsy
   *
   * @example
   * escapeHtml('<script>alert(1)</script>')
   * // → "&lt;script&gt;alert(1)&lt;/script&gt;"
   */
  function escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    // Text-node serialization escapes & < > and leaves both quote characters intact, so the
    // result was safe between tags but broke out of alt="...". Seventeen shipped Instagram
    // titles contain a double quote. Escaping happens here, not at call sites, so there is one
    // place to audit. Mirrors escapeHtml in admin-dashboard-core.js. Only the double-quote half
    // has a test that bites: every attribute built here is double-quoted, so a single quote
    // cannot break out of one, and inside onerror="fn('...')" the parser decodes &#39; back to a
    // quote before the JS parser runs, which is why that hole is closed by delegation instead.
    // The single-quote half is kept for a future single-quoted attribute and for parity with the
    // canonical escaper, not because anything today exercises it.
    return div.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* ---------- DOMReady ---------- */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
